import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
// AdminApp is now rendered by AppRoot, not by App
import axios from 'axios';
import html2canvas from 'html2canvas';
// Firebase Phone Auth - real SMS via Firebase
import { sendPhoneVerificationCode, verifyPhoneCode, getFirebaseAuth, initRecaptcha, clearRecaptcha } from './lib/firebase';
import { registerPushNotifications, isPushSupported } from './lib/pushNotifications';
// Legal Pages
import PrivacyPolicy from './legal/PrivacyPolicy';
import { BRAND_ASSETS } from './brandAssets';
import TermsOfService from './legal/TermsOfService';
import CommunityGuidelines from './legal/CommunityGuidelines';
// Modals
import AgeVerificationModal from './components/AgeVerificationModal';
import TermsAcceptanceModal from './components/TermsAcceptanceModal';
import CommunityGuidelinesModal from './components/CommunityGuidelinesModal';
import ShareStreamModal from './components/ShareStreamModal';
import { CurrentEventsPostBody } from './components/CurrentEventsCards';
import IDVerificationModal from './components/IDVerificationModal';
import BannedUserScreen from './components/BannedUserScreen';
import LiveInvitePopup, { useLiveInvites } from './components/LiveInvitePopup';
import GiftStage from './components/GiftStage';
import GiftPicker from './components/GiftPicker';
import MentionBox from './components/MentionBox';
import SpectatorGameOverlay from './components/SpectatorGameOverlay';
import LiveKitLinkGrid from './LiveKitLinkGrid';
import LiveKitMultiViewer from './LiveKitMultiViewer';
import { openMediaLightbox } from './utils/mediaLightbox';
import { ownLinks } from './utils/ownLinks';
import TournamentCards from './components/TournamentCards';

// FannedHand (Aug 18, 2026): overlapping arc of cards floating over the
// video (marketing look). Used by in-live Spades and in-live C8 hands.
function FannedHand({ cards, onPlay, disabled = false, renderCard, cardKey, inline = false }) {
  const n = cards.length;
  if (!n) return null;
  const step = Math.min(38, 320 / n);
  const maxRot = Math.min(6, 64 / n);
  return (
    <div
      // `inline` (Feb 2026): render IN document flow — required when the
      // host container is statically positioned (the card-game BAND).
      // The old absolute-bottom variant anchored to whatever positioned
      // ancestor happened to be nearest, which floated the cards over the
      // video tiles (or off-screen) once the fan moved out of the overlay.
      className={inline
        ? 'relative mx-auto z-30 pointer-events-none'
        : 'absolute bottom-1 left-1/2 -translate-x-1/2 z-30 pointer-events-none'}
      // inline: full-width — cards spread evenly across the whole band by
      // PERCENTAGE (see leftPos below) instead of bunching in a 320px arc.
      style={{ height: inline ? '8.5rem' : '8rem', width: inline ? 'calc(100% - 56px)' : 'min(94vw, 680px)' }}
      data-testid="fanned-hand"
    >
      {cards.map((card, i) => {
        const off = i - (n - 1) / 2;
        // inline: evenly distribute across the full container width so a
        // 13-card hand fills the band edge-to-edge on any screen size.
        const leftPos = inline
          ? `${((i + 0.5) / n) * 100}%`
          : `calc(50% + ${off * step}px)`;
        return (
          <button
            key={cardKey ? cardKey(card, i) : i}
            type="button"
            disabled={disabled}
            className={`absolute pointer-events-auto transition-transform duration-150 ${
              disabled ? '' : 'hover:-translate-y-5 hover:z-50 cursor-pointer'
            }`}
            style={{
              left: leftPos,
              bottom: `${-(off * off) * (inline ? 0.5 : 0.9)}px`,
              transform: `translateX(-50%) rotate(${off * maxRot}deg)`,
              transformOrigin: 'bottom center',
              zIndex: 10 + i,
            }}
            onClick={() => !disabled && onPlay(card)}
          >
            {renderCard(card)}
          </button>
        );
      })}
    </div>
  );
}

// FitToParent (Aug 18, 2026): scales its child down to fit inside the
// parent while preserving aspect ratio; used by the in-live board games
// (4iaR, Checkers) so the ENTIRE board is visible without scrolling on
// any viewport. Card games (Spades, Crazy Eights) do NOT use this.
function FitToParent({ children, className = '' }) {
  const outerRef = React.useRef(null);
  const innerRef = React.useRef(null);
  const [scale, setScale] = React.useState(1);
  React.useEffect(() => {
    const measure = () => {
      const o = outerRef.current, i = innerRef.current;
      if (!o || !i || !i.offsetWidth || !i.offsetHeight) return;
      const s = Math.min(1, o.clientWidth / i.offsetWidth, o.clientHeight / i.offsetHeight);
      if (s > 0 && Number.isFinite(s)) setScale(s);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (outerRef.current) ro.observe(outerRef.current);
    if (innerRef.current) ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={outerRef} className={`w-full h-full flex items-center justify-center overflow-hidden ${className}`}>
      <div ref={innerRef} style={{ transform: `scale(${scale})`, transformOrigin: 'center' }}>{children}</div>
    </div>
  );
}
// LiveKit for Solo Streaming
import LiveKitStreamPublisher from './LiveKitStreamPublisher';
import LiveKitStreamViewer from './LiveKitStreamViewer';
// Agora components
import AgoraVideoPlayer from './AgoraVideoPlayer';
import AgoraMultiplayerViewer from './AgoraMultiplayerViewer';
import { uidFromUserId } from './utils/agoraUid';

// Checkpoint 3: viewer UID resolver — wraps both auth + anon paths.
// Authenticated viewer → hash of user.id. Anonymous viewer → hash of
// gems_visitor_id (persisted in localStorage). Never returns 0.
function resolveViewerAgoraUid(userObj) {
  if (userObj && userObj.id) return uidFromUserId(userObj.id);
  return uidFromUserId(getOrCreateVisitorId());
}

// Checkpoint 3: shared mesh-debug logger for every client.on('user-published')
// handler. The user requested ONE diagnostic surface across all 6 handlers so
// a 3-broadcaster smoke produces a consistent trace from A, B, and C. Active
// in dev and prod — these are cheap log lines that are essential when
// reproducing the "B and C see only A and self" mesh regression.
function meshDebugLog(label, client, remoteUser, mediaType) {
  try {
    const agoraClientRefCurrent = (typeof window !== 'undefined' && window.__GEMS_AGORA_CLIENT_REF)
      ? window.__GEMS_AGORA_CLIENT_REF.current
      : undefined;
    // eslint-disable-next-line no-console
    console.log('[mesh-debug]', label, {
      myChannel: client?.channelName,
      myUid: uidFromUserId(__GEMS_CURRENT_USER_ID),
      remoteUid: remoteUser?.uid,
      mediaType,
      remoteUsersBefore: (client?.remoteUsers || []).map(u => u.uid),
      clientRefSame: agoraClientRefCurrent ? client === agoraClientRefCurrent : 'unknown',
      // Mesh-Fix (May 15): expose published-track ids so we can correlate
      // a "missing video" report to a stale/orphan publish on the inviter.
      publishedTracks: (typeof window !== 'undefined' && window.__GEMS_PUBLISHED_TRACKS) || null,
      ts: Date.now(),
    });
  } catch (e) {
    // never throw from a debug logger
    // eslint-disable-next-line no-console
    console.warn('[mesh-debug] log failed', e?.message);
  }
}

// Mesh-Fix (May 15, 2026): on-demand mesh snapshot the user can call from
// the browser console on each of the 3 devices during a smoke test:
//   window.__GEMS_MESH_DUMP()
// Returns the exact runtime state required to determine whether a
// hub-spoke regression is (a) channel-divergence, (b) co-channel
// no-subscribe, or (c) co-channel subscribed but render-derive bug.
if (typeof window !== 'undefined') {
  window.__GEMS_MESH_DUMP = function meshDump() {
    try {
      const c = (window.__GEMS_AGORA_CLIENT_REF && window.__GEMS_AGORA_CLIENT_REF.current) || null;
      const snapshot = {
        myUid: uidFromUserId(__GEMS_CURRENT_USER_ID),
        myUserId: __GEMS_CURRENT_USER_ID,
        clientPresent: !!c,
        channelName: c?.channelName || null,
        connectionState: c?.connectionState || null,
        remoteUsersUids: (c?.remoteUsers || []).map((u) => u.uid),
        remoteUsersDetail: (c?.remoteUsers || []).map((u) => ({
          uid: u.uid,
          hasVideo: !!u.hasVideo,
          hasAudio: !!u.hasAudio,
          videoTrackId: u.videoTrack?.getTrackId?.() || null,
          audioTrackId: u.audioTrack?.getTrackId?.() || null,
        })),
        publishedTracks: window.__GEMS_PUBLISHED_TRACKS || [],
        ts: Date.now(),
      };
      // eslint-disable-next-line no-console
      console.log('[mesh-dump]', JSON.stringify(snapshot, null, 2));
      return snapshot;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[mesh-dump] failed', e);
      return { error: e?.message };
    }
  };
}
// Games
import FourInARowGame from './components/FourInARowGame';
import CheckersMultiplayerGame from './components/CheckersMultiplayerGame';
// Crazy Eights client mirror (shared with the golden-transcript test)
import {
  C8_RANKS as C8_RANKS_IMPORT,
  C8_SUIT_IDS as C8_SUIT_IDS_IMPORT,
  C8 as C8_IMPORT,
  c8NewGame as c8NewGameFn,
  c8IsLegal as c8IsLegalFn,
  c8PlayCard as c8PlayCardFn,
  c8DrawCard as c8DrawCardFn,
  c8PassAfterDraw as c8PassAfterDrawFn,
  c8ChooseAISuit as c8ChooseAISuitFn,
  c8AIChoosePlay as c8AIChoosePlayFn,
  c8Current as c8CurrentFn,
} from './c8_engine';
import {
  CheckersPracticePage,
  CheckersEntryModal,
  CheckersBoard,
} from './checkers_ui';
// Pages
import ProfilePageComponent from './components/ProfilePage';
import NotificationsPage from './components/NotificationsPage';

// ============================================================================
// Bug 5 (May 7 testing): module-level mirror of the current authenticated
// user's id. Read by every Agora `client.on('user-published', ...)` handler
// to filter out our own echo from the SFU (which manifested as a mirrored-
// self tile when the same user briefly appeared as both publisher and
// remote subscriber on republish). Module-level (vs React ref) because the
// callbacks shadow the React `user` with the Agora SDK's `user` object,
// making outer-scope access fragile and refactor-prone. App's useEffect
// keeps this in lockstep with the authenticated user.
// ============================================================================
// ============================================================================
// Viewer linked/multiplayer predicate (May 11 audit — Bug candidate 1)
// Single source of truth for both the FETCH path (which token to request)
// and the RENDER path (which player component to mount). Pre-fix the two
// callsites disagreed: fetch checked `linked_session_id`/`linked_to_stream`
// but render only checked `is_linked`/`stream_type === 'multiplayer'`. A
// stream with linked_session_id set but neither of those would get an
// Agora token and then fall through the render predicate to the LiveKit
// branch — except LiveKit token was null, so the viewer sat forever on the
// "Connecting to video stream..." fallback (the reported white screen).
// ============================================================================
function isLinkedOrMultiplayerStream(stream) {
  if (!stream) return false;
  // Agora is ONLY for linked multi-broadcaster sessions (2+ cameras).
  // Games do NOT change the video transport: the game board is a DOM
  // overlay driven by WebSockets, so a stream with an active game but
  // no link stays on LiveKit for broadcaster AND viewers.
  const isLinked = !!(stream.is_linked || stream.linked_to_stream || stream.linked_session_id);
  return isLinked || stream.stream_type === 'multiplayer';
}

// Link v2: linked sessions on LiveKit (behind feature flag).
// When false, all linked-session behavior is UNCHANGED (Agora path). When true,
// broadcaster + viewer branches (App.js steps 5-6) route linked sessions through
// LiveKit. Currently the wiring in steps 5-6 is PENDING (next session) — this
// flag is intentionally a no-op until then.
// SEP 4 2026 — AGORA RETIRED: Link v2 (LiveKit) is permanent, so the flag
// is hardcoded ON. No /config/features round-trip, no Agora fallback —
// a fetch hiccup can never flip a client onto a dead transport.
let LIVEKIT_LINKS = true;
let __featureFlagsPromise = null;
function ensureFeatureFlags() {
  if (!__featureFlagsPromise) __featureFlagsPromise = Promise.resolve();
  return __featureFlagsPromise;
}
// kickoff moved below the `api` axios instance definition (TDZ-safe).

function livekitLinkRoomName(s) {
  // One shared room per linked session — broadcasters AND viewers join it.
  if (s?.linked_session_id) return `linked_${s.linked_session_id}`;
  const anchor = s?.linked_to_stream || s?.stream_id || s?.id;
  return `linked_stream_${anchor}`;
}

let __GEMS_CURRENT_USER_ID = null;

// ============================================================================
// Checkpoint 3 (May 13 2026): viewer Agora UID resolution.
//
// Pre-fix, anonymous viewers passed `uid: 0` to /agora/token (and to
// client.join), which made Agora's server assign a random UID — every
// anonymous viewer mapped to the same key in the participants map and the
// 3rd remote tile rendered as "Broadcaster 3" because resolveIdentity()
// couldn't find a user.id for that UID.
//
// Fix: derive a stable per-browser anonymous identity from
// localStorage.gems_visitor_id (already persisted for viewer-count purposes),
// then run it through the SAME FNV-1a hash broadcasters use. Result:
//   - authenticated users: uidFromUserId(user.id)  (unchanged contract)
//   - anonymous viewers:   uidFromUserId(gems_visitor_id)
//                          → unique, non-zero, stable per tab.
// Broadcasters keep their deterministic UID. Viewers no longer collide.
// ============================================================================
function getOrCreateVisitorId() {
  try {
    let v = localStorage.getItem('gems_visitor_id');
    if (!v) {
      // crypto.randomUUID is available everywhere we support.
      v = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? `viewer_${crypto.randomUUID()}`
        : `viewer_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
      localStorage.setItem('gems_visitor_id', v);
    }
    return v;
  } catch {
    // localStorage blocked (rare). Fall back to an ephemeral id; collisions
    // here only affect a single private-mode tab session.
    return `viewer_ephemeral_${Math.random().toString(36).slice(2)}`;
  }
}

import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from './components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Separator } from './components/ui/separator';
import { Progress } from './components/ui/progress';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';
import { 
  Play, Users, User, Trophy, Crown, Gem, Coins, Video, MessageCircle, Settings, LogOut,
  Signal, Wifi, WifiOff, Gift, Heart, Zap, Star, Flame, Rocket, Home, Monitor,
  Eye, EyeOff, Mic, MicOff, VideoIcon, VideoOff, PhoneCall, PhoneOff,
  Spade, Club, Diamond as DiamondIcon, Gamepad2, Clock, Target,
  TrendingUp, DollarSign, ArrowUpDown, Send, MoreVertical,
  Camera, Edit, Share, Save, MapPin, Calendar, Wallet, Mail, Phone, Search, Book, Minimize2, Maximize2,
  Bell, Check, X, ArrowLeft, ArrowRight, UserPlus, Ban, Shield, Lock, AlertCircle, Calculator, ChevronRight, ChevronLeft, HelpCircle,
  UserCheck, UserMinus, Share2, Loader2, Paperclip, Trash2, Pause, Music, Volume2, VolumeX, Menu
} from 'lucide-react';
import { API_BASE_URL } from './config';

const BACKEND_URL = API_BASE_URL;
const API = `${BACKEND_URL}/api`;
const LIVEKIT_URL = process.env.REACT_APP_LIVEKIT_URL;

// ==================== LINK v2 EVENT DEDUPE ====================
// Multiple WS handlers (App-level universal sync + LivePage-level socket)
// receive the same broadcaster_joined_session / link_started / invite_response
// payload and each dispatches a CustomEvent. Downstream listeners were
// firing TWICE per real event, which triggered a second /livekit/token
// fetch and a second setLinkRoomInfo — remounting LiveKitLinkGrid and
// killing the first connection within ~1s. Dedupe at dispatch time,
// keyed on stable IDs from the payload, with a 5s window.
if (typeof window !== 'undefined') {
  window.__gemsLinkEventKeys = window.__gemsLinkEventKeys || new Map();
}
const dispatchLinkEventOnce = (eventName, detail, keyParts = []) => {
  try {
    const store = (typeof window !== 'undefined' && window.__gemsLinkEventKeys) || new Map();
    const filtered = (keyParts || []).filter(Boolean);
    if (filtered.length > 0) {
      const key = `${eventName}:${filtered.join(':')}`;
      const now = Date.now();
      const last = store.get(key);
      if (last && now - last < 5000) {
        // Duplicate of an event we already dispatched — swallow silently.
        return;
      }
      store.set(key, now);
      if (store.size > 200) {
        const oldestKey = store.keys().next().value;
        store.delete(oldestKey);
      }
    }
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  } catch (_e) {
    try { window.dispatchEvent(new CustomEvent(eventName, { detail })); } catch (__) { /* noop */ }
  }
};

// ==================== PRIVATE BETA MODE ====================
// Check if Private Beta Mode is enabled (web only)
const PRIVATE_BETA_MODE = process.env.REACT_APP_PRIVATE_BETA_MODE === 'true';

// Beta Mode Restriction Modal Component
const BetaRestrictionModal = ({ isOpen, onClose, featureName }) => {
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
            <AlertCircle className="w-6 h-6 text-amber-400" />
            Feature Temporarily Disabled
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-gray-300 mb-4">
            <span className="font-semibold text-amber-400">{featureName}</span> is temporarily disabled during Private Beta.
          </p>
          <p className="text-gray-400 text-sm">
            This feature will be enabled as we expand access.
          </p>
        </div>
        <div className="flex justify-end">
          <Button 
            onClick={onClose}
            className="bg-emerald-600 hover:bg-emerald-500"
          >
            Understood
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
// ==================== END PRIVATE BETA MODE ====================

// Custom Emerald Gem Icon Component
const EmeraldIcon = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="emeraldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#34D399" />
        <stop offset="50%" stopColor="#10B981" />
        <stop offset="100%" stopColor="#059669" />
      </linearGradient>
      <linearGradient id="emeraldShine" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#6EE7B7" stopOpacity="0.8" />
        <stop offset="100%" stopColor="#34D399" stopOpacity="0" />
      </linearGradient>
    </defs>
    {/* Main gem shape */}
    <path d="M12 2L4 8L12 22L20 8L12 2Z" fill="url(#emeraldGrad)" stroke="#047857" strokeWidth="0.5"/>
    {/* Top facet */}
    <path d="M12 2L4 8H20L12 2Z" fill="#6EE7B7" fillOpacity="0.6"/>
    {/* Left facet */}
    <path d="M4 8L12 12L12 22L4 8Z" fill="#10B981"/>
    {/* Right facet */}
    <path d="M20 8L12 12L12 22L20 8Z" fill="#059669"/>
    {/* Center line */}
    <path d="M12 2L12 12M4 8L12 12L20 8" stroke="#047857" strokeWidth="0.3" strokeOpacity="0.5"/>
    {/* Shine effect */}
    <path d="M8 6L10 8L8 10" fill="url(#emeraldShine)"/>
  </svg>
);

// Custom Ruby Gem Icon Component  
const RubyIcon = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="rubyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F87171" />
        <stop offset="50%" stopColor="#EF4444" />
        <stop offset="100%" stopColor="#DC2626" />
      </linearGradient>
      <linearGradient id="rubyShine" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FCA5A5" stopOpacity="0.8" />
        <stop offset="100%" stopColor="#F87171" stopOpacity="0" />
      </linearGradient>
    </defs>
    {/* Main gem shape */}
    <path d="M12 2L4 8L12 22L20 8L12 2Z" fill="url(#rubyGrad)" stroke="#B91C1C" strokeWidth="0.5"/>
    {/* Top facet */}
    <path d="M12 2L4 8H20L12 2Z" fill="#FCA5A5" fillOpacity="0.6"/>
    {/* Left facet */}
    <path d="M4 8L12 12L12 22L4 8Z" fill="#EF4444"/>
    {/* Right facet */}
    <path d="M20 8L12 12L12 22L20 8Z" fill="#DC2626"/>
    {/* Center line */}
    <path d="M12 2L12 12M4 8L12 12L20 8" stroke="#B91C1C" strokeWidth="0.3" strokeOpacity="0.5"/>
    {/* Shine effect */}
    <path d="M8 6L10 8L8 10" fill="url(#rubyShine)"/>
  </svg>
);

// Custom Rose Quartz Gem Icon — the "like" currency gem (matches Emerald/Ruby)
const RoseQuartzIcon = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="roseQuartzGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F9A8D4" />
        <stop offset="50%" stopColor="#EC6FA6" />
        <stop offset="100%" stopColor="#D6467F" />
      </linearGradient>
      <linearGradient id="roseQuartzShine" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FBCFE2" stopOpacity="0.85" />
        <stop offset="100%" stopColor="#F9A8D4" stopOpacity="0" />
      </linearGradient>
    </defs>
    <path d="M12 2L4 8L12 22L20 8L12 2Z" fill="url(#roseQuartzGrad)" stroke="#B03A6B" strokeWidth="0.5"/>
    <path d="M12 2L4 8H20L12 2Z" fill="#FBCFE2" fillOpacity="0.6"/>
    <path d="M4 8L12 12L12 22L4 8Z" fill="#EC6FA6"/>
    <path d="M20 8L12 12L12 22L20 8Z" fill="#D6467F"/>
    <path d="M12 2L12 12M4 8L12 12L20 8" stroke="#B03A6B" strokeWidth="0.3" strokeOpacity="0.5"/>
    <path d="M8 6L10 8L8 10" fill="url(#roseQuartzShine)"/>
  </svg>
);

// Sapphire Badge Icon - Celebrity Verified (Blue gem stone)
const SapphireBadgeIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sapphireGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#60A5FA" />
        <stop offset="50%" stopColor="#3B82F6" />
        <stop offset="100%" stopColor="#1D4ED8" />
      </linearGradient>
    </defs>
    {/* Gem shape */}
    <path d="M12 2L5 7L12 22L19 7L12 2Z" fill="url(#sapphireGrad)" stroke="#1E40AF" strokeWidth="0.5"/>
    {/* Top facet */}
    <path d="M12 2L5 7H19L12 2Z" fill="#93C5FD" fillOpacity="0.7"/>
    {/* Left facet */}
    <path d="M5 7L12 11L12 22L5 7Z" fill="#3B82F6"/>
    {/* Right facet */}
    <path d="M19 7L12 11L12 22L19 7Z" fill="#1D4ED8"/>
    {/* Shine */}
    <path d="M9 5L11 7L9 9" fill="#BFDBFE" fillOpacity="0.8"/>
  </svg>
);

// Canary Badge Icon - Official GEMS Live Verified (Yellow/Gold gem stone)
const CanaryBadgeIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="canaryGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FDE047" />
        <stop offset="50%" stopColor="#FACC15" />
        <stop offset="100%" stopColor="#EAB308" />
      </linearGradient>
    </defs>
    {/* Gem shape */}
    <path d="M12 2L5 7L12 22L19 7L12 2Z" fill="url(#canaryGrad)" stroke="#A16207" strokeWidth="0.5"/>
    {/* Top facet */}
    <path d="M12 2L5 7H19L12 2Z" fill="#FEF08A" fillOpacity="0.7"/>
    {/* Left facet */}
    <path d="M5 7L12 11L12 22L5 7Z" fill="#FACC15"/>
    {/* Right facet */}
    <path d="M19 7L12 11L12 22L19 7Z" fill="#EAB308"/>
    {/* Shine */}
    <path d="M9 5L11 7L9 9" fill="#FEF9C3" fillOpacity="0.8"/>
    {/* Checkmark overlay */}
    <path d="M8 12L11 15L16 9" stroke="#713F12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// API helpers
const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    console.log(`🔑 API Request to ${config.url} - Token: ${token ? 'present (' + token.substring(0,8) + '...)' : 'MISSING'}`);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Reset the one-shot "signed out elsewhere" guard on any successful
    // authed response — a new session has clearly been established, so
    // a future 401 should re-fire the alert exactly once.
    try {
      if (response?.config?.headers?.Authorization && window.__authSignedOutFired) {
        window.__authSignedOutFired = false;
      }
    } catch (_e) { /* noop */ }
    // Phase 3 (Infra resilience): Cloudflare-528 / origin-timeout pages come
    // back with HTTP 200 (or 5xx via Axios) but a `text/html` body. Pre-fix,
    // axios.then() would resolve and callers would JSON-parse the HTML as
    // "state data" → silent UI corruption. Detect non-JSON `/api/*` responses
    // and convert them into a hard, uniform error so callers hit their
    // catch() paths instead.
    try {
      const ct = String(response?.headers?.['content-type'] || '').toLowerCase();
      const url = String(response?.config?.url || '');
      const isApiCall = url.startsWith('/') ? true : url.includes('/api/');
      const expectsJson = isApiCall && !ct.includes('application/json') && !ct.includes('application/problem+json');
      const looksLikeHtml = typeof response?.data === 'string' && /<\s*html/i.test(response.data);
      if (expectsJson && (ct.includes('text/html') || looksLikeHtml)) {
        const err = new Error('Upstream returned non-JSON (likely Cloudflare 528 / origin timeout)');
        err.code = 'NON_JSON_UPSTREAM';
        err.response = response;
        console.warn('[InfraResilience] Non-JSON /api response — converting to error:', { url, ct });
        return Promise.reject(err);
      }
    } catch (e) {
      console.warn('[InfraResilience] response interceptor inspection failed (ignored):', e?.message);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Server rejected our token (expired / revoked / signed-in elsewhere).
      // Clear the token and — if we HAD one — surface a global event so the
      // AuthProvider can drop user state + show a one-shot alert.
      // Guarded so repeated 401s don't spam alerts or hit a state loop.
      let hadToken = false;
      try {
        hadToken = !!localStorage.getItem('authToken');
        localStorage.removeItem('authToken');
      } catch (_e) { /* noop */ }
      if (hadToken && !window.__authSignedOutFired) {
        window.__authSignedOutFired = true;
        try {
          window.dispatchEvent(new CustomEvent('auth:signed-out-elsewhere'));
        } catch (_e) { /* noop */ }
      }
    } else if (error.response?.status === 403 && error.response?.data?.code === 'BETA_ACCESS_REQUIRED') {
      // Non-beta user trying to access protected endpoint
      window.location.href = '/beta-access';
      return new Promise(() => {}); // Prevent further processing
    } else if (error.response?.status === 429) {
      console.warn('Rate limit exceeded - backing off API requests');
      // Don't retry on 429 errors
    } else if (error.response?.status === 528 || error.response?.status === 502 || error.response?.status === 504) {
      // Cloudflare origin-timeout family. Tag the error with a uniform
      // code so callers can render a recoverable message instead of
      // mistaking the HTML body for JSON state.
      error.code = error.code || 'UPSTREAM_TIMEOUT';
      console.warn('[InfraResilience] Upstream timeout', error.response.status, 'for', error.config?.url);
    }

    // Only log non-429 errors to avoid spam
    if (error.response?.status !== 429) {
      console.error('API Error:', error.response?.data || error.message);
    }

    return Promise.reject(error);
  }
);

// Link v2: kick off the feature-flag fetch now that `api` is fully defined.
ensureFeatureFlags();

// Round 13 (App Store 1.2): a suspended/banned account is refused by the
// server on every call (403 account_suspended) — surface the banned screen
// at once instead of waiting for the next app load.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const d = error?.response?.data?.detail;
      if (error?.response?.status === 403 && d && typeof d === 'object' && d.code === 'account_suspended') {
        window.dispatchEvent(new CustomEvent('gems:account-suspended', { detail: d }));
      }
    } catch (_e) { /* noop */ }
    return Promise.reject(error);
  }
);

const AuthContext = React.createContext();

// Round 13: report reasons (mirrors the server list)
const REPORT_REASONS = [
  ['harassment', 'Harassment or bullying'],
  ['hate', 'Hate speech or symbols'],
  ['sexual', 'Sexual or explicit content'],
  ['violence', 'Threats or violence'],
  ['spam', 'Spam or scam'],
  ['underage', 'Someone under 18'],
  ['impersonation', 'Impersonation'],
  ['self_harm', 'Self-harm'],
  ['other', 'Something else'],
];

// Round 13: the report dialog — one for the whole app, opened through
// AuthContext.openReport({ targetType, targetId, targetUserId, targetName, snapshot, streamId }).
const ReportDialog = ({ target, onClose }) => {
  const [reason, setReason] = useState('harassment');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post('/reports', {
        target_type: target.targetType, target_id: target.targetId || '', target_user_id: target.targetUserId || '',
        reason, details, snapshot: target.snapshot || '', stream_id: target.streamId || null,
      });
      toast.success(r.data?.status === 'already_reported' ? 'You already reported this — thanks, our team has it.' : 'Thanks — our team will review this within 24 hours.');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the report');
    } finally { setBusy(false); }
  };
  const what = { user: 'profile', post: 'post', comment: 'comment', chat_message: 'chat message', stream: 'live', dm: 'message' }[target.targetType] || 'content';
  return (
    <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="report-dialog">
      <div className="w-full max-w-md rounded-2xl border p-5" style={{ backgroundColor: 'var(--gems-bg-1)', borderColor: 'var(--gems-emerald)', color: 'var(--gems-text)' }} onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold">Report this {what}</h3>
        {target.targetName && <p className="text-sm mt-1" style={{ color: 'var(--gems-text-dim)' }}>by {target.targetName}</p>}
        {target.snapshot && <p className="text-sm mt-2 p-2 rounded border" style={{ borderColor: 'var(--card-border)', color: 'var(--gems-text-dim)' }}>“{String(target.snapshot).slice(0, 200)}”</p>}
        <div className="mt-3 space-y-1">
          {REPORT_REASONS.map(([id, label]) => (
            <label key={id} className="flex items-center gap-2 text-sm cursor-pointer py-1">
              <input type="radio" name="report-reason" checked={reason === id} onChange={() => setReason(id)} className="accent-emerald-500" data-testid={`report-reason-${id}`} />
              {label}
            </label>
          ))}
        </div>
        <textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Anything else we should know? (optional)" className="mt-3 w-full rounded-lg p-2 text-sm border bg-transparent" style={{ borderColor: 'var(--card-border)', color: 'var(--gems-text)' }} rows={2} />
        <div className="flex gap-2 mt-4">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={submit} disabled={busy} data-testid="report-submit">{busy ? 'Sending…' : 'Send report'}</Button>
        </div>
      </div>
    </div>
  );
};

// Round 13: the ⋯ menu on someone else's post / comment / chat message /
// live: Report, Block. Hidden on your own content.
const ModerationMenu = ({ targetType, targetId, targetUserId, targetName, snapshot, streamId, size = 'sm', label }) => {
  const ctx = React.useContext(AuthContext) || {};
  const [open, setOpen] = useState(false);
  if (!ctx.user || !targetUserId || targetUserId === ctx.user.id) return null;
  const btnCls = size === 'sm' ? 'text-xs px-1.5 py-0.5' : 'text-sm px-2 py-1';
  return (
    <span className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`rounded border ${btnCls}`}
        style={{ color: 'var(--gems-text-dim)', borderColor: 'var(--card-border)' }}
        onClick={() => setOpen((o) => !o)}
        title="Report or block"
        data-testid={`mod-menu-${targetType}-${targetId || targetUserId}`}
      >
        {label || '⋯'}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[150]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-[151] rounded-lg border shadow-xl min-w-[170px] py-1 text-sm" style={{ backgroundColor: 'var(--gems-bg-1)', borderColor: 'var(--card-border)', color: 'var(--gems-text)' }}>
            <button className="w-full text-left px-3 py-2 hover:bg-white/5" data-testid="mod-report-btn" onClick={() => { setOpen(false); ctx.openReport?.({ targetType, targetId, targetUserId, targetName, snapshot, streamId }); }}>🚩 Report</button>
            <button className="w-full text-left px-3 py-2 hover:bg-white/5 text-red-400" data-testid="mod-block-btn" onClick={async () => {
              setOpen(false);
              if (!window.confirm(`Block ${targetName || 'this user'}? You won't see each other's posts, chat, lives or messages.`)) return;
              await ctx.blockUser?.(targetUserId, targetName);
            }}>⛔ Block {targetName ? targetName.split(' ')[0] : 'user'}</button>
          </div>
        </>
      )}
    </span>
  );
};

// WebRTC Configuration
const rtcConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' }
  ]
};

// ============================================================
// MATCH-RESULT SCREENSHOT — dispute evidence uploader
// Called from the game-over overlays of Spades, 4-in-a-Row, and
// Crazy Eights on LIVE matches only. Captures the passed DOM node
// with html2canvas, converts to base64 JPEG, and POSTs to
// /api/game/{game_id}/result-screenshot. Fire-and-forget: it never
// throws or blocks the game-end flow.
// ============================================================
const captureAndUploadGameResult = async ({
  gameId, gameType, selector,
  participants, winnerId = null, resultSummary = '', wager = 0,
  isPractice = false,
}) => {
  if (isPractice) return;
  if (!gameId || !gameType || !selector) return;
  try {
    await new Promise((r) => setTimeout(r, 350));
    const node = (typeof selector === 'string' ? document.querySelector(selector) : selector) || document.body;
    const canvas = await html2canvas(node, {
      backgroundColor: '#0F1420',
      logging: false,
      useCORS: true,
      scale: Math.min(1, 1600 / Math.max(node.clientWidth || 1600, 1)),
    });
    const dataUrl = canvas.toDataURL('image/jpeg', 0.72);
    await api.post(`/game/${gameId}/result-screenshot`, {
      image_base64: dataUrl,
      game_type: gameType,
      participants: (participants || []).map((p) => ({
        user_id: p.user_id || p.id,
        name: p.name,
        handle: p.handle || null,
      })),
      winner_id: winnerId,
      result_summary: resultSummary,
      wager: Number(wager) || 0,
    });
  } catch (e) {
    console.warn('[matchRecord] capture/upload failed', e);
  }
};



// WebRTC Hook
const useWebRTC = () => {
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [peerConnection, setPeerConnection] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  const startStream = async () => {
    try {
      console.log('🎥 Starting camera stream...');
      
      const constraints = { 
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 }
        }, 
        audio: true 
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('✅ Camera stream obtained:', stream);
      
      setLocalStream(stream);
      
      // Set video element source immediately
      setTimeout(() => {
        if (localVideoRef.current && stream) {
          console.log('📹 Setting video element source');
          localVideoRef.current.srcObject = stream;
          localVideoRef.current.play().catch(console.error);
        }
      }, 100);
      
      const pc = new RTCPeerConnection(rtcConfiguration);
      
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
        console.log('🎵 Added track:', track.kind);
      });
      
      pc.ontrack = (event) => {
        console.log('📡 Remote track received');
        const [remoteStream] = event.streams;
        setRemoteStream(remoteStream);
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = remoteStream;
        }
      };
      
      pc.onconnectionstatechange = () => {
        setIsConnected(pc.connectionState === 'connected');
      };
      
      setPeerConnection(pc);
      setIsStreaming(true);
      
      toast.success('📹 Camera started successfully!');
      return stream;
    } catch (error) {
      console.error('❌ Camera error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Camera permission denied. Please allow camera access and try again.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera found. Please connect a camera and try again.');
      } else {
        toast.error('Failed to start camera: ' + error.message);
      }
      return null;
    }
  };

  const stopStream = () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }
    
    if (peerConnection) {
      peerConnection.close();
      setPeerConnection(null);
    }
    
    setIsStreaming(false);
    setIsConnected(false);
    
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    // Auto-attach stream when it changes
    if (localStream && localVideoRef.current) {
      console.log('🔄 Attaching stream to video element');
      localVideoRef.current.srcObject = localStream;
      localVideoRef.current.play().then(() => {
        console.log('✅ Video playing successfully');
      }).catch(error => {
        console.error('❌ Video play failed:', error);
      });
    }
  }, [localStream]);

  return {
    localStream,
    remoteStream,
    isStreaming,
    isConnected,
    localVideoRef,
    remoteVideoRef,
    startStream,
    stopStream
  };
};

// New Simple Splash Screen Component
const SimpleSplashScreen = () => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');

  // Simple effect for progress bar
  React.useEffect(() => {
    const timer = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) return 100;
        return prev + 4;
      });
    }, 80);

    const messageTimer1 = setTimeout(() => setLoadingMessage('Preparing your game...'), 1000);
    const messageTimer2 = setTimeout(() => setLoadingMessage('Almost ready...'), 2000);

    return () => {
      clearInterval(timer);
      clearTimeout(messageTimer1);
      clearTimeout(messageTimer2);
    };
  }, []);

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#000000', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      fontFamily: 'Arial, sans-serif'
    }}>
      {/* GEMS Logo */}
      <div style={{ marginBottom: '40px' }}>
        <img 
          src={BRAND_ASSETS.logo}
          alt="GEMS Logo"
          style={{ 
            width: '280px', 
            height: '280px', 
            objectFit: 'contain'
          }}
        />
      </div>

      {/* Tagline */}
      <div style={{ marginBottom: '60px' }}>
        <p style={{ 
          color: '#ffffff', 
          fontSize: '24px', 
          fontWeight: '300',
          letterSpacing: '6px',
          margin: 0,
          textAlign: 'center'
        }}>
          GAME • ENGAGE • MONETIZE • STREAM
        </p>
      </div>

      {/* Loading Section */}
      <div style={{ width: '400px', textAlign: 'center' }}>
        {/* Progress Bar */}
        <div style={{ 
          width: '100%', 
          height: '8px', 
          backgroundColor: '#333333', 
          borderRadius: '4px',
          marginBottom: '20px',
          overflow: 'hidden'
        }}>
          <div style={{ 
            height: '100%', 
            backgroundColor: '#ffffff', 
            borderRadius: '4px',
            width: `${loadingProgress}%`,
            transition: 'width 0.3s ease'
          }} />
        </div>
        
        {/* Loading Message */}
        <p style={{ 
          color: '#ffffff', 
          fontSize: '18px',
          margin: '0 0 15px 0' 
        }}>
          {loadingMessage}
        </p>

        {/* Percentage */}
        <p style={{ 
          color: '#888888', 
          fontSize: '16px',
          fontFamily: 'monospace',
          margin: 0
        }}>
          {Math.round(loadingProgress)}%
        </p>
      </div>

      {/* Round 44: no release-stage pill on the loading screen */}
    </div>
  );
};

// ==================== INVITATION POP-UP COMPONENT ====================
const InvitationPopup = ({ invitation, onAccept, onDecline }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timer, setTimer] = useState(45);

  useEffect(() => {
    if (invitation) {
      setIsVisible(true);
      setTimer(45);

      // Countdown timer
      const countdown = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(countdown);
            handleDecline();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Auto-dismiss after 45 seconds
      const timeout = setTimeout(() => {
        handleDecline();
      }, 45000);

      return () => {
        clearInterval(countdown);
        clearTimeout(timeout);
      };
    }
  }, [invitation]);

  const handleAccept = () => {
    setIsVisible(false);
    setTimeout(() => onAccept(invitation), 300);
  };

  const handleDecline = () => {
    setIsVisible(false);
    setTimeout(() => onDecline(invitation), 300);
  };

  if (!invitation) return null;

  return (
    <div
      className={`fixed top-20 right-4 z-[100] transition-all duration-500 transform ${
        isVisible ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'
      }`}
      style={{
        animation: isVisible ? 'slideInBounce 0.6s ease-out, pulse 2s infinite' : 'none',
      }}
    >
      <div className="relative bg-gradient-to-br from-black via-gray-900 to-black border-4 border-transparent rounded-2xl shadow-2xl overflow-hidden max-w-md">
        {/* Animated Border - GOLD theme */}
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 via-red-500 to-emerald-500 animate-gradient-rotate opacity-80 blur-sm"></div>
        
        {/* Content */}
        <div className="relative bg-gradient-to-br from-black via-gray-900 to-black p-6 m-1 rounded-xl">
          {/* Sparkle effects */}
          <div className="absolute top-2 right-2 animate-ping">
            <Star className="h-4 w-4 text-yellow-400" />
          </div>
          <div className="absolute top-4 right-8 animate-pulse">
            <Star className="h-3 w-3 text-red-500" />
          </div>
          <div className="absolute top-6 right-4 animate-bounce">
            <Zap className="h-3 w-3 text-emerald-400" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-red-500 rounded-full flex items-center justify-center animate-pulse">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-red-500 to-emerald-500 animate-gradient">
                  Game Invitation!
                </h3>
                <p className="text-xs text-gray-400">Auto-declines in {timer}s</p>
              </div>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-yellow-500 rounded-full flex items-center justify-center font-bold text-white text-lg animate-pulse">
              {timer}
            </div>
          </div>

          {/* Inviter Info */}
          <div className="bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xl">
                {invitation.inviter_name?.charAt(0) || 'G'}
              </div>
              <div className="flex-1">
                <p className="text-white font-bold text-lg">{invitation.inviter_name || 'Guest'}</p>
                <p className="text-gray-400 text-sm">@{invitation.inviter_handle || 'player'}</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-700">
              <p className="text-gray-300 text-sm">
                invited you to join their stream:
              </p>
              <p className="text-emerald-400 font-semibold mt-1">
                {invitation.stream_title || 'Live Spades Game'}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleAccept}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold py-3 rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              <Check className="h-5 w-5 mr-2" />
              Accept & Go Live
            </Button>
            <Button
              onClick={handleDecline}
              className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-3 rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              <X className="h-5 w-5 mr-2" />
              Decline
            </Button>
          </div>

          {/* Pulse effect overlay */}
          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-yellow-400/10 via-red-500/10 to-emerald-500/10 animate-pulse pointer-events-none"></div>
        </div>
      </div>
    </div>
  );
};

// Profile Page Wrapper to pass props
const ProfilePageWrapper = () => {
  const { user } = React.useContext(AuthContext);
  return <ProfilePageComponent user={user} NavBar={NavBar} AuthContext={AuthContext} />;
};
// Public/visitor profile — renders the SAME ProfilePage component in
// read-only mode (viewHandle set), so what a user builds/edits on their
// own page is exactly what everyone else sees when they open it.
const PublicProfileWrapper = () => {
  const { user } = React.useContext(AuthContext);
  const { handle } = useParams();
  return <ProfilePageComponent user={user} viewHandle={handle} NavBar={NavBar} AuthContext={AuthContext} />;
};

// Notifications Page Wrapper to pass props
const NotificationsPageWrapper = () => {
  const { user } = React.useContext(AuthContext);
  // Bell clears when the user opens the notifications page (Feb 20, 2026).
  React.useEffect(() => { api.post('/notifications/mark-all-read').catch(() => {}); }, []);
  return <NotificationsPage user={user} NavBar={NavBar} />;
};

// ==================== SETTINGS PAGE ====================
const SettingsPage = () => {
  const { user, setUser, logout } = React.useContext(AuthContext);
  const location = useLocation();
  const isOnLivePage = location.pathname === '/live';
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('account');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [blockedUsers, setBlockedUsers] = useState([]);
  // Round 12: self-service account deletion (App Store / Google Play requirement)
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') return;
    if (!window.confirm('Schedule your account for deletion? It is hidden right away and permanently removed in 30 days. Sign back in before then to cancel.')) return;
    setSaving(true);
    try {
      await api.post('/users/me/delete-request', {});
      toast.success('Your account is scheduled for deletion in 30 days. Sign back in before then to cancel.');
      const me = await api.get('/auth/me').catch(() => null);
      if (me?.data) setUser(me.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not schedule deletion');
    } finally { setSaving(false); }
  };
  
  // Form states
  const [profileForm, setProfileForm] = useState({
    name: '',
    handle: '',
    bio: '',
    location: ''
  });
  const [emailForm, setEmailForm] = useState({
    newEmail: '',
    password: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [privacySettings, setPrivacySettings] = useState({
    show_profile_public: true,
    show_earnings_public: false,
    allow_messages_from: 'everyone',
    show_online_status: true
  });
  const [notificationSettings, setNotificationSettings] = useState({
    push_notifications: true,
    email_notifications: true,
    stream_start_alerts: true,
    gift_notifications: true,
    follower_notifications: true,
    game_invites: true,
    // Round 23: Current Events — hide cards / opt-in push per type (all off)
    hide_news: false,
    news_push_sports: false,
    news_push_entertainment: false,
    news_push_drama: false,
    news_push_scores: false
  });

  useEffect(() => {
    fetchSettings();
    fetchBlockedUsers();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/user/settings');
      setSettings(response.data);
      setProfileForm({
        name: response.data.profile.name || '',
        handle: response.data.profile.handle || '',
        bio: response.data.profile.bio || '',
        location: response.data.profile.location || ''
      });
      setPrivacySettings(response.data.privacy);
      setNotificationSettings(response.data.notifications);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedUsers = async () => {
    try {
      const response = await api.get('/user/blocked');
      setBlockedUsers(response.data.blocked_users || []);
    } catch (error) {
      console.error('Error fetching blocked users:', error);
    }
  };

  const handleProfileUpdate = async () => {
    setSaving(true);
    try {
      const response = await api.put('/user/profile', profileForm);
      toast.success('Profile updated successfully');
      if (response.data.user) {
        setUser(response.data.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleEmailUpdate = async () => {
    if (!emailForm.newEmail || !emailForm.password) {
      toast.error('Please fill in all fields');
      return;
    }
    setSaving(true);
    try {
      await api.put('/user/email', {
        new_email: emailForm.newEmail,
        password: emailForm.password
      });
      toast.success('Email updated successfully');
      setEmailForm({ newEmail: '', password: '' });
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    try {
      await api.put('/user/password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      toast.success('Password updated successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const handlePrivacyUpdate = async () => {
    setSaving(true);
    try {
      await api.put('/user/privacy', privacySettings);
      toast.success('Privacy settings updated');
    } catch (error) {
      toast.error('Failed to update privacy settings');
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationUpdate = async () => {
    setSaving(true);
    try {
      await api.put('/user/notifications', notificationSettings);
      toast.success('Notification settings updated');
    } catch (error) {
      toast.error('Failed to update notification settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUnblock = async (userId) => {
    try {
      await api.delete(`/user/block/${userId}`);
      toast.success('User unblocked');
      fetchBlockedUsers();
    } catch (error) {
      toast.error('Failed to unblock user');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white">
        <NavBar />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'account', label: 'Account', icon: <Shield className="h-4 w-4" /> },
    { id: 'privacy', label: 'Privacy', icon: <Lock className="h-4 w-4" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="h-4 w-4" /> },
    { id: 'blocked', label: 'Blocked Users', icon: <Ban className="h-4 w-4" /> },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white">
      <NavBar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Manage your account settings and preferences</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-emerald-100 overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-200 bg-gray-50">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'text-emerald-600 border-b-2 border-emerald-500 bg-white'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Account Tab */}
            {activeTab === 'account' && (
              <div className="space-y-8">
                {/* Linked Accounts */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Linked Accounts</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium">Google</p>
                          <p className="text-sm text-gray-500">
                            {settings?.linked_accounts?.google ? 'Connected' : 'Not connected'}
                          </p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        settings?.linked_accounts?.google 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {settings?.linked_accounts?.google ? 'Linked' : 'Not Linked'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Mail className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">Email</p>
                          <p className="text-sm text-gray-500">{settings?.profile?.email || 'Not set'}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        settings?.linked_accounts?.email 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {settings?.linked_accounts?.email ? 'Linked' : 'Not Linked'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Change Email */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Change Email</h3>
                  <div className="space-y-4 max-w-md">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New Email</label>
                      <Input
                        type="email"
                        value={emailForm.newEmail}
                        onChange={(e) => setEmailForm({ ...emailForm, newEmail: e.target.value })}
                        placeholder="newemail@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                      <Input
                        type="password"
                        value={emailForm.password}
                        onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                        placeholder="Enter your password"
                      />
                    </div>
                    <Button onClick={handleEmailUpdate} disabled={saving} variant="outline">
                      Update Email
                    </Button>
                  </div>
                </div>

                {/* Change Password */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Change Password</h3>
                  <div className="space-y-4 max-w-md">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                      <Input
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                        placeholder="Enter current password"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                      <Input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                        placeholder="Enter new password (min 8 characters)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                      <Input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                        placeholder="Confirm new password"
                      />
                    </div>
                    <Button onClick={handlePasswordUpdate} disabled={saving} variant="outline">
                      Update Password
                    </Button>
                  </div>
                </div>

                {/* Round 12: Delete account (30-day grace; hidden immediately) */}
                <div className="rounded-xl p-6 border" style={{ borderColor: 'rgba(227,65,62,0.5)' }} data-testid="delete-account-card">
                  <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--gems-red)' }}>Delete Account</h3>
                  <p className="text-sm mb-4" style={{ color: 'var(--gems-text-dim)' }}>
                    Your profile is hidden right away and everything is permanently removed after 30 days. Sign back in before then to cancel.
                  </p>
                  <input
                    type="text"
                    value={deleteConfirm}
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    placeholder="Type DELETE to confirm"
                    className="w-full rounded-lg px-3 py-2 mb-3 border bg-transparent"
                    style={{ borderColor: 'rgba(227,65,62,0.5)', color: 'var(--gems-text)' }}
                    data-testid="delete-account-input"
                  />
                  <Button onClick={handleDeleteAccount} disabled={deleteConfirm !== 'DELETE' || saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-account-btn">
                    Delete my account
                  </Button>
                </div>
              </div>
            )}

            {/* Privacy Tab */}
            {activeTab === 'privacy' && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Public Profile</p>
                      <p className="text-sm text-gray-500">Allow others to see your profile</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={privacySettings.show_profile_public}
                        onChange={(e) => setPrivacySettings({ ...privacySettings, show_profile_public: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Show Earnings</p>
                      <p className="text-sm text-gray-500">Display your earnings on your profile</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={privacySettings.show_earnings_public}
                        onChange={(e) => setPrivacySettings({ ...privacySettings, show_earnings_public: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Show Online Status</p>
                      <p className="text-sm text-gray-500">Let others see when you're online</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={privacySettings.show_online_status}
                        onChange={(e) => setPrivacySettings({ ...privacySettings, show_online_status: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="font-medium mb-2">Who can message you</p>
                    <select
                      value={privacySettings.allow_messages_from}
                      onChange={(e) => setPrivacySettings({ ...privacySettings, allow_messages_from: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="everyone">Everyone</option>
                      <option value="followers">Followers Only</option>
                      <option value="none">No One</option>
                    </select>
                  </div>
                </div>

                <Button onClick={handlePrivacyUpdate} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600">
                  {saving ? 'Saving...' : 'Save Privacy Settings'}
                </Button>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div className="space-y-4">
                  {[
                    { key: 'push_notifications', label: 'Push Notifications', desc: 'Receive real push notifications on your device' },
                    { key: 'email_notifications', label: 'Email Notifications', desc: 'Receive email updates' },
                    { key: 'stream_start_alerts', label: 'Live Notifications', desc: 'Get notified when people you follow go live' },
                    { key: 'gift_notifications', label: 'Gift Notifications', desc: 'Get notified when you receive gifts' },
                    { key: 'follower_notifications', label: 'New Followers', desc: 'Get notified when someone follows you' },
                    { key: 'game_invites', label: 'Game Invites', desc: 'Get notified about game invitations' },
                    // Round 23: Current Events (Table Talk) — every one of these is OFF until you turn it on
                    { key: 'news_push_sports', label: 'Current Events: Sports', desc: 'Push alerts for sports headlines from Table Talk' },
                    { key: 'news_push_entertainment', label: 'Current Events: Entertainment', desc: 'Push alerts for entertainment headlines' },
                    { key: 'news_push_drama', label: 'Current Events: Drama & Reality TV', desc: 'Push alerts for drama and reality-TV headlines' },
                    { key: 'news_push_scores', label: 'Current Events: Scores', desc: 'Push alerts for final scores and daily roundups' },
                    { key: 'hide_news', label: 'Hide Current Events', desc: 'Remove Table Talk news and score cards from your feed' },
                  ].map((setting) => (
                    <div key={setting.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{setting.label}</p>
                        <p className="text-sm text-gray-500">{setting.desc}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings[setting.key]}
                          onChange={(e) => setNotificationSettings({ ...notificationSettings, [setting.key]: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                      </label>
                    </div>
                  ))}
                </div>

                <Button onClick={handleNotificationUpdate} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600">
                  {saving ? 'Saving...' : 'Save Notification Settings'}
                </Button>
              </div>
            )}

            {/* Blocked Users Tab */}
            {activeTab === 'blocked' && (
              <div className="space-y-4">
                {blockedUsers.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Ban className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>You haven't blocked anyone</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {blockedUsers.map((blockedUser) => (
                      <div key={blockedUser.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={blockedUser.profilePhoto} />
                            <AvatarFallback>{blockedUser.name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{blockedUser.name}</p>
                            <p className="text-sm text-gray-500">@{blockedUser.handle || blockedUser.id?.slice(0, 8)}</p>
                          </div>
                        </div>
                        <Button 
                          onClick={() => handleUnblock(blockedUser.id)}
                          variant="outline"
                          size="sm"
                        >
                          Unblock
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* --------- Log Out (labeled, destructive) --------- */}
      <div className="max-w-4xl mx-auto mt-8 px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-rose-100 p-5">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <h3 className="text-base font-semibold text-gray-900">Log Out</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                End your session on this device. You&apos;ll need to sign in again to come back.
              </p>
            </div>
            <Button
              variant="destructive"
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="settings-logout-btn"
              onClick={async () => {
                if (isOnLivePage) {
                  toast.info('End your stream before logging out');
                  return;
                }
                await logout?.();
                navigate('/');
              }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Round 12 (Sep 11 2026): "Get the app" — one server-controlled link.
// /get-app reads /api/config/app-links and sends iPhones to the iOS link
// (TestFlight today, the App Store later), Android phones to the Play /
// APK link, and shows both badges on a desktop. Change the targets on the
// server; nothing here or in the apps needs a rebuild.
// ---------------------------------------------------------------------------
// Round 22: every store-link tap is counted (Admin → KPI → App Downloads).
// Fire-and-forget; never delays or blocks the navigation.
const trackStoreClick = (storeType, page) => {
  try {
    api.post('/app-store/track-click', { store_type: storeType, page: page || 'landing', referrer: document.referrer }).catch(() => {});
  } catch (e) { /* noop */ }
};

const GetAppPage = () => {
  const [links, setLinks] = useState(null);
  useEffect(() => {
    api.get('/config/app-links').then((r) => setLinks(r.data || {})).catch(() => setLinks({}));
  }, []);
  useEffect(() => {
    if (!links) return;
    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    if (isIOS && links.ios) { trackStoreClick('apple_store', 'get-app-redirect'); window.location.replace(links.ios); }
    else if (isAndroid && links.android) { trackStoreClick('google_play', 'get-app-redirect'); window.location.replace(links.android); }
  }, [links]);
  const btn = (label, href, sub) => (
    href ? (
      <a href={href} onClick={() => trackStoreClick(/iphone/i.test(label) ? 'apple_store' : 'google_play', 'get-app')} className="block w-full rounded-xl py-4 text-center font-bold text-lg" style={{ backgroundColor: 'var(--gems-emerald)', color: '#FFFFFF' }} data-testid={`get-app-${label.toLowerCase().replace(/\s+/g, '-')}`}>{label}</a>
    ) : (
      <div className="block w-full rounded-xl py-4 text-center font-bold text-lg border" style={{ borderColor: 'var(--gems-emerald)', color: 'var(--gems-text-dim)' }}>{label} · {sub || 'coming soon'}</div>
    )
  );
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: 'var(--gems-bg-1)', color: 'var(--gems-text)' }} data-testid="get-app-page">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-3xl font-bold">Get the GEMS app</h1>
        <p style={{ color: 'var(--gems-text-dim)' }}>Watch, chat, gift and play face-to-face with creators during their lives.</p>
        {!links ? <p style={{ color: 'var(--gems-text-dim)' }}>Loading…</p> : (
          <>
            {btn('Download for iPhone', links.ios, 'App Store listing coming soon')}
            {btn('Download for Android', links.android, 'Google Play listing coming soon')}
          </>
        )}
        <a href="/" className="block text-sm underline" style={{ color: 'var(--gems-text-dim)' }}>Continue on the web</a>
      </div>
    </div>
  );
};

// Round 12: public account-deletion page (Google Play's Data safety form
// needs a web link; Apple wants the path to be obvious).
const DeleteAccountInfoPage = ({ user }) => (
  <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: 'var(--gems-bg-1)', color: 'var(--gems-text)' }} data-testid="delete-account-page">
    <div className="max-w-md w-full space-y-4">
      <h1 className="text-2xl font-bold">Delete your GEMS account</h1>
      <p style={{ color: 'var(--gems-text-dim)' }}>You can delete your account yourself, in the app or on the web:</p>
      <ol className="list-decimal pl-5 space-y-2" style={{ color: 'var(--gems-text-dim)' }}>
        <li>Open <strong>Settings → Account</strong> (in the app: Me tab → gear icon → Account Management).</li>
        <li>Tap <strong>Delete my account</strong> and type DELETE to confirm.</li>
        <li>Your profile is hidden immediately. Everything — profile, posts, messages, follows, photos — is permanently removed after 30 days. Wallet purchase records are kept for accounting as required by law.</li>
        <li>Changed your mind? Sign back in within 30 days and tap <strong>Keep my account</strong>.</li>
      </ol>
      <a href={user ? '/settings' : '/'} className="block w-full rounded-xl py-3 text-center font-bold" style={{ backgroundColor: 'var(--gems-emerald)', color: '#FFFFFF' }}>{user ? 'Go to Settings' : 'Sign in to continue'}</a>
      <p className="text-xs" style={{ color: 'var(--gems-text-dim)' }}>Questions? support@gemslive.tv</p>
    </div>
  </div>
);

// Round 12: the whole app is replaced by this while an account is scheduled
// for deletion — cancel and everything is back, or sign out.
const AccountDeletionGateWeb = ({ user, setUser, onLogout }) => {
  const [busy, setBusy] = useState(false);
  const when = user?.deletion_scheduled_for ? new Date(user.deletion_scheduled_for) : null;
  const whenText = when && !isNaN(when.getTime()) ? when.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : 'in 30 days';
  const cancel = async () => {
    setBusy(true);
    try {
      await api.post('/users/me/delete-cancel');
      const me = await api.get('/auth/me');
      setUser(me.data);
      toast.success('Welcome back — your account is staying.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel. Try again.');
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: 'var(--gems-bg-1)', color: 'var(--gems-text)' }} data-testid="account-deletion-gate">
      <div className="max-w-md w-full text-center space-y-4 rounded-2xl p-8 border" style={{ borderColor: 'var(--gems-emerald)' }}>
        <h1 className="text-2xl font-bold">Your account is scheduled for deletion</h1>
        <p style={{ color: 'var(--gems-text-dim)' }}>Everything will be permanently removed on {whenText}. Until then your profile is hidden and you can't go live, post or play. Change your mind? Cancel below and you're back instantly.</p>
        <Button onClick={cancel} disabled={busy} className="w-full py-4 text-lg font-bold" style={{ backgroundColor: 'var(--gems-emerald)', color: '#FFFFFF' }} data-testid="deletion-cancel-btn">Keep my account</Button>
        <Button onClick={onLogout} variant="outline" disabled={busy} className="w-full" data-testid="deletion-signout-btn">Sign out</Button>
      </div>
    </div>
  );
};

function App({ basename = '' }) {
  const [user, setUser] = useState(null);
  // Round 12: iOS Smart App Banner — Safari shows "Open in the GEMS app" /
  // "View in the App Store" on every page once the app has a store ID.
  useEffect(() => {
    api.get('/config/app-links').then((r) => {
      const id = r?.data?.ios_app_id;
      if (id && !document.querySelector('meta[name="apple-itunes-app"]')) {
        const m = document.createElement('meta');
        m.name = 'apple-itunes-app';
        m.content = `app-id=${id}, app-argument=${window.location.href}`;
        document.head.appendChild(m);
      }
    }).catch(() => {});
  }, []);
  // Bug 5: keep module-level mirror in sync with the authenticated user.
  // Every Agora user-published handler reads __GEMS_CURRENT_USER_ID via
  // uidFromUserId() to skip self-echo events.
  useEffect(() => { __GEMS_CURRENT_USER_ID = user?.id || null; }, [user?.id]);

  // Onboarding (Feb 19, 2026): Suggested Friends after signup. Web signup
  // does not auto-login, so we set a localStorage flag at signup and
  // consume it on the first authenticated load. Also HOLDS the existing
  // first-post nudge while the modal is open so the two never stack.
  useEffect(() => {
    if (!user?.id) return;
    let pending = false;
    try { pending = localStorage.getItem('gems_suggested_pending') === '1'; } catch {}
    if (!pending) return;
    try { localStorage.removeItem('gems_suggested_pending'); } catch {}
    (async () => {
      try {
        const res = await api.get('/users/suggested-friends?limit=12');
        const list = res.data?.suggested || [];
        if (list.length === 0) return;
        const init = {};
        list.forEach((u) => { init[u.id] = true; });
        setSuggestedFriends(list);
        setSuggestedChecked(init);
        setShowFirstPostNudge(false);
        setShowSuggestedFriends(true);
      } catch (e) { console.warn('[SuggestedFriends] fetch failed', e?.message); }
    })();
  }, [user?.id]);

  const confirmSuggestedFriends = async () => {
    const ids = Object.keys(suggestedChecked).filter((id) => suggestedChecked[id]);
    setSuggestedBusy(true);
    try {
      if (ids.length > 0) {
        const res = await api.post('/users/follow-batch', { user_ids: ids });
        toast.success(`You're now following ${res.data?.followed ?? ids.length} creators 🎉`);
      }
    } catch {
      toast.error('Could not follow everyone — you can follow from Discover anytime');
    }
    setSuggestedBusy(false);
    setShowSuggestedFriends(false);
    setShowFirstPostNudge(true);
  };
  const skipSuggestedFriends = () => {
    setShowSuggestedFriends(false);
    setShowFirstPostNudge(true);
  };

  const [showingSplash, setShowingSplash] = useState(true);
  const [authAttempted, setAuthAttempted] = useState(false);
  const [authComplete, setAuthComplete] = useState(false);
  const [pendingInvitation, setPendingInvitation] = useState(null);
  const [banInfo, setBanInfo] = useState(null);
  const [isBanned, setIsBanned] = useState(false);
  // Round 13: people you blocked + people who blocked you — hidden everywhere
  const [hiddenIds, setHiddenIds] = useState(() => new Set());
  const [reportTarget, setReportTarget] = useState(null);
  const loadHidden = async () => {
    try {
      const r = await api.get('/user/blocked');
      setHiddenIds(new Set(r.data?.hidden_ids || r.data?.blocked_ids || []));
    } catch (_e) { /* noop */ }
  };
  const blockUser = async (userId, name) => {
    try {
      const r = await api.post(`/user/block/${userId}`);
      setHiddenIds(new Set(r.data?.hidden_ids || [...hiddenIds, userId]));
      toast.success(`${name || 'User'} blocked. You won't see each other on GEMS.`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not block');
      return false;
    }
  };
  const unblockUser = async (userId) => {
    try {
      const r = await api.delete(`/user/block/${userId}`);
      setHiddenIds(new Set(r.data?.hidden_ids || [...hiddenIds].filter((x) => x !== userId)));
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not unblock');
      return false;
    }
  };
  const openReport = (target) => setReportTarget(target);
  useEffect(() => {
    if (user?.id) loadHidden(); else setHiddenIds(new Set());
  }, [user?.id]);
  useEffect(() => {
    const h = () => { checkBanStatus(); };
    window.addEventListener('gems:account-suspended', h);
    return () => window.removeEventListener('gems:account-suspended', h);
  }, []);

  // Check if user is banned
  const checkBanStatus = async () => {
    try {
      const response = await api.get('/user/ban-status');
      if (response.data.is_banned) {
        setIsBanned(true);
        setBanInfo(response.data);
        return true;
      }
      setIsBanned(false);
      setBanInfo(null);
      return false;
    } catch (error) {
      console.error('Error checking ban status:', error);
      return false;
    }
  };

  useEffect(() => {
    // Prevent multiple auth attempts that cause 429 errors
    if (authAttempted) return;
    
    const initAuth = async () => {
      try {
        setAuthAttempted(true);
        
        const hash = window.location.hash;
        // Round 39: our Google sign-in reports a problem the same way it reports success — in the hash
        if (hash.includes('error=') && !hash.includes('session_id=')) {
          const code = hash.split('error=')[1].split('&')[0];
          toast.error(code === 'cancelled' ? 'Google sign-in was cancelled.'
            : code === 'not_configured' ? 'Google sign-in is not available right now — use email instead.'
            : 'Google sign-in did not finish. Please try again.');
          window.history.replaceState(null, null, window.location.pathname);
        }
        if (hash.includes('session_id=')) {
          const sessionId = hash.split('session_id=')[1].split('&')[0];
          
          try {
            const response = await api.post('/auth/session', { session_id: sessionId });
            // Store session token for Bearer auth (critical for cross-domain/incognito)
            if (response.data.session_token) {
              localStorage.setItem('authToken', response.data.session_token);
            }
            setUser(response.data.user);
            window.history.replaceState(null, null, window.location.pathname);
            
            // Check ban status after login
            const banned = await checkBanStatus();
            if (!banned) {
              toast.success(`Welcome to GEMS, ${response.data.user.name}!`);
              // Register push notifications (non-blocking)
              if (isPushSupported()) {
                registerPushNotifications(api).catch(() => {});
              }
              // Post-login: redirect back to stream if user came from shared link
              try {
                const returnStream = JSON.parse(localStorage.getItem('gems_return_stream') || 'null');
                if (returnStream && returnStream.streamId && (Date.now() - returnStream.timestamp) < 3600000) {
                  localStorage.removeItem('gems_return_stream');
                  window.location.href = `/app/watch/${returnStream.streamId}`;
                  return;
                }
              } catch {}
            }
          } catch (error) {
            console.error('Session auth failed:', error);
            // Auth resilience: only surface an error for a real 401. Do NOT
            // clear the stored token on 5xx/network — a server blip is not a
            // logout.
            if (error.response?.status === 401) {
              toast.error('Authentication failed');
            }
          }
        } else {
          // Auth resilience: only a 401 means "logged out". 5xx / network /
          // timeout gets retried (2 more times, 2s apart) and never clears the
          // stored token or drops the session.
          let meResp = null;
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              meResp = await api.get('/auth/me');
              break;
            } catch (error) {
              const status = error.response?.status;
              if (status === 401) { meResp = null; break; }
              if (status === 429) { console.warn('Rate limited - will not retry auth'); meResp = null; break; }
              // 5xx / network / timeout — retry, do NOT log out.
              if (attempt < 2) {
                await new Promise((r) => setTimeout(r, 2000));
                continue;
              }
              console.warn('auth/me failed after retries — keeping token, not logging out');
              meResp = null;
            }
          }
          if (meResp) {
            const response = meResp;
            // Store session token for Bearer auth (re-populate on page refresh)
            if (response.data.session_token) {
              localStorage.setItem('authToken', response.data.session_token);
            }
            setUser(response.data);
            
            // Check ban status for existing session
            await checkBanStatus();

            // Register push notifications on session restore (non-blocking)
            if (isPushSupported()) {
              registerPushNotifications(api).catch(() => {});
            }

            // Post-login: redirect back to stream if user came from shared link
            try {
              const returnStream = JSON.parse(localStorage.getItem('gems_return_stream') || 'null');
              if (returnStream && returnStream.streamId && (Date.now() - returnStream.timestamp) < 3600000) {
                localStorage.removeItem('gems_return_stream');
                window.location.href = `/app/watch/${returnStream.streamId}`;
                return;
              }
            } catch {}
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setAuthComplete(true);
      }
    };

    // Always hide splash after 3.5 seconds regardless of auth status
    const splashTimer = setTimeout(() => {
      setShowingSplash(false);
    }, 3500);

    // Only attempt auth once
    if (!authAttempted) {
      initAuth();
    }

    return () => {
      clearTimeout(splashTimer);
    };
  }, []); // Remove authAttempted from dependencies to prevent loops
  
  // ========================================================
  // UNIVERSAL SYNC WEBSOCKET CONNECTION
  // Connects ALL authenticated users for real-time updates
  // ========================================================
  const [appSyncWs, setAppSyncWs] = useState(null);
  const [appSyncConnected, setAppSyncConnected] = useState(false);
  
  useEffect(() => {
    // Only connect when user is authenticated
    if (!user?.id) {
      console.log('🌐 Universal Sync: No user ID, skipping connection');
      return;
    }
    
    // Don't reconnect if already connected
    if (appSyncWs && appSyncWs.readyState === WebSocket.OPEN) {
      console.log('🌐 Universal Sync: Already connected');
      return;
    }
    
    console.log('🌐 Universal Sync: Connecting for user', user.id);
    
    const backendUrl = API_BASE_URL || '';
    const baseUrl = backendUrl.replace('/api', '');
    const wsUrl = baseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    const fullWsUrl = `${wsUrl}/api/ws/sync?user_id=${user.id}`;
    
    console.log('🌐 WebSocket URL:', fullWsUrl);
    
    try {
      const socket = new WebSocket(fullWsUrl);
      
      socket.onopen = () => {
        console.log('✅ Universal Sync WebSocket CONNECTED!');
        setAppSyncConnected(true);
        setAppSyncWs(socket);
        
        // Ping every 30 seconds to keep alive
        socket.pingInterval = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };
      
      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('🌐 Sync message:', data.type);
          
          // Handle different message types
          if (data.type === 'wallet_update' && data.data?.user_id === user.id) {
            setUser(prev => ({
              ...prev,
              emeralds: data.data.emeralds ?? prev?.emeralds,
              rubies: data.data.rubies ?? prev?.rubies
            }));
          } else if (data.type === 'live_invite') {
            // Bug 3 (May 7 testing): dedupe by invite_id (NOT sender id — a
            // sender can legitimately send multiple distinct invites). The
            // same invite_id can arrive on both /api/ws/sync and the polling
            // fallback in useLiveInvites; we only want one popup per id.
            const inviteId = data?.data?.invite_id;
            if (inviteId) {
              if (!window.__gemsConsumedInvites) window.__gemsConsumedInvites = new Set();
              if (window.__gemsConsumedInvites.has(inviteId)) {
                console.log('[Bug3] Suppressed duplicate live_invite for invite_id', inviteId);
                return;
              }
              window.__gemsConsumedInvites.add(inviteId);
            }
            window.dispatchEvent(new CustomEvent('sync:live_invite', { detail: data.data }));
          } else if (data.type === 'leaderboard_update') {
            window.dispatchEvent(new CustomEvent('sync:leaderboard_update', { detail: data.data }));
          } else if (data.type === 'leaderboard_reset') {
            window.dispatchEvent(new CustomEvent('sync:leaderboard_reset', { detail: data.data }));
          } else if (data.type === 'game_started') {
            window.dispatchEvent(new CustomEvent('sync:game_started', { detail: data.data }));
          } else if (data.type === 'linked_game_challenge') {
            // App-level sync WS also dispatches linked-game challenge
            // events — LivePage's addEventListener modal handler will
            // fire regardless of which sync WS the message landed on.
            window.dispatchEvent(new CustomEvent('linked-game-challenge', { detail: data.data }));
          } else if (data.type === 'linked_game_accepted') {
            dispatchLinkEventOnce(
              'linked-game-accepted',
              data.data,
              [data.data?.game_id, data.data?.accepted_by]
            );
          } else if (data.type === 'linked_game_declined') {
            window.dispatchEvent(new CustomEvent('linked-game-declined', { detail: data.data }));
          } else if (data.type === 'linked_game_update') {
            window.dispatchEvent(new CustomEvent('linked-game-update', { detail: data.data }));
          } else if (data.type === 'linked_game_invite') {
            window.dispatchEvent(new CustomEvent('linked-game-invite', { detail: data.data }));
          } else if (data.type === 'link_ended') {
            window.dispatchEvent(new CustomEvent('partner-ended-link', {
              detail: {
                endedById: data.data?.ended_by_id,
                endedByName: data.data?.ended_by_name,
              },
            }));
          } else if (data.type === 'broadcaster_joined_session') {
            dispatchLinkEventOnce(
              'broadcaster-joined-session',
              data.data,
              [data.data?.linked_session_id, data.data?.new_broadcaster_id]
            );
          } else if (data.type === 'broadcaster_left_session') {
            dispatchLinkEventOnce(
              'broadcaster-left-session',
              data.data,
              [data.data?.linked_session_id, data.data?.left_by_id]
            );
          } else if (data.type === 'stream_invite') {
            window.dispatchEvent(new CustomEvent('sync:stream_invite', { detail: data.data }));
          }
        } catch (e) {
          console.error('Failed to parse sync message:', e);
        }
      };
      
      socket.onerror = (error) => {
        console.error('❌ Universal Sync error:', error);
      };
      
      socket.onclose = (event) => {
        console.log('🌐 Universal Sync disconnected:', event.code, event.reason);
        setAppSyncConnected(false);
        setAppSyncWs(null);
        if (socket.pingInterval) clearInterval(socket.pingInterval);
        
        // Auto-reconnect after 5 seconds if not clean close
        if (!event.wasClean && user?.id) {
          setTimeout(() => {
            console.log('🔄 Attempting reconnect...');
          }, 5000);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
    
    return () => {
      if (appSyncWs) {
        appSyncWs.close();
        setAppSyncWs(null);
      }
    };
  }, [user?.id]);

  // ========================================================
  // BETA CODE REDEMPTION FLOW
  // Check for pending beta code from /beta-access redirect
  // ========================================================
  const [showSignupFromBeta, setShowSignupFromBeta] = useState(false);
  const [pendingBetaCode, setPendingBetaCode] = useState(null);
  
  useEffect(() => {
    // Check URL params for signup/login trigger from beta-access page
    const urlParams = new URLSearchParams(window.location.search);
    const signupParam = urlParams.get('signup');
    const loginParam = urlParams.get('login');
    const betaCodeParam = urlParams.get('beta_code');
    
    // Also check localStorage for pending code
    const storedCode = localStorage.getItem('gems_pending_beta_code');
    
    if (betaCodeParam || storedCode) {
      setPendingBetaCode(betaCodeParam || storedCode);
    }
    
    // If signup=true, trigger the signup modal (will be handled by LandingPage)
    // This works for both /?signup=true and /app?signup=true
    if (signupParam === 'true') {
      setShowSignupFromBeta(true);
      // Clean up URL
      window.history.replaceState(null, null, window.location.pathname);
    }
    
    // If login=true, trigger the login modal
    if (urlParams.get('login') === 'true') {
      // This will be handled by LandingPage
      window.history.replaceState(null, null, window.location.pathname);
    }
  }, []);

  // After successful signup/login, redeem pending beta code
  useEffect(() => {
    const redeemPendingBetaCode = async () => {
      if (!user || !pendingBetaCode) return;
      
      // Don't redeem if user already has beta access
      if (user.beta_access) {
        localStorage.removeItem('gems_pending_beta_code');
        localStorage.removeItem('gems_referred_by');
        localStorage.removeItem('gems_signup_source');
        setPendingBetaCode(null);
        return;
      }
      
      // Redeem the code
      try {
        const token = localStorage.getItem('authToken');
        const referredBy = localStorage.getItem('gems_referred_by');
        const signupSource = localStorage.getItem('gems_signup_source');
        
        const response = await api.post('/beta/validate-code', {
          code: pendingBetaCode,
          ...(referredBy && { referred_by_user_id: referredBy }),
          ...(signupSource && { signup_source: signupSource })
        });
        
        if (response.data.success) {
          toast.success('Beta access granted! Welcome to GEMS!');
          // Update user state with beta access
          setUser(prev => prev ? { ...prev, beta_access: true } : prev);
        }
      } catch (error) {
        console.error('Failed to redeem beta code:', error);
        // Don't show error - code might already be used or invalid
      } finally {
        // Clean up
        localStorage.removeItem('gems_pending_beta_code');
        localStorage.removeItem('gems_referred_by');
        localStorage.removeItem('gems_signup_source');
        setPendingBetaCode(null);
      }
    };
    
    redeemPendingBetaCode();
  }, [user, pendingBetaCode]);

  const logout = async () => {
    try {
      await api.post('/auth/logout');
      setUser(null);
      setIsBanned(false);
      setBanInfo(null);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleAppealSubmitted = (appealData) => {
    setBanInfo(prev => ({
      ...prev,
      has_pending_appeal: true,
      appeal_status: 'pending'
    }));
    toast.success('Your appeal has been submitted successfully');
  };

  // Signed-out-elsewhere: the axios 401 interceptor fires
  // `auth:signed-out-elsewhere` when a valid-looking token was rejected
  // (revoked / expired / new device claimed the session). Drop React state
  // and surface a one-shot toast. The window-level `__authSignedOutFired`
  // flag in the interceptor already prevents duplicate dispatches per session.
  useEffect(() => {
    const onSignedOutElsewhere = () => {
      setUser(null);
      setIsBanned(false);
      setBanInfo(null);
      try {
        toast.error('You were signed out because your account signed in on another device.', {
          duration: 8000,
        });
      } catch (_e) { /* noop */ }
    };
    window.addEventListener('auth:signed-out-elsewhere', onSignedOutElsewhere);
    return () => window.removeEventListener('auth:signed-out-elsewhere', onSignedOutElsewhere);
  }, []);

  // Poll for new invitations (only when user is authenticated)
  useEffect(() => {
    if (!user) return;

    const checkInvitations = async () => {
      try {
        const response = await api.get('/invitations/pending');
        const invitations = response.data.invitations || [];
        
        // Show popup only if there's a new invitation and no popup is currently showing
        if (invitations.length > 0 && !pendingInvitation) {
          setPendingInvitation(invitations[0]);
        }
      } catch (error) {
        console.error('Error checking invitations:', error);
      }
    };

    checkInvitations();
    // POLL WIDENED (Feb 2026): WebSocket ('live_invite' / Universal Sync)
    // is the primary channel — this REST poll is a sparse reconciliation
    // fallback for the case where the WS silently drops. Bumped 5s → 15s
    // to eliminate hot-timer re-render pressure on the Live view.
    const interval = setInterval(checkInvitations, 15000);

    return () => clearInterval(interval);
  }, [user, pendingInvitation]);

  // Instant stream-invite delivery (Aug 18, 2026): both sync sockets dispatch
  // 'sync:stream_invite'; this is the ONLY place the popup state is set.
  // Dedupe by invitation_id — both sockets receive the same event.
  useEffect(() => {
    const handleStreamInvite = (event) => {
      const p = event.detail || {};
      const invId = p.invitation_id;
      if (!invId) return;
      if (!window.__gemsConsumedStreamInvites) window.__gemsConsumedStreamInvites = new Set();
      if (window.__gemsConsumedStreamInvites.has(invId)) return;
      window.__gemsConsumedStreamInvites.add(invId);
      const inv = {
        id: invId,
        invitation_id: invId,
        stream_id: p.stream_id,
        stream_title: p.stream_title,
        inviter_name: p.inviter_name,
        inviter_handle: p.inviter_handle,
        inviter_gem_id: p.inviter_gem_id,
        expires_at: p.expires_at
      };
      // AUTO-REJOIN (Feb 2026): the backend sends this when a broadcaster
      // who just dropped out of a linked session goes live again within
      // the grace window — skip the popup and put them straight back in.
      if (p.auto_rejoin) {
        console.log('🔁 Auto-rejoin invite — reconnecting to the linked live', p);
        toast.info('Reconnecting you to your linked live…', { duration: 4000 });
        handleAcceptInvitation(inv);
        if (p.game_id) {
          // Game still active — rejoin the table too (seat was kept).
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('spades-rejoin-game', {
              detail: { game_id: p.game_id, game_type: p.game_type },
            }));
          }, 2500);
        }
        return;
      }
      setPendingInvitation(inv);
      toast.success(`${p.inviter_name || p.inviter_handle} invited you to play!`, { duration: 5000 });
    };
    window.addEventListener('sync:stream_invite', handleStreamInvite);
    return () => window.removeEventListener('sync:stream_invite', handleStreamInvite);
  }, []);

  const handleAcceptInvitation = async (invitation) => {
    console.log('📥 Accepting stream invitation:', invitation);
    
    try {
      // Accept the invitation via API
      const response = await api.post(`/invitations/accept/${invitation.id}`);
      console.log('✅ Invitation accept response:', response.data);
      
      toast.success('Invitation accepted! Connecting to linked live...');
      setPendingInvitation(null);
      
      // Check if we're on LivePage and already broadcasting
      // If so, use linked-live approach instead of navigation
      const isOnLivePage = window.location.pathname.includes('/live');
      
      if (isOnLivePage) {
        // Already on live page - the LivePage component will handle the linking
        // via joinStreamId in location state or directly
        console.log('🔗 Already on live page - initiating linked live');
        // Force a state update to trigger the linked-live flow
        window.dispatchEvent(new CustomEvent('link-live-accepted', { 
          detail: { 
            streamId: invitation.stream_id,
            inviterId: invitation.inviter_user_id,
            inviterName: invitation.inviter_name || invitation.inviter_handle
          } 
        }));
      } else {
        // Not on live page - navigate to /live with join state
        navigate('/live', { state: { joinStreamId: invitation.stream_id } });
      }
    } catch (error) {
      console.error('❌ Failed to accept invitation:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error('Failed to accept invitation');
      setPendingInvitation(null);
    }
  };
  
  // ========================================================
  // ACCEPT LIVE INVITE WHILE ALREADY BROADCASTING
  // Joins the linked session directly without page navigation
  // Both users appear as equal hosts in side-by-side view
  // ========================================================
  const handleAcceptLiveInviteWhileLive = async (invite) => {
    await ensureFeatureFlags(); // Link v2: resolve the runtime flag before deciding transport
    console.log('🔗 Accepting live invite while already broadcasting:', invite);
    toast.info('Connecting to linked live...', { duration: 3000 });
    
    try {
      // 1. Respond to the invite via API
      const token = localStorage.getItem('authToken');
      await api.post(`/notifications/live-invites/${invite.id}/respond`, null, {
        params: { action: 'accept' }
      });
      
      console.log('✅ Invite acceptance confirmed');
      
      const streamId = invite.stream_id;

      // Link v2: route the already-live accept through the LiveKit broadcaster
      // path. handleLinkLiveAccepted (LivePage) listens for this event and
      // publishes into the shared LiveKit linked room instead of Agora.
      if (LIVEKIT_LINKS) {
        window.dispatchEvent(new CustomEvent('link-live-accepted', {
          detail: {
            streamId,
            inviterId: invite.sender_id,
            inviterName: invite.sender_name || invite.sender_handle,
          },
        }));
        return;
      }

      // 2. Get Agora token to join the inviter's channel
      const agoraResponse = await api.post('/agora/token', {
        channelName: `stream_${streamId}`,
        uid: uidFromUserId(user?.id),
        role: 'publisher',
        sessionType: 'linked_live'
      });
      
      const { token: agoraToken, channelName, appId } = agoraResponse.data;
      console.log('✅ Got Agora token for linked session:', { channelName });
      
      // Checkpoint 4: unified factory replaces 90 lines of duplicated
      // createClient + handler wiring + join + existing-user-walk.
      const AgoraRTC = (await import('agora-rtc-sdk-ng')).default;
      const myUid = uidFromUserId(user?.id);
      const client = await createAndJoinAgoraChannel({
        AgoraRTC,
        channelName,
        token: agoraToken,
        appId,
        uid: myUid,
        onUserLeftToast: `${invite.sender_name} has disconnected from the linked session`,
      });

      // Create and publish local tracks
      const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
        { echoCancellation: true, noiseSuppression: true },
        { encoderConfig: '720p_2', facingMode: 'user' }
      );
      
      await client.publish([audioTrack, videoTrack]);
      console.log('✅ Published local tracks to linked session');
      
      // Update state for side-by-side view
      setSharedAgoraTracks({ audioTrack, videoTrack });
      setAgoraToken(agoraToken);
      setAgoraChannel(channelName);
      setAgoraAppId(appId);
      setUseAgora(true);
      
      // Add inviter to invitedPlayers for 2-host layout
      setInvitedPlayers(prev => {
        if (prev.find(p => p.id === invite.sender_id)) return prev;
        return [...prev, {
          id: invite.sender_id,
          name: invite.sender_name,
          handle: invite.sender_handle || invite.sender_name,
          status: 'connected'
        }];
      });
      
      // Set shared chat to the inviter's stream
      setSharedChatStreamId(streamId);
      
      // Stop WebRTC camera since Agora now has it
      if (webrtc && webrtc.localStream) {
        webrtc.localStream.getTracks().forEach(track => track.stop());
        console.log('🛑 WebRTC stopped - Agora has camera for linked session');
      }
      
      toast.success(`Linked with ${invite.sender_name}! You're now streaming together.`);
      console.log('✅ Successfully joined linked live session');
      
    } catch (error) {
      console.error('❌ Failed to join linked session:', error);
      toast.error('Failed to connect to linked session. Please try again.');
    }
  };

  const handleDeclineInvitation = async (invitation) => {
    try {
      await api.post(`/invitations/decline/${invitation.id}`);
      toast.info('Invitation declined');
    } catch (error) {
      console.error('Failed to decline invitation');
    } finally {
      setPendingInvitation(null);
    }
  };

  // Show splash screen while loading (but not for admin routes or legal pages)
  const isAdminRoute = window.location.pathname.startsWith('/admin');
  const isLegalPage = window.location.pathname === '/terms' || 
                      window.location.pathname === '/privacy' || 
                      window.location.pathname === '/community-guidelines' ||
                      window.location.pathname === '/support';
  if (showingSplash && !isAdminRoute && !isLegalPage) {
    return <SimpleSplashScreen />;
  }

  // Beta access gate - redirect non-beta users AND unauthenticated users to /beta-access
  // Exception: /watch/ routes allow viewer mode for non-beta users
  // Exception: ?signup=true or ?login=true allows the auth flow to complete (from beta-access code validation)
  // Exception: localStorage has pending beta code (user came from beta-access flow)
  // Wait until both splash and auth are done before deciding
  const isStreamViewerRoute = window.location.pathname.match(/\/app\/watch\//);
  const betaGateUrlParams = new URLSearchParams(window.location.search);
  const isAuthFlowFromUrl = betaGateUrlParams.get('signup') === 'true' || betaGateUrlParams.get('login') === 'true';
  const hasPendingBetaCode = localStorage.getItem('gems_pending_beta_code');
  const isAuthFlow = isAuthFlowFromUrl || hasPendingBetaCode;
  
  if (!showingSplash && authComplete && !isStreamViewerRoute && !isAuthFlow) {
    if (!user || user.beta_access === false) {
      window.location.href = '/beta-access';
      return null;
    }
  }
  
  // If in auth flow but no user, show the landing page with signup/login modal
  if (!showingSplash && authComplete && isAuthFlow && !user) {
    // Don't redirect - let the LandingPage handle signup/login
  }

  // Show banned user screen if user is banned
  if (user && isBanned && banInfo) {
    return (
      <BannedUserScreen 
        banInfo={banInfo} 
        onLogout={logout}
        onAppealSubmitted={handleAppealSubmitted}
      />
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout, hiddenIds, blockUser, unblockUser, openReport }}>
      <BrowserRouter basename={basename}>
        <div className="App">
          <Toaster position="top-right" />
          {/* Round 13: one report dialog for every surface */}
          <ReportDialog target={reportTarget} onClose={() => setReportTarget(null)} />
          
          {/* Global Invitation Popup - Only visible to authenticated user */}
          {user && pendingInvitation && (
            <InvitationPopup
              invitation={pendingInvitation}
              onAccept={handleAcceptInvitation}
              onDecline={handleDeclineInvitation}
            />
          )}
          
          {user?.deletion_requested_at ? (
            <AccountDeletionGateWeb user={user} setUser={setUser} onLogout={logout} />
          ) : (
          <Routes>
            {/* Legal Pages */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
            <Route path="/support" element={<SupportPage />} />
            {/* Round 12: device-aware app download + public deletion page */}
            <Route path="/get-app" element={<GetAppPage />} />
            <Route path="/delete-account" element={<DeleteAccountInfoPage user={user} />} />
            
            <Route path="/" element={user ? <><GemsPlusOffer user={user} /><DiscoverPage /></> : <LandingPage showSignupFromBeta={showSignupFromBeta} pendingBetaCode={pendingBetaCode} />} />
            <Route path="/home" element={<LandingPage showSignupFromBeta={showSignupFromBeta} pendingBetaCode={pendingBetaCode} />} />
            <Route path="/live" element={user ? <LivePage /> : <Navigate to="/" />} />
            <Route path="/discover" element={user ? <DiscoverPage /> : <Navigate to="/" />} />
            <Route path="/games" element={user ? <GamesPage /> : <Navigate to="/" />} />
            <Route path="/wallet" element={user ? <WalletPage /> : <Navigate to="/" />} />
            <Route path="/messages" element={user ? <MessagesPage /> : <Navigate to="/" />} />
            <Route path="/leaderboards" element={user ? <LeaderboardPage /> : <Navigate to="/" />} />
            <Route path="/notifications" element={user ? <NotificationsPageWrapper /> : <Navigate to="/" />} />
            <Route path="/settings" element={user ? <SettingsPage /> : <Navigate to="/" />} />
            <Route path="/invitations" element={<Navigate to="/notifications" />} />
            <Route path="/profile" element={user ? <ProfilePageWrapper /> : <Navigate to="/" />} />
            <Route path="/profile/:handle" element={<PublicProfileWrapper />} />
            <Route path="/watch/:streamId" element={<WatchStreamPage />} />
            <Route path="/game/:gameId" element={user ? <GameRoomPage /> : <Navigate to="/" />} />
            <Route path="/four-in-a-row/:gameId" element={user ? <FourInARowGame user={user} /> : <Navigate to="/" />} />
            <Route path="/checkers/:gameId" element={user ? <CheckersMultiplayerGame user={user} /> : <Navigate to="/" />} />
            <Route path="/crazy-eights/practice" element={user ? <CrazyEightsPracticePage /> : <Navigate to="/" />} />
            <Route path="/checkers/practice" element={user ? <CheckersPracticePage api={api} user={user} setUser={setUser} /> : <Navigate to="/" />} />
            {/* Bare-handle shortcut: /app/{handle} → that user's public profile.
                MUST stay last so it only catches paths no specific route claims. */}
            <Route path="/:handle" element={<PublicProfileWrapper />} />
          </Routes>
          )}
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

// Support Page Component
const SupportPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-8"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Support Center</h1>
          <p className="text-gray-400 text-lg">We're here to help you with any questions or issues</p>
        </div>
        
        {/* Support Options */}
        <div className="space-y-6">
          {/* Email Support */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-white mb-2">Email Support</h3>
                <p className="text-gray-400 mb-4">Get help via email. We typically respond within 24 hours.</p>
                <a 
                  href="mailto:support@gemslive.tv"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  support@gemslive.tv
                </a>
              </div>
            </div>
          </div>
          
          {/* Submit Ticket */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-emerald-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-white mb-2">Submit a Support Ticket</h3>
                <p className="text-gray-400 mb-4">Log in to submit a detailed support ticket and track your request.</p>
                <button 
                  onClick={() => navigate('/')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
                >
                  <User className="w-4 h-4" />
                  Sign In to Submit Ticket
                </button>
              </div>
            </div>
          </div>
          
          {/* FAQ */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-white mb-2">Common Questions</h3>
                <ul className="text-gray-400 space-y-2">
                  <li>• <strong>How do I earn Rubies?</strong> Win games, receive gifts from viewers, and participate in tournaments.</li>
                  <li>• <strong>How do I withdraw earnings?</strong> Navigate to Wallet → Withdraw after meeting the minimum threshold.</li>
                  <li>• <strong>How do I go live?</strong> Click "Live" in the navigation, set up your stream, and click "Go Live Now".</li>
                  <li>• <strong>How do I report a user?</strong> Use the Help & Support ticket system or email us directly.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer Links */}
        <div className="mt-12 text-center">
          <div className="flex items-center justify-center gap-6 text-sm">
            <a href="/terms" className="text-emerald-400 hover:text-emerald-300">Terms of Service</a>
            <a href="/privacy" className="text-emerald-400 hover:text-emerald-300">Privacy Policy</a>
            <a href="/community-guidelines" className="text-emerald-400 hover:text-emerald-300">Community Guidelines</a>
          </div>
          <p className="text-gray-500 mt-4">© 2025 GEMS Live LLC. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

const LandingPage = ({ showSignupFromBeta, pendingBetaCode }) => {
  const { setUser } = React.useContext(AuthContext);
  
  // Check URL params on initial render for beta signup/login flow
  const urlParams = new URLSearchParams(window.location.search);
  const initialSignup = urlParams.get('signup') === 'true' || showSignupFromBeta;
  const initialLogin = urlParams.get('login') === 'true';
  
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(initialLogin);
  const [showEmailSignup, setShowEmailSignup] = useState(initialSignup);
  const [showPhoneLogin, setShowPhoneLogin] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [name, setName] = useState('');
  // Referral system (Feb 2026). `signupReferralCode` is optional at
  // signup; empty/invalid codes never block registration. Passed
  // through to /api/auth/email-signup along with a persistent
  // device_id read from localStorage `gems_visitor_id`.
  const [signupReferralCode, setSignupReferralCode] = useState('');
  // Marketing opt-in checkbox in the email-signup dialog. Default OFF —
  // users have to explicitly tick it to receive marketing.
  // Round 35: marketing email is part of the Terms every member accepts
  // (Section 12A) — the sign-up form states it plainly instead of a checkbox.
  const [signupMarketingOptIn] = useState(true);
  const [showFirstPostNudge, setShowFirstPostNudge] = useState(false);
  // Onboarding (Feb 19, 2026): Suggested Friends modal state — appears
  // once, right after the first authenticated load following a signup.
  const [showSuggestedFriends, setShowSuggestedFriends] = useState(false);
  const [suggestedFriends, setSuggestedFriends] = useState([]);
  const [suggestedChecked, setSuggestedChecked] = useState({});
  const [suggestedBusy, setSuggestedBusy] = useState(false);
  const [firstPostText, setFirstPostText] = useState('');
  const [firstPostPromptIdx] = useState(() => Math.floor(Math.random() * 8));
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  
  // Login method selector (email, google, phone) - matches mobile UI
  const [loginMethod, setLoginMethod] = useState('email');
  const [showPassword, setShowPassword] = useState(false);
  
  // Mobile App Prompt State
  const [showMobileAppPrompt, setShowMobileAppPrompt] = useState(false);
  const [mobileOS, setMobileOS] = useState(null); // 'ios' | 'android' | null
  // Round 12b: the download targets come from the server (Admin → App Store):
  // TestFlight / APK today, the store listings later — no redeploy needed.
  const [appLinks, setAppLinks] = useState(null);
  useEffect(() => {
    api.get('/config/app-links').then((r) => setAppLinks(r.data || {})).catch(() => setAppLinks({}));
  }, []);
  
  // App Store Readiness - Verification Modals State
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [showTermsAcceptance, setShowTermsAcceptance] = useState(false);
  const [showCommunityGuidelines, setShowCommunityGuidelines] = useState(false);
  const [ageVerificationData, setAgeVerificationData] = useState(null);
  const [signupStep, setSignupStep] = useState('form'); // 'form', 'age', 'terms', 'guidelines', 'complete'
  
  // Show toast for beta code if present, and clean up URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const signupParam = urlParams.get('signup');
    const loginParam = urlParams.get('login');
    const storedCode = localStorage.getItem('gems_pending_beta_code');
    
    if ((signupParam === 'true' || loginParam === 'true') && storedCode) {
      const action = signupParam === 'true' ? 'signup' : 'login';
      toast.info(`Your code ${storedCode} will be applied after ${action}!`);
    }
    
    // Clean up URL params
    if (signupParam || loginParam) {
      window.history.replaceState(null, null, window.location.pathname);
    }
  }, []); // Run once on mount
  
  // Detect mobile browser and show app prompt
  useEffect(() => {
    const detectMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      
      // Check if already dismissed in this session
      const dismissed = sessionStorage.getItem('mobileAppPromptDismissed');
      if (dismissed) return;
      
      // iOS detection
      if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        setMobileOS('ios');
        setShowMobileAppPrompt(true);
        return;
      }
      
      // Android detection
      if (/android/i.test(userAgent)) {
        setMobileOS('android');
        setShowMobileAppPrompt(true);
        return;
      }
    };
    
    detectMobile();
  }, []);
  
  const handleDownloadApp = () => {
    // Round 12b: server-controlled links; /get-app is the device-aware fallback.
    const link = mobileOS === 'ios' ? appLinks?.ios : mobileOS === 'android' ? appLinks?.android : null;
    if (link) trackStoreClick(mobileOS === 'ios' ? 'apple_store' : 'google_play', 'landing-modal');   // Round 22
    window.location.href = link || '/get-app';
  };
  
  const handleContinueInBrowser = () => {
    sessionStorage.setItem('mobileAppPromptDismissed', 'true');
    setShowMobileAppPrompt(false);
  };

  const handleGoogleLogin = () => {
    // Redirect back to /app/ after Google auth
    const redirectUrl = `${window.location.origin}/app/`;
    // Round 39: our own Google sign-in (server-side OAuth) — no third-party broker in the path
    window.location.href = `${API}/auth/google/start?redirect=${encodeURIComponent(redirectUrl)}&platform=web`;
  };

  const handleEmailLogin = async () => {
    try {
      const response = await api.post('/auth/email-login', {
        email: email,
        password: password
      });
      
      if (response.data.success) {
        // Store the session token for subsequent API calls
        if (response.data.session_token) {
          localStorage.setItem('authToken', response.data.session_token);
        }
        toast.success('Login successful!');
        setUser(response.data.user);
        setShowLoginModal(false);
        setShowEmailLogin(false);
        setEmail('');
        setPassword('');
      } else {
        toast.error('Login failed - unexpected response');
      }
    } catch (error) {
      console.error('[LOGIN] Error:', error?.message);
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Invalid email or password');
    }
  };

  // Initiate signup - First show age verification
  const handleInitiateSignup = () => {
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    
    // Start verification flow
    setShowEmailSignup(false);
    setShowAgeVerification(true);
    setSignupStep('age');
  };

  // Handle age verification completed
  const handleAgeVerified = (verificationData) => {
    setAgeVerificationData(verificationData);
    setShowAgeVerification(false);
    setShowTermsAcceptance(true);
    setSignupStep('terms');
  };

  // Handle terms acceptance
  const handleTermsAccepted = () => {
    setShowTermsAcceptance(false);
    setShowCommunityGuidelines(true);
    setSignupStep('guidelines');
  };

  // Handle community guidelines acceptance - Final step, create account
  const handleGuidelinesAccepted = async () => {
    setShowCommunityGuidelines(false);
    setSignupStep('complete');
    
    try {
      // Persistent device id for casual referral duplicate prevention.
      // Piggybacks on gems_visitor_id — created lazily elsewhere but we
      // also seed here to guarantee it exists at signup time.
      let deviceId = '';
      try {
        deviceId = localStorage.getItem('gems_visitor_id') || '';
        if (!deviceId) {
          deviceId = 'v-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now();
          localStorage.setItem('gems_visitor_id', deviceId);
        }
      } catch (_e) { /* localStorage disabled */ }

      const response = await api.post('/auth/email-signup', {
        email: email,
        password: password,
        name: name,
        age_verified: true,
        age_verification_date: ageVerificationData?.verifiedAt,
        birth_date: ageVerificationData?.birthDate,
        terms_accepted: true,
        community_guidelines_accepted: true,
        referral_code: (signupReferralCode || '').trim() || undefined,
        device_id: deviceId || undefined,
      });
      
      if (response.data.success) {
        toast.success('Account created successfully! Please sign in.');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setName('');
        setSignupReferralCode('');
        setAgeVerificationData(null);
        setSignupStep('form');
        // Onboarding (Feb 19, 2026): flag Suggested Friends to fire once
        // on the next authenticated load. Web signup doesn't auto-login,
        // so the flag survives localStorage until the first sign-in.
        try { localStorage.setItem('gems_suggested_pending', '1'); } catch {}
        // First-post nudge — front-and-center but skippable. Fires after
        // the confirmation flow so the account is guaranteed to exist.
        setShowFirstPostNudge(true);
        setShowEmailLogin(true);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Email already registered. Please sign in instead.');
      } else {
        toast.error('Registration failed. Please try again.');
      }
      setSignupStep('form');
      setShowEmailSignup(true);
    }
  };

  // Handle declining any verification step
  const handleVerificationDeclined = () => {
    setShowAgeVerification(false);
    setShowTermsAcceptance(false);
    setShowCommunityGuidelines(false);
    setSignupStep('form');
    setShowEmailSignup(true);
    toast.error('You must accept all terms to create an account.');
  };

  const handleEmailSignup = async () => {
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      let deviceId = '';
      try {
        deviceId = localStorage.getItem('gems_visitor_id') || '';
        if (!deviceId) {
          deviceId = 'v-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now();
          localStorage.setItem('gems_visitor_id', deviceId);
        }
      } catch (_e) { /* localStorage disabled */ }

      const response = await api.post('/auth/email-signup', {
        email: email,
        password: password,
        name: name,
        referral_code: (signupReferralCode || '').trim() || undefined,
        device_id: deviceId || undefined,
        marketing_opt_in: signupMarketingOptIn,
      });
      
      if (response.data.success) {
        toast.success('Account created successfully! Please sign in.');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setName('');
        setSignupReferralCode('');
        setShowEmailSignup(false);
        // Onboarding (Feb 19, 2026): flag Suggested Friends for the next
        // authenticated load.
        try { localStorage.setItem('gems_suggested_pending', '1'); } catch {}
        setShowFirstPostNudge(true);
        setShowEmailLogin(true);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Email already registered. Please sign in instead.');
      } else {
        toast.error('Registration failed. Please try again.');
      }
    }
  };

  const handleSendPhoneCode = async () => {
    const fullPhoneNumber = countryCode + phoneNumber.replace(/\D/g, '');
    
    if (!fullPhoneNumber || fullPhoneNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    try {
      toast.info('Sending verification code...');
      await sendPhoneVerificationCode(fullPhoneNumber);
      setIsVerifying(true);
      toast.success('Verification code sent!');
    } catch (error) {
      console.error('Phone auth error:', error);
      // Reset reCAPTCHA for retry
      clearRecaptcha();
      
      if (error.code === 'auth/invalid-phone-number') {
        toast.error('Invalid phone number format.');
      } else if (error.code === 'auth/too-many-requests') {
        toast.error('Too many attempts. Please try again later.');
      } else if (error.code === 'auth/captcha-check-failed') {
        toast.error('Security verification failed. Please try again.');
      } else if (error.code === 'auth/unauthorized-domain') {
        toast.error('This domain is not authorized for phone auth.');
      } else if (error.code === 'auth/billing-not-enabled') {
        toast.error('Phone authentication is temporarily unavailable.');
      } else {
        toast.error(error.message || 'Failed to send code. Please try again.');
      }
      // Re-init reCAPTCHA for retry
      initRecaptcha().catch(() => {});
    }
  };

  const handleVerifyPhone = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    try {
      // Step 1: Verify code with Firebase (gets real Firebase ID token)
      const result = await verifyPhoneCode(verificationCode);
      
      if (result.success) {
        // Step 2: Send Firebase token to our backend to create/login user
        const fullPhoneNumber = countryCode + phoneNumber.replace(/\D/g, '');
        const response = await api.post('/auth/firebase-phone-login', {
          idToken: result.idToken,
          phoneNumber: fullPhoneNumber
        });
        
        if (response.data.success) {
          // Store session token for Bearer auth
          if (response.data.session_token) {
            localStorage.setItem('authToken', response.data.session_token);
          }
          toast.success('Phone verification successful!');
          clearRecaptcha();
          window.confirmationResult = null;
          setUser(response.data.user);
          setShowPhoneLogin(false);
          setShowLoginModal(false);
          setIsVerifying(false);
          setVerificationCode('');
          setPhoneNumber('');
        }
      }
    } catch (error) {
      console.error('Verification error:', error);
      if (error.code === 'auth/invalid-verification-code') {
        toast.error('Invalid verification code. Please check and try again.');
      } else if (error.code === 'auth/code-expired') {
        toast.error('Code expired. Please request a new code.');
        setIsVerifying(false);
        clearRecaptcha();
        window.confirmationResult = null;
        initRecaptcha().catch(() => {});
      } else if (error.code === 'auth/no-verification') {
        toast.error('Session expired. Please request a new code.');
        setIsVerifying(false);
        clearRecaptcha();
        window.confirmationResult = null;
        initRecaptcha().catch(() => {});
      } else {
        toast.error(error.response?.data?.detail || error.message || 'Verification failed. Please try again.');
      }
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    try {
      const response = await api.post('/auth/forgot-password', {
        email: email
      });
      
      if (response.data.success) {
        toast.success('Password reset link sent! Check your email.');
        // In development, show the reset link
        if (response.data.reset_link && response.data.token) {
          console.log('🔗 Password Reset Link:', response.data.reset_link);
          toast.success(`Development: Reset token is ${response.data.token}`);
          setResetToken(response.data.token);
          setTimeout(() => {
            setShowForgotPassword(false);
            setShowResetPassword(true);
          }, 2000);
        } else {
          setShowForgotPassword(false);
          setShowEmailLogin(true);
        }
      }
    } catch (error) {
      toast.error('Failed to send reset email. Please try again.');
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await api.post('/auth/reset-password', {
        token: resetToken,
        new_password: newPassword
      });
      
      if (response.data.success) {
        toast.success('Password reset successfully! You can now sign in.');
        // Clear form and redirect to login
        setNewPassword('');
        setConfirmNewPassword('');
        setResetToken('');
        setShowResetPassword(false);
        setShowEmailLogin(true);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Invalid or expired reset token. Please request a new reset link.');
      } else {
        toast.error('Failed to reset password. Please try again.');
      }
    }
  };

  const resetForms = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setName('');
    setPhoneNumber('');
    setVerificationCode('');
    setResetToken('');
    setIsVerifying(false);
    setCountryCode('+1');
  };

  const startCardDealingAnimation = (engine) => {
    let cardsDealt = 0;
    const totalCards = 52;
    
    const dealInterval = setInterval(() => {
      cardsDealt += 4; // Deal 4 cards at once (one to each player)
      
      if (cardsDealt >= totalCards) {
        clearInterval(dealInterval);
        // Cards dealt, now start bidding after short delay
        setTimeout(() => {
          engine.startBiddingPhase();
          setGameState(engine.getGameState());
          toast.success('Cards dealt! Time to bid. Check your hand and see what your partner bid.');
          
          // Start bot bidding if it's not player's turn
          setTimeout(() => {
            simulateNextBotBid();
          }, 1000);
        }, 1000);
      } else {
        const progress = Math.round((cardsDealt / totalCards) * 100);
        toast.info(`Dealing cards... ${progress}%`);
      }
    }, 300); // Deal every 300ms
  };

  return (
    <div className="min-h-screen relative overflow-hidden text-[#F2F5F8]" style={{ backgroundColor: '#0A0E15' }}>
      {/* Round 45: the top strip points to the mobile app (it used to announce a private beta) */}
      <div className="fixed top-0 left-0 right-0 z-[90] py-2 px-4 shadow-lg" style={{ background: 'linear-gradient(to right,#111726,#151C2C,#111726)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
          <AlertCircle className="w-5 h-5" style={{ color: '#34E3A4' }} />
          <span className="font-semibold text-sm md:text-base" style={{ color: '#34E3A4' }}>
            For the best experience, download the GEMS mobile app.
          </span>
          <a href="/get-app" className="font-bold text-sm md:text-base underline" style={{ color: '#F2F5F8' }} data-testid="landing-strip-get-app">
            Get the app
          </a>
        </div>
      </div>
      
      {/* Mobile App Prompt Modal */}
      {showMobileAppPrompt && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-700 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-emerald-500/30">
            {/* Logo */}
            <div className="flex justify-center mb-4">
              <img 
                src={BRAND_ASSETS.logo} 
                alt="GEMS" 
                className="w-20 h-20 object-contain"
              />
            </div>
            
            {/* Title */}
            <h2 className="text-2xl font-bold text-white text-center mb-2">
              Get the Best Experience
            </h2>
            <p className="text-emerald-200 text-center mb-6">
              Download the GEMS app for the ultimate streaming and gaming experience on your {mobileOS === 'ios' ? 'iPhone' : 'Android device'}.
            </p>
            
            {/* Features */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-white">
                <div className="w-8 h-8 rounded-full bg-emerald-500/30 flex items-center justify-center">
                  <Play className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-sm">Go live instantly with one tap</span>
              </div>
              <div className="flex items-center gap-3 text-white">
                <div className="w-8 h-8 rounded-full bg-emerald-500/30 flex items-center justify-center">
                  <Trophy className="w-4 h-4 text-amber-400" />
                </div>
                <span className="text-sm">Play with friends and family</span>
              </div>
              <div className="flex items-center gap-3 text-white">
                <div className="w-8 h-8 rounded-full bg-emerald-500/30 flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 text-blue-400" />
                </div>
                <span className="text-sm">Chat and vibe with your community</span>
              </div>
            </div>
            
            {/* Download Button */}
            <button
              onClick={handleDownloadApp}
              className="w-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-amber-900 font-bold py-4 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-amber-500/30 flex items-center justify-center gap-2 mb-3"
            >
              {mobileOS === 'ios' ? (
                <>
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                  Download on App Store
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M3.609 1.814L13.792 12 3.609 22.186a.996.996 0 0 1-.609-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.807 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/></svg>
                  Get it on Google Play
                </>
              )}
            </button>
            
            {/* Continue in Browser Link */}
            <button
              onClick={handleContinueInBrowser}
              className="w-full text-emerald-300 text-sm py-2 hover:text-white transition-colors"
            >
              Continue in browser
            </button>
          </div>
        </div>
      )}
      
      {/* ═════════════════════════════════════════════════════════════
          REDESIGNED LANDING (Feb 2026) — VISUAL RE-SKIN ONLY.
          Every CTA below calls the SAME handlers as before:
            • "Create Account" / "Create your account" → setShowEmailSignup(true)
              → existing sequence: age verify → terms → guidelines → /auth/email-signup
            • "Sign In" → setShowLoginModal(true) → existing email/Google/phone modal
          Mobile-app prompt, beta-code handling, forgot/reset password, and
          every existing modal below (from `showLoginModal` onward) are
          preserved untouched.
         ═════════════════════════════════════════════════════════════ */}

      {/* Sticky top nav */}
      <header
        className="sticky z-50 backdrop-blur-md"
        style={{
          top: 40,   // sits below the private-beta strip
          backgroundColor: 'rgba(10,14,21,0.85)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-6 py-3">
          <div className="flex items-center gap-3">
            <img src={BRAND_ASSETS.logo} alt="GEMS" className="w-10 h-10 object-contain" />
            <span className="text-lg font-extrabold tracking-tight">
              GEMS <span style={{ color: '#34E3A4' }}>Live</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowLoginModal(true)}
              variant="outline"
              className="rounded-full font-semibold border-white/15 text-[#F2F5F8] hover:bg-white/5 hover:text-white bg-transparent"
              data-testid="login-button"
            >
              Sign In
            </Button>
            {/* Round 12b: the mobile apps, one tap from the top of the page */}
            <Button
              onClick={() => { window.location.href = '/get-app'; }}
              variant="outline"
              className="rounded-full font-semibold bg-transparent hover:bg-white/5"
              style={{ borderColor: 'rgba(52,227,164,0.6)', color: '#34E3A4' }}
              data-testid="nav-get-app"
            >
              📱 Get the App
            </Button>
            <Button
              onClick={() => setShowEmailSignup(true)}
              className="rounded-full font-semibold text-[#0A0E15] shadow-lg"
              style={{ backgroundColor: '#34E3A4' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#22C98C')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#34E3A4')}
              data-testid="nav-create-account"
            >
              Create Account
            </Button>
          </div>
        </div>
      </header>

      <main className="relative">
        {/* ── HERO ─────────────────────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-10 md:pt-16 pb-14">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border" style={{ borderColor: 'rgba(52,227,164,0.35)', backgroundColor: 'rgba(52,227,164,0.08)', color: '#34E3A4' }}>
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#34E3A4' }}></span>
                Live now
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight" style={{ color: '#F2F5F8' }}>
                <div>Go Live.</div>
                <div>Play Together.</div>
                <div
                  style={{
                    backgroundImage: 'linear-gradient(90deg,#34E3A4,#E6C34A)',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    color: 'transparent',
                  }}
                >
                  Have a Blast.
                </div>
              </h1>
              <p className="text-base md:text-lg max-w-xl" style={{ color: '#9BA7B6' }}>
                The interactive social entertainment platform where creators go live, share their world, and play face-to-face games like Spades, Crazy Eights, Checkers, Four-in-a-Row, and more with their audience — and where every viewer can leave the sidelines and get in the game.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={() => setShowEmailSignup(true)}
                  className="rounded-full font-bold px-6 py-6 h-auto text-base text-[#0A0E15] shadow-xl"
                  style={{ backgroundColor: '#34E3A4' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#22C98C')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#34E3A4')}
                  data-testid="hero-create-account"
                >
                  <Play className="w-4 h-4 mr-2 fill-current" />
                  Create your account
                </Button>
                <Button
                  onClick={() => setShowLoginModal(true)}
                  variant="outline"
                  className="rounded-full font-semibold px-6 py-6 h-auto text-base border-white/20 text-[#F2F5F8] hover:bg-white/5 bg-transparent"
                  data-testid="hero-sign-in"
                >
                  Sign in
                </Button>
              </div>
              <p className="text-xs" style={{ color: '#9BA7B6' }}>
                Free to join.
              </p>
            </div>

            {/* Right: live-room card */}
            <div className="lg:pl-8">
              <p className="text-sm mb-3" style={{ color: '#9BA7B6' }}>
                Go live, talk, compete, and stream — all in one place.
              </p>
              <div className="rounded-2xl overflow-hidden border shadow-2xl" style={{ backgroundColor: '#111726', borderColor: 'rgba(255,255,255,0.08)' }}>
                {/* Two-broadcaster live shot — displayed at natural ratio
                    so nothing is cropped. The screenshot already contains
                    its own LIVE badge / viewer count / control bar, so we
                    don't overlay duplicate chrome. */}
                <img
                  src={BRAND_ASSETS.creatorsLive}
                  alt="Two GEMS creators streaming"
                  className="block w-full h-auto"
                />
              </div>
              <div className="text-xs font-semibold tracking-widest uppercase mt-6 mb-3" style={{ color: '#9BA7B6' }}>
                See real gameplay in action
              </div>

              {/* Also live — three equal thumbnails */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Spades',     src: BRAND_ASSETS.spadesTable, alt: 'Live Spades match' },
                  { label: '4-In-A-Row', src: BRAND_ASSETS.connect4,    alt: '4-In-A-Row live' },
                ].map((g) => (
                  <div key={g.label} className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111726', borderColor: 'rgba(255,255,255,0.08)' }}>
                    <div className="relative w-full" style={{ aspectRatio: '1 / 1', backgroundColor: '#0F1520' }}>
                      <img src={g.src} alt={g.alt} className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                    <div className="px-3 py-2">
                      <div className="text-[10px] font-mono tracking-widest" style={{ color: '#9BA7B6' }}>ALSO LIVE</div>
                      <div className="text-sm font-semibold">{g.label}</div>
                    </div>
                  </div>
                ))}
                {/* Checkers — tinted icon until a real photo ships. */}
                <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111726', borderColor: 'rgba(255,255,255,0.08)' }}>
                  <div className="relative w-full flex items-center justify-center" style={{ aspectRatio: '1 / 1', backgroundColor: 'rgba(230,195,74,0.08)' }}>
                    <Trophy className="w-10 h-10" style={{ color: '#E6C34A' }} />
                  </div>
                  <div className="px-3 py-2">
                    <div className="text-[10px] font-mono tracking-widest" style={{ color: '#9BA7B6' }}>ALSO LIVE</div>
                    <div className="text-sm font-semibold">Checkers</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── PLATFORM STRIP ──────────────────────────────────────── */}
        <section style={{ borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)', backgroundColor: '#0D1219' }}>
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 flex flex-wrap items-center justify-center gap-6 text-sm" style={{ color: '#9BA7B6' }}>
            {/* Round 12b: live download links as soon as they're set in Admin → App Store */}
            {appLinks?.ios ? (
              <a href={appLinks.ios} onClick={() => trackStoreClick('apple_store', 'landing-strip')} className="hover:text-white" data-testid="strip-ios-download">
                📱 iOS App — <span style={{ color: '#34E3A4' }}>{/testflight/i.test(appLinks.ios) ? 'Download' : 'Download on the App Store'}</span>
              </a>
            ) : (
              <span>📱 iOS App — <span style={{ color: '#E6C34A' }}>Coming Soon</span></span>
            )}
            <span className="hidden md:inline" style={{ color: 'rgba(255,255,255,0.15)' }}>·</span>
            {appLinks?.android ? (
              <a href={appLinks.android} onClick={() => trackStoreClick('google_play', 'landing-strip')} className="hover:text-white" data-testid="strip-android-download">
                📱 Android App — <span style={{ color: '#34E3A4' }}>{/play\.google/i.test(appLinks.android) ? 'Get it on Google Play' : 'Download'}</span>
              </a>
            ) : (
              <span>📱 Android App — <span style={{ color: '#E6C34A' }}>Coming Soon</span></span>
            )}
            <span className="hidden md:inline" style={{ color: 'rgba(255,255,255,0.15)' }}>·</span>
            <span>🖥 Web App — <span style={{ color: '#34E3A4' }}>Live now.</span></span>
          </div>
        </section>

        {/* ── WHAT IS GEMS LIVE? ──────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-16">
          <div className="text-center mb-10">
            <div className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#34E3A4' }}>What is GEMS?</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold" style={{ color: '#F2F5F8' }}>
              An interactive social entertainment platform built for creators, players, and communities
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { i: '🎥', t: 'Go Live Instantly',      c: '#34E3A4', bg: 'rgba(52,227,164,0.10)', d: 'Start streaming with one tap. Share your gameplay, your vibe, and your personality with a growing audience.' },
              { i: '🎮', t: 'Play Live',              c: '#5B9DF9', bg: 'rgba(91,157,249,0.10)', d: 'Spades, 4-In-A-Row, Checkers and more — real-time multiplayer with live video. Play with friends, family, or match with new opponents.' },
              { i: '👥', t: 'Build Your Community',   c: '#A78BFA', bg: 'rgba(167,139,250,0.10)', d: 'Grow your follower base, connect with fans, and build meaningful relationships through shared experiences.' },
              { i: '🎁', t: 'Gift & Earn',            c: '#F471B5', bg: 'rgba(244,113,181,0.10)', d: 'Send and receive virtual gifts during live streams. Support your favorite creators and get recognized.' },
              { i: '💬', t: 'Real-Time Chat',         c: '#FB923C', bg: 'rgba(251,146,60,0.10)', d: 'Direct messaging, live chat during streams, and community interactions that keep you connected.' },
              { i: '🏆', t: 'Compete & Win',          c: '#E6C34A', bg: 'rgba(230,195,74,0.10)', d: 'Climb the leaderboards, earn streaks, and show off your skills across every game on GEMS.' },
            ].map((f) => (
              <div key={f.t} className="rounded-2xl border p-6" style={{ backgroundColor: '#111726', borderColor: 'rgba(255,255,255,0.08)' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl mb-4" style={{ backgroundColor: f.bg, color: f.c }}>{f.i}</div>
                <h3 className="text-lg font-bold mb-1.5" style={{ color: '#F2F5F8' }}>{f.t}</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#9BA7B6' }}>{f.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── HOW IT WORKS ────────────────────────────────────────── */}
        <section style={{ backgroundColor: '#0D1219', borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
            <div className="text-center mb-10">
              <div className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#34E3A4' }}>How It Works</div>
              <h2 className="mt-2 text-3xl md:text-4xl font-extrabold" style={{ color: '#F2F5F8' }}>Three steps to start streaming</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { n: '01', t: 'Create Your Profile',      d: 'Sign up, customize your profile, and set your GEMS Tag. Your identity, your way.' },
                { n: '02', t: 'Go Live or Join a Game',   d: 'Start a solo stream or jump into a live match — Spades, 4-In-A-Row, Checkers. Invite friends or match with new players.' },
                { n: '03', t: 'Grow & Monetize',          d: 'Build your audience, receive gifts from fans, and turn your passion into a community experience.' },
              ].map((s) => (
                <div key={s.n} className="rounded-2xl border p-6 relative overflow-hidden" style={{ backgroundColor: '#111726', borderColor: 'rgba(255,255,255,0.08)' }}>
                  <div className="text-[80px] font-black leading-none opacity-15 absolute -right-2 -top-4" style={{ color: '#34E3A4' }}>{s.n}</div>
                  <div className="relative z-10">
                    <div className="text-xs font-mono font-bold tracking-widest mb-2" style={{ color: '#34E3A4' }}>{s.n}</div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#F2F5F8' }}>{s.t}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: '#9BA7B6' }}>{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FOR CREATORS / FOR PLAYERS ──────────────────────────── */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-16">
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: '⭐',
                iconColor: '#E6C34A',
                iconBg: 'rgba(230,195,74,0.10)',
                title: 'For Creators',
                sub: 'GEMS gives you the tools to build a real audience. Go live, play games, receive gifts, and grow your community — all in one place.',
                items: ['One-tap live streaming','Virtual gifting & support','Follower & fan analytics','Creator verification badges'],
              },
              {
                icon: '🎮',
                iconColor: '#34E3A4',
                iconBg: 'rgba(52,227,164,0.10)',
                title: 'For Players',
                sub: 'Find your people and play the way it was meant to be — live, social, and competitive. Every game is a shared experience.',
                items: ['Real-time multiplayer games','Live video during gameplay','Matchmaking & invitations','Leaderboards & win streaks'],
              },
            ].map((p) => (
              <div key={p.title} className="rounded-2xl border p-6" style={{ backgroundColor: '#111726', borderColor: 'rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl" style={{ backgroundColor: p.iconBg, color: p.iconColor }}>{p.icon}</div>
                  <h3 className="text-xl font-bold" style={{ color: '#F2F5F8' }}>{p.title}</h3>
                </div>
                <p className="text-sm mb-4" style={{ color: '#9BA7B6' }}>{p.sub}</p>
                <ul className="space-y-2">
                  {p.items.map((it) => (
                    <li key={it} className="flex items-center gap-2 text-sm" style={{ color: '#F2F5F8' }}>
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgba(52,227,164,0.15)', color: '#34E3A4' }}>✓</span>
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* ── SAFETY ──────────────────────────────────────────────── */}
        <section style={{ backgroundColor: '#0D1219', borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
            <div className="text-center mb-10">
              <div className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#34E3A4' }}>Built with Safety First</div>
              <p className="mt-3 max-w-3xl mx-auto text-base" style={{ color: '#9BA7B6' }}>
                GEMS is powered by an AI workforce that monitors content, protects users, and ensures a safe environment for everyone. Our Trust &amp; Safety systems run 24/7.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { i: '🛡', t: 'AI Moderation',        c: '#34E3A4', bg: 'rgba(52,227,164,0.10)' },
                { i: '⚡', t: 'Fraud Protection',     c: '#E6C34A', bg: 'rgba(230,195,74,0.10)' },
                { i: '👥', t: 'Community Guidelines', c: '#5B9DF9', bg: 'rgba(91,157,249,0.10)' },
                { i: '⭐', t: 'Verified Badges',      c: '#A78BFA', bg: 'rgba(167,139,250,0.10)' },
              ].map((p) => (
                <div key={p.t} className="rounded-2xl border p-5 text-center" style={{ backgroundColor: '#111726', borderColor: 'rgba(255,255,255,0.08)' }}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl mx-auto mb-3" style={{ backgroundColor: p.bg, color: p.c }}>{p.i}</div>
                  <div className="text-sm font-semibold" style={{ color: '#F2F5F8' }}>{p.t}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── READY TO PLAY? ──────────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4" style={{ color: '#F2F5F8' }}>Ready to Play?</h2>
          <p className="max-w-2xl mx-auto text-base md:text-lg mb-8" style={{ color: '#9BA7B6' }}>
            GEMS is live. Create your account and start streaming, playing, and connecting today.
          </p>
          <Button
            onClick={() => setShowEmailSignup(true)}
            className="rounded-full font-bold px-8 py-6 h-auto text-base text-[#0A0E15] shadow-2xl"
            style={{ backgroundColor: '#34E3A4' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#22C98C')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#34E3A4')}
            data-testid="ready-create-account"
          >
            <Play className="w-4 h-4 mr-2 fill-current" />
            Create your account
          </Button>
        </section>
      </main>

      {/* ── FOOTER ───────────────────────────────────────────────── */}
      <footer style={{ backgroundColor: '#0A0E15', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-3">
                <img src={BRAND_ASSETS.logo} alt="GEMS" className="w-10 h-10 object-contain" />
                <span className="text-lg font-extrabold">
                  GEMS <span style={{ color: '#34E3A4' }}>Live</span>
                </span>
              </div>
              <p className="text-sm" style={{ color: '#9BA7B6' }}>
                Game. Engage. Monetize. Stream. The interactive social entertainment platform.
              </p>
            </div>

            {[
              { title: 'Product', links: [
                { l: 'Web App',     href: '/' },
                { l: 'iOS App',     href: '/download-ios' },
                { l: 'Android App', href: '/download-android' },
              ] },
              { title: 'Legal', links: [
                { l: 'Terms of Service',   href: '/terms' },
                { l: 'Privacy Policy',     href: '/privacy' },
                { l: 'Community Guidelines', href: '/guidelines' },
              ] },
              { title: 'Support', links: [
                { l: 'Help Center', href: '/help' },
                { l: 'Contact Us',  href: 'mailto:support@gemslive.tv' },
              ] },
            ].map((col) => (
              <div key={col.title}>
                <div className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#F2F5F8' }}>{col.title}</div>
                <ul className="space-y-2">
                  {col.links.map((k) => (
                    <li key={k.l}>
                      <a href={k.href} className="text-sm transition-colors" style={{ color: '#9BA7B6' }} onMouseEnter={(e) => (e.currentTarget.style.color = '#F2F5F8')} onMouseLeave={(e) => (e.currentTarget.style.color = '#9BA7B6')}>{k.l}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-6 flex flex-col md:flex-row items-center justify-between gap-2" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="text-xs" style={{ color: '#9BA7B6' }}>© 2026 GEMS Live LLC. All rights reserved.</p>
            
          </div>
        </div>
      </footer>
      {/* Login Options Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto bg-[#0F172A] text-white border-slate-700">
          {/* GEMS Logo Header - Matching Mobile */}
          <div className="flex flex-col items-center pt-4">
            <div className="bg-[#1E293B] rounded-2xl p-6 mb-4">
              <img 
                src={BRAND_ASSETS.logo} 
                alt="GEMS" 
                className="w-20 h-20 object-contain" 
              />
            </div>
            <p className="text-emerald-500 text-xs font-bold tracking-[0.2em] uppercase">Interactive Social Entertainment Platform</p>
          </div>

          {/* Hero Text - Matching Mobile */}
          <div className="text-center py-4">
            <h1 className="text-2xl font-bold text-white mb-2">This Is the Table.</h1>
            <p className="text-gray-400 text-sm">Post, go live, play social games face-to-face with your people — and let anyone watching pull up a chair.</p>
          </div>

          {/* Login Card - Dark Theme */}
          <div className="bg-[#1E293B] rounded-2xl p-6 space-y-5">
            <h2 className="text-xl font-bold text-white text-center">Welcome Back</h2>
            
            {/* Login Method Tabs - Matching Mobile Style */}
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setLoginMethod('email')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                  loginMethod === 'email' 
                    ? 'bg-[#0F172A] text-emerald-500 border border-emerald-500/50' 
                    : 'bg-transparent text-gray-400 border border-slate-600 hover:border-slate-500'
                }`}
              >
                <Mail className="h-4 w-4" />
                <span className="text-sm">Email</span>
              </button>
              <button
                onClick={() => setLoginMethod('google')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                  loginMethod === 'google' 
                    ? 'bg-[#0F172A] text-white border border-slate-500' 
                    : 'bg-transparent text-gray-400 border border-slate-600 hover:border-slate-500'
                }`}
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-sm">Google</span>
              </button>
              {/* Phone login temporarily disabled - will be re-enabled after Firebase reCAPTCHA fix */}
            </div>

            {/* Email Login Form */}
            {loginMethod === 'email' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-emerald-500" />
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-[#0F172A] border border-slate-600 rounded-xl py-3 pl-11 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 transition-colors"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleEmailLogin()}
                      className="w-full bg-[#0F172A] border border-slate-600 rounded-xl py-3 pl-11 pr-11 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 transition-colors"
                    />
                    <button 
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-400"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                
                <button
                  onClick={handleEmailLogin}
                  disabled={!email || !password}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-500/50 disabled:cursor-not-allowed text-white font-semibold py-3.5 rounded-full transition-colors"
                >
                  Sign In
                </button>
              </div>
            )}

            {/* Google Login */}
            {loginMethod === 'google' && (
              <div className="space-y-4">
                <p className="text-gray-400 text-sm text-center">Sign in with your Google account for quick access</p>
                <button
                  onClick={handleGoogleLogin}
                  className="w-full bg-white hover:bg-gray-100 text-gray-800 font-semibold py-3.5 rounded-full transition-colors flex items-center justify-center gap-3"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>
              </div>
            )}

            {/* Phone Login */}
            {loginMethod === 'phone' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <input
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && phoneNumber) { setShowLoginModal(false); setShowPhoneLogin(true); }}}
                      className="w-full bg-[#0F172A] border border-slate-600 rounded-xl py-3 pl-11 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 transition-colors"
                    />
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    setShowLoginModal(false);
                    setShowPhoneLogin(true);
                  }}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3.5 rounded-full transition-colors"
                >
                  Continue with Phone
                </button>
              </div>
            )}

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-[#1E293B] text-gray-500">or</span>
              </div>
            </div>

            {/* Sign Up Link */}
            <p className="text-center text-gray-400 text-sm">
              Don't have an account?{' '}
              <button 
                onClick={() => {
                  setShowLoginModal(false);
                  setShowEmailSignup(true);
                }}
                className="text-emerald-500 hover:text-emerald-400 font-semibold"
              >
                Sign Up
              </button>
            </p>
          </div>

          {/* Footer */}
          <div className="text-center pt-4 pb-2">
            <p className="text-gray-500 text-xs">
              <a href="/terms" className="hover:text-gray-400">Terms of Service</a>
              {' • '}
              <a href="/privacy" className="hover:text-gray-400">Privacy Policy</a>
              {' • '}
              <a href="/guidelines" className="hover:text-gray-400">Guidelines</a>
            </p>
            <p className="text-gray-600 text-xs mt-2">© 2025 GEMS Live LLC. All rights reserved.</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email/Password Login Modal */}
      <Dialog open={showEmailLogin} onOpenChange={(open) => {
        setShowEmailLogin(open);
        if (!open) resetForms();
      }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-bold">Sign In with Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email Address</label>
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <Input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleEmailLogin()}
                className="w-full"
              />
            </div>
            
            <div className="text-right">
              <button 
                onClick={() => {
                  setShowEmailLogin(false);
                  setShowForgotPassword(true);
                }}
                className="text-sm text-emerald-600 hover:text-emerald-800"
              >
                Forgot Password?
              </button>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleEmailLogin}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={!email || !password}
              >
                Sign In
              </Button>
              <Button
                onClick={() => {
                  setShowEmailLogin(false);
                  setShowLoginModal(true);
                }}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
            </div>
            
            <div className="text-center text-sm text-gray-600">
              Don't have an account?{' '}
              <button 
                onClick={() => {
                  setShowEmailLogin(false);
                  setShowEmailSignup(true);
                }}
                className="text-emerald-600 hover:text-emerald-800 font-semibold"
              >
                Sign Up
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email/Password Signup Modal */}
      <Dialog open={showEmailSignup} onOpenChange={(open) => {
        setShowEmailSignup(open);
        if (!open) resetForms();
      }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-bold">Create Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Age Requirement Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
              <p className="text-amber-800 text-sm font-medium">
                ⚠️ You must be at least 18 years old to use GEMS
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Name</label>
              <Input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Email Address</label>
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <Input
                type="password"
                placeholder="At least 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Confirm Password</label>
              <Input
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleInitiateSignup()}
                className="w-full"
              />
            </div>
            
            {/* Referral code (optional) — invalid codes never block signup */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Referral code <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <Input
                type="text"
                placeholder="GEMSXXXXXX"
                value={signupReferralCode}
                onChange={(e) => setSignupReferralCode(e.target.value.toUpperCase())}
                maxLength={12}
                className="w-full uppercase tracking-wider"
                data-testid="signup-referral-code-input"
              />
            </div>

            {/* Live Notifications Checkbox */}
            <div className="flex items-start gap-3 py-2">
              <input
                type="checkbox"
                id="live-notif-signup"
                defaultChecked={true}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                data-testid="signup-live-notifications-checkbox"
              />
              <label htmlFor="live-notif-signup" className="text-sm text-gray-600">
                Notify me when people I follow go live
              </label>
            </div>

            {/* Round 35: email consent lives in the Terms (12A); say so here. */}
            <p className="text-xs text-gray-500 py-1" data-testid="signup-email-consent-note">
              By creating an account you agree to receive emails from GEMS — account and security messages, plus news,
              tips and offers. Every marketing email has a one-click unsubscribe.
            </p>
            
            <div className="flex gap-3">
              <Button
                onClick={handleInitiateSignup}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={!email || !password || !name || !confirmPassword}
              >
                Continue
              </Button>
              <Button
                onClick={() => {
                  setShowEmailSignup(false);
                  setShowLoginModal(true);
                }}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              By creating an account, you'll be asked to verify your age and accept our Terms of Service, Privacy Policy, and Community Guidelines.
            </p>
            
            <div className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <button 
                onClick={() => {
                  setShowEmailSignup(false);
                  setShowEmailLogin(true);
                }}
                className="text-emerald-600 hover:text-emerald-800 font-semibold"
              >
                Sign In
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Age Verification Modal */}
      <AgeVerificationModal
        isOpen={showAgeVerification}
        onVerified={handleAgeVerified}
        onClose={handleVerificationDeclined}
      />

      {/* Terms Acceptance Modal */}
      <TermsAcceptanceModal
        isOpen={showTermsAcceptance}
        onAccepted={handleTermsAccepted}
        onDeclined={handleVerificationDeclined}
      />

      {/* Community Guidelines Modal */}
      <CommunityGuidelinesModal
        isOpen={showCommunityGuidelines}
        onAccepted={handleGuidelinesAccepted}
        onDeclined={handleVerificationDeclined}
      />

      {/* Suggested Friends Modal — post-signup onboarding */}
      <SuggestedFriendsModal
        open={showSuggestedFriends}
        suggestions={suggestedFriends}
        checked={suggestedChecked}
        onToggle={(id) => setSuggestedChecked((p) => ({ ...p, [id]: !p[id] }))}
        onFollow={confirmSuggestedFriends}
        onSkip={skipSuggestedFriends}
        busy={suggestedBusy}
      />

      {/* First-Post Nudge — appears once after signup. Skippable but
          front-and-center. Posts through the existing /api/posts
          endpoint so the new user lands on the timeline with a
          first-post already in place. */}
      <Dialog open={showFirstPostNudge} onOpenChange={setShowFirstPostNudge}>
        <DialogContent className="sm:max-w-md" data-testid="first-post-nudge-modal">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-bold">
              Make your first post
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-center text-sm text-gray-500">
              A quick hello, a game callout, whatever — put yourself on the timeline.
            </p>
            <textarea
              value={firstPostText}
              onChange={(e) => setFirstPostText(e.target.value)}
              placeholder={[
                "What's on your mind?",
                "Talk ya shit 🗣️",
                "Let them know you're here 👋",
                "Introduce yourself",
                "Drop a pic 📸",
                "What game you pulling up on?",
                "New here — put me on",
                "Say something the timeline needs to hear",
              ][firstPostPromptIdx]}
              rows={5}
              maxLength={500}
              className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="first-post-nudge-textarea"
            />
            <div className="text-xs text-gray-400 text-right">{firstPostText.length}/500</div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowFirstPostNudge(false); setFirstPostText(''); }}
                className="flex-1"
                data-testid="first-post-nudge-skip"
              >
                Skip
              </Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="first-post-nudge-submit"
                disabled={!firstPostText.trim()}
                onClick={async () => {
                  const content = firstPostText.trim();
                  if (!content) return;
                  try {
                    await api.post('/posts', { content, post_type: 'text', local_date: new Date().toLocaleDateString('en-CA') });
                    toast.success('Posted! Welcome to GEMS.');
                  } catch (e) {
                    // Non-blocking — user is already signed up.
                    toast.info('Post saved for later. Welcome to GEMS!');
                  }
                  setFirstPostText('');
                  setShowFirstPostNudge(false);
                }}
              >
                Post
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => {
        setShowForgotPassword(open);
        if (!open) resetForms();
      }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-bold">Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center text-sm text-gray-600 mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Email Address</label>
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleForgotPassword}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={!email}
              >
                Send Reset Link
              </Button>
              <Button
                onClick={() => {
                  setShowForgotPassword(false);
                  setShowEmailLogin(true);
                }}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
            </div>
            
            <div className="text-center text-sm text-gray-600">
              Remember your password?{' '}
              <button 
                onClick={() => {
                  setShowForgotPassword(false);
                  setShowEmailLogin(true);
                }}
                className="text-emerald-600 hover:text-emerald-800 font-semibold"
              >
                Sign In
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetPassword} onOpenChange={(open) => {
        setShowResetPassword(open);
        if (!open) resetForms();
      }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-bold">Set New Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center text-sm text-gray-600 mb-4">
              Enter your new password below.
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">New Password</label>
              <Input
                type="password"
                placeholder="At least 6 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Confirm New Password</label>
              <Input
                type="password"
                placeholder="Confirm your new password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="w-full"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleResetPassword}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={!newPassword || !confirmNewPassword}
              >
                Reset Password
              </Button>
              <Button
                onClick={() => {
                  setShowResetPassword(false);
                  setShowForgotPassword(true);
                }}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Phone Number Login Modal */}
      <Dialog open={showPhoneLogin} onOpenChange={(open) => {
        setShowPhoneLogin(open);
        if (open) {
          // Pre-initialize reCAPTCHA when modal opens
          initRecaptcha().catch(err => console.error('reCAPTCHA init error:', err));
        } else {
          resetForms();
          clearRecaptcha();
          window.confirmationResult = null;
        }
      }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-bold">
              {isVerifying ? 'Enter Verification Code' : 'Sign In with Phone'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!isVerifying ? (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone Number</label>
                  <div className="flex gap-2">
                    <Select value={countryCode} onValueChange={setCountryCode}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="+1">🇺🇸 +1</SelectItem>
                        <SelectItem value="+44">🇬🇧 +44</SelectItem>
                        <SelectItem value="+33">🇫🇷 +33</SelectItem>
                        <SelectItem value="+49">🇩🇪 +49</SelectItem>
                        <SelectItem value="+39">🇮🇹 +39</SelectItem>
                        <SelectItem value="+34">🇪🇸 +34</SelectItem>
                        <SelectItem value="+31">🇳🇱 +31</SelectItem>
                        <SelectItem value="+32">🇧🇪 +32</SelectItem>
                        <SelectItem value="+41">🇨🇭 +41</SelectItem>
                        <SelectItem value="+43">🇦🇹 +43</SelectItem>
                        <SelectItem value="+45">🇩🇰 +45</SelectItem>
                        <SelectItem value="+46">🇸🇪 +46</SelectItem>
                        <SelectItem value="+47">🇳🇴 +47</SelectItem>
                        <SelectItem value="+358">🇫🇮 +358</SelectItem>
                        <SelectItem value="+7">🇷🇺 +7</SelectItem>
                        <SelectItem value="+86">🇨🇳 +86</SelectItem>
                        <SelectItem value="+91">🇮🇳 +91</SelectItem>
                        <SelectItem value="+81">🇯🇵 +81</SelectItem>
                        <SelectItem value="+82">🇰🇷 +82</SelectItem>
                        <SelectItem value="+61">🇦🇺 +61</SelectItem>
                        <SelectItem value="+55">🇧🇷 +55</SelectItem>
                        <SelectItem value="+52">🇲🇽 +52</SelectItem>
                        <SelectItem value="+54">🇦🇷 +54</SelectItem>
                        <SelectItem value="+56">🇨🇱 +56</SelectItem>
                        <SelectItem value="+57">🇨🇴 +57</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      placeholder="555-123-4567"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && phoneNumber && handleSendPhoneCode()}
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    We'll send you a verification code via SMS
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleSendPhoneCode}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={!phoneNumber}
                    data-testid="send-code-btn"
                  >
                    Send Code
                  </Button>
                  <Button
                    onClick={() => {
                      setShowPhoneLogin(false);
                      setShowLoginModal(true);
                      setIsVerifying(false);
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Back
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Verification Code</label>
                  <Input
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.slice(0, 6))}
                    onKeyDown={(e) => e.key === 'Enter' && verificationCode.length === 6 && handleVerifyPhone()}
                    className="w-full text-center text-xl tracking-wider"
                    maxLength="6"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Code sent to {countryCode} {phoneNumber}
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleVerifyPhone}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={verificationCode.length !== 6}
                  >
                    Verify & Sign In
                  </Button>
                  <Button
                    onClick={() => setIsVerifying(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Back
                  </Button>
                </div>
                
                <div className="text-center">
                  <button 
                    onClick={handleSendPhoneCode}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Resend Code
                  </button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer with Legal Links */}
      <footer className="relative z-10 mt-16 border-t border-emerald-600/30 bg-emerald-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Main Footer Row */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img 
                src={BRAND_ASSETS.logo} 
                alt="GEMS" 
                className="w-8 h-8 object-contain"
              />
              <span className="text-emerald-200 text-sm">© 2025 GEMS Live LLC. All rights reserved.</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="/support" className="text-emerald-200 hover:text-white transition-colors">
                Support
              </a>
              <a href="/privacy" className="text-emerald-200 hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="text-emerald-200 hover:text-white transition-colors">
                Terms of Service
              </a>
              <a href="/community-guidelines" className="text-emerald-200 hover:text-white transition-colors">
                Community Guidelines
              </a>
            </div>
          </div>
          
          {/* Currency Disclosure */}
          <div className="mt-6 pt-4 border-t border-emerald-700/50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-emerald-400/80">
              <div className="bg-emerald-950/50 rounded-lg p-4">
                <h4 className="font-semibold text-emerald-300 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Virtual Currency Notice
                </h4>
                <p>Emeralds and Rubies are virtual items used for in-app engagement and participation. They are not real currency, stored value, or financial accounts and have no cash value.</p>
              </div>
              <div className="bg-emerald-950/50 rounded-lg p-4">
                <h4 className="font-semibold text-emerald-300 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Interactive Social Entertainment Platform
                </h4>
                <p>GEMS is an interactive social entertainment platform where people go live, post to a social feed, and play face-to-face games with their audience. Must be 18+ to participate.</p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// App Download Buttons Component
const AppDownloadButtons = () => {
  const [storeLinks, setStoreLinks] = useState({
    google_play: { url: '#', is_published: false },
    apple_store: { url: '#', is_published: false }
  });

  useEffect(() => {
    fetchStoreLinks();
  }, []);

  const fetchStoreLinks = async () => {
    try {
      const response = await api.get('/app-store/links');
      setStoreLinks(response.data);
    } catch (error) {
      console.log('Store links not available');
    }
  };

  const trackClick = async (storeType) => {
    try {
      await api.post('/app-store/track-click', {
        store_type: storeType,
        page: 'landing',
        referrer: document.referrer
      });
    } catch (error) {
      console.log('Click tracking failed');
    }
  };

  const handleStoreClick = (storeType, url) => {
    trackClick(storeType);
    if (url && url !== '#') {
      window.open(url, '_blank');
    } else {
      toast.info('Mobile app coming soon! Sign up to be notified.');
    }
  };

  return (
    <div className="mt-8">
      <p className="text-emerald-200 text-sm mb-4">Download the mobile app</p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
        {/* Google Play Button */}
        <button
          onClick={() => handleStoreClick('google_play', storeLinks.google_play?.url)}
          className="group flex items-center gap-3 bg-black/80 hover:bg-black text-white px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl"
        >
          <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
          </svg>
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-wide opacity-80">Get it on</div>
            <div className="text-lg font-semibold -mt-1">Google Play</div>
          </div>
          {!storeLinks.google_play?.is_published && (
            <span className="absolute -top-2 -right-2 bg-amber-500 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded-full">SOON</span>
          )}
        </button>

        {/* Apple Store Button */}
        <button
          onClick={() => handleStoreClick('apple_store', storeLinks.apple_store?.url)}
          className="group flex items-center gap-3 bg-black/80 hover:bg-black text-white px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl relative"
        >
          <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
          </svg>
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-wide opacity-80">Download on the</div>
            <div className="text-lg font-semibold -mt-1">App Store</div>
          </div>
          {!storeLinks.apple_store?.is_published && (
            <span className="absolute -top-2 -right-2 bg-amber-500 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded-full">SOON</span>
          )}
        </button>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:bg-gradient-to-br hover:from-white/15 hover:to-white/10 transition-all duration-500 group shadow-2xl hover:shadow-3xl hover:scale-105 hover:border-amber-400/30">
    <div className="text-amber-400 mb-6 group-hover:scale-110 group-hover:text-amber-300 transition-all duration-300 transform">{icon}</div>
    <h3 className="text-2xl font-semibold text-white mb-4 group-hover:text-amber-100 transition-colors duration-300">{title}</h3>
    <p className="text-emerald-200 leading-relaxed group-hover:text-emerald-100 transition-colors duration-300">{description}</p>
  </div>
);

// ============================================================
// Modern in-stream playing card (July 26, 2026)
// - Larger, rounded, high-contrast face for readability on video overlays.
// - Big/Little Joker gets a distinct face (BIG/LIL corner + jester glyph).
// - Trump card (2♦) keeps its amber ring hint.
// - Cards overlap (-0.55rem margin) so the whole hand fits inside the
//   video area on mobile; hover-lift only applies when `playable`.
// - `onPlay(card)` fires when the player taps a playable card. Existing
//   sendGameMessage/optimistic-remove logic lives in the hand row below.
// ============================================================
const SUIT_SYMBOLS = { spades: '♠', hearts: '♥', diamonds: '♦', clubs: '♣' };

const PlayingCard = ({ card, playable, onPlay, isTrump, notMyTurn = false }) => {
  const isJoker = card.value === 'BIG_JOKER' || card.value === 'LITTLE_JOKER';
  const isBigJoker = card.value === 'BIG_JOKER';
  const isRed = card.suit === 'hearts' || card.suit === 'diamonds';
  const suit = SUIT_SYMBOLS[card.suit] || '';
  const rank = card.value;
  const ink = isRed ? '#E4002B' : '#111827';
  return (
    <button
      type="button"
      disabled={!playable}
      onClick={() => playable && onPlay(card)}
      title={isTrump ? '2♦ — TRUMP' : `${rank} of ${card.suit}`}
      data-testid={`playing-card-${card.suit}-${card.value}`}
      className={[
        'relative select-none rounded-2xl bg-white flex-shrink-0',
        'shadow-[0_6px_16px_rgba(0,0,0,0.35)] ring-1 ring-black/10',
        'transition-transform duration-150 ease-out',
        // Part 2 (Feb 19, 2026): NEVER ghost the whole hand.
        //   playable                 → lift + hover ring
        //   !playable && notMyTurn   → readable (opponent turn — this is
        //                              info, not "you can't play this")
        //   !playable && !notMyTurn  → dimmed (your turn, this card is
        //                              illegal — original behavior)
        playable
          ? 'cursor-pointer hover:-translate-y-6 hover:scale-105 hover:shadow-[0_18px_36px_rgba(0,0,0,0.5)] hover:z-50'
          : notMyTurn
            ? 'opacity-100 cursor-default'
            : 'opacity-55 cursor-not-allowed',
        isTrump ? 'ring-2 ring-amber-400' : '',
      ].join(' ')}
      style={{ width: '4.6rem', height: '6.6rem', marginLeft: '-0.55rem' }}
    >
      {isJoker ? (
        <div className="absolute inset-0 flex flex-col items-center justify-between p-2">
          <span
            className="self-start text-[0.72rem] font-extrabold tracking-wide"
            style={{ color: isBigJoker ? '#E4002B' : '#111827' }}
          >
            {isBigJoker ? 'BIG' : 'LIL'}
          </span>
          <span className="text-3xl" role="img" aria-label="joker">🃏</span>
          <span
            className="self-end text-[0.72rem] font-extrabold tracking-wide rotate-180"
            style={{ color: isBigJoker ? '#E4002B' : '#111827' }}
          >
            JOKER
          </span>
        </div>
      ) : (
        <div className="absolute inset-0 p-2" style={{ color: ink }}>
          <div className="absolute top-1.5 left-2 leading-none text-center">
            <div className="text-lg font-extrabold">{rank}</div>
            <div className="text-base -mt-0.5">{suit}</div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-5xl">{suit}</span>
          </div>
          <div className="absolute bottom-1.5 right-2 leading-none text-center rotate-180">
            <div className="text-lg font-extrabold">{rank}</div>
            <div className="text-base -mt-0.5">{suit}</div>
          </div>
        </div>
      )}
    </button>
  );
};

// ============================================================
// ============================================================
// GAME_CATALOG — SINGLE source of truth for every game surface:
//   * Games page tiles (`GameTile` grid)
//   * In-stream `GamePickerModal` (derived from this)
// Promote a game by flipping `status` from 'soon' → 'playable' (or 'practice').
// Add a game by pushing an entry here; both surfaces update.
// (July 26, 2026 — replaced `COMING_SOON_GAMES` with this richer catalog.)
// ============================================================
// SEP 6 2026: `art` = the OFFICIAL game icons (frontend/public/game-icons/,
// same set the mobile app bundles) — rendered object-contain so the full
// icon is always visible. Emoji/gradient stay as instant fallback.
const GAME_CATALOG = [
  { id: 'spades',        name: 'Spades',       tagline: 'NY-style, face-to-face', players: '2–4', status: 'playable', icon: '♠',  art: '/game-icons/spades.jpg',        gradient: 'from-emerald-500 via-emerald-600 to-teal-800' },
  { id: 'four_in_a_row', name: '4 In A Row',   tagline: 'Connect four to win',    players: '2',   status: 'playable', icon: '🔴', art: '/game-icons/four-in-a-row.jpg', gradient: 'from-blue-500 via-indigo-600 to-violet-800' },
  { id: 'crazy_eights',  name: 'Crazy Eights', tagline: 'Shed your hand first',   players: '2–6', status: 'practice', icon: '🎴', art: '/game-icons/crazy-eights.jpg',  gradient: 'from-fuchsia-500 via-purple-600 to-indigo-800' },
  { id: 'chess',         name: 'Chess',        tagline: 'Outthink your rival',    players: '2',   status: 'soon',     icon: '♛',  art: '/game-icons/chess.jpg',         gradient: 'from-slate-500 via-slate-700 to-slate-900' },
  { id: 'checkers',      name: 'Checkers',     tagline: 'King the board',         players: '2',   status: 'live',     icon: '⛃',  art: '/game-icons/checkers.jpg',      gradient: 'from-rose-500 via-red-600 to-red-900' },
  { id: 'tonk',          name: 'Tonk',         tagline: 'Fast rummy hustle',      players: '2–4', status: 'soon',     icon: '🃏', art: '/game-icons/tonk.jpg',          gradient: 'from-amber-500 via-orange-600 to-red-800' },
  { id: 'dominoes',      name: 'Dominoes',     tagline: 'Bones on the table',     players: '2–4', status: 'soon',     icon: '🁫', art: '/game-icons/dominoes.jpg',      gradient: 'from-cyan-500 via-sky-600 to-blue-900' },
  { id: 'pitty_pat',     name: 'Pitty Pat',    tagline: 'Match and move',         players: '2–4', status: 'soon',     icon: '🀄', art: '/game-icons/pitty-pat.jpg',     gradient: 'from-lime-500 via-green-600 to-emerald-900' },
];

const STATUS_BADGE = {
  playable: { label: 'LIVE READY',   cls: 'bg-emerald-400/90 text-emerald-950' },
  practice: { label: 'PRACTICE',     cls: 'bg-fuchsia-400/90 text-fuchsia-950' },
  soon:     { label: 'COMING SOON',  cls: 'bg-amber-400/90 text-amber-950' },
};

// Universal minimum for LIVE match entries (Spades / 4-In-A-Row / Crazy Eights).
// Practice fees are separate (see PRACTICE_FEE on the backend, 10 emeralds).
const MIN_MATCH_ENTRY = 100;

// In-stream game picker options, derived from GAME_CATALOG so both surfaces
// stay in sync automatically.
const GAME_PICKER_OPTIONS = GAME_CATALOG.map((g) => ({
  id: g.id,
  name: g.name,
  emoji: g.icon,
  desc: g.tagline,
  players: `${g.players} Players`,
  playable: g.status === 'playable' || g.id === 'crazy_eights' || g.id === 'checkers',
  accent: g.id === 'spades' ? 'emerald' : g.id === 'four_in_a_row' ? 'rose' : g.id === 'crazy_eights' ? 'fuchsia' : g.id === 'checkers' ? 'slate' : 'slate',
}));

const GamePickerModal = ({ open, onClose, onSelect }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      data-testid="game-picker-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl"
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Choose a Game</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              All players connected — pick what you want to play.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600"
            data-testid="game-picker-close-btn"
          >
            ×
          </button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {GAME_PICKER_OPTIONS.map((g) => (
            <button
              key={g.id}
              type="button"
              disabled={!g.playable}
              onClick={() => g.playable && onSelect(g.id)}
              data-testid={`game-picker-${g.id}`}
              className={[
                'text-left p-4 rounded-xl border-2 transition-all flex items-start gap-3',
                g.playable
                  ? g.accent === 'emerald'
                    ? 'border-emerald-500 bg-emerald-50 hover:bg-emerald-100 cursor-pointer'
                    : g.accent === 'fuchsia'
                      ? 'border-fuchsia-500 bg-fuchsia-50 hover:bg-fuchsia-100 cursor-pointer'
                      : g.accent === 'slate'
                        ? 'border-slate-500 bg-slate-50 hover:bg-slate-100 cursor-pointer'
                        : 'border-rose-500 bg-rose-50 hover:bg-rose-100 cursor-pointer'
                  : 'border-gray-200 bg-gray-50 opacity-70 cursor-not-allowed',
              ].join(' ')}
            >
              <div className="w-12 h-12 rounded-full bg-white shadow-inner flex items-center justify-center text-2xl flex-shrink-0">
                <span>{g.emoji}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-gray-900">{g.name}</h3>
                  {!g.playable && (
                    <span className="text-[10px] uppercase tracking-wide bg-gray-300 text-gray-700 px-1.5 py-0.5 rounded font-semibold">
                      Coming Soon
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{g.desc}</p>
                <span className="inline-block text-[10px] font-semibold bg-white/60 text-gray-700 px-1.5 py-0.5 rounded mt-1">
                  {g.players}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================

// ============================================================
// SEP 7 2026 — GAME RULES CATALOG (games page). One entry per playable
// game; each `sections` item renders a heading + bullet list in the shared
// GameRulesModal below. Mirrors mobile's src/components/games/gameRules.ts —
// keep the two in sync when rules change.
// ============================================================
const GAME_RULES_CATALOG = {
  spades: {
    title: 'NY Style Spades — Full Rules',
    sections: [
      { h: '🎯 Objective', items: [
        'Teams of 2 — you + your partner (seated across) vs 2 opponents.',
        'First team to reach the target score wins the match and the prize (classic practice plays to 500; live tables set their own target).',
      ]},
      { h: '♠️ Trump Cards (Highest → Lowest)', items: [
        '1. Big Joker (highest card in the game)',
        '2. Little Joker',
        '3. 2♦ — the Two of Diamonds COUNTS AS A SPADE',
        '4. 2♠',
        'Then A♠, K♠, Q♠, J♠, 10♠ … down to 3♠.',
      ]},
      { h: '📖 What is a Book? What is a Bag?', items: [
        'A BOOK (trick): each of the 4 players plays one card; the best card wins all 4 — that pile is one book. 13 books per hand.',
        'A BAG (overtrick): every book your team wins ABOVE your bid. A couple of bags are fine — too many will hurt you (see Scoring).',
        'Getting SET: failing to win at least the number of books your team bid. You LOSE 10 × your bid.',
      ]},
      { h: '🎲 Hand 1 — "Bid Itself"', items: [
        'NO bidding in the first hand — cards are dealt and you play immediately.',
        'Score = 10 points × books won. No bags, no penalties — simple scoring.',
      ]},
      { h: '📋 Bidding (Hand 2+)', items: [
        'Bidding order: the team that plays first — the opponent bids first.',
        'Within a team the partner bids before you, so you always get the final say.',
        'Minimums: each team must bid at least 4, and the table total must be at least 11 — otherwise it is a MISDEAL and everyone bids again.',
      ]},
      { h: '💯 Scoring', items: [
        'MADE your bid (0–2 bags): score 10 × bid, plus 1 point per bag. Example: bid 7, won 9 books → 70 + 2 = +72.',
        'SET (failed the bid OR 3+ bags in one hand): score MINUS 10 × bid. Example: bid 8, won 11 (3 bags) → −80.',
        '⚠️ Three SETs = instant loss of the match.',
        'Bags accumulate across hands — every 10 total bags costs a −100 penalty.',
      ]},
      { h: '🎴 Special Rules', items: [
        'BOSTON — win all 13 books in a hand: +200 bonus points.',
        'ACE CHECK — declared BEFORE bidding (when the table allows it): win the very first book with an Ace for +100; declare it and fail, −100.',
        'BLIND 6+ — only when your team trails by 100+: bid 6 or more BEFORE looking at your cards, and the hand scores DOUBLE (make it or miss it).',
      ]},
      { h: '🔄 Gameplay', items: [
        'Turn order is clockwise.',
        'You MUST follow the suit that was led if you have it.',
        'If 2♦ is led, it is a SPADE lead — you must play trump, not diamonds.',
        'You cannot LEAD spades until spades are broken (someone trumped a book) — unless spades are all you have left.',
        'The book is won by the highest trump played, or the highest card of the led suit if nobody trumped.',
      ]},
    ],
  },
  crazy_eights: {
    title: 'Crazy Eights — Full Rules',
    sections: [
      { h: '🎯 Objective', items: [
        'Empty your hand first. Play a card that matches the discard top by SUIT or RANK. 8s are wild.',
      ]},
      { h: '🔄 On Your Turn', items: [
        'Play a legal card, OR draw a card — if the drawn card is playable you may play it or pass.',
        'You may draw even if you have a playable card (strategic drawing is allowed).',
      ]},
      { h: '🎴 Action Cards (Official GEMS Rules)', items: [
        '2 — DRAW 2. The next player draws 2. Stacks: answer with another 2 (or an Ace) to pass the growing pile on.',
        'A — DRAW 4. The next player draws 4. Stacks with 2s and other Aces — the pile keeps growing until someone eats it.',
        'J — SKIP. The next player loses their turn (2-player: you go again).',
        'Q — REVERSE. The direction of play flips (2-player: you go again).',
        '8 — WILD. Play on anything and choose the new active suit (an 8 as your winning card doesn\'t change suit).',
      ]},
      { h: '🗣️ Last Card', items: [
        'Down to one card? You must call LAST CARD within 5 seconds — miss the call and you draw a 2-card penalty.',
      ]},
      { h: '⚙️ Table Rules (set by the host)', items: [
        'Skip Stacking — Queens can stack skips.',
        'Draw Until Play — no passing: keep drawing until you can play.',
        'Ten Go Again — playing a 10 gives you another turn.',
        'Eight Cancels Stack — an 8 can be played onto a draw-2 pile to cancel it.',
      ]},
    ],
  },
  checkers: {
    title: 'Checkers — Full Rules',
    sections: [
      { h: '🎯 Objective', items: [
        'Capture every opponent piece — or lock them so they have no legal move.',
      ]},
      { h: '🔄 Moving', items: [
        'Pieces move diagonally, one square, on the dark squares only — forward only until crowned.',
        'JUMP an adjacent opponent piece into the empty square beyond to capture it.',
        'Chain jumps: if another jump is available after you land, keep jumping in the same turn.',
      ]},
      { h: '👑 Kings', items: [
        'Reach the far row to crown a KING. Kings move and jump BOTH directions.',
      ]},
      { h: '🏆 Winning', items: [
        'You win when your opponent has no pieces left or no legal moves.',
        'Live tables: entry paid in Emeralds, winner earns the Ruby prize.',
      ]},
    ],
  },
  four_in_a_row: {
    title: '4 In A Row — Full Rules',
    sections: [
      { h: '🎯 Objective', items: [
        'Connect FOUR of your discs in a row — horizontally, vertically, or diagonally — before your opponent.',
      ]},
      { h: '🔄 On Your Turn', items: [
        'Drop one disc into any column — it falls to the lowest empty slot.',
        'Turns alternate. Watch for double threats: two ways to win at once cannot be blocked.',
      ]},
      { h: '🏆 Winning', items: [
        'First to line up four wins the match (and the prize on live tables).',
        'If the board fills with no line of four, the match is a draw.',
      ]},
    ],
  },
};

// Shared games-page rules modal — renders any GAME_RULES_CATALOG entry.
const GameRulesModal = ({ gameId, onClose }) => {
  const rules = gameId ? GAME_RULES_CATALOG[gameId] : null;
  if (!rules) return null;
  return (
    <Dialog open={!!gameId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-gradient-to-br from-gray-900 to-black text-white" data-testid="games-page-rules-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center text-emerald-400">{rules.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-3">
          {rules.sections.map((sec) => (
            <section key={sec.h}>
              <h3 className="text-lg font-bold text-emerald-300 mb-1.5">{sec.h}</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-300 text-sm leading-relaxed">
                {sec.items.map((it, i) => <li key={i}>{it}</li>)}
              </ul>
            </section>
          ))}
        </div>
        <div className="flex justify-center pb-2">
          <Button onClick={onClose} data-testid="games-page-rules-close">Got it</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// GameTile — the tile used on the Games page grid.
// Reads a GAME_CATALOG entry; disables 'soon' status; delegates the
// actual launch to `onPlay(g)` so the page can wire each id to its handler.
// ============================================================
const GameTile = ({ g, onPlay, onRules }) => {
  const badge = STATUS_BADGE[g.status];
  const disabled = g.status === 'soon';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onPlay(g)}
      data-testid={`game-tile-${g.id}`}
      className={[
        'group relative text-left rounded-3xl overflow-hidden ring-1 ring-white/10',
        'shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-transform duration-200',
        disabled ? 'opacity-60 cursor-not-allowed' : 'hover:-translate-y-1.5 hover:shadow-[0_20px_44px_rgba(0,0,0,0.5)]',
      ].join(' ')}
    >
      <div className={`relative h-44 ${g.art ? 'bg-[#07130D]' : `bg-gradient-to-br ${g.gradient}`} flex items-center justify-center`}>
        {g.art ? (
          /* SEP 6 2026: official icon art — object-contain so the WHOLE
             icon shows, never cropped. Emoji fallback below if it 404s. */
          <img
            src={g.art}
            alt={`${g.name} icon`}
            className="h-full w-full object-contain"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <>
            <span className="absolute -right-3 -top-4 text-[7rem] leading-none opacity-20 select-none">{g.icon}</span>
            <span className="text-6xl drop-shadow-lg">{g.icon}</span>
          </>
        )}
        {/* Only 'soon' games show a status badge (LIVE READY/PRACTICE removed). */}
        {g.status === 'soon' && (
          <span className={`absolute top-3 left-3 text-[0.6rem] font-extrabold tracking-wider px-2.5 py-1 rounded-full ${STATUS_BADGE.soon.cls}`}>
            {STATUS_BADGE.soon.label}
          </span>
        )}
        {/* Player-count badge stays on ALL tiles. */}
        <span className="absolute top-3 right-3 text-[0.6rem] font-bold px-2 py-1 rounded-full bg-black/40 text-white/90">{g.players} players</span>
      </div>
      <div className="p-4 bg-[#0F1420]">
        <h3 className="text-lg font-extrabold text-white">{g.name}</h3>
        <p className="text-sm text-white/60">{g.tagline}</p>
        <div className="mt-3 flex items-center justify-between">
          <span className={`inline-flex items-center gap-2 text-sm font-bold ${disabled ? 'text-white/40' : 'text-emerald-300 group-hover:gap-3 transition-all'}`}>
            {g.status === 'soon' ? 'Coming soon' : 'Play now'} {!disabled && <span>→</span>}
          </span>
          {/* SEP 7 2026: per-game Rules button is BACK on every playable
              tile (span, not button — tiles are already <button>s and
              nesting buttons is invalid HTML). */}
          {!disabled && onRules && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onRules(g); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onRules(g); } }}
              className="text-xs font-bold px-3 py-1.5 rounded-full ring-1 ring-white/25 text-white/80 hover:bg-white/10"
              data-testid={`game-rules-btn-${g.id}`}
            >
              📖 Rules
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

// ============================================================
// PracticeTable — shared "green felt" table for solo-vs-AI practice.
// Renders opponents around the top with card-backs + turn glow, a central
// area for played cards / active suit / stock, and the player's hand row.
// (Jul 27, 2026 — used by Crazy Eights practice; Spades practice retrofit
//  planned as a follow-up given GameRoomPage's size.)
// ============================================================
const CardBack = ({ style, size = 'sm' }) => {
  const dims = size === 'lg'
    ? { width: '4.6rem', height: '6.6rem', radius: '1rem' }
    : { width: '1.7rem', height: '2.4rem', radius: '0.4rem' };
  return (
    <div
      className="ring-1 ring-black/40 shadow-md flex items-center justify-center overflow-hidden"
      style={{
        width: dims.width,
        height: dims.height,
        borderRadius: dims.radius,
        background: 'radial-gradient(120% 120% at 30% 20%, #0f5132 0%, #06371f 55%, #041a10 100%)',
        ...style,
      }}
      data-testid="branded-card-back"
    >
      <img src={BRAND_ASSETS.logo} alt="" className="w-3/4 h-3/4 object-contain opacity-95" />
    </div>
  );
};

const OpponentSeat = ({ seat }) => (
  <div
    className={`flex flex-col items-center gap-1 transition ${seat.isCurrentTurn ? 'scale-105' : 'opacity-90'}`}
    data-testid={`opponent-seat-${seat.id}`}
  >
    <div
      className={`relative w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold text-white
                  bg-gradient-to-br from-slate-600 to-slate-800 ring-2
                  ${seat.isCurrentTurn ? 'ring-emerald-400 shadow-[0_0_18px_rgba(52,211,153,0.7)]' : 'ring-white/20'}`}
    >
      {seat.avatar ? (
        <img src={seat.avatar} alt={seat.name} className="w-full h-full rounded-full object-cover" />
      ) : (
        (seat.name?.[0] || '?')
      )}
      {seat.isBot && (
        <span className="absolute -bottom-1 -right-1 text-[9px] bg-purple-600 px-1 rounded">AI</span>
      )}
    </div>
    <div className="text-xs font-semibold text-white/90 max-w-[5rem] truncate">{seat.name}</div>
    <div className="flex" data-testid={`opponent-cards-${seat.id}`}>
      {Array.from({ length: Math.min(seat.cardCount || 0, 8) }).map((_, i) => (
        <CardBack key={i} style={{ marginLeft: i ? '-1.1rem' : 0 }} />
      ))}
    </div>
    {seat.isCurrentTurn && (
      <div className="text-[10px] text-emerald-300 animate-pulse">
        {seat.isBot ? 'thinking…' : 'their turn'}
      </div>
    )}
  </div>
);

const PracticeTable = ({ opponents, centerContent, myHand, myTurn, myName }) => {
  // Round-table seating: distribute opponents around the top / left / right
  // of the felt so it reads like a real card table. The player always sits
  // at the bottom.
  //   1 opponent  → top
  //   2 opponents → left, right
  //   3 opponents → left, top, right
  //   4+          → left, top, top, right, ...
  const n = opponents.length;
  let leftSeats = [], topSeats = [], rightSeats = [];
  if (n === 1) topSeats = opponents;
  else if (n === 2) { leftSeats = [opponents[0]]; rightSeats = [opponents[1]]; }
  else if (n === 3) { leftSeats = [opponents[0]]; topSeats = [opponents[1]]; rightSeats = [opponents[2]]; }
  else {
    leftSeats = [opponents[0]];
    rightSeats = [opponents[n - 1]];
    topSeats = opponents.slice(1, n - 1);
  }
  return (
    <div
      className="relative w-full rounded-3xl p-4 sm:p-6"
      style={{
        background: 'radial-gradient(120% 90% at 50% 40%, #0f5132 0%, #08301f 60%, #052015 100%)',
        boxShadow: 'inset 0 0 60px rgba(0,0,0,0.5)',
      }}
      data-testid="practice-table"
    >
      {/* Top row */}
      {topSeats.length > 0 && (
        <div className="flex justify-center gap-6 sm:gap-12 flex-wrap">
          {topSeats.map((o) => <OpponentSeat key={o.id} seat={o} />)}
        </div>
      )}

      {/* Middle row: left seats · center table · right seats */}
      <div className="mt-3 grid grid-cols-[auto_1fr_auto] items-center gap-3 sm:gap-6">
        <div className="flex flex-col items-center gap-4">
          {leftSeats.map((o) => <OpponentSeat key={o.id} seat={o} />)}
        </div>
        <div className="min-h-[7rem] flex items-center justify-center gap-4" data-testid="practice-table-center">
          {centerContent}
        </div>
        <div className="flex flex-col items-center gap-4">
          {rightSeats.map((o) => <OpponentSeat key={o.id} seat={o} />)}
        </div>
      </div>

      {/* Player seat (bottom) */}
      <div
        className={`mt-4 rounded-2xl px-3 py-2 ${myTurn ? 'ring-2 ring-emerald-400 shadow-[0_0_18px_rgba(52,211,153,0.6)]' : ''}`}
        data-testid="practice-table-my-seat"
      >
        <div className="text-center text-xs text-emerald-200 font-semibold mb-1">
          {myName || 'You'}{myTurn ? ' — your turn' : ''}
        </div>
        {myHand}
      </div>
    </div>
  );
};

// ============================================================
// Crazy Eights — UI helpers (active-suit badge, suit chooser, timer).
// ============================================================
const C8_RULES = [
  'Match the top card by suit or rank.',
  '8s are wild — play anytime, then name the new suit.',
  '2 = next player draws 2 (stackable). Queen = skip. Ace = reverse.',
  "Can't play? Draw one. First to empty their hand wins.",
];

// Named AI opponents (used in Crazy Eights practice; assigned in order).
const C8_AI_NAMES = ['Ace', 'Ruby', 'Deuce', 'Jasmine', 'Marcus', 'Nina'];

// Reusable player-count selector — 11×11 pill row with active state.
const PlayerCountSelector = ({ min, max, value, onChange, note }) => (
  <div>
    <label className="block text-sm font-medium mb-2 text-white/80">Number of players</label>
    <div className="flex gap-2 flex-wrap" data-testid="player-count-selector">
      {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          data-testid={`player-count-${n}`}
          className={`w-11 h-11 rounded-xl font-bold transition ring-1 ${
            value === n
              ? 'bg-emerald-500 text-emerald-950 ring-emerald-300 scale-105'
              : 'bg-white/5 text-white/80 ring-white/15 hover:bg-white/10'
          }`}
        >
          {n}
        </button>
      ))}
    </div>
    {note && <p className="text-xs text-white/50 mt-2">{note}</p>}
  </div>
);
const C8_SUITS = [
  { id: 'spades',   sym: '♠', red: false },
  { id: 'hearts',   sym: '♥', red: true },
  { id: 'diamonds', sym: '♦', red: true },
  { id: 'clubs',    sym: '♣', red: false },
];

const ActiveSuitBadge = ({ suit }) => {
  const s = C8_SUITS.find(x => x.id === suit);
  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur"
      data-testid="c8-active-suit-badge"
    >
      <span className="text-[0.65rem] uppercase tracking-wide text-white/70">Active suit</span>
      <span className={`text-2xl leading-none ${s?.red ? 'text-red-500' : 'text-white'}`}>{s?.sym || '?'}</span>
    </div>
  );
};

const SuitChooser = ({ open, onChoose, onCancel }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-4"
      data-testid="c8-suit-chooser"
      onClick={onCancel}
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-[min(90vw,360px)] text-center">
        <h3 className="text-lg font-bold mb-4">Choose the active suit</h3>
        <div className="grid grid-cols-4 gap-3">
          {C8_SUITS.map((s) => (
            <button
              key={s.id}
              onClick={() => onChoose(s.id)}
              data-testid={`c8-suit-${s.id}`}
              className="aspect-square rounded-xl bg-gray-100 hover:bg-gray-200 active:scale-95 transition flex items-center justify-center text-4xl"
            >
              <span className={s.red ? 'text-red-600' : 'text-black'}>{s.sym}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const TurnTimer = ({ secondsLeft, label }) => (
  <div className="flex items-center gap-2 text-white/90 text-sm">
    <span className="opacity-70">{label}</span>
    <span className={`font-bold tabular-nums ${secondsLeft <= 5 ? 'text-red-400 animate-pulse' : ''}`}>
      {secondsLeft != null ? `${secondsLeft}s` : '—'}
    </span>
  </div>
);

// ============================================================
// Crazy Eights — client rules mirror. Extracted to a shared module
// (`c8_engine.js`) so the golden-transcript test can import the same
// functions the practice-mode UI runs on. Bugs found here MUST be
// fixed in backend/crazy_eights.py FIRST, then ported to c8_engine.js.
// ============================================================
// Constants kept as local aliases so downstream JSX / render code
// isn't rewritten. All logic delegates to the shared module.
const C8_RANKS = C8_RANKS_IMPORT;
const C8_SUIT_IDS = C8_SUIT_IDS_IMPORT;
const C8 = C8_IMPORT;

const c8NewGame = c8NewGameFn;
const c8IsLegal = c8IsLegalFn;
const c8PlayCard = c8PlayCardFn;
const c8DrawCard = c8DrawCardFn;
const c8PassAfterDraw = c8PassAfterDrawFn;
const c8ChooseAISuit = c8ChooseAISuitFn;
const c8AIChoosePlay = c8AIChoosePlayFn;
const c8Current = c8CurrentFn;

// ============================================================
// Part 1 (Feb 19, 2026): Legality on the SERVER public projection.
// The c8_engine.js helpers need the FULL local game object (g.discard,
// g.hands, g.stock) and CRASH on the projection with
//   "undefined is not an object (evaluating 'e.discard.length')"
// (prod ErrorBoundary hit on first render of a dealt live hand).
// Never call engine helpers with live state — use this helper instead.
// ============================================================
function c8IsPlayablePublic(state, card) {
  if (!state || !card) return false;
  const top = state.discard_top;
  const suit = state.active_suit || top?.suit;
  if (state.pending_skip) return card.rank === 'Q';           // skip_stacking
  if ((state.pending_draw || 0) > 0) {
    if (card.rank === '2') return true;
    if (card.rank === '8') return !!(state.rules && state.rules.eight_cancels_stack);
    return false;
  }
  if (card.rank === '8') return true;
  return card.rank === top?.rank || card.suit === suit;
}

// ============================================================
// GameStatusBar (Feb 19, 2026, Part 1) — shared "one bar, every game,
// both viewports". Renders the game's existing turn/suit indicator on
// the left, session-wins chips + a solid red Forfeit button on the
// right. Used by C8, Checkers, 4iaR — desktop band + mobile overlay.
// ============================================================
// ============================================================
// LinkedGameEndOverlay (Feb 19, 2026, Part 3) — shared end overlay for
// every in-live game. Displays winner + session-wins line, offers
// Rematch + Back to Live, and auto-returns after 15s. Used by C8,
// Checkers, and 4iaR — one overlay for all.
// ============================================================
const LinkedGameEndOverlay = ({ winnerName, sessionWinsLine, onRematch, onBackToLive }) => {
  const [secondsLeft, setSecondsLeft] = useState(15);
  useEffect(() => {
    const id = setInterval(() => {
      setSecondsLeft((n) => {
        if (n <= 1) { clearInterval(id); onBackToLive?.(); return 0; }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [onBackToLive]);
  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm pointer-events-auto"
      data-testid="linked-game-end-overlay"
    >
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl px-6 py-5 max-w-sm w-[90%] text-center space-y-3">
        <div className="text-3xl">🏆</div>
        <div className="text-xl font-bold text-white" data-testid="end-overlay-winner">
          {winnerName ? `${winnerName} won!` : 'Game Over'}
        </div>
        {sessionWinsLine && (
          <div className="text-sm text-emerald-400 font-semibold" data-testid="end-overlay-session">
            {sessionWinsLine}
          </div>
        )}
        <div className="flex gap-2 pt-2">
          {onRematch && (
            <button
              onClick={onRematch}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-2 rounded-lg"
              data-testid="rematch-btn"
            >
              Rematch
            </button>
          )}
          <button
            onClick={onBackToLive}
            className="flex-1 bg-slate-700 hover:bg-slate-600 text-white text-sm font-semibold px-3 py-2 rounded-lg"
            data-testid="back-to-live-btn"
          >
            Back to Live
          </button>
        </div>
        <div className="text-xs text-gray-400" data-testid="end-overlay-countdown">
          Returning to live in {secondsLeft}s
        </div>
      </div>
    </div>
  );
};


// ============================================================
// SuggestedFriendsModal (Feb 19, 2026, Onboarding) — post-signup
// "Suggested for you" list. All entries pre-checked; user unchecks any
// they don't want, then Follow → batch follow. Skip is allowed.
// ============================================================
const SuggestedFriendsModal = ({ open, suggestions, checked, onToggle, onFollow, onSkip, busy }) => {
  const count = Object.values(checked || {}).filter(Boolean).length;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onSkip(); }}>
      <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700 text-white" data-testid="suggested-friends-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Suggested for you ✨</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-400 -mt-1">
          Follow a few creators to fill your feed. Uncheck any you'd rather skip.
        </p>
        <div className="max-h-[50vh] overflow-y-auto space-y-1 pr-1">
          {(suggestions || []).map((u) => (
            <button key={u.id} onClick={() => onToggle(u.id)}
              className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-slate-800/70 text-left"
              data-testid="suggested-friend-row">
              {u.picture ? (
                <img src={u.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center font-bold">
                  {(u.name || '?')[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center gap-1">
                  {u.name}{u.is_verified && <span className="text-emerald-400">✔</span>}
                </div>
                <div className="text-xs text-slate-400 truncate">
                  @{u.handle}{u.followers_count ? ` • ${u.followers_count.toLocaleString()} followers` : ''}
                </div>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                checked?.[u.id] ? 'bg-emerald-500 border-emerald-500' : 'border-slate-500'}`}>
                {checked?.[u.id] && <span className="text-white text-xs font-bold">✓</span>}
              </div>
            </button>
          ))}
        </div>
        <Button onClick={onFollow} disabled={busy || count === 0}
          className="w-full bg-emerald-600 hover:bg-emerald-700 font-semibold"
          data-testid="suggested-friends-follow-btn">
          {busy ? 'Following…' : `Follow ${count} creator${count === 1 ? '' : 's'}`}
        </Button>
        <button onClick={onSkip} className="w-full text-sm text-slate-400 hover:text-white py-1"
          data-testid="suggested-friends-skip-btn">Skip for now</button>
      </DialogContent>
    </Dialog>
  );
};


const GameStatusBar = ({ left, players, sessionWins, names, onForfeit, showForfeit = true }) => (
  <div
    className="w-full flex items-center justify-between gap-3 px-3 py-1.5 bg-slate-900/95 border-y border-slate-700"
    data-testid="game-status-bar"
  >
    <div className="flex items-center gap-3 min-w-0">{left}</div>
    <div className="flex items-center gap-2 flex-shrink-0">
      {(players || []).map((p) => (
        <span
          key={p}
          className="text-xs font-semibold text-white bg-slate-700/80 rounded-full px-2.5 py-1"
          data-testid={`session-wins-${p}`}
        >
          {names?.[p] || 'Guest'} <span className="text-emerald-400">{sessionWins?.[p] || 0}</span>
        </span>
      ))}
      {showForfeit && onForfeit && (
        <button
          data-testid="forfeit-game-btn"
          className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-4 py-1.5 rounded-lg shadow-lg"
          onClick={() => { console.log('[Forfeit] GameStatusBar click'); onForfeit(); }}
        >
          Forfeit
        </button>
      )}
    </div>
  </div>
);

// ============================================================
// CrazyEightsBoard — center table (discard/stock/active suit/dir/turn/
// timers), the local hand (via PlayingCard), and the action buttons.
// Consumes public_state broadcasts (live) or the practice hook's state.
// ============================================================
const CrazyEightsBoard = ({ state, meId, timers = {}, onPlay, onDraw, onPass, onDeclareLastCard, onForfeit, sessionWins = {}, playerNames = {}, fanned = false }) => {
  const [suitChooserOpen, setSuitChooserOpen] = useState(false);
  const [pendingEightIndex, setPendingEightIndex] = useState(null);
  if (!state) return null;
  const isMyTurn = state.current_player === meId;
  const iHaveOne = (state.hand_counts?.[meId] || 0) === 1;
  const iAmPending = state.last_card_pending_pid === meId;
  const currentName = playerNames[state.current_player] || (state.current_player || '').slice(0, 6);
  const dirArrow = state.direction === 1 ? '↻' : '↺';
  const facingStack = (state.pending_draw || 0) > 0;
  const engineView = state.__engine_view__ || state;
  // Part 1 (Feb 19, 2026): live path uses the projection-safe helper;
  // practice path keeps its full-object c8_engine.js check.
  const isCardPlayable = (card) => state.__engine_view__
    ? c8IsLegal(engineView, card)
    : c8IsPlayablePublic(state, card);

  const handleCardClick = (card, idx) => {
    if (!isMyTurn) return;
    const wouldWin = (state.hand_counts?.[meId] || 0) === 1;
    if (card.rank === '8' && !wouldWin) { setPendingEightIndex(idx); setSuitChooserOpen(true); return; }
    onPlay(idx, null);
  };
  const handleSuitChosen = (suit) => {
    setSuitChooserOpen(false);
    if (pendingEightIndex != null) { onPlay(pendingEightIndex, suit); setPendingEightIndex(null); }
  };

  return (
    <div className="relative w-full h-full flex flex-col" data-testid="c8-board">
      {/* Part 5b (Feb 19, 2026): if any required projection field is
          missing (early snapshot race, WS ordering), render a calm
          "Syncing…" placeholder so the whole board never blanks out to
          the ErrorBoundary. */}
      {(!state || !Array.isArray(state.players) || !state.hand_counts ||
        !state.my_hand || !state.discard_top) ? (
        <div
          className="flex-1 flex items-center justify-center text-white/70 text-sm"
          data-testid="c8-syncing-placeholder"
        >
          Syncing game…
        </div>
      ) : (
        <>
      <GameStatusBar
        players={state.players}
        sessionWins={sessionWins}
        names={playerNames}
        onForfeit={!state.winner ? onForfeit : null}
        left={(
          <>
            <ActiveSuitBadge suit={state.active_suit} />
            <span className="text-white/80 text-sm">
              <span className="opacity-60">Turn:</span>{' '}
              <span className="font-bold">{currentName}</span>{' '}
              <span className="opacity-60">{dirArrow}</span>
            </span>
            {timers.turn != null && <TurnTimer secondsLeft={timers.turn} label="Turn" />}
            {timers.suit != null && <TurnTimer secondsLeft={timers.suit} label="Suit" />}
            {timers.stack != null && <TurnTimer secondsLeft={timers.stack} label="Stack" />}
          </>
        )}
      />

      {(() => {
        // Part 2d (Feb 19, 2026): compute "no playable card" state so the
        // deck can pulse + the drawn-playable card can lift + a Pass
        // button can appear beside the deck.
        const myHand = state.my_hand || [];
        const hasAnyPlayable = myHand.some((c) => isCardPlayable(c));
        const stuckOnTurn = isMyTurn && !hasAnyPlayable && state.state !== 'AWAITING_PLAY_DRAWN';
        const drawnPlayable = state.state === 'AWAITING_PLAY_DRAWN' && isMyTurn;
        return (
      <div className="flex-1 flex items-center justify-center gap-6 relative px-4 py-6 overflow-visible">
        <div className="flex flex-col items-center gap-2">
          {/* Part 2a (Feb 19, 2026): stock deck is a real button. Click
              sends c8_draw. Pulses when the player has no legal card. */}
          <button
            type="button"
            onClick={() => { console.log('[C8] draw click'); if (typeof onDraw === 'function') onDraw(); }}
            disabled={!isMyTurn || state.winner}
            data-testid="c8-stock-btn"
            className={`relative rounded-lg pointer-events-auto ${
              stuckOnTurn ? 'ring-4 ring-yellow-400 animate-pulse' : ''
            } ${isMyTurn && !state.winner ? 'cursor-pointer hover:ring-2 hover:ring-emerald-400' : 'cursor-not-allowed opacity-70'}`}
            aria-label="Draw a card"
          >
            <CardBack size="lg" />
          </button>
          <span className="text-white/70 text-xs" data-testid="c8-stock-count">Stock: {state.stock_count}</span>
          {stuckOnTurn && (
            <span className="text-yellow-300 text-xs font-semibold text-center max-w-[10rem]" data-testid="c8-no-playable-hint">
              No playable card — tap the deck to draw
            </span>
          )}
          {drawnPlayable && (
            <button
              type="button"
              onClick={() => { console.log('[C8] pass click'); if (typeof onPass === 'function') onPass(); }}
              data-testid="c8-pass-btn"
              className="mt-1 bg-slate-700 hover:bg-slate-600 text-white text-xs font-semibold px-3 py-1 rounded-md pointer-events-auto"
            >
              Pass
            </button>
          )}
        </div>
        <div className="flex flex-col items-center gap-2" data-testid="c8-discard-top">
          {state.discard_top && (
            <PlayingCard
              card={{ suit: state.discard_top.suit, value: state.discard_top.rank }}
              playable={false}
              isTrump={false}
              onPlay={() => {}}
            />
          )}
          {facingStack && (
            <span className="text-red-300 text-xs font-bold" data-testid="c8-pending-draw">
              +{state.pending_draw} pending
            </span>
          )}
          {drawnPlayable && (
            <span className="text-emerald-300 text-xs font-semibold text-center max-w-[10rem]" data-testid="c8-drawn-playable-hint">
              You drew a playable card — tap it to play, or Pass
            </span>
          )}
        </div>
      </div>
        );
      })()}

      {state.winner && (
        <div className="absolute inset-0 z-[110] bg-black/80 flex items-center justify-center" data-testid="c8-win-overlay">
          <div className="bg-white rounded-2xl p-8 text-center max-w-sm">
            <div className="text-5xl mb-3">🏆</div>
            <h2 className="text-2xl font-extrabold text-gray-900">
              {state.winner === meId ? 'You won!' : `${playerNames[state.winner] || state.winner.slice(0, 8)} won!`}
            </h2>
          </div>
        </div>
      )}

      <div className="pb-3">
        {(state.my_hand?.length || 0) > 0 && (
          fanned ? (
            <FannedHand
              cards={state.my_hand}
              disabled={!isMyTurn}
              cardKey={(_c, i) => i}
              onPlay={(card) => {
                const idx = state.my_hand.findIndex(
                  (c) => c.suit === card.suit && c.rank === card.rank,
                );
                if (idx >= 0) handleCardClick(card, idx);
              }}
              renderCard={(card) => (
                <PlayingCard
                  card={{ suit: card.suit, value: card.rank }}
                  playable={isMyTurn && isCardPlayable(card)}
                  notMyTurn={!isMyTurn}
                  isTrump={false}
                  onPlay={() => {}}
                />
              )}
            />
          ) : (
            <div className="flex justify-center items-end pl-3 pr-1 pb-2 overflow-x-auto" data-testid="c8-hand-row">
              {state.my_hand.map((card, idx) => (
                <PlayingCard
                  key={idx}
                  card={{ suit: card.suit, value: card.rank }}
                  playable={isMyTurn && isCardPlayable(card)}
                  notMyTurn={!isMyTurn}
                  isTrump={false}
                  onPlay={() => handleCardClick(card, idx)}
                />
              ))}
            </div>
          )
        )}
        <div className="flex items-center justify-center gap-3 mt-2">
          <button
            type="button"
            disabled={!isMyTurn || state.state === 'AWAITING_PLAY_DRAWN'}
            onClick={onDraw}
            data-testid="c8-draw-btn"
            className={[
              'px-4 py-2 rounded-xl text-sm font-bold',
              (isMyTurn && state.state !== 'AWAITING_PLAY_DRAWN')
                ? 'bg-white text-gray-900 hover:bg-gray-100'
                : 'bg-white/20 text-white/40 cursor-not-allowed',
            ].join(' ')}
          >
            {facingStack ? `Draw +${state.pending_draw}` : 'Draw'}
          </button>
          {state.state === 'AWAITING_PLAY_DRAWN' && isMyTurn && (
            <button
              type="button"
              onClick={onPass}
              data-testid="c8-pass-btn"
              className="px-4 py-2 rounded-xl text-sm font-bold bg-amber-400 text-amber-950 hover:bg-amber-300"
            >
              Pass
            </button>
          )}
          {iHaveOne && iAmPending && !state.winner && (
            <button
              type="button"
              onClick={onDeclareLastCard}
              data-testid="c8-last-card-btn"
              className="px-4 py-2 rounded-xl text-sm font-extrabold bg-rose-500 text-white hover:bg-rose-400 animate-pulse"
            >
              Last Card! {timers.last_card != null && `(${timers.last_card}s)`}
            </button>
          )}
        </div>
      </div>

      <SuitChooser
        open={suitChooserOpen}
        onChoose={handleSuitChosen}
        onCancel={() => { setSuitChooserOpen(false); setPendingEightIndex(null); }}
      />
        </>
      )}
    </div>
  );
};

// Practice hook — drives a local game + AI opponents for the Games page.
// Supports 1-3 AI opponents (default 3, so a 4-handed hand can be verified).
const useCrazyEightsPractice = (meId = 'human', aiIds = ['cpu1', 'cpu2', 'cpu3']) => {
  const [game, setGame] = useState(() => c8NewGame([meId, ...aiIds]));
  const publicState = React.useMemo(() => ({
    players: game.players,
    hand_counts: Object.fromEntries(game.players.map(p => [p, game.hands[p].length])),
    my_hand: game.hands[meId] || [],
    discard_top: game.discard[game.discard.length - 1],
    active_suit: game.active_suit,
    stock_count: game.stock.length,
    current_player: game.players[game.current_index],
    direction: game.direction,
    pending_draw: game.pending_draw,
    state: game.state,
    winner: game.winner,
    last_card_pending_pid: game.last_card_pending_pid,
    __engine_view__: game,
  }), [game, meId]);

  const cloneAndSet = (mutate) => setGame((g) => { const copy = JSON.parse(JSON.stringify(g)); mutate(copy); return copy; });
  const play = (idx, chosenSuit) => cloneAndSet((g) => c8PlayCard(g, meId, idx, chosenSuit));
  const draw = () => cloneAndSet((g) => c8DrawCard(g, meId));
  const pass = () => cloneAndSet((g) => c8PassAfterDraw(g, meId));
  const declareLastCard = () => cloneAndSet((g) => { if (g.last_card_pending_pid === meId && g.hands[meId].length === 1) g.last_card_declared_pid = meId; });
  const reset = () => setGame(c8NewGame([meId, ...aiIds]));

  React.useEffect(() => {
    if (game.state === 'GAME_OVER') return;
    const cur = c8Current(game);
    if (cur === meId) return; // Wait for the human.
    // Watchable AI: ~800-1200ms between moves.
    const delay = 800 + Math.floor(Math.random() * 400);
    const t = setTimeout(() => {
      cloneAndSet((g) => {
        const aiId = c8Current(g);
        const decision = c8AIChoosePlay(g, aiId);
        if (decision.action === 'draw') {
          const r = c8DrawCard(g, aiId);
          if (r.ok && g.state === 'AWAITING_PLAY_DRAWN') {
            const d = c8AIChoosePlay(g, aiId);
            if (d.action === 'play') c8PlayCard(g, aiId, d.card_index, d.chosen_suit);
            else c8PassAfterDraw(g, aiId);
          }
        } else {
          c8PlayCard(g, aiId, decision.card_index, decision.chosen_suit);
          if (g.hands[aiId].length === 1) g.last_card_declared_pid = aiId;
        }
      });
    }, delay);
    return () => clearTimeout(t);
  }, [game, meId]);

  return { state: publicState, play, draw, pass, declareLastCard, reset };
};

const LivePage = () => {
  const { user, setUser } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  const [currentStream, setCurrentStream] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  // Round 13: blocked users never reach the broadcaster's chat
  const liveCtx = React.useContext(AuthContext) || {};
  const liveHiddenRef = useRef(new Set());
  useEffect(() => { liveHiddenRef.current = liveCtx.hiddenIds || new Set(); }, [liveCtx.hiddenIds]);
  // Canonical rake/economy rates from /api/economy/rates. Used by every
  // game-entry preview so the winner/fee split always matches the
  // backend (RAKE_1V1 = 8%). Falls back to 0.08 if the fetch is still in
  // flight so first render never shows a stale hardcoded 20%/10%.
  const [economyRates, setEconomyRates] = useState(null);
  const [lookingToPlay, setLookingToPlay] = useState(false);
  const [streamTitle, setStreamTitle] = useState('');  // Empty - let streamer choose their own title
  // Pre-live cover photo (Bigo-style). Prefilled from user.live_cover below.
  const [coverUrl, setCoverUrl] = useState('');
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showGameOptionsModal, setShowGameOptionsModal] = useState(false);
  // Which game the "Choose Game Mode" modal is currently opened for.
  // Drives title/copy + routing of Practice-with-AI. (Jul 27, 2026)
  const [selectedGameForModal, setSelectedGameForModal] = useState('spades');
  // In-stream game picker (July 26, 2026) — opens after all invited
  // broadcasters accept/join so host chooses which game to play. The
  // selection flows into handleSetWagerFromLiveStream via selectedGameType.
  const [showGamePickerModal, setShowGamePickerModal] = useState(false);
  const [selectedGameType, setSelectedGameType] = useState('spades');
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [showTournamentsModal, setShowTournamentsModal] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [inviteGemId, setInviteGemId] = useState('');
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteGameId, setInviteGameId] = useState(null);
  const [invitedPlayers, setInvitedPlayers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loadingOnlineUsers, setLoadingOnlineUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  
  // Check for join stream from invitation
  const [joiningStreamId, setJoiningStreamId] = useState(location.state?.joinStreamId || null);
  // Live TYPE picked on Discover ('go_live' | 'irl' | 'audio_live'). Passed
  // through to /streams/create so the stream doc knows what kind it is.
  const [liveType] = useState(location.state?.live_type || 'go_live');
  // Round 63: how many may link — the SESSION's cap once the server has said
  // (roster frames carry max_broadcasters), else by my own live type: a Party
  // seats 16, any other link 6.
  const [linkCap, setLinkCap] = useState(null);
  const maxLinkPartners = (linkCap || (liveType === 'party' ? 16 : 6)) - 1;
  
  // Share Stream Modal State
  const [showShareStreamModal, setShowShareStreamModal] = useState(false);
  
  // Messages while live modal
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  
  // Private Beta Mode - state for restriction modal
  const [showBetaModal, setShowBetaModal] = useState(false);
  
  // Matchmaking state
  const [isSearchingForMatch, setIsSearchingForMatch] = useState(false);
  const [matchmakingStatus, setMatchmakingStatus] = useState(null);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [selectedGift, setSelectedGift] = useState(null);
  const [showWagerModal, setShowWagerModal] = useState(false);
  const [selectedGameMode, setSelectedGameMode] = useState(null);
  const [wagerAmount, setWagerAmount] = useState(50);
  const [allowBlindSix, setAllowBlindSix] = useState(true);
  const [allowAceCheck, setAllowAceCheck] = useState(true);
  // Round 10: 'Play to' — match target score (NY tables play to 250 / 300 / 500).
  const [spadesTargetScore, setSpadesTargetScore] = useState(250);
  
  // Live-stream handoff — Resume banner state. Populated from
  // GET /streams/mine-active on LivePage mount. Powers the "You're still
  // LIVE — Resume broadcast" banner shown after a device switch inside the
  // 30s broadcaster-disconnect grace window.
  const [mineActive, setMineActive] = useState(null);
  const [resumingLive, setResumingLive] = useState(false);

  // Agora streaming for publisher
  const [agoraToken, setAgoraToken] = useState(null);
  const [agoraChannel, setAgoraChannel] = useState(null);
  const [agoraAppId, setAgoraAppId] = useState(null);
  const [useAgora, setUseAgora] = useState(false);
  const [realTimeViewerCount, setRealTimeViewerCount] = useState(0);
  
  // Shared Agora tracks - created ONCE and shared across all views
  const [sharedAgoraTracks, setSharedAgoraTracks] = useState(null);
  const agoraClientRef = useRef(null);
  // Link v2 (LiveKit multi-broadcaster). When LIVEKIT_LINKS is true, linked
  // sessions publish via a shared LiveKit room instead of the Agora mesh.
  // linkRoomInfo holds { token, roomName, url }; linkRoomRef holds the live Room.
  const [linkRoomInfo, setLinkRoomInfo] = useState(null);
  const linkRoomRef = useRef(null);
  // Mirror of linkRoomInfo for use in async closures / dedupe guards.
  const linkRoomInfoRef = useRef(null);
  useEffect(() => { linkRoomInfoRef.current = linkRoomInfo; }, [linkRoomInfo]);
  // Checkpoint 3: expose the ref on window so the module-level meshDebugLog
  // helper (defined before this component) can compare each handler's
  // `client` argument to the *current* agoraClientRef.current. If they
  // disagree, the singleton-guard tear-down theory is confirmed live.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__GEMS_AGORA_CLIENT_REF = agoraClientRef;
    }
    return () => {
      if (typeof window !== 'undefined' && window.__GEMS_AGORA_CLIENT_REF === agoraClientRef) {
        delete window.__GEMS_AGORA_CLIENT_REF;
      }
    };
  }, []);

  // Fetch canonical economy rates once so game-entry previews (Spades,
  // 4-in-a-Row, 1v1 challenges) show the real rake (RAKE_1V1) instead
  // of hardcoded 20% / 10% numbers that could drift from the backend.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/economy/rates`);
        if (!cancelled) setEconomyRates(resp.data);
      } catch (err) {
        // Non-fatal — previews fall back to the 0.08 default.
        console.error('Failed to fetch economy rates in LivePage:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // Regression F (May 8 testing): suppresses concurrent Agora-client init
  // attempts. Two events (link-live-accepted + broadcaster_joined_session)
  // can fire on a single accept; without this guard each path runs its own
  // createClient → join → publish, double-publishing local tracks. Holds
  // the channel currently being initialised (or null when idle).
  const agoraInitInProgressRef = useRef(null);
  // Bug 3: guards every Agora-link entry point against duplicate
  // live_invite events that slip past the WS-level dedupe.
  const agoraLinkInProgressRef = useRef(false);
  const pendingAcceptsRef = useRef([]);
  
  // Remote Agora users - tracks from other broadcasters in multiplayer
  // Maps UID to { videoTrack, audioTrack, uid }
  const [remoteAgoraUsers, setRemoteAgoraUsers] = useState({});

  // ============================================================================
  // CHECKPOINT 4 (May 14, 2026) — UNIFIED AGORA CLIENT FACTORY
  // ============================================================================
  // ONE function that owns every Agora client lifecycle: create, leave previous
  // (only on channel change — see below), wire handlers, join, subscribe to
  // pre-existing publishers with PLACEHOLDER identity. All 6 prior createClient
  // sites in LivePage call into this — the duplication that produced repeated
  // mesh / mislabel regressions is gone.
  //
  // SINGLETON SEMANTICS — the May-12 mesh bug root cause:
  //   The pre-fix BugF guard called `prev.leave()` UNCONDITIONALLY on every
  //   createClient. When a second link event for the SAME channel arrived
  //   (e.g. broadcaster_joined_session fires right after link-live-accepted),
  //   the second init tore down the still-active client mid-stream, killing
  //   B's subscriptions to A right when C joined. That is the actual cause
  //   of "B and C see only A and self".
  //
  //   Fix: leave previous ONLY if it's joined to a DIFFERENT channel. Same
  //   channel → reuse the existing client (no-op). This preserves active
  //   subscriptions when a duplicate init fires.
  //
  // EXISTING-USER SUBSCRIPTION — guarantees N-1 remote tiles for late joiners:
  //   After client.join(), we walk client.remoteUsers and explicitly subscribe
  //   to each pre-existing publisher. Identity is set as a PLACEHOLDER
  //   {id:null, name:null, agoraUid} — never the latest partner's name, which
  //   was the source of the "Broadcaster 3" mislabel.
  // ============================================================================
  const createAndJoinAgoraChannel = useCallback(async ({
    AgoraRTC,
    channelName,
    token,
    appId,
    uid,
    onUserLeftToast = null,
  }) => {
    if (!AgoraRTC || !channelName || !token || !appId) {
      throw new Error('[AgoraFactory] missing required args: AgoraRTC, channelName, token, appId');
    }

    // 1. Singleton: leave previous client ONLY if it's joined to a different
    //    channel. Same-channel duplicate inits return the existing client.
    const prevClient = agoraClientRef.current;
    if (prevClient) {
      const prevChannel = prevClient.channelName;
      if (prevChannel && prevChannel === channelName) {
        console.log('♻️ [AgoraFactory] reusing existing client on same channel:', channelName);
        return prevClient;
      }
      try {
        await prevClient.leave();
        console.log('🔁 [AgoraFactory] left previous client (channel changed', prevChannel || '<unjoined>', '→', channelName, ')');
      } catch (e) {
        console.warn('[AgoraFactory] previous-client leave failed (ignored):', e?.message);
      }
    }

    // 2. Create new client
    const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });

    // ============================================================
    // MESH-REFACTOR (May 16, 2026): IDEMPOTENT subscribeAndStore
    // ============================================================
    // Pre-refactor, TWO independent paths called client.subscribe on the
    // same uid+mediaType:
    //   (a) the live `user-published` handler
    //   (b) the post-join client.remoteUsers walk
    // The 2nd would throw "INVALID_OPERATION: already subscribed", the
    // catch swallowed it, and the track was never stored → permanent
    // "Connecting…" tile. Both paths now converge on this helper:
    //   - Detect already-subscribed via uid presence in client.remoteUsers
    //     AND whether the relevant *Track field is already populated.
    //   - Subscribe only if needed.
    //   - ALWAYS read the track from remoteUser AFTER, never rely on
    //     subscribe's resolution as the only write path.
    //   - ALWAYS store into remoteAgoraUsers.
    // ============================================================
    const subscribeAndStore = async (remoteUser, mediaType) => {
      if (!remoteUser || (mediaType !== 'video' && mediaType !== 'audio')) return;
      // Self-echo filter (Bug 5): never subscribe to our own UID.
      if (remoteUser.uid === uidFromUserId(__GEMS_CURRENT_USER_ID)) {
        console.log('🚫 [subscribeAndStore][Bug5] ignoring self-echo uid', remoteUser.uid);
        return;
      }
      const alreadyHasTrack = mediaType === 'video'
        ? !!remoteUser.videoTrack
        : !!remoteUser.audioTrack;
      if (!alreadyHasTrack) {
        try {
          await client.subscribe(remoteUser, mediaType);
        } catch (err) {
          const msg = err?.message || String(err);
          // INVALID_OPERATION is the canonical "already subscribed" — fall
          // through to the read-from-remoteUser path. Any OTHER throw we
          // surface but still try to store whatever track is present.
          if (!/INVALID_OPERATION|already.*subscribed/i.test(msg)) {
            console.error('[subscribeAndStore] subscribe threw for uid', remoteUser?.uid, mediaType, err);
          } else {
            console.log('[subscribeAndStore] already subscribed (ok)', remoteUser?.uid, mediaType);
          }
        }
      }
      // Read the track AFTER subscribe (or fall-through). This is the
      // single write site for remoteAgoraUsers in the subscribe pipeline.
      if (mediaType === 'video') {
        setRemoteAgoraUsers(prev => ({
          ...prev,
          [remoteUser.uid]: {
            ...prev[remoteUser.uid],
            videoTrack: remoteUser.videoTrack || prev[remoteUser.uid]?.videoTrack || null,
            uid: remoteUser.uid,
          },
        }));
        // Placeholder identity — resolved later via cohostsByUid map.
        setConnectedCoHosts(prev => {
          if (prev.find(ch => ch.agoraUid === remoteUser.uid)) return prev;
          return [...prev, { id: null, name: null, agoraUid: remoteUser.uid }];
        });
      } else {
        // audio
        if (remoteUser.audioTrack) {
          try {
            remoteUser.audioTrack.setVolume(100);
            await remoteUser.audioTrack.play();
          } catch (audioErr) {
            setTimeout(() => { try { remoteUser.audioTrack?.play(); } catch { /* noop */ } }, 500);
          }
        }
        setRemoteAgoraUsers(prev => ({
          ...prev,
          [remoteUser.uid]: {
            ...prev[remoteUser.uid],
            audioTrack: remoteUser.audioTrack || prev[remoteUser.uid]?.audioTrack || null,
            uid: remoteUser.uid,
          },
        }));
      }
    };

    // 3. Register handlers BEFORE join so user-published can replay for
    //    pre-existing publishers (on SDK versions that support it).
    // ══════════════════════════════════════════════════════════════════
    // ⛔ DO NOT REFACTOR — INTENTIONAL, BREAKS LIVE MESH
    // ══════════════════════════════════════════════════════════════════
    // These Agora handlers (user-published / user-unpublished / user-left)
    // and the sibling copies in AgoraStreamViewer.js, AgoraVideoGrid.js
    // and AgoraMultiplayerViewer.js are load-bearing for the 2–6
    // broadcaster mesh. Six rounds of debugging landed on this exact
    // shape — every previous "consolidation / dedupe / extraction"
    // attempt broke live streaming. If you are reading this and think
    // this looks like tech debt: you are wrong for this codebase.
    // Leave the handlers exactly as they are. See PRD.md → "🛑 Do Not
    // Touch (Agora Mesh)" section for the full rationale.
    // ══════════════════════════════════════════════════════════════════
    client.on('user-published', async (remoteUser, mediaType) => {
      meshDebugLog('user-published', client, remoteUser, mediaType);
      await subscribeAndStore(remoteUser, mediaType);
    });

    client.on('user-unpublished', (remoteUser, mediaType) => {
      // MESH-REFACTOR: GHOST EVICTION
      // When BOTH tracks become null we delete the row entirely — no more
      // null-ghost rows lingering in remoteAgoraUsers and getting mislabeled
      // by stale cohostsByUid bindings.
      setRemoteAgoraUsers(prev => {
        const cur = prev[remoteUser.uid];
        if (!cur) return prev;
        const updated = { ...prev };
        const nextEntry = { ...cur };
        if (mediaType === 'video') nextEntry.videoTrack = null;
        if (mediaType === 'audio') nextEntry.audioTrack = null;
        if (!nextEntry.videoTrack && !nextEntry.audioTrack) {
          delete updated[remoteUser.uid];
          console.log(`👻 [subscribeAndStore] ghost-evicted uid ${remoteUser.uid} (both tracks null)`);
        } else {
          updated[remoteUser.uid] = nextEntry;
        }
        return updated;
      });
    });

    client.on('user-left', (remoteUser) => {
      // Immediate UID removal — no wait for unpublish events.
      setRemoteAgoraUsers(prev => {
        if (!(remoteUser.uid in prev)) return prev;
        const updated = { ...prev };
        delete updated[remoteUser.uid];
        return updated;
      });
      setConnectedCoHosts(prev => {
        const remaining = prev.filter(ch => ch.agoraUid !== remoteUser.uid);
        if (remaining.length === 0 && client._gemsRefreshInterval) {
          clearInterval(client._gemsRefreshInterval);
          client._gemsRefreshInterval = null;
        }
        return remaining;
      });
      if (onUserLeftToast) toast.info(onUserLeftToast);
    });

    // Generic connection-state-change recovery toasts (previously only one of
    // the 6 sites wired this; now all sessions benefit).
    client.on('connection-state-change', (curState, prevState, reason) => {
      console.log(`📡 [AgoraFactory] connection: ${prevState} → ${curState}${reason ? ` (${reason})` : ''}`);
      if (curState === 'DISCONNECTED' && reason === 'NETWORK_ERROR') {
        toast.info('Connection lost, reconnecting…');
      }
      if (curState === 'CONNECTED' && prevState === 'RECONNECTING') {
        toast.success('Connection restored');
      }
    });

    // 4. Adopt the client BEFORE join() so mesh-debug clientRefSame is accurate
    //    even if a parallel handler fires during the join handshake.
    agoraClientRef.current = client;

    // 5. Join the channel.
    await client.join(appId, channelName, token, uid);
    console.log('✅ [AgoraFactory] joined channel', channelName, 'uid', uid);

    // 6. Walk pre-existing remoteUsers via the SAME subscribeAndStore helper
    //    that handles live `user-published` events — guarantees no
    //    double-subscribe throw races and no lost track-store on the
    //    catch path.
    const existing = client.remoteUsers || [];
    for (const remoteUser of existing) {
      if (remoteUser.uid === uidFromUserId(__GEMS_CURRENT_USER_ID)) continue;
      if (remoteUser.hasAudio) {
        await subscribeAndStore(remoteUser, 'audio');
      }
      if (remoteUser.hasVideo) {
        await subscribeAndStore(remoteUser, 'video');
      }
    }

    return client;
  }, []);  // factory has zero external deps — all closures captured at call time

  // ========================================================
  // AGORA FULL CLEANUP UTILITY
  // Ensures ALL audio/video is stopped, all subscriptions removed,
  // all intervals cleared, and all state reset.
  // Called on: end stream, end link, disconnect, navigate away
  // ========================================================
  const fullStreamCleanup = useCallback(async (reason = 'unknown') => {
    console.log(`🧹 FULL STREAM CLEANUP - Reason: ${reason}`);

    // Link v2: tear down the LiveKit linked room if one is active (no-op on
    // the Agora path). Runs first so it covers both the no-client early return
    // and the full cleanup path below.
    if (linkRoomRef.current) {
      try { linkRoomRef.current.disconnect(); } catch {}
      linkRoomRef.current = null;
    }
    setLinkRoomInfo(null);
    // Part 4 (Feb 19, 2026): reset the per-link session win counter on
    // every link teardown path.
    setSessionWins({});

    const client = agoraClientRef.current;
    if (!client) {
      console.log('No Agora client to clean up');
      // Still reset state even without client
      setSharedAgoraTracks(null);
      setAgoraToken(null);
      setAgoraChannel(null);
      setAgoraAppId(null);
      setUseAgora(false);
      setConnectedCoHosts([]);
      setRemoteAgoraUsers({});
      setActiveLinkedGame(null);
      setLinkedGameOpponent(null);
      return;
    }
    
    // STEP 1: Unpublish local tracks
    try {
      const tracksToUnpublish = [];
      if (sharedAgoraTracks?.audioTrack) tracksToUnpublish.push(sharedAgoraTracks.audioTrack);
      if (sharedAgoraTracks?.videoTrack) tracksToUnpublish.push(sharedAgoraTracks.videoTrack);
      if (tracksToUnpublish.length > 0) {
        await client.unpublish(tracksToUnpublish);
        console.log('✅ Unpublished local tracks');
      }
    } catch (e) { console.log('Unpublish error (ignored):', e.message); }
    
    // STEP 2: Stop and close local tracks
    try {
      if (sharedAgoraTracks?.audioTrack) {
        sharedAgoraTracks.audioTrack.stop();
        sharedAgoraTracks.audioTrack.close();
      }
      if (sharedAgoraTracks?.videoTrack) {
        sharedAgoraTracks.videoTrack.stop();
        sharedAgoraTracks.videoTrack.close();
      }
      console.log('✅ Local tracks stopped and closed');
    } catch (e) { console.log('Track close error (ignored):', e.message); }
    
    // STEP 3: Unsubscribe from ALL remote users (stops their audio/video)
    try {
      const remoteUsers = client.remoteUsers || [];
      for (const remoteUser of remoteUsers) {
        try {
          if (remoteUser.audioTrack) {
            remoteUser.audioTrack.stop();
            await client.unsubscribe(remoteUser, 'audio');
          }
          if (remoteUser.videoTrack) {
            await client.unsubscribe(remoteUser, 'video');
          }
        } catch (e) { /* ignore per-user errors */ }
      }
      console.log(`✅ Unsubscribed from ${remoteUsers.length} remote users`);
    } catch (e) { console.log('Remote cleanup error (ignored):', e.message); }
    
    // STEP 4: Leave the channel
    try {
      await client.leave();
      console.log('✅ Left Agora channel');
    } catch (e) { console.log('Leave error (ignored):', e.message); }
    
    // STEP 5: Clear token refresh interval
    if (client._gemsRefreshInterval) {
      clearInterval(client._gemsRefreshInterval);
      client._gemsRefreshInterval = null;
      console.log('✅ Cleared token refresh interval');
    }
    
    // STEP 6: Null out the client reference
    agoraClientRef.current = null;
    
    // STEP 7: Reset ALL related state
    setSharedAgoraTracks(null);
    setAgoraToken(null);
    setAgoraChannel(null);
    setAgoraAppId(null);
    setUseAgora(false);
    setConnectedCoHosts([]);
    setRemoteAgoraUsers({});
    setActiveLinkedGame(null);
    setLinkedGameOpponent(null);
    setSharedChatStreamId(null);
    setInvitedPlayers([]);
    
    console.log('✅ FULL STREAM CLEANUP COMPLETE');
  }, [sharedAgoraTracks]);
  
  // ========================================================
  // CO-HOST INVITE STATE MANAGEMENT
  // ========================================================
  
  // State for invites I've SENT (as host)
  const [sentCoHostInvites, setSentCoHostInvites] = useState([]); // { recipientId, recipientName, status: 'pending'|'accepted'|'declined', timestamp }
  const [coHostInviteStatus, setCoHostInviteStatus] = useState(null); // 'sending' | 'sent' | 'accepted' | 'declined' | 'busy' | 'expired' | null
  
  // State for invites I've RECEIVED - shown via WebSocket or polling
  const [incomingLiveInvite, setIncomingLiveInvite] = useState(null);
  const [showLiveInvitePopup, setShowLiveInvitePopup] = useState(false);
  
  // Connected linked hosts (after invite accepted)
  const [connectedCoHosts, setConnectedCoHosts] = useState([]); // { id, name, agoraUid }
  
  // Multi-party linked session tracking
  const [linkedSessionId, setLinkedSessionId] = useState(null);  // Shared Agora channel for all linked broadcasters

  // Viewport tracking (Aug 18, 2026) — lg breakpoint (1024px). Board games
  // use `sides` layout on desktop and `strip` on mobile.
  const [isDesktopViewport, setIsDesktopViewport] = useState(
    () => (typeof window !== 'undefined') && window.matchMedia('(min-width: 1024px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const fn = (e) => setIsDesktopViewport(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  const [allLinkedUsers, setAllLinkedUsers] = useState([]);  // All user IDs in current linked session
  const [allLinkedUserNames, setAllLinkedUserNames] = useState([]);  // Parallel array of names for LiveKitLinkGrid labels
  // SEP 3 2026 (missed-invite fix): live mirror of the roster pair so the
  // once-registered link_started / broadcaster_joined handlers can MERGE
  // into current state instead of replacing it with a possibly-stale
  // payload. Union only — removals flow through broadcaster_left /
  // link_ended, and the 15s stream-doc poll reconciles anything residual.
  const linkedRosterRef = useRef({ users: [], names: [] });
  useEffect(() => {
    linkedRosterRef.current = { users: allLinkedUsers || [], names: allLinkedUserNames || [] };
  }, [allLinkedUsers, allLinkedUserNames]);
  const mergeLinkedRoster = useCallback((nextUsers, nextNames) => {
    const cur = linkedRosterRef.current || { users: [], names: [] };
    const nameOf = new Map();
    (cur.users || []).forEach((u, i) => { if (cur.names?.[i]) nameOf.set(u, cur.names[i]); });
    (nextUsers || []).forEach((u, i) => { if (nextNames?.[i]) nameOf.set(u, nextNames[i]); });
    const users = [...(cur.users || [])];
    (nextUsers || []).forEach((u) => { if (u && !users.includes(u)) users.push(u); });
    return { users, names: users.map((u) => nameOf.get(u) || '') };
  }, []);
  
  // ========================================================
  // LIVE MODE: Notifications dropdown and Profile modal
  // While live, these open as overlays instead of navigation
  // ========================================================
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [showProfileModalDuringLive, setShowProfileModalDuringLive] = useState(false);
  const [notificationsList, setNotificationsList] = useState([]);

  // Send link invite to another live broadcaster
  const sendCoHostInvite = async (recipientId, recipientName) => {
    if (!currentStream?.stream_id) {
      toast.error('You must be live to send stream link invites');
      return;
    }
    
    setCoHostInviteStatus('sending');
    
    // Check if we're already in a linked session (inviting 3rd or 4th player)
    const isMultiPartyInvite = invitedPlayers.length > 0 || linkedSessionId;
    const existingSessionId = linkedSessionId || (invitedPlayers.length > 0 ? `stream_${currentStream.stream_id}` : null);
    
    console.log('📤 Sending link invite to:', recipientName, {
      stream: currentStream.stream_id,
      isMultiParty: isMultiPartyInvite,
      existingSession: existingSessionId,
      currentLinkedCount: invitedPlayers.length + 1
    });
    
    // Check capacity (max 6 broadcasters including self)
    if (invitedPlayers.length >= maxLinkPartners) {
      toast.error(`This live is full — up to ${maxLinkPartners + 1} on camera`);
      setCoHostInviteStatus(null);
      return;
    }
    
    try {
      const response = await api.post('/notifications/live-invite', null, {
        params: {
          friend_id: recipientId,
          stream_id: currentStream.stream_id,
          // Pass multi-party session info
          linked_session_id: existingSessionId,
          existing_linked_count: invitedPlayers.length + 1
        }
      });
      
      console.log('✅ Link invite API response:', response.data);
      
      // Validate response has required field
      if (!response.data?.notification_id) {
        console.warn('⚠️ Unexpected response format:', response.data);
      }
      
      setSentCoHostInvites(prev => [...prev, {
        id: response.data.notification_id,
        recipientId,
        recipientName,
        status: 'pending',
        timestamp: Date.now(),
        isMultiParty: isMultiPartyInvite,
        linkedSessionId: existingSessionId
      }]);
      
      setCoHostInviteStatus('sent');
      toast.success(`Invite sent to ${recipientName}! Waiting for response...`);
      
      // Start polling for response
      pollInviteResponse(response.data.notification_id);
      
    } catch (error) {
      console.error('❌ Failed to send co-host invite:', error);
      console.error('Error details:', error.response?.data || error.message);
      setCoHostInviteStatus(null);
      
      // Provide specific error message if available
      const errorMsg = error.response?.data?.detail || 'Failed to send invite. Please try again.';
      toast.error(errorMsg);
    }
  };
  
  // Poll for invite response
  const pollInviteResponse = (inviteId) => {
    const checkResponse = async () => {
      try {
        const response = await api.get(`/notifications/live-invites/${inviteId}/status`);
        const status = response.data.status;
        
        if (status === 'accepted') {
          setCoHostInviteStatus('accepted');
          toast.success('Invite accepted! Connecting to linked session...');
          
          // Update the sent invite
          const acceptedInvite = sentCoHostInvites.find(inv => inv.id === inviteId);
          setSentCoHostInvites(prev => 
            prev.map(inv => inv.id === inviteId ? { ...inv, status: 'accepted' } : inv)
          );
          
          // ========================================================
          // CRITICAL: Add accepted user to invitedPlayers and init Agora
          // This enables the 2-player layout and joins the Agora channel
          // ========================================================
          if (acceptedInvite) {
            // Add to invitedPlayers to trigger 2-player layout
            setInvitedPlayers(prev => {
              // Don't add duplicates
              if (prev.find(p => p.id === acceptedInvite.recipientId)) return prev;
              return [...prev, { 
                id: acceptedInvite.recipientId, 
                name: acceptedInvite.recipientName,
                handle: acceptedInvite.recipientName.toLowerCase().replace(/\s+/g, ''),
                status: 'accepted'
              }];
            });
            
            // Initialize Agora if not already done
            if (LIVEKIT_LINKS && currentStream?.stream_id) {
              // Link v2: publish into the shared LiveKit linked room instead
              // of joining the Agora mesh. `initLiveKitLinkRoom` is
              // idempotent (skips if we're already in the same room) and
              // tears down the solo LiveKit publisher before mounting.
              try {
                const roomName = livekitLinkRoomName(currentStream || {});
                const before = linkRoomInfoRef.current?.roomName;
                await initLiveKitLinkRoom(roomName, { sessionType: 'linked_broadcaster' });
                if (before !== roomName) {
                  toast.success('Video connected! You can now see each other.');
                }
              } catch (lkErr) {
                toast.error('Failed to connect video');
              }
            } else if (!useAgora && !sharedAgoraTracks && currentStream?.stream_id) {
              console.log('🎥 Host initializing Agora after invite accepted...');
              
              try {
                const AgoraRTC = (await import('agora-rtc-sdk-ng')).default;
                
                const agoraResponse = await api.post('/agora/token', {
                  channelName: `stream_${currentStream.stream_id}`,
                  uid: uidFromUserId(user?.id),
                  role: 'publisher',
                  sessionType: 'multiplayer_stream'
                });
                
                const { token, channelName, appId } = agoraResponse.data;
                console.log('✅ Host Agora token received:', { channelName });

                // Checkpoint 4: unified factory.
                const client = await createAndJoinAgoraChannel({
                  AgoraRTC,
                  channelName,
                  token,
                  appId,
                  uid: uidFromUserId(user?.id),
                });

                // Create and publish local tracks
                const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
                  { echoCancellation: true, noiseSuppression: true },
                  { encoderConfig: '720p_2', facingMode: 'user' }
                );
                
                await client.publish([audioTrack, videoTrack]);
                console.log('✅ Host published tracks to Agora');
                
                setSharedAgoraTracks({ audioTrack, videoTrack });
                setAgoraToken(token);
                setAgoraChannel(channelName);
                setAgoraAppId(appId);
                setUseAgora(true);
                
                // Stop WebRTC since Agora now has the camera
                if (webrtc && webrtc.localStream) {
                  webrtc.localStream.getTracks().forEach(track => track.stop());
                }
                
                toast.success('Video connected! You can now see each other.');
              } catch (agoraError) {
                console.error('❌ Host failed to init Agora:', agoraError);
                toast.error('Failed to connect video');
              }
            }
          }
          
          return true; // Stop polling
        } else if (status === 'declined') {
          setCoHostInviteStatus('declined');
          // Don't auto-clear - let user dismiss
          setSentCoHostInvites(prev => 
            prev.map(inv => inv.id === inviteId ? { ...inv, status: 'declined' } : inv)
          );
          return true; // Stop polling
        }
        
        return false; // Continue polling
      } catch (error) {
        console.error('Failed to check invite status:', error);
        return false;
      }
    };
    
    // Poll every 2 seconds for 30 seconds max (15 attempts)
    let attempts = 0;
    const maxAttempts = 15;  // 30 seconds total
    
    const poll = async () => {
      const done = await checkResponse();
      if (!done && attempts < maxAttempts) {
        attempts++;
        setTimeout(poll, 2000);
      } else if (attempts >= maxAttempts && !done) {
        setCoHostInviteStatus('expired');
        // Don't auto-clear - let user dismiss
      }
    };
    
    setTimeout(poll, 2000); // Start polling after 2s
  };
  
  // Cancel a pending co-host invite
  const cancelCoHostInvite = async (inviteId) => {
    try {
      await api.post(`/notifications/live-invites/${inviteId}/cancel`);
      setSentCoHostInvites(prev => prev.filter(inv => inv.id !== inviteId));
      setCoHostInviteStatus(null);
      toast.info('Invite cancelled');
    } catch (error) {
      console.error('Failed to cancel invite:', error);
      // Still remove from local state
      setSentCoHostInvites(prev => prev.filter(inv => inv.id !== inviteId));
      setCoHostInviteStatus(null);
    }
  };
  
  // Disconnect a connected linked broadcaster (removes them from your stream)
  const disconnectCoHost = async (coHostId) => {
    console.log('🔌 Ending linked broadcast with:', coHostId);
    toast.info('Disconnecting linked broadcast...');
    
    try {
      // STEP 0: Notify partner via backend
      try {
        // Determine if the remaining users will form a 2+ party (partial leave)
        // or if this is a final unlink (drop everyone back to solo).
        // linkedCount === 3 → after I leave, 2 remain → still partial (Agora 2-party).
        // linkedCount === 2 → after I leave, 1 remains → FULL unlink (solo mode for remainder).
        const linkedCount = (allLinkedUsers && allLinkedUsers.length) || 0;
        const partialLeave = linkedCount >= 3; // 3+ before leaving = partial leave

        await api.post('/streams/link-ended', {
          partner_id: coHostId,
          stream_id: currentStream?.stream_id,
          leave_session_only: partialLeave
        });
        console.log(`📡 Broadcasted link_ended (partial=${partialLeave}, before=${linkedCount})`);
      } catch (e) {
        console.log('Failed to broadcast link_ended:', e);
      }
      
      // STEP 1: FULL STREAM CLEANUP (unpublish, unsubscribe, leave, clear all state)
      await fullStreamCleanup('disconnect_cohost');
      
      // STEP 2: RESTORE SOLO STREAMING WITH WEBRTC
      // Re-initialize the camera for solo broadcasting
      // ========================================================
      console.log('📹 Restoring solo streaming...');
      
      // If we have webrtc instance, restart the local stream
      if (webrtc) {
        try {
          // Get fresh camera access
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user', width: 720, height: 1280 },
            audio: true
          });
          
          // Set the new stream to webrtc
          webrtc.localStream = stream;
          
          // Update the video preview element if it exists
          const videoElement = document.querySelector('video#local-preview, video.local-video');
          if (videoElement) {
            videoElement.srcObject = stream;
          }
          
          console.log('✅ Solo camera stream restored');
        } catch (e) {
          console.error('Failed to restore camera:', e);
          // Try using startPreview as fallback
          try {
            await startPreview();
            console.log('✅ Solo stream restored via startPreview');
          } catch (e2) {
            console.error('Failed to restore solo stream:', e2);
          }
        }
      } else {
        // No webrtc instance, try startPreview
        try {
          await startPreview();
          console.log('✅ Solo stream restored via startPreview');
        } catch (e) {
          console.error('Failed to restore solo stream:', e);
        }
      }
      
      toast.success('Linked broadcast ended - back to solo streaming');
      console.log('✅ Fully disconnected from linked session');
      
    } catch (error) {
      console.error('❌ Failed to disconnect linked broadcaster:', error);
      toast.error('Error disconnecting. Please refresh if needed.');
    }
  };

  // LiveKit for Solo Streaming
  const [livekitToken, setLivekitToken] = useState(null);
  const [livekitRoomName, setLivekitRoomName] = useState(null);
  const [livekitUrl, setLivekitUrl] = useState(null); // Dynamic LiveKit URL from token response
  const [useLiveKit, setUseLiveKit] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  // NOTE: `livekitPublisherRef`, the `initLiveKitLinkRoom` helper, and the
  // Link-v2 reverse-handoff effect have been RELOCATED to immediately AFTER
  // `const webrtc = useWebRTC()` (~L7000) because they depend on `webrtc`
  // which is declared there. Placing them here caused a
  // "Cannot access 'webrtc' before initialization" ReferenceError at mount
  // and crashed the Live page (prod 2026-02).

  // Screenshare toggle function
  const toggleScreenShare = async () => {
    if (livekitPublisherRef.current) {
      try {
        const newState = await livekitPublisherRef.current.toggleScreenShare();
        setIsScreenSharing(newState);
        if (newState) {
          toast.success('🖥️ Screen sharing started! Viewers can now see your screen.');
        } else {
          toast.success('📹 Camera restored! Screen sharing stopped.');
        }
      } catch (error) {
        console.error('Failed to toggle screenshare:', error);
        toast.error('Failed to toggle screen share');
      }
    } else {
      toast.error('Screen sharing is only available when streaming via LiveKit');
    }
  };
  
  // Stream Health Monitoring
  const [streamHealth, setStreamHealth] = useState({
    status: 'healthy', // 'healthy', 'warning', 'critical'
    lastHeartbeat: null,
    consecutiveFailures: 0,
    latency: null,
    uptime: 0
  });
  
  // Partner assignment for 4-player Spades
  const [selectedPartner, setSelectedPartner] = useState(null);
  // showPartnerSelection removed (Feb 20, 2026) — partner picking lives in the Spades table modal.
  const [teamAssignments, setTeamAssignments] = useState({
    team1: [], // Host + Player 4 (selected partner)
    team2: []  // Player 2 + Player 3 (remaining players)
  });
  
  // Stream ending and suggestions
  const [showStreamEndedModal, setShowStreamEndedModal] = useState(false);
  const [showStreamStatsModal, setShowStreamStatsModal] = useState(false);
  const [suggestedStreams, setSuggestedStreams] = useState([]);
  const [showStartGameModal, setShowStartGameModal] = useState(false);
  
  // Stream analytics tracking
  const [streamStartTime, setStreamStartTime] = useState(null);
  const [streamStats, setStreamStats] = useState({
    liveTime: 0, // in seconds
    totalViewers: 0,
    peakViewers: 0,
    engagedViewers: 0, // viewers who interacted (chat, gifts)
    newRubies: 0,
    giftGivers: 0,
    newFans: 0
  });
  
  // Real-time rubies earned counter for the stream
  const [streamRubiesEarned, setStreamRubiesEarned] = useState(0);
  
  // Track actually connected players (not just invited)
  const [connectedPlayers, setConnectedPlayers] = useState([]);
  
  // In-stream game state
  const [inStreamGame, setInStreamGame] = useState(null); // Active game in livestream
  // Crazy Eights live-mode state (Jul 27, 2026). Separate from `gameState`
  // (Spades) so the two pipelines don't collide.
  const [c8LiveState, setC8LiveState] = useState(null);
  const [checkersLiveState, setCheckersLiveState] = useState(null); // in-stream checkers board (from checkers_state WS push)
  const [gameState, setGameState] = useState(null); // Current game state
  const [myCards, setMyCards] = useState([]);
  // Feb 20, 2026: shared Spades hydrator — one source of truth for host + invitee
  // and safe to call from anywhere (game WS handler, sync listeners, effects).
  const hydrateSpadesFromServer = useCallback(async (gameId, attempt = 0) => {
    if (!gameId) return;
    try {
      const res = await api.get(`/game/${gameId}`);
      const g = res.data || {};
      const me = (g.players || []).find((p) => p.id === user?.id);
      if (!me?.cards?.length && attempt === 0) {
        setTimeout(() => hydrateSpadesFromServer(gameId, 1), 1500);
        return;
      }
      if (me?.cards?.length) setMyCards(sortCards(me.cards));
      const handNo = g.current_hand ?? 1;
      setGameState((prev) => ({
        ...(prev || {}),
        // Server phase verbatim — hands 2+ open in 'bidding'; the fan's
        // turn banner and bid modal both key off it.
        phase: g.phase || 'playing',
        gameState: g.gameState || 'PLAYING',
        currentHand: handNo,
        handNumber: handNo,
        noBidding: handNo === 1,
        players: g.players || prev?.players || [],
        matchScores: g.match_scores || prev?.matchScores || [0, 0],
        teamBids: g.team_bids || prev?.teamBids || [0, 0],
        booksWon: g.books_won || prev?.booksWon || [0, 0],
        spadesBroken: !!g.spades_broken,
        currentTrick: g.current_trick || [],
        targetScore: g.target_score ?? prev?.targetScore ?? 150,
        matchSettings: g.match_settings || prev?.matchSettings || {},
        currentPlayer: g.current_player ?? null,
        // The overlay's turn gates compare currentPlayer === myPosition.
        myPosition: (me?.position ?? g.current_position) ?? null,
      }));
      setSpadesInvitePending(null);
      setInStreamGame(gameId);
      console.log('[Spades] hydrated', { cards: me?.cards?.length || 0, hand: g.current_hand ?? 1 });
    } catch (e) { console.error('[Spades] hydrate failed', e); }
  }, [user?.id]);
  const [showBiddingModal, setShowBiddingModal] = useState(false);
  // Completed trick hold (Feb 2026): keep the 4th card + winner visible for
  // 3s after a book resolves so EVERY player sees the full trick land.
  const [lastTrickHold, setLastTrickHold] = useState(null); // {trick, winner}
  const currentTrickRef = useRef([]);
  // Blind-bid gate (Feb 2026): when my team trails by 100+, my new hand
  // stays FACE-DOWN during bidding until I either bid blind or peek.
  const [spadesHandRevealed, setSpadesHandRevealed] = useState(true);
  const [currentBid, setCurrentBid] = useState(0);

  // SPADES IN-STREAM INVITE/ACCEPT (May 16, 2026) — minimal but complete.
  const [incomingSpadesInvite, setIncomingSpadesInvite] = useState(null); // {game_id, host_name, ...}
  const [spadesInvitePending, setSpadesInvitePending] = useState(null); // host-side: {game_id, invitee_ids, joined_ids, started_at}
  const spadesCancelTimerRef = useRef(null);
  
  // Linked-live 4 In A Row game overlay state
  const [showLinkedGameWagerModal, setShowLinkedGameWagerModal] = useState(false);
  // Part 4 (Feb 19, 2026): per-link-session win counter. Bumped on every
  // linked-game end (4iaR / checkers / C8 — win/forfeit/timeout). Reset
  // to {} whenever the link tears down (End Link, partner left, stream
  // ended) so a fresh link starts at 0—0. Persists across rematches.
  const [sessionWins, setSessionWins] = useState({}); // { user_id: n }
  // Part 3 (Feb 19, 2026): last-game end context — captured when any
  // linked game ends so the shared end overlay can render winner + wins
  // line + Rematch/Back-to-Live. Cleared on Back to Live.
  // Shape: { gameType, gameId, winnerId, winnerName, players, config }
  const [lastGameEnd, setLastGameEnd] = useState(null);
  // Part 3b (Feb 19, 2026): Spades TABLE STEP modal. Opened by the Play
  // Together Spades card BEFORE the entry modal so the host can see all
  // 4 seats (self + linked broadcasters), invite more if needed, and
  // pick their partner in one place. Feeds selectedPartner +
  // teamAssignments before showWagerModal opens.
  const [showSpadesTableModal, setShowSpadesTableModal] = useState(false);
  // C8 per-game House Rules (Aug 18, 2026) — surface in the wager modal
  // when linkedGameType === 'crazy_eights'; persisted onto the game doc
  // via /c8/create so the engine honors them for this match.
  const [c8Rules, setC8Rules] = useState({
    stacking_enabled: true,
    eight_cancels_stack: false,
    timer_enabled: true,
    // Part C (Feb 2026): 4 new live variants — all default OFF so the
    // engine defaults match the tests.
    skip_stacking: false,
    draw_until_play: false,
    ten_go_again: false,
    ace_reversal: false,
  });
  const [showC8HowToPlay, setShowC8HowToPlay] = useState(false);
  // Which 2-player game the in-stream wager modal is launching:
  // 'four_in_a_row' | 'checkers' | 'crazy_eights'. Used by the shared
  // wager modal to pick the right title, icon, and create-endpoint so
  // Checkers/C8 stay IN the live screen (no navigate to /games).
  const [linkedGameType, setLinkedGameType] = useState('four_in_a_row');
  const [linkedGameWager, setLinkedGameWager] = useState(MIN_MATCH_ENTRY);
  const [linkedGameOpponent, setLinkedGameOpponent] = useState(null);
  const [activeLinkedGame, setActiveLinkedGame] = useState(null); // {gameId, board, currentPlayer, etc}
  const [linkedSessionWins, setLinkedSessionWins] = useState({ me: 0, opponent: 0 }); // Session win counts
  const [showGameChallengeModal, setShowGameChallengeModal] = useState(false);
  const [pendingGameChallenge, setPendingGameChallenge] = useState(null); // {gameId, challengerId, challengerName, wager}

  // Link v2 — unified partners roster for LiveKitLinkGrid / LiveKitMultiViewer
  // labels. Seeds from invitedPlayers (has pictures + handles) and overlays
  // the authoritative link_started / broadcaster_joined_session payload
  // (allLinkedUsers + allLinkedUserNames) so late joiners and the accepter
  // side both display REAL names — never a raw UUID prefix.
  const linkPartners = useMemo(() => {
    const byId = new Map();
    (invitedPlayers || []).forEach((p) => {
      if (!p?.id || p.id === user?.id) return;
      byId.set(p.id, { id: p.id, name: p.name, picture: p.picture });
    });
    if (Array.isArray(allLinkedUsers)) {
      allLinkedUsers.forEach((uid, idx) => {
        if (!uid || uid === user?.id) return;
        const existing = byId.get(uid) || { id: uid };
        const nameFromPayload = allLinkedUserNames?.[idx];
        byId.set(uid, {
          id: uid,
          name: existing.name || nameFromPayload || undefined,
          picture: existing.picture,
        });
      });
    }
    return Array.from(byId.values());
  }, [invitedPlayers, allLinkedUsers, allLinkedUserNames, user?.id]);
  
  // WebSocket state for in-stream game
  const [gameWs, setGameWs] = useState(null);
  const [gameWsConnected, setGameWsConnected] = useState(false);
  
  // Universal Sync WebSocket state
  const [syncWs, setSyncWs] = useState(null);
  const [syncConnected, setSyncConnected] = useState(false);
  
  // Stream Chat state
  const [streamChatMessages, setStreamChatMessages] = useState([]);
  const [streamChatInput, setStreamChatInput] = useState('');
  const [streamChatWs, setStreamChatWs] = useState(null);
  // For multiplayer: both hosts share the ORIGINAL host's chat stream
  const [sharedChatStreamId, setSharedChatStreamId] = useState(null);
  
  // AI Chat Bots - Simulated viewers for engagement
  const [chatBotsEnabled, setChatBotsEnabled] = useState(true);
  const [activeChatBots, setActiveChatBots] = useState([]);

  // ==========================================================
  // BROADCASTER VIEWER COUNT — Single source of truth (Feb 2026)
  // ==========================================================
  // Renders the SAME stable number in every broadcaster-side viewer badge
  // (header people-icon, stream stats card, etc.). It's real viewers
  // (tracked via WebSocket heartbeats -> realTimeViewerCount) plus the
  // AI creators/chat bots currently in-stream. NO random increments.
  // The previous jittery Math.random eye-icon overlay has been removed.
  // ==========================================================
  const displayViewers = useMemo(
    () => (realTimeViewerCount || 0) + (activeChatBots?.length || 0),
    [realTimeViewerCount, activeChatBots?.length]
  );

  // Sync bot count to backend so viewers see the same viewer count as broadcaster
  useEffect(() => {
    const sid = currentStream?.stream_id || currentStream?.id;
    if (!sid) return;
    api.post(`/streams/${sid}/bot-count`, { count: activeChatBots?.length || 0 }).catch(() => {});
  }, [activeChatBots?.length, currentStream?.stream_id]);

  // Optimistic-UI busy flags — surface a spinner the moment the user
  // taps Go Live / Send Invitation so the action feels instant even
  // while the backend/LiveKit handshake completes.
  const [isGoingLive, setIsGoingLive] = useState(false);
  const [isSendingInvites, setIsSendingInvites] = useState(false);
  
  // @ Mention feature for chat
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  
  // User action popup (when clicking username in chat)
  const [showUserActionPopup, setShowUserActionPopup] = useState(null); // { username, userId, profilePic, x, y }
  
  // Profile modal (shows user profile without leaving page)
  const [showProfileModal, setShowProfileModal] = useState(null); // user profile data
  
  // Stream moderation - admin status
  const [streamAdminStatus, setStreamAdminStatus] = useState({ is_admin: false, is_owner: false });
  
  // Audio/Video controls for host
  const [isMicMuted, setIsMicMuted] = useState(false);
  
  // Header visibility toggle during gameplay
  const [showHeader, setShowHeader] = useState(true);
  
  // ========================================================
  // HANDLE INCOMING SYNC MESSAGES
  // Define this BEFORE connectUniversalSync so it can be used in dependencies
  // ========================================================
  const handleSyncMessage = useCallback((data) => {
    const { type, data: payload } = data;
    
    switch (type) {
      case 'session_revoked':
        // Newest-login-wins: another device just signed in on the same account.
        // Clear stored token, drop user in memory, show one clear message.
        console.warn('🚫 Session revoked (signed in elsewhere) — logging out this device');
        try { localStorage.removeItem('authToken'); } catch (_e) { /* noop */ }
        setUser(null);
        toast.error(
          "You were signed out because your account signed in on another device.",
          { duration: 6000 }
        );
        break;

      case 'profile_update':
        // Refresh user context if it's the current user's profile
        if (payload.user_id === user?.id) {
          console.log('🔄 Refreshing user profile from sync...');
          // Trigger a re-fetch of user data
          api.get('/auth/me').then(response => {
            setUser(response.data);
          }).catch(err => console.error('Failed to refresh user:', err));
        }
        // Emit custom event for other components to listen
        window.dispatchEvent(new CustomEvent('sync:profile_update', { detail: payload }));
        break;
        
      case 'new_post':
        console.log('📝 New post received via sync:', payload);
        // Emit custom event for Discover page to listen
        window.dispatchEvent(new CustomEvent('sync:new_post', { detail: payload }));
        // Show toast notification
        toast.info(`New post from ${payload.user_name || 'someone'}!`, { duration: 3000 });
        break;
        
      case 'wallet_update':
        // Update wallet balance in real-time
        if (payload.user_id === user?.id) {
          console.log('💰 Wallet update received:', payload);
          setUser(prev => ({
            ...prev,
            emeralds: payload.emeralds !== undefined ? payload.emeralds : prev?.emeralds,
            rubies: payload.rubies !== undefined ? payload.rubies : prev?.rubies
          }));
          // Show toast for significant changes
          if (payload.change_type === 'gift_received') {
            toast.success(`+${payload.amount} Rubies received!`, { duration: 3000 });
          }
        }
        break;
        
      case 'badge_update':
        console.log('🏅 Badge update received:', payload);
        // Update local user state if it's the current user
        if (payload.user_id === user?.id) {
          setUser(prev => ({
            ...prev,
            badge_type: payload.badge_type,
            is_verified: payload.is_verified
          }));
          toast.success(`Your badge has been updated!`, { duration: 3000 });
        }
        // Emit custom event for profile displays
        window.dispatchEvent(new CustomEvent('sync:badge_update', { detail: payload }));
        break;
        
      case 'social_update':
        // Friend/follower/fan counts changed
        console.log('👥 Social update received:', payload);
        window.dispatchEvent(new CustomEvent('sync:social_update', { detail: payload }));
        break;
        
      case 'tournament_update':
        // Tournament status changed
        console.log('🏆 Tournament update received:', payload);
        window.dispatchEvent(new CustomEvent('sync:tournament_update', { detail: payload }));
        break;
      
      // ========================================================
      // LIVE INVITE - Real-time host link invite delivery
      // Shows popup immediately for any authenticated user
      // The user can accept to join or decline
      // ========================================================
      case 'live_invite':
        console.log('🔴 LIVE INVITE RECEIVED VIA WEBSOCKET!', payload);
        // Bug 3 (May 7 testing): dedupe by invite_id (Set on window). The WS
        // room and the polling fallback can both deliver the same invite —
        // pre-fix this caused the popup to fire 2-3 times after acceptance.
        {
          const inviteId = payload.invite_id;
          if (inviteId) {
            if (!window.__gemsConsumedInvites) window.__gemsConsumedInvites = new Set();
            if (window.__gemsConsumedInvites.has(inviteId)) {
              console.log('[Bug3] LivePage suppressed duplicate live_invite', inviteId);
              break;
            }
            window.__gemsConsumedInvites.add(inviteId);
          }
        }
        setIncomingLiveInvite({
          id: payload.invite_id,
          sender_id: payload.sender_id,
          sender_name: payload.sender_name,
          sender_handle: payload.sender_handle,
          stream_id: payload.stream_id,
          stream_title: payload.stream_title,
          message: payload.message
        });
        setShowLiveInvitePopup(true);
        toast.success(`${payload.sender_name} wants to link streams with you!`, { duration: 5000 });
        break;
      
      // ========================================================
      // STREAM INVITE - Real-time stream invitation delivery
      // Shows popup for invited user to join a stream game
      // ========================================================
      case 'stream_invite':
        console.log('🎮 STREAM INVITE RECEIVED VIA WEBSOCKET!', payload);
        // App-level listener owns the popup state (LivePage has no
        // setPendingInvitation — referencing it threw and killed WS invites).
        window.dispatchEvent(new CustomEvent('sync:stream_invite', { detail: payload }));
        break;
      
      // ========================================================
      // INVITE RESPONSE - Sender receives notification when their invite is accepted/declined
      // ========================================================
      case 'invite_response':
        console.log('📩 INVITE RESPONSE RECEIVED:', payload);
        if (payload.action === 'accepted') {
          toast.success(`${payload.responder_name} accepted your invite! Linking streams...`, { duration: 5000 });
          // The sender should now initialize Agora to link with the acceptor
          dispatchLinkEventOnce(
            'link-live-partner-accepted',
            {
              streamId: payload.stream_id,
              partnerId: payload.responder_id,
              partnerName: payload.responder_name,
              invitationId: payload.invitation_id
            },
            [payload.invitation_id, payload.stream_id, payload.responder_id]
          );
        } else if (payload.action === 'declined') {
          toast.info(`${payload.responder_name} declined your invite`, { duration: 3000 });
        }
        break;
      
      // ========================================================
      // LINK_ENDED - Partner ended the linked broadcast
      // ========================================================
      case 'link_ended':
        console.log('🔌 LINK_ENDED received:', payload);
        toast.info(`${payload.ended_by_name} ended the linked broadcast`, { duration: 5000 });
        
        // Trigger cleanup on this side - dispatch event to LivePage
        window.dispatchEvent(new CustomEvent('partner-ended-link', {
          detail: {
            endedById: payload.ended_by_id,
            endedByName: payload.ended_by_name
          }
        }));
        break;
      
      // ========================================================
      // LINK_STARTED - Broadcasters have linked (for viewers)
      // ========================================================
      case 'link_started':
        console.log('🔗 LINK_STARTED received:', payload);
        // Notify linked broadcasters to sync their session state
        dispatchLinkEventOnce(
          'linked-session-updated',
          payload,
          [payload?.linked_session_id, payload?.linked_count]
        );
        // Legacy: this event also dispatched to viewers
        dispatchLinkEventOnce(
          'broadcasters-linked',
          payload,
          [payload?.linked_session_id, payload?.linked_count]
        );
        break;
      
      // ========================================================
      // LINKED_GAME_UPDATE - Game state sync for linked games
      // ========================================================
      case 'linked_game_update':
        // Part 6b (Feb 19, 2026): the 4-In-A-Row + Checkers forfeit-
        // driven "game_over" broadcast can also duplicate. Dedupe by
        // game_id when this payload marks game_over via forfeit.
        {
          const gid = payload?.game_id;
          const isForfeit = payload?.game_over === true && payload?.forfeited_by;
          if (isForfeit && gid) {
            window.__forfeitDedupe = window.__forfeitDedupe || new Set();
            if (window.__forfeitDedupe.has(gid)) {
              console.log('🎮 LINKED_GAME_UPDATE forfeit dedup skipped:', gid);
              break;
            }
            window.__forfeitDedupe.add(gid);
          }
          console.log('🎮 LINKED_GAME_UPDATE received:', payload);
          // Part 4 (Feb 19, 2026): bump session-wins for 4iaR when the
          // payload marks game_over with a winner. Idempotent via
          // window.__forfeitDedupe / __4iar_wins Sets so repeat pushes
          // don't double-count.
          try {
            if (payload?.game_over === true && payload?.winner_id && gid) {
              window.__4iar_wins_captured = window.__4iar_wins_captured || new Set();
              if (!window.__4iar_wins_captured.has(gid)) {
                window.__4iar_wins_captured.add(gid);
                setSessionWins((prev) => ({
                  ...prev,
                  [payload.winner_id]: (prev[payload.winner_id] || 0) + 1,
                }));
                // Part 3: capture end context for 4iaR.
                const winnerName = payload.winner_id === user?.id
                  ? (user?.name || 'You')
                  : ((linkPartners || []).find((x) => x.id === payload.winner_id)?.name || 'Guest');
                setLastGameEnd({
                  gameType: 'four_in_a_row',
                  gameId: gid,
                  winnerId: payload.winner_id,
                  winnerName,
                  players: [user?.id, ...(linkPartners || []).map((p) => p.id)].filter(Boolean),
                  config: {},
                });
              }
            }
          } catch (_e) { /* never disrupt */ }
          window.dispatchEvent(new CustomEvent('linked-game-update', {
            detail: payload,
          }));
        }
        break;
      
      // ========================================================
      // LINKED_GAME_CHALLENGE - Opponent challenges you to a game
      // ========================================================
      case 'linked_game_challenge':
        console.log('🎮 LINKED_GAME_CHALLENGE received:', payload);
        window.dispatchEvent(new CustomEvent('linked-game-challenge', {
          detail: payload
        }));
        break;
      
      // ========================================================
      // LINKED_GAME_ACCEPTED - Opponent accepted your challenge
      // ========================================================
      case 'linked_game_accepted':
        console.log('🎮 LINKED_GAME_ACCEPTED received:', payload);
        dispatchLinkEventOnce(
          'linked-game-accepted',
          payload,
          [payload?.game_id, payload?.accepted_by]
        );
        break;
      
      // ========================================================
      // LINKED_GAME_DECLINED - Opponent declined your challenge
      // ========================================================
      case 'linked_game_declined':
        console.log('🎮 LINKED_GAME_DECLINED received:', payload);
        window.dispatchEvent(new CustomEvent('linked-game-declined', {
          detail: payload
        }));
        break;
      
      // ========================================================
      // LINKED_GAME_INVITE - (Legacy) Partner started a game
      // ========================================================
      case 'linked_game_invite':
        console.log('🎮 LINKED_GAME_INVITE received:', payload);
        toast.info(`${payload.inviter_name} started a 4 In A Row game!`, { duration: 5000 });
        window.dispatchEvent(new CustomEvent('linked-game-invite', {
          detail: payload
        }));
        break;
      
      // ========================================================
      // BROADCASTER_JOINED_SESSION - New broadcaster joined linked session
      // ========================================================
      case 'broadcaster_joined_session':
        console.log('👥 BROADCASTER_JOINED_SESSION received:', payload);
        toast.success(`${payload.new_broadcaster_name} joined the linked session!`, { duration: 4000 });
        dispatchLinkEventOnce(
          'broadcaster-joined-session',
          payload,
          [payload?.linked_session_id, payload?.new_broadcaster_id]
        );
        break;
      
      // ========================================================
      // BROADCASTER_LEFT_SESSION - Broadcaster left linked session (multi-party)
      // ========================================================
      case 'broadcaster_left_session':
        console.log('👤 BROADCASTER_LEFT_SESSION received:', payload);
        toast.info(`${payload.left_by_name} left the linked session`, { duration: 4000 });
        dispatchLinkEventOnce(
          'broadcaster-left-session',
          payload,
          [payload?.linked_session_id, payload?.left_by_id]
        );
        break;
      
      // ========================================================
      // GAME_STARTED - Host started a Spades game from linked live
      // ========================================================
      case 'game_started':
        console.log('🎮 GAME_STARTED received from host:', payload);
        window.dispatchEvent(new CustomEvent('spades-game-started', {
          detail: payload
        }));
        break;

      // ========================================================
      // SPADES IN-STREAM INVITE / ACCEPT / CANCEL (May 16, 2026)
      // ========================================================
      case 'spades_game_invite':
        console.log('🎴 SPADES_GAME_INVITE received:', payload);
        window.dispatchEvent(new CustomEvent('spades-game-invite', { detail: payload }));
        break;
      case 'spades_game_invite_declined':
        console.log('🎴 SPADES_GAME_INVITE_DECLINED received:', payload);
        window.dispatchEvent(new CustomEvent('spades-game-invite-declined', { detail: payload }));
        break;
      case 'spades_game_player_joined':
        console.log('🎴 SPADES_GAME_PLAYER_JOINED received:', payload);
        window.dispatchEvent(new CustomEvent('spades-game-player-joined', { detail: payload }));
        break;
      case 'spades_game_cancelled':
        console.log('🎴 SPADES_GAME_CANCELLED received:', payload);
        window.dispatchEvent(new CustomEvent('spades-game-cancelled', { detail: payload }));
        break;
      case 'spades_player_disconnected':
        // 30s grace warning — if they don't re-link, their team auto-forfeits.
        console.log('🎴 SPADES_PLAYER_DISCONNECTED received:', payload);
        toast.warning(
          `${payload?.user_name || 'A player'} left the link — ${payload?.grace_seconds || 30}s to return or their team forfeits`,
          { duration: 8000 },
        );
        break;
      case 'spades_game_forfeit':
        // Part 6b (Feb 19, 2026): even with the atomic server guard, a
        // client can still receive dupes across reconnects / ws_bus
        // fan-out. Dedupe by game_id via a window-level Set.
        {
          const gid = payload?.game_id;
          window.__forfeitDedupe = window.__forfeitDedupe || new Set();
          if (gid && window.__forfeitDedupe.has(gid)) {
            console.log('🎴 SPADES_GAME_FORFEIT dedup skipped:', gid);
            break;
          }
          if (gid) window.__forfeitDedupe.add(gid);
          console.log('🎴 SPADES_GAME_FORFEIT received:', payload);
          window.dispatchEvent(new CustomEvent('spades-game-forfeit', { detail: payload }));
        }
        break;
        
      case 'pong':
        // Keepalive response
        break;
        
      case 'connected':
        console.log('✅ Universal Sync confirmed connected for user:', payload?.user_id);
        break;

      // Part 5 (Feb 19, 2026): the WS sync handler in this callback runs
      // in addition to the raw-WS handler at L1830. Fanning to a window
      // event lets the leaderboard queries refresh without a warning.
      case 'leaderboard_update':
        console.log('🏆 leaderboard_update received:', payload);
        window.dispatchEvent(new CustomEvent('sync:leaderboard_update', { detail: payload }));
        break;
        
      default:
        console.log('Unknown sync message type:', type);
    }
  }, [user?.id, setUser]);
  
  // ========================================================
  // UNIVERSAL SYNC WEBSOCKET CONNECTION
  // ========================================================
  // Connects to /api/ws/sync for real-time updates across platforms
  // Handles: profile updates, new posts, wallet changes, badges, etc.
  // ========================================================
  // Use ref to track the WebSocket instance to avoid re-render loops
  const syncWsRef = useRef(null);
  
  const connectUniversalSync = useCallback((userId) => {
    if (!userId) {
      console.warn('⚠️ connectUniversalSync called without userId');
      return;
    }
    
    // Check ref instead of state to avoid race conditions
    if (syncWsRef.current && syncWsRef.current.readyState === WebSocket.OPEN) {
      console.log('🌐 syncWs already connected, skipping');
      return;
    }
    
    // Close any existing connection that might be in a bad state
    if (syncWsRef.current) {
      try {
        syncWsRef.current.close();
      } catch (e) {
        console.log('🌐 Error closing existing socket:', e);
      }
      syncWsRef.current = null;
    }
    
    const backendUrl = API_BASE_URL || '';
    console.log('🌐 API_BASE_URL:', API_BASE_URL);
    console.log('🌐 backendUrl:', backendUrl);
    
    // Remove /api suffix if present (we add it back in the WS URL)
    const baseUrl = backendUrl.replace('/api', '');
    console.log('🌐 baseUrl after removing /api:', baseUrl);
    
    // Convert http(s) to ws(s)
    const wsUrl = baseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    console.log('🌐 wsUrl after protocol conversion:', wsUrl);
    
    const fullWsUrl = `${wsUrl}/api/ws/sync?user_id=${userId}`;
    console.log('🌐 FULL WebSocket URL:', fullWsUrl);
    
    try {
      console.log('🌐 Creating WebSocket connection...');
      const socket = new WebSocket(fullWsUrl);
      syncWsRef.current = socket; // Store in ref immediately
      
      socket.onopen = () => {
        console.log('✅ Universal Sync WebSocket CONNECTED successfully!');
        console.log('✅ WebSocket readyState:', socket.readyState);
        setSyncConnected(true);
        setSyncWs(socket); // Also update state for components that need it
        
        // Send ping every 30 seconds to keep connection alive
        const pingInterval = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'ping' }));
          } else {
            clearInterval(pingInterval);
          }
        }, 30000);
        
        socket.pingInterval = pingInterval;
      };
      
      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('🌐 Universal Sync message received:', data);
          handleSyncMessage(data);
        } catch (e) {
          console.error('Failed to parse sync message:', e);
        }
      };
      
      socket.onerror = (error) => {
        console.error('❌ Universal Sync WebSocket ERROR:', error);
        // Don't log the full error object as it's usually empty for security
      };
      
      socket.onclose = (event) => {
        console.log('🌐 Universal Sync WebSocket CLOSED');
        console.log('🌐 Close code:', event.code, 'reason:', event.reason, 'wasClean:', event.wasClean);
        setSyncConnected(false);
        setSyncWs(null);
        syncWsRef.current = null;
        if (socket.pingInterval) clearInterval(socket.pingInterval);
        
        // Auto-reconnect after 5 seconds if not clean close and user is still logged in
        if (!event.wasClean && event.code !== 1000) {
          setTimeout(() => {
            if (user?.id && !syncWsRef.current) {
              console.log('🔄 Attempting to reconnect Universal Sync...');
              connectUniversalSync(user.id);
            }
          }, 5000);
        }
      };
    } catch (error) {
      console.error('❌ Failed to create WebSocket:', error);
      syncWsRef.current = null;
    }
  }, [user?.id, handleSyncMessage]); // Removed syncWs from dependencies
  
  // Connect to Universal Sync when user logs in
  useEffect(() => {
    console.log('🔄 Universal Sync useEffect triggered');
    console.log('🔄 user?.id:', user?.id);
    console.log('🔄 syncWsRef.current:', syncWsRef.current ? 'exists' : 'null');
    
    // Only connect if user is logged in and no connection exists
    if (user?.id && !syncWsRef.current) {
      console.log('🔄 Conditions met, calling connectUniversalSync...');
      connectUniversalSync(user.id);
    } else if (!user?.id && syncWsRef.current) {
      // User logged out - close the connection
      console.log('🔄 User logged out, closing syncWs');
      try {
        syncWsRef.current.close(1000, 'User logged out');
      } catch (e) {
        console.log('Error closing socket on logout:', e);
      }
      syncWsRef.current = null;
      setSyncWs(null);
      setSyncConnected(false);
    }
    
    // Cleanup on component unmount
    return () => {
      if (syncWsRef.current) {
        console.log('🔄 Component unmount, closing syncWs');
        try {
          syncWsRef.current.close(1000, 'Component unmount');
        } catch (e) {
          console.log('Error closing socket on unmount:', e);
        }
        syncWsRef.current = null;
      }
    };
  }, [user?.id, connectUniversalSync]); // Only depend on user.id and the stable callback
  
  // Partner selection auto-open removed (Feb 20, 2026): partner selection
  // lives ONLY in the Spades table modal. The legacy effect used to auto-
  // open the "Choose Your Partner" dialog for EVERYONE the moment 3 linked
  // partners arrived — before any game was chosen.
  
  // ========================================================
  // CRITICAL: SESSION LIFECYCLE MANAGEMENT
  // ========================================================
  // Enforce hard cleanup on: tab close, refresh, navigate away,
  // visibility change, route change, and app backgrounding.
  // Broadcasters CANNOT navigate while live.
  // ========================================================
  
  // Helper function to force end all active sessions
  const forceEndAllSessions = useCallback(async (reason = 'unknown') => {
    console.log(`🛑 FORCE END ALL SESSIONS - Reason: ${reason}`);

    // ============================================================
    // MESH-FIX (May 15, 2026): NOTIFY LINK PARTNERS BEFORE TEAR-DOWN
    // ============================================================
    // Pre-fix, ending a stream (forceEndAllSessions) called /streams/{id}/stop
    // but NOT /streams/link-ended. The remaining linked broadcasters never
    // got `broadcaster_left_session` / `link_ended` WebSocket events, so:
    //   - Their tiles for the leaver remained stale.
    //   - A lone remaining broadcaster never dropped back to solo (LiveKit).
    //   - Their viewers stayed wired to the (now-dead) Agora channel.
    // disconnectCoHost (line 4093) did call link-ended; End Stream did not.
    // Fix: when linked, POST link-ended FIRST with the correct
    // leave_session_only flag so the backend can fan
    // `broadcaster_left_session` (3+ remain) or `link_ended` (1 remains).
    // ============================================================
    try {
      const linkedCount = (allLinkedUsers && allLinkedUsers.length) || 0;
      const isLinked = linkedCount > 1
        || !!linkedSessionId
        || !!currentStream?.linked_session_id;
      if (isLinked) {
        // 3+ before leaving = partial leave; 2 before leaving = full unlink
        // (remaining single user must drop back to solo).
        const partialLeave = linkedCount >= 3;
        await api.post('/streams/link-ended', {
          stream_id: currentStream?.stream_id,
          leave_session_only: partialLeave,
        });
        console.log(`📡 [Mesh-Fix] forceEndAllSessions broadcasted link_ended (partial=${partialLeave}, before=${linkedCount})`);
      } else {
        console.log('[Mesh-Fix] forceEndAllSessions: not linked — skipping /streams/link-ended');
      }
    } catch (e) {
      console.warn('[Mesh-Fix] forceEndAllSessions /streams/link-ended failed (continuing teardown):', e?.message);
    }

    // Full Agora cleanup (unpublish, unsubscribe, leave, clear state)
    await fullStreamCleanup(reason);
    
    // End LiveKit session
    if (useLiveKit && livekitRoomName) {
      try {
        await api.post('/livekit/session-end', {
          roomName: livekitRoomName,
          reason: reason
        });
      } catch (err) {
        console.error('Failed to end LiveKit session:', err);
      }
    }
    
    // End stream on backend
    if (currentStream?.stream_id) {
      try {
        await api.post(`/streams/${currentStream.stream_id}/stop`);
      } catch (err) {
        console.error('Failed to stop stream:', err);
      }
    }
    
    // Clear remaining states
    setLivekitToken(null);
    setLivekitRoomName(null);
    setUseLiveKit(false);
    setCurrentStream(null);
    setIsPreviewMode(false);
  }, [fullStreamCleanup, useLiveKit, livekitRoomName, currentStream, allLinkedUsers, linkedSessionId]);
  
  // Track if user is actively broadcasting (not preview, actually live)
  const isActiveBroadcaster = currentStream && !isPreviewMode;
  
  // ========================================================
  // NAVIGATION BLOCKER: Prevent in-app navigation while broadcasting
  // Uses custom click interception since BrowserRouter doesn't support useBlocker
  // ========================================================
  const [showNavigationBlockedModal, setShowNavigationBlockedModal] = useState(false);
  const [blockedNavigationPath, setBlockedNavigationPath] = useState(null);
  
  // Handle forced navigation after ending stream
  const handleEndStreamAndNavigate = async () => {
    try {
      await forceEndAllSessions();
      setShowNavigationBlockedModal(false);
      if (blockedNavigationPath) {
        navigate(blockedNavigationPath);
      }
    } catch (error) {
      console.error('Failed to end stream:', error);
      toast.error('Failed to end stream');
    }
  };
  
  // Custom navigation blocking for nav links while broadcasting
  const handleNavClick = useCallback((e, targetPath) => {
    if (isActiveBroadcaster && location.pathname === '/live' && targetPath !== '/live') {
      e.preventDefault();
      e.stopPropagation();
      console.log('🚫 Navigation blocked while broadcasting:', targetPath);
      setBlockedNavigationPath(targetPath);
      setShowNavigationBlockedModal(true);
      return false;
    }
    return true;
  }, [isActiveBroadcaster, location.pathname]);
  
  // ========================================================
  // POLLING FALLBACK: Use useLiveInvites hook when WebSocket fails
  // This polls every 3 seconds when user is logged in to catch invites
  // Polling works regardless of whether the user is currently broadcasting
  // ========================================================
  const { pendingInvite: polledInvite, showInvitePopup: showPolledPopup, closeInvitePopup: closePolledPopup } = useLiveInvites(user?.id, true);
  
  // When polled invite arrives, trigger the popup (fallback for WebSocket)
  useEffect(() => {
    if (polledInvite && showPolledPopup && !showLiveInvitePopup) {
      // Bug 3: dedupe — if WS already delivered this invite_id, skip the
      // polling-fallback duplicate.
      const inviteId = polledInvite.id;
      if (inviteId) {
        if (!window.__gemsConsumedInvites) window.__gemsConsumedInvites = new Set();
        if (window.__gemsConsumedInvites.has(inviteId)) {
          console.log('[Bug3] Polling fallback suppressed duplicate', inviteId);
          return;
        }
        window.__gemsConsumedInvites.add(inviteId);
      }
      console.log('🔔 Live invite received via POLLING fallback:', polledInvite);
      setIncomingLiveInvite({
        id: polledInvite.id,
        sender_id: polledInvite.sender_id,
        sender_name: polledInvite.sender_name,
        sender_handle: polledInvite.sender_handle,
        stream_id: polledInvite.stream_id,
        stream_title: polledInvite.stream_title,
        message: polledInvite.message
      });
      setShowLiveInvitePopup(true);
    }
  }, [polledInvite, showPolledPopup, showLiveInvitePopup]);
  
  // HARD CLEANUP: beforeunload, pagehide, visibilitychange
  useEffect(() => {
    if (!isActiveBroadcaster) return;
    
    // Handler for tab close / refresh
    const handleBeforeUnload = (event) => {
      console.log('🚨 beforeunload - Force ending session');
      // Synchronous cleanup - use sendBeacon for reliability
      const data = JSON.stringify({
        channelName: agoraChannel,
        roomName: livekitRoomName,
        reason: 'tab_close_or_refresh'
      });
      
      // Use sendBeacon for reliable cleanup on tab close
      if (agoraChannel) {
        navigator.sendBeacon(`${BACKEND_URL}/api/agora/session-end`, data);
      }
      if (livekitRoomName) {
        navigator.sendBeacon(`${BACKEND_URL}/api/livekit/session-end`, data);
      }
      if (currentStream?.stream_id) {
        navigator.sendBeacon(`${BACKEND_URL}/api/streams/${currentStream.stream_id}/force-stop`, '{}');
      }
      
      // Show confirmation dialog
      event.preventDefault();
      event.returnValue = 'You are currently live! Leaving will end your stream.';
      return event.returnValue;
    };
    
    // Handler for page hide (mobile backgrounding, tab switching)
    const handlePageHide = () => {
      console.log('🚨 pagehide - Force ending session');
      forceEndAllSessions('page_hide');
    };
    
    // Handler for visibility change (tab switching, app backgrounding)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        console.log('🚨 visibilitychange:hidden - User left tab while live');
        // Don't immediately end - but log and track
        // Could implement a grace period here if desired
      }
    };
    
    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isActiveBroadcaster, agoraChannel, livekitRoomName, currentStream, forceEndAllSessions]);
  
  // NAVIGATION LOCK: Prevent broadcaster from navigating while live
  useEffect(() => {
    if (!isActiveBroadcaster) return;
    
    // Block browser back/forward
    const handlePopState = (event) => {
      console.log('🚫 Navigation blocked - Broadcaster is live');
      // Push current state back to prevent navigation
      window.history.pushState(null, '', window.location.href);
      
      // Show alert
      toast.error('You cannot navigate while live! End your stream first.');
    };
    
    // Push initial state to enable popstate blocking
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isActiveBroadcaster]);
  
  // HEARTBEAT: Send periodic heartbeat while broadcasting
  useEffect(() => {
    if (!isActiveBroadcaster || !currentStream?.stream_id) return;
    
    console.log('💓 Starting heartbeat for stream:', currentStream.stream_id);
    
    // Send heartbeat immediately
    const sendHeartbeat = async () => {
      const startTime = Date.now();
      try {
        await api.post(`/streams/${currentStream.stream_id}/heartbeat`);
        const latency = Date.now() - startTime;
        console.log('💓 Heartbeat sent, latency:', latency, 'ms');
        
        // Update stream health - successful heartbeat
        setStreamHealth(prev => ({
          ...prev,
          status: 'healthy',
          lastHeartbeat: new Date(),
          consecutiveFailures: 0,
          latency: latency
        }));
      } catch (err) {
        console.error('💔 Heartbeat failed:', err);
        
        // Update stream health - failed heartbeat
        setStreamHealth(prev => {
          const failures = prev.consecutiveFailures + 1;
          return {
            ...prev,
            status: failures >= 3 ? 'critical' : failures >= 1 ? 'warning' : 'healthy',
            consecutiveFailures: failures,
            latency: null
          };
        });
      }
    };
    
    sendHeartbeat(); // Initial heartbeat
    
    // Send heartbeat every 30 seconds
    const heartbeatInterval = setInterval(sendHeartbeat, 30000);
    
    // Update uptime every second
    const uptimeInterval = setInterval(() => {
      setStreamHealth(prev => ({
        ...prev,
        uptime: prev.uptime + 1
      }));
    }, 1000);
    
    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(uptimeInterval);
      console.log('💓 Heartbeat stopped');
      // Reset health on stop
      setStreamHealth({
        status: 'healthy',
        lastHeartbeat: null,
        consecutiveFailures: 0,
        latency: null,
        uptime: 0
      });
    };
  }, [isActiveBroadcaster, currentStream?.stream_id]);

  // Fetch live streams for suggestions
  const fetchLiveStreams = async () => {
    try {
      const response = await api.get('/feeds/live-now');
      setSuggestedStreams(response.data.streams || []);
    } catch (error) {
      console.error('Failed to fetch live streams:', error);
    }
  };
  
  // Connect WebSocket for in-stream game
  const connectGameWebSocket = (gameId, userId) => {
    return new Promise((resolve, reject) => {
      try {
        const backendUrl = API_BASE_URL || 'http://localhost:8001';
        console.log('BACKEND_URL from config:', backendUrl);
        
        // Add /api prefix for WebSocket (ingress routing requirement)
        const baseUrl = backendUrl.replace(/\/api$/, ''); // Remove /api if present
        const wsUrl = baseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
        const fullWsUrl = `${wsUrl}/api/ws/game/${gameId}?user_id=${userId}`; // Explicitly add /api/ws
        
        console.log('🔌 Connecting to game WebSocket:', fullWsUrl);
        console.log('🔍 WebSocket URL breakdown:', { backendUrl, baseUrl, wsUrl, fullWsUrl });
        const socket = new WebSocket(fullWsUrl);
        
        socket.onopen = () => {
          console.log('✅ In-stream game WebSocket connected - connection fully established!');
          setGameWsConnected(true);
          setGameWs(socket);
          resolve(socket); // Resolve promise when connection is ready
        };
        
        socket.onmessage = (event) => {
          const data = JSON.parse(event.data);
          console.log('🎮 Game WebSocket message:', data);
          handleGameWebSocketMessage(data);
        };
        
        socket.onerror = (error) => {
          console.error('❌ Game WebSocket error:', error);
          setGameWsConnected(false);
          reject(error);
        };
        
        socket.onclose = () => {
          console.log('Game WebSocket disconnected');
          setGameWsConnected(false);
        };
        
        // Timeout after 10 seconds if connection not established
        setTimeout(() => {
          if (socket.readyState !== WebSocket.OPEN) {
            console.error('❌ WebSocket connection timeout after 10 seconds');
            socket.close();
            reject(new Error('WebSocket connection timeout'));
          }
        }, 10000);
        
      } catch (error) {
        console.error('Failed to connect game WebSocket:', error);
        reject(error);
      }
    });
  };
  
  // Clear the in-stream board state whenever the live game ends or the
  // broadcaster leaves it (covers every setInStreamGame(null) exit path).
  useEffect(() => {
    if (!inStreamGame) { setC8LiveState(null); setCheckersLiveState(null); }
  }, [inStreamGame]);
  // Card sorting helper - order by suit (Spades, Diamonds, Clubs, Hearts) then by value
  const sortCards = (cards) => {
    // Sort order: Spades (including 2♦ as trump) > Diamonds > Clubs > Hearts
    // Trump hierarchy: Big Joker > Little Joker > 2♦ > 2♠ > A♠ > K♠...
    const valueOrder = { 'BIG_JOKER': 1000, 'LITTLE_JOKER': 999, 'A': 14, 'K': 13, 'Q': 12, 'J': 11, '10': 10, '9': 9, '8': 8, '7': 7, '6': 6, '5': 5, '4': 4, '3': 3, '2': 2 };
    
    // Helper to check if card is a trump
    const isTrump = (card) => {
      return card.suit === 'spades' || 
             (card.suit === 'diamonds' && card.value === '2') ||
             card.value === 'BIG_JOKER' || 
             card.value === 'LITTLE_JOKER';
    };
    
    // Get trump rank for sorting
    const getTrumpRank = (card) => {
      if (card.value === 'BIG_JOKER') return 1000;
      if (card.value === 'LITTLE_JOKER') return 999;
      if (card.suit === 'diamonds' && card.value === '2') return 998; // 2♦ is 3rd highest trump
      if (card.suit === 'spades' && card.value === '2') return 997;  // 2♠ is 4th highest trump
      if (card.suit === 'spades') {
        // Regular spades: A, K, Q, J, 10, 9, 8, 7, 6, 5, 4, 3
        const spadeRanks = { 'A': 996, 'K': 995, 'Q': 994, 'J': 993, '10': 992, '9': 991, '8': 990, '7': 989, '6': 988, '5': 987, '4': 986, '3': 985 };
        return spadeRanks[card.value] || 0;
      }
      return 0;
    };
    
    return [...cards].sort((a, b) => {
      const aIsTrump = isTrump(a);
      const bIsTrump = isTrump(b);
      
      // Trumps first
      if (aIsTrump && !bIsTrump) return -1;
      if (!aIsTrump && bIsTrump) return 1;
      
      // Both trumps - sort by trump rank (highest first)
      if (aIsTrump && bIsTrump) {
        return getTrumpRank(b) - getTrumpRank(a);
      }
      
      // Neither is trump - sort by suit: Diamonds, Clubs, Hearts
      const suitOrder = { 'diamonds': 0, 'clubs': 1, 'hearts': 2 };
      const suitDiff = suitOrder[a.suit] - suitOrder[b.suit];
      if (suitDiff !== 0) return suitDiff;
      
      // Same suit - sort by value (high to low)
      return (valueOrder[b.value] || 0) - (valueOrder[a.value] || 0);
    });
  };

  // Handle game WebSocket messages
  const handleGameWebSocketMessage = (data) => {
    const { type } = data;

    // Crazy Eights in-stream (Jul 27, 2026) — the c8_state push is the
    // authoritative view for each participant. Store it separately so the
    // Spades gameState pipeline stays untouched.
    if (type === 'c8_state') {
      // Part 5c (Feb 19, 2026): render-gate log — if the C8 board never
      // appears, this line tells us whether the state landed but was
      // blocked by inStreamGame/gameState precedence.
      console.log('[C8] gate', {
        inStream: !!inStreamGame,
        hasState: !!data.state,
        hasSpades: !!gameState,
        winner: data.state?.winner,
      });
      // Part 2 (Feb 19, 2026): timer_paused toast — surfaces the backend
      // consecutive-timeout brake so players know to make a move to
      // resume. Fires once per pause event; muted for game-over pushes.
      if (data.action === 'timer_paused' && data.state?.state !== 'GAME_OVER') {
        toast('Turn timer paused — make a move to resume', {
          icon: '⏸️',
          duration: 5000,
        });
      }
      // Part 2b (Feb 19, 2026): stale-seq drop. If the incoming state's
      // seq is <= the current state's seq, ignore — some other push
      // already advanced us past this snapshot.
      const incomingSeq = data.state?.seq;
      const currentSeq = c8LiveState?.seq;
      if (typeof incomingSeq === 'number' && typeof currentSeq === 'number' && incomingSeq <= currentSeq) {
        console.log('[sync] stale c8 seq dropped', { incomingSeq, currentSeq });
        return;
      }
      setC8LiveState(data.state);
      // Fire-and-forget match-record capture the first time this game
      // reaches a winner. window-level Set dedupes across state pushes
      // (each participant's client also runs this — the backend
      // endpoint is idempotent via upsert on game_id).
      try {
        const st = data.state || {};
        if (st.winner && st.state === 'GAME_OVER') {
          window.__c8_capturedGames = window.__c8_capturedGames || new Set();
          const gid = data.game_id;
          if (gid && !window.__c8_capturedGames.has(gid)) {
            window.__c8_capturedGames.add(gid);
            // Part 4 (Feb 19, 2026): bump the per-link session win counter
            // for the winner exactly once per game_id.
            setSessionWins((prev) => ({
              ...prev,
              [st.winner]: (prev[st.winner] || 0) + 1,
            }));
            // Part 3 (Feb 19, 2026): capture context for the auto-return
            // overlay. Names resolve via linkPartners / self.
            const winnerName = st.winner === user?.id
              ? (user?.name || 'You')
              : ((linkPartners || []).find((x) => x.id === st.winner)?.name || 'Guest');
            setLastGameEnd({
              gameType: 'crazy_eights',
              gameId: gid,
              winnerId: st.winner,
              winnerName,
              players: st.players || [],
              config: { rules: st.rules || {} },
            });
            const players = st.players || [];
            captureAndUploadGameResult({
              gameId: gid,
              gameType: 'crazy_eights',
              selector: '[data-testid="c8-board"]',
              isPractice: false,
              winnerId: st.winner,
              resultSummary: `Winner: ${st.winner}`,
              wager: 0,
              // Minimal participants — backend resolves timestamps
              // from the game doc; the handle enrichment happens
              // client-side on any surface that already knows names.
              participants: players.map((pid) => ({ user_id: pid, name: pid })),
            });
          }
        }
      } catch (e) { /* never disrupt gameplay */ }
      return;
    }
    // Checkers in-stream — mirrors the c8_state pattern. The checkers_state
    // push is authoritative for each participant; store it separately so the
    // Spades/C8 pipelines stay untouched. The board renders in the live
    // overlay (no navigation away from the stream).
    if (type === 'checkers_state') {
      // color_map is a SIBLING of `state` in the broadcast (not inside
      // public_state) — merge it in so CheckersBoard can resolve myColor.
      const cmap = data.color_map || (data.state && data.state.color_map) || {};
      // Part 2 (Feb 19, 2026): timer_paused toast for checkers.
      if (data.action === 'timer_paused' && !data.state?.winner) {
        toast('Turn timer paused — make a move to resume', {
          icon: '⏸️',
          duration: 5000,
        });
      }
      // Part 2b (Feb 19, 2026): stale-seq drop for checkers too.
      const incomingSeq = data.state?.seq;
      const currentSeq = checkersLiveState?.seq;
      if (typeof incomingSeq === 'number' && typeof currentSeq === 'number' && incomingSeq <= currentSeq) {
        console.log('[sync] stale checkers seq dropped', { incomingSeq, currentSeq });
        return;
      }
      setCheckersLiveState(data.state ? { ...data.state, color_map: cmap } : null);
      try {
        const st = data.state || {};
        if (st.winner || st.draw) {
          window.__checkers_capturedGames = window.__checkers_capturedGames || new Set();
          const gid = data.game_id;
          if (gid && !window.__checkers_capturedGames.has(gid)) {
            window.__checkers_capturedGames.add(gid);
            const winnerUserId = st.winner ? (Object.keys(cmap).find((uid) => cmap[uid] === st.winner) || null) : null;
            // Part 4 (Feb 19, 2026): session-wins bump for checkers.
            if (winnerUserId) {
              setSessionWins((prev) => ({
                ...prev,
                [winnerUserId]: (prev[winnerUserId] || 0) + 1,
              }));
            }
            // Part 3 (Feb 19, 2026): capture context for the end overlay.
            if (winnerUserId || st.draw) {
              const winnerName = winnerUserId === user?.id
                ? (user?.name || 'You')
                : ((linkPartners || []).find((x) => x.id === winnerUserId)?.name
                   || (st.draw ? '' : 'Guest'));
              setLastGameEnd({
                gameType: 'checkers',
                gameId: gid,
                winnerId: winnerUserId,
                winnerName: st.draw ? 'Draw' : winnerName,
                players: Object.keys(cmap),
                config: {},
              });
            }
            captureAndUploadGameResult({
              gameId: gid,
              gameType: 'checkers',
              selector: '[data-testid="checkers-in-stream-board"]',
              isPractice: false,
              winnerId: winnerUserId,
              resultSummary: st.draw ? 'Draw' : `Winner: ${st.winner}`,
              wager: 0,
              participants: Object.keys(cmap).map((uid) => ({ user_id: uid, name: uid })),
            });
          }
        }
      } catch (e) { /* never disrupt gameplay */ }
      return;
    }
    switch (type) {
      case 'game_state':          // spades engine pushes (deal, new hand, resync)
      case 'game_state_update': {
        console.log('📊 Received game state update:', data.game_state);
        setGameState(data.game_state);
        // Update player's cards - backend sends myCards field directly
        if (data.game_state.myCards) {
          const sortedCards = sortCards(data.game_state.myCards);
          setMyCards(sortedCards);
          console.log('🎴 My cards updated:', sortedCards.length, 'cards');
        }
        // BLIND-BID GATE (Feb 2026): on a fresh bidding hand, a team down
        // 100+ keeps its cards FACE-DOWN until each player bids blind or
        // peeks (View Cards). NY rules: blind is decided before looking.
        const gs = data.game_state || {};
        if (gs.phase === 'bidding' && gs.myPosition != null) {
          const myTeam = gs.myPosition % 2;
          const scores = gs.matchScores || [0, 0];
          const eligible = scores[myTeam] <= scores[1 - myTeam] - 100;
          const alreadyBid = (gs.players || []).some(
            (p) => p.position === gs.myPosition && p.bid != null,
          );
          setSpadesHandRevealed(!(eligible && !alreadyBid));
        } else {
          setSpadesHandRevealed(true);
        }
        break;
      }
      case 'bid_submitted':
        console.log(`Player at seat ${data.seat} bid: ${data.bid}`);
        toast.info(`Seat ${data.seat + 1} bid ${data.bid}${data.is_blind ? ' BLIND' : ''}`);
        // Advance the bid turn; when the 4th bid lands the server also
        // pushes a fresh per-player game_state that flips phase to playing.
        // Also record the bid on the seat so the bid tracker shows WHO bid
        // WHAT to everyone at the table.
        setGameState((prev) => prev ? ({
          ...prev,
          currentPlayer: (data.current_player ?? prev.currentPlayer),
          teamBids: data.team_bids || prev.teamBids,
          players: (prev.players || []).map((p) =>
            p.position === data.seat ? { ...p, bid: data.bid, is_blind: data.is_blind } : p),
        }) : prev);
        break;
      case 'card_played':
        // Server-authoritative play echo: current_trick renders in the
        // center display; current_player advances the YOUR TURN banner.
        console.log(`Player at seat ${data.seat} played card:`, data.card);
        currentTrickRef.current = data.current_trick || currentTrickRef.current;
        setGameState((prev) => prev ? ({
          ...prev,
          currentTrick: data.current_trick || prev.currentTrick,
          currentPlayer: (data.current_player ?? prev.currentPlayer),
          spadesBroken: (data.spades_broken ?? prev.spadesBroken),
        }) : prev);
        break;
      case 'trick_complete': {
        console.log(`Book won by seat ${data.winner_seat}`);
        toast.success(`${data.winner_name || `Seat ${data.winner_seat + 1}`} takes the book!`);
        // HOLD the completed trick on screen for 3s so EVERYONE (including
        // whoever played the 4th card) sees the full book land, even while
        // the winner leads the next trick underneath.
        const heldTrick = currentTrickRef.current;
        if ((heldTrick || []).length >= 4) {
          setLastTrickHold({ trick: heldTrick, winner: data.winner_name || `Seat ${data.winner_seat + 1}` });
          setTimeout(() => setLastTrickHold(null), 3000);
        }
        currentTrickRef.current = [];
        setGameState((prev) => prev ? ({
          ...prev,
          booksWon: data.books_won || prev.booksWon,
          currentPlayer: (data.current_player ?? data.winner_seat ?? prev.currentPlayer),
        }) : prev);
        // Clear the live trick promptly — the 3s hold above owns the display.
        setTimeout(() => {
          setGameState((prev) => prev && (prev.currentTrick || []).length >= 4
            ? ({ ...prev, currentTrick: [] })
            : prev);
        }, 400);
        break;
      }
      case 'hand_complete':
        console.log('Hand complete:', data);
        toast.info(`Hand ${data.hand_number} complete! Score — T1: ${data.match_scores?.[0] ?? '?'} | T2: ${data.match_scores?.[1] ?? '?'}`);
        setGameState((prev) => prev ? ({
          ...prev,
          matchScores: data.match_scores || prev.matchScores,
        }) : prev);
        break;
      case 'match_complete': {
        console.log('Match complete:', data);
        toast.success(`Match complete! Winner: Team ${data.winning_team + 1}`);
        // Settle the entry payout — idempotent server-side (completed
        // flag), so it's safe for all four clients to fire it.
        const mcGameId = data.game_id || inStreamGame;
        if (mcGameId) {
          api.post(`/game/${mcGameId}/complete`, {
            winningTeam: data.winning_team,
            finalScores: data.final_scores || [0, 0],
          }).catch(() => {});
        }
        setInStreamGame(null);
        setGameState(null);
        setMyCards([]);
        break;
      }
      case 'move_rejected':
        // Server refused the play/bid — resync so the optimistically
        // removed card returns to the fan.
        toast.error(data.reason || 'Move rejected');
        if ((data.game_type || 'spades') === 'spades') {
          hydrateSpadesFromServer(data.game_id || inStreamGame);
        }
        break;

      // Part 3f (Feb 19, 2026): the game WS also emits `game_started`
      // when the host fires /game/{id}/start. It's a state-refresh
      // trigger — the follow-up `game_state` / `c8_state` / etc. messages
      // carry the actual dealt hand. Handled explicitly so it no longer
      // shows up as "Unknown game message type" in the console.
      case 'game_started':
        console.log('🎮 game_started (game WS):', data);
        if ((data.game_type || 'spades') === 'spades') {
          hydrateSpadesFromServer(data.game_id || inStreamGame);
        }
        break;

      // Part 4a (Feb 19, 2026): server-side move rejection. The engine
      // returns errors like "not your turn", "must play a 2 or draw",
      // "eight cancels disabled" — surface them so the player understands
      // why their click bounced instead of assuming the board is frozen.
      case 'c8_error':
        console.warn('[C8] server rejected action:', data);
        toast.error(data.detail || data.error || data.message || 'Move rejected');
        break;
      
      default:
        console.log('Unknown game message type:', type);
    }
  };
  
  // Send game WebSocket message
  const sendGameMessage = (message) => {
    if (gameWs && gameWs.readyState === WebSocket.OPEN) {
      gameWs.send(JSON.stringify(message));
      console.log('📤 Sent game message:', message);
    } else {
      console.warn('Game WebSocket not connected');
    }
  };
  
  // Cleanup game WebSocket
  useEffect(() => {
    return () => {
      if (gameWs) {
        gameWs.close();
      }
    };
  }, [gameWs]);

  // Part 2c (Feb 19, 2026): 8s resync loop for C8 + Checkers. Sends
  // `c8_sync` / `checkers_sync` on the game WS; the backend replies on
  // this socket with the current per-viewer projection under
  // action='resync'. Self-heals any lost push within 8s, including
  // mobile Safari tab throttling. The stale-seq drop keeps this cheap.
  useEffect(() => {
    if (!inStreamGame || !gameWs || (!c8LiveState && !checkersLiveState)) return;
    const kind = c8LiveState ? 'c8_sync' : 'checkers_sync';
    const id = setInterval(() => {
      try {
        if (gameWs.readyState === 1) gameWs.send(JSON.stringify({ type: kind }));
      } catch { /* noop */ }
    }, 8000);
    return () => clearInterval(id);
  }, [inStreamGame, gameWs, !!c8LiveState, !!checkersLiveState]);
  
  // Fetch online users when invite modal opens
  useEffect(() => {
    if (showInviteModal) {
      fetchOnlineUsers();
    }
  }, [showInviteModal]);
  
  const fetchOnlineUsers = async () => {
    setLoadingOnlineUsers(true);
    try {
      const response = await api.get('/users/online');
      setOnlineUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setOnlineUsers([]);
    } finally {
      setLoadingOnlineUsers(false);
    }
  };
  
  // Filter users based on search query (null-guarded — a single user with a
  // missing name/handle used to throw here and blank the WHOLE list), then
  // surface live/online broadcasters first so a just-unlinked partner (still
  // live) shows at the TOP of the invite box instead of buried or missed.
  const filteredOnlineUsers = onlineUsers
    .filter(u =>
      (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.handle || '').toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) =>
      (Number(!!b.is_live) - Number(!!a.is_live)) ||
      (Number(!!b.is_online) - Number(!!a.is_online))
    );
  
  // Game States
  const [isInGame, setIsInGame] = useState(false);
  const [currentGameId, setCurrentGameId] = useState(null);
  const [playedCards, setPlayedCards] = useState([]);
  const [currentTurn, setCurrentTurn] = useState(0);
  const [gamePhase, setGamePhase] = useState('bidding');
  const [bids, setBids] = useState([0, 0, 0, 0]);
  const [myBid, setMyBid] = useState(0);
  const [gamePlayers, setGamePlayers] = useState([]);
  
  const webrtc = useWebRTC();

  // Relocated from ~L5677 because these depend on `webrtc` (declared just
  // above). Placing them earlier caused
  // "Cannot access 'webrtc' before initialization" at mount and crashed the
  // Live page in prod (Feb 2026). Do NOT move back above `useWebRTC()`.
  const livekitPublisherRef = useRef(null);

  // ============================================================
  // LINK v2 — SINGLE ENTRY POINT to switch broadcaster from SOLO
  // publisher to the shared LiveKit linked room. Enforces:
  //   1) IDEMPOTENCE: if we're already in the same linked roomName,
  //      no refetch and no setLinkRoomInfo (was killing conn #1).
  //   2) EXACTLY-ONE-ENGINE: solo LiveKitStreamPublisher is fully
  //      torn down BEFORE we mount LiveKitLinkGrid — solo and grid
  //      must never be mounted simultaneously.
  //   3) Local WebRTC preview tracks are stopped.
  // Returns the linkRoomInfo (existing or new) on success.
  // ============================================================
  const initLiveKitLinkRoom = useCallback(async (roomName, opts = {}) => {
    if (!LIVEKIT_LINKS || !roomName) return null;
    // Idempotence guard — skip if we're already in this exact room.
    if (linkRoomInfoRef.current?.roomName === roomName) {
      console.log('[LiveKitLink] init skipped — already in linked room:', roomName);
      return linkRoomInfoRef.current;
    }
    try {
      // LINK v2 RACE FIX (Aug 18, 2026): the accepter's device asks for the
      // linked-room token BEFORE the inviter's client has written
      // /streams/link-started + /link/add-broadcaster, so the very first
      // /livekit/token call can 403 on membership. Retry a few times before
      // giving up. All other errors propagate immediately.
      let lk = null;
      let lastErr = null;
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          lk = await api.post('/livekit/token', {
            roomName,
            isPublisher: true,
            sessionType: opts.sessionType || 'linked_broadcaster',
          });
          break;
        } catch (err) {
          lastErr = err;
          // 403 = membership race (accept registered, link docs still
          // being written). Retry briefly; rethrow anything else.
          if (err?.response?.status === 403 && attempt < 3) {
            console.warn(`[LiveKitLink] token 403 (membership race) — retry ${attempt + 1}/3`);
            await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
            continue;
          }
          throw err;
        }
      }
      if (!lk) throw lastErr;
      // TEAR DOWN SOLO PUBLISHER FIRST — the LiveKitStreamPublisher
      // unmount hook calls room.disconnect() and leaves solo_stream_{id}.
      // Without this, the solo Room stays connected and the grid Room
      // gets torn down within ~1s (observed on prod).
      try {
        if (livekitPublisherRef.current?.disconnect) {
          try { livekitPublisherRef.current.disconnect(); } catch { /* noop */ }
        }
      } catch { /* noop */ }
      setLivekitToken(null);
      setLivekitRoomName(null);
      setLivekitUrl(null);
      setUseLiveKit(false);

      const info = {
        token: lk.data.token,
        roomName: lk.data.roomName || roomName,
        url: lk.data.url || null,
      };
      linkRoomInfoRef.current = info;
      setLinkRoomInfo(info);
      // Stop any local preview tracks — LiveKit takes over.
      try {
        if (webrtc && webrtc.localStream) {
          webrtc.localStream.getTracks().forEach(t => t.stop());
        }
      } catch { /* noop */ }
      return info;
    } catch (e) {
      console.error('[LiveKitLink] initLiveKitLinkRoom failed for room', roomName, e);
      throw e;
    }
  }, [webrtc]);

  // ============================================================
  // LINK v2 — REVERSE HANDOFF: when we leave a linked room but the
  // stream is still live (partner left, unlink, game end), start a
  // fresh solo LiveKit publisher so we keep broadcasting.
  // ============================================================
  const wasLinkedRef = useRef(false);
  useEffect(() => {
    const wasLinked = wasLinkedRef.current;
    const isLinked = !!linkRoomInfo;
    wasLinkedRef.current = isLinked;
    if (!(wasLinked && !isLinked)) return;
    if (!currentStream?.stream_id || isPreviewMode) return;
    let cancelled = false;
    (async () => {
      try {
        const roomName = `solo_stream_${currentStream.stream_id}`;
        const lk = await api.post('/livekit/token', {
          roomName,
          isPublisher: true,
          sessionType: 'solo_stream',
        });
        if (cancelled) return;
        setLivekitToken(lk.data.token);
        setLivekitRoomName(lk.data.roomName || roomName);
        setLivekitUrl(lk.data.url || null);
        setUseLiveKit(true);
        console.log('[LiveKitLink] Solo publisher restarted after unlink:', roomName);
      } catch (e) {
        console.error('[LiveKitLink] Solo restart after unlink failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [linkRoomInfo, currentStream?.stream_id, isPreviewMode]);

  // DEV-only bridge to poke linkRoomInfo from Playwright/console — used by
  // the "PROVE the mount path" QUICK CHECK. Guarded so it never ships to a
  // production bundle build. Consumers: window.__gemsDevSetLinkRoomInfo(info).
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    window.__gemsDevSetLinkRoomInfo = (info) => setLinkRoomInfo(info);
    window.__gemsDevSetInStreamGame = (v) => setInStreamGame(v);
    // Part 1 (Feb 19, 2026): smoke bridge — poke a fake c8LiveState into
    // the LivePage so we can verify the desktop band layout without a
    // real game.
    window.__gemsDevSetC8LiveState = (s) => setC8LiveState(s);
    return () => {
      try { delete window.__gemsDevSetLinkRoomInfo; } catch { /* noop */ }
      try { delete window.__gemsDevSetInStreamGame; } catch { /* noop */ }
      try { delete window.__gemsDevSetC8LiveState; } catch { /* noop */ }
    };
  }, []);

  // Auto-start preview when component mounts
  useEffect(() => {
    if (!webrtc.isStreaming && !isPreviewMode) {
      startPreview();
    }
  }, []);

  // Attach video stream to all video elements when layout changes
  useEffect(() => {
    if (!webrtc.localStream) return;
    
    const attachStream = () => {
      // Attach to 2-way split
      const video2way = document.getElementById('host-video-2way');
      if (video2way && !video2way.srcObject) {
        video2way.srcObject = webrtc.localStream;
        console.log('✅ Attached stream to 2-way video');
      }
      
      // Attach to 3-way split
      const video3way = document.getElementById('host-video-3way');
      if (video3way && !video3way.srcObject) {
        video3way.srcObject = webrtc.localStream;
        console.log('✅ Attached stream to 3-way video');
      }
      
      // Attach to 4-way split
      const video4way = document.getElementById('host-video-4way');
      if (video4way && !video4way.srcObject) {
        video4way.srcObject = webrtc.localStream;
        console.log('✅ Attached stream to 4-way video');
      }
    };
    
    // Attach immediately and also after a small delay for React render
    attachStream();
    const timeout = setTimeout(attachStream, 100);
    
    return () => clearTimeout(timeout);
  }, [webrtc.localStream, invitedPlayers.length]);
  
  // Fetch tournaments when modal opens
  useEffect(() => {
    if (showTournamentsModal) {
      const fetchTournaments = async () => {
        try {
          const response = await api.get('/tournaments');
          setTournaments(response.data.tournaments || []);
        } catch (error) {
          console.log('Error fetching tournaments:', error);
        }
      };
      fetchTournaments();
    }
  }, [showTournamentsModal]);

  // Poll for viewer count and rubies updates when streaming
  useEffect(() => {
    if (!currentStream || isPreviewMode) return;

    const updateStreamStats = async () => {
      try {
        const response = await api.get(`/streams/${currentStream.stream_id}`);
        setRealTimeViewerCount(response.data.viewer_count || 0);
        setStreamRubiesEarned(response.data.rubies_earned || 0);
        // Also update currentStream object
        setCurrentStream(prev => ({
          ...prev,
          viewer_count: response.data.viewer_count || 0,
          rubies_earned: response.data.rubies_earned || 0
        }));
        // SEP 3 2026 — LINK ROSTER RECONCILIATION NET: the stream doc is
        // the server's authoritative linked-session roster (link-started /
        // add-broadcaster write it to EVERY member's stream). If it lists
        // me and differs from local state, adopt it wholesale. Heals any
        // missed or out-of-order link event on BOTH the inviter's and the
        // accepter's clients within one 15s tick — this is what guarantees
        // every seat shows up in the Spades table no matter who opens it.
        const srvUsers = response.data.linked_users;
        if (Array.isArray(srvUsers) && srvUsers.includes(user?.id)) {
          const local = linkedRosterRef.current?.users || [];
          const differs = srvUsers.length !== local.length || srvUsers.some((u) => !local.includes(u));
          if (differs) {
            console.log('🔗 [LinkRoster] reconciled from stream doc:', srvUsers);
            setAllLinkedUsers(srvUsers);
            setAllLinkedUserNames(response.data.linked_user_names || []);
            if (response.data.linked_session_id) setLinkedSessionId(response.data.linked_session_id);
          }
        }
      } catch (error) {
        console.error('Failed to update stream stats:', error);
      }
    };

    // Update immediately
    updateStreamStats();

    // POLL WIDENED (Feb 2026): viewer_count / rubies_earned are also
    // pushed to the client in real time via WebSocket heartbeats and
    // the Universal Sync 'wallet_update' events. This REST poll is
    // now a slow reconciliation net (5s → 15s) so it doesn't force
    // the large live video container to re-render every tick.
    const interval = setInterval(updateStreamStats, 15000);

    return () => clearInterval(interval);
  }, [currentStream?.stream_id, isPreviewMode, user?.id]);

  // Track peak viewers and update stats during stream
  // NOTE: We include AI creators/chat bots in the same displayViewers total
  // so streamStats matches the live overlay (Feb 2026 consolidation).
  useEffect(() => {
    if (displayViewers > 0 && streamStartTime) {
      setStreamStats(prev => ({
        ...prev,
        peakViewers: Math.max(prev.peakViewers, displayViewers),
        totalViewers: displayViewers
      }));
    }
  }, [displayViewers, streamStartTime]);

  // Check if current user is admin/owner of the stream (for kick permissions)
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentStream?.stream_id || !user?.id) return;
      
      try {
        const response = await api.get(`/streams/${currentStream.stream_id}/is-admin`);
        setStreamAdminStatus(response.data);
      } catch (error) {
        setStreamAdminStatus({ is_admin: false, is_owner: false });
      }
    };
    
    checkAdminStatus();
  }, [currentStream?.stream_id, user?.id]);

  // Debug Agora state changes
  useEffect(() => {
    console.log('🔍 Agora State:', {
      useAgora,
      hasToken: !!agoraToken,
      hasChannel: !!agoraChannel,
      hasAppId: !!agoraAppId,
      isPreviewMode,
      shouldShowAgora: useAgora && agoraToken && agoraChannel && agoraAppId && !isPreviewMode
    });
  }, [useAgora, agoraToken, agoraChannel, agoraAppId, isPreviewMode]);

  // Update video stream without causing re-renders
  useEffect(() => {
    if (videoRef.current && webrtc.localStream) {
      videoRef.current.srcObject = webrtc.localStream;
    }
  }, [webrtc.localStream]);

  // Stream Chat WebSocket connection
  // In multiplayer, use sharedChatStreamId (host's stream) so both hosts share same chat
  useEffect(() => {
    if (!currentStream || isPreviewMode) {
      if (streamChatWs) {
        streamChatWs.close();
        setStreamChatWs(null);
      }
      return;
    }
    
    // Use shared chat stream ID if available (multiplayer), otherwise own stream
    const chatStreamId = sharedChatStreamId || currentStream.stream_id;
    
    // Connect to stream chat WebSocket
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/api/ws/stream/${chatStreamId}`;
    
    console.log('📡 Connecting to stream chat WebSocket:', wsUrl, sharedChatStreamId ? '(shared/unified)' : '(own stream)');
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('✅ Stream chat WebSocket connected');
      // Send join message
      ws.send(JSON.stringify({
        type: 'join',
        user_id: user?.id,
        username: user?.name || user?.username || 'Streamer',
        is_streamer: true
      }));
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('💬 Stream chat message:', data);
        
        if (data.type === 'chat_removed' && data.id) {
          setStreamChatMessages(prev => prev.filter(m => m.id !== data.id));
          return;
        }
        if (data.type === 'chat_rejected') {
          toast.error(data.reason || 'That message was not sent.');
          return;
        }
        if ((data.type === 'chat' || data.type === 'message' || data.type === 'gift' || data.type === 'heart') && data.user_id && liveHiddenRef.current.has(data.user_id)) return;
        if (data.type === 'chat' || data.type === 'message') {
          const messageContent = data.message || data.content;
          const senderUsername = data.username || data.user_name || 'Anonymous';
          
          setStreamChatMessages(prev => [...prev.slice(-99), {
            id: data.id || Date.now(),
            userId: data.user_id,
            user_id: data.user_id,
            username: senderUsername,
            message: messageContent,
            isStreamer: data.is_streamer || data.user_id === user?.id,
            isBot: data.is_bot || false,
            avatar: data.avatar,
            profilePic: data.user_picture,
            badge_type: data.badge_type,
            timestamp: new Date()
          }]);
          
          // Check if message is negative/hateful and trigger bot defense
          if (chatBotsEnabled && messageContent && isNegativeMessage(messageContent)) {
            botDefendStreamer(messageContent);
          }
          
          // Bots reply when a real user @-mentions them (social behavior).
          // Routed via sendBotMessage → WS → every viewer + broadcaster sees the same reply.
          if (!data.is_bot && messageContent && activeChatBots.length > 0 && !data.is_streamer && data.user_id !== user?.id) {
            const lower = messageContent.toLowerCase();
            const mentioned = activeChatBots.find(b => lower.includes('@' + b.name.toLowerCase()));
            if (mentioned) {
              const replies = [
                `@${senderUsername} heyyy! 👋 glad you're here!`,
                `@${senderUsername} yessir! What's good! 🔥`,
                `@${senderUsername} lol you already know 😂`,
                `@${senderUsername} facts! This stream is heating up`,
                `@${senderUsername} 💯💯 who you got winning?`,
                `@${senderUsername} haha I'm locked in, this game is intense`,
              ];
              const reply = replies[Math.floor(Math.random() * replies.length)];
              setTimeout(() => sendBotMessage('engagement', mentioned, reply), 2000 + Math.random() * 2500);
            }
          }
          
          // Track engaged viewers for analytics
          if (data.user_id && data.user_id !== user?.id) {
            setStreamStats(prev => ({
              ...prev,
              engagedViewers: prev.engagedViewers + 1
            }));
          }
        } else if (data.type === 'gift') {
          // Dedupe: REST broadcast + WS echo may arrive; play only once.
          window.__gemsSeenGiftEvents = window.__gemsSeenGiftEvents || new Set();
          if (data.gift_event_id) {
            if (window.__gemsSeenGiftEvents.has(data.gift_event_id)) return;
            window.__gemsSeenGiftEvents.add(data.gift_event_id);
            if (window.__gemsSeenGiftEvents.size > 200) window.__gemsSeenGiftEvents.clear();
          }
          // Handle gift notifications - show animation for broadcaster
          console.log('🎁 Gift received via WebSocket:', data);
          if (data.gift_id || data.reveal_gift_id) {
            window.dispatchEvent(new CustomEvent('gems:gift', {
              detail: { giftId: data.reveal_gift_id || data.gift_id, qty: data.quantity || 1, sender: data.sender }
            }));
          }
          
          // Add chat message
          setStreamChatMessages(prev => [...prev.slice(-99), {
            id: Date.now(),
            username: data.sender || data.username || 'Anonymous',
            message: `sent ${data.gift?.name || data.gift_name || 'a gift'} 🎁`,
            isGift: true,
            giftIcon: data.gift?.icon || data.gift_icon,
            timestamp: new Date()
          }]);
          
          // Update gift stats
          setStreamStats(prev => ({
            ...prev,
            newRubies: prev.newRubies + (data.gift?.ruby_value || data.ruby_value || 0),
            giftGivers: prev.giftGivers + 1
          }));
          
          // Show gift animation for broadcaster (legacy emoji fallback for old messages).
          if (!(data.gift_id || data.reveal_gift_id) && data.gift) {
            showGiftAnimation({
              name: data.gift.name,
              icon: data.gift.icon,
              emerald_cost: data.gift.emerald_cost,
              ruby_value: data.gift.ruby_value
            }, data.sender || 'Anonymous');
          }
        } else if (data.type === 'viewer_join') {
          setStreamChatMessages(prev => [...prev.slice(-99), {
            id: Date.now(),
            username: data.username || 'A viewer',
            message: 'joined the stream 👋',
            isSystem: true,
            timestamp: new Date()
          }]);
        }
      } catch (err) {
        console.error('Failed to parse chat message:', err);
      }
    };
    
    ws.onerror = (error) => {
      console.error('❌ Stream chat WebSocket error:', error);
    };
    
    ws.onclose = () => {
      console.log('🔌 Stream chat WebSocket closed');
    };
    
    setStreamChatWs(ws);
    
    return () => {
      ws.close();
    };
  }, [currentStream?.stream_id, isPreviewMode, user?.id, sharedChatStreamId]);

  // Send chat message function
  const sendStreamChatMessage = () => {
    if (!streamChatInput.trim() || !streamChatWs || streamChatWs.readyState !== WebSocket.OPEN) {
      return;
    }
    
    const message = {
      type: 'chat',
      user_id: user?.id,
      username: user?.name || user?.username || 'Streamer',
      message: streamChatInput.trim(),
      is_streamer: true,
      profilePic: user?.profilePhoto
    };
    
    streamChatWs.send(JSON.stringify(message));
    
    // Don't add optimistically - we'll receive it back from WebSocket to avoid duplicates
    setStreamChatInput('');
  };

  // Handle @ mentions in chat
  const handleChatInputChange = (e) => {
    const value = e.target.value;
    setStreamChatInput(value);
    
    // Check for @ mention
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const query = value.slice(lastAtIndex + 1).toLowerCase();
      const textAfterAt = value.slice(lastAtIndex);
      
      // Only show suggestions if @ is at the start or after a space, and no space after @
      const charBeforeAt = lastAtIndex > 0 ? value[lastAtIndex - 1] : ' ';
      if ((charBeforeAt === ' ' || lastAtIndex === 0) && !query.includes(' ')) {
        setMentionQuery(query);
        
        // Get unique usernames from chat messages and bots
        const chatUsers = streamChatMessages
          .filter(msg => !msg.isSystem)
          .map(msg => ({ 
            username: msg.username, 
            profilePic: msg.profilePic 
          }));
        
        const botUsers = activeChatBots.map(bot => ({
          username: bot.name,
          profilePic: bot.profilePic
        }));
        
        // Add host
        const allUsers = [
          { username: user?.name || 'Host', profilePic: user?.profilePhoto, isHost: true },
          ...chatUsers,
          ...botUsers
        ];
        
        // Filter unique and matching
        const uniqueUsers = Array.from(new Map(allUsers.map(u => [u.username, u])).values());
        const filtered = uniqueUsers.filter(u => 
          u.username.toLowerCase().includes(query)
        ).slice(0, 5);
        
        setMentionSuggestions(filtered);
        setShowMentionSuggestions(filtered.length > 0);
      } else {
        setShowMentionSuggestions(false);
      }
    } else {
      setShowMentionSuggestions(false);
    }
  };

  // Insert mention into chat input
  const insertMention = (username) => {
    const lastAtIndex = streamChatInput.lastIndexOf('@');
    const newValue = streamChatInput.slice(0, lastAtIndex) + '@' + username + ' ';
    setStreamChatInput(newValue);
    setShowMentionSuggestions(false);
  };

  // Toggle microphone mute/unmute for host
  // Toggle microphone mute/unmute for host
  const isMicMutedRef = useRef(false);
  useEffect(() => { isMicMutedRef.current = isMicMuted; }, [isMicMuted]);
  const handleSoloPublishSuccess = useCallback((info) => {
    console.log('✅ LiveKit publishing:', info);
    // Re-apply the user's mute choice on every (re)publish — solo→linked→solo.
    if (isMicMutedRef.current) {
      livekitPublisherRef.current?.setMicEnabled?.(false)?.catch?.(() => {});
    }
  }, []);
  const handleSoloPublishError = useCallback((err) => {
    console.error('❌ LiveKit error:', err);
  }, []);
  const toggleMicMute = async () => {
    const next = !isMicMuted;
    const enable = !next;
    let applied = false;
    // Linked room first (Link v2), then the solo publisher. Never the legacy
    // preview stream — its tracks are stopped once LiveKit takes over.
    if (linkRoomRef.current) {
      try { await linkRoomRef.current.localParticipant.setMicrophoneEnabled(enable); applied = true; }
      catch (e) { console.error('[mute] linked room failed', e); }
    }
    if (!applied && livekitPublisherRef.current?.setMicEnabled) {
      try { const r = await livekitPublisherRef.current.setMicEnabled(enable); applied = r !== null; }
      catch (e) { console.error('[mute] solo publisher failed', e); }
    }
    if (!applied) { toast.error('Microphone not ready yet'); return; }
    isMicMutedRef.current = next;
    setIsMicMuted(next);
    console.log('[mute] applied', next ? 'MUTED' : 'UNMUTED');
    toast.info(next ? '🔇 Microphone muted' : '🎤 Microphone unmuted');
  };

  // AI Chat Bots - Human-like names and messages for engagement with profile pictures
  // ==================== INTELLIGENT AI BOT CHAT SYSTEM ====================
  
  // Dynamic message pools organized by context
  const botMessagePools = {
    // Greetings - varied and natural
    greetings: [
      "Just joined! 👋", "Hey everyone!", "What's up! 🔥", "Excited to be here!",
      "Let's go! 💪", "Finally made it!", "Stream looking good tonight 🙌",
      "Ayeee what's good!", "Made it just in time!", "Hey y'all!",
      "What did I miss?", "Stream is live let's gooo", "In here! 🎉",
      "Good evening everyone", "Sup squad!", "Here for the vibes ✨",
      "Just got off work, ready to watch!", "Perfect timing!", "Hey fam 💯"
    ],
    
    // Game reactions - positive plays
    goodPlay: [
      "Nice play! 🔥", "That was CLUTCH!", "Oh that's smart!!", "Beautiful!",
      "They read that perfectly!", "Big brain move right there", "LETS GO!!!",
      "That's how you do it 💪", "Oooh that was clean!", "Perfect timing on that one",
      "Sheesh! 🔥🔥", "That's what I'm talking about!", "W play right there",
      "Y'all saw that?!", "Pro move fr fr", "That's experience right there",
      "Couldn't have played it better", "That's the one!", "Big W 🏆"
    ],
    
    // Game reactions - exciting moments
    exciting: [
      "This is getting intense!", "Omg this game tho! 😱", "I can't look!",
      "My heart is racing rn", "This is CRAZY!", "Who's gonna win?!",
      "Edge of my seat fr", "The tension! 😬", "Bruh this is wild",
      "I love this game!", "This is why I watch!", "Can't look away 👀",
      "The pressure is ON", "Somebody's about to get got 😂", "It's getting spicy!"
    ],
    
    // Game reactions - trump/spades played
    trumpPlayed: [
      "SPADES OUT! 😤", "Oh they cutting!", "Here come the spades!",
      "Can't escape the spades 💀", "They wasn't playing!", "Cut city!",
      "No mercy lol", "Spades broken baby!", "It's going DOWN now",
      "Things just got real", "Oh it's ON now!", "Trump card time 🃏"
    ],
    
    // Game reactions - joker played
    jokerPlayed: [
      "THE JOKER!! 🃏", "Big Joker in the building!", "Can't beat that!",
      "GAME OVER for that book 😂", "The big one!", "Joker said MINE",
      "Nobody touching that!", "That's the boss right there", "Joker supremacy 👑",
      "Little joker gets it done!", "Joker gang! 🃏🔥"
    ],
    
    // Game reactions - books won
    bookWon: [
      "That's a book! 📚", "Got that one!", "Securing the bag 💰",
      "One more in the pocket", "Stack them books!", "Easy money",
      "Clean sweep!", "They're cooking rn 🔥", "On pace for the W",
      "That's what winners do", "Book secured! ✅"
    ],
    
    // General engagement
    engagement: [
      "This stream is fire 🔥", "Love watching Spades!", "Best game ever fr",
      "Who else addicted to this? 😂", "I could watch this all day",
      "This is my kind of content", "Quality stream right here 💯",
      "The vibes are immaculate ✨", "Why is this so entertaining lol",
      "Been waiting for this!", "Never gets old", "Peak entertainment"
    ],
    
    // Supportive messages for streamer
    supportStreamer: [
      "You got this! 💪", "Let's go streamer!", "We believe in you!",
      "Show them how it's done!", "King/Queen of Spades! 👑",
      "Best player on here fr", "Nobody does it like you!",
      "Carry us to victory!", "The goat 🐐", "MVP in the making!",
      "You're too good at this", "Teaching a masterclass rn"
    ],
    
    // Defending streamer from hate
    defendStreamer: [
      "Why you hating tho? 🤔", "Let people enjoy things bruh",
      "If you don't like it you can leave 👋", "Imagine being negative in 2024",
      "Who hurt you? 😂", "Mods where you at?", "That's not it chief",
      "Weird comment but ok", "Negativity ain't welcome here ✋",
      "We don't do that here", "Touch grass fr fr", "L comment 🗑️",
      "Haters gonna hate, winners gonna play", "Couldn't be me being that negative",
      "Did anyone ask for your opinion tho?", "Just say you're jealous and go 🚪",
      "That's a YOU problem", "Rent free in your head huh 😂",
      "Keep that energy somewhere else", "The block button exists for a reason"
    ],
    
    // Questions and conversation starters
    questions: [
      "Who's everyone rooting for?", "What's the score rn?",
      "Anyone else from the East Coast?", "How long y'all been watching?",
      "Is there a tournament tonight?", "What's the entry on this game?",
      "Who's the best Spades player here?", "Any tips for a newbie?",
      "What hand are we on?", "Anyone else hungry watching this? 😂"
    ],
    
    // Emote-heavy reactions
    emoteReactions: [
      "🔥🔥🔥", "💪💪", "👏👏👏", "😱😱", "🙌🙌", "💯💯",
      "🎉🎉", "👀👀", "😤😤", "🏆", "⭐⭐⭐", "🃏🃏"
    ],
    
    // Conversational responses when mentioned
    mentionReplies: [
      "Hey! What's up? 👋", "You talking to me? 😄", "Yeah I'm here!",
      "What's good? 🙌", "Haha you called?", "Present! What's happening?",
      "Yo! I see you 👀", "Hey hey! What's going on?", "You rang? 😂",
      "I'm listening! What's up?", "Ayeee what you need?", "Here! What's poppin?"
    ],
    
    // Responses to questions when mentioned
    questionReplies: [
      "Honestly I think this game is lit 🔥", "Good question! I'd say it's pretty even rn",
      "Hmm let me think... I'm feeling good vibes!", "Great point! I was wondering the same",
      "That's a tough one! But I'm enjoying this stream", "Real talk? This is entertaining fr",
      "I'm just here for the action! 😂", "Facts though, good observation",
      "Interesting take! I can see that", "You know what, I agree with you on that"
    ],
    
    // Responses about the game when mentioned
    gameReplies: [
      "This game is getting intense! 😱", "I think they're playing smart rn",
      "That last play was solid 💪", "The strategy here is on point",
      "Someone's about to make a big move, I can feel it!", "This is quality Spades right here",
      "The cards are being played well tonight", "I've seen some clutch moments already",
      "This matchup is fire 🔥", "They really know what they're doing"
    ],
    
    // Casual chat responses
    casualReplies: [
      "Haha yeah for real 😂", "Right?! That's what I'm saying", "Exactly! You get it",
      "Lol same energy here", "Facts! 💯", "I feel that tbh",
      "Mood honestly", "No cap, you're right", "That's valid tbh",
      "I was just thinking that!", "Big facts right there", "True true"
    ]
  };
  
  // Function to get a random message from a pool (avoiding recent messages)
  const recentBotMessages = useRef([]);
  const getRandomBotMessage = (pool, context = '') => {
    const messages = botMessagePools[pool] || botMessagePools.engagement;
    let availableMessages = messages.filter(m => !recentBotMessages.current.includes(m));
    
    // If all messages used, reset and use any
    if (availableMessages.length === 0) {
      recentBotMessages.current = [];
      availableMessages = messages;
    }
    
    const message = availableMessages[Math.floor(Math.random() * availableMessages.length)];
    recentBotMessages.current.push(message);
    
    // Keep only last 20 messages in memory
    if (recentBotMessages.current.length > 20) {
      recentBotMessages.current = recentBotMessages.current.slice(-20);
    }
    
    return message;
  };
  
  // Function to detect negative/hateful messages
  const isNegativeMessage = (message) => {
    const negativePhrases = [
      'trash', 'garbage', 'suck', 'terrible', 'worst', 'bad player',
      'noob', 'loser', 'stupid', 'dumb', 'idiot', 'hate', 'boring',
      'waste of time', 'unfollow', 'leave', 'quit', "can't play",
      'cheating', 'rigged', 'fake', 'pathetic', 'cringe', 'lame'
    ];
    const lowerMessage = message.toLowerCase();
    return negativePhrases.some(phrase => lowerMessage.includes(phrase));
  };
  
  // Send context-aware bot message — routes through the real stream chat WS so
  // every viewer + broadcaster sees the same message, no local duplicates.
  const sendBotMessage = useCallback((contextType = 'engagement', specificBot = null, overrideText = null) => {
    if (activeChatBots.length === 0) return;
    if (!streamChatWs || streamChatWs.readyState !== WebSocket.OPEN) return;
    const bot = specificBot || activeChatBots[Math.floor(Math.random() * activeChatBots.length)];
    const message = overrideText || getRandomBotMessage(contextType);
    streamChatWs.send(JSON.stringify({
      type: 'chat',
      user_id: bot.id,
      username: bot.name,
      message: message,
      is_streamer: false,
      is_bot: true,
      avatar: bot.avatar,
      user_picture: bot.profilePic || null
    }));
  }, [activeChatBots, streamChatWs, getRandomBotMessage]);
  
  // React to game events
  const botReactToGameEvent = useCallback((eventType) => {
    if (!chatBotsEnabled || activeChatBots.length === 0) return;
    
    // Random delay to seem natural (1-4 seconds)
    const delay = 1000 + Math.random() * 3000;
    
    setTimeout(() => {
      const contextMap = {
        'good_play': 'goodPlay',
        'trump_played': 'trumpPlayed',
        'joker_played': 'jokerPlayed',
        'book_won': 'bookWon',
        'exciting': 'exciting'
      };
      
      const context = contextMap[eventType] || 'engagement';
      sendBotMessage(context);
      
      // Sometimes add a second reaction from another bot
      if (Math.random() > 0.6 && activeChatBots.length > 1) {
        setTimeout(() => {
          const otherBots = activeChatBots.filter(b => b.id !== activeChatBots[0].id);
          if (otherBots.length > 0) {
            const secondBot = otherBots[Math.floor(Math.random() * otherBots.length)];
            sendBotMessage(context, secondBot);
          }
        }, 1500 + Math.random() * 2000);
      }
    }, delay);
  }, [chatBotsEnabled, activeChatBots, sendBotMessage]);
  
  // Defend streamer when negative comments appear
  const botDefendStreamer = useCallback((hatefulMessage) => {
    if (!chatBotsEnabled || activeChatBots.length === 0) return;
    
    // React with slight delay (seems more natural)
    setTimeout(() => {
      sendBotMessage('defendStreamer');
      
      // Sometimes a second bot piles on in defense
      if (Math.random() > 0.5 && activeChatBots.length > 1) {
        setTimeout(() => {
          sendBotMessage('supportStreamer');
        }, 2000 + Math.random() * 2000);
      }
    }, 1500 + Math.random() * 2500);
  }, [chatBotsEnabled, activeChatBots, sendBotMessage]);
  
  // Chat message handler - check for negativity and respond
  const handleChatMessageWithBotResponse = useCallback((message) => {
    // If it's a negative message towards the streamer, defend them
    if (isNegativeMessage(message) && chatBotsEnabled) {
      botDefendStreamer(message);
    }
  }, [chatBotsEnabled, botDefendStreamer]);
  
  // Detect if a bot is mentioned in the message and respond
  const handleBotMention = useCallback((messageText, senderUsername) => {
    if (!chatBotsEnabled || activeChatBots.length === 0) return;
    
    const lowerMessage = messageText.toLowerCase();
    
    // Check if any active bot is mentioned
    for (const bot of activeChatBots) {
      const botNameLower = bot.name.toLowerCase();
      if (lowerMessage.includes(`@${botNameLower}`) || lowerMessage.includes(botNameLower)) {
        // Bot was mentioned! Respond after a natural delay (2-5 seconds)
        const delay = 2000 + Math.random() * 3000;
        
        setTimeout(() => {
          // Determine response type based on message content
          let responsePool = 'mentionReplies';
          
          // Check if it's a question
          if (messageText.includes('?') || 
              lowerMessage.includes('what do you think') || 
              lowerMessage.includes('your opinion') ||
              lowerMessage.includes('thoughts')) {
            responsePool = 'questionReplies';
          }
          // Check if it's about the game
          else if (lowerMessage.includes('game') || 
                   lowerMessage.includes('play') || 
                   lowerMessage.includes('cards') ||
                   lowerMessage.includes('spades') ||
                   lowerMessage.includes('bid')) {
            responsePool = 'gameReplies';
          }
          // Check if it's casual chat (contains lol, haha, etc.)
          else if (lowerMessage.includes('lol') || 
                   lowerMessage.includes('haha') || 
                   lowerMessage.includes('right') ||
                   lowerMessage.includes('agree') ||
                   lowerMessage.includes('true')) {
            responsePool = 'casualReplies';
          }
          
          const response = getRandomBotMessage(responsePool);
          
          // Sometimes include the sender's name for more natural conversation
          let finalResponse = response;
          if (Math.random() > 0.6) {
            finalResponse = `@${senderUsername} ${response}`;
          }
          
          setStreamChatMessages(prev => [...prev.slice(-99), {
            id: Date.now() + Math.random(),
            username: bot.name,
            message: finalResponse,
            isStreamer: false,
            isBot: true,
            avatar: bot.avatar,
            profilePic: bot.profilePic,
            botId: bot.id,
            timestamp: new Date()
          }]);
          
          // Sometimes another bot chimes in (30% chance)
          if (Math.random() > 0.7 && activeChatBots.length > 1) {
            const otherBots = activeChatBots.filter(b => b.id !== bot.id);
            if (otherBots.length > 0) {
              const secondBot = otherBots[Math.floor(Math.random() * otherBots.length)];
              setTimeout(() => {
                const followUp = getRandomBotMessage('casualReplies');
                setStreamChatMessages(prev => [...prev.slice(-99), {
                  id: Date.now() + Math.random(),
                  username: secondBot.name,
                  message: followUp,
                  isStreamer: false,
                  isBot: true,
                  avatar: secondBot.avatar,
                  profilePic: secondBot.profilePic,
                  botId: secondBot.id,
                  timestamp: new Date()
                }]);
              }, 1500 + Math.random() * 2000);
            }
          }
        }, delay);
        
        // Only respond to the first mentioned bot
        break;
      }
    }
  }, [chatBotsEnabled, activeChatBots, getRandomBotMessage]);
  
  const chatBotProfiles = [
    { 
      id: 'bot_sarah_nyc',
      name: 'Sarah_NYC', 
      avatar: '👩', 
      profilePic: 'https://randomuser.me/api/portraits/women/44.jpg',
      bio: 'NYC girl 🗽 | Spades enthusiast | Always looking for good games!',
      location: 'New York, NY',
      followers: 1243,
      following: 567,
      gamesWon: 89,
      emeralds: 5420,
      messages: ['Hey everyone! Just got here 🔥', 'This stream is fire 🙌', 'Who else loves Spades?', 'Good game, everybody!'] 
    },
    { 
      id: 'bot_mike_atl',
      name: 'Mike_ATL', 
      avatar: '👨', 
      profilePic: 'https://randomuser.me/api/portraits/men/32.jpg',
      bio: 'ATL native 🍑 | Card game lover | Play big or go home',
      location: 'Atlanta, GA',
      followers: 2156,
      following: 432,
      gamesWon: 156,
      emeralds: 12350,
      messages: ['Yo what\'s good!', 'Let\'s gooo! 💪', 'Who\'s ready to play?', 'Great stream vibes tonight'] 
    },
    { 
      id: 'bot_jessica_la',
      name: 'Jessica_LA', 
      avatar: '👱‍♀️', 
      profilePic: 'https://randomuser.me/api/portraits/women/67.jpg',
      bio: 'LA vibes ☀️ | Love watching card games | Positive energy only ✨',
      location: 'Los Angeles, CA',
      followers: 3421,
      following: 892,
      gamesWon: 45,
      emeralds: 8760,
      messages: ['Hiiii 💕', 'Love watching Spades!', 'You got this!', 'Such a fun game'] 
    },
    { 
      id: 'bot_deandre_chi',
      name: 'DeAndre_Chi', 
      avatar: '🧔', 
      profilePic: 'https://randomuser.me/api/portraits/men/45.jpg',
      bio: 'Chicago 🌆 | Real ones only | Spades is life 💯',
      location: 'Chicago, IL',
      followers: 1876,
      following: 234,
      gamesWon: 203,
      emeralds: 18900,
      messages: ['Aye what up fam', 'Who winning tonight?', 'That was a good play!', 'Real ones know the rules 💯'] 
    },
    { 
      id: 'bot_aaliyah_mia',
      name: 'Aaliyah_MIA', 
      avatar: '👩‍🦱', 
      profilePic: 'https://randomuser.me/api/portraits/women/28.jpg',
      bio: 'Miami heat 🌴 | Card shark in training | Living my best life',
      location: 'Miami, FL',
      followers: 2543,
      following: 678,
      gamesWon: 78,
      emeralds: 6540,
      messages: ['Hey! Just joined 👋', 'This is exciting!', 'Play well, everybody!', 'Let\'s see some skills!'] 
    },
    { 
      id: 'bot_chris_phx',
      name: 'Chris_PHX', 
      avatar: '🧑', 
      profilePic: 'https://randomuser.me/api/portraits/men/52.jpg',
      bio: 'Phoenix rising 🌵 | Casual gamer | Here for the fun',
      location: 'Phoenix, AZ',
      followers: 987,
      following: 456,
      gamesWon: 34,
      emeralds: 2340,
      messages: ['What\'s up everyone', 'Ready for some cards?', 'Nice move!', 'This is getting intense'] 
    },
    { 
      id: 'bot_taylor_sea',
      name: 'Taylor_SEA', 
      avatar: '🧑‍🦰', 
      profilePic: 'https://randomuser.me/api/portraits/women/12.jpg',
      bio: 'Seattle ☕ | New to Spades | Learning from the best',
      location: 'Seattle, WA',
      followers: 654,
      following: 321,
      gamesWon: 12,
      emeralds: 1230,
      messages: ['Hey stream! 🌟', 'First time watching', 'This looks fun!', 'Go team!'] 
    },
    { 
      id: 'bot_marcus_det',
      name: 'Marcus_DET', 
      avatar: '👨‍🦲', 
      profilePic: 'https://randomuser.me/api/portraits/men/78.jpg',
      bio: 'Detroit stand up 🚗 | OG Spades player | Been in the game for years',
      location: 'Detroit, MI',
      followers: 4532,
      following: 123,
      gamesWon: 342,
      emeralds: 34560,
      messages: ['What up doe!', 'Detroit in the building', 'Play smart!', 'Good cards good cards'] 
    }
  ];
  
  // Start AI chat bots when going live
  useEffect(() => {
    if (!currentStream || isPreviewMode || !chatBotsEnabled) return;
    
    // Initial bot messages when stream starts
    const initialBots = chatBotProfiles.slice(0, 3 + Math.floor(Math.random() * 3));
    setActiveChatBots(initialBots);
    
    // Send initial greeting messages with varied timing
    initialBots.forEach((bot, idx) => {
      setTimeout(() => {
        const greeting = getRandomBotMessage('greetings');
        
        setStreamChatMessages(prev => [...prev.slice(-99), {
          id: Date.now() + Math.random(),
          username: bot.name,
          message: greeting,
          isStreamer: false,
          isBot: true,
          avatar: bot.avatar,
          profilePic: bot.profilePic,
          botId: bot.id,
          timestamp: new Date()
        }]);
      }, 3000 + (idx * 2500) + Math.random() * 4000);
    });
    
    // Periodic bot messages with varied contexts
    const botInterval = setInterval(() => {
      if (activeChatBots.length === 0) return;
      
      // Pick random context based on weights
      const contexts = [
        { type: 'engagement', weight: 30 },
        { type: 'questions', weight: 15 },
        { type: 'supportStreamer', weight: 20 },
        { type: 'exciting', weight: 15 },
        { type: 'emoteReactions', weight: 20 }
      ];
      
      const totalWeight = contexts.reduce((sum, c) => sum + c.weight, 0);
      let random = Math.random() * totalWeight;
      let selectedContext = 'engagement';
      
      for (const context of contexts) {
        random -= context.weight;
        if (random <= 0) {
          selectedContext = context.type;
          break;
        }
      }
      
      const randomBot = activeChatBots[Math.floor(Math.random() * activeChatBots.length)];
      const message = getRandomBotMessage(selectedContext);
      
      setStreamChatMessages(prev => [...prev.slice(-99), {
        id: Date.now() + Math.random(),
        username: randomBot.name,
        message: message,
        isStreamer: false,
        isBot: true,
        avatar: randomBot.avatar,
        profilePic: randomBot.profilePic,
        botId: randomBot.id,
        timestamp: new Date()
      }]);
    }, 12000 + Math.random() * 18000); // Random interval between 12-30 seconds
    
    return () => clearInterval(botInterval);
  }, [currentStream?.stream_id, isPreviewMode, chatBotsEnabled]);

  // ==================== BROADCASTER CHAT POLLING ====================
  // Poll for chat messages every 2 seconds to catch viewer messages sent via REST API
  // This complements WebSocket messages and ensures sync with viewers
  useEffect(() => {
    if (!currentStream?.stream_id || !currentStream?.id || isPreviewMode) return;
    
    const streamIdToUse = currentStream.stream_id || currentStream.id;
    const seenMessageIds = new Set();
    
    const loadChatMessages = async () => {
      try {
        const response = await api.get(`/streams/${streamIdToUse}/chat?limit=50`);
        const messages = response.data || [];
        
        // Process and merge with existing messages
        const newMessages = messages.filter(msg => {
          if (seenMessageIds.has(msg.id)) return false;
          seenMessageIds.add(msg.id);
          return true;
        }).map(msg => ({
          id: msg.id,
          username: msg.user_name || 'Anonymous',
          userId: msg.user_id,
          user_id: msg.user_id,
          message: msg.message,
          isStreamer: msg.user_id === user?.id,
          isSystem: msg.message_type === 'system_join',
          isGift: msg.message_type === 'gift',
          giftIcon: msg.gift_data?.gift_icon,
          user_picture: msg.user_picture,
          user_handle: msg.user_handle,
          timestamp: new Date(msg.timestamp)
        }));
        
        if (newMessages.length > 0) {
          setStreamChatMessages(prev => {
            // Merge new messages, avoiding duplicates
            const existingIds = new Set(prev.map(m => m.id));
            const uniqueNew = newMessages.filter(m => !existingIds.has(m.id));
            // Sort by timestamp
            const merged = [...prev, ...uniqueNew].sort((a, b) => 
              new Date(a.timestamp) - new Date(b.timestamp)
            ).slice(-100); // Keep last 100
            return merged;
          });
        }
      } catch (error) {
        console.error('Error polling chat:', error);
      }
    };
    
    // Initial load
    loadChatMessages();

    // POLL WIDENED (Feb 2026): the stream chat WebSocket above is the
    // primary real-time channel. This REST poll is a fallback for the
    // rare case where a viewer's REST-sent message races the WS. Bumped
    // 2s → 8s so users on the happy path never notice, but a dropped
    // WS message still reconciles within one interval.
    const chatPollInterval = setInterval(loadChatMessages, 8000);
    
    // Cleanup old message IDs to prevent memory leak
    const cleanupInterval = setInterval(() => {
      if (seenMessageIds.size > 200) {
        const idsArray = Array.from(seenMessageIds);
        idsArray.slice(0, 100).forEach(id => seenMessageIds.delete(id));
      }
    }, 30000);
    
    return () => {
      clearInterval(chatPollInterval);
      clearInterval(cleanupInterval);
    };
  }, [currentStream?.stream_id, currentStream?.id, isPreviewMode, user?.id]);

  // Sample cards for game demonstration
  const sampleCards = [
    { suit: '♠', value: 'A', color: 'black' },
    { suit: '♠', value: 'K', color: 'black' },
    { suit: '♥', value: 'Q', color: 'red' },
    { suit: '♦', value: 'J', color: 'red' },
    { suit: '♣', value: '10', color: 'black' },
    { suit: '♠', value: '9', color: 'black' },
    { suit: '♥', value: '8', color: 'red' },
    { suit: '♦', value: '7', color: 'red' },
    { suit: '♣', value: '6', color: 'black' },
    { suit: '♠', value: '5', color: 'black' },
    { suit: '♥', value: '4', color: 'red' },
    { suit: '♦', value: '3', color: 'red' },
    { suit: '♣', value: '2', color: 'black' }
  ];

  // Gift options with different emerald prices
  const gifts = [
    { id: 1, name: "👍 Like", price: 1, icon: "👍" },
    { id: 2, name: "❤️ Heart", price: 2, icon: "❤️" },
    { id: 3, name: "🔥 Fire", price: 5, icon: "🔥" },
    { id: 4, name: "⭐ Star", price: 10, icon: "⭐" },
    { id: 5, name: "Gem", price: 20, icon: "gem", isCustomIcon: true },
    { id: 6, name: "🎉 Party", price: 25, icon: "🎉" },
    { id: 7, name: "🏆 Trophy", price: 50, icon: "🏆" },
    { id: 8, name: "👑 Crown", price: 100, icon: "👑" },
    { id: 9, name: "🚀 Rocket", price: 150, icon: "🚀" },
    { id: 10, name: "💰 Money Bag", price: 200, icon: "💰" },
    { id: 11, name: "🦄 Unicorn", price: 300, icon: "🦄" },
    { id: 12, name: "🎯 Bullseye", price: 400, icon: "🎯" },
    { id: 13, name: "⚡ Lightning", price: 500, icon: "⚡" },
    { id: 14, name: "🌟 Shooting Star", price: 750, icon: "🌟" },
    { id: 15, name: "🎪 Carnival", price: 1000, icon: "🎪" },
    { id: 16, name: "🏰 Castle", price: 1500, icon: "🏰" },
    { id: 17, name: "🎭 Theater", price: 2000, icon: "🎭" },
    { id: 18, name: "🚁 Helicopter", price: 2500, icon: "🚁" },
    { id: 19, name: "🛸 UFO", price: 3000, icon: "🛸" },
    { id: 20, name: "🌈 Rainbow", price: 4000, icon: "🌈" },
    { id: 21, name: "🎆 Fireworks", price: 5000, icon: "🎆" },
    { id: 22, name: "💫 Galaxy", price: 10000, icon: "💫" }
  ];

  // Handle pending Quick Match and Invites from Games page
  useEffect(() => {
    const handlePendingActions = async () => {
      // Handle Quick Match
      const pendingMatch = localStorage.getItem('pendingQuickMatch');
      if (pendingMatch) {
        try {
          const matchData = JSON.parse(pendingMatch);
          localStorage.removeItem('pendingQuickMatch');
          
          setWagerAmount(matchData.wagerAmount);
          setAllowBlindSix(matchData.allowBlindSix);
          setAllowAceCheck(matchData.allowAceCheck);
          setSelectedGameMode('quickMatch');
          
          toast.info('Starting live stream for Quick Match...', { duration: 2000 });
          
          setTimeout(async () => {
            await startPreview();
            setTimeout(async () => {
              toast.info('Going live and joining matchmaking...', { duration: 2000 });
              await goLive();
            }, 1000);
          }, 500);
          
        } catch (error) {
          console.error('Error handling pending Quick Match:', error);
          localStorage.removeItem('pendingQuickMatch');
        }
        return;
      }
      
      // Handle Pending Invites
      const pendingInvites = localStorage.getItem('pendingInvites');
      if (pendingInvites) {
        try {
          const usersToInvite = JSON.parse(pendingInvites);
          localStorage.removeItem('pendingInvites');
          
          toast.info('Starting live stream to send invitations...', { duration: 2000 });
          
          // Auto-start camera and go live
          setTimeout(async () => {
            await startPreview();
            setTimeout(async () => {
              // Force go live
              await goLive();
              
              // Wait for stream to be ready
              setTimeout(async () => {
                // Now send invitations
                const streamIdToUse = currentStream?.stream_id;
                if (!streamIdToUse) {
                  toast.error('Stream not started. Please try again.');
                  return;
                }
                
                const invitePromises = usersToInvite.map(user => 
                  api.post('/streams/invite-player', {
                    streamId: streamIdToUse,
                    inviterGemId: user?.handle,
                    inviteeGemId: user.handle,
                    streamTitle: streamTitle
                  })
                );
                
                await Promise.all(invitePromises);
                toast.success(`Invited ${usersToInvite.length} player(s)!`);
                setInvitedPlayers(usersToInvite);
                // Regression H (May 8 testing): removed `setLookingToPlay(true);`
                // here. Sending an invite is NOT matchmaking — flipping the
                // flag in the invite-send flow caused the yellow
                // "🏆 Looking for opponents…" banner to appear and stick on
                // LivePage even though the user had targeted specific people.
                // The flag is now only flipped by the explicit "Look for Game"
                // button.
              }, 2000);
            }, 1000);
          }, 500);
          
        } catch (error) {
          console.error('Error handling pending invites:', error);
          localStorage.removeItem('pendingInvites');
        }
      }
    };
    
    handlePendingActions();
  }, []);

  // ========================================================
  // HANDLE LINK-LIVE-ACCEPTED EVENT (from handleAcceptInvitation)
  // When an already-live broadcaster accepts an invite from another broadcaster
  // Both remain in their own streams but become linked side-by-side
  // ========================================================
  useEffect(() => {
    const handleLinkLiveAccepted = async (event) => {
      await ensureFeatureFlags(); // Link v2: resolve the runtime flag before deciding transport
      const { streamId, inviterId, inviterName } = event.detail;
      console.log('🔗 Link-live-accepted event received:', { streamId, inviterId, inviterName });
      
      if (!isActiveBroadcaster || !currentStream?.stream_id) {
        console.log('❌ Not currently broadcasting - cannot link');
        toast.error('You must be live to link streams');
        return;
      }
      
      // Bug 3 (May 7 testing): in-flight guard. A duplicate live_invite that
      // sneaks past the WS dedupe would otherwise trigger a second
      // client.join()/publish in parallel with the first → broken video,
      // duplicate tiles. Only one accept-flow runs at a time.
      if (agoraLinkInProgressRef.current) {
        console.log('[Bug3] Link-live-accepted suppressed: link already in progress');
        return;
      }
      agoraLinkInProgressRef.current = true;

      toast.info(`Connecting to ${inviterName}'s stream...`, { duration: 3000 });
      
      try {
        // Link v2: publish into the shared LiveKit linked room instead of the
        // Agora mesh. Anchor the room to the inviter's stream so both sides
        // resolve the SAME room name.
        if (LIVEKIT_LINKS) {
          const roomName = livekitLinkRoomName({
            linked_session_id: currentStream?.linked_session_id,
            linked_to_stream: streamId,
          });
          await initLiveKitLinkRoom(roomName, { sessionType: 'linked_broadcaster' });
          setInvitedPlayers(prev => {
            if (prev.find(p => p.id === inviterId)) return prev;
            return [...prev, { id: inviterId, name: inviterName, handle: inviterName, status: 'linked' }];
          });
          setSharedChatStreamId(streamId);
          toast.success(`Linked with ${inviterName}! You're now streaming together.`);
          return;
        }

        // Get Agora token to join the inviter's channel (for video/audio sync)
        const agoraResponse = await api.post('/agora/token', {
          channelName: `stream_${streamId}`,
          uid: uidFromUserId(user?.id),
          role: 'publisher',
          sessionType: 'linked_live'
        });
        
        const { token: agoraToken, channelName, appId } = agoraResponse.data;
        console.log('✅ Got Agora token for linked session:', { channelName });

        // Checkpoint 4: unified factory.
        const AgoraRTC = (await import('agora-rtc-sdk-ng')).default;
        const client = await createAndJoinAgoraChannel({
          AgoraRTC,
          channelName,
          token: agoraToken,
          appId,
          uid: uidFromUserId(user?.id),
          onUserLeftToast: `${inviterName} has disconnected from the linked session`,
        });

        // Create and publish local tracks
        const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
          { echoCancellation: true, noiseSuppression: true },
          { encoderConfig: '720p_2', facingMode: 'user' }
        );
        
        await client.publish([audioTrack, videoTrack]);
        console.log('✅ Published local tracks to linked session', {
          audioTrackId: audioTrack?.getTrackId?.(),
          videoTrackId: videoTrack?.getTrackId?.(),
        });
        try {
          window.__GEMS_PUBLISHED_TRACKS = window.__GEMS_PUBLISHED_TRACKS || [];
          window.__GEMS_PUBLISHED_TRACKS.push({
            when: 'link-live-accepted',
            channel: channelName,
            audioTrackId: audioTrack?.getTrackId?.(),
            videoTrackId: videoTrack?.getTrackId?.(),
            ts: Date.now(),
          });
        } catch {}
        
        // ── Proactive token refresh every 8 minutes for linked sessions ──
        const linkedRefreshInterval = setInterval(async () => {
          try {
            console.log('🔄 Refreshing Agora token for linked session...');
            const refreshResponse = await api.post('/agora/token-refresh', {
              channelName,
              uid: uidFromUserId(user?.id),
              role: 'publisher'
            });
            await client.renewToken(refreshResponse.data.token);
            console.log('✅ Linked session Agora token refreshed');
          } catch (err) {
            console.error('❌ Linked session token refresh failed:', err);
          }
        }, 8 * 60 * 1000);

        // Clean up interval when component unmounts or link ends
        // Store on the client object itself so the cleanup handler can reach it
        client._gemsRefreshInterval = linkedRefreshInterval;
        
        // Update state for side-by-side view
        setSharedAgoraTracks({ audioTrack, videoTrack });
        setAgoraToken(agoraToken);
        setAgoraChannel(channelName);
        setAgoraAppId(appId);
        setUseAgora(true);
        
        // Add inviter to invitedPlayers for 2-host layout
        setInvitedPlayers(prev => {
          if (prev.find(p => p.id === inviterId)) return prev;
          return [...prev, {
            id: inviterId,
            name: inviterName,
            handle: inviterName,
            status: 'connected'
          }];
        });
        
        // Set shared chat to the inviter's stream
        setSharedChatStreamId(streamId);
        
        // Stop WebRTC camera since Agora now has it
        if (webrtc && webrtc.localStream) {
          webrtc.localStream.getTracks().forEach(track => track.stop());
          console.log('🛑 WebRTC stopped - Agora has camera for linked session');
        }
        
        toast.success(`Linked with ${inviterName}! You're now streaming together.`);
        console.log('✅ Successfully joined linked live session');
        
      } catch (error) {
        console.error('❌ Failed to join linked session:', error);
        toast.error('Failed to connect to linked session. Please try again.');
      } finally {
        // Bug 3: release the in-flight guard so a future legitimate accept works.
        agoraLinkInProgressRef.current = false;
      }
    };
    
    window.addEventListener('link-live-accepted', handleLinkLiveAccepted);
    return () => window.removeEventListener('link-live-accepted', handleLinkLiveAccepted);
  }, [isActiveBroadcaster, currentStream, api, webrtc]);

  // ========================================================
  // HANDLE LINK-LIVE-PARTNER-ACCEPTED EVENT (for SENDER)
  // When the person you invited accepts, link up with them
  // ========================================================
  useEffect(() => {
    const handlePartnerAccepted = async (event) => {
      await ensureFeatureFlags(); // Link v2: resolve the runtime flag before deciding transport
      const { streamId, partnerId, partnerName, invitationId } = event.detail;
      console.log('🔗 Partner accepted our invite:', { streamId, partnerId, partnerName });
      
      if (!isActiveBroadcaster || !currentStream?.stream_id) {
        console.log('❌ Not currently broadcasting - cannot link');
        return;
      }
      
      // Bug 3: in-flight guard against duplicate partner-accepted events.
      if (agoraLinkInProgressRef.current) {
        pendingAcceptsRef.current.push(event.detail);
        console.log('[Link] accept queued behind in-flight link:', partnerName);
        return;
      }
      agoraLinkInProgressRef.current = true;

      try {
        // Link v2: publish into the shared LiveKit linked room (inviter side).
        // Anchor to our own stream so viewers + partners resolve the same room.
        if (LIVEKIT_LINKS) {
          const roomName = livekitLinkRoomName(currentStream || {});
          await initLiveKitLinkRoom(roomName, { sessionType: 'linked_broadcaster' });
          setInvitedPlayers(prev => {
            const idx = prev.findIndex(p => p.id === partnerId || p.handle === partnerName);
            if (idx >= 0) {
              const upgraded = [...prev];
              upgraded[idx] = { ...upgraded[idx], id: partnerId, name: partnerName, status: 'linked' };
              return upgraded;
            }
            return [...prev, { id: partnerId, name: partnerName, handle: partnerName, status: 'linked' }];
          });
          if (currentStream?.stream_id) setSharedChatStreamId(currentStream.stream_id);
          toast.success(`Now linked with ${partnerName}!`);
          // Still notify backend so viewers + other broadcasters update layouts.
          try {
            await api.post('/streams/link-started', {
              partner_id: partnerId,
              partner_name: partnerName,
              my_stream_id: currentStream?.stream_id,
              partner_stream_id: streamId,
              linked_session_id: linkedSessionId || currentStream?.linked_session_id || `stream_${currentStream?.stream_id}`,
              existing_linked_users: allLinkedUsers || [],
            });
          } catch (e) { console.log('Failed to broadcast link_started (livekit):', e); }
          try {
            await api.post('/streams/link/add-broadcaster', {
              new_broadcaster_id: partnerId,
              new_broadcaster_name: partnerName,
              linked_session_id: linkedSessionId || currentStream?.linked_session_id || `stream_${currentStream?.stream_id}`,
            });
          } catch (e) { console.log('Failed to add broadcaster to linked session (livekit):', e); }
          return;
        }

        // ============================================================
        // MESH-FIX (May 15, 2026): IDEMPOTENT PUBLISH ON INVITER SIDE
        // ============================================================
        // `handlePartnerAccepted` fires ONCE per partner who accepts. The
        // 3-broadcaster regression (B and C can't see each other) traces to
        // this handler running a 2nd time when C accepts: the factory's
        // same-channel guard correctly returned the existing client, but
        // the code below blindly called createMicrophoneAndCameraTracks +
        // client.publish AGAIN, leaving the previous tracks orphaned (still
        // published in Agora) and stomping `sharedAgoraTracks` with the
        // new refs. Symptoms:
        //   - SFU sees the same UID double-publishing → B's subscriptions
        //     to A can break right when C joins (the hub-spoke bug).
        //   - toggleMicMute only mutes the LATEST track; orphan keeps
        //     streaming audio (the "they heard A through unmuted mic" bug).
        // FIX: if sharedAgoraTracks already exists we're already published
        // on this channel from a previous accept — skip track create AND
        // publish entirely. Just notify the backend so the new partner
        // gets added to the linked session.
        // ============================================================
        const alreadyPublishedToChannel =
          !!sharedAgoraTracks?.audioTrack && !!sharedAgoraTracks?.videoTrack && useAgora;

        // Get Agora token to join our own stream's channel (partner will join too)
        const agoraResponse = await api.post('/agora/token', {
          channelName: `stream_${currentStream.stream_id}`,
          uid: uidFromUserId(user?.id),
          role: 'publisher',
          sessionType: 'linked_live'
        });

        const { token: agoraToken, channelName, appId } = agoraResponse.data;
        console.log('✅ Got Agora token for our linked session:', {
          channelName,
          alreadyPublishedToChannel,
        });

        // Checkpoint 4: unified factory.
        const AgoraRTC = (await import('agora-rtc-sdk-ng')).default;
        const client = await createAndJoinAgoraChannel({
          AgoraRTC,
          channelName,
          token: agoraToken,
          appId,
          uid: uidFromUserId(user?.id),
          onUserLeftToast: `${partnerName} disconnected from linked session`,
        });

        if (!alreadyPublishedToChannel) {
          // First partner accept — create + publish tracks for the first time.
          const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
            { echoCancellation: true, noiseSuppression: true },
            { encoderConfig: '720p_2', facingMode: 'user' }
          );
          await client.publish([audioTrack, videoTrack]);
          console.log('✅ Published local tracks as sender', {
            audioTrackId: audioTrack?.getTrackId?.(),
            videoTrackId: videoTrack?.getTrackId?.(),
          });
          // Mesh-debug: record published track ids on window for cross-device diagnostics.
          try {
            window.__GEMS_PUBLISHED_TRACKS = window.__GEMS_PUBLISHED_TRACKS || [];
            window.__GEMS_PUBLISHED_TRACKS.push({
              when: 'partner-accepted (first)',
              channel: channelName,
              audioTrackId: audioTrack?.getTrackId?.(),
              videoTrackId: videoTrack?.getTrackId?.(),
              ts: Date.now(),
            });
          } catch {}

          // Update state
          setSharedAgoraTracks({ audioTrack, videoTrack });
        } else {
          console.log('♻️ [Mesh-Fix] sharedAgoraTracks already exist — skipping double-publish (3rd+ broadcaster path)', {
            existingAudioTrackId: sharedAgoraTracks.audioTrack?.getTrackId?.(),
            existingVideoTrackId: sharedAgoraTracks.videoTrack?.getTrackId?.(),
          });
          try {
            window.__GEMS_PUBLISHED_TRACKS = window.__GEMS_PUBLISHED_TRACKS || [];
            window.__GEMS_PUBLISHED_TRACKS.push({
              when: 'partner-accepted (reused)',
              channel: channelName,
              audioTrackId: sharedAgoraTracks.audioTrack?.getTrackId?.(),
              videoTrackId: sharedAgoraTracks.videoTrack?.getTrackId?.(),
              ts: Date.now(),
            });
          } catch {}
        }

        setAgoraToken(agoraToken);
        setAgoraChannel(channelName);
        setAgoraAppId(appId);
        setUseAgora(true);
        
        setInvitedPlayers(prev => {
          if (prev.find(p => p.id === partnerId)) return prev;
          return [...prev, { id: partnerId, name: partnerName, handle: partnerName, status: 'connected' }];
        });
        
        // Set shared chat to use the sender's stream (both users will share this chat)
        // The sender's stream becomes the canonical chat stream for the linked session
        if (currentStream?.stream_id) {
          setSharedChatStreamId(currentStream.stream_id);
        }
        
        // Stop WebRTC
        if (webrtc && webrtc.localStream) {
          webrtc.localStream.getTracks().forEach(track => track.stop());
        }
        
        toast.success(`Now linked with ${partnerName}!`);
        
        // Broadcast link_started to viewers, including the existing linked users
        // so the backend can preserve them. Use any linked_session_id from currentStream.
        try {
          await api.post('/streams/link-started', {
            partner_id: partnerId,
            partner_name: partnerName,
            my_stream_id: currentStream?.stream_id,
            partner_stream_id: streamId,
            linked_session_id: linkedSessionId || currentStream?.linked_session_id || `stream_${currentStream?.stream_id}`,
            existing_linked_users: allLinkedUsers || [],
          });
        } catch (e) {
          console.log('Failed to broadcast link_started:', e);
        }

        // Register the partner with the linked session so all existing broadcasters
        // receive a `broadcaster_joined_session` WebSocket event and update layouts.
        try {
          await api.post('/streams/link/add-broadcaster', {
            new_broadcaster_id: partnerId,
            new_broadcaster_name: partnerName,
            linked_session_id: linkedSessionId || currentStream?.linked_session_id || `stream_${currentStream?.stream_id}`,
          });
        } catch (e) {
          console.log('Failed to add broadcaster to linked session:', e);
        }
        
      } catch (error) {
        console.error('❌ Failed to set up linked session:', error);
        toast.error('Failed to set up linked session');
      } finally {
        agoraLinkInProgressRef.current = false;
        const next = pendingAcceptsRef.current.shift();
        if (next) { console.log('[Link] processing queued accept:', next.partnerName); handlePartnerAccepted({ detail: next }); }
      }
    };
    
    window.addEventListener('link-live-partner-accepted', handlePartnerAccepted);
    return () => window.removeEventListener('link-live-partner-accepted', handlePartnerAccepted);
  }, [isActiveBroadcaster, currentStream, api, webrtc]);

  // ========================================================
  // HANDLE PARTNER-ENDED-LINK EVENT
  // When the other broadcaster ends the link, we need to clean up too
  // ========================================================
  useEffect(() => {
    const handlePartnerEndedLink = async (event) => {
      const { endedById, endedByName } = event.detail;
      console.log('🔌 Partner ended link:', endedByName);
      // MULTI-PARTY GUARD (Feb 2026): link_ended means the SESSION ended
      // (≤1 broadcaster would remain). If the live LiveKit room still shows
      // 2+ OTHER broadcasters besides the leaver, this is a stray/legacy
      // full-teardown signal — drop just the leaver and stay linked.
      try {
        const room = linkRoomRef.current;
        if (LIVEKIT_LINKS && room?.remoteParticipants) {
          const others = Array.from(room.remoteParticipants.keys()).filter((id) => id !== endedById);
          if (others.length >= 2) {
            console.log(`🔗 [partial-guard] ${others.length} broadcasters remain — staying linked`);
            setAllLinkedUsers((prev) => (prev || []).filter((u) => u !== endedById));
            setInvitedPlayers((prev) => (prev || []).filter((p) => p?.id !== endedById));
            toast.info(`${endedByName} left the linked session`);
            return;
          }
        }
      } catch (e) { console.warn('[partial-guard] check failed:', e?.message); }

      // Full Agora cleanup (unpublish, unsubscribe, leave, reset all state)
      await fullStreamCleanup('partner_ended_link');
      setLinkedSessionWins({ me: 0, opponent: 0 });
      // Part 4 (Feb 19, 2026): reset the per-link session win counter so
      // the next link opens fresh at 0—0.
      setSessionWins({});

      // LINK v2 teardown — clearing linkRoomInfo fires the reverse-handoff
      // effect, which restarts the solo publisher automatically. Also
      // clear every in-flight game state so the "You win" ghost overlay
      // cannot linger after the partner drops.
      linkRoomInfoRef.current = null;
      setLinkRoomInfo(null);
      setInvitedPlayers([]);
      setLinkedSessionId(null);
      setAllLinkedUsers([]);
      setActiveLinkedGame(null);
      setInStreamGame(null);
      setCheckersLiveState(null);
      setC8LiveState(null);
      setGameState(null);
      setSharedChatStreamId(null);

      // Restore solo streaming — Link v2 owns republishing via the
      // reverse-handoff effect; only legacy Agora needs the manual
      // getUserMedia/startPreview restore.
      if (!LIVEKIT_LINKS && webrtc) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user', width: 720, height: 1280 },
            audio: true
          });
          webrtc.localStream = stream;
          const videoElement = document.querySelector('video#local-preview, video.local-video');
          if (videoElement) videoElement.srcObject = stream;
        } catch (e) {
          try { await startPreview(); } catch (e2) {}
        }
      }
      
      toast.info(`${endedByName} ended the linked broadcast - back to solo`);
    };
    
    window.addEventListener('partner-ended-link', handlePartnerEndedLink);
    return () => window.removeEventListener('partner-ended-link', handlePartnerEndedLink);
  }, [webrtc, fullStreamCleanup]);

  // ========================================================
  // HANDLE LINKED GAME CHALLENGE (New flow)
  // Opponent sent a game challenge - show accept/decline modal
  // ========================================================
  useEffect(() => {
    const handleGameChallenge = (event) => {
      const { game_id, challenger_id, challenger_name, wager_amount, game_type, c8_rules } = event.detail;
      console.log('🎮 Received game CHALLENGE from:', challenger_name, 'entry:', wager_amount);
      
      // Show challenge modal instead of directly starting game
      setPendingGameChallenge({
        gameId: game_id,
        challengerId: challenger_id,
        challengerName: challenger_name,
        wagerAmount: wager_amount,
        gameType: game_type,
        c8Rules: c8_rules || null,
      });
      setShowGameChallengeModal(true);
    };
    
    window.addEventListener('linked-game-challenge', handleGameChallenge);
    return () => window.removeEventListener('linked-game-challenge', handleGameChallenge);
  }, []);

  // ========================================================
  // HANDLE LINKED GAME ACCEPTED (Challenger side)
  // Opponent accepted our challenge - start the game
  // ========================================================
  useEffect(() => {
    const handleGameAccepted = async (event) => {
      const { game_id, accepted_by, accepted_by_name, total_pot, board, current_player, game_type } = event.detail;
      const isCheckers = game_type === 'checkers';
      console.log(`🎮 Challenge ACCEPTED by:`, accepted_by_name, '- game_type:', game_type);

      if (isCheckers) {
        // LINK v2 dedupe (Aug 18, 2026): if the same linked_game_accepted
        // event fires twice (both sync sockets), the second /start returns
        // 400 "already started". Guard idempotency and treat 400 as SUCCESS
        // — only hard failures reset activeLinkedGame.
        if (inStreamGame === game_id) return;
        // Checkers: fire the (unchanged) money-critical /game/{id}/start,
        // then render IN-STREAM (no navigate — that white-screened both
        // players by dropping the Agora session). Connect the game WS so the
        // checkers_state broadcast (fired inside /start) drives the board in
        // the live overlay, exactly like 4iaR / Crazy Eights.
        let startOk = true;
        try {
          await api.post(`/game/${game_id}/start`);
        } catch (err) {
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail || '';
          const alreadyStarted = status === 400 && /already|playing|started/i.test(String(detail));
          if (alreadyStarted) {
            console.warn('[checkers] /start returned 400 (already started) — treating as success and wiring WS/board:', detail);
          } else {
            startOk = false;
            console.error('[checkers] /start failed:', err);
            toast.error(detail || 'Could not start Checkers match');
            setActiveLinkedGame(null);
          }
        }
        if (!startOk) return;
        try {
          setLinkedGameOpponent({ id: accepted_by, name: accepted_by_name });
          try { await connectGameWebSocket(game_id, user?.id); } catch (e) { console.warn('[checkers] game WS connect failed:', e); }
          // Seed the initial board (GET returns checkers_state with color_map
          // inside); the WS then delivers every move.
          try { const g = await api.get(`/game/${game_id}`); if (g.data?.checkers_state) setCheckersLiveState(g.data.checkers_state); } catch (e) { /* WS will seed it */ }
          setInStreamGame(game_id);
          setActiveLinkedGame({
            gameId: game_id,
            gameType: 'checkers',
            myPlayerNum: 1,
            players: [
              { id: user?.id, name: user?.name },
              { id: accepted_by, name: accepted_by_name },
            ],
            phase: 'playing',
          });
          toast.success(`${accepted_by_name} accepted! Checkers match starting…`);
        } catch (err) {
          console.error('[checkers] post-start wiring failed:', err);
          toast.error('Checkers wiring failed');
          setActiveLinkedGame(null);
        }
        return;
      }

      if (game_type === 'crazy_eights') {
        // C8 challenger flow (Aug 18, 2026) — mirrors checkers.
        // Acceptance is our signal to /start; cards then arrive via
        // snapshot-on-connect + c8_state broadcast. Dedupe against
        // the duplicate sync-socket delivery.
        if (inStreamGame === game_id) return;
        let startOk = true;
        try {
          await api.post(`/game/${game_id}/start`);
        } catch (err) {
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail || '';
          const alreadyStarted = status === 400 && /already|playing|started/i.test(String(detail));
          if (!alreadyStarted) {
            startOk = false;
            console.error('[c8] /start failed:', err);
            toast.error(detail || 'Could not start Crazy Eights match');
            setActiveLinkedGame(null);
          }
        }
        if (!startOk) return;
        try {
          setLinkedGameOpponent({ id: accepted_by, name: accepted_by_name });
          try { await connectGameWebSocket(game_id, user?.id); } catch (e) { console.warn('[c8] game WS connect failed:', e); }
          setInStreamGame(game_id);
          setActiveLinkedGame({
            gameId: game_id,
            gameType: 'crazy_eights',
            myPlayerNum: 1,
            players: [
              { id: user?.id, name: user?.name },
              { id: accepted_by, name: accepted_by_name },
            ],
            phase: 'playing',
          });
          toast.success(`${accepted_by_name} accepted! Dealing cards…`);
        } catch (err) {
          console.error('[c8] post-start wiring failed:', err);
        }
        return;
      }

      toast.success(`${accepted_by_name} accepted! Game starting...`);

      // 4iaR — legacy inline flow (unchanged).
      setActiveLinkedGame(prev => ({
        ...prev,
        gameId: game_id,
        board: board || prev?.board || Array(7).fill(null).map(() => Array(6).fill(0)),
        currentPlayer: current_player || 1,
        myPlayerNum: 1, // Challenger is Player 1
        phase: 'playing',
        totalPot: total_pot
      }));
      setLinkedGameOpponent({ id: accepted_by, name: accepted_by_name });
    };
    
    window.addEventListener('linked-game-accepted', handleGameAccepted);
    return () => window.removeEventListener('linked-game-accepted', handleGameAccepted);
  }, [navigate]);

  // ========================================================
  // HANDLE LINKED GAME DECLINED (Challenger side)
  // Opponent declined our challenge
  // ========================================================
  useEffect(() => {
    const handleGameDeclined = (event) => {
      const { game_id, declined_by_name, reason } = event.detail;

      // Phantom-decline guard (Aug 18, 2026): a late/duplicate decline can
      // fire minutes after the game already started (both frontend timers
      // AND backend expirers). If we're already playing this game (or any
      // linked game already in playing phase), swallow it — never toast,
      // never wipe activeLinkedGame.
      if (
        (inStreamGame && inStreamGame === game_id) ||
        (activeLinkedGame && activeLinkedGame.gameId === game_id) ||
        (activeLinkedGame && activeLinkedGame.phase === 'playing')
      ) {
        console.warn('[linked-game-declined] ignored — game already active:', game_id, reason);
        return;
      }
      console.log('🎮 Challenge DECLINED by:', declined_by_name, reason);

      if (reason === 'insufficient_funds') {
        toast.error(`${declined_by_name} doesn't have enough emeralds for this entry`);
      } else {
        toast.info(`${declined_by_name} declined the challenge`);
      }

      // Clear pending game state
      setActiveLinkedGame(null);
    };

    window.addEventListener('linked-game-declined', handleGameDeclined);
    return () => window.removeEventListener('linked-game-declined', handleGameDeclined);
  }, [inStreamGame, activeLinkedGame]);

  // ========================================================
  // HANDLE LINKED GAME INVITE (Legacy - for backwards compat)
  // When the linked partner starts a game, show it on this side too
  // ========================================================
  useEffect(() => {
    const handleLinkedGameInvite = (event) => {
      const { game_id, inviter_id, inviter_name, wager_amount, board } = event.detail;
      console.log('🎮 Received game invite from linked partner:', inviter_name);
      
      // Initialize the game overlay for this user (Player 2)
      setActiveLinkedGame({
        gameId: game_id,
        board: board || Array(7).fill(null).map(() => Array(6).fill(0)),
        currentPlayer: 1,
        myPlayerNum: 2, // We are player 2 (the invited player)
        players: [
          { id: inviter_id, name: inviter_name, player_num: 1 },
          { id: user?.id, name: user?.name, player_num: 2 }
        ],
        phase: 'playing',
        wagerAmount: wager_amount
      });
      setLinkedGameOpponent({ id: inviter_id, name: inviter_name });
    };
    
    window.addEventListener('linked-game-invite', handleLinkedGameInvite);
    return () => window.removeEventListener('linked-game-invite', handleLinkedGameInvite);
  }, [user]);

  // ========================================================
  // HANDLE LINKED GAME UPDATE
  // Sync game moves between players
  // ========================================================
  useEffect(() => {
    const handleLinkedGameUpdate = (event) => {
      const { game_id, board, current_player, phase, winner, column, moved_by } = event.detail;
      
      if (!activeLinkedGame || activeLinkedGame.gameId !== game_id) {
        console.log('Received game update for different game, ignoring');
        return;
      }
      
      console.log('🎮 Game update received - col:', column, 'player:', current_player);
      
      setActiveLinkedGame(prev => ({
        ...prev,
        board: board,
        currentPlayer: current_player,
        phase: phase
      }));
      
      if (phase === 'finished') {
        if (winner === activeLinkedGame.myPlayerNum) {
          toast.success(<span className="flex items-center gap-1">You won! +{Math.floor(activeLinkedGame.wagerAmount * 2 * (1 - (economyRates?.rake_1v1 ?? 0.08)))} <RubyIcon className="h-4 w-4 inline" /></span>);
          // Update session wins
          setLinkedSessionWins(prev => ({ ...prev, me: prev.me + 1 }));
        } else if (winner === 0) {
          toast.info("It's a draw!");
        } else {
          toast.error(`${linkedGameOpponent?.name} won!`);
          setLinkedSessionWins(prev => ({ ...prev, opponent: prev.opponent + 1 }));
        }

        // Match-record screenshot (LIVE 4iaR only, from the WS-driven
        // remote-move path). Idempotent on the server via upsert.
        captureAndUploadGameResult({
          gameId: activeLinkedGame.gameId,
          gameType: 'four_in_a_row',
          selector: '[data-testid="linked-game-overlay"]',
          isPractice: false,
          winnerId: winner === activeLinkedGame.myPlayerNum
            ? (user?.id || null)
            : (winner === 0 ? null : (linkedGameOpponent?.id || null)),
          resultSummary: winner === 0 ? 'Draw' : `Player ${winner} won`,
          wager: Number(activeLinkedGame.wagerAmount) || 0,
          participants: [
            { user_id: user?.id, name: user?.name, handle: user?.handle },
            { user_id: linkedGameOpponent?.id, name: linkedGameOpponent?.name, handle: linkedGameOpponent?.handle },
          ],
        });

        setTimeout(() => {
          setActiveLinkedGame(null);
          setInStreamGame(null);
          setCheckersLiveState(null);
          setC8LiveState(null);
        }, 3000);
      }
    };
    
    window.addEventListener('linked-game-update', handleLinkedGameUpdate);
    return () => window.removeEventListener('linked-game-update', handleLinkedGameUpdate);
  }, [activeLinkedGame, linkedGameOpponent]);

  // ========================================================
  // HANDLE NEW BROADCASTER JOINING SESSION (Multi-party)
  // When a 3rd or 4th broadcaster joins the linked session
  // ========================================================
  useEffect(() => {
    const handleBroadcasterJoined = (event) => {
      const { new_broadcaster_id, new_broadcaster_name, all_linked_users, all_linked_user_names, linked_count, linked_session_id, max_broadcasters } = event.detail;
      if (max_broadcasters) setLinkCap(max_broadcasters);   // Round 63
      
      console.log('👥 New broadcaster joined:', new_broadcaster_name, 'Total:', linked_count);
      
      // Update linked session tracking. SEP 3 2026: union-merge, never
      // replace — see the link_started handler for why (stale echoes).
      setLinkedSessionId(linked_session_id);
      const mergedRoster = mergeLinkedRoster(all_linked_users, all_linked_user_names);
      setAllLinkedUsers(mergedRoster.users);
      setAllLinkedUserNames(mergedRoster.names);

      // Add or upgrade broadcaster in invitedPlayers.
      // If they were pre-populated by the invite-send flow (no status),
      // upgrade their entry from "Pending" to "Linked". Otherwise append.
      setInvitedPlayers(prev => {
        // Don't add self
        if (new_broadcaster_id === user?.id) return prev;
        const existingIdx = prev.findIndex(p => p.id === new_broadcaster_id || p.handle === new_broadcaster_name?.toLowerCase()?.replace(/\s+/g, ''));
        if (existingIdx >= 0) {
          const upgraded = [...prev];
          upgraded[existingIdx] = { ...upgraded[existingIdx], id: new_broadcaster_id, status: 'linked' };
          return upgraded;
        }
        return [...prev, {
          id: new_broadcaster_id,
          name: new_broadcaster_name,
          handle: new_broadcaster_name.toLowerCase().replace(/\s+/g, ''),
          status: 'linked'
        }];
      });
      
      // The new broadcaster will publish their tracks to Agora
      // Our existing user-published handler will pick them up automatically

      // LINK v2 SELF-HEAL (Aug 18, 2026): if this event includes us in
      // all_linked_users but we're not yet in the LiveKit linked room
      // (e.g. accept-time token 403'd), join now. Idempotent.
      if (LIVEKIT_LINKS && isActiveBroadcaster && Array.isArray(all_linked_users) && all_linked_users.includes(user?.id) && linked_session_id) {
        const expected = `linked_${linked_session_id}`;
        if (linkRoomInfoRef.current?.roomName !== expected) {
          console.log('[LiveKitLink] self-heal: joining', expected, 'after broadcaster_joined_session');
          initLiveKitLinkRoom(expected, { sessionType: 'linked_broadcaster' }).catch((e) =>
            console.error('[LiveKitLink] self-heal join failed:', e)
          );
        }
      }
    };
    
    window.addEventListener('broadcaster-joined-session', handleBroadcasterJoined);
    return () => window.removeEventListener('broadcaster-joined-session', handleBroadcasterJoined);
  }, [user?.id, isActiveBroadcaster, initLiveKitLinkRoom, mergeLinkedRoster]);

  // ========================================================
  // HANDLE BROADCASTER LEFT SESSION (Multi-party)
  // When a broadcaster leaves but others remain linked
  // ========================================================
  useEffect(() => {
    const handleBroadcasterLeft = async (event) => {
      const { left_by_id, left_by_name, remaining_users, linked_count } = event.detail;

      console.log('👤 Broadcaster left:', left_by_name, 'Remaining count:', linked_count);

      // Update linked session tracking
      setAllLinkedUsers(remaining_users || []);

      // Remove the leaver from local lists
      setInvitedPlayers((prev) => prev.filter((p) => p.id !== left_by_id));
      setConnectedCoHosts((prev) => prev.filter((ch) => ch.id !== left_by_id));

      // ============================================================
      // MESH-FIX (May 15, 2026): WS-DRIVEN UID EVICTION
      // ============================================================
      // Agora's native `user-left` event can be delayed by 2-10s, or never
      // fire at all when the leaver abrupt-closes their tab (no graceful
      // leave). The leaver's UID then sits in remoteAgoraUsers and renders
      // a frozen tile. WebSocket-driven `broadcaster_left_session` is our
      // authoritative signal — evict the leaver's UID immediately.
      // ============================================================
      if (left_by_id) {
        try {
          const leaverUid = String(uidFromUserId(left_by_id));
          setRemoteAgoraUsers((prev) => {
            if (!(leaverUid in prev)) return prev;
            const updated = { ...prev };
            try {
              if (updated[leaverUid]?.audioTrack?.stop) updated[leaverUid].audioTrack.stop();
            } catch {}
            delete updated[leaverUid];
            console.log(`🧹 [Mesh-Fix] WS-evicted UID ${leaverUid} (${left_by_name}) from remoteAgoraUsers`);
            return updated;
          });
        } catch (e) {
          console.warn('[Mesh-Fix] WS-evict failed:', e?.message);
        }
      }

      // CRITICAL: If only 1 broadcaster remains (just me), drop back to solo mode
      // This handles the case where a 2-party session loses one user — the lone
      // remaining broadcaster should NOT stay in multi-party Agora mode.
      const onlyMeRemains = linked_count === 1 || (Array.isArray(remaining_users) && remaining_users.length === 1 && remaining_users[0] === user?.id);

      if (onlyMeRemains) {
        console.log('🎯 Only I remain — transitioning back to solo mode');
        toast.info(`${left_by_name} left — back to solo streaming`);

        try {
          // Full Agora cleanup (unpublish, leave channel, clear tracks)
          await fullStreamCleanup('last_cohost_left');
        } catch (e) {
          console.warn('Agora cleanup error (non-fatal):', e?.message);
        }

        // Restore solo WebRTC streaming
        if (webrtc) {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: 'user', width: 720, height: 1280 },
              audio: true,
            });
            webrtc.localStream = stream;
            const videoElement = document.querySelector('video#local-preview, video.local-video');
            if (videoElement) videoElement.srcObject = stream;
            console.log('✅ Solo camera restored');
          } catch (e) {
            console.warn('Failed to restore camera, trying startPreview fallback:', e?.message);
            try { await startPreview(); } catch (e2) { /* noop */ }
          }
        }

        // Update stream record so viewers also transition to LiveKit
        if (currentStream?.stream_id) {
          try {
            await api.patch(`/streams/${currentStream.stream_id}`, {
              stream_type: 'solo',
              linked_session_id: null,
              linked_users: [],
              linked_user_names: [],
            });
          } catch (e) {
            console.warn('Failed to update stream record:', e?.message);
          }
        }
      } else {
        // Multi-party (2+ remaining including me) — just notify and stay in Agora
        toast.info(`${left_by_name} left the session`);
      }
    };

    window.addEventListener('broadcaster-left-session', handleBroadcasterLeft);
    return () => window.removeEventListener('broadcaster-left-session', handleBroadcasterLeft);
  }, [user?.id, currentStream?.stream_id, webrtc]);

  // ========================================================
  // KEEP linkedSessionId AND allLinkedUsers IN SYNC WITH BACKEND
  // Fires whenever a `link_started` WebSocket event arrives with us as a participant.
  // ========================================================
  useEffect(() => {
    const handleLinkedSessionUpdate = (event) => {
      const { linked_session_id: lsid, all_linked_users, all_linked_user_names, linked_count, max_broadcasters } = event.detail || {};
      if (!lsid) return;
      if (max_broadcasters) setLinkCap(max_broadcasters);   // Round 63
      if (Array.isArray(all_linked_users) && all_linked_users.includes(user?.id)) {
        console.log('🔗 Updating local linked session state:', { lsid, count: linked_count });
        setLinkedSessionId(lsid);
        // SEP 3 2026 FIX (missed-invite bug): link_started echoes arrive
        // OUT OF ORDER when several accepts race — a stale 3-member payload
        // landing after the 4th join used to REPLACE the roster and drop a
        // partner, so the spades challenge skipped them. link_started only
        // ever ADDS members → union-merge, never replace.
        const merged = mergeLinkedRoster(all_linked_users, all_linked_user_names);
        setAllLinkedUsers(merged.users);
        setAllLinkedUserNames(merged.names);

        // LINK v2 SELF-HEAL (Aug 18, 2026): we are a confirmed member of
        // this session but may not be in its LiveKit room yet (accept-time
        // token 403'd on the membership race). link_started proves
        // membership — join now. Idempotent: init skips if already in room.
        if (LIVEKIT_LINKS && isActiveBroadcaster) {
          const expected = `linked_${lsid}`;
          if (linkRoomInfoRef.current?.roomName !== expected) {
            console.log('[LiveKitLink] self-heal: joining', expected, 'after link_started');
            initLiveKitLinkRoom(expected, { sessionType: 'linked_broadcaster' }).catch((e) =>
              console.error('[LiveKitLink] self-heal join failed:', e)
            );
          }
        }
      }
    };

    window.addEventListener('linked-session-updated', handleLinkedSessionUpdate);
    return () => window.removeEventListener('linked-session-updated', handleLinkedSessionUpdate);
  }, [user?.id, isActiveBroadcaster, initLiveKitLinkRoom, mergeLinkedRoster]);

  // ========================================================
  // HANDLE SPADES GAME STARTED (From linked live host)
  // When host starts Spades, non-host players initialize their game
  // ========================================================
  useEffect(() => {
    const handleSpadesGameStarted = async (event) => {
      const { game_id, started_by, game_type } = event.detail;
      console.log('🎮 game_started received from host:', started_by, 'type:', game_type);
      
      // Only non-host players should react
      if (started_by === user?.id) {
        console.log('🎮 I am the host, ignoring game_started');
        return;
      }
      
      if (!game_id) {
        console.log('🎮 No game_id in event');
        return;
      }
      
      setInStreamGame(game_id);
      // Crazy Eights drives its own board off the c8_state WS push
      // (c8LiveState). The invitee's game WS is already connected from the
      // invite-accept, so cards will arrive there. Do NOT initialize the
      // Spades gameState pipeline — the C8 overlay only renders when
      // `!gameState`, so setting a Spades gameState would hide the C8 board.
      if (game_type === 'crazy_eights') {
        toast.success('Crazy Eights starting! Dealing cards…', { duration: 3000 });
        return;
      }
      toast.success('Game starting! Get ready...', { duration: 3000 });
      
      // Feb 20, 2026: single shared hydrator — retries once if the deal
      // race means /start is still writing hands.
      await hydrateSpadesFromServer(game_id);
    };
    
    window.addEventListener('spades-game-started', handleSpadesGameStarted);
    return () => window.removeEventListener('spades-game-started', handleSpadesGameStarted);
  }, [user?.id]);

  // AUTO-REJOIN A LIVE GAME (Feb 2026): fired by the auto_rejoin invite
  // path when a dropped broadcaster comes back within the grace window and
  // their seat is still active — reconnect the game WS and rehydrate.
  useEffect(() => {
    const handleRejoinGame = async (e) => {
      const gid = e.detail?.game_id;
      if (!gid) return;
      const gtype = e.detail?.game_type || 'spades';
      console.log('🔁 Rejoining active game:', gid, gtype);
      setInStreamGame(gid);
      // Reconnecting the game WS triggers the server's snapshot-on-connect,
      // which repopulates C8 / Checkers / Spades state alike.
      try { await connectGameWebSocket(gid, user?.id); } catch (err) {
        console.warn('rejoin game WS failed (hydrate still runs):', err?.message);
      }
      if (gtype === 'spades') await hydrateSpadesFromServer(gid);
    };
    window.addEventListener('spades-rejoin-game', handleRejoinGame);
    return () => window.removeEventListener('spades-rejoin-game', handleRejoinGame);
  }, [user?.id]);

  // ============================================================
  // SPADES IN-STREAM INVITE FLOW (May 16, 2026)
  // ============================================================
  // Invitee modal + host pending-state listeners. Video mesh untouched —
  // these only drive the card-UI overlay.
  useEffect(() => {
    const onInvite = (e) => {
      const detail = e.detail || {};
      // Ignore self-echo (host sees their own invite event)
      if (detail.host_id === user?.id) return;
      // Don't show modal if user is already in a game
      if (inStreamGame) return;
      console.log('🎴 Spades invite modal opening for', detail.host_name);
      setIncomingSpadesInvite(detail);
    };
    const onInviteCancelled = (e) => {
      const detail = e.detail || {};
      // Dismiss modal if it's for this game
      setIncomingSpadesInvite((prev) => (prev && prev.game_id === detail.game_id ? null : prev));
      // Host-side cancel echo: drop the pending state
      setSpadesInvitePending((prev) => (prev && prev.game_id === detail.game_id ? null : prev));
      // Anyone already in the pre-game layout: tear the game state down
      // and return to the plain linked grid.
      setInStreamGame((prev) => {
        if (prev && detail.game_id && prev === detail.game_id) {
          setGameState(null);
          setMyCards([]);
          return null;
        }
        return prev;
      });
      toast.info(detail.cancelled_by_name ? `${detail.cancelled_by_name} cancelled the spades game` : 'Spades game cancelled');
    };
    const onPlayerJoined = (e) => {
      const detail = e.detail || {};
      setSpadesInvitePending((prev) => {
        if (!prev || prev.game_id !== detail.game_id) return prev;
        const joinedIds = new Set(prev.joined_ids || []);
        if (detail.joined_by_id) joinedIds.add(detail.joined_by_id);
        return { ...prev, joined_ids: Array.from(joinedIds), player_count: detail.player_count };
      });
      // Echo to invitee themselves: dismiss the local invite modal once they
      // joined (in case they accept on another device).
      setIncomingSpadesInvite((prev) => (prev && prev.game_id === detail.game_id && detail.joined_by_id === user?.id ? null : prev));
    };
    const onForfeit = (e) => {
      const detail = e.detail || {};
      toast.info(detail.forfeited_by_name ? `${detail.forfeited_by_name} forfeited the game` : 'A player forfeited');
      // Everyone touching the affected game rolls back to the linked grid.
      setInStreamGame((prev) => {
        if (prev && detail.game_id && prev === detail.game_id) {
          setGameState(null);
          setMyCards([]);
          return null;
        }
        // If WE forfeited (no game_id in detail on some paths), still clear.
        if (detail.forfeited_by_id === user?.id) {
          setGameState(null);
          setMyCards([]);
          return null;
        }
        return prev;
      });
    };
    window.addEventListener('spades-game-invite', onInvite);
    window.addEventListener('spades-game-invite-cancelled', onInviteCancelled);
    window.addEventListener('spades-game-cancelled', onInviteCancelled); // same handler — same dismiss
    window.addEventListener('spades-game-player-joined', onPlayerJoined);
    window.addEventListener('spades-game-forfeit', onForfeit);
    return () => {
      window.removeEventListener('spades-game-invite', onInvite);
      window.removeEventListener('spades-game-invite-cancelled', onInviteCancelled);
      window.removeEventListener('spades-game-cancelled', onInviteCancelled);
      window.removeEventListener('spades-game-player-joined', onPlayerJoined);
      window.removeEventListener('spades-game-forfeit', onForfeit);
    };
  }, [user?.id, inStreamGame]);

  // Auto-cancel a pending spades invite after 30 seconds if not everyone accepted.
  useEffect(() => {
    if (!spadesInvitePending) {
      if (spadesCancelTimerRef.current) {
        clearTimeout(spadesCancelTimerRef.current);
        spadesCancelTimerRef.current = null;
      }
      return;
    }
    // Already started? clear timer.
    const allAccepted = (spadesInvitePending.invitee_ids || []).every((id) =>
      (spadesInvitePending.joined_ids || []).includes(id),
    );
    if (allAccepted) {
      if (spadesCancelTimerRef.current) {
        clearTimeout(spadesCancelTimerRef.current);
        spadesCancelTimerRef.current = null;
      }
      return;
    }
    spadesCancelTimerRef.current = setTimeout(async () => {
      try {
        await api.post(`/game/${spadesInvitePending.game_id}/cancel-game`, {
          invitee_user_ids: spadesInvitePending.invitee_ids,
          reason: 'timeout_30s',
        });
        toast.info("Not everyone joined — back to the stream.");
      } catch (e) {
        console.warn('Spades auto-cancel failed:', e?.message);
      }
      setSpadesInvitePending(null);
      setInStreamGame(null);
      setGameState(null);
    }, 30000);
    return () => {
      if (spadesCancelTimerRef.current) {
        clearTimeout(spadesCancelTimerRef.current);
        spadesCancelTimerRef.current = null;
      }
    };
  }, [spadesInvitePending]);

  // Part 3d (Feb 19, 2026): once all invitees have accepted, the host
  // fires /game/{id}/start. This is the trigger that dispatches the
  // real deal on the backend and unblocks every player's quadrant +
  // scoreboard + fanned hand.
  const spadesStartedRef = useRef(new Set());
  useEffect(() => {
    if (!spadesInvitePending) return;
    const invitees = spadesInvitePending.invitee_ids || [];
    const joined = spadesInvitePending.joined_ids || [];
    if (invitees.length === 0) return;
    const allAccepted = invitees.every((id) => joined.includes(id));
    if (!allAccepted) return;
    const gid = spadesInvitePending.game_id;
    if (!gid || spadesStartedRef.current.has(gid)) return;
    spadesStartedRef.current.add(gid);
    (async () => {
      try {
        await api.post(`/game/${gid}/start`);
        console.log('✅ Spades /start dispatched after all invitees accepted');
        // Mirror the sync broadcast so every player's frontend gets a
        // second poke (the game WS `game_started` may race the accept).
        try {
          await api.post('/sync/broadcast', {
            event_type: 'game_started',
            data: { game_id: gid, started_by: user?.id, game_type: 'spades' },
            target_users: invitees,
          });
        } catch {}
      } catch (e) {
        console.warn('Deferred Spades /start failed:', e?.message);
        spadesStartedRef.current.delete(gid);
      }
    })();
  }, [spadesInvitePending, user?.id]);

  // Invitee accept/decline actions.
  const acceptIncomingSpadesInvite = useCallback(async () => {
    if (!incomingSpadesInvite) return;
    const gid = incomingSpadesInvite.game_id;
    try {
      await api.post(`/game/${gid}/join-invite`);
      setInStreamGame(gid);
      setGameState({ phase: 'waiting', team1Score: 0, team2Score: 0 });
      try { connectGameWebSocket(gid, user?.id); } catch {}
      setIncomingSpadesInvite(null);
      toast.success('Joined the spades game');
    } catch (e) {
      toast.error('Failed to join game');
      console.error('Spades join failed:', e);
    }
  }, [incomingSpadesInvite, user?.id]);

  const declineIncomingSpadesInvite = useCallback(async () => {
    if (!incomingSpadesInvite) return;
    const gid = incomingSpadesInvite.game_id;
    try {
      await api.post(`/game/${gid}/decline-invite`);
    } catch (e) {
      console.warn('Spades decline failed:', e?.message);
    }
    setIncomingSpadesInvite(null);
  }, [incomingSpadesInvite]);

  // Host-side helpers.
  const sendSpadesInviteToLinkedBroadcasters = useCallback(async (gameId) => {
    // Part 1a (Feb 19, 2026 SPADES FIX): `allLinkedUsers` holds ID
    // STRINGS, not user objects — the old `.map(u => u?.id)` returned
    // all-undefined and the invite silently short-circuited. Normalize
    // string OR object shapes here.
    const inviteeIds = (allLinkedUsers || [])
      .map((u) => (typeof u === 'string' ? u : u?.id))
      .filter((id) => id && id !== user?.id);
    // SEP 3 2026: no hard early-return on an empty local list — the server
    // now resolves the authoritative invitee set from the linked-session DB
    // roster and 400s only if IT finds nobody. A momentarily-stale local
    // roster must not block the challenge.
    // Part 3e: build the partner map + teams from the current
    // teamAssignments state (set inline by the wager-modal partner radio).
    // The invitee accept modal reads this from the sync payload.
    const partnerMap = {};
    let teamsPayload = {};
    try {
      if (teamAssignments && Array.isArray(teamAssignments.team1) && Array.isArray(teamAssignments.team2)) {
        const t1 = teamAssignments.team1.map((p) => p?.id).filter(Boolean);
        const t2 = teamAssignments.team2.map((p) => p?.id).filter(Boolean);
        teamsPayload = { team1: t1, team2: t2 };
        // Team 1 partners: the two team1 members are partners; ditto team2.
        if (t1.length === 2) { partnerMap[t1[0]] = t1[1]; partnerMap[t1[1]] = t1[0]; }
        if (t2.length === 2) { partnerMap[t2[0]] = t2[1]; partnerMap[t2[1]] = t2[0]; }
      }
    } catch {}
    try {
      const res = await api.post(`/game/${gameId}/invite-broadcasters`, {
        invitee_user_ids: inviteeIds,
        stream_id: currentStream?.stream_id,
        partner_map: partnerMap,
        teams: teamsPayload,
      });
      // SEP 3 2026: track accepts against the list the SERVER actually
      // invited (it may have corrected a stale local roster) so the
      // "x / y accepted" counter and 30s auto-cancel judge the real set.
      const serverInvited = res?.data?.invited?.length ? res.data.invited : inviteeIds;
      setSpadesInvitePending({
        game_id: gameId,
        invitee_ids: serverInvited,
        joined_ids: [],
        started_at: Date.now(),
      });
      return { invitee_ids: serverInvited };
    } catch (e) {
      toast.error('Could not send the challenge');
      console.error('Spades invite-broadcasters failed:', e);
      return { invitee_ids: [], error: 'invite_failed' };
    }
  }, [allLinkedUsers, user?.id, currentStream?.stream_id, teamAssignments]);

  const cancelPendingSpadesGame = useCallback(async () => {
    if (!spadesInvitePending) return;
    try {
      await api.post(`/game/${spadesInvitePending.game_id}/cancel-game`, {
        invitee_user_ids: spadesInvitePending.invitee_ids,
        reason: 'host_cancelled',
      });
    } catch (e) {
      console.warn('Spades cancel failed:', e?.message);
    }
    setSpadesInvitePending(null);
    setInStreamGame(null);
    setGameState(null);
  }, [spadesInvitePending]);

  const forfeitSpadesGame = useCallback(async () => {
    if (!inStreamGame) return;
    try {
      await api.post(`/game/${inStreamGame}/forfeit`);
    } catch (e) {
      console.warn('Spades forfeit failed:', e?.message);
    }
    setInStreamGame(null);
    setGameState(null);
    setMyCards([]);
    toast.info('You forfeited the game');
  }, [inStreamGame]);

  // ========================================================
  // AGORA INITIALIZATION FOR JOINING BROADCASTERS
  // Shared function to init Agora when joining a stream/game
  // ========================================================
  const initializeAgoraAsJoiner = async (agoraData) => {
    const { token, channelName, appId } = agoraData;
    console.log('🎥 Initializing Agora as joiner:', { channelName, hasToken: !!token });

    // Regression F: in-flight guard. If another concurrent path is already
    // initialising Agora for this same channel, bail. This catches the
    // overlap between link-live-accepted and broadcaster_joined_session
    // when both fire on a single accept.
    if (agoraInitInProgressRef.current === channelName) {
      console.log('🚫 [BugF] initializeAgoraAsJoiner suppressed — concurrent init for', channelName);
      return;
    }
    agoraInitInProgressRef.current = channelName;
    try {
    // Import Agora SDK
    const AgoraRTC = (await import('agora-rtc-sdk-ng')).default;

    // Checkpoint 4: unified factory.
    const client = await createAndJoinAgoraChannel({
      AgoraRTC,
      channelName,
      token,
      appId,
      uid: uidFromUserId(user?.id),
    });

    // Create and publish local tracks
    console.log('📷 Creating local camera/mic tracks...');
    const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
      { AEC: true, AGC: true, ANS: true, encoderConfig: 'speech_standard' },
      { encoderConfig: '720p_2', facingMode: 'user' }
    );
    
    await client.publish([audioTrack, videoTrack]);
    console.log('✅ Published local tracks to Agora');
    
    // Store tracks and state
    setSharedAgoraTracks({ audioTrack, videoTrack });
    setAgoraToken(token);
    setAgoraChannel(channelName);
    setAgoraAppId(appId);
    setUseAgora(true);
    
    // Stop WebRTC since Agora now has the camera
    if (webrtc && webrtc.localStream) {
      webrtc.localStream.getTracks().forEach(track => track.stop());
      console.log('🛑 WebRTC stopped - Agora has camera now');
    }
    
    console.log('✅ Agora ready as joiner!');
    } finally {
      // Regression F: release the in-flight guard so a future legitimate
      // accept can re-init (e.g. after End Link → re-link).
      if (agoraInitInProgressRef.current === channelName) {
        agoraInitInProgressRef.current = null;
      }
    }
  };

  // Handle joining a stream from invitation
  useEffect(() => {
    const handleJoinStream = async () => {
      if (!joiningStreamId || !user?.id) return;
      
      console.log('📨 Joining stream from invitation:', joiningStreamId);
      toast.info('Joining stream from invitation...', { duration: 2000 });
      
      try {
        // Get the stream info
        const streamResponse = await api.get(`/streams/${joiningStreamId}`);
        const streamData = streamResponse.data;
        console.log('📺 Stream data:', streamData);
        
        // Check if the stream has a game
        const gameId = streamData.game_id;
        
        // Start preview and go live (as invited player)
        await startPreview();
        
        setTimeout(async () => {
          // Start a stream as invited player
          const response = await api.post('/streams/create', {
            title: `${user.name} joining ${streamData.title}`,
            stream_type: 'multiplayer',
            join_stream_id: joiningStreamId,  // Link to host's stream
            game_id: gameId,  // Link to game if exists
            live_type: liveType
          });
          
          const myStreamId = response.data.stream_id;
          setCurrentStream({
            stream_id: myStreamId,
            title: response.data.title || `${user.name} joining ${streamData.title}`,
            user_id: user.id
          });
          setIsPreviewMode(false);
          
          // If there's a game, connect to it
          if (gameId) {
            console.log('🎮 Connecting to game:', gameId);
            
            // Connect to game WebSocket
            await connectGameWebSocket(gameId, user.id);
            
            // Join the game as a player
            const joinResponse = await api.post('/game/join', {
              gameId: gameId,
              playerId: user.id,
              playerName: user.name
            });
            
            console.log('✅ Joined game:', joinResponse.data);
            
            // Get Agora token to join multi-stream (game channel)
            try {
              if (LIVEKIT_LINKS) {
                // Link v2: publish into the shared LiveKit linked room; the game
                // board overlays the LiveKit mesh (video transport unchanged).
                const roomName = livekitLinkRoomName(streamData);
                await initLiveKitLinkRoom(roomName, { sessionType: 'linked_broadcaster' });
                toast.success('Joined game! Video connected!');
                setInStreamGame(gameId);
              } else {
                const agoraResponse = await api.post('/agora/token', {
                  channelName: `game_${gameId}`,
                  uid: uidFromUserId(user?.id),
                  role: 'publisher',
                  sessionType: 'multiplayer_game'
                });
                
                await initializeAgoraAsJoiner(agoraResponse.data);
                toast.success('Joined game! Video connected!');
                setInStreamGame(gameId);
              }
            } catch (agoraError) {
              console.error('❌ Failed to get Agora token:', agoraError);
              toast.error('Failed to join video stream');
            }
          } else {
            // ========================================================
            // BROADCASTER-TO-BROADCASTER (No Game)
            // Join the host's stream channel for 2-way video/audio
            // ========================================================
            console.log('🎥 Joining as co-broadcaster (no game):', joiningStreamId);

            // Resolve the canonical chat room from the inviter's stream record.
            // If the inviter is themselves a co-broadcaster of an existing linked
            // session, their `linked_session_id` points to the host's stream — we
            // must subscribe there so all linked streamers share one chat room.
            let canonicalChatStream = joiningStreamId;
            try {
              const streamData = await api.get(`/streams/${joiningStreamId}`);
              if (streamData?.data?.linked_session_id) {
                canonicalChatStream = streamData.data.linked_session_id.replace('stream_', '');
              }
            } catch (e) {
              console.warn('Could not resolve canonical linked_session_id, falling back to inviter stream:', e?.message);
            }
            setSharedChatStreamId(canonicalChatStream);
            
            try {
              if (LIVEKIT_LINKS) {
                // Link v2: co-broadcast join publishes into the shared LiveKit
                // linked room anchored to the host's stream.
                const roomName = livekitLinkRoomName(streamData);
                await initLiveKitLinkRoom(roomName, { sessionType: 'linked_broadcaster' });
                toast.success('Connected! You can now see and hear each other!');
              } else {
                const agoraResponse = await api.post('/agora/token', {
                  channelName: `stream_${joiningStreamId}`,
                  uid: uidFromUserId(user?.id),
                  role: 'publisher',
                  sessionType: 'multiplayer_stream'
                });
                
                await initializeAgoraAsJoiner(agoraResponse.data);
                toast.success('Connected! You can now see and hear each other!');
              }
            } catch (agoraError) {
              console.error('❌ Failed to join stream as co-broadcaster:', agoraError);
              toast.error('Failed to connect video');
            }
          }
          
          // Clear the joiningStreamId so we don't re-run
          setJoiningStreamId(null);
          
        }, 1000);
        
      } catch (error) {
        console.error('Error joining stream:', error);
        toast.error('Failed to join stream');
        setJoiningStreamId(null);
      }
    };
    
    handleJoinStream();
  }, [joiningStreamId, user?.id]);

  // Check matchmaking status on page load (only auto-start for Quick Match)
  useEffect(() => {
    const checkMatchmakingStatus = async () => {
      try {
        const response = await api.get('/matchmaking/status');
        if (response.data.inQueue) {
          console.log('User in matchmaking queue - auto-starting camera');
          setIsSearchingForMatch(true);
          setMatchmakingStatus(response.data);
          
          // Auto-start LIVE stream (not just preview) for Quick Match
          if (!webrtc.isStreaming) {
            console.log('Auto-starting live stream for Quick Match');
            await goLive();
          }
          
          // If matched, navigate to game
          if (response.data.status === 'matched') {
            toast.success('Match found! Starting game...');
            navigate(`/game/${response.data.gameId}`);
          }
        } else {
          console.log('User not in queue - manual start required');
        }
      } catch (error) {
        console.error('Error checking matchmaking status:', error);
      }
    };
    
    checkMatchmakingStatus();
    
    // Poll for match status every 3 seconds while searching
    const pollInterval = setInterval(() => {
      if (isSearchingForMatch) {
        checkMatchmakingStatus();
      }
    }, 3000);
    
    return () => clearInterval(pollInterval);
  }, [isSearchingForMatch, isPreviewMode]);

  const startPreview = async () => {
    console.log('🎬 Starting camera preview...');
    console.log('Called from matchmaking:', isSearchingForMatch);
    const stream = await webrtc.startStream();
    if (stream) {
      setIsPreviewMode(true);
      toast.success('Camera preview started. Ready to go live!');
      console.log('✅ Preview mode activated');
    } else {
      console.log('❌ Failed to start preview');
    }
  };

  // Prefill the cover from the user's remembered live_cover on entering preview.
  useEffect(() => {
    if (isPreviewMode && !coverUrl && user?.live_cover) {
      setCoverUrl(user.live_cover);
    }
  }, [isPreviewMode, user?.live_cover]);

  // Pre-live cover: resize the chosen image to max 1280px wide client-side,
  // then upload via the shared /media/upload mechanism.
  const handleCoverSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    setCoverUploading(true);
    try {
      const resizedBlob = await new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();
        reader.onload = () => { img.src = reader.result; };
        reader.onerror = reject;
        img.onload = () => {
          const maxW = 1280;
          const scale = Math.min(1, maxW / img.width);
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((b) => b ? resolve(b) : reject(new Error('resize failed')), 'image/jpeg', 0.85);
        };
        img.onerror = reject;
        reader.readAsDataURL(file);
      });
      const formData = new FormData();
      formData.append('file', new File([resizedBlob], 'cover.jpg', { type: 'image/jpeg' }));
      const uploadRes = await api.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = uploadRes.data.url || uploadRes.data.file_url;
      if (url) {
        setCoverUrl(url);
        toast.success('Cover photo set');
      } else {
        toast.error('Upload failed');
      }
    } catch (err) {
      console.error('Cover upload failed:', err);
      toast.error('Failed to upload cover photo');
    } finally {
      setCoverUploading(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  // Live-stream handoff — fetch our own active stream (if any) on LivePage
  // mount. If there is one AND we don't already have currentStream in memory
  // (i.e. this is a fresh browser after signing in on a new device), show the
  // "You're still LIVE — Resume broadcast" banner.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!user?.id) return;
        const r = await api.get('/streams/mine-active');
        if (cancelled) return;
        const s = r?.data?.stream || null;
        if (s && s.is_active) setMineActive(s);
      } catch (_e) {
        // 401 / 404 / offline — silently skip; the banner is optional.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Live-stream handoff — Resume broadcast. Reclaims the existing active
  // stream (same room, same game state, same viewers) by minting a
  // PUBLISHER LiveKit token for the SAME room, then flipping the UI into
  // the live state. The [Bug8] pending_broadcaster_disconnects grace (30s)
  // means viewers are still in the room; the stream-chat WS reconnect below
  // sends `is_streamer: true` on join, which cancels the pending
  // stream_ended. In-stream game state lives server-side and is untouched.
  const resumeLive = async () => {
    if (!mineActive || resumingLive) return;
    setResumingLive(true);
    try {
      // Freshest copy in case counts/linked state changed mid-window
      const fresh = await api.get(`/streams/${mineActive.id}`).catch(() => null);
      const s = fresh?.data || mineActive;
      const isLinked = !!(s?.linked_session_id || s?.linked_to_stream || s?.is_linked || s?.stream_type === 'multiplayer');
      const roomName = isLinked ? livekitLinkRoomName(s) : `solo_stream_${s.id}`;
      const sessionType = isLinked ? 'linked_broadcaster' : 'solo_stream';

      const lk = await api.post('/livekit/token', {
        roomName,
        isPublisher: true,
        sessionType,
      });

      // Hydrate LivePage state so the existing render path lights up the
      // LiveKit publisher view (LiveKitLinkView / LiveKitBroadcasterView).
      setCurrentStream({
        ...s,
        stream_id: s.id,
        looking_to_play: !!s.looking_to_play,
      });
      setLivekitToken(lk.data.token);
      setLivekitRoomName(lk.data.roomName || roomName);
      setLivekitUrl(lk.data.url || null);
      setUseLiveKit(true);
      setIsPreviewMode(false);
      setStreamStartTime(Date.now() - ((s.duration_seconds || 0) * 1000));

      // Stop any local WebRTC preview tracks; LiveKit takes over.
      try {
        if (webrtc && webrtc.localStream) {
          webrtc.localStream.getTracks().forEach(t => t.stop());
        }
      } catch (_e) { /* noop */ }

      setMineActive(null);
      toast.success('Resumed your live broadcast');
    } catch (e) {
      console.error('resumeLive failed:', e);
      toast.error('Could not resume broadcast — try again');
    } finally {
      setResumingLive(false);
    }
  };

  const goLive = async () => {
    // Optimistic-UI: flip spinner ON immediately so the button feels
    // instant even while /streams/create + /livekit/token round-trip.
    setIsGoingLive(true);try {
      let streamId = currentStream?.stream_id;
      
      if (!currentStream) {
        // Create stream first
        const response = await api.post('/streams/create', {
          title: streamTitle,
          description: '',
          portrait_mode: true,
          max_viewers: 100,
          stream_type: 'solo', // Mark as solo stream - Uses LiveKit, NOT Agora
          live_type: liveType,
          thumbnail: coverUrl || null
        });
        streamId = response.data.stream_id;
        setCurrentStream(response.data);
      }
      
      // ========================================================
      // LIVEKIT FOR SOLO STREAMS
      // ========================================================
      // Solo streams use LiveKit for scalable video distribution.
      // Agora is ONLY used for multiplayer games (4-player Spades).
      // ========================================================
      
      console.log('📺 Starting SOLO stream with LiveKit - stream_id:', streamId);
      
      // Get LiveKit token for publishing
      try {
        console.log('🎥 Fetching LiveKit token for publisher...');
        const livekitResponse = await api.post('/livekit/token', {
          roomName: `solo_stream_${streamId}`,
          isPublisher: true,
          sessionType: 'solo_stream'
        });
        
        console.log('✅ LiveKit token received:', {
          roomName: livekitResponse.data.roomName,
          url: livekitResponse.data.url,
          role: livekitResponse.data.role
        });
        
        setLivekitToken(livekitResponse.data.token);
        setLivekitRoomName(livekitResponse.data.roomName);
        setLivekitUrl(livekitResponse.data.url || null);
        setUseLiveKit(true);
        
        // Stop WebRTC since LiveKit will handle video
        if (webrtc && webrtc.localStream) {
          webrtc.localStream.getTracks().forEach(track => track.stop());
          console.log('🛑 WebRTC stopped - LiveKit taking over');
        }
        
        toast.success('LiveKit video streaming enabled! 📹');
      } catch (livekitError) {
        console.error('❌ Failed to get LiveKit token:', livekitError);
        toast.error('Video streaming unavailable. Using fallback preview.');
        // Continue without LiveKit - WebRTC preview only
      }
      
      // Start streaming with the correct stream_id
      const startResponse = await api.post(`/streams/${streamId}/start`);
      console.log('✅ Stream start response:', startResponse.data);
      setIsPreviewMode(false);
      console.log('✅ Stream started - isPreviewMode set to false');
      console.log('📺 Stream ID for sharing:', streamId);
      console.log('🔗 Share URL:', `${window.location.origin}/watch/${streamId}`);
      toast.success('You\'re now live! 🔴');
      
      // Start tracking stream time
      setStreamStartTime(Date.now());
      setStreamStats(prev => ({
        ...prev,
        liveTime: 0,
        totalViewers: 0,
        peakViewers: 0,
        engagedViewers: 0,
        newRubies: 0,
        giftGivers: 0,
        newFans: 0
      }));
      
      // If Quick Match mode, start matchmaking after going live
      if (selectedGameMode === 'quickMatch') {
        setTimeout(async () => {
          try {
            await handleQuickMatchFromStream();
          } catch (error) {
            console.error('Error starting Quick Match:', error);
          }
        }, 1000);
      }
      
    } catch (error) {
      console.error('Go Live error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start stream');
    } finally {
      setIsGoingLive(false);
    }
  };

  const stopStream = async () => {
    if (!currentStream) return;

    // Bug 4 (May 7 testing): the cleanup work below MUST run even when the
    // backend /stop call throws (403 owner-mismatch after link-up, 404 if
    // the doc was already mutated, network 5xx). Pre-fix the whole block
    // sat inside one try/catch, so a thrown API error left the user with a
    // hot camera and a dead "End Stream" button. We now isolate the API
    // call and run cleanup unconditionally inside `finally`.
    let stopApiError = null;
    try {
      const streamDuration = streamStartTime ? Math.floor((Date.now() - streamStartTime) / 1000) : 0;
      let finalStats = {
        liveTime: streamDuration,
        totalViewers: displayViewers,
        peakViewers: Math.max(displayViewers, streamStats.peakViewers),
        engagedViewers: streamStats.engagedViewers,
        newRubies: streamStats.newRubies,
        giftGivers: streamStats.giftGivers,
        newFans: streamStats.newFans
      };
      try {
        const statsResponse = await api.get(`/streams/${currentStream.stream_id}/stats`);
        if (statsResponse.data) {
          finalStats = { ...finalStats, ...statsResponse.data, liveTime: streamDuration };
        }
      } catch (statsError) {
        console.log('Using client-side stats:', statsError);
      }
      setStreamStats(finalStats);

      try {
        await api.post(`/streams/${currentStream.stream_id}/stop`);
      } catch (apiErr) {
        stopApiError = apiErr;
        const status = apiErr?.response?.status;
        const body = apiErr?.response?.data;
        console.error('❌ [Bug4] /streams/:id/stop failed (continuing with cleanup):', { status, body, message: apiErr?.message });
      }
    } finally {
      // ===== LOCAL CLEANUP — always runs regardless of API outcome =====
      try { webrtc?.stopStream?.(); } catch (e) { console.error('[Bug4] webrtc.stopStream cleanup err:', e); }

      if (inStreamGame) {
        if (gameWs) { try { gameWs.close(); } catch {} setGameWs(null); }
        setGameWsConnected(false);
        setInStreamGame(null);
        setGameState(null);
        setMyCards([]);
        console.log('🎮 In-stream game ended with stream');
      }

      // CRITICAL: Full Agora cleanup runs unconditionally (Bug 4 finally-block).
      try { await fullStreamCleanup('stream_ended'); }
      catch (e) { console.error('[Bug4] fullStreamCleanup error (ignored):', e); }

      if (useLiveKit && livekitRoomName) {
        api.post('/livekit/session-end', { roomName: livekitRoomName, reason: 'stream_ended' })
          .catch(err => console.error('[Bug4] LiveKit session-end log failed (ignored):', err));
      }
      setLivekitToken(null);
      setLivekitRoomName(null);
      setLivekitUrl(null);
      setUseLiveKit(false);
      console.log('📹 LiveKit state cleared');

      setShowStreamStatsModal(true);
      try { fetchLiveStreams(); } catch {}

      // Link v2 + game state hard reset (Aug 18, 2026): stale linkRoomInfo
      // or an in-flight game state was preventing the desktop Stream Ended
      // modal from opening. Wipe every branch synchronously.
      linkRoomInfoRef.current = null;
      setLinkRoomInfo(null);
      setActiveLinkedGame(null);
      setInStreamGame(null);
      setCheckersLiveState(null);
      setC8LiveState(null);
      setGameState(null);
      setLinkedSessionId(null);

      // Hard fallback: if the modal is not open 2s from now, punt the user
      // to /discover so they are NEVER stranded on a dead live page.
      setTimeout(() => {
        try {
          const modalOpen = document.querySelector('[data-testid="stream-ended-modal"]');
          if (!modalOpen) {
            console.warn('[endStream] Stream Ended modal never opened — routing to /discover');
            try { navigate('/discover'); } catch {}
          }
        } catch (e) { console.warn('[endStream] fallback check failed:', e); }
      }, 2000);

      setCurrentStream(null);
      setIsPreviewMode(false);
      setLookingToPlay(false);
      setStreamStartTime(null);
      setInvitedPlayers([]);
      setInviteGameId(null);
      setSelectedPartner(null);

      if (stopApiError) {
        toast.error('Stream ended locally (server reported an error — your camera is off).');
      }
    }
  };

  const toggleLookingToPlay = async () => {
    if (currentStream) {
      try {
        const response = await api.post(`/streams/${currentStream.stream_id}/toggle-looking-to-play`);
        setLookingToPlay(response.data.looking_to_play);
        toast.success(response.data.looking_to_play ? 'Now looking for opponents!' : 'No longer looking to play');
      } catch (error) {
        toast.error('Failed to update status');
      }
    }
  };

  const handleSelectGameMode = (mode) => {
    setSelectedGameMode(mode);
    setShowInviteModal(false);
    setShowWagerModal(true);
  };

  // Removed duplicate handleSetWager function - using the comprehensive one below

  const handleInvitePlayerFromStream = async () => {
    // Optimistic-UI: flip spinner ON immediately so the send button
    // reflects the intent right away even during Go Live + invite fan-out.
    setIsSendingInvites(true);
    // Get selected users or manual GEM ID
    let usersToInvite = [];
    
    if (selectedUserIds.length > 0) {
      // Use checkbox selections
      usersToInvite = onlineUsers.filter(u => selectedUserIds.includes(u.id));
    } else if (inviteGemId.trim()) {
      // Use manual GEM ID entry
      const user = onlineUsers.find(u => u.handle === inviteGemId.trim());
      if (user) {
        usersToInvite = [user];
      } else {
        toast.error('User not found');
        setIsSendingInvites(false);
        return;
      }
    } else {
      toast.error('Please select players to invite');
      setIsSendingInvites(false);
      return;
    }

    try {
      // Close modal first
      setShowInviteModal(false);
      
      // STEP 1: Force inviter to go live FIRST (skip preview entirely)
      if (!currentStream || isPreviewMode) {
        toast.info('Going live...', { duration: 2000 });
        
        // If in preview mode, need to create stream and start
        if (isPreviewMode && currentStream) {
          // Already have stream, just start it
          await api.post(`/streams/${currentStream.stream_id}/start`);
          setIsPreviewMode(false);
        } else {
          // Need to create stream first and start immediately
          const streamResponse = await api.post('/streams/create', {
            title: streamTitle,
            description: '',
            portrait_mode: true,
            max_viewers: 100,
            live_type: liveType
          });
          
          const streamId = streamResponse.data.stream_id;
          setCurrentStream(streamResponse.data);
          
          // Start streaming immediately
          await api.post(`/streams/${streamId}/start`);
          setIsPreviewMode(false);
          
          // Start camera
          await startPreview();
        }
        
        toast.success('You\'re now live! 🔴');
        // Wait for stream to stabilize
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
      
      // STEP 2: Now that we're live, send invitations
      const streamIdToUse = currentStream?.stream_id;
      if (!streamIdToUse) {
        toast.error('Stream not started. Please try again.');
        setIsSendingInvites(false);
        return;
      }
      
      // Send stream invitations
      const invitePromises = usersToInvite.map(userToInvite => 
        api.post('/streams/invite-player', {
          streamId: streamIdToUse,
          inviterGemId: user?.handle || user?.email?.split('@')[0],  // Use logged-in user's handle
          inviteeGemId: userToInvite.handle,
          streamTitle: streamTitle
        })
      );
      
      await Promise.all(invitePromises);
      
      toast.success(`Invited ${usersToInvite.length} player(s)!`);
      
      // Add to invited players list
      const newInvitedPlayers = [...invitedPlayers, ...usersToInvite];
      setInvitedPlayers(newInvitedPlayers);
      setInviteGemId('');
      setSelectedUserIds([]);

      // Regression H (May 8 testing): removed `setLookingToPlay(true);`
      // here. Sending a targeted invite is NOT matchmaking — that flag
      // is reserved for the explicit "Look for Game" button. Pre-fix this
      // line lit the yellow "Looking for opponents…" banner the moment a
      // host sent a single cohost invite.
      
      // ========================================================
      // INITIALIZE AGORA WITH SHARED TRACKS (Prevents blinking)
      // Create tracks ONCE here and share them across all video views
      // ========================================================
      if (LIVEKIT_LINKS && newInvitedPlayers.length > 0) {
        // Link v2: publish into the shared LiveKit linked room instead of
        // creating Agora shared tracks. Idempotent — no-op if we're already
        // in this same linked room.
        try {
          const roomName = livekitLinkRoomName(currentStream || {});
          await initLiveKitLinkRoom(roomName, { sessionType: 'linked_broadcaster' });
        } catch (e) {
          console.error('[LiveKitLink] invite-phase init failed:', e);
        }
      } else if (newInvitedPlayers.length > 0 && !useAgora && !sharedAgoraTracks) {
        try {
          console.log('🎥 Initializing Agora with SHARED tracks for multi-stream...');
          
          // Import Agora SDK
          const AgoraRTC = (await import('agora-rtc-sdk-ng')).default;
          
          // Get token
          const agoraResponse = await api.post('/agora/token', {
            channelName: `stream_${currentStream?.stream_id || Date.now()}`,
            uid: uidFromUserId(user?.id),
            role: 'publisher',
            sessionType: 'multiplayer_stream'
          });
          
          const { token, channelName, appId } = agoraResponse.data;
          console.log('✅ Agora token received:', { channelName, hasToken: !!token });

          // Checkpoint 4: unified factory.
          const client = await createAndJoinAgoraChannel({
            AgoraRTC,
            channelName,
            token,
            appId,
            uid: uidFromUserId(user?.id),
          });

          // Create tracks ONCE - these will be shared
          console.log('📷 Creating shared camera/mic tracks...');
          const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
            { AEC: true, AGC: true, ANS: true, encoderConfig: 'speech_standard' },
            { encoderConfig: '720p_2', facingMode: 'user' }
          );
          
          // Publish tracks
          await client.publish([audioTrack, videoTrack]);
          console.log('✅ Published shared tracks to Agora');
          
          // Store shared tracks
          setSharedAgoraTracks({ audioTrack, videoTrack });
          setAgoraToken(token);
          setAgoraChannel(channelName);
          setAgoraAppId(appId);
          setUseAgora(true);
          
          // NOW it's safe to stop WebRTC
          if (webrtc && webrtc.localStream) {
            webrtc.localStream.getTracks().forEach(track => track.stop());
            console.log('🛑 WebRTC stopped - Agora has camera now');
          }
          
          console.log('✅ Agora ready with shared tracks - no more blinking!');
          
        } catch (agoraError) {
          console.error('❌ Failed to initialize Agora:', agoraError);
          toast.error('Video streaming unavailable');
        }
      }
      
    } catch (error) {
      console.error('Invite error:', error);
      toast.error(error.response?.data?.detail || 'Failed to invite players');
    } finally {
      setIsSendingInvites(false);
    }
  };

  const handleQuickMatchFromStream = async () => {
    // Close modal first
    setShowGameOptionsModal(false);
    
    try {
      // Validate emeralds
      if (wagerAmount > (user?.emeralds || 0)) {
        toast.error('Insufficient Emeralds. Please reduce match entry amount or recharge your balance.');
        return;
      }
      
      // Phase 1: No minimum wager requirement
      
      // Join matchmaking queue with wager
      setIsSearchingForMatch(true);
      const response = await api.post('/matchmaking/join', {
        wagerAmount: wagerAmount
      });
      
      toast.success(`Searching for players with ${wagerAmount} Emerald entry...`);
      
      // Poll for match found
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await api.get('/matchmaking/status');
          setMatchmakingStatus(statusResponse.data);
          
          if (statusResponse.data.status === 'matched') {
            clearInterval(pollInterval);
            setIsSearchingForMatch(false);
            toast.success('Match found! Starting game...');
            
            // Navigate to game room with proper Agora integration
            navigate(`/game/${statusResponse.data.game_id}`);
          }
        } catch (error) {
          console.error('Matchmaking status error:', error);
          clearInterval(pollInterval);
          setIsSearchingForMatch(false);
        }
      }, 2000);
      
      // Stop searching after 45 seconds
      setTimeout(() => {
        clearInterval(pollInterval);
        if (isSearchingForMatch) {
          setIsSearchingForMatch(false);
          toast.error('No match found. Please try again.');
        }
      }, 45000);
      
    } catch (error) {
      console.error('Quick match error:', error);
      setIsSearchingForMatch(false);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Insufficient')) {
        toast.error('Insufficient Emeralds. Minimum 50 Emeralds required.');
      } else {
        toast.error('Failed to start quick match');
      }
    }
  };

  const handleSendGift = async () => {
    if (!selectedGift || !currentStream) {
      toast.error('Please select a gift');
      return;
    }

    if ((user?.emeralds || 0) < selectedGift.price) {
      toast.error('Insufficient emeralds');
      return;
    }

    try {
      await api.post('/gifts/send', {
        recipient_stream_id: currentStream.stream_id,
        gift_type: selectedGift.name,
        amount_emeralds: selectedGift.price
      });

      toast.success(`${selectedGift.icon} ${selectedGift.name} sent!`);
      setShowGiftModal(false);
      setSelectedGift(null);

      // Update user's emerald count (would normally come from backend)
      // In a real app, refetch user data or update from API response
    } catch (error) {
      toast.error('Failed to send gift');
    }
  };

  // Game Functions
  const playCard = (card) => {
    if (currentTurn !== 0) return; // Not your turn
    
    setPlayedCards(prev => [...prev, { ...card, player: 0 }]);
    setMyCards(prev => prev.filter(c => c.suit !== card.suit || c.value !== card.value));
    setCurrentTurn(1); // Move to next player (bot)
    
    // Simulate bots playing after a delay
    setTimeout(() => simulateBotPlay(), 1500);
  };

  const simulateBotPlay = () => {
    const botCards = [
      { suit: '♥', value: '10', color: 'red' },
      { suit: '♦', value: '9', color: 'red' },
      { suit: '♣', value: '8', color: 'black' }
    ];
    
    if (currentTurn < 4) {
      const randomCard = botCards[Math.floor(Math.random() * botCards.length)];
      setPlayedCards(prev => [...prev, { ...randomCard, player: currentTurn }]);
      
      if (currentTurn === 3) {
        // All cards played, clear after 2 seconds
        setTimeout(() => {
          setPlayedCards([]);
          setCurrentTurn(0);
        }, 2000);
      } else {
        setCurrentTurn(prev => prev + 1);
        setTimeout(() => simulateBotPlay(), 1500);
      }
    }
  };

  const handleBid = (bidAmount) => {
    setMyBid(bidAmount);
    setBids(prev => {
      const newBids = [...prev];
      newBids[0] = bidAmount;
      return newBids;
    });
    // Move to playing phase after bidding
    setTimeout(() => setGamePhase('playing'), 1000);
  };

  const handleSetWagerFromLiveStream = async () => {
    try {
      // Part A1 (Feb 2026): normalize the in-live invite variants into a
      // single mode. The wager-modal chooser flips `selectedGameMode` to
      // 'invitePlayer', while the direct in-live picker sets 'invite' —
      // both must run the same /game/create → set-wager → /streams/{id}/game
      // → /game/{id}/start → /sync/broadcast pipeline.
      const inviteMode = selectedGameMode === 'invite' || selectedGameMode === 'invitePlayer';
      // SEP 3 2026 FIX (accepter-host bug): this branch selector used
      // invitedPlayers, which is populated ONLY on the link INVITER's
      // client. When a link ACCEPTER hosted a challenge, the in-live
      // condition was false, so the flow FELL THROUGH into the games-page
      // practice branch below — /game/create(practiceMode)+/game/add-bots —
      // and no broadcaster ever received the invite while the host sat on
      // a "waiting for cards" board. Route on the LIVE linked roster
      // (linkPartners — identical on every member, self-healed from the
      // stream doc every 15s), falling back to invitedPlayers for the
      // inviter's pre-link edge cases.
      const liveRoster = (linkPartners || []).length ? linkPartners : (invitedPlayers || []);
      // Multiplayer in-stream launch. Spades needs exactly 4 players (3
      // invitees); Crazy Eights is 2–4 players (1–3 invitees). Player count
      // is game-type-aware; everything else in this branch is generic.
      if (inviteMode && (
            (selectedGameType || 'spades') === 'crazy_eights'
              ? (liveRoster.length >= 1 && liveRoster.length <= 3)
              : (liveRoster.length === 3)
          )) {
        // gameType flows from the in-stream picker (defaults to 'spades' when
        // launched via legacy paths that don't set selectedGameType).
        const gameTypeToStart = selectedGameType || 'spades';
        const gameResponse = await api.post('/game/create', {
          gameType: gameTypeToStart,
          isPrivate: true,
          streamId: currentStream?.stream_id
        });
        
        const gameId = gameResponse.data.gameId;
        
        // Set wager and settings
        await api.post('/game/set-entry', {
          gameId: gameId,
          entryAmount: wagerAmount,
          allowBlindSix: allowBlindSix,
          allowAceCheck: allowAceCheck,
          targetScore: spadesTargetScore
        });
        
        // Update the stream with the game_id so invited players can find it
        try {
          await api.put(`/streams/${currentStream?.stream_id}/game`, {
            game_id: gameId
          });
          console.log('✅ Stream updated with game_id:', gameId);
        } catch (e) {
          console.log('Note: Stream game_id update failed (non-critical):', e);
        }
        
        // Connect WebSocket FIRST and WAIT for it to be fully connected
        console.log('🔌 Attempting WebSocket connection...');
        console.log('📋 Game ID:', gameId);
        console.log('👤 User ID:', user?.id);
        
        if (!user?.id) {
          console.error('❌ No user ID available!');
          toast.error('User not logged in. Please refresh and try again.');
          return;
        }
        
        try {
          await connectGameWebSocket(gameId, user.id);
          console.log('✅ WebSocket connection confirmed');
        } catch (error) {
          console.error('❌ WebSocket connection failed:', error);
          toast.error('Failed to connect to game server. Please try again.');
          setShowWagerModal(true);
          return;
        }
        
        // DON'T add other players - they will join themselves when they accept the invite
        // Instead, broadcast game_started to all linked users via sync WebSocket
        // so they can connect their game WebSockets
        
        // Part 3c (Feb 19, 2026): Spades in-live must WAIT for all 3
        // invitees to accept before /start dispatches the deal. C8 keeps
        // its old immediate-start behavior — its accept model is different.
        const isSpadesStart = gameTypeToStart === 'spades';
        if (!isSpadesStart) {
          try {
            await api.post(`/game/${gameId}/start`);
            console.log('✅ Game started on backend');
          } catch (err) {
            console.log('Game start signal sent (some routes may not exist yet):', err.message);
          }
        } else {
          console.log('⏳ Spades /start deferred until all invitees accept');
        }
        
        // Part 2 (Feb 19, 2026 SPADES FIX): DELETED the premature
        // game_started sync broadcast for SPADES — last night it made
        // the 3 invitees half-initialize a game that never started. For
        // spades the flow is create → set-wager → invite-broadcasters →
        // accepts → the Part-3d effect fires /game/{id}/start + the
        // post-start broadcast. C8's immediate-start broadcast stays.
        if (!isSpadesStart) {
          try {
            const linkedUserIds = liveRoster.map(p => p.id);
            await api.post('/sync/broadcast', {
              event_type: 'game_started',
              data: {
                game_id: gameId,
                started_by: user?.id,
                game_type: gameTypeToStart,
                wager_amount: wagerAmount
              },
              target_users: linkedUserIds
            });
            console.log('📡 Game started broadcast sent to:', linkedUserIds);
          } catch (e) {
            console.log('Sync broadcast sent (fallback):', e.message);
          }
        }
        
        console.log('👥 Invited players:', liveRoster.map(p => p.name));
        // Spades host toast — "Challenge sent — waiting for players to accept"
        // is set below on successful invite (skip the "dealing cards" line
        // that only applies to immediate-start games).
        if (!isSpadesStart) {
          toast.info(`Game starting! Dealing cards...`, { duration: 3000 });
        }
        
        setShowWagerModal(false);
        setInStreamGame(gameId);

        // SPADES INVITE FLOW (May 16, 2026): send per-broadcaster invites.
        // Part 1b: if the invite fails, roll the host back — cancel the
        // pending game + clear in-live state + reopen the entry modal.
        if (isSpadesStart) {
          const inviteResult = await sendSpadesInviteToLinkedBroadcasters(gameId);
          if (!inviteResult || inviteResult.error || (inviteResult.invitee_ids || []).length === 0) {
            console.warn('[Spades] invite send failed — rolling back host state');
            try { await cancelPendingSpadesGame(); } catch {}
            setInStreamGame(null);
            setGameState(null);
            setMyCards([]);
            setShowWagerModal(true);
            return;
          }
          toast.success('Challenge sent — waiting for players to accept');
        }

        console.log('🎮 Game created:', { gameId, invitedPlayersCount: liveRoster.length });
        
        // ========================================================
        // AGORA: Should already be initialized from invite phase
        // No need to re-initialize - just verify it's running
        // ========================================================
        if ((!useAgora || !agoraToken) && !(LIVEKIT_LINKS && linkRoomInfo)) {
          console.log('⚠️ Agora not initialized yet - this should have happened on invite');
          // If for some reason Agora wasn't initialized, do it now with same channel as stream
          try {
            const channelName = `stream_${currentStream?.stream_id || Date.now()}`;
            console.log('🎥 Late-initializing Agora with channel:', channelName);
            const agoraResponse = await api.post('/agora/token', {
              channelName: channelName,
              uid: uidFromUserId(user?.id),
              role: 'publisher',
              sessionType: 'multiplayer_game'
            });
            
            setAgoraToken(agoraResponse.data.token);
            setAgoraChannel(agoraResponse.data.channelName);
            setAgoraAppId(agoraResponse.data.appId);
            setUseAgora(true);
          } catch (agoraError) {
            console.error('❌ Failed to initialize Agora:', agoraError);
          }
        } else {
          console.log('✅ Agora already running on channel:', agoraChannel);
        }
        
        // Initialize game state (will be updated by WebSocket)
        // Set a placeholder to show the UI immediately
        setGameState({
          phase: 'waiting',
          gameState: 'WAITING',
          team1Score: 0,
          team2Score: 0,
          currentBid: null,
          currentHand: 1,
          booksWon: [0, 0],
          matchScores: [0, 0],
          teamBids: [0, 0],
          teamSetCount: [0, 0],
          totalBags: [0, 0],
          teamRemainingBlinds: [3, 3],
          aceCheckDeclared: [false, false],
          matchSettings: {
            allowBlindSix: allowBlindSix,
            allowAceCheck: allowAceCheck
          }
        });
        
        // Fallback: Fetch game state via API after 2 seconds if WebSocket didn't update
        // This handles cases where WebSocket message wasn't received
        setTimeout(async () => {
          try {
            const gameResponse = await api.get(`/game/${gameId}`);
            const gameData = gameResponse.data;
            
            console.log('🔄 Fallback: Fetched game state via API:', gameData);
            
            // Find my position and cards
            const myPlayerData = gameData.players?.find(p => p.id === user?.id);
            const myPosition = gameData.players?.findIndex(p => p.id === user?.id) || 0;
            
            if (myPlayerData && myPlayerData.cards && myPlayerData.cards.length > 0) {
              console.log('🎴 Found my cards via API:', myPlayerData.cards.length);
              const sortedCards = sortCards(myPlayerData.cards);
              setMyCards(sortedCards);
              
              // Update game state with proper phase
              // Host (position 0) always goes first on hand 1
              setGameState(prev => ({
                ...prev,
                phase: gameData.phase || 'playing',
                gameState: gameData.gameState || 'PLAYING',
                currentHand: gameData.currentHand || 1,
                handNumber: gameData.currentHand || 1,
                currentPlayer: gameData.currentPlayer ?? 0,  // Default to 0 (host goes first)
                myPosition: myPosition,
                players: gameData.players || [],
                noBidding: gameData.noBidding || gameData.currentHand === 1
              }));
              
              toast.success('Cards dealt! Your turn to play!');
            }
          } catch (error) {
            console.log('❌ Fallback API fetch failed:', error);
          }
        }, 2000);
        
        return;
      }
      
      // Enforce minimum 50 Emerald wager
      if (wagerAmount < 50) {
        toast.error('Minimum entry is 50 Emeralds');
        return;
      }
      
      if (selectedGameMode === 'quickMatch') {
        if (wagerAmount > (user?.emeralds || 0)) {
          toast.error('Insufficient Emeralds. Please reduce match entry or recharge.');
          return;
        }
        
        setShowWagerModal(false);
        handleQuickMatchFromStream();
        return;
      }
      
      if (selectedGameMode === 'invitePlayer') {
        if (wagerAmount > (user?.emeralds || 0)) {
          toast.error('Insufficient Emeralds for this match entry.');
          return;
        }
        
        // Create invite game
        const inviteGameResponse = await api.post('/game/create', {
          gameType: 'spades',
          isPrivate: true,
          streamId: currentStream?.stream_id
        });
        
        const gameId = inviteGameResponse.data.gameId;
        setInviteGameId(gameId);
        
        // Set wager and match settings
        await api.post('/game/set-entry', {
          gameId: gameId,
          entryAmount: wagerAmount,
          allowBlindSix: allowBlindSix,
          allowAceCheck: allowAceCheck,
          targetScore: spadesTargetScore
        });
        
        setShowWagerModal(false);
        setShowInviteModal(true);
        toast.success('Game created! Now invite players by GEM ID');
        return;
      }
      
      // Part 3a (Feb 19, 2026): DELETED the "2 broadcasters + 2 bots" in-
      // live branch. In-live spades is EXACTLY 4 linked human broadcasters
      // — the TABLE STEP modal enforces that seat count before this
      // submit ever runs. If we still fall through with < 3 invitees at
      // this point, it's a state-desync bug; short-circuit with a clear
      // message instead of silently adding bots.
      if ((selectedGameType || 'spades') === 'spades' &&
          (selectedGameMode === 'invite' || selectedGameMode === 'invitePlayer') &&
          // SEP 3 2026: liveRoster (computed at the top of this function)
          // is the linked-session truth — identical on inviter and
          // accepter clients.
          liveRoster.length !== 3) {
        toast.error('Spades needs 4 linked broadcasters. Invite the rest, then try again.');
        return;
      }
      
      // Games-page practice with 3 bots (kept — this path never runs in-live).
      if ((user?.emeralds || 0) < 50) {
        toast.error('You need at least 50 Emeralds for Spades.');
        return;
      }
      
      const gameResponse = await api.post('/game/create', { 
        gameType: 'spades', 
        isPrivate: true,
        practiceMode: true,
        streamId: currentStream?.stream_id
      });
      
      const gameId = gameResponse.data.gameId;
      
      await api.post('/game/set-entry', {
        gameId: gameId,
        entryAmount: wagerAmount,
        allowBlindSix: allowBlindSix,
        allowAceCheck: allowAceCheck,
        targetScore: spadesTargetScore
      });
      
      await api.post(`/game/add-bots/${gameId}`, { 
        bot_count: 3 
      });
      
      setShowWagerModal(false);
      toast.success('Practice game starting in livestream!');
      setInStreamGame(gameId);
      
      // Connect WebSocket
      connectGameWebSocket(gameId, user?.id);
      
      setGameState({ phase: 'waiting', team1Score: 0, team2Score: 0 });
      
    } catch (error) {
      console.error('Game creation error:', error);
      toast.error('Failed to create game');
    }
  };

  const endGame = () => {
    setIsInGame(false);
    setCurrentGameId(null);
    setMyCards([]);
    setPlayedCards([]);
    setCurrentTurn(0);
    setGamePhase('bidding');
    setBids([0, 0, 0, 0]);
    setMyBid(0);
    setGamePlayers([]);
    toast.success('Game ended - stream continues!');
  };

  // ========================================================
  // BROWSER NAVIGATION PROTECTION
  // Prevent accidental page leave while live streaming
  // ========================================================
  useEffect(() => {
    const isLive = currentStream && !isPreviewMode;
    
    if (!isLive) return;
    
    // Prevent page refresh/close
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'You are currently live! Are you sure you want to leave?';
      return e.returnValue;
    };
    
    // Prevent browser back/forward
    const handlePopState = (e) => {
      // Push a new state to prevent navigation
      window.history.pushState(null, '', window.location.href);
      
      // Show confirmation
      const confirmLeave = window.confirm(
        'You are currently LIVE! Leaving will end your stream. Are you sure you want to leave?'
      );
      
      if (confirmLeave) {
        // User confirmed - end stream and allow navigation
        if (livekitPublisherRef.current) {
          livekitPublisherRef.current.disconnect();
        }
        window.history.back();
      }
    };
    
    // Push initial state
    window.history.pushState(null, '', window.location.href);
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentStream, isPreviewMode]);

  // Auto-hide header when game starts
  useEffect(() => {
    if (inStreamGame) {
      setShowHeader(false);
    } else {
      setShowHeader(true);
    }
  }, [inStreamGame]);

  // ========================================================
  // HELPER: UID-keyed remote tile resolution
  // ----------------------------------------------------------
  // PROBLEM we're solving: connectedCoHosts/invitedPlayers are arrays.
  // When someone leaves and the array compresses, every higher index shifts
  // down — names re-bind to the wrong video tile.
  //
  // FIX: build a UID → identity lookup map, then derive the rendered tile list
  // from `remoteAgoraUsers` (which is keyed by Agora UID and is the source of
  // truth for who is actually publishing). Sorting by numeric UID gives a
  // deterministic on-screen position that doesn't reshuffle when a user leaves.
  // ========================================================
  const cohostsByUid = React.useMemo(() => {
    const map = {};
    // Source of truth: invitedPlayers + currently-connected co-hosts.
    // Each user gets a deterministic UID from their user.id.
    for (const p of invitedPlayers) {
      if (!p?.id) continue;
      const uidStr = String(uidFromUserId(p.id));
      map[uidStr] = {
        id: p.id,
        name: p.name,
        handle: p.handle || '',
        picture: p.picture || '',
        agoraUid: uidStr,
      };
    }
    // Fill in any remaining co-hosts already known locally (defensive — covers
    // cases where someone joined without going through the invite flow first).
    for (const c of connectedCoHosts) {
      const uidStr = c?.agoraUid != null ? String(c.agoraUid) : (c?.id ? String(uidFromUserId(c.id)) : null);
      if (uidStr && !map[uidStr] && (c.name || c.id)) {
        map[uidStr] = c;
      }
    }
    return map;
  }, [invitedPlayers, connectedCoHosts]);

  // ============================================================
  // MESH-REFACTOR (May 16, 2026): SINGLE SOURCE OF TRUTH for tiles.
  // ============================================================
  // Pre-refactor: when a UID hadn't been mapped to cohostsByUid yet, we
  // fell back to "the next unmapped invited player" via a round-robin
  // cursor. Symptom: with 3 broadcasters where C's identity bound first,
  // B's tile would borrow "GEMS Admin" too — producing the doubled-name
  // regression. Fix: identity resolution is now STRICTLY uid → cohostsByUid.
  // If a UID can't be resolved yet, we show a neutral "Connecting…"
  // placeholder. The tile NEVER borrows another invitee's name.
  // ============================================================
  const remoteTiles = React.useMemo(() => {
    const entries = Object.entries(remoteAgoraUsers)
      .sort(([a], [b]) => Number(a) - Number(b));
    // A tile that is already streaming video is NOT "connecting". For an
    // UNAMBIGUOUS single-remote link the partner's real name is safe to use;
    // with several remotes we never borrow another invitee's name (that was
    // the doubled-name regression), so we fall back to a neutral "Live".
    const soleFallbackName = entries.length === 1
      ? (linkedGameOpponent?.name || connectedCoHosts[0]?.name || invitedPlayers[0]?.name || null)
      : null;
    return entries.map(([uid, agoraUser]) => {
      const identity = cohostsByUid[String(uid)] || null;
      const hasVideo = !!agoraUser?.videoTrack;
      // Only show "Connecting…" when there is genuinely no video yet. Once the
      // track is live, show the resolved name → sole-partner name → "Live",
      // never a stuck "Connecting…".
      const fallbackName = hasVideo ? (soleFallbackName || 'Live') : 'Connecting…';
      return {
        uid,
        videoTrack: agoraUser?.videoTrack || null,
        audioTrack: agoraUser?.audioTrack || null,
        identity: identity || { name: fallbackName, id: null, agoraUid: String(uid) },
      };
    });
  }, [remoteAgoraUsers, cohostsByUid, linkedGameOpponent, connectedCoHosts, invitedPlayers]);

  const getRemoteUserByPosition = (position) => remoteTiles[position] || null;
  const getRemoteUserIdentity = (position) => remoteTiles[position]?.identity || null;
  // Get total connected remote users
  const connectedRemoteCount = remoteTiles.length;

  // Determine if we're in multi-stream mode (4 players invited)
  const isMultiStreamMode = invitedPlayers.length > 0;
  const shouldHideNav = inStreamGame || isMultiStreamMode;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--gems-bg-0)' }}>
      {/* Live-stream handoff — Resume banner. Shows only when a fresh
          browser detects the caller has an active stream (device switch
          within the 30s broadcaster-disconnect grace). Resuming reclaims
          the SAME room so viewers get video back without game state reset. */}
      {mineActive && !currentStream && (
        <div
          data-testid="resume-live-banner"
          className="w-full px-4 sm:px-6 py-3 flex items-center gap-3 border-b"
          style={{
            background: 'linear-gradient(90deg, rgba(220,38,38,0.14) 0%, rgba(16,185,129,0.14) 100%)',
            borderColor: 'rgba(16,185,129,0.35)',
          }}
        >
          <span
            className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-extrabold tracking-wide"
            style={{ backgroundColor: '#DC2626', color: '#FFFFFF' }}
          >
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
            LIVE
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-sm sm:text-base font-bold" style={{ color: 'var(--gems-text-strong, #E7F2EC)' }}>
              You're still LIVE
            </div>
            <div className="text-xs sm:text-sm truncate" style={{ color: 'var(--gems-text-muted, #8AA69B)' }}>
              {mineActive.title || 'Your broadcast is still running — reconnect within the grace window to keep viewers.'}
            </div>
          </div>
          <button
            data-testid="resume-broadcast-btn"
            onClick={resumeLive}
            disabled={resumingLive}
            className="px-4 py-2 rounded-full font-extrabold text-sm shadow-lg transition-transform active:scale-95 disabled:opacity-60"
            style={{ backgroundColor: '#22C98C', color: '#062015' }}
          >
            {resumingLive ? 'Resuming…' : 'Resume broadcast'}
          </button>
        </div>
      )}

      {/* ========================================================
          LIVE LINK INVITE POPUP - Shows when another host invites you
          This uses WebSocket for instant delivery
          ======================================================== */}
      {showLiveInvitePopup && incomingLiveInvite && (
        <LiveInvitePopup
          isOpen={showLiveInvitePopup}
          invite={incomingLiveInvite}
          onAccept={async () => {
            console.log('✅ Accepting link invite:', incomingLiveInvite);
            console.log('📊 Current broadcast state:', { isActiveBroadcaster, currentStream: currentStream?.stream_id });
            
            // Close popup immediately for better UX
            setShowLiveInvitePopup(false);
            
            // Check if user is already broadcasting
            if (isActiveBroadcaster && currentStream?.stream_id) {
              // Already live - join linked session directly without navigation
              console.log('🔗 User is already live - joining linked session directly');
              await handleAcceptLiveInviteWhileLive(incomingLiveInvite);
            } else {
              // Not live - navigate to /live to start streaming and join
              console.log('📺 User is not live - navigating to /live to join');
              toast.info('Joining linked live session...', { duration: 2000 });
              navigate('/live', { state: { joinStreamId: incomingLiveInvite.stream_id } });
            }
            
            setIncomingLiveInvite(null);
          }}
          onDecline={async () => {
            console.log('❌ Declining link invite:', incomingLiveInvite);
            // Close popup immediately
            setShowLiveInvitePopup(false);
            setIncomingLiveInvite(null);
            
            // Only call decline API - no navigation, no connection, no blank state
            try {
              await api.post(`/notifications/live-invites/${incomingLiveInvite.id}/respond`, null, {
                params: { action: 'decline' }
              });
              console.log('✅ Decline response sent to server');
            } catch (e) {
              console.error('Failed to send decline response:', e);
            }
            toast.info('Invite declined');
            // No other state changes - user stays exactly where they are
          }}
          onClose={() => {
            // Close without any side effects
            setShowLiveInvitePopup(false);
            setIncomingLiveInvite(null);
          }}
        />
      )}
      
      {/* STREAM LINK INVITE STATUS TOAST - Shows status of invites I've sent */}
      {coHostInviteStatus && coHostInviteStatus !== 'accepted' && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[200] bg-gray-900/95 border border-gray-700 rounded-xl p-6 shadow-2xl min-w-[300px]" data-testid="cohost-status-popup">
          <div className="text-center">
            {coHostInviteStatus === 'sending' && (
              <>
                <Loader2 className="w-10 h-10 text-emerald-400 animate-spin mx-auto mb-3" />
                <p className="text-white font-medium">Sending invite...</p>
              </>
            )}
            {coHostInviteStatus === 'sent' && (
              <>
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-6 h-6 text-emerald-400" />
                </div>
                <p className="text-white font-medium">Invite sent!</p>
                <p className="text-gray-400 text-sm mt-1">Waiting for response...</p>
                {sentCoHostInvites[0] && (
                  <button
                    onClick={() => cancelCoHostInvite(sentCoHostInvites[0].id)}
                    className="mt-4 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors"
                  >
                    Cancel Invite
                  </button>
                )}
              </>
            )}
            {coHostInviteStatus === 'declined' && (
              <>
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-3">
                  <X className="w-6 h-6 text-red-400" />
                </div>
                <p className="text-white font-medium">User is busy</p>
                <p className="text-gray-400 text-sm mt-1">They're unavailable to connect right now</p>
                <button
                  onClick={() => setCoHostInviteStatus(null)}
                  className="mt-4 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors"
                >
                  OK
                </button>
              </>
            )}
            {coHostInviteStatus === 'expired' && (
              <>
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-3">
                  <AlertCircle className="w-6 h-6 text-amber-400" />
                </div>
                <p className="text-white font-medium">Invite timed out</p>
                <p className="text-gray-400 text-sm mt-1">No response after 30 seconds</p>
                <button
                  onClick={() => setCoHostInviteStatus(null)}
                  className="mt-4 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors"
                >
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Navigation Blocked Modal - Shown when broadcaster tries to navigate away */}
      {showNavigationBlockedModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[200]">
          <div className="bg-gray-900 border-2 border-red-500 rounded-xl p-6 max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">You're Live!</h2>
              <p className="text-gray-300 mb-4">
                You cannot navigate away while broadcasting. End your stream first to leave this page.
              </p>
              <p className="text-gray-500 text-sm mb-6">
                Trying to go to: <span className="text-emerald-400">{blockedNavigationPath}</span>
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowNavigationBlockedModal(false);
                    setBlockedNavigationPath(null);
                  }}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  Stay on Stream
                </button>
                <button
                  onClick={handleEndStreamAndNavigate}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  End Stream & Leave
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Hide NavBar during in-stream game OR multi-stream mode */}
      {(!shouldHideNav || showHeader) && <NavBar />}
      
      {/* Toggle button to show/hide header during game/multi-stream */}
      {shouldHideNav && !showHeader && (
        <button
          onClick={() => setShowHeader(true)}
          className="fixed top-4 right-4 z-[100] bg-emerald-600 hover:bg-emerald-700 text-white p-3 rounded-full shadow-lg transition-all"
          title="Show navigation"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}
      
      {shouldHideNav && showHeader && (
        <button
          onClick={() => setShowHeader(false)}
          className="fixed top-4 right-4 z-[100] bg-gray-800 hover:bg-gray-900 text-white p-3 rounded-full shadow-lg transition-all"
          title="Hide navigation for more space"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      
      <div className={`mx-auto px-4 ${inStreamGame ? 'max-w-full py-0' : 'max-w-7xl py-8'}`}>
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Main Stream Area */}
          <div className="lg:col-span-3">
            <Card className="shadow-xl border-0 overflow-hidden">
              {/* GREEN HEADER REMOVED - User requested removal of fake network rating */}
              
              <CardContent className="p-0">
                {(() => {
                  // Part 1b (Feb 19, 2026): shrink the video area on DESKTOP
                  // when a card game is active so the new game band + controls
                  // fit at 1280×800 with zero page scroll. Mobile is unchanged
                  // — it already uses the top-strip layout for card games.
                  const desktopCardGameActive = isDesktopViewport && !!inStreamGame &&
                    (!!c8LiveState || !!gameState);
                  const videoH = inStreamGame || invitedPlayers.length > 0
                    ? (desktopCardGameActive ? 'h-[44vh]' : 'h-[60vh]')
                    : 'aspect-video';
                  return (
                <div className={`relative bg-black ${videoH}`}>
                  {/* ============================================================
                      LINK v2 — STABLE MOUNT for LiveKitLinkGrid.
                      TOP-LEVEL child of the broadcaster video card, OUTSIDE
                      every conditional (game-active / multi-broadcaster /
                      useAgora / isPreviewMode / useLiveKit). As long as
                      `linkRoomInfo` is set, this element sits at ONE stable
                      position in the tree and its LiveKit Room instance
                      survives EVERY state transition — no re-fetch, no
                      remount. Only the `layout` prop varies ("strip" while
                      a game is active, "auto" otherwise).

                      Visibility log: LiveKitLinkGrid prints
                        [LinkGrid] MOUNTED room=<name>
                        [LinkGrid] UNMOUNTED room=<name>
                      A healthy session shows exactly ONE MOUNTED and zero
                      UNMOUNTED between invite → linked → in-game → back.
                      DO NOT nest this inside the "Stream Preview Area" or
                      the "multi-broadcaster split" — that reintroduces the
                      regression where the mount never reaches on the
                      accepter side (Feb 2026 prod).
                      ============================================================ */}
                  {LIVEKIT_LINKS && linkRoomInfo && (() => {
                    // In-live game classification (Aug 18, 2026):
                    //   card game — spades (`gameState`) or crazy eights
                    //     (`c8LiveState`): grid stays FULL-BLEED; hand/stats
                    //     overlays render on top of the video.
                    //   board game — 4iaR (`activeLinkedGame` w/ 4iaR/other
                    //     board type) or checkers (`checkersLiveState`):
                    //     video sits on the sides (desktop) or strip on top
                    //     (mobile) and the board takes the freed area.
                    const cardGameActive = !!inStreamGame && (!!gameState || !!c8LiveState);
                    // Only switch to sides/strip layout while a BOARD is
                    // actually rendering — pending / waiting / post-game
                    // stays on the full-bleed 'auto' grid so the desktop
                    // doesn't show edge-pinned tiles around an empty middle.
                    const boardGameActive = !cardGameActive && (
                      !!checkersLiveState ||
                      (activeLinkedGame && activeLinkedGame.phase === 'playing' &&
                       activeLinkedGame.gameType !== 'checkers' &&
                       activeLinkedGame.gameType !== 'crazy_eights')
                    );
                    const gameActive = cardGameActive || boardGameActive;
                    // 4iaR renders its own scoreboard at absolute top-0
                    // (~44px). During that game push the strip/sides below
                    // it so the "X wins / Y wins" header stays visible.
                    const has4iaRHeader = activeLinkedGame && activeLinkedGame.phase !== 'waiting_accept'
                      && activeLinkedGame.gameType !== 'checkers'
                      && activeLinkedGame.gameType !== 'crazy_eights';
                    const useSides = boardGameActive && isDesktopViewport;
                    // Part 2 (Feb 19, 2026): on MOBILE, a card game must
                    // also switch the cameras into the top strip so the
                    // fanned hand renders UNDER the video instead of over
                    // the broadcasters' faces. Desktop keeps the full-
                    // bleed marketing overlay (2×2 grid + bottom-overlay
                    // hand).
                    const cardGameStripMobile = cardGameActive && !isDesktopViewport;
                    // Part 1b (Feb 19, 2026): during in-live gameplay the
                    // camera tiles don't need clicks; the game overlay owns
                    // interaction. Turning the grid wrapper into
                    // pointer-events-none lets the z-30 board underneath
                    // receive clicks (fixes desktop C8 where the full-bleed
                    // wrapper ate every click).
                    const pointerCls = gameActive ? ' pointer-events-none' : '';
                    const wrapperCls = ((cardGameActive && isDesktopViewport)
                      // Desktop card game: grid starts BELOW the score
                      // header (~76px) so the scoreboard never covers a
                      // broadcaster; the hand lives in the band below.
                      ? 'absolute top-[76px] bottom-0 left-0 right-0 z-10'
                      : !gameActive
                      ? 'absolute inset-0 z-10'
                      : useSides
                        ? (has4iaRHeader ? 'absolute top-[48px] bottom-0 left-0 right-0 z-10'
                                         : 'absolute inset-0 z-10')
                        : (has4iaRHeader ? 'absolute top-[48px] left-0 right-0 h-[28vh] z-20'
                                         : 'absolute top-0 left-0 right-0 h-[28vh] z-20')) + pointerCls;
                    return (
                      <div className={wrapperCls} data-testid={boardGameActive ? (useSides ? 'livekit-link-sides-mount' : 'livekit-link-strip') : (cardGameStripMobile ? 'livekit-link-strip-card' : 'livekit-link-grid-full')}>
                        <LiveKitLinkGrid
                          token={linkRoomInfo.token}
                          serverUrl={linkRoomInfo.url || LIVEKIT_URL}
                          roomName={linkRoomInfo.roomName}
                          selfInfo={{ user_id: user?.id, name: user?.name || 'You', picture: user?.picture }}
                          partners={linkPartners}
                          micMuted={isMicMuted}
                          camOff={false}
                          layout={boardGameActive ? (useSides ? 'sides' : 'strip') : (cardGameStripMobile ? 'strip' : 'auto')}
                          onRoomReady={(room) => { linkRoomRef.current = room; }}
                          onParticipantLost={(identity) => {
                            // TRUE departure detector (Feb 2026): a partner's
                            // video actually left the room (network death,
                            // crash, tab close). Debounce 12s — reconnect
                            // flaps re-add them — then report; the backend
                            // runs the same departure handling as an explicit
                            // End Link (survivors stay linked, game grace
                            // arms, leaver gets an auto-rejoin hint).
                            if (!identity || identity === user?.id) return;
                            setTimeout(() => {
                              try {
                                const stillGone = !linkRoomRef.current?.remoteParticipants?.has?.(identity);
                                if (stillGone) {
                                  console.log('[link-drop] reporting departed broadcaster:', identity);
                                  api.post('/streams/link/report-drop', { user_id: identity }).catch(() => {});
                                }
                              } catch { /* noop */ }
                            }, 12000);
                          }}
                          onError={(e) => console.error('[LiveKitLink] stable grid error', e, { serverUrl: linkRoomInfo.url || LIVEKIT_URL, roomName: linkRoomInfo.roomName })}
                          onDisconnected={(reason) => {
                            linkRoomRef.current = null;
                            console.warn('[LiveKitLink] grid disconnected:', reason);
                            const rn = linkRoomInfoRef.current?.roomName;
                            if (rn && String(reason || '').toLowerCase() !== 'client_initiated') {
                              toast.info('Link dropped — reconnecting…');
                              linkRoomInfoRef.current = null;
                              setTimeout(() => { initLiveKitLinkRoom(rn, { sessionType: 'linked_broadcaster' }).catch((e) => console.error('[LiveKitLink] re-init failed', e)); }, 1500);
                            }
                          }}
                        />
                        {/* End Link — only visible while no game is running.
                            During a game the hint tells you to forfeit first. */}
                        {!gameActive && (linkPartners || []).length > 0 && (
                          <button
                            data-testid="end-link-btn-v2"
                            onClick={() => {
                              const targetId = invitedPlayers[0]?.id || connectedCoHosts[0]?.id
                                || (allLinkedUsers || []).find((u) => u !== user?.id);
                              if (targetId) disconnectCoHost(targetId);
                            }}
                            className="absolute bottom-3 left-3 z-30 bg-red-600/80 hover:bg-red-600 px-3 py-1.5 rounded-lg text-white text-xs font-semibold pointer-events-auto flex items-center gap-1.5"
                          >
                            <X className="w-3 h-3" /> End Link
                          </button>
                        )}
                        {/* Part 3 (Feb 19, 2026): removed the "Forfeit the
                            game to end the link" hint chip. It rendered
                            over the broadcaster's name pill and confused
                            people. The visible Forfeit button inside the
                            card-game band replaces it. */}
                      </div>
                    );
                  })()}

                  {/* Stream Preview Area - BIGO Live Style */}
                  <div className={`relative bg-gray-900 rounded-lg overflow-hidden shadow-2xl ${inStreamGame || invitedPlayers.length > 0 ? 'h-full' : 'aspect-video'}`}>

                    {/* Old stable-mount lived here — moved UP one level to
                        the broadcaster video card top so it survives every
                        subtree conditional. Do NOT re-add a
                        <LiveKitLinkGrid> here or Room instances conflict. */}

                    {/* Main Video Stream - HIDE when in-stream game is active */}
                    {!inStreamGame && (() => {
                      // Determine which streaming service to use:
                      // - Agora: Only for multiplayer games (4-player Spades)
                      // - LiveKit: For solo streams
                      // - WebRTC: Preview only (before going live)
                      // NOTE: `!linkRoomInfo` is a HARD guard — if a LiveKit
                      // linked room is active, the LiveKitLinkGrid renders
                      // (see block below), so the solo LiveKitStreamPublisher
                      // must NOT mount in parallel or it disconnects the grid.
                      const shouldUseAgora = !isPreviewMode && useAgora && sharedAgoraTracks?.videoTrack;
                      const shouldUseLiveKit = !isPreviewMode && useLiveKit && livekitToken && livekitRoomName && !linkRoomInfo;
                      
                      // AGORA - For multiplayer games (using SHARED tracks - no blinking!)
                      if (shouldUseAgora) {
                        return (
                          <AgoraVideoPlayer
                            videoTrack={sharedAgoraTracks.videoTrack}
                            audioTrack={sharedAgoraTracks.audioTrack}
                          />
                        );
                      }
                      
                      // LIVEKIT - For solo streams
                      if (shouldUseLiveKit) {
                        return (
                          <LiveKitStreamPublisher
                            ref={livekitPublisherRef}
                            token={livekitToken}
                            serverUrl={livekitUrl || LIVEKIT_URL}
                            roomName={livekitRoomName}
                            onPublishSuccess={handleSoloPublishSuccess}
                            onPublishError={handleSoloPublishError}
                            onDisconnected={() => {
                              console.log('👋 LiveKit disconnected');
                              setUseLiveKit(false);
                              setIsScreenSharing(false);
                            }}
                            onScreenShareChange={(isSharing) => {
                              console.log('🖥️ Screen share state:', isSharing);
                              setIsScreenSharing(isSharing);
                            }}
                          />
                        );
                      }
                      
                      // WEBRTC - Preview only (before going live)
                      return (
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-full h-full object-cover"
                          style={{ transform: 'scaleX(-1)' }}
                        />
                      );
                    })()}
                        
                        {/* Top Overlay Bar - Hide when in multi-stream or game mode */}
                        {invitedPlayers.length === 0 && !inStreamGame && (
                          <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent p-4 z-10">
                            <div className="flex items-center justify-between">
                              {/* Left: Profile Info */}
                              <div className="flex items-center gap-3">
                                {(() => {
                                  const coverSrc = coverUrl || user?.picture;
                                  const clickable = isPreviewMode;
                                  return (
                                    <button
                                      type="button"
                                      onClick={clickable ? () => coverInputRef.current?.click() : undefined}
                                      disabled={!clickable || coverUploading}
                                      className={`relative w-10 h-10 rounded-lg overflow-hidden bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center text-white font-bold shrink-0 ${clickable ? 'cursor-pointer ring-2 ring-transparent hover:ring-emerald-400 transition-all' : ''}`}
                                      data-testid="stream-cover-avatar"
                                      title={clickable ? 'Set cover photo' : undefined}
                                    >
                                      {coverUploading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : coverSrc ? (
                                        <img src={coverSrc} alt="cover" className="absolute inset-0 w-full h-full object-cover" />
                                      ) : (
                                        <span>{user?.name?.charAt(0)}</span>
                                      )}
                                      {clickable && !coverUploading && (
                                        <span className="absolute bottom-0 right-0 bg-black/70 rounded-tl-md p-0.5">
                                          <Camera className="h-3 w-3 text-white" />
                                        </span>
                                      )}
                                    </button>
                                  );
                                })()}
                                <div>
                                  <p className="text-white font-bold text-sm">{user?.name}</p>
                                  <p className="text-white/80 text-xs">@{user?.handle}</p>
                                </div>
                                {!isPreviewMode && (
                                  <div className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                                    <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                                    LIVE
                                  </div>
                                )}
                                {/* Stream Health Indicator - Only show when live */}
                                {!isPreviewMode && (
                                  <div 
                                    className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${
                                      streamHealth.status === 'healthy' 
                                        ? 'bg-emerald-500/80 text-white' 
                                        : streamHealth.status === 'warning'
                                        ? 'bg-amber-500/80 text-white'
                                        : 'bg-red-600/80 text-white animate-pulse'
                                    }`}
                                    title={`Connection: ${streamHealth.status}\nLatency: ${streamHealth.latency ? streamHealth.latency + 'ms' : 'N/A'}\nUptime: ${Math.floor(streamHealth.uptime / 60)}:${(streamHealth.uptime % 60).toString().padStart(2, '0')}`}
                                  >
                                    {streamHealth.status === 'healthy' ? (
                                      <Wifi className="h-3 w-3" />
                                    ) : streamHealth.status === 'warning' ? (
                                      <WifiOff className="h-3 w-3" />
                                    ) : (
                                      <AlertCircle className="h-3 w-3" />
                                    )}
                                    <span className="hidden sm:inline">
                                      {streamHealth.status === 'critical' ? 'Reconnecting'
                                        : streamHealth.status === 'warning' ? 'Weak'
                                        : streamHealth.latency == null ? 'Live'
                                        : streamHealth.latency < 250 ? 'Strong'
                                        : streamHealth.latency < 600 ? 'Good'
                                        : 'Weak'}
                                    </span>
                                  </div>
                                )}
                                {isPreviewMode && (
                                  <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                                    PREVIEW
                                  </div>
                                )}
                              </div>
                              
                              {/* Right: Viewer Count + Ruby Counter */}
                              <div className="flex items-center gap-2">
                                <div className="bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-2" data-testid="broadcaster-viewer-count">
                                  <Users className="h-4 w-4 text-white" />
                                  <span className="text-white font-bold text-sm">{displayViewers}</span>
                                </div>
                                {/* Ruby Counter - Rubies earned this stream */}
                                <div className="bg-pink-500/80 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1">
                                  <RubyIcon className="h-4 w-4" />
                                  <span className="text-white font-bold text-sm">{streamRubiesEarned.toLocaleString()}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Live Comments/Chat Overlay - Smaller, with hide/show toggle */}
                        {/* On-screen chat overlay removed - using bottom chat only */}
                        
                        {/* Join notifications removed - will appear in comments instead */}
                        
                        {/* Reaction buttons removed per user request */}
                      </div>
                    
                    {/* Fallback Video Element for other cases */}
                    {(!webrtc.isStreaming || invitedPlayers.length > 0) && (
                      <video
                        ref={webrtc.localVideoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                        style={{ transform: 'scaleX(-1)' }}
                        onLoadedMetadata={() => {
                          console.log('📹 Video metadata loaded');
                          if (webrtc.localVideoRef.current) {
                            webrtc.localVideoRef.current.play().catch(console.error);
                          }
                        }}
                        onError={(e) => {
                          console.error('❌ Video error:', e);
                        }}
                      />
                    )}
                    
                    {/* Searching for Match Overlay */}
                    {/* Quick Match Searching Strip - Positioned to not block controls */}
                    {isSearchingForMatch && matchmakingStatus?.status === 'searching' && (
                      <div className="absolute bottom-20 left-0 right-0 z-40 pointer-events-none">
                        <div className="bg-gradient-to-r from-emerald-600 via-blue-600 to-emerald-600 bg-[length:200%_100%] animate-gradient border-y-2 border-white/30 shadow-2xl pointer-events-auto">
                          <div className="px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="relative">
                                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                                  <Users className="h-5 w-5 text-white" />
                                </div>
                                <div className="absolute -inset-1 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                              </div>
                              <div>
                                <h4 className="font-bold text-white text-base">Quick Match - Finding Players...</h4>
                                <p className="text-white/90 text-sm">
                                  <EmeraldIcon className="h-4 w-4 inline" /> {matchmakingStatus?.wagerAmount} Emeralds • Looking for 3 players
                                </p>
                              </div>
                            </div>
                            <Button
                              onClick={async () => {
                                await api.delete('/matchmaking/leave');
                                setIsSearchingForMatch(false);
                                setMatchmakingStatus(null);
                                toast.info('Cancelled matchmaking');
                              }}
                              variant="outline"
                              className="border-white/50 text-white hover:bg-white/20 bg-white/10 pointer-events-auto"
                            >
                              Cancel Search
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Multi-Streamer Split View (When players join) */}
                    {invitedPlayers.length > 0 && !isSearchingForMatch && !inStreamGame && (
                      <div className="absolute inset-0 bg-black">
                        {/* 2-Way Split: Host + 1 Player (side by side) */}
                        {invitedPlayers.length === 1 && (
                          <div className="h-full flex">
                            {/* When game is active: 3-column layout (broadcaster | board | broadcaster).
                                4iaR ONLY — checkers/crazy-eights render their own overlays. */}
                            {activeLinkedGame && activeLinkedGame.phase !== 'waiting_accept' && activeLinkedGame.gameType !== 'checkers' && activeLinkedGame.gameType !== 'crazy_eights' ? (
                              <>
                                {/* Part 1 (Feb 19, 2026): shared status bar
                                    replaces the legacy 4iaR wins scoreboard —
                                    one bar, never two. Left slot keeps the
                                    turn/entry indicator; right slot shows
                                    session-wins chips + Forfeit. */}
                                {(() => {
                                  const me = user?.id;
                                  const opp = linkedGameOpponent?.id;
                                  const players = [me, opp].filter(Boolean);
                                  const names = Object.fromEntries(players.map((p) => [
                                    p,
                                    p === me
                                      ? (user?.name || 'You')
                                      : (linkedGameOpponent?.name || 'Guest'),
                                  ]));
                                  const forfeitHandler = async () => {
                                    console.log('[Forfeit] 4iaR bar click', { gid: activeLinkedGame?.gameId });
                                    if (!window.confirm('Forfeit this match? Your opponent wins the prize.')) return;
                                    try {
                                      await api.post(`/four-in-a-row/${activeLinkedGame.gameId}/forfeit`);
                                    } catch (e) {
                                      toast.error(e?.response?.data?.detail || 'Could not forfeit');
                                    }
                                  };
                                  const turnLabel = activeLinkedGame.currentPlayer === activeLinkedGame.myPlayerNum ? 'YOU' : (linkedGameOpponent?.name || 'Opponent');
                                  return (
                                    <div className="absolute top-0 left-0 right-0 z-20" data-testid="game-scoreboard">
                                      <GameStatusBar
                                        players={players}
                                        sessionWins={sessionWins}
                                        names={names}
                                        onForfeit={forfeitHandler}
                                        left={(
                                          <div className="flex items-center gap-2 text-white text-xs">
                                            <span className={`px-2 py-1 rounded-full font-bold ${
                                              activeLinkedGame.currentPlayer === activeLinkedGame.myPlayerNum
                                                ? 'bg-emerald-500'
                                                : 'bg-gray-600'
                                            }`}>Turn: {turnLabel}</span>
                                            <span className="text-emerald-400">Entry: {activeLinkedGame.wagerAmount}</span>
                                          </div>
                                        )}
                                      />
                                    </div>
                                  );
                                })()}
                                {/* Legacy 4iaR wins scoreboard removed
                                    (Feb 19, 2026) — replaced by the shared
                                    <GameStatusBar> above. */}

                                {/* Left Broadcaster (narrower during game).
                                    Hidden in Link v2 — the stable
                                    LiveKitLinkGrid strip already renders
                                    every broadcaster's video above the
                                    board, so these legacy Agora-era columns
                                    just showed dead "Connecting…" tiles
                                    once the mesh was on LiveKit. */}
                                {!(LIVEKIT_LINKS && linkRoomInfo) && (
                                <div className="w-[30%] relative" style={{ overflow: 'hidden' }}>
                                  {!isPreviewMode && useAgora && sharedAgoraTracks?.videoTrack ? (
                                    <AgoraVideoPlayer
                                      videoTrack={sharedAgoraTracks.videoTrack}
                                      audioTrack={sharedAgoraTracks.audioTrack}
                                    />
                                  ) : (
                                    <video
                                      id="host-video-game"
                                      autoPlay playsInline muted
                                      className="w-full h-full object-cover"
                                      style={{ backgroundColor: '#000' }}
                                    />
                                  )}
                                  <div className="absolute top-12 left-2 z-10">
                                    <div className="bg-black/70 px-2 py-1 rounded text-xs text-white font-semibold">{user?.name}</div>
                                    <div className="bg-red-500 px-1.5 py-0.5 rounded text-[10px] text-white font-bold mt-1 flex items-center gap-1 w-fit">
                                      <div className="w-1.5 h-1.5 bg-white rounded-full animate-ping"></div>
                                      LIVE
                                    </div>
                                  </div>
                                  {connectedCoHosts.length > 0 && (
                                    <button
                                      onClick={() => disconnectCoHost(invitedPlayers[0].id)}
                                      className="absolute bottom-2 left-2 z-10 bg-red-600/80 hover:bg-red-600 px-2 py-1 rounded text-white text-[10px] font-medium flex items-center gap-1"
                                    >
                                      <X className="w-2.5 h-2.5" />
                                      End Link
                                    </button>
                                  )}
                                </div>
                                )}

                                {/* Center: Game Board */}
                                <div
                                  className={
                                    (LIVEKIT_LINKS && linkRoomInfo)
                                      ? (isDesktopViewport
                                          ? 'absolute left-[29%] right-[29%] top-[48px] bottom-1 z-10 bg-black/90 flex flex-col items-stretch justify-start pt-2 pb-2'
                                          : 'absolute inset-x-1 top-[calc(28vh+52px)] bottom-1 z-10 bg-black/90 flex flex-col items-stretch justify-start pt-2 pb-2')
                                      : 'flex-1 relative bg-black/90 flex flex-col items-center justify-start pt-[72px] pb-3 min-h-0 overflow-y-auto'
                                  }
                                  data-testid="linked-game-overlay"
                                >
                                  {/* Forfeit — real server-side forfeit
                                      (Aug 18, 2026): opponent wins the
                                      prize, both screens return to grid via
                                      the existing game-over broadcast. */}
                                  <button 
                                    className="absolute top-2 right-2 w-auto px-2.5 h-7 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center z-40 text-[11px] font-semibold pointer-events-auto shadow-lg"
                                    data-testid="forfeit-game-btn"
                                    onClick={async () => {
                                      console.log('[Forfeit] click', { checkers: !!checkersLiveState, c8: !!c8LiveState, iar: activeLinkedGame?.gameId, gid: inStreamGame });
                                      if (!confirm('Forfeit this match? Your opponent wins the prize.')) return;
                                      try {
                                        if (checkersLiveState && inStreamGame) {
                                          await api.post(`/checkers/${inStreamGame}/forfeit`);
                                        } else if (c8LiveState && inStreamGame) {
                                          sendGameMessage({ type: 'c8_forfeit' });
                                        } else if (activeLinkedGame?.gameId) {
                                          await api.post(`/four-in-a-row/${activeLinkedGame.gameId}/forfeit`);
                                        }
                                      } catch (e) {
                                        toast.error(e?.response?.data?.detail || 'Could not forfeit');
                                      }
                                    }}
                                  >
                                    Forfeit
                                  </button>
                                  
                                  {/* Board — Link v2 wraps in FitToParent to
                                      guarantee the full 7×6 grid is always
                                      visible; legacy Agora path keeps
                                      original flow. */}
                                  {(LIVEKIT_LINKS && linkRoomInfo) ? (
                                    <div className="flex-1 min-h-0 w-full">
                                      <FitToParent className="pointer-events-auto">
                                  <div className="p-1.5 rounded-xl shadow-2xl" style={{ background: '#0B1F16', border: '2px solid #B8860B' }}>
                                    {/* Column drop buttons */}
                                    <div className="grid grid-cols-7 gap-0.5 mb-0.5">
                                      {[0, 1, 2, 3, 4, 5, 6].map(col => (
                                        <button
                                          key={`col-${col}`}
                                          data-testid={`drop-col-${col}`}
                                          className={`w-7 h-6 sm:w-9 sm:h-7 rounded-t flex items-center justify-center transition-all
                                            ${activeLinkedGame.currentPlayer === activeLinkedGame.myPlayerNum && activeLinkedGame.phase === 'playing'
                                              ? 'hover:bg-emerald-500 cursor-pointer bg-emerald-700/60' 
                                              : 'cursor-not-allowed opacity-30 bg-gray-500/30'}`}
                                          onClick={async () => {
                                            if (activeLinkedGame.currentPlayer !== activeLinkedGame.myPlayerNum) {
                                              toast.info("Wait for your turn!");
                                              return;
                                            }
                                            if (activeLinkedGame.phase !== 'playing') return;
                                            
                                            try {
                                              const response = await api.post(`/four-in-a-row/${activeLinkedGame.gameId}/move`, {
                                                column: col
                                              });
                                              
                                              const newBoard = response.data.board || response.data.game?.board;
                                              const newPlayer = response.data.current_player;
                                              const newPhase = response.data.phase || response.data.game?.phase;
                                              const winner = response.data.winner || response.data.game?.winner;
                                              
                                              if (newBoard) {
                                                setActiveLinkedGame(prev => ({
                                                  ...prev,
                                                  board: newBoard,
                                                  currentPlayer: newPlayer,
                                                  phase: newPhase
                                                }));
                                                
                                                try {
                                                  await api.post(`/four-in-a-row/${activeLinkedGame.gameId}/broadcast-move`, {
                                                    opponent_id: linkedGameOpponent?.id,
                                                    column: col,
                                                    board: newBoard,
                                                    current_player: newPlayer,
                                                    phase: newPhase,
                                                    winner: winner
                                                  });
                                                } catch (e) {
                                                  console.log('Failed to broadcast move:', e);
                                                }
                                                
                                                if (newPhase === 'finished') {
                                                  const winnerPayout = Math.floor(activeLinkedGame.wagerAmount * 2 * (1 - (economyRates?.rake_1v1 ?? 0.08)));
                                                  if (winner === activeLinkedGame.myPlayerNum) {
                                                    toast.success(<span className="flex items-center gap-1">You won! +{winnerPayout} <RubyIcon className="h-4 w-4 inline" /></span>);
                                                    setLinkedSessionWins(prev => ({ ...prev, me: prev.me + 1 }));
                                                  } else if (winner === 0) {
                                                    toast.info("It's a draw! Entries refunded.");
                                                  } else {
                                                    toast.error(`${linkedGameOpponent?.name} won!`);
                                                    setLinkedSessionWins(prev => ({ ...prev, opponent: prev.opponent + 1 }));
                                                  }

                                                  // Match-record screenshot (LIVE 4iaR only; the invite path
                                                  // never runs in practice mode).
                                                  captureAndUploadGameResult({
                                                    gameId: activeLinkedGame.gameId,
                                                    gameType: 'four_in_a_row',
                                                    selector: '[data-testid="linked-game-overlay"]',
                                                    isPractice: false,
                                                    winnerId: winner === activeLinkedGame.myPlayerNum
                                                      ? (user?.id || null)
                                                      : (winner === 0 ? null : (linkedGameOpponent?.id || null)),
                                                    resultSummary: winner === 0
                                                      ? 'Draw'
                                                      : `Player ${winner} won`,
                                                    wager: Number(activeLinkedGame.wagerAmount) || 0,
                                                    participants: [
                                                      { user_id: user?.id, name: user?.name, handle: user?.handle },
                                                      { user_id: linkedGameOpponent?.id, name: linkedGameOpponent?.name, handle: linkedGameOpponent?.handle },
                                                    ],
                                                  });

                                                  setTimeout(() => {
                                                    setActiveLinkedGame(null);
                                                    setInStreamGame(null);
                                                    setCheckersLiveState(null);
                                                    setC8LiveState(null);
                                                  }, 3000);
                                                }
                                              }
                                            } catch (error) {
                                              console.error('Move failed:', error);
                                              toast.error(error.response?.data?.detail || 'Move failed');
                                            }
                                          }}
                                        >
                                          <span className="text-white/80 text-sm">▼</span>
                                        </button>
                                      ))}
                                    </div>
                                    
                                    {/* Board cells */}
                                    {[5, 4, 3, 2, 1, 0].map(row => (
                                      <div key={row} className="grid grid-cols-7 gap-0.5 mt-0.5">
                                        {[0, 1, 2, 3, 4, 5, 6].map(col => {
                                          const cell = activeLinkedGame.board[col]?.[row] || 0;
                                          return (
                                            <div 
                                              key={`${col}-${row}`} 
                                              className="w-7 h-7 sm:w-9 sm:h-9 bg-emerald-950/90 ring-1 ring-yellow-700/50 rounded-full flex items-center justify-center"
                                              data-testid={`cell-${col}-${row}`}
                                            >
                                              {cell === 1 && <div className="w-5 h-5 sm:w-7 sm:h-7 rounded-full bg-red-500 shadow-lg shadow-red-500/50" data-testid="piece-red" />}
                                              {cell === 2 && <div className="w-5 h-5 sm:w-7 sm:h-7 rounded-full bg-green-500 shadow-lg shadow-green-500/50" data-testid="piece-green" />}
                                              {cell === 0 && <div className="w-5 h-5 sm:w-7 sm:h-7 rounded-full bg-black/60" />}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ))}
                                  </div>
                                      </FitToParent>
                                    </div>
                                  ) : (
                                  <div className="p-1.5 rounded-xl shadow-2xl my-auto" style={{ background: '#0B1F16', border: '2px solid #B8860B' }}>
                                    {/* Column drop buttons */}
                                    <div className="grid grid-cols-7 gap-0.5 mb-0.5">
                                      {[0, 1, 2, 3, 4, 5, 6].map(col => (
                                        <button
                                          key={`col-${col}`}
                                          data-testid={`drop-col-${col}-legacy`}
                                          className="w-9 h-7 rounded-t bg-gray-500/30 opacity-30 cursor-not-allowed"
                                          disabled
                                        >
                                          <span className="text-white/80 text-sm">▼</span>
                                        </button>
                                      ))}
                                    </div>
                                    {/* Legacy Agora board — kept for
                                        non-Link-v2 sessions. Interactive
                                        moves route via the Link v2 board
                                        above; this fallback is display-only
                                        and unreachable in production while
                                        LIVEKIT_LINKS is on. */}
                                    {[5, 4, 3, 2, 1, 0].map(row => (
                                      <div key={row} className="grid grid-cols-7 gap-0.5 mt-0.5">
                                        {[0, 1, 2, 3, 4, 5, 6].map(col => {
                                          const cell = activeLinkedGame.board[col]?.[row] || 0;
                                          return (
                                            <div key={`legacy-${col}-${row}`} className="w-9 h-9 bg-emerald-950/90 ring-1 ring-yellow-700/50 rounded-full flex items-center justify-center">
                                              {cell === 1 && <div className="w-7 h-7 rounded-full bg-red-500" />}
                                              {cell === 2 && <div className="w-7 h-7 rounded-full bg-green-500" />}
                                              {cell === 0 && <div className="w-7 h-7 rounded-full bg-black/60" />}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ))}
                                  </div>
                                  )}
                                  
                                  {/* Turn indicator below board */}
                                  <div className="text-center mt-2">
                                    <div className={`inline-block px-4 py-1.5 rounded-full text-xs font-bold ${
                                      activeLinkedGame.phase === 'playing'
                                        ? activeLinkedGame.currentPlayer === activeLinkedGame.myPlayerNum 
                                          ? 'bg-emerald-500 text-white' 
                                          : 'bg-gray-700 text-gray-300'
                                        : 'bg-amber-500 text-black'
                                    }`}>
                                      {activeLinkedGame.phase === 'playing' 
                                        ? activeLinkedGame.currentPlayer === activeLinkedGame.myPlayerNum 
                                          ? "Your turn!" 
                                          : `${linkedGameOpponent?.name}'s turn`
                                        : 'Game Over'
                                      }
                                    </div>
                                  </div>
                                </div>

                                {/* Right Broadcaster (narrower during game).
                                    Hidden in Link v2 — see left column note. */}
                                {!(LIVEKIT_LINKS && linkRoomInfo) && (
                                <div className="w-[30%] relative border-l-2 border-emerald-500" style={{ overflow: 'hidden' }}>
                                  {getRemoteUserByPosition(0)?.videoTrack ? (
                                    <AgoraVideoPlayer
                                      videoTrack={getRemoteUserByPosition(0).videoTrack}
                                      audioTrack={getRemoteUserByPosition(0).audioTrack}
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                                      <div className="text-white text-center">
                                        <p className="font-bold">{getRemoteUserIdentity(0)?.name || invitedPlayers[0]?.name}</p>
                                        <div className="mt-2 bg-yellow-500 px-3 py-1 rounded-full animate-pulse inline-block text-xs">
                                          Connecting...
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  <div className="absolute top-12 right-2 z-10 text-right">
                                    <div className="bg-black/70 px-2 py-1 rounded text-xs text-white font-semibold">{getRemoteUserIdentity(0)?.name || invitedPlayers[0]?.name}</div>
                                    {getRemoteUserByPosition(0)?.videoTrack && (
                                      <div className="bg-red-500 px-1.5 py-0.5 rounded text-[10px] text-white font-bold mt-1 flex items-center gap-1 justify-end w-fit ml-auto">
                                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-ping"></div>
                                        LIVE
                                      </div>
                                    )}
                                  </div>
                                </div>
                                )}
                              </>
                            ) : null}
                          </div>
                        )}

                        {/* ============================================================
                            MESH-REFACTOR (May 16, 2026): DYNAMIC MULTI-BROADCASTER GRID
                            ============================================================
                            SINGLE SOURCE OF TRUTH layout. Drives off remoteTiles (the
                            real Agora truth) + 1 (self). Replaces the old hardcoded
                            invitedPlayers.length === 1/2/3 blocks that drifted under
                            race conditions.

                            Layouts:
                              2 (self + 1) → side-by-side 50/50
                              3 (self + 2) → host left 50%, 2 stacked right 50%
                              4 (self + 3) → 2×2 grid
                              5-6          → 3×2 grid (product spec supports up to 6)

                            On broadcaster leave, this auto-reflows because the count
                            comes from live state (4→3, 3→2 head-to-head, etc.).
                            ============================================================ */}
                        {!(invitedPlayers.length === 1 && activeLinkedGame && activeLinkedGame.phase !== 'waiting_accept') && (remoteTiles.length >= 1 || invitedPlayers.length >= 1) && !isSearchingForMatch && !inStreamGame && (() => {
                          // Link v2: when publishing into a LiveKit linked room,
                          // the STABLE LiveKitLinkGrid mount at the top of this
                          // video card (see "STABLE MOUNT for LiveKitLinkGrid"
                          // above ~L10870) already renders the mesh full-size
                          // via layout="auto". Skipping this block avoids a
                          // second Room instance that would fight the first
                          // and disconnect within ~1s.
                          if (LIVEKIT_LINKS && linkRoomInfo) return null;
                          const participantCount = remoteTiles.length + 1; // self + remote tiles
                          // Layout strategy keyed off live participantCount.
                          let containerCls, selfWrapperCls, remoteWrapperCls;
                          if (participantCount <= 2) {
                            // 50/50 side-by-side
                            containerCls = 'h-full flex';
                            selfWrapperCls = 'flex-1 relative';
                            remoteWrapperCls = 'flex-1 relative border-l-2 border-emerald-500';
                          } else if (participantCount === 3) {
                            // Host left 50%, 2 stacked right 50%
                            containerCls = 'h-full flex';
                            selfWrapperCls = 'flex-1 relative';
                            remoteWrapperCls = null; // custom column container below
                          } else if (participantCount === 4) {
                            // 2×2 grid
                            containerCls = 'h-full w-full grid grid-cols-2 grid-rows-2 gap-1';
                            selfWrapperCls = 'relative';
                            remoteWrapperCls = 'relative';
                          } else {
                            // 5 or 6 → 3×2 grid
                            containerCls = 'h-full w-full grid grid-cols-3 grid-rows-2 gap-1';
                            selfWrapperCls = 'relative';
                            remoteWrapperCls = 'relative';
                          }

                          const canEndLink = useAgora && remoteTiles.length >= 1 && !activeLinkedGame && !inStreamGame;
                          // disconnectCoHost passes a partner_id only as a hint; the
                          // backend leaves OUR user from the linked session and fans
                          // broadcaster_left_session/link_ended correctly via /streams/link-ended.
                          const endLinkTargetId = remoteTiles[0]?.identity?.id || invitedPlayers[0]?.id || null;

                          // SELF TILE
                          const renderSelfTile = (extraClass = '') => (
                            <div key="self" className={`${selfWrapperCls} ${extraClass}`} style={{ overflow: 'hidden' }}>
                              {!isPreviewMode && useAgora && sharedAgoraTracks?.videoTrack ? (
                                <AgoraVideoPlayer
                                  videoTrack={sharedAgoraTracks.videoTrack}
                                  audioTrack={sharedAgoraTracks.audioTrack}
                                />
                              ) : (
                                <video
                                  id="host-video-dynamic"
                                  autoPlay
                                  playsInline
                                  muted
                                  className="w-full h-full object-cover"
                                  style={{ backgroundColor: '#000' }}
                                />
                              )}
                              <div className="absolute top-3 left-3 z-10 space-y-2">
                                <div className="bg-black/70 px-3 py-2 rounded-lg">
                                  <p className="text-white font-semibold text-sm">{user?.name}</p>
                                  <p className="text-gray-300 text-xs">@{user?.handle}</p>
                                </div>
                                <div className="bg-red-500 px-2 py-1 rounded text-xs text-white font-bold flex items-center gap-1">
                                  <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                                  LIVE
                                </div>
                              </div>
                              {canEndLink && endLinkTargetId && (
                                <button
                                  onClick={() => disconnectCoHost(endLinkTargetId)}
                                  data-testid="end-link-btn"
                                  className="absolute bottom-3 left-3 z-10 bg-red-600/80 hover:bg-red-600 px-3 py-1.5 rounded-lg text-white text-xs font-medium transition-colors flex items-center gap-1.5"
                                >
                                  <X className="w-3 h-3" />
                                  End Link
                                </button>
                              )}
                            </div>
                          );

                          // REMOTE TILE — driven STRICTLY by remoteTiles uid; identity
                          // resolves via cohostsByUid only. No round-robin fallback.
                          const renderRemoteTile = (tile, idx) => (
                            <div
                              key={`remote-${tile.uid}`}
                              className={`${remoteWrapperCls || 'flex-1 relative border-l-2 border-emerald-500'}`}
                              style={{ overflow: 'hidden' }}
                              data-testid={`remote-tile-${tile.uid}`}
                            >
                              {tile.videoTrack ? (
                                <AgoraVideoPlayer
                                  videoTrack={tile.videoTrack}
                                  audioTrack={tile.audioTrack}
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                                  <div className="text-white text-center">
                                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-2">
                                      <span className="text-2xl">👤</span>
                                    </div>
                                    <p className="font-bold text-sm">{tile.identity?.name || 'Connecting…'}</p>
                                    <div className="mt-1 bg-yellow-500 px-3 py-1 rounded-full animate-pulse text-xs">
                                      Connecting…
                                    </div>
                                  </div>
                                </div>
                              )}
                              <div className="absolute top-2 right-2 bg-black/70 px-2 py-1 rounded-lg text-xs z-10 max-w-[60%]">
                                <p className="text-white font-semibold truncate">{tile.identity?.name || 'Connecting…'}</p>
                                {tile.videoTrack && (
                                  <div className="mt-1 bg-red-500 px-2 py-0.5 rounded text-xs inline-block">🔴 LIVE</div>
                                )}
                              </div>
                            </div>
                          );

                          // 3-up special-case: right column has two stacked remote tiles.
                          if (participantCount === 3) {
                            return (
                              <div className="absolute inset-0 bg-black" data-testid="multi-broadcaster-grid" data-participant-count={participantCount}>
                                <div className={containerCls}>
                                  {renderSelfTile()}
                                  <div className="flex-1 flex flex-col">
                                    {remoteTiles.slice(0, 2).map((tile, idx) => (
                                      <div
                                        key={`remote-${tile.uid}`}
                                        className={`flex-1 relative border-l-2 ${idx === 0 ? 'border-b' : 'border-t'} border-emerald-500`}
                                        style={{ overflow: 'hidden' }}
                                        data-testid={`remote-tile-${tile.uid}`}
                                      >
                                        {tile.videoTrack ? (
                                          <AgoraVideoPlayer
                                            videoTrack={tile.videoTrack}
                                            audioTrack={tile.audioTrack}
                                          />
                                        ) : (
                                          <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                                            <div className="text-white text-center">
                                              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-2">
                                                <span className="text-2xl">👤</span>
                                              </div>
                                              <p className="font-bold text-sm">{tile.identity?.name || 'Connecting…'}</p>
                                              <div className="mt-1 bg-yellow-500 px-3 py-1 rounded-full animate-pulse text-xs">
                                                Connecting…
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        <div className="absolute top-2 right-2 bg-black/70 px-2 py-1 rounded-lg text-xs z-10 max-w-[80%]">
                                          <p className="text-white font-semibold truncate">{tile.identity?.name || 'Connecting…'}</p>
                                          {tile.videoTrack && (
                                            <div className="mt-1 bg-red-500 px-2 py-0.5 rounded text-xs inline-block">🔴</div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          // 2-up, 4-up, 5-up, 6-up: single container, self + remoteTiles flat.
                          return (
                            <div className="absolute inset-0 bg-black" data-testid="multi-broadcaster-grid" data-participant-count={participantCount}>
                              <div className={containerCls}>
                                {renderSelfTile()}
                                {remoteTiles.slice(0, 5).map((tile, idx) => renderRemoteTile(tile, idx))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    
                    {/* Crazy Eights in-stream overlay (Jul 27, 2026).
                        Renders CrazyEightsBoard over the video mesh so the
                        hand sits under the players' cameras, cards animate
                        to the center discard, and opponents/viewers see only
                        played cards. Uses the same c8_* WS messages as the
                        practice board — server is authoritative. */}
                    {inStreamGame && c8LiveState && !gameState && !isDesktopViewport && (
                      <div
                        className={(LIVEKIT_LINKS && linkRoomInfo && !isDesktopViewport)
                          ? 'absolute inset-x-0 top-[28vh] bottom-0 z-30 pointer-events-auto'
                          : 'absolute inset-x-0 bottom-0 z-30 pointer-events-auto'}
                        data-testid="c8-in-stream-board"
                      >
                        <CrazyEightsBoard
                          state={c8LiveState}
                          meId={user?.id}
                          timers={{}}
                          fanned
                          sessionWins={sessionWins}
                          onPlay={(idx, chosen_suit) =>
                            sendGameMessage({ type: 'c8_play_card', card_index: idx, chosen_suit })
                          }
                          onDraw={() => sendGameMessage({ type: 'c8_draw' })}
                          onPass={() => sendGameMessage({ type: 'c8_pass' })}
                          onDeclareLastCard={() => sendGameMessage({ type: 'c8_declare_last_card' })}
                          onForfeit={() => {
                            console.log('[Forfeit] click (mobile-overlay)', { gid: inStreamGame });
                            if (!window.confirm('Forfeit this match? Your opponent wins the prize.')) return;
                            sendGameMessage({ type: 'c8_forfeit' });
                          }}
                          playerNames={Object.fromEntries(
                            // Part 4b (Feb 19, 2026): resolve each player id to
                            // a real name — self → user.name, linked partner →
                            // linkPartners lookup, unknown → 'Guest'. The old
                            // seat-0-is-you mapping mislabelled hosts on the
                            // wrong device.
                            (c8LiveState.players || []).map((p) => [
                              p,
                              p === user?.id
                                ? (user?.name || 'You')
                                : ((linkPartners || []).find((x) => x.id === p)?.name || 'Guest'),
                            ])
                          )}
                        />
                      </div>
                    )}
                    {/* Checkers in-stream overlay — renders CheckersBoard over
                        the video mesh, centered like the 4iaR board. Server is
                        authoritative via the checkers_state WS push; moves go
                        back over the same game WS. No navigation away. */}
                    {inStreamGame && checkersLiveState && !gameState && !c8LiveState && (() => {
                      const linkV2 = !!(LIVEKIT_LINKS && linkRoomInfo);
                      // Part 1 (Feb 19, 2026): shared status bar wired for
                      // Checkers. Player ids come from color_map; names use
                      // the same self+linkPartners resolution as C8.
                      const cmap = checkersLiveState?.color_map || {};
                      const players = Object.keys(cmap);
                      const names = Object.fromEntries(players.map((p) => [
                        p,
                        p === user?.id
                          ? (user?.name || 'You')
                          : ((linkPartners || []).find((x) => x.id === p)?.name || 'Guest'),
                      ]));
                      const myColor = cmap[user?.id];
                      const turnLabel = checkersLiveState?.winner
                        ? `Winner: ${checkersLiveState.winner}`
                        : `Turn: ${checkersLiveState?.turn || '?'}${myColor ? ` (you: ${myColor})` : ''}`;
                      const forfeitHandler = () => {
                        console.log('[Forfeit] Checkers bar click', { gid: inStreamGame });
                        if (!window.confirm('Forfeit this match? Your opponent wins the prize.')) return;
                        api.post(`/checkers/${inStreamGame}/forfeit`).catch((e) => toast.error(e?.response?.data?.detail || 'Could not forfeit'));
                      };
                      // Under Link v2: board sits between the two side
                      // cameras on desktop, or under the top strip on mobile.
                      // Legacy path keeps the previous centered overlay.
                      const cls = linkV2
                        ? (isDesktopViewport
                            ? 'absolute left-[29%] right-[29%] top-0 bottom-1 z-10 flex flex-col items-stretch justify-start pointer-events-none'
                            : 'absolute inset-x-1 top-[28vh] bottom-1 z-10 flex flex-col items-stretch justify-start pointer-events-none')
                        : 'absolute inset-0 flex flex-col items-stretch justify-start pointer-events-none z-10';
                      return (
                      <div className={cls} data-testid="checkers-in-stream-board">
                        <div className="pointer-events-auto">
                          <GameStatusBar
                            players={players}
                            sessionWins={sessionWins}
                            names={names}
                            onForfeit={!checkersLiveState?.winner ? forfeitHandler : null}
                            left={<span className="text-white/85 text-sm font-semibold">{turnLabel}</span>}
                          />
                        </div>
                        {linkV2 ? (
                          <FitToParent className="pointer-events-auto flex-1">
                            <CheckersBoard
                              state={checkersLiveState}
                              myColor={checkersLiveState?.color_map?.[user?.id]}
                              onMove={(move) => sendGameMessage({ type: 'checkers_move', move })}
                              onHuff={(pos) => sendGameMessage({ type: 'checkers_huff', pos })}
                            />
                          </FitToParent>
                        ) : (
                          <div className="pointer-events-auto flex-1">
                            <CheckersBoard
                              state={checkersLiveState}
                              myColor={checkersLiveState?.color_map?.[user?.id]}
                              onMove={(move) => sendGameMessage({ type: 'checkers_move', move })}
                              onHuff={(pos) => sendGameMessage({ type: 'checkers_huff', pos })}
                            />
                          </div>
                        )}
                      </div>
                      );
                    })()}
                    {/* Part 3a (Feb 19, 2026 SPADES FIX): standalone
                        waiting panel for the host while the board shell
                        is suppressed (invites pending, no deal yet). */}
                    {inStreamGame && spadesInvitePending &&
                     spadesInvitePending.game_id === inStreamGame &&
                     (myCards || []).length === 0 &&
                     !['playing', 'PLAYING', 'bidding', 'BIDDING'].includes(gameState?.phase) && (
                      <div
                        className="absolute inset-0 z-40 flex items-center justify-center pointer-events-auto bg-black/50 backdrop-blur-sm"
                        data-testid="spades-host-waiting-panel"
                      >
                        <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl px-6 py-5 max-w-sm w-[90%] text-center space-y-3">
                          <div className="text-xl font-bold text-white">
                            ⏳ Challenge sent
                          </div>
                          <div className="text-sm text-emerald-400 font-semibold">
                            {(spadesInvitePending.joined_ids || []).length} / {(spadesInvitePending.invitee_ids || []).length} accepted
                          </div>
                          <div className="text-xs text-gray-400">
                            Waiting for players to accept • auto-cancels in 30s
                          </div>
                          <button
                            onClick={cancelPendingSpadesGame}
                            data-testid="spades-cancel-game-btn-panel"
                            className="mt-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold py-2 px-4 rounded-lg"
                          >
                            Cancel Challenge
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Part 3b (Feb 19, 2026 SPADES FIX): standalone chip
                        for INVITEES who have accepted but the deal hasn't
                        arrived yet — replaces the empty board experience. */}
                    {inStreamGame && !spadesInvitePending && gameState &&
                     (myCards || []).length === 0 &&
                     !['playing', 'PLAYING', 'bidding', 'BIDDING'].includes(gameState?.phase) && (
                      <div
                        className="absolute inset-x-0 top-4 z-40 flex justify-center pointer-events-none"
                        data-testid="spades-invitee-waiting-chip"
                      >
                        <div className="bg-slate-900/90 border border-slate-700 rounded-full px-4 py-2 text-white text-xs font-semibold shadow-lg">
                          ⏳ Joined — waiting for the other players…
                        </div>
                      </div>
                    )}

                    {/* Game Overlay — Transparent overlay showing cards and
                        game UI OVER video streams.
                        Feb 20, 2026: gate strictly on myCards.length so nobody
                        (host or invitee) sees an empty black game area before
                        the deal arrives. The waiting chip above handles the
                        pre-deal state. */}
                    {inStreamGame && gameState && (myCards || []).length > 0 && (
                      <div
                        className={(LIVEKIT_LINKS && linkRoomInfo && !isDesktopViewport)
                          ? 'absolute inset-x-0 top-[28vh] bottom-0 z-30 flex flex-col pointer-events-none overflow-hidden'
                          : 'absolute inset-0 z-30 flex flex-col pointer-events-none overflow-hidden'}
                      >
                        {/* Game Header - Complete Stats - NOW WITH SEMI-TRANSPARENT BACKGROUND */}
                        <div className="bg-black/80 backdrop-blur-md px-2 py-1 flex-shrink-0 pointer-events-auto" style={{ maxHeight: '60px' }}>
                          <div className="flex items-center justify-between text-white">
                            <div className="flex-1">
                              <h2 className="text-xs font-bold">Live Spades (NY Ruleset) • Hand {gameState?.currentHand || 1}</h2>
                              <div className="flex items-center gap-2 text-xs flex-wrap">
                                <span className="text-emerald-200">
                                  Books: T1: {gameState?.booksWon?.[0] || 0} | T2: {gameState?.booksWon?.[1] || 0}
                                </span>
                                {gameState?.matchScores && (
                                  <span className="text-yellow-300 font-bold">
                                    • Score: T1: {gameState.matchScores[0]} | T2: {gameState.matchScores[1]}
                                  </span>
                                )}
                                {gameState?.gameState === 'PLAYING' && gameState.teamBids && (
                                  <span className="text-cyan-300">
                                    • Bids: T1: {gameState.teamBids[0]} | T2: {gameState.teamBids[1]}
                                  </span>
                                )}
                              </div>
                              {/* Advanced Stats Row */}
                              <div className="flex items-center gap-2 mt-0.5 text-xs flex-wrap">
                                {gameState?.matchSettings?.allowBlindSix && (
                                  <span className="text-amber-300">
                                    Blinds: T1: {gameState.teamRemainingBlinds?.[0] || 3}/3 | T2: {gameState.teamRemainingBlinds?.[1] || 3}/3
                                  </span>
                                )}
                                {gameState?.matchSettings?.allowAceCheck && (
                                  <span className="text-blue-400">
                                    • Ace Check: T1: {gameState.aceCheckDeclared?.[0] ? '✗' : '✓'} | T2: {gameState.aceCheckDeclared?.[1] ? '✗' : '✓'}
                                  </span>
                                )}
                                {gameState?.teamSetCount && (
                                  <span className="text-red-400 font-bold">
                                    • SETs: T1: {gameState.teamSetCount[0] || 0} | T2: {gameState.teamSetCount[1] || 0}
                                  </span>
                                )}
                                {gameState?.totalBags && (
                                  <span className="text-orange-400">
                                    • Bags: T1: {gameState.totalBags[0] || 0} | T2: {gameState.totalBags[1] || 0}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                              {/* Forfeit — EVERY seated player gets this
                                  (Feb 2026). Forfeiting ends the game and
                                  declares the other team the winners; the
                                  video link stays up. */}
                              <button
                                onClick={forfeitSpadesGame}
                                data-testid="spades-forfeit-header-btn"
                                className="bg-red-600/80 hover:bg-red-600 px-2 py-1 rounded-full text-xs text-white font-bold pointer-events-auto"
                              >
                                Forfeit
                              </button>
                              <div className="bg-red-500 px-2 py-1 rounded-full animate-pulse text-xs">
                                🔴 LIVE
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Middle: Video Grid (fills available space, constrained height) */}
                        <div className="flex-1 relative overflow-hidden" style={{ minHeight: '300px', maxHeight: 'calc(100vh - 200px)' }}>
                          {/* ============================================================
                              GAME-VIDEO-IS-MESH (May 16, 2026):
                              The card game is an OVERLAY on the existing Agora mesh.
                              Pre-fix this block rendered avatar placeholders for the
                              3 remote players, which is why production showed empty
                              P2/P3/P4 tiles during "Waiting for players to connect"
                              even though they were already publishing on Agora.
                              Now we render the SAME tiles as the dynamic
                              multi-broadcaster grid (self + remoteTiles) so video
                              continuity is preserved across game start/end.
                              ============================================================ */}
                          {(LIVEKIT_LINKS && linkRoomInfo) ? (
                            /* LiveKit link mode (Feb 2026): the video is the
                               full-bleed LinkGrid BEHIND this overlay — the old
                               Agora mesh below rendered a dead <video> + empty
                               tiles ON TOP of it (the "black box"). Keep the
                               middle transparent and use it for the current
                               trick so everyone sees who played what. */
                            (() => {
                              // Completed-trick HOLD wins the display for 3s
                              // (with a winner caption) so everyone sees the
                              // full book; otherwise show the live trick.
                              const displayTrick = lastTrickHold?.trick
                                || ((gameState?.currentTrick || []).length > 0 ? gameState.currentTrick : null);
                              if (displayTrick && displayTrick.length > 0) {
                                return (
                                  <div className="absolute inset-x-0 top-3 flex flex-col items-center pointer-events-none" data-testid="spades-current-trick">
                                    <div className="flex items-end gap-2 bg-black/65 backdrop-blur-sm rounded-xl px-3 py-2">
                                      {displayTrick.map((t, i) => (
                                        <div key={`${t.seat}-${i}`} className="flex flex-col items-center" style={{ transform: 'scale(0.8)', transformOrigin: 'bottom center' }}>
                                          <PlayingCard
                                            card={t.card}
                                            playable={false}
                                            notMyTurn={false}
                                            isTrump={t.card?.suit === 'diamonds' && t.card?.value === '2'}
                                            onPlay={() => {}}
                                          />
                                          <span className="text-white text-[10px] font-semibold mt-1 max-w-[72px] truncate">
                                            {t.player_name || `P${(t.seat ?? 0) + 1}`}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                    {lastTrickHold && (
                                      <div className="mt-1 bg-emerald-600/90 text-white text-xs font-bold px-3 py-1 rounded-full">
                                        🏆 {lastTrickHold.winner} takes the book!
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              // BID TRACKER (Feb 2026): during bidding, show
                              // every seat's bid to the whole table.
                              if (gameState?.phase === 'bidding') {
                                return (
                                  <div className="absolute inset-x-0 top-3 flex justify-center pointer-events-none" data-testid="spades-bid-tracker">
                                  <div className="flex items-center gap-4 bg-black/85 backdrop-blur-sm rounded-xl px-5 py-2.5 ring-1 ring-white/20">
                                      {[...(gameState.players || [])]
                                        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                                        .map((p) => (
                                          <div key={p.position} className="flex flex-col items-center">
                                            <span className="text-white text-xs font-semibold max-w-[76px] truncate">{p.name}</span>
                                            <span className={`text-lg font-bold ${p.bid != null ? 'text-emerald-300' : 'text-gray-400 animate-pulse'}`}>
                                              {p.bid != null ? `${p.is_blind ? '🙈 ' : ''}${p.bid}` : '…'}
                                            </span>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })()
                          ) : (() => {
                            const participantCount = remoteTiles.length + 1;
                            // For Spades (4 players), use 2x2. For 2-3 fallback, simpler grids.
                            let gridCls = 'h-full w-full grid grid-cols-2 grid-rows-2 gap-1 p-1';
                            if (participantCount <= 2) gridCls = 'h-full flex gap-1 p-1';
                            else if (participantCount === 3) gridCls = 'h-full grid grid-cols-2 grid-rows-2 gap-1 p-1';
                            else if (participantCount >= 5) gridCls = 'h-full grid grid-cols-3 grid-rows-2 gap-1 p-1';
                            return (
                              <div className={gridCls} data-testid="game-video-mesh">
                                {/* Self tile (host or co-host) */}
                                <div className="relative rounded overflow-hidden" data-testid="game-self-tile">
                                  {!isPreviewMode && useAgora && sharedAgoraTracks?.videoTrack ? (
                                    <AgoraVideoPlayer
                                      videoTrack={sharedAgoraTracks.videoTrack}
                                      audioTrack={sharedAgoraTracks.audioTrack}
                                    />
                                  ) : (
                                    <video id="host-video-game" autoPlay playsInline muted className="w-full h-full object-cover bg-black" />
                                  )}
                                  <div className="absolute top-1 left-1 bg-blue-600/90 px-2 py-1 rounded text-xs text-white font-bold z-10">
                                    👑 {user?.name}
                                  </div>
                                </div>
                                {/* Remote tiles — live Agora mesh */}
                                {remoteTiles.slice(0, 5).map((tile, idx) => (
                                  <div key={`game-remote-${tile.uid}`} className="relative rounded overflow-hidden" data-testid={`game-remote-tile-${tile.uid}`}>
                                    {tile.videoTrack ? (
                                      <AgoraVideoPlayer
                                        videoTrack={tile.videoTrack}
                                        audioTrack={tile.audioTrack}
                                      />
                                    ) : (
                                      <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                                        <div className="text-white text-center">
                                          <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-1">
                                            <span className="text-lg">👤</span>
                                          </div>
                                          <p className="font-bold text-xs">{tile.identity?.name || 'Connecting…'}</p>
                                        </div>
                                      </div>
                                    )}
                                    <div className="absolute top-1 right-1 bg-red-600/90 px-2 py-1 rounded text-xs text-white font-bold z-10">
                                      P{idx + 2}
                                    </div>
                                    <div className="absolute bottom-1 left-1 bg-black/70 px-2 py-1 rounded text-[10px] text-white truncate max-w-[80%] z-10">
                                      {tile.identity?.name || 'Connecting…'}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                        
                        {/* Hand moved to the card-game BAND below the video (Feb 2026) — broadcasters stay fully visible. */}
                        
                        {/* Bidding Modal - Only shows for hands 2+ (hand 1 is auto blind bid) */}
                        {gameState?.phase === 'bidding' && gameState?.currentPlayer === gameState?.myPosition && gameState?.handNumber > 1 && (
                          /* FIXED to the viewport (Feb 2026) — the old
                             absolute-in-overlay version centered inside the
                             short video container and got clipped on phones. */
                          <div className="fixed inset-x-0 top-2 z-[300] flex justify-center pointer-events-none p-2">
                            <div className="bg-white rounded-xl p-4 max-w-md w-full max-h-[70vh] overflow-y-auto pointer-events-auto shadow-2xl ring-2 ring-emerald-500">
                              <h2 className="text-xl font-bold text-center mb-4">Place Your Bid - Hand {gameState?.handNumber}</h2>
                              <p className="text-center text-gray-600 mb-2">How many books will you take?</p>
                              <p className="text-center text-sm text-emerald-600 mb-4">🔴 Live viewers are watching!</p>
                              <div className="grid grid-cols-7 gap-2 mb-6">
                                {[0, 1, 2, 3, 4, 5, 6].map(bid => (
                                  <Button
                                    key={bid}
                                    onClick={() => setCurrentBid(bid)}
                                    variant={currentBid === bid ? "default" : "outline"}
                                    className="aspect-square"
                                  >
                                    {bid}
                                  </Button>
                                ))}
                              </div>
                              <div className="grid grid-cols-7 gap-2 mb-6">
                                {[7, 8, 9, 10, 11, 12, 13].map(bid => (
                                  <Button
                                    key={bid}
                                    onClick={() => setCurrentBid(bid)}
                                    variant={currentBid === bid ? "default" : "outline"}
                                    className="aspect-square"
                                  >
                                    {bid}
                                  </Button>
                                ))}
                              </div>
                              <div className="text-center space-y-2">
                                <Button
                                  onClick={() => {
                                    sendGameMessage({
                                      type: 'submit_bid',
                                      bid: currentBid,
                                      is_blind: false
                                    });
                                    setSpadesHandRevealed(true);
                                  }}
                                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                                >
                                  Confirm Bid: {currentBid} {currentBid === 1 ? 'Book' : 'Books'}
                                </Button>
                                {/* BLIND (Feb 2026): only offered when YOUR
                                    TEAM trails by 100+ AND your cards are
                                    still face-down (NY rules: blind is
                                    decided before looking). Min blind = 6. */}
                                {(() => {
                                  const myTeam = (gameState?.myPosition ?? 0) % 2;
                                  const scores = gameState?.matchScores || [0, 0];
                                  const blindEligible = scores[myTeam] <= scores[1 - myTeam] - 100;
                                  return blindEligible && !spadesHandRevealed && currentBid >= 6 && (
                                    <Button
                                      onClick={() => {
                                        sendGameMessage({
                                          type: 'submit_bid',
                                          bid: currentBid,
                                          is_blind: true
                                        });
                                        setSpadesHandRevealed(true);
                                      }}
                                      className="w-full bg-yellow-600 hover:bg-yellow-700"
                                    >
                                      🙈 Bid BLIND {currentBid}! (2x points)
                                    </Button>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Waiting notification moved to useEffect toast - no longer blocking video */}
                    {/* The waiting status is now shown as a toast notification when invites are sent */}
                    
                    {/* Old isInGame overlay removed - using inStreamGame system only */}
                    
                    {/* Stream Overlay */}
                    <div className="absolute inset-0 pointer-events-none">
                      {/* Jittery random eye-icon viewer count REMOVED (Feb 2026). Single stable count lives in the header (displayViewers). */}
                      
                      {/* Stream Title - Hidden during in-stream game */}
                      {!inStreamGame && (
                        <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-10">
                          <div className="bg-black/70 backdrop-blur-sm rounded-lg px-6 py-2 text-center">
                            <h3 className="text-white font-bold text-xl">{isInGame ? 'Live Spades Game' : streamTitle}</h3>
                          </div>
                        </div>
                      )}

                      {/* Chat removed - using comments overlay only */}

                      {/* Bottom Right Buttons — webrtc.isStreaming is FALSE in
                          the LiveKit era ("WebRTC stopped - LiveKit taking
                          over"), which hid this stack (incl. the Invite
                          button) for live/linked broadcasters. A linked live
                          must always be able to add another broadcaster
                          (3-way → 4-way) — capacity is enforced downstream. */}
                      {(webrtc.isStreaming || isPreviewMode || currentStream || (LIVEKIT_LINKS && linkRoomInfo)) && (
                        <div className="absolute bottom-6 right-6 flex flex-col gap-3 pointer-events-auto">
                          {/* Invite Streamer to Go Live Together (Two People Icon) */}
                          {!isPreviewMode && (
                            <Button
                              onClick={() => setShowInviteModal(true)}
                              data-testid="live-players-btn"
                              className="bg-purple-600/80 hover:bg-purple-700 text-white backdrop-blur-sm rounded-full p-3 shadow-lg border border-purple-500/30"
                              size="sm"
                              title="Invite streamer to go live side-by-side"
                            >
                              <Users className="h-5 w-5" />
                            </Button>
                          )}
                          
                          {/* Gift Button */}
                          {!isPreviewMode && (
                            <Button
                              onClick={() => setShowGiftModal(true)}
                              data-testid="live-gift-btn"
                              className="bg-pink-600/80 hover:bg-pink-700 text-white backdrop-blur-sm rounded-full p-3 shadow-lg border border-pink-500/30"
                              size="sm"
                              title="Send a gift"
                            >
                              <Gift className="h-5 w-5" />
                            </Button>
                          )}
                          
                          {/* Recharge/Emerald Packages Button */}
                          {!isPreviewMode && (
                            <Button
                              onClick={() => {
                                // TODO: Open recharge modal instead of navigating
                                toast.info('Recharge feature coming soon!');
                              }}
                              className="bg-emerald-600/80 hover:bg-emerald-700 text-white backdrop-blur-sm rounded-full p-3 shadow-lg border border-emerald-500/30"
                              size="sm"
                              title="Recharge Emeralds"
                            >
                              <EmeraldIcon className="h-5 w-5" />
                            </Button>
                          )}
                          
                          {/* Reactions/Likes Button - Visible but disabled for streamers */}
                          {!isPreviewMode && (
                            <div className="relative">
                              <Button
                                onClick={() => {
                                  // This will only work for viewers (disabled for streamer)
                                  // Show flying heart animation
                                  const heartId = Date.now();
                                  const heartEl = document.createElement('div');
                                  heartEl.id = `heart-${heartId}`;
                                  heartEl.className = 'fixed text-4xl animate-float-heart pointer-events-none z-50';
                                  heartEl.style.bottom = '100px';
                                  heartEl.style.right = '30px';
                                  heartEl.innerHTML = '❤️';
                                  document.body.appendChild(heartEl);
                                  
                                  // Remove after animation completes
                                  setTimeout(() => {
                                    const el = document.getElementById(`heart-${heartId}`);
                                    if (el) el.remove();
                                  }, 2000);
                                }}
                                disabled={true} // Disabled for streamers
                                className="bg-red-600/80 hover:bg-red-700 text-white backdrop-blur-sm rounded-full p-3 shadow-lg border border-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                                size="sm"
                                title="Likes (viewers only)"
                              >
                                <Heart className="h-5 w-5" />
                              </Button>
                              {/* Share button removed - use "Share Live" in control bar below */}
                            </div>
                          )}
                        </div>
                      )}
                      <GiftStage />
                    </div>
                  </div>
                  );
                })()}

                {/* Part 3 (Feb 19, 2026): shared end overlay for C8 /
                    Checkers / 4iaR. Auto-returns to the live after 15s
                    (or via Back to Live / Rematch), which tears down
                    the game state on BOTH sides without unlinking. */}
                {lastGameEnd && (
                  <LinkedGameEndOverlay
                    winnerName={lastGameEnd.winnerName}
                    sessionWinsLine={(() => {
                      const players = lastGameEnd.players || [];
                      if (players.length < 2) return null;
                      return players.map((p) => {
                        const name = p === user?.id
                          ? (user?.name || 'You')
                          : ((linkPartners || []).find((x) => x.id === p)?.name || 'Guest');
                        return `${name} ${sessionWins[p] || 0}`;
                      }).join(' — ');
                    })()}
                    onBackToLive={() => {
                      // Clear every in-live game state on this side. The
                      // partner's side clears via the same mechanism when
                      // their overlay's countdown fires — or manually via
                      // their Back to Live button.
                      setInStreamGame(null);
                      setC8LiveState(null);
                      setCheckersLiveState(null);
                      setActiveLinkedGame(null);
                      setGameState(null);
                      setMyCards([]);
                      setLastGameEnd(null);
                    }}
                    onRematch={() => {
                      // Part 6 (Feb 19, 2026): open the linked-game entry
                      // modal PREFILLED — same gameType, last entry (editable),
                      // last C8 rules (editable), same opponent. From there the
                      // existing challenge pipeline runs unchanged.
                      try {
                        setLinkedGameType(lastGameEnd.gameType);
                        if (lastGameEnd.gameType === 'crazy_eights' && lastGameEnd.config?.rules) {
                          setC8Rules((r) => ({ ...r, ...lastGameEnd.config.rules }));
                        }
                      } catch {}
                      // Clear the game state so the video mesh returns to
                      // 2-up + the entry modal takes focus.
                      setInStreamGame(null);
                      setC8LiveState(null);
                      setCheckersLiveState(null);
                      setActiveLinkedGame(null);
                      setGameState(null);
                      setMyCards([]);
                      setLastGameEnd(null);
                      setShowLinkedGameWagerModal(true);
                    }}
                  />
                )}



                {/* Part 1a (Feb 19, 2026): DESKTOP card-game BAND. Sibling
                    to the video area — a dedicated band below the video
                    where the C8 board and Spades hand render, so the
                    broadcasters stay fully visible. Mobile keeps its
                    existing top-strip layout untouched. */}
                {inStreamGame && (gameState || (isDesktopViewport && c8LiveState)) && (
                  <div
                    className="w-full bg-slate-900/95 border-t border-slate-700 px-4 py-2"
                    data-testid="card-game-band"
                    // Spades band hugs its content (banner + fan ≈ 10rem) so
                    // it never dwarfs the video boxes; C8 keeps its 13rem.
                    style={c8LiveState && !gameState ? { minHeight: '13rem' } : undefined}
                  >
                    {c8LiveState && !gameState && (
                      <CrazyEightsBoard
                        state={c8LiveState}
                        meId={user?.id}
                        timers={{}}
                        fanned
                        sessionWins={sessionWins}
                        onPlay={(idx, chosen_suit) => {
                          console.log('[C8] play click', { idx, chosen_suit });
                          sendGameMessage({ type: 'c8_play_card', card_index: idx, chosen_suit });
                        }}
                        onDraw={() => {
                          console.log('[C8] draw click');
                          sendGameMessage({ type: 'c8_draw' });
                        }}
                        onPass={() => {
                          console.log('[C8] pass click');
                          sendGameMessage({ type: 'c8_pass' });
                        }}
                        onDeclareLastCard={() => sendGameMessage({ type: 'c8_declare_last_card' })}
                        onForfeit={() => {
                          console.log('[Forfeit] click (band)', { gid: inStreamGame });
                          if (!window.confirm('Forfeit this match? Your opponent wins the prize.')) return;
                          sendGameMessage({ type: 'c8_forfeit' });
                        }}
                        playerNames={Object.fromEntries(
                          (c8LiveState.players || []).map((p) => [
                            p,
                            p === user?.id
                              ? (user?.name || 'You')
                              : ((linkPartners || []).find((x) => x.id === p)?.name || 'Guest'),
                          ])
                        )}
                      />
                    )}
                    {gameState && !c8LiveState && (
                      <div data-testid="spades-hand-band">
                        {/* Turn banner — always names the player whose turn
                            it is, and shows YOUR TURN to that player. */}
                        {(() => {
                          const myTurn = gameState.currentPlayer === gameState.myPosition;
                          const turnName = (gameState.players || []).find(
                            (p) => p.position === gameState.currentPlayer,
                          )?.name || `Player ${(gameState.currentPlayer ?? 0) + 1}`;
                          return (
                            <div className="text-center mb-1">
                              {gameState.phase === 'bidding' ? (
                                <span className="text-yellow-400 text-sm font-semibold animate-pulse">
                                  ⏳ Hand {gameState.handNumber} Bidding — {myTurn ? '🎯 YOUR TURN TO BID!' : `Waiting for ${turnName} to bid…`}
                                </span>
                              ) : myTurn ? (
                                <span className="text-emerald-400 text-base font-bold animate-pulse">
                                  🎯 YOUR TURN — Click a card to play!
                                </span>
                              ) : (
                                <span className="text-gray-300 text-sm">
                                  Hand {gameState.handNumber ?? 1} • Waiting for <span className="font-semibold text-white">{turnName}</span> to play…
                                </span>
                              )}
                            </div>
                          );
                        })()}
                        {myCards.length > 0 && !spadesHandRevealed ? (
                          /* FACE-DOWN hand (Feb 2026): team is 100+ behind —
                             blind window. Cards stay hidden until they bid
                             blind (on their turn) or peek. */
                          <div className="text-center py-6" data-testid="spades-blind-window">
                            <p className="text-yellow-300 text-sm font-semibold mb-1">
                              🙈 Your team is down 100+ — your new cards are face-down.
                            </p>
                            <p className="text-gray-300 text-xs mb-3">
                              Bid BLIND on your turn for 2x points, or view your cards now (forfeits blind).
                            </p>
                            <button
                              onClick={() => setSpadesHandRevealed(true)}
                              data-testid="spades-view-cards-btn"
                              className="bg-slate-600 hover:bg-slate-500 text-white text-sm font-bold py-2 px-5 rounded-lg"
                            >
                              View Cards
                            </button>
                          </div>
                        ) : myCards.length > 0 ? (
                          <FannedHand
                            inline
                            cards={myCards}
                            disabled={
                              !(
                                (['playing', 'PLAYING'].includes(gameState?.phase) ||
                                  ['playing', 'PLAYING'].includes(gameState?.gameState) ||
                                  gameState?.handNumber === 1) &&
                                gameState?.currentPlayer === gameState?.myPosition
                              )
                            }
                            cardKey={(_c, i) => i}
                            onPlay={(card) => {
                              const index = myCards.findIndex(
                                (c) => c.suit === card.suit && c.value === card.value,
                              );
                              if (index < 0) return;
                              sendGameMessage({
                                type: 'play_card',
                                card_index: index,
                                card,
                                seat: gameState?.myPosition || 0,
                              });
                              setMyCards((prev) => prev.filter((_, i) => i !== index));
                              toast.success(`Played ${card.value} of ${card.suit}`);
                            }}
                            renderCard={(card) => {
                              const isTrump = card.suit === 'diamonds' && card.value === '2';
                              const isMyTurnSp = gameState?.currentPlayer === gameState?.myPosition;
                              return (
                                <PlayingCard
                                  card={card}
                                  playable={true}
                                  notMyTurn={!isMyTurnSp}
                                  isTrump={isTrump}
                                  onPlay={() => {}}
                                />
                              );
                            }}
                          />
                        ) : (
                          <div className="text-center text-white text-sm py-4">
                            <p>⏳ {invitedPlayers?.length === 3
                              ? 'Waiting for all players to connect...'
                              : 'Waiting for cards to be dealt...'
                            }</p>
                            {/* HOST-side Cancel button while waiting for accepts. */}
                            {spadesInvitePending && spadesInvitePending.game_id === inStreamGame && (
                              <div className="mt-3 flex flex-col items-center gap-2">
                                <p className="text-xs text-yellow-300">
                                  {(spadesInvitePending.joined_ids || []).length} / {(spadesInvitePending.invitee_ids || []).length} accepted • auto-cancels in 30s
                                </p>
                                <button
                                  onClick={cancelPendingSpadesGame}
                                  data-testid="spades-cancel-game-btn"
                                  className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2 px-4 rounded-lg transition-colors"
                                >
                                  Cancel Game
                                </button>
                              </div>
                            )}
                            {/* INVITEE-side Forfeit button once they've joined the game. */}
                            {!spadesInvitePending && inStreamGame && (
                              <div className="mt-3">
                                <button
                                  onClick={() => { console.log('[Forfeit-Spades] click', { gid: inStreamGame }); forfeitSpadesGame(); }}
                                  data-testid="spades-forfeit-btn"
                                  className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2 px-4 rounded-lg transition-colors shadow-lg"
                                >
                                  Forfeit / Leave Game
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Stream Controls - Hidden when any modal is open to prevent blocking modal content */}
                {(isPreviewMode || webrtc.isStreaming || currentStream || useAgora) && !showWagerModal && !showStartGameModal && !showInviteModal && (
                  <div className="p-4 bg-gray-900 border-t border-gray-800 relative z-[40]">
                    {/* Preview Mode: Show title input and Go Live Now button */}
                    {isPreviewMode ? (
                      <div className="space-y-4">
                        <div className="max-w-md mx-auto">
                          <label className="block text-sm font-medium text-gray-300 mb-2">Stream Title</label>
                          <Input
                            value={streamTitle}
                            onChange={(e) => setStreamTitle(e.target.value)}
                            placeholder="Enter stream title..."
                            className="w-full bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        {/* Hidden cover input — triggered by the avatar square in the header */}
                        <input
                          ref={coverInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleCoverSelect}
                          data-testid="cover-photo-input"
                        />
                        <div className="flex flex-wrap gap-4 justify-center">
                          <Button 
                            onClick={() => {
                              if (PRIVATE_BETA_MODE) {
                                setShowBetaModal(true);
                              } else {
                                goLive();
                              }
                            }}
                            size="lg" 
                            disabled={isGoingLive}
                            className={`${PRIVATE_BETA_MODE ? 'bg-gray-500 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600 disabled:opacity-80 disabled:cursor-wait'} text-white px-12 py-6 text-lg font-bold`}
                            data-testid="go-live-now-btn"
                          >
                            {isGoingLive ? (
                              <>
                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                Going Live…
                              </>
                            ) : (
                              <>
                                <Play className="mr-2 h-5 w-5" />
                                Go Live Now
                                {PRIVATE_BETA_MODE && <Lock className="ml-2 h-5 w-5" />}
                              </>
                            )}
                          </Button>
                          <Button 
                            onClick={async () => {
                              console.log('Cancelling preview and returning to discover');
                              webrtc.stopStream();
                              try { await fullStreamCleanup('preview_cancelled'); } catch(e) {}
                              if (useLiveKit) { setLivekitToken(null); setLivekitRoomName(null); setLivekitUrl(null); setUseLiveKit(false); }
                              setIsPreviewMode(false);
                              setCurrentStream(null);
                              setInvitedPlayers([]);
                              setStreamTitle('');
                              setCoverUrl('');
                              navigate('/discover');
                            }} 
                            variant="outline"
                            className="border-gray-600 text-gray-300"
                          >
                            <VideoOff className="mr-2 h-4 w-4" />
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* Live Mode: Show game controls - pointer-events-auto ensures clickability */
                      <div className="flex flex-wrap gap-3 justify-center pointer-events-auto">
                        {/* Feb 20, 2026: single generic branch — the Play
                            Together picker renders for every linked count.
                            Spades partner selection lives inside the picker's
                            table modal, not in a separate hardcoded button. */}
                        {!inStreamGame ? (
                          <Button 
                            onClick={() => setShowGameOptionsModal(true)}
                            variant={lookingToPlay ? "default" : "outline"}
                            className={`pointer-events-auto ${lookingToPlay ? "bg-amber-500 hover:bg-amber-600 text-white" : "border-gray-600 text-gray-300"}`}
                            data-testid="looking-to-play-button"
                          >
                            <Gamepad2 className="mr-2 h-4 w-4" />
                            {connectedCoHosts.length > 0 || invitedPlayers.length > 0 
                              ? "Start Game Together" 
                              : lookingToPlay 
                                ? "Looking for Game" 
                                : "Look for Game"}
                          </Button>
                        ) : null}
                        
                        {/* Mute/Unmute Mic Button - Always visible for host */}
                        <Button 
                          onClick={toggleMicMute}
                          variant="outline"
                          data-testid="live-mute-btn"
                          className={`pointer-events-auto ${isMicMuted ? "bg-red-600 hover:bg-red-700 text-white border-red-500" : "border-gray-600 text-gray-300 hover:bg-gray-800"}`}
                        >
                          {isMicMuted ? (
                            <>
                              <MicOff className="mr-2 h-4 w-4" />
                              Unmute
                            </>
                          ) : (
                            <>
                              <Mic className="mr-2 h-4 w-4" />
                              Mute
                            </>
                          )}
                        </Button>
                        
                        {/* Screenshare Toggle Button - Only for LiveKit streams */}
                        {useLiveKit && (
                          <Button 
                            onClick={toggleScreenShare}
                            variant="outline"
                            className={`pointer-events-auto ${isScreenSharing ? "bg-blue-600 hover:bg-blue-700 text-white border-blue-500" : "border-gray-600 text-gray-300 hover:bg-gray-800"}`}
                            data-testid="screenshare-btn"
                          >
                            {isScreenSharing ? (
                              <>
                                <Camera className="mr-2 h-4 w-4" />
                                Camera
                              </>
                            ) : (
                              <>
                                <Monitor className="mr-2 h-4 w-4" />
                                Screen
                              </>
                            )}
                          </Button>
                        )}
                        
                        {/* Share Stream Button - Full options modal */}
                        <Button 
                          onClick={() => setShowShareStreamModal(true)}
                          variant="outline"
                          className="pointer-events-auto border-emerald-600 text-emerald-400 hover:bg-emerald-900/30"
                          data-testid="broadcaster-share-btn"
                        >
                          <Share2 className="mr-2 h-4 w-4" />
                          Share Live
                        </Button>
                        
                        {/* Messages while live */}
                        <Button 
                          onClick={() => setShowMessagesModal(true)}
                          variant="outline"
                          className="pointer-events-auto border-blue-600 text-blue-400 hover:bg-blue-900/30"
                          data-testid="live-messages-btn"
                        >
                          <Mail className="mr-2 h-4 w-4" />
                          Messages
                        </Button>
                        
                        {/* Invite — ALWAYS reachable while live (Feb 2026).
                            The floating bottom-right stack proved unreliable
                            on desktop, which left no way to add/re-add a
                            broadcaster (e.g. bringing back someone who
                            dropped). Works solo AND linked, up to 6 total. */}
                        {LIVEKIT_LINKS && currentStream && (linkPartners || []).length < maxLinkPartners && (
                          <Button
                            onClick={() => setShowInviteModal(true)}
                            variant="outline"
                            className="pointer-events-auto border-purple-600 text-purple-400 hover:bg-purple-900/30"
                            data-testid="invite-btn-controls"
                          >
                            <Users className="mr-2 h-4 w-4" />
                            Invite
                          </Button>
                        )}

                        {/* End Link — visible to EVERY linked broadcaster
                            (Feb 2026). Leaves ONLY me: the backend keeps
                            the others linked when 2+ remain (4-way → 3-way).
                            Hidden during a game — forfeit first. */}
                        {LIVEKIT_LINKS && linkRoomInfo && (linkPartners || []).length > 0 && !inStreamGame && !activeLinkedGame && !checkersLiveState && !c8LiveState && (
                          <Button
                            onClick={() => {
                              const targetId = invitedPlayers[0]?.id || connectedCoHosts[0]?.id
                                || (allLinkedUsers || []).find((u) => u !== user?.id);
                              if (targetId) disconnectCoHost(targetId);
                            }}
                            variant="outline"
                            className="pointer-events-auto border-red-600 text-red-400 hover:bg-red-900/30"
                            data-testid="end-link-btn-controls"
                          >
                            <X className="mr-2 h-4 w-4" />
                            End Link
                          </Button>
                        )}

                        <Button onClick={stopStream} variant="destructive" data-testid="live-end-stream-btn" className="pointer-events-auto">
                          <PhoneOff className="mr-2 h-4 w-4" />
                          End Stream
                        </Button>
                      </div>
                    )}

                    {/* Hide "Looking for opponents" when all 3 players have been invited */}
                    {lookingToPlay && !isPreviewMode && invitedPlayers.length < maxLinkPartners && (
                      <div className="mt-3 bg-amber-900/30 border border-amber-700/50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-amber-400">
                          <Trophy className="h-4 w-4 animate-pulse" />
                          <span className="font-semibold text-sm">Looking for opponents...</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Bigo-Style Live Chat - Below Buttons */}
                {!isPreviewMode && currentStream && (
                  <div className="bg-gradient-to-t from-black via-gray-900/95 to-gray-900/80 p-3 pointer-events-auto relative z-50">
                    {/* Chat Messages - Scrollable */}
                    <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto" id="stream-chat-messages">
                      {streamChatMessages.length === 0 ? (
                        <div className="text-center py-4">
                          <p className="text-gray-500 text-sm">💬 Chat with your viewers!</p>
                          <p className="text-gray-600 text-xs">Messages will appear here</p>
                        </div>
                      ) : (
                        streamChatMessages.map((msg) => (
                          <div 
                            key={msg.id} 
                            className={`flex items-start gap-2 py-1 px-2 rounded-lg backdrop-blur-sm ${
                              msg.isStreamer 
                                ? 'bg-gradient-to-r from-emerald-600/40 to-emerald-500/20' 
                                : msg.isGift 
                                ? 'bg-gradient-to-r from-pink-600/40 to-purple-500/30' 
                                : msg.isSystem 
                                ? 'bg-gray-700/30' 
                                : 'bg-white/10'
                            }`}
                          >
                            {/* Avatar - Use profile picture if available */}
                            {msg.profilePic ? (
                              <img 
                                src={msg.profilePic} 
                                alt={msg.username}
                                className={`w-7 h-7 rounded-full object-cover flex-shrink-0 ${
                                  msg.isStreamer ? 'ring-2 ring-emerald-400' :
                                  msg.isBot ? 'ring-2 ring-purple-400' :
                                  ''
                                }`}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${
                                msg.isStreamer ? 'bg-gradient-to-br from-emerald-500 to-teal-600 ring-2 ring-emerald-400' :
                                msg.isGift ? 'bg-gradient-to-br from-pink-500 to-purple-600 ring-2 ring-pink-400' :
                                msg.isSystem ? 'bg-gray-600' :
                                msg.isBot ? 'bg-gradient-to-br from-purple-500 to-blue-600' :
                                'bg-gradient-to-br from-blue-500 to-indigo-600'
                              }`}>
                                {msg.isGift ? '🎁' : msg.isSystem ? '📢' : msg.avatar || msg.username?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                            )}
                            
                            {/* Message Content */}
                            <div className="flex-1 min-w-0 relative">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {/* Username with badge - Click to show options */}
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const rect = e.target.getBoundingClientRect();
                                    setShowUserActionPopup({
                                      username: msg.username,
                                      userId: msg.userId || msg.user_id,
                                      profilePic: msg.profilePic,
                                      isBot: msg.isBot,
                                      isStreamer: msg.isStreamer,
                                      messageId: msg.id,
                                      messageText: msg.message,
                                      x: rect.left,
                                      y: rect.bottom + 5
                                    });
                                  }}
                                  className={`text-xs font-bold hover:underline cursor-pointer ${
                                    msg.isStreamer ? 'text-emerald-400' : 
                                    msg.isGift ? 'text-pink-400' : 
                                    msg.isSystem ? 'text-gray-400' :
                                    'text-blue-400'
                                  }`}
                                >
                                  {msg.username}
                                </button>
                                {/* Sapphire Badge */}
                                {msg.badge_type === 'sapphire' && (
                                  <SapphireBadgeIcon className="w-4 h-4" />
                                )}
                                {/* Canary Badge */}
                                {(msg.badge_type === 'canary' || msg.badge_type === 'verified') && (
                                  <CanaryBadgeIcon className="w-4 h-4" />
                                )}
                                {msg.isStreamer && (
                                  <span className="px-1.5 py-0.5 bg-emerald-500 text-white text-[10px] rounded font-bold">HOST</span>
                                )}
                                {msg.isGift && (
                                  <span className="px-1.5 py-0.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-[10px] rounded font-bold">GIFT</span>
                                )}
                              </div>
                              <p className="text-white/90 text-sm break-words leading-tight">
                                {msg.isGift && msg.giftIcon && <span className="mr-1">{msg.giftIcon}</span>}
                                {msg.message}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* User Action Popup - View Profile or Mention */}
                    {showUserActionPopup && (
                      <div 
                        className="fixed inset-0 z-[100]"
                        onClick={() => setShowUserActionPopup(null)}
                      >
                        <div 
                          className="absolute bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden min-w-[160px]"
                          style={{ 
                            left: Math.min(showUserActionPopup.x, window.innerWidth - 180), 
                            top: showUserActionPopup.y 
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* User Info Header */}
                          <div className="p-3 border-b border-gray-700 flex items-center gap-2">
                            {showUserActionPopup.profilePic ? (
                              <img 
                                src={showUserActionPopup.profilePic} 
                                alt={showUserActionPopup.username}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                                {showUserActionPopup.username?.charAt(0)?.toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="text-white text-sm font-semibold">{showUserActionPopup.username}</p>
                              {showUserActionPopup.isBot && <p className="text-purple-400 text-xs">Viewer</p>}
                              {showUserActionPopup.isStreamer && <p className="text-emerald-400 text-xs">Host</p>}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="py-1">
                            <button
                              onClick={() => {
                                // Find bot profile if it's a bot
                                if (showUserActionPopup.isBot) {
                                  const botProfile = chatBotProfiles.find(b => b.name === showUserActionPopup.username);
                                  if (botProfile) {
                                    setShowProfileModal({
                                      ...botProfile,
                                      isBot: true
                                    });
                                  }
                                } else if (showUserActionPopup.userId) {
                                  // Fetch real user profile
                                  api.get(`/users/${showUserActionPopup.userId}`)
                                    .then(res => {
                                      setShowProfileModal({
                                        ...res.data,
                                        isBot: false
                                      });
                                    })
                                    .catch(() => {
                                      toast.error('Could not load profile');
                                    });
                                } else if (showUserActionPopup.isStreamer && currentStream?.user_id) {
                                  // Fetch the STREAMER's actual profile, not the viewer's
                                  api.get(`/users/${currentStream.user_id}`)
                                    .then(res => {
                                      setShowProfileModal({
                                        ...res.data,
                                        isBot: false,
                                        isStreamer: true
                                      });
                                    })
                                    .catch(() => toast.error('Could not load streamer profile'));
                                }
                                setShowUserActionPopup(null);
                              }}
                              className="w-full px-4 py-2 text-left text-white text-sm hover:bg-gray-700 flex items-center gap-2"
                            >
                              <User className="h-4 w-4 text-blue-400" />
                              View Profile
                            </button>
                            <button
                              onClick={() => {
                                setStreamChatInput(prev => prev + '@' + showUserActionPopup.username + ' ');
                                setShowUserActionPopup(null);
                              }}
                              className="w-full px-4 py-2 text-left text-white text-sm hover:bg-gray-700 flex items-center gap-2"
                            >
                              <MessageCircle className="h-4 w-4 text-emerald-400" />
                              Mention in Chat
                            </button>
                            
                            {/* Kick option - only show if current user is host/admin and target is not the host */}
                            {(streamAdminStatus?.is_owner || streamAdminStatus?.is_admin) && 
                             !showUserActionPopup.isStreamer && 
                             showUserActionPopup.userId && 
                             showUserActionPopup.userId !== user?.id && (
                              <>
                                <div className="border-t border-gray-700 my-1" />
                                <button
                                  onClick={async () => {
                                    try {
                                      const currentStreamId = currentStream?.stream_id || location.pathname.split('/').pop();
                                      await api.post(`/streams/${currentStreamId}/kick/${showUserActionPopup.userId}`);
                                      toast.success(`${showUserActionPopup.username} has been kicked from the stream`);
                                      setShowUserActionPopup(null);
                                    } catch (error) {
                                      toast.error('Failed to kick user');
                                    }
                                  }}
                                  className="w-full px-4 py-2 text-left text-red-400 text-sm hover:bg-gray-700 flex items-center gap-2"
                                >
                                  <Ban className="h-4 w-4" />
                                  Kick from Stream
                                </button>
                              </>
                            )}
                            
                            {/* Round 13: report / block from the chat popup */}
                            {showUserActionPopup.userId && showUserActionPopup.userId !== user?.id && !showUserActionPopup.isBot && (
                              <>
                                <button
                                  onClick={() => { const p = showUserActionPopup; setShowUserActionPopup(null); liveCtx.openReport?.({ targetType: p.messageId && typeof p.messageId === 'string' ? 'chat_message' : 'user', targetId: p.messageId && typeof p.messageId === 'string' ? p.messageId : p.userId, targetUserId: p.userId, targetName: p.username, snapshot: p.messageText, streamId: currentStream?.stream_id }); }}
                                  className="w-full px-4 py-2 text-left text-amber-300 text-sm hover:bg-gray-700 flex items-center gap-2"
                                  data-testid="chat-report-btn"
                                >
                                  🚩 Report
                                </button>
                                <button
                                  onClick={async () => { const p = showUserActionPopup; setShowUserActionPopup(null); if (!window.confirm(`Block ${p.username}?`)) return; await liveCtx.blockUser?.(p.userId, p.username); }}
                                  className="w-full px-4 py-2 text-left text-red-400 text-sm hover:bg-gray-700 flex items-center gap-2"
                                  data-testid="chat-block-btn"
                                >
                                  ⛔ Block
                                </button>
                              </>
                            )}
                            {/* Set as Admin option - only show if current user is the stream owner */}
                            {streamAdminStatus?.is_owner && 
                             !showUserActionPopup.isStreamer && 
                             !showUserActionPopup.isBot &&
                             showUserActionPopup.userId && 
                             showUserActionPopup.userId !== user?.id && (
                              <button
                                onClick={async () => {
                                  try {
                                    const currentStreamId = currentStream?.stream_id || location.pathname.split('/').pop();
                                    // Check if already admin
                                    const adminCheck = await api.get(`/streams/${currentStreamId}/admins`);
                                    const isAlreadyAdmin = adminCheck.data.admins?.some(a => a.user_id === showUserActionPopup.userId);
                                    
                                    if (isAlreadyAdmin) {
                                      // Remove admin
                                      await api.delete(`/streams/${currentStreamId}/admin/${showUserActionPopup.userId}`);
                                      toast.success(`${showUserActionPopup.username} is no longer a moderator`);
                                    } else {
                                      // Add admin
                                      await api.post(`/streams/${currentStreamId}/admin/${showUserActionPopup.userId}`);
                                      toast.success(`${showUserActionPopup.username} is now a moderator`);
                                    }
                                    setShowUserActionPopup(null);
                                  } catch (error) {
                                    toast.error('Failed to update moderator status');
                                  }
                                }}
                                className="w-full px-4 py-2 text-left text-amber-400 text-sm hover:bg-gray-700 flex items-center gap-2"
                              >
                                <Shield className="h-4 w-4" />
                                Set as Moderator
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Profile Modal - Shows profile without leaving stream */}
                    {showProfileModal && (
                      <Dialog open={!!showProfileModal} onOpenChange={() => setShowProfileModal(null)}>
                        <DialogContent className="sm:max-w-md bg-gray-900 border-gray-800 text-white z-[200]">
                          <DialogHeader>
                            <DialogTitle className="sr-only">User Profile</DialogTitle>
                          </DialogHeader>
                          
                          {/* Profile Header with Cover */}
                          <div className="relative">
                            <div className="h-24 bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-500 rounded-t-lg" />
                            <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2">
                              <div className="relative">
                                <img 
                                  src={showProfileModal.picture || showProfileModal.profilePic || showProfileModal.profilePhoto || `https://ui-avatars.com/api/?name=${showProfileModal.name}&background=random`}
                                  alt={showProfileModal.name}
                                  className="w-24 h-24 rounded-full border-4 border-gray-900 object-cover"
                                />
                                {showProfileModal.isStreamer && (
                                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                                    HOST
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Profile Info */}
                          <div className="pt-14 px-4 pb-4 text-center">
                            <h2 className="text-xl font-bold text-white">{showProfileModal.name}</h2>
                            {showProfileModal.location && (
                              <p className="text-gray-400 text-sm flex items-center justify-center gap-1 mt-1">
                                <MapPin className="h-3 w-3" />
                                {showProfileModal.location}
                              </p>
                            )}
                            {showProfileModal.bio && (
                              <p className="text-gray-300 text-sm mt-3 px-4">{showProfileModal.bio}</p>
                            )}
                            
                            {/* Stats */}
                            <div className="grid grid-cols-4 gap-2 mt-6 bg-gray-800/50 rounded-lg p-4">
                              <div className="text-center">
                                <p className="text-lg font-bold text-white">{showProfileModal.followers?.toLocaleString() || 0}</p>
                                <p className="text-xs text-gray-400">Followers</p>
                              </div>
                              <div className="text-center">
                                <p className="text-lg font-bold text-white">{showProfileModal.following?.toLocaleString() || 0}</p>
                                <p className="text-xs text-gray-400">Following</p>
                              </div>
                              <div className="text-center">
                                <p className="text-lg font-bold text-emerald-400">{showProfileModal.gamesWon?.toLocaleString() || 0}</p>
                                <p className="text-xs text-gray-400">Wins</p>
                              </div>
                              <div className="text-center">
                                <p className="text-lg font-bold text-yellow-400">{showProfileModal.emeralds?.toLocaleString() || 0}</p>
                                <p className="text-xs text-gray-400">Emeralds</p>
                              </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex flex-col gap-3 mt-6">
                              <div className="flex gap-3">
                                {showProfileModal.id && showProfileModal.id !== user?.id && !showProfileModal.isBot && (
                                  <Button 
                                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                    data-testid="live-follow-btn"
                                    onClick={async () => {
                                      try {
                                        await api.post(`/users/${showProfileModal.id}/follow`);
                                        toast.success(`Following ${showProfileModal.name}!`);
                                      } catch (e) {
                                        if (e.response?.status === 400) {
                                          // Already following — unfollow
                                          try {
                                            await api.post(`/users/${showProfileModal.id}/unfollow`);
                                            toast.info(`Unfollowed ${showProfileModal.name}`);
                                          } catch (e2) {
                                            toast.error('Action failed');
                                          }
                                        } else {
                                          toast.error('Action failed');
                                        }
                                      }
                                    }}
                                  >
                                    <UserPlus className="h-4 w-4 mr-2" />
                                    Follow
                                  </Button>
                                )}
                                <Button 
                                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                                  onClick={() => {
                                    setStreamChatInput(`@${showProfileModal.name} `);
                                    setShowProfileModal(null);
                                  }}
                                >
                                  <MessageCircle className="h-4 w-4 mr-2" />
                                  Message
                                </Button>
                              </div>
                              
                              {/* View Full Profile - Only for viewers (not for host who is live) */}
                              {!currentStream && !showProfileModal.isBot && showProfileModal.id && (
                                <Button 
                                  variant="outline"
                                  className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
                                  onClick={() => {
                                    setShowProfileModal(null);
                                    navigate(`/profile/${showProfileModal.id}`);
                                  }}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Full Profile
                                </Button>
                              )}
                              
                              {/* Message for host - can't leave while live */}
                              {currentStream && !showProfileModal.isBot && showProfileModal.id && (
                                <p className="text-center text-gray-500 text-xs">
                                  View full profile after your stream ends
                                </p>
                              )}
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                    
                    {/* Quick Reactions Row */}
                    <div className="flex items-center gap-2 mb-2 overflow-x-auto pb-1">
                      {['👋 Hello', '🔥 Fire', '❤️', '😂', '👏', '🎉'].map((reaction, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setStreamChatInput(reaction.includes(' ') ? reaction.split(' ')[1] : reaction);
                            sendStreamChatMessage();
                          }}
                          className="flex-shrink-0 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-white text-xs font-medium transition-all hover:scale-105 active:scale-95"
                        >
                          {reaction}
                        </button>
                      ))}
                    </div>
                    
                    {/* Chat Input with @ Mentions */}
                    <div className="flex gap-2 items-center relative">
                      <div className="flex-1 relative">
                        {/* @ Mention Suggestions Popup */}
                        {showMentionSuggestions && (
                          <div className="absolute bottom-full mb-2 left-0 right-0 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden z-50">
                            {mentionSuggestions.map((user, idx) => (
                              <button
                                key={idx}
                                onClick={() => insertMention(user.username)}
                                className="w-full px-3 py-2 flex items-center gap-2 hover:bg-gray-700 transition-colors text-left"
                              >
                                {user.profilePic ? (
                                  <img 
                                    src={user.profilePic} 
                                    alt={user.username}
                                    className="w-6 h-6 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs">
                                    {user.username?.charAt(0)?.toUpperCase()}
                                  </div>
                                )}
                                <span className="text-white text-sm font-medium">{user.username}</span>
                                {user.isHost && <span className="text-xs bg-emerald-500 text-white px-1.5 py-0.5 rounded">HOST</span>}
                              </button>
                            ))}
                          </div>
                        )}
                        
                        <MentionBox
                          value={streamChatInput}
                          onChange={setStreamChatInput}
                          onSubmit={sendStreamChatMessage}
                          placeholder="Say hi to viewers... (@ to mention)"
                          api={api}
                          className="flex-1"
                        />
                      </div>
                      <Button 
                        size="sm"
                        className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-full px-4 h-10"
                        onClick={sendStreamChatMessage}
                        disabled={!streamChatInput.trim()}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-white hover:bg-white/10 rounded-full h-10 w-10 p-0"
                        onClick={() => setShowGiftModal(true)}
                      >
                        <Gift className="h-5 w-5 text-pink-400" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            {/* Minimized chat removed - using bottom chat only */}
          </div>

          {/* Sidebar - Hidden when live */}
          {isPreviewMode && (
            <div className="space-y-6">
              <LeaderboardWidget compact={true} showUserRank={true} />
            </div>
          )}
        </div>
      </div>

      {/* Game Selection Modal - Step 1: Choose Game */}
      <Dialog open={showGameOptionsModal} onOpenChange={setShowGameOptionsModal}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gamepad2 className="h-5 w-5" />
              {connectedCoHosts.length > 0 || invitedPlayers.length > 0 
                ? "Play Together" 
                : "Select a Game"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {connectedCoHosts.length > 0 || invitedPlayers.length > 0 ? (
              <p className="text-gray-600">
                You're linked with {connectedCoHosts[0]?.name || invitedPlayers[0]?.name}. Choose a game to play together!
              </p>
            ) : (
              <p className="text-gray-600">Choose which game you'd like to play.</p>
            )}
            
            <div className="space-y-3">
              {/* 4 In A Row - Great for 2 players! */}
              <Card 
                className="cursor-pointer hover:shadow-lg transition-all border-2 border-red-500 bg-red-50"
                onClick={() => {
                  setShowGameOptionsModal(false);
                  
                  // If linked, show wager modal for in-live game (NO NAVIGATION)
                  // Check both connectedCoHosts AND invitedPlayers for the partner
                  const hasLinkedPartner = connectedCoHosts.length > 0 || invitedPlayers.length > 0;
                  
                  if (hasLinkedPartner) {
                    // Get partner from either list (inviter or invitee perspective)
                    const partnerId = connectedCoHosts[0]?.id || invitedPlayers[0]?.id;
                    const partnerName = connectedCoHosts[0]?.name || invitedPlayers[0]?.name;
                    console.log('🎮 Starting linked 4 In A Row with:', partnerName, 'ID:', partnerId);
                    
                    if (!partnerId || !partnerName) {
                      toast.error('Could not identify your linked partner. Please try reconnecting.');
                      return;
                    }
                    
                    setLinkedGameOpponent({ id: partnerId, name: partnerName });
                    setLinkedGameType('four_in_a_row');
                    setLinkedGameWager(MIN_MATCH_ENTRY);
                    setShowLinkedGameWagerModal(true);
                  } else {
                    // Not linked - navigate to games page for solo/AI play
                    navigate('/games');
                  }
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                      <span className="text-2xl">🔴</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-red-800">4 In A Row</h3>
                      <p className="text-sm text-red-700">Classic connect-four strategy game</p>
                      <div className="flex gap-2 mt-1">
                        <Badge className="bg-red-600">2 Players</Badge>
                        {(connectedCoHosts.length > 0 || invitedPlayers.length > 0) && (
                          <Badge className="bg-green-600">Perfect for Linked!</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Spades - Available */}
              <Card 
                className="cursor-pointer hover:shadow-lg transition-all border-2 border-emerald-500 bg-emerald-50"
                onClick={() => {
                  // Part 3b (Feb 19, 2026): Play Together → Spades now
                  // opens the TABLE STEP first (seats + partner + invite
                  // banner) instead of the entry modal. In-live Spades
                  // requires exactly 4 human broadcasters — no bots.
                  setShowGameOptionsModal(false);
                  setSelectedGameType('spades');
                  setSelectedGameMode('invite');
                  setShowSpadesTableModal(true);
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center">
                      <span className="text-2xl">♠️</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-emerald-800">Spades</h3>
                      <p className="text-sm text-emerald-700">Classic book-taking card game</p>
                      <Badge className="mt-1 bg-emerald-600">4 Players</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Checkers - Available (1v1, linked-friendly).
                  Opens the IN-STREAM linked wager modal — no navigate
                  to /games (nav-lock: broadcaster stays LIVE). */}
              <Card
                className="cursor-pointer hover:shadow-lg transition-all border-2 border-slate-600 bg-slate-50"
                data-testid="game-select-checkers"
                onClick={() => {
                  const partnerId = connectedCoHosts[0]?.id || invitedPlayers[0]?.id;
                  const partnerName = connectedCoHosts[0]?.name || invitedPlayers[0]?.name;
                  if (!partnerId) {
                    toast.error('Link with a broadcaster to start a live Checkers match.');
                    return;
                  }
                  setShowGameOptionsModal(false);
                  setLinkedGameOpponent({ id: partnerId, name: partnerName || partnerId });
                  setLinkedGameType('checkers');
                  setLinkedGameWager(MIN_MATCH_ENTRY);
                  setShowLinkedGameWagerModal(true);
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center">
                      <span className="text-2xl">⛃</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-800">Checkers</h3>
                      <p className="text-sm text-slate-700">Classic strategy board game</p>
                      <div className="flex gap-2 mt-1">
                        <Badge className="bg-slate-700">2 Players</Badge>
                        {(connectedCoHosts.length > 0 || invitedPlayers.length > 0) && (
                          <Badge className="bg-green-600">Perfect for Linked!</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Crazy Eights - Available (2-player LIVE 1v1).
                  Opens the IN-STREAM linked wager modal (no navigate to
                  /games). Multi-player games still go through the
                  Games-page tile. */}
              <Card
                className="cursor-pointer hover:shadow-lg transition-all border-2 border-fuchsia-500 bg-fuchsia-50"
                data-testid="game-select-crazy-eights"
                onClick={() => {
                  // Crazy Eights — LINKED flow (Aug 18, 2026). Mirror the
                  // checkers path: seat the partner, use the shared linked
                  // wager modal (with a C8 House-Rules section), then invite
                  // via /c8/create.
                  const partnerId = connectedCoHosts[0]?.id || invitedPlayers[0]?.id;
                  const partnerName = connectedCoHosts[0]?.name || invitedPlayers[0]?.name;
                  if (!partnerId) {
                    toast.error('Link with a broadcaster to start a live Crazy Eights match.');
                    return;
                  }
                  setShowGameOptionsModal(false);
                  setLinkedGameOpponent({ id: partnerId, name: partnerName || partnerId });
                  setLinkedGameType('crazy_eights');
                  setLinkedGameWager(MIN_MATCH_ENTRY);
                  setShowLinkedGameWagerModal(true);
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-fuchsia-600 rounded-full flex items-center justify-center">
                      <span className="text-2xl">🃏</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-fuchsia-800">Crazy Eights</h3>
                      <p className="text-sm text-fuchsia-700">Fast, chaotic card-shedding game</p>
                      <Badge className="mt-1 bg-fuchsia-600">2–4 Players (Live)</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Round 53: tournaments are real — this card opens what's on (sign-up, check-in and the bracket live on the tournament's page) */}
              <Card className="cursor-pointer border-2 border-amber-300 bg-amber-50/60 hover:bg-amber-50" onClick={() => { setShowGameOptionsModal(false); navigate('/discover'); }} data-testid="game-select-tournaments">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-2xl">🏆</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-amber-800">Tournaments</h3>
                      <p className="text-sm text-amber-700">Spades, teams of two — see what's on and sign up</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="pt-2">
              <Button 
                variant="outline" 
                onClick={() => setShowGameOptionsModal(false)} 
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Linked Game Wager Modal — shared across 4iaR, Checkers, and
          Crazy Eights (in-stream 1v1). The `linkedGameType` state
          determines the title, icon, and the create-endpoint. */}
      <Dialog open={showLinkedGameWagerModal} onOpenChange={setShowLinkedGameWagerModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">
                {linkedGameType === 'checkers' ? '⛃' : linkedGameType === 'crazy_eights' ? '🃏' : '🔴'}
              </span>
              {linkedGameType === 'checkers'
                ? 'Checkers — Set Entry'
                : linkedGameType === 'crazy_eights'
                  ? 'Crazy Eights — Set Entry'
                  : '4 In A Row — Set Entry'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              Challenge {linkedGameOpponent?.name} to a game of{' '}
              {linkedGameType === 'checkers'
                ? 'Checkers'
                : linkedGameType === 'crazy_eights'
                  ? 'Crazy Eights'
                  : '4 In A Row'}!
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Note:</strong> Emeralds will only be deducted after {linkedGameOpponent?.name} accepts the challenge.
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-medium">Your Entry:</span>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setLinkedGameWager(Math.max(MIN_MATCH_ENTRY, linkedGameWager - 10))}
                  >
                    -
                  </Button>
                  <div className="flex items-center gap-1 min-w-[80px] justify-center">
                    <span className="text-xl font-bold text-emerald-600">{linkedGameWager}</span>
                    <span className="text-emerald-600"><EmeraldIcon className="h-4 w-4 inline" /></span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setLinkedGameWager(linkedGameWager + 10)}
                  >
                    +
                  </Button>
                </div>
              </div>
              
              <div className="text-sm text-gray-500 text-center">
                Winner takes the prize: {Math.floor(linkedGameWager * 2 * (1 - (economyRates?.rake_1v1 ?? 0.08)))} <RubyIcon className="h-3.5 w-3.5 inline" /> (after {Math.round((economyRates?.rake_1v1 ?? 0.08) * 100)}% GEMS fee)
              </div>
            </div>

            {/* Crazy Eights — per-match House Rules (Aug 18, 2026) */}
            {linkedGameType === 'crazy_eights' && (
              <div className="border rounded-lg p-3 bg-fuchsia-50/60 space-y-2" data-testid="c8-house-rules">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-fuchsia-900 text-sm">Table Rules</div>
                  <button
                    type="button"
                    onClick={() => setShowC8HowToPlay(true)}
                    className="text-xs text-fuchsia-700 hover:text-fuchsia-900 underline underline-offset-2"
                    data-testid="c8-howtoplay-btn"
                  >
                    How to play
                  </button>
                </div>
                <label className="flex items-start justify-between gap-3 text-sm text-fuchsia-900">
                  <span className="flex-1">
                    <span className="font-medium">Draw Stacking</span>
                    <span className="block text-xs text-fuchsia-800/80">Playing a 2 makes the next player draw 2 — stackable up to 8.</span>
                  </span>
                  <input
                    type="checkbox"
                    data-testid="c8-rule-stacking"
                    className="mt-1 h-4 w-4 accent-fuchsia-600"
                    checked={c8Rules.stacking_enabled}
                    onChange={(e) => setC8Rules(r => ({ ...r, stacking_enabled: e.target.checked }))}
                  />
                </label>
                <label className="flex items-start justify-between gap-3 text-sm text-fuchsia-900">
                  <span className="flex-1">
                    <span className="font-medium">8 Cancels a Stack</span>
                    <span className="block text-xs text-fuchsia-800/80">A wild 8 can be played to cancel a pending draw stack.</span>
                  </span>
                  <input
                    type="checkbox"
                    data-testid="c8-rule-eight-cancels"
                    className="mt-1 h-4 w-4 accent-fuchsia-600"
                    checked={c8Rules.eight_cancels_stack}
                    onChange={(e) => setC8Rules(r => ({ ...r, eight_cancels_stack: e.target.checked }))}
                  />
                </label>
                <label className="flex items-start justify-between gap-3 text-sm text-fuchsia-900">
                  <span className="flex-1">
                    <span className="font-medium">Turn Timer</span>
                    <span className="block text-xs text-fuchsia-800/80">20s per turn.</span>
                  </span>
                  <input
                    type="checkbox"
                    data-testid="c8-rule-timer"
                    className="mt-1 h-4 w-4 accent-fuchsia-600"
                    checked={c8Rules.timer_enabled}
                    onChange={(e) => setC8Rules(r => ({ ...r, timer_enabled: e.target.checked }))}
                  />
                </label>
                {/* Part C (Feb 2026): the 4 previously-locked variants are
                    now live. All default OFF so the classic Crazy Eights
                    ruleset is what players see on first render. */}
                <label className="flex items-start justify-between gap-3 text-sm text-fuchsia-900">
                  <span className="flex-1">
                    <span className="font-medium">Skip Stacking</span>
                    <span className="block text-xs text-fuchsia-800/80">Consecutive Queens each skip an additional player.</span>
                  </span>
                  <input
                    type="checkbox"
                    data-testid="c8-rule-skip-stacking"
                    className="mt-1 h-4 w-4 accent-fuchsia-600"
                    checked={c8Rules.skip_stacking}
                    onChange={(e) => setC8Rules(r => ({ ...r, skip_stacking: e.target.checked }))}
                  />
                </label>
                <label className="flex items-start justify-between gap-3 text-sm text-fuchsia-900">
                  <span className="flex-1">
                    <span className="font-medium">Draw Until You Play</span>
                    <span className="block text-xs text-fuchsia-800/80">Keep drawing until you get a card you can play.</span>
                  </span>
                  <input
                    type="checkbox"
                    data-testid="c8-rule-draw-until-play"
                    className="mt-1 h-4 w-4 accent-fuchsia-600"
                    checked={c8Rules.draw_until_play}
                    onChange={(e) => setC8Rules(r => ({ ...r, draw_until_play: e.target.checked }))}
                  />
                </label>
                <label className="flex items-start justify-between gap-3 text-sm text-fuchsia-900">
                  <span className="flex-1">
                    <span className="font-medium">Ten = Go Again</span>
                    <span className="block text-xs text-fuchsia-800/80">Playing a 10 gives you an immediate extra turn.</span>
                  </span>
                  <input
                    type="checkbox"
                    data-testid="c8-rule-ten-go-again"
                    className="mt-1 h-4 w-4 accent-fuchsia-600"
                    checked={c8Rules.ten_go_again}
                    onChange={(e) => setC8Rules(r => ({ ...r, ten_go_again: e.target.checked }))}
                  />
                </label>
                <label className="flex items-start justify-between gap-3 text-sm text-fuchsia-900">
                  <span className="flex-1">
                    <span className="font-medium">Ace Reversal</span>
                    <span className="block text-xs text-fuchsia-800/80">Aces always reverse direction (in 2p the turn stays with you).</span>
                  </span>
                  <input
                    type="checkbox"
                    data-testid="c8-rule-ace-reversal"
                    className="mt-1 h-4 w-4 accent-fuchsia-600"
                    checked={c8Rules.ace_reversal}
                    onChange={(e) => setC8Rules(r => ({ ...r, ace_reversal: e.target.checked }))}
                  />
                </label>
              </div>
            )}

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowLinkedGameWagerModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={async () => {
                  try {
                    if (linkedGameWager < MIN_MATCH_ENTRY) {
                      toast.error(`Minimum match entry is ${MIN_MATCH_ENTRY} emeralds.`);
                      return;
                    }

                    // ---- CHECKERS live 1v1 (in-stream) --------------
                    // Uses /checkers/create (deduction in /start after
                    // accept). Money/settlement path is UNCHANGED.
                    if (linkedGameType === 'checkers') {
                      const createRes = await api.post('/checkers/create', {
                        wagerAmount: linkedGameWager,
                        linkedOpponentId: linkedGameOpponent?.id,
                        checkersConfig: { capture_rule: 'huffing', pool_rules: true, timer_on: true },
                      });
                      const gameId = createRes.data?.gameId;
                      if (!gameId) throw new Error('No gameId returned');
                      setActiveLinkedGame({
                        gameId,
                        gameType: 'checkers',
                        myPlayerNum: 1,
                        players: [
                          { id: user?.id, name: user?.name, player_num: 1 },
                          { id: linkedGameOpponent?.id, name: linkedGameOpponent?.name, player_num: 2 },
                        ],
                        phase: 'waiting_accept',
                        wagerAmount: linkedGameWager,
                      });
                      await api.post('/four-in-a-row/linked-game-invite', {
                        game_id: gameId,
                        opponent_id: linkedGameOpponent?.id,
                        wager_amount: linkedGameWager,
                        inviter_name: user?.name,
                        game_type: 'checkers',
                      });
                      setShowLinkedGameWagerModal(false);
                      toast.info(`Checkers challenge sent! Waiting for ${linkedGameOpponent?.name} to accept…`);
                      return;
                    }

                    // ---- CRAZY EIGHTS live 1v1 (in-stream) ----------
                    // Reuse the same challenge pipeline as checkers via the
                    // new /c8/create endpoint (single-seat doc, per-game
                    // House Rules, no /game/set-wager 500 loop).
                    if (linkedGameType === 'crazy_eights') {
                      const createRes = await api.post('/c8/create', {
                        wagerAmount: linkedGameWager,
                        linkedOpponentId: linkedGameOpponent?.id,
                        c8Rules: c8Rules,
                      });
                      const gameId = createRes.data?.gameId;
                      if (!gameId) throw new Error('No gameId returned');
                      setActiveLinkedGame({
                        gameId,
                        gameType: 'crazy_eights',
                        myPlayerNum: 1,
                        players: [
                          { id: user?.id, name: user?.name, player_num: 1 },
                          { id: linkedGameOpponent?.id, name: linkedGameOpponent?.name, player_num: 2 },
                        ],
                        phase: 'waiting_accept',
                        wagerAmount: linkedGameWager,
                      });
                      await api.post('/four-in-a-row/linked-game-invite', {
                        game_id: gameId,
                        opponent_id: linkedGameOpponent?.id,
                        wager_amount: linkedGameWager,
                        inviter_name: user?.name,
                        game_type: 'crazy_eights',
                      });
                      setShowLinkedGameWagerModal(false);
                      toast.info(`Crazy Eights challenge sent! Waiting for ${linkedGameOpponent?.name} to accept…`);
                      return;
                    }

                    // ---- 4 IN A ROW live 1v1 (default) --------------
                    console.log('🎮 Creating linked 4 In A Row game challenge with entry:', linkedGameWager);
                    
                    // Create the game via API (no wager deducted yet)
                    const response = await api.post('/four-in-a-row/create', {
                      practiceMode: false,
                      wagerAmount: linkedGameWager,
                      linkedOpponentId: linkedGameOpponent?.id
                    });
                    
                    const gameId = response.data.gameId;
                    console.log('✅ Game created (pending accept):', gameId);
                    
                    // Set up pending game state (waiting for accept)
                    setActiveLinkedGame({
                      gameId,
                      board: response.data.game?.board || Array(7).fill(null).map(() => Array(6).fill(0)),
                      currentPlayer: 1,
                      myPlayerNum: 1,
                      players: [
                        { id: user?.id, name: user?.name, player_num: 1 },
                        { id: linkedGameOpponent?.id, name: linkedGameOpponent?.name, player_num: 2 }
                      ],
                      phase: 'waiting_accept', // New phase - waiting for opponent
                      wagerAmount: linkedGameWager
                    });
                    
                    // Send CHALLENGE (not direct invite) to the linked partner
                    try {
                      await api.post('/four-in-a-row/linked-game-invite', {
                        game_id: gameId,
                        opponent_id: linkedGameOpponent?.id,
                        wager_amount: linkedGameWager,
                        inviter_name: user?.name
                      });
                      console.log('📡 Game CHALLENGE sent to partner');
                    } catch (e) {
                      console.log('Failed to send game challenge:', e);
                    }
                    
                    setShowLinkedGameWagerModal(false);
                    toast.info(`Challenge sent! Waiting for ${linkedGameOpponent?.name} to accept...`);
                    
                  } catch (error) {
                    console.error('Failed to create linked game:', error);
                    toast.error(error.response?.data?.detail || 'Failed to start game. Please try again.');
                  }
                }}
              >
                Send Challenge
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Part C (Feb 2026): Crazy Eights How-to-Play dialog. Opened from
          the "How to play" link in the C8 house-rules card. Reflects the
          current toggled rules so players see exactly what they're
          about to play. */}
      <Dialog open={showC8HowToPlay} onOpenChange={setShowC8HowToPlay}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="c8-how-to-play-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>🎴</span>
              <span>Crazy Eights — How to Play</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-gray-700">
            <div>
              <div className="font-semibold text-gray-900 mb-1">Goal</div>
              <p>Empty your hand first. Play a card that matches the discard top by SUIT or RANK. 8s are wild.</p>
            </div>
            <div>
              <div className="font-semibold text-gray-900 mb-1">On your turn</div>
              <ul className="list-disc pl-5 space-y-1">
                <li>Play a legal card, or</li>
                <li>Draw a card. If it's playable you may play it or pass.</li>
                <li>When you're down to <b>one</b> card, tap <b>Last Card</b> within 5s or draw a 2-card penalty.</li>
              </ul>
            </div>
            <div>
              <div className="font-semibold text-gray-900 mb-1">Standard actions</div>
              <ul className="list-disc pl-5 space-y-1">
                <li><b>2</b> — DRAW 2 for the next player (stacks with 2s and Aces).</li>
                <li><b>Ace</b> — DRAW 4 for the next player (stacks with 2s and Aces).</li>
                <li><b>Jack</b> — SKIP the next player. In 2p, extra turn.</li>
                <li><b>Queen</b> — REVERSE direction. In 2p, extra turn.</li>
                <li><b>8</b> — Wild. Choose a new active suit (unless it's your winning card).</li>
              </ul>
            </div>
            <div className="border-t pt-3">
              <div className="font-semibold text-gray-900 mb-2">This match</div>
              <ul className="text-xs space-y-1" data-testid="c8-howto-current-rules">
                {[
                  ['stacking_enabled', 'Draw Stacking'],
                  ['eight_cancels_stack', '8 Cancels a Stack'],
                  ['skip_stacking', 'Skip Stacking (Queens stack)'],
                  ['draw_until_play', 'Draw Until You Play'],
                  ['ten_go_again', 'Ten = Go Again'],
                  ['ace_reversal', 'Ace Reversal'],
                  ['timer_enabled', 'Turn Timer (20s)'],
                ].map(([k, label]) => (
                  <li key={k} className="flex items-center justify-between">
                    <span>{label}</span>
                    <span className={c8Rules[k] ? 'text-emerald-600 font-semibold' : 'text-gray-400'}>
                      {c8Rules[k] ? 'ON' : 'OFF'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setShowC8HowToPlay(false)} data-testid="c8-howto-close-btn">Got it</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Game Challenge Accept Modal - When opponent challenges you */}
      {/* Uses high z-index to render above live controls, no X button (decline only) */}
      {showGameChallengeModal && pendingGameChallenge && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="game-challenge-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
          <div className="relative z-10 bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" data-testid="game-challenge-content">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <span className="text-2xl">{pendingGameChallenge?.gameType === 'checkers' ? '⛃' : '🎮'}</span>
              {pendingGameChallenge?.gameType === 'checkers' ? 'Checkers Challenge!' : 'Game Challenge!'}
            </h3>
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <p className="text-lg font-bold text-red-800">
                  {pendingGameChallenge?.challengerName} challenged you!
                </p>
                <p className="text-red-600 mt-1">
                  {pendingGameChallenge?.gameType === 'checkers' ? 'Checkers match'
                    : pendingGameChallenge?.gameType === 'crazy_eights' ? 'Crazy Eights match'
                    : '4 In A Row'}
                </p>
              </div>

              {pendingGameChallenge?.gameType === 'crazy_eights' && pendingGameChallenge?.c8Rules && (
                <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3 text-xs text-fuchsia-900 space-y-1" data-testid="c8-challenge-rules">
                  <div className="font-semibold text-sm">Table Rules</div>
                  {[
                    ['stacking_enabled', 'Draw Stacking'],
                    ['eight_cancels_stack', '8 Cancels a Stack'],
                    ['skip_stacking', 'Skip Stacking'],
                    ['draw_until_play', 'Draw-Until-You-Play'],
                    ['ten_go_again', 'Ten = Go Again'],
                    ['ace_reversal', 'Ace Reversal (3+ players)'],
                    ['timer_enabled', 'Turn Timer (20s)'],
                  ].map(([k, label]) => (
                    <div key={k} className="flex items-center justify-between">
                      <span>{label}</span>
                      <span className={pendingGameChallenge.c8Rules[k] ? 'text-emerald-600 font-bold' : 'text-gray-400'}>
                        {pendingGameChallenge.c8Rules[k] ? 'ON' : 'OFF'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="bg-emerald-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600">Entry Amount</p>
                <p className="text-2xl font-bold text-emerald-600 flex items-center justify-center gap-1">
                  {pendingGameChallenge?.wagerAmount || 0} <EmeraldIcon className="h-5 w-5 inline" />
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Winner takes: {Math.floor((pendingGameChallenge?.wagerAmount || 0) * 2 * (1 - (economyRates?.rake_1v1 ?? 0.08)))} <RubyIcon className="h-3.5 w-3.5 inline" /> (after {Math.round((economyRates?.rake_1v1 ?? 0.08) * 100)}% GEMS fee)
                </p>
              </div>
              
              {pendingGameChallenge?.wagerAmount > 0 && (user?.emeralds || 0) < pendingGameChallenge.wagerAmount && (
                <div className="bg-red-100 border border-red-300 rounded-lg p-3 text-sm text-red-700">
                  You don't have enough emeralds for this entry!
                </div>
              )}
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  data-testid="decline-challenge-btn"
                  onClick={async () => {
                    const isCheckers = pendingGameChallenge?.gameType === 'checkers';
                    const isC8Decline = pendingGameChallenge?.gameType === 'crazy_eights';
                    try {
                      await api.post(`/four-in-a-row/${pendingGameChallenge.gameId}/decline-challenge`, {
                        challenger_id: pendingGameChallenge.challengerId,
                        game_type: isCheckers ? 'checkers' : isC8Decline ? 'crazy_eights' : 'four_in_a_row'
                      });
                      toast.info('Challenge declined');
                    } catch (e) {
                      console.error('Failed to decline:', e);
                    }
                    setShowGameChallengeModal(false);
                    setPendingGameChallenge(null);
                  }}
                  className="flex-1"
                >
                  Decline
                </Button>
                <Button 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="accept-challenge-btn"
                  disabled={pendingGameChallenge?.wagerAmount > 0 && (user?.emeralds || 0) < pendingGameChallenge?.wagerAmount}
                  onClick={async () => {
                    const isCheckers = pendingGameChallenge?.gameType === 'checkers';
                    const isC8 = pendingGameChallenge?.gameType === 'crazy_eights';
                    try {
                      console.log(`🎮 Accepting ${isCheckers ? 'checkers' : isC8 ? 'crazy_eights' : '4iaR'} challenge:`, pendingGameChallenge.gameId);

                      if (isC8) {
                        // Crazy Eights linked flow (Aug 18, 2026) — same
                        // shape as checkers accept path. /c8/accept-challenge
                        // seats us as Player 2 and notifies the challenger;
                        // their client then fires /game/{id}/start. Snapshot-
                        // on-connect (backend WS) hydrates the initial cards.
                        const gid = pendingGameChallenge.gameId;
                        const challengerId = pendingGameChallenge.challengerId;
                        const challengerName = pendingGameChallenge.challengerName;
                        await api.post(`/c8/${gid}/accept-challenge`, {
                          challenger_id: challengerId
                        });
                        setLinkedGameOpponent({ id: challengerId, name: challengerName });
                        setShowGameChallengeModal(false);
                        setPendingGameChallenge(null);
                        try { await connectGameWebSocket(gid, user?.id); } catch (e) { console.warn('[c8] game WS connect failed:', e); }
                        setInStreamGame(gid);
                        setActiveLinkedGame({
                          gameId: gid,
                          gameType: 'crazy_eights',
                          myPlayerNum: 2,
                          players: [
                            { id: challengerId, name: challengerName },
                            { id: user?.id, name: user?.name },
                          ],
                          phase: 'playing',
                        });
                        toast.success('Crazy Eights match starting…');
                        return;
                      }

                      if (isCheckers) {
                        // Checkers: seat invitee as Player 2; challenger's client
                        // then fires /game/{id}/start (deduction happens there,
                        // atomic per-seat). Render IN-STREAM (no navigate) —
                        // connect the game WS so the checkers_state broadcast
                        // drives the board in the live overlay.
                        const gid = pendingGameChallenge.gameId;
                        const challengerId = pendingGameChallenge.challengerId;
                        const challengerName = pendingGameChallenge.challengerName;
                        await api.post(`/checkers/${gid}/accept-challenge`, {
                          challenger_id: challengerId
                        });
                        setLinkedGameOpponent({ id: challengerId, name: challengerName });
                        setShowGameChallengeModal(false);
                        setPendingGameChallenge(null);
                        try { await connectGameWebSocket(gid, user?.id); } catch (e) { console.warn('[checkers] game WS connect failed:', e); }
                        // Seed the board if the host already started; otherwise
                        // the checkers_state WS broadcast seeds it on start.
                        try { const g = await api.get(`/game/${gid}`); if (g.data?.checkers_state) setCheckersLiveState(g.data.checkers_state); } catch (e) { /* WS will seed it */ }
                        setInStreamGame(gid);
                        setActiveLinkedGame({
                          gameId: gid,
                          gameType: 'checkers',
                          myPlayerNum: 2,
                          players: [
                            { id: challengerId, name: challengerName },
                            { id: user?.id, name: user?.name },
                          ],
                          phase: 'playing',
                        });
                        toast.success('Checkers match starting…');
                        return;
                      }

                      const response = await api.post(`/four-in-a-row/${pendingGameChallenge.gameId}/accept-challenge`, {
                        challenger_id: pendingGameChallenge.challengerId
                      });
                      
                      console.log('✅ Challenge accepted:', response.data);
                      
                      setActiveLinkedGame({
                        gameId: pendingGameChallenge.gameId,
                        board: response.data.board || Array(7).fill(null).map(() => Array(6).fill(0)),
                        currentPlayer: response.data.current_player || 1,
                        myPlayerNum: 2,
                        phase: 'playing',
                        wagerAmount: pendingGameChallenge.wagerAmount,
                        totalPot: response.data.total_pot
                      });
                      setLinkedGameOpponent({ 
                        id: pendingGameChallenge.challengerId, 
                        name: pendingGameChallenge.challengerName 
                      });
                      
                      setShowGameChallengeModal(false);
                      setPendingGameChallenge(null);
                      toast.success('Game starting!');
                      
                    } catch (error) {
                      console.error('Failed to accept challenge:', error);
                      toast.error(error.response?.data?.detail || 'Failed to accept challenge');
                    }
                  }}
                >
                  Accept Challenge
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 4 In A Row Game Board is now rendered INLINE within the video panel layout above */}
      
      {/* Messages Quick Modal - usable while streaming */}
      <MessagesQuickModal isOpen={showMessagesModal} onClose={() => setShowMessagesModal(false)} />

      {/* Waiting for Accept Overlay */}
      {activeLinkedGame && activeLinkedGame.phase === 'waiting_accept' && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center pointer-events-none" data-testid="waiting-accept-overlay">
          <div className="bg-black/50 absolute inset-0" />
          <div className="relative z-10 bg-gray-900/95 backdrop-blur-sm rounded-2xl p-6 shadow-2xl pointer-events-auto max-w-sm w-full mx-4 text-center">
            <div className="animate-pulse mb-4">
              <span className="text-4xl">🎮</span>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              Challenge Sent!
            </h3>
            <p className="text-gray-400 mb-4">
              Waiting for {linkedGameOpponent?.name} to accept...
            </p>
            <div className="text-emerald-400 font-bold mb-4">
              Entry: {activeLinkedGame.wagerAmount} <EmeraldIcon className="h-4 w-4 inline" />
            </div>
            <Button 
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
              onClick={() => {
                setActiveLinkedGame(null);
                toast.info('Challenge cancelled');
              }}
            >
              Cancel Challenge
            </Button>
          </div>
        </div>
      )}

      {/* Player Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Invite Players ({invitedPlayers.length + 1}/{maxLinkPartners + 1}){liveType === 'party' ? ' · Party' : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 flex-1 overflow-auto">
            <p className="text-gray-600">Invite players by their GEM ID or select from online players.</p>

            {/* Invited Players List */}
            {invitedPlayers.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <h4 className="font-semibold text-sm text-gray-700">Invited Players:</h4>
                {invitedPlayers.map((player, index) => {
                  const isLinked = player.status === 'linked' || player.status === 'connected';
                  return (
                    <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                      <span className="font-medium">{player.name}</span>
                      {isLinked ? (
                        <Badge className="bg-emerald-600 text-white">Linked</Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending</Badge>
                      )}
                    </div>
                  );
                })}
                <div className="flex items-center justify-between bg-emerald-50 p-2 rounded border border-emerald-200">
                  <span className="font-medium">{user?.name} (You)</span>
                  <Badge className="bg-emerald-600">Host</Badge>
                </div>
              </div>
            )}

            {/* Only show the invite form while there is room (Round 63: 16 for a Party, 6 for a link) */}
            {invitedPlayers.length < maxLinkPartners && (
              <>
                {/* Search/Manual Entry */}
                <div>
                  <label className="block text-sm font-medium mb-2">Search Player or Enter GEM ID</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search by name or enter GEM ID"
                      value={searchQuery || inviteGemId}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSearchQuery(val);
                        setInviteGemId(val);
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && handleInvitePlayerFromStream()}
                      className="pl-10"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Room for {maxLinkPartners - invitedPlayers.length} more · up to {maxLinkPartners + 1} on camera
                  </p>
                </div>
                
                {/* All Users List with Checkboxes */}
                <div className="border rounded-lg">
                  <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
                    <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                      <Users className="h-4 w-4 text-gray-700" />
                      All Players
                    </h4>
                    <div className="flex items-center gap-2">
                      {selectedUserIds.length > 0 && (
                        <Badge className="text-xs bg-emerald-600">
                          {selectedUserIds.length} selected
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">
                        {onlineUsers.filter(u => u.is_live).length} live
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {onlineUsers.filter(u => u.is_online).length} online
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="max-h-60 overflow-y-auto">
                    {loadingOnlineUsers ? (
                      <div className="p-4 text-center text-gray-500">Loading players...</div>
                    ) : filteredOnlineUsers.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        {searchQuery ? 'No players found matching your search' : 'No players available'}
                      </div>
                    ) : (
                      <div className="divide-y">
                        {filteredOnlineUsers.map((player) => (
                          <div 
                            key={player.id}
                            className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => {
                              // Toggle selection
                              if (selectedUserIds.includes(player.id)) {
                                setSelectedUserIds(selectedUserIds.filter(id => id !== player.id));
                              } else {
                                setSelectedUserIds([...selectedUserIds, player.id]);
                              }
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={selectedUserIds.includes(player.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (e.target.checked) {
                                    setSelectedUserIds([...selectedUserIds, player.id]);
                                  } else {
                                    setSelectedUserIds(selectedUserIds.filter(id => id !== player.id));
                                  }
                                }}
                                className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <div className="font-medium">{player.name}</div>
                                  {/* Sapphire Badge */}
                                  {player.badge_type === 'sapphire' && (
                                    <SapphireBadgeIcon className="w-4 h-4" />
                                  )}
                                  {/* Canary Badge */}
                                  {(player.badge_type === 'canary' || player.badge_type === 'verified') && (
                                    <CanaryBadgeIcon className="w-4 h-4" />
                                  )}
                                  {player.is_live && (
                                    <Badge className="text-xs bg-red-500 text-white animate-pulse">
                                      🔴 LIVE
                                    </Badge>
                                  )}
                                  {!player.is_live && player.is_online && (
                                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                      <Signal className="h-2 w-2 mr-1" />
                                      Online
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500">@{player.handle}</div>
                                <div className="flex gap-3 mt-1 text-xs text-gray-600">
                                  <span className="flex items-center gap-1">
                                    <Trophy className="h-3 w-3" />
                                    {player.wins}W / {player.losses}L
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <EmeraldIcon className="h-4 w-4" />
                                    {player.emeralds}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="flex gap-3">
              <Button 
                onClick={handleInvitePlayerFromStream} 
                className="flex-1 disabled:opacity-80 disabled:cursor-wait" 
                disabled={isSendingInvites || (selectedUserIds.length === 0 && !inviteGemId.trim())}
                data-testid="send-invitation-btn"
              >
                {isSendingInvites ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Invitation{selectedUserIds.length > 1 ? 's' : ''} ({selectedUserIds.length || (inviteGemId.trim() ? 1 : 0)})
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={() => {
                setShowInviteModal(false);
                setSelectedUserIds([]);
              }} className="flex-1" disabled={isSendingInvites}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============================================================
          SPADES IN-STREAM INVITE MODAL (May 16, 2026)
          Shown to a linked broadcaster when the host starts a spades game.
          Video stays untouched — only the card UI appears on Join.
          ============================================================ */}
      <Dialog open={!!incomingSpadesInvite} onOpenChange={(open) => { if (!open) declineIncomingSpadesInvite(); }}>
        <DialogContent className="sm:max-w-md" data-testid="spades-invite-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>🎴</span>
              <span>{incomingSpadesInvite?.host_name || 'Host'} started Spades</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              You've been invited to play Spades in this live stream.
              {incomingSpadesInvite?.wager_amount ? ` Entry: ${incomingSpadesInvite.wager_amount} Emeralds.` : ''}
            </p>
            {/* Part A (Feb 2026): expose the host's chosen rules so the
                invitee knows what they're joining before hitting Join. */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-md px-2 py-1.5 border bg-emerald-500/10 border-emerald-500/40 text-emerald-200" data-testid="spades-invite-rule-playto">
                <span className="font-semibold">Play to</span>
                <span className="ml-1">{incomingSpadesInvite?.target_score || 150}</span>
              </div>
              <div
                className={`rounded-md px-2 py-1.5 border ${
                  incomingSpadesInvite?.allow_blind_six
                    ? 'bg-yellow-500/10 border-yellow-500/40 text-yellow-200'
                    : 'bg-white/5 border-white/10 text-gray-400'
                }`}
                data-testid="spades-invite-rule-blind6"
              >
                <span className="font-semibold">Blind 6+</span>
                <span className="ml-1">{incomingSpadesInvite?.allow_blind_six ? 'On' : 'Off'}</span>
              </div>
              <div
                className={`rounded-md px-2 py-1.5 border ${
                  incomingSpadesInvite?.allow_ace_check
                    ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200'
                    : 'bg-white/5 border-white/10 text-gray-400'
                }`}
                data-testid="spades-invite-rule-acecheck"
              >
                <span className="font-semibold">Ace Check</span>
                <span className="ml-1">{incomingSpadesInvite?.allow_ace_check ? 'On' : 'Off'}</span>
              </div>
            </div>
            {/* Part 3e (Feb 19, 2026): show the invitee who their partner
                is so they know what team they'd be joining. */}
            {(() => {
              const pmap = incomingSpadesInvite?.partner_map || {};
              const myPartnerId = user?.id ? pmap[user.id] : null;
              if (!myPartnerId) return null;
              // Part 1c (Feb 19, 2026 SPADES FIX): `allLinkedUsers` is an
              // array of ID STRINGS with `allLinkedUserNames` as its
              // parallel names array — the old `u?.id` match returned
              // undefined for every entry. Resolve by index instead.
              let partnerName = null;
              const idx = (allLinkedUsers || []).indexOf(myPartnerId);
              if (idx >= 0) partnerName = allLinkedUserNames?.[idx] || null;
              if (!partnerName) {
                const fromInvited = (invitedPlayers || []).find((p) => p?.id === myPartnerId);
                partnerName = fromInvited?.name || null;
              }
              const names = (id) => { const i = (allLinkedUsers || []).indexOf(id); return (i >= 0 && allLinkedUserNames?.[i]) || (invitedPlayers || []).find((p) => p?.id === id)?.name || 'Guest'; };
              const others = Object.keys(pmap).filter((id) => id !== user?.id && id !== myPartnerId && pmap[id] !== user?.id);
              return (
                <p className="text-xs text-gray-300" data-testid="spades-invite-partner">
                  <span className="font-semibold text-white">Teams:</span> You + {partnerName || names(myPartnerId)}
                  {others.length === 2 ? ` vs ${names(others[0])} + ${names(others[1])}` : ''}
                  <span className="block text-gray-500 mt-0.5">Partners were chosen by {incomingSpadesInvite?.host_name || 'the host'}.</span>
                </p>
              );
            })()}
            <p className="text-xs text-gray-500">
              Your video stays connected — only the card UI will appear.
            </p>
            <div className="flex gap-3 pt-2">
              <Button
                onClick={declineIncomingSpadesInvite}
                variant="outline"
                className="flex-1"
                data-testid="spades-invite-decline-btn"
              >
                Decline
              </Button>
              <Button
                onClick={acceptIncomingSpadesInvite}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="spades-invite-accept-btn"
              >
                Join Game
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Part 3b (Feb 19, 2026): Spades TABLE STEP modal — seats + partner
          + invite banner. Opens FIRST from Play Together → Spades. Closes
          on Next which opens the Match Entry & Rules modal. */}
      <Dialog open={showSpadesTableModal} onOpenChange={setShowSpadesTableModal}>
        <DialogContent className="sm:max-w-lg bg-[#0F1420] text-white border border-white/10" data-testid="spades-table-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>♠</span>
              <span>Spades — Set the Table</span>
            </DialogTitle>
          </DialogHeader>
          {(() => {
            // SEP 3 2026 FIX (Coach P bug): seats must come from the LIVE
            // link roster (linkPartners = allLinkedUsers + names), not from
            // connectedCoHosts / invitedPlayers — those are only populated
            // on the link INVITER's client. On an accepter's screen they're
            // empty, so Spades showed "seats 2/3/4 empty — invite 3 more"
            // inside a healthy 4-way link and they could never send a
            // challenge. linkPartners is identical on every linked member.
            const rosterSource = (linkPartners || []).length
              ? linkPartners
              : (connectedCoHosts.length ? connectedCoHosts : invitedPlayers);
            const linkedHumans = [
              { id: user?.id, name: `${user?.name || 'You'} (You)`, isSelf: true },
              ...rosterSource
                .filter((p) => p?.id && p.id !== user?.id)
                .map((p) => ({ id: p.id, name: p.name || 'Guest', isSelf: false })),
            ];
            const seatsToFill = Math.max(0, 4 - linkedHumans.length);
            const seats = [
              ...linkedHumans,
              ...Array.from({ length: seatsToFill }, () => null),
            ];
            const otherHumans = linkedHumans.filter((h) => !h.isSelf);
            const canPickPartner = linkedHumans.length === 4;
            const partnerChosen = !!selectedPartner;
            const nextEnabled = canPickPartner && partnerChosen;
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2" data-testid="spades-table-seats">
                  {seats.map((seat, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border ${
                        seat ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-white/5 border-white/10 border-dashed'
                      }`}
                    >
                      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Seat {idx + 1}</div>
                      <div className="text-sm font-semibold truncate">
                        {seat ? seat.name : <span className="text-gray-500 italic">Empty</span>}
                      </div>
                    </div>
                  ))}
                </div>

                {seatsToFill > 0 ? (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm space-y-2">
                    <div className="font-medium text-amber-200">
                      Spades needs 4 linked broadcasters — invite {seatsToFill} more.
                    </div>
                    <Button
                      onClick={() => {
                        setShowSpadesTableModal(false);
                        setShowInviteModal(true);
                      }}
                      className="w-full bg-amber-500 hover:bg-amber-400 text-amber-950"
                      data-testid="spades-table-invite-btn"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Invite Broadcasters
                    </Button>
                    <p className="text-xs text-amber-200/80">
                      Once they accept, this table updates automatically — no bots.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold">Your Partner</div>
                    <p className="text-xs text-gray-400">
                      Team 1: You + Your Partner. Team 2: the other two.
                    </p>
                    <div className="space-y-2" role="radiogroup" aria-label="Your partner">
                      {otherHumans.map((p, i) => {
                        const selected = selectedPartner?.id === p.id;
                        return (
                          <label
                            key={p.id}
                            className={`flex items-center gap-3 w-full p-3 rounded-lg border cursor-pointer transition-all ${
                              selected
                                ? 'border-emerald-500 bg-emerald-500/10'
                                : 'border-white/10 hover:border-emerald-500/60 hover:bg-white/5'
                            }`}
                            data-testid={`spades-table-partner-${i}`}
                          >
                            <input
                              type="radio"
                              name="spades-table-partner"
                              className="accent-emerald-500"
                              checked={selected}
                              onChange={() => {
                                const fullPartner = invitedPlayers.find((ip) => ip.id === p.id) || p;
                                setSelectedPartner(fullPartner);
                                const opponents = invitedPlayers.filter(
                                  (ip) => ip.id !== p.id,
                                );
                                setTeamAssignments({
                                  team1: [user, fullPartner],
                                  team2: opponents,
                                });
                              }}
                            />
                            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                              {p.name?.charAt(0) || '?'}
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                              <div className="text-sm font-semibold text-white truncate">{p.name}</div>
                            </div>
                            {selected && <span className="text-emerald-400 text-xs font-semibold">Partner</span>}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowSpadesTableModal(false)}
                    className="flex-1 border-white/20 text-gray-300 hover:bg-white/10"
                    data-testid="spades-table-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      setShowSpadesTableModal(false);
                      setSelectedGameType('spades');
                      setSelectedGameMode('invite');
                      setShowWagerModal(true);
                    }}
                    disabled={!nextEnabled}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="spades-table-next-btn"
                  >
                    Next: Entry & Rules
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>



      {/* Match Entry & Rules Modal */}
      <Dialog open={showWagerModal} onOpenChange={setShowWagerModal}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto bg-[#0F1420] text-white border border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Match Entry & Rules Setup
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Game Mode Selection */}
            {!selectedGameMode && (
              <div className="space-y-3">
                <p className="text-gray-600">Choose how you want to find players:</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2"
                    onClick={() => setSelectedGameMode('quickMatch')}
                  >
                    <Trophy className="h-6 w-6 text-amber-600" />
                    <span className="font-semibold">Quick Match</span>
                    <span className="text-xs text-gray-500">Auto-match with live players</span>
                  </Button>
                  <Button 
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2"
                    onClick={() => {
                      setSelectedGameMode('invitePlayer');
                      setShowWagerModal(false);
                      setShowInviteModal(true);
                    }}
                  >
                    <Users className="h-6 w-6 text-emerald-600" />
                    <span className="font-semibold">Invite Player</span>
                    <span className="text-xs text-gray-500">Choose specific players</span>
                  </Button>
                </div>
              </div>
            )}
            
            {selectedGameMode && (
              <>
                <p className="text-gray-600">
                  {selectedGameMode === 'invitePlayer' 
                    ? `Configure game settings and match entry for your competition.`
                    : 'Configure game settings and match entry. Matching with live players who want to compete...'
                  }
                </p>
                
                <div className="space-y-4">
              {/* Match Entry Amount */}
              <div>
                <label className="block text-sm font-medium mb-2">Match Entry Amount (Emeralds per player)</label>
                <div className="space-y-2">
                  <Input
                    type="number"
                    placeholder="Minimum 50 Emeralds"
                    value={wagerAmount}
                    onChange={(e) => setWagerAmount(Math.max(50, parseInt(e.target.value) || 50))}
                    min="50"
                    max={user?.emeralds || 0}
                  />
                  <div className="flex gap-2">
                    {[50, 100, 250, 500].map(amt => (
                      <Button
                        key={amt}
                        onClick={() => setWagerAmount(amt)}
                        variant="outline"
                        size="sm"
                        className={`flex-1 ${wagerAmount === amt ? 'bg-emerald-100 border-emerald-500' : 'border-gray-300'}`}
                      >
                        {amt} <EmeraldIcon className="h-3 w-3 ml-1 inline" />
                      </Button>
                    ))}
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Minimum entry: 50 Emeralds per player. GEMS keeps {Math.round((economyRates?.rake_1v1 ?? 0.08) * 100)}% of the prize as a platform fee.
                </p>
              </div>

              {/* Game Rules Toggles */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Game Rules</h4>
                
                {/* Round 10: PLAY TO — match target score (NY tables play to 250/300/500). */}
                <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg mb-3" data-testid="spades-play-to">
                  <div className="flex-1">
                    <div className="font-medium text-emerald-800">Play to</div>
                    <div className="text-sm text-emerald-700">First team to reach this score wins the match.</div>
                  </div>
                  <div className="flex gap-1">
                    {[250, 300, 500].map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setSpadesTargetScore(t)}
                        className={`px-3 py-1 rounded-full text-sm font-semibold border ${spadesTargetScore === t ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-800 border-emerald-300'}`}
                        data-testid={`spades-play-to-${t}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Allow Blind 6+ Toggle */}
                <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-yellow-800">Allow Blind 6+</div>
                    <div className="text-sm text-yellow-700">
                      Players can declare blind bids ≥6 before seeing cards. Max 3 per team per match.
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allowBlindSix}
                      onChange={(e) => setAllowBlindSix(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                  </label>
                </div>

                {/* Allow Ace Check Toggle */}
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-green-800">Allow Ace Check</div>
                    <div className="text-sm text-green-700">
                      Teams can declare Ace Check to capture all four Aces. +100/-100 points.
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allowAceCheck}
                      onChange={(e) => setAllowAceCheck(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>
              </div>
              
              {/* Part A2 (Feb 2026): Partner picker inline in the spades
                  entry modal — writes to the same setSelectedPartner /
                  setTeamAssignments state the standalone showPartnerSelection
                  dialog uses, so downstream team logic is unchanged. Only
                  shown for in-live 4-player spades. */}
              {(selectedGameType || 'spades') === 'spades' &&
                (selectedGameMode === 'invite' || selectedGameMode === 'invitePlayer') &&
                invitedPlayers.length === 3 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-1">Your Partner</h4>
                  <p className="text-xs text-gray-400 mb-3">
                    Team 1: You + Your Partner (Player 4). Team 2: the remaining two.
                  </p>
                  <div className="space-y-2" role="radiogroup" aria-label="Your partner">
                    {invitedPlayers.map((player, idx) => {
                      const selected = selectedPartner?.gem_id === player.gem_id ||
                        selectedPartner?.id === player.id;
                      return (
                        <label
                          key={player.gem_id || player.id || idx}
                          className={`flex items-center gap-3 w-full p-3 rounded-lg border cursor-pointer transition-all ${
                            selected
                              ? 'border-emerald-500 bg-emerald-500/10'
                              : 'border-white/10 hover:border-emerald-500/60 hover:bg-white/5'
                          }`}
                          data-testid={`wager-partner-radio-${idx}`}
                        >
                          <input
                            type="radio"
                            name="spades-partner"
                            className="accent-emerald-500"
                            checked={selected}
                            onChange={() => {
                              setSelectedPartner(player);
                              const opponents = invitedPlayers.filter(
                                (p) => (p.gem_id || p.id) !== (player.gem_id || player.id),
                              );
                              setTeamAssignments({
                                team1: [user, player],
                                team2: opponents,
                              });
                            }}
                          />
                          <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                            {player.name?.charAt(0) || '?'}
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <div className="text-sm font-semibold text-white truncate">{player.name}</div>
                            <div className="text-xs text-gray-400 truncate">@{player.handle}</div>
                          </div>
                          {selected && (
                            <span className="text-emerald-400 text-xs font-semibold">Partner</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {wagerAmount > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h4 className="font-semibold text-emerald-800 mb-2">Match Entry Details</h4>
                  <div className="text-sm text-emerald-700 space-y-1">
                    <div className="flex justify-between">
                      <span>Per player:</span>
                      <span>{wagerAmount} Emeralds</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Match Entry Pool:</span>
                      <span>{wagerAmount * 4} Emeralds</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Platform service fee (10%):</span>
                      <span>{Math.floor(wagerAmount * 4 * (economyRates?.rake_1v1 ?? 0.08))} Emeralds</span>
                    </div>
                    <div className="flex justify-between font-semibold border-t pt-1">
                      <span>Prize Pool:</span>
                      <span>{wagerAmount * 4 - Math.floor(wagerAmount * 4 * (economyRates?.rake_1v1 ?? 0.08))} Emeralds</span>
                    </div>
                    <p className="text-xs mt-2 text-emerald-600">
                      Winners receive the match prize in Rubies
                    </p>
                  </div>
                </div>
              )}
              
              {wagerAmount === 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-700">
                    No match entry selected - this will be a practice game, just for fun
                  </p>
                </div>
              )}
              
              {/* Skill-based disclaimer */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs text-gray-600 text-center">
                  This is a skill-based competitive game. Outcomes are determined by player performance, not chance.
                </p>
              </div>
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    onClick={handleSetWagerFromLiveStream} 
                    className="flex-1"
                    disabled={
                      wagerAmount > (user?.emeralds || 0) ||
                      // Part A2: partner is mandatory for in-live spades
                      ((selectedGameType || 'spades') === 'spades' &&
                        (selectedGameMode === 'invite' || selectedGameMode === 'invitePlayer') &&
                        invitedPlayers.length === 3 &&
                        !selectedPartner)
                    }
                    data-testid="wager-submit-btn"
                  >
                    <Trophy className="h-4 w-4 mr-2" />
                    {selectedGameMode === 'quickMatch' ? `Find Match (${wagerAmount} Emeralds)` : 
                     (selectedGameMode === 'invite' || selectedGameMode === 'invitePlayer')
                       ? ((selectedGameType || 'spades') === 'spades'
                            ? `Start Spades Game (${wagerAmount})`
                            : `Start Game (${wagerAmount})`)
                       : 'Start Game'}
                  </Button>
                  <Button variant="outline" onClick={() => {
                    setShowWagerModal(false);
                    setSelectedGameMode(null);
                  }} className="flex-1">
                    Back
                  </Button>
                </div>
              </>
            )}
            
            {!selectedGameMode && (
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowWagerModal(false)} className="w-full">
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      

      {/* Stream Ended Modal with Suggestions */}
      <Dialog open={showStreamEndedModal} onOpenChange={setShowStreamEndedModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center animate-pulse">
                📺
              </div>
              Stream Ended
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="text-center">
              <div className="inline-block px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 rounded-lg text-white font-semibold animate-fade-out">
                🎬 Going Offline...
              </div>
              <p className="text-gray-600 mt-4">Thank you for streaming with GEMS!</p>
            </div>
            
            {suggestedStreams.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Video className="h-5 w-5 text-red-500" />
                  Watch Other Live Streams
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {suggestedStreams.slice(0, 5).map((stream) => (
                    <button
                      key={stream.stream_id}
                      onClick={() => {
                        setShowStreamEndedModal(false);
                        navigate(`/watch/${stream.stream_id}`);
                      }}
                      className="w-full p-4 rounded-lg border-2 border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-pink-500 rounded-lg flex items-center justify-center text-white text-2xl font-bold">
                            {stream.streamer_name?.charAt(0) || '?'}
                          </div>
                          <div className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 animate-pulse">
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                            LIVE
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-gray-900">{stream.title}</p>
                          <p className="text-sm text-gray-600">{stream.streamer_name}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {stream.viewer_count || 0} viewers
                            </span>
                          </div>
                        </div>
                        <ArrowRight className="h-5 w-5 text-emerald-600" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button 
                onClick={() => {
                  setShowStreamEndedModal(false);
                  navigate('/discover');
                }} 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                <Video className="mr-2 h-4 w-4" />
                Browse Live Streams
              </Button>
              <Button 
                onClick={() => {
                  setShowStreamEndedModal(false);
                }} 
                variant="outline"
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stream Stats Modal - Shows when stream ends with analytics */}
      <Dialog open={showStreamStatsModal} onOpenChange={setShowStreamStatsModal}>
        <DialogContent className="sm:max-w-lg bg-black border-2 border-gray-800" data-testid="stream-ended-modal">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/20 via-transparent to-purple-900/20 rounded-lg pointer-events-none" />
          <DialogHeader className="relative">
            <DialogTitle className="text-center text-2xl font-bold text-white">
              <div className="flex flex-col items-center gap-3">
                <div className="w-20 h-20 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center border-4 border-gray-700">
                  <div className="text-4xl">📊</div>
                </div>
                <span className="bg-gradient-to-r from-emerald-400 to-emerald-600 bg-clip-text text-transparent">
                  Stream Ended
                </span>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4 relative">
            {/* Live Data Stats Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Live Time */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-5 w-5 text-emerald-400" />
                  <span className="text-gray-400 text-sm">Live Time</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  {Math.floor(streamStats.liveTime / 3600)}h {Math.floor((streamStats.liveTime % 3600) / 60)}m {streamStats.liveTime % 60}s
                </p>
              </div>
              
              {/* Viewers */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-blue-400" />
                  <span className="text-gray-400 text-sm">Viewers</span>
                </div>
                <p className="text-2xl font-bold text-white">{streamStats.totalViewers}</p>
                <p className="text-xs text-gray-500">Peak: {streamStats.peakViewers}</p>
              </div>
              
              {/* Engaged Viewers */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle className="h-5 w-5 text-purple-400" />
                  <span className="text-gray-400 text-sm">Engaged Viewers</span>
                </div>
                <p className="text-2xl font-bold text-white">{streamStats.engagedViewers}</p>
              </div>
              
              {/* New Rubies */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <RubyIcon className="h-6 w-6" />
                  <span className="text-gray-400 text-sm">New Rubies</span>
                </div>
                <p className="text-2xl font-bold text-red-400">{streamStats.newRubies}</p>
              </div>
              
              {/* Gift Givers */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="h-5 w-5 text-pink-400" />
                  <span className="text-gray-400 text-sm">Gift Givers</span>
                </div>
                <p className="text-2xl font-bold text-white">{streamStats.giftGivers}</p>
              </div>
              
              {/* New Fans */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="h-5 w-5 text-red-500" />
                  <span className="text-gray-400 text-sm">New Fans</span>
                </div>
                <p className="text-2xl font-bold text-white">{streamStats.newFans}</p>
              </div>
            </div>
            
            {/* Thank you message */}
            <div className="text-center py-3">
              <p className="text-gray-400 text-sm">Thank you for streaming with GEMS!</p>
            </div>
            
            {/* Suggested Streams */}
            {suggestedStreams.length > 0 && (
              <div className="border-t border-gray-800 pt-4">
                <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2">
                  <Video className="h-4 w-4 text-red-500" />
                  Watch Other Live Streams
                </h3>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {suggestedStreams.slice(0, 3).map((stream) => (
                    <button
                      key={stream.stream_id}
                      onClick={() => {
                        setShowStreamStatsModal(false);
                        navigate(`/watch/${stream.stream_id}`);
                      }}
                      className="w-full p-3 rounded-lg bg-gray-800/50 hover:bg-gray-800 border border-gray-700 hover:border-emerald-500 transition-all text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-500 rounded-lg flex items-center justify-center text-white font-bold">
                            {stream.streamer_name?.charAt(0) || '?'}
                          </div>
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-white text-sm truncate">{stream.title}</p>
                          <p className="text-xs text-gray-500">{stream.streamer_name}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-emerald-500" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button 
                onClick={() => {
                  setShowStreamStatsModal(false);
                  navigate('/discover');
                }} 
                className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white"
              >
                <Video className="mr-2 h-4 w-4" />
                Browse Streams
              </Button>
              <Button 
                onClick={() => {
                  setShowStreamStatsModal(false);
                  navigate('/');
                }} 
                variant="outline"
                className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                Go Home
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Part A3 (Feb 2026): removed the redundant "Ready to Start Game?"
          dialog. The wager modal now owns partner selection + entry + rules
          in one step. `showStartGameModal` state kept as a no-op flag until
          all callers are cleaned up in a follow-up pass. */}

      {/* In-stream Game Picker Modal (July 26, 2026) — opens after all
          invited broadcasters accept/join. Choice flows into the existing
          start pipeline via selectedGameType. Spades → wager modal;
          Four-In-A-Row → linked 1v1 wager modal (uses invitedPlayers[0]
          as the opponent, matching the existing linked-game flow). */}
      <GamePickerModal
        open={showGamePickerModal}
        onClose={() => setShowGamePickerModal(false)}
        onSelect={(gameId) => {
          setShowGamePickerModal(false);
          setSelectedGameType(gameId);
          if (gameId === 'spades') {
            // Part 3b: route Spades through the table step first.
            setSelectedGameMode('invite');
            setShowSpadesTableModal(true);
          } else if (gameId === 'crazy_eights') {
            // Crazy Eights (2–4) uses the multiplayer invite/start flow, not
            // the 2-player wager modal (the linked broadcasters are players).
            const hasLinkedPartner = connectedCoHosts.length > 0 || invitedPlayers.length > 0;
            if (!hasLinkedPartner) {
              toast.error('Link with at least one broadcaster to play Crazy Eights.');
              return;
            }
            setSelectedGameType('crazy_eights');
            setSelectedGameMode('invite');
            setShowWagerModal(true);
          } else if (gameId === 'checkers') {
            // In-stream 1v1 wager modal (no navigate to /games).
            const partner = connectedCoHosts[0] || invitedPlayers[0];
            if (!partner || !partner.id) {
              toast.error('No linked broadcaster found to play Checkers.');
              return;
            }
            setLinkedGameOpponent({ id: partner.id, name: partner.name });
            setLinkedGameType('checkers');
            setLinkedGameWager(MIN_MATCH_ENTRY);
            setShowLinkedGameWagerModal(true);
          } else if (gameId === 'four_in_a_row') {
            // Route to the existing linked 4-in-a-row 1v1 flow.
            const partner = connectedCoHosts[0] || invitedPlayers[0];
            if (!partner || !partner.id) {
              toast.error('No linked broadcaster found to play Four-In-A-Row.');
              return;
            }
            setLinkedGameOpponent({ id: partner.id, name: partner.name });
            setLinkedGameType('four_in_a_row');
            setLinkedGameWager(MIN_MATCH_ENTRY);
            setShowLinkedGameWagerModal(true);
          }
        }}
      />

      {/* Gift Panel - Small transparent box at bottom */}
      {showGiftModal && (
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/60 p-3" onClick={() => setShowGiftModal(false)}>
          <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <GiftPicker
              emeralds={user?.emeralds || 0}
              onClose={() => setShowGiftModal(false)}
              onSend={() => {
                toast.info('This is your own stream — viewers send gifts to you here.');
                setShowGiftModal(false);
              }}
            />
          </div>
        </div>
      )}

      {/* Bidding Phase Modal - During Live Stream */}
      {isInGame && gamePhase === 'bidding' && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md mx-4">
            <h2 className="text-2xl font-bold text-center mb-6">Place Your Bid</h2>
            <p className="text-center text-gray-600 mb-2">How many books will you take?</p>
            <p className="text-center text-sm text-emerald-600 mb-6">🔴 Live viewers are watching!</p>
            <div className="grid grid-cols-7 gap-2 mb-6">
              {[0, 1, 2, 3, 4, 5, 6].map(bid => (
                <Button
                  key={bid}
                  onClick={() => handleBid(bid)}
                  variant={myBid === bid ? "default" : "outline"}
                  className="aspect-square"
                >
                  {bid}
                </Button>
              ))}
            </div>
            <div className="text-center">
              <Button onClick={() => handleBid(myBid)} className="w-full bg-emerald-600 hover:bg-emerald-700">
                Confirm Bid: {myBid} {myBid === 1 ? 'Book' : 'Books'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Tournaments Modal */}
      <Dialog open={showTournamentsModal} onOpenChange={setShowTournamentsModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <span className="text-2xl">🏆</span>
              Spades Tournaments
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-700 font-medium flex items-center gap-2">
                <span>🚀</span> Tournaments Coming Soon!
              </p>
              <p className="text-sm text-amber-600 mt-1">
                Compete against other players in Bronze, Silver, and Gold tier tournaments.
              </p>
            </div>
            
            {/* Tournament Tiers */}
            <div className="space-y-3">
              {/* Bronze */}
              <div className="border-2 border-amber-600 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-amber-800 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🥉</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-amber-800">Bronze Championship</h3>
                    <p className="text-sm text-amber-700">Entry: 250 Emeralds</p>
                  </div>
                  <Badge className="bg-gray-400 text-white">Coming Soon</Badge>
                </div>
              </div>
              
              {/* Silver */}
              <div className="border-2 border-gray-400 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🥈</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800">Silver Championship</h3>
                    <p className="text-sm text-gray-700">Entry: 500 Emeralds</p>
                  </div>
                  <Badge className="bg-gray-400 text-white">Coming Soon</Badge>
                </div>
              </div>
              
              {/* Gold */}
              <div className="border-2 border-yellow-500 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-700 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🥇</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-yellow-800">Gold Championship</h3>
                    <p className="text-sm text-yellow-700">Entry: 1,000 Emeralds</p>
                  </div>
                  <Badge className="bg-gray-400 text-white">Coming Soon</Badge>
                </div>
              </div>
            </div>
            
            <div className="text-center text-gray-500 text-sm">
              Stay tuned for tournament announcements! 🎮
            </div>
            
            <Button onClick={() => setShowTournamentsModal(false)} variant="outline" className="w-full">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Private Beta Restriction Modal */}
      <BetaRestrictionModal 
        isOpen={showBetaModal} 
        onClose={() => setShowBetaModal(false)} 
        featureName="Go Live Streaming"
      />
      
      {/* Share Stream Modal for Broadcaster */}
      <ShareStreamModal
        isOpen={showShareStreamModal}
        onClose={() => setShowShareStreamModal(false)}
        streamId={currentStream?.stream_id || currentStream?.id}
        streamerName={user?.name || 'Streamer'}
        viewerCount={displayViewers}
        isHost={true}
        api={api}
        userId={user?.id}
      />
    </div>
  );
};

const DiscoverPage = () => {
  const { user, setUser } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const { isPlus: userIsPlus } = useIsPlus();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('live-now');
  
  // Game/type filter chips (replaces the old tab row). Values:
  // 'all' | 'spades' | 'four_in_a_row' | 'checkers' | 'crazy_eights'
  // | 'audio' | 'irl' | 'nearby'
  const [liveFilter, setLiveFilter] = useState('all');
  // Discovery mode toggle above Live Now: 'for_you' | 'following' | 'nearby'
  const [discoveryMode, setDiscoveryMode] = useState('for_you');
  
  // Feed state
  const [liveStreams, setLiveStreams] = useState([]);
  const [irlStreams, setIrlStreams] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Creator posts state
  const [creatorPosts, setCreatorPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  
  // Go Live preview state
  const [streamMode, setStreamMode] = useState('camera'); // camera only on Discover (screen-share lives in the live control bar, not here)
  // Live TYPE (not capture source): go_live = video + games, irl = video no games,
  // audio_live = coming soon (locked). Passed to the stream on go-live.
  const [liveType, setLiveType] = useState('go_live');
  const [localStream, setLocalStream] = useState(null);
  const [isMicEnabled, setIsMicEnabled] = useState(true);
  const [isCamEnabled, setIsCamEnabled] = useState(true);
  const [includeSystemAudio, setIncludeSystemAudio] = useState(false);
  const [showWebcamPip, setShowWebcamPip] = useState(false);
  const [pipPosition, setPipPosition] = useState('bottom-right');
  const [networkQuality, setNetworkQuality] = useState('Good');
  const [availableCameras, setAvailableCameras] = useState([]);
  const [availableMics, setAvailableMics] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [selectedMic, setSelectedMic] = useState('');
  const videoRef = useRef(null);

  // Round 13: blocked users never appear in the feed
  const { hiddenIds: modHidden = new Set() } = React.useContext(AuthContext) || {};

  // Fetch creator posts
  const fetchCreatorPosts = async () => {
    setPostsLoading(true);
    try {
      const response = await api.get('/posts?limit=20');
      // A successful refresh that comes back empty must not wipe a loaded feed.
      const next = response.data.posts || [];
      setCreatorPosts(prev => (next.length === 0 && prev.length > 0) ? prev : next);
    } catch (error) {
      console.error('Failed to load creator posts:', error);
    } finally {
      setPostsLoading(false);
    }
  };

  // Like a post
  // Like a post — PAID (1 Emerald → 0.75 Rubies to creator). One-time
  // confirm on first-ever like (per browser). No unlike / no refund — a
  // like is a paid tip. Server is idempotent per user/post.
  const handleLikePost = async (postId) => {
    const target = creatorPosts.find(p => p.id === postId);
    if (target && (target.liked_by || []).includes(user?.id)) return;
    try {
      const response = await api.post(`/posts/${postId}/like`);
      const d = response.data || {};
      setCreatorPosts(prev => prev.map(post =>
        post.id === postId
          ? { ...post, likes: (typeof d.likes === 'number' ? d.likes : (post.likes || 0) + 1), liked_by: [...(post.liked_by || []), user?.id] }
          : post
      ));
      if (typeof d.emeralds === 'number' && setUser) {
        setUser(prev => (prev ? { ...prev, emeralds: d.emeralds } : prev));
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 402) toast.error("You can't like this — a like costs 1 Emerald and you're out.", { action: { label: 'Get Emeralds', onClick: () => navigate('/wallet') } });
      else if (status === 400) toast.info("You can't like your own post.");
      else { console.error('Failed to like post:', error); toast.error('Failed to like post'); }
    }
  };

  // Comment on a post
  const [commentingPostId, setCommentingPostId] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [postComments, setPostComments] = useState({});

  const handleAddComment = async (postId) => {
    if (!newComment.trim()) return;
    try {
      const response = await api.post(`/posts/${postId}/comment`, { content: newComment.trim() });
      // Update comments for this post
      setPostComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), response.data.comment]
      }));
      // Update comment count
      setCreatorPosts(prev => prev.map(post => 
        post.id === postId 
          ? { ...post, comments_count: post.comments_count + 1 }
          : post
      ));
      setNewComment('');
      toast.success('Comment added!');
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const loadComments = async (postId) => {
    try {
      const response = await api.get(`/posts/${postId}/comments?limit=10`);
      setPostComments(prev => ({
        ...prev,
        [postId]: response.data.comments || []
      }));
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  // Fetch feeds
  const fetchFeeds = async () => {
    setLoading(true);
    try {
      const [liveResponse, irlResponse] = await Promise.all([
        api.get('/feeds/live-now').catch(() => ({ data: [] })),
        api.get('/feeds/irl-streams').catch(() => ({ data: [] })),
      ]);
      
      // Remove duplicates by stream_id and filter out test streams
      const uniqueLiveStreams = (liveResponse.data || [])
        .filter((stream, index, self) =>
          index === self.findIndex((s) => s.stream_id === stream.stream_id)
        )
        // Round 6 FIX: the old "test-title" filter silently hid ANY real
        // stream whose title contained the word "test" (a live titled
        // "Testing" never appeared in Discover). Only the internal
        // viewer-tracking fixture stays hidden.
        .filter(stream => (stream.title || '').toLowerCase() !== 'viewer tracking');
      
      const uniqueIrlStreams = (irlResponse.data || [])
        .filter((stream, index, self) =>
          index === self.findIndex((s) => s.stream_id === stream.stream_id)
        )
        .filter(stream => (stream.title || '').toLowerCase() !== 'viewer tracking');
      
      setLiveStreams(uniqueLiveStreams);
      setIrlStreams(uniqueIrlStreams);
      // Tables in Progress / Looking to Play tabs removed — chip filter row
      // (all / games / IRL / nearby) filters `liveStreams` client-side.
    } catch (error) {
      console.error('Failed to load feeds:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get available devices
  const getDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(d => d.kind === 'videoinput' && d.deviceId);
      const mics = devices.filter(d => d.kind === 'audioinput' && d.deviceId);
      
      setAvailableCameras(cameras);
      setAvailableMics(mics);
      
      if (cameras.length > 0 && !selectedCamera) {
        setSelectedCamera(cameras[0].deviceId || 'default-camera');
      }
      if (mics.length > 0 && !selectedMic) {
        setSelectedMic(mics[0].deviceId || 'default-mic');
      }
    } catch (error) {
      console.error('Error enumerating devices:', error);
    }
  };

  // Start camera preview
  const startCameraPreview = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: selectedCamera ? { deviceId: selectedCamera } : true,
        audio: selectedMic ? { deviceId: selectedMic } : true
      });
      
      setLocalStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error starting camera:', error);
      toast.error('Failed to access camera/microphone');
    }
  };

  // Start screen capture
  const startScreenCapture = async () => {
    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'monitor' },
        audio: includeSystemAudio
      });
      
      setLocalStream(displayStream);
      if (videoRef.current) {
        videoRef.current.srcObject = displayStream;
      }
    } catch (error) {
      console.error('Error starting screen capture:', error);
      toast.error('Screen capture cancelled or failed');
    }
  };

  // Toggle mic
  const toggleMic = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => {
        track.enabled = !isMicEnabled;
      });
      setIsMicEnabled(!isMicEnabled);
    }
  };

  // Toggle camera
  const toggleCam = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach(track => {
        track.enabled = !isCamEnabled;
      });
      setIsCamEnabled(!isCamEnabled);
    }
  };

  // Handle stream mode change
  const handleModeChange = async (mode) => {
    // Stop current stream
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }
    
    setStreamMode(mode);
    
    // Start new stream based on mode
    if (mode === 'camera') {
      await startCameraPreview();
    } else {
      await startScreenCapture();
    }
  };

  // Start streaming (navigate to Live page with mode)
  const handleStartStreaming = () => {
    // Don't stop tracks - pass them to LivePage to reuse and avoid double getUserMedia
    navigate('/live', { 
      state: { 
        mode: streamMode, 
        live_type: liveType,
        includeSystemAudio, 
        showWebcamPip, 
        pipPosition,
        skipPreviewInit: true
      } 
    });
  };

  // Initialize
  useEffect(() => {
    fetchFeeds();
    fetchCreatorPosts();
    const interval = setInterval(fetchFeeds, 30000); // Poll every 30s as per spec
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    getDevices();
    startCameraPreview();
    
    return () => {
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--gems-bg-0)' }}>
      <NavBar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Non-Plus Go-Live CTA (auto-hides for Plus members). */}
        {userIsPlus === false && (
          <div
            data-testid="plus-cta-golive"
            className="mb-6 rounded-2xl border shadow-lg overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(16,185,129,0.14), rgba(16,185,129,0.04))',
              borderColor: 'rgba(16,185,129,0.35)',
            }}
          >
            <div className="p-5 sm:p-6 flex flex-wrap items-center gap-4">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <span className="text-2xl">✦</span>
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 text-emerald-300 text-xs font-semibold tracking-wide uppercase">
                  GEMS Plus · <PlusPriceTag /> 
                </div>
                <div className="mt-0.5 text-lg font-semibold" style={{ color: 'var(--gems-text)' }}>
                  Stream more, keep more.
                </div>
                <div className="mt-0.5 text-sm" style={{ color: 'var(--gems-text-dim)' }}>
                  1,200 Emeralds/mo · 5% platform fee at your tables · half-price Ruby → Emerald conversions (1.5% vs 3%) · subscriber badge · early game access.
                </div>
              </div>
              <button
                type="button"
                onClick={startGemsPlusFlow}
                className="px-5 py-2.5 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600 shadow-sm"
              >
                Upgrade to GEMS Plus
              </button>
            </div>
          </div>
        )}
        {/* HERO: Go Live. Have Fun. Earn. (mockup styling — colorful gradient + crown) */}
        <div className="mb-12 rounded-2xl p-8 border shadow-2xl relative overflow-hidden" style={{
          background: 'linear-gradient(120deg, rgba(52,227,164,.16), rgba(16,22,32,.62) 42%, rgba(230,195,74,.12))',
          borderColor: 'rgba(255,255,255,.14)'
        }}>
          <span aria-hidden="true" style={{ position:'absolute', right:'-12px', top:'-34px', fontSize:'150px', opacity:.08, pointerEvents:'none', lineHeight:1 }}>♛</span>
          <h1 className="font-black mb-2" style={{ color: 'var(--gems-text)', fontSize: '46px', lineHeight: '1.05', letterSpacing: '-0.02em' }}>Go Live. Have Fun. Earn.</h1>
          <p className="mb-8" style={{ color: 'var(--gems-text-dim)', fontSize: '15.5px' }}>Pick how you want to broadcast. Viewers watch, chat, and gift — winners earn withdrawable Rubies.</p>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* LEFT: Live Preview */}
            <div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4" data-testid="golive-type-picker">
                {[
                  { id: 'go_live', label: 'Go Live', icon: '📹', sub: 'Video · play games', tag: 'Play games', enabled: true },
                  { id: 'party', label: 'Party', icon: '🎉', sub: 'Up to 16 on camera · one room', tag: 'Up to 16', enabled: true },
                  { id: 'audio_live', label: 'Audio Live', icon: '🎙️', sub: 'Up to 12 · talk & vibe', tag: 'Coming soon', enabled: false },
                  { id: 'irl', label: 'IRL', icon: '🌆', sub: 'Outdoor · no games', tag: 'Outdoor', enabled: true },
                ].map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    disabled={!m.enabled}
                    onClick={() => m.enabled && setLiveType(m.id)}
                    className="rounded-2xl p-3.5 text-left transition-all"
                    style={{
                      backgroundColor: liveType === m.id ? 'rgba(52,227,164,.12)' : 'rgba(8,12,18,.5)',
                      border: liveType === m.id ? '1px solid var(--gems-emerald)' : '1px solid rgba(255,255,255,.09)',
                      opacity: m.enabled ? 1 : 0.55,
                      cursor: m.enabled ? 'pointer' : 'not-allowed',
                    }}
                    title={m.enabled ? '' : 'Audio Live is coming soon'}
                    data-testid={`golive-type-${m.id}`}
                  >
                    <div style={{ fontSize: '20px', marginBottom: '6px' }}>{m.icon}</div>
                    <div className="font-bold text-sm" style={{ color: 'var(--gems-text)' }}>{m.label}</div>
                    <div className="text-[11px] mt-0.5 leading-snug" style={{ color: 'var(--gems-text-dim)' }}>{m.sub}</div>
                    <span className="inline-block mt-2 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{
                      background: liveType === m.id ? 'rgba(52,227,164,.16)' : 'rgba(255,255,255,.06)',
                      color: liveType === m.id ? 'var(--gems-emerald)' : 'var(--gems-text-dim)',
                    }}>{m.tag}</span>
                  </button>
                ))}
              </div>
              
              {/* Video Preview */}
              <div className="relative rounded-xl overflow-hidden aspect-video mb-4 border" style={{
                backgroundColor: 'var(--gems-bg-0)',
                borderColor: 'var(--card-border)'
              }}>
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                />
                
                {/* Controls Overlay */}
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <div className="flex gap-2">
                    <button
                      onClick={toggleMic}
                      className="p-2 rounded-full transition-colors"
                      style={{
                        backgroundColor: isMicEnabled ? 'var(--gems-emerald)' : 'var(--gems-red)',
                        color: '#FFFFFF'
                      }}
                    >
                      {isMicEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={toggleCam}
                      className="p-2 rounded-full transition-colors"
                      style={{
                        backgroundColor: isCamEnabled ? 'var(--gems-emerald)' : 'var(--gems-red)',
                        color: '#FFFFFF'
                      }}
                    >
                      {isCamEnabled ? <VideoIcon className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                    </button>
                  </div>
                  
                  {/* Network Quality */}
                  <div className="px-3 py-1 rounded-full text-sm flex items-center gap-2" style={{
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    color: 'var(--gems-text)'
                  }}>
                    <Signal className="h-4 w-4" style={{ color: 'var(--gems-emerald)' }} />
                    <span>{networkQuality}</span>
                  </div>
                </div>
              </div>
              
              {/* Input Selectors */}
              <div className="space-y-3">
                <div>
                  <label className="text-sm mb-1 block" style={{ color: 'var(--gems-text-dim)' }}>Camera</label>
                  <Select value={selectedCamera} onValueChange={setSelectedCamera}>
                    <SelectTrigger style={{ 
                      backgroundColor: 'var(--gems-bg-0)', 
                      borderColor: 'var(--card-border)',
                      color: 'var(--gems-text)'
                    }}>
                      <SelectValue placeholder="Select camera" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCameras.length > 0 ? (
                        availableCameras.map((cam, index) => (
                          <SelectItem key={cam.deviceId || `cam-${index}`} value={cam.deviceId || `default-cam-${index}`}>
                            {cam.label || `Camera ${index + 1}`}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-camera" disabled>No cameras found</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm mb-1 block" style={{ color: 'var(--gems-text-dim)' }}>Microphone</label>
                  <Select value={selectedMic} onValueChange={setSelectedMic}>
                    <SelectTrigger style={{ 
                      backgroundColor: 'var(--gems-bg-0)', 
                      borderColor: 'var(--card-border)',
                      color: 'var(--gems-text)'
                    }}>
                      <SelectValue placeholder="Select microphone" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableMics.length > 0 ? (
                        availableMics.map((mic, index) => (
                          <SelectItem key={mic.deviceId || `mic-${index}`} value={mic.deviceId || `default-mic-${index}`}>
                            {mic.label || `Microphone ${index + 1}`}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-mic" disabled>No microphones found</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Screen-share options intentionally removed from Discover —
                    screen sharing belongs in the in-stream control bar. */}
              </div>
            </div>
            
            {/* RIGHT: Start Button */}
            <div className="flex flex-col justify-center items-center">
              <button
                onClick={handleStartStreaming}
                className="px-16 py-6 text-2xl rounded-2xl shadow-2xl transform hover:scale-[1.03] transition-all w-full max-w-md font-black"
                style={{
                  background: 'linear-gradient(90deg, var(--gems-emerald), #22C98C)',
                  color: '#04150e'
                }}
              >
                <div className="flex items-center justify-center gap-3">
                  Start streaming
                  <span>→</span>
                </div>
              </button>
              
              <p className="text-sm mt-6 text-center max-w-md" style={{ color: 'var(--gems-text-dim)' }}>
                {liveType === 'irl'
                  ? 'IRL mode — take viewers outside. Games are off in IRL.'
                  : 'Go Live with your camera. Challenge other broadcasters to games once you\u2019re live. Share your screen from the live controls.'}
              </p>
            </div>
          </div>
        </div>
        
        {/* LEADERBOARD moved to bottom of the page — see below (after Tournaments). */}
        
        {/* Discovery mode toggle (above Live Now) */}
        <div className="mb-3 flex items-center gap-2" data-testid="discover-mode-row">
          {[
            { id: 'for_you', label: 'For You' },
            { id: 'following', label: 'Following' },
            { id: 'nearby', label: 'Nearby' },
          ].map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setDiscoveryMode(m.id)}
              className="rounded-full px-4 py-1.5 text-sm font-semibold transition-all"
              style={{
                backgroundColor: discoveryMode === m.id ? 'var(--gems-emerald)' : 'transparent',
                color: discoveryMode === m.id ? '#04150e' : 'var(--gems-text-dim)',
                border: discoveryMode === m.id ? 'none' : '1px solid var(--card-border)',
              }}
              data-testid={`discover-mode-${m.id}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {(() => {
          // Dynamic Discover order: lives on top when anyone is live,
          // otherwise From the Community first with the empty live card below.
          const hasLives = ((liveStreams?.length || 0) + (irlStreams?.length || 0)) > 0;
          const liveNowSection = (
            <React.Fragment key="live-now-section">
        {/* LIVE NOW — filter chip row (replaces the old tab bar). Combined
            feed merges live game streams + IRL streams so a single chip
            filter can slice across both. */}
        <div className="mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2 mb-3" style={{ color: 'var(--gems-text)' }}>
            <Video className="h-5 w-5" style={{ color: 'var(--gems-emerald)' }} /> Live Now
          </h2>
          <div className="flex flex-wrap gap-2" data-testid="live-filter-chips">
            {[
              { id: 'all', label: 'All' },
              { id: 'spades', label: 'Spades' },
              { id: 'four_in_a_row', label: '4-In-A-Row' },
              { id: 'checkers', label: 'Checkers' },
              { id: 'crazy_eights', label: 'Crazy Eights' },
              { id: 'audio', label: 'Audio' },
              { id: 'irl', label: 'IRL' },
              { id: 'nearby', label: 'Nearby' },
            ].map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setLiveFilter(c.id)}
                className="rounded-full px-3.5 py-1.5 text-xs font-semibold tracking-wide uppercase transition-all"
                style={{
                  backgroundColor: liveFilter === c.id ? 'var(--gems-emerald)' : 'transparent',
                  color: liveFilter === c.id ? '#04150e' : 'var(--gems-text-dim)',
                  border: liveFilter === c.id ? 'none' : '1px solid var(--card-border)',
                }}
                data-testid={`live-chip-${c.id}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mb-12">
          <LiveNowFeed
            streams={[...(liveStreams || []), ...(irlStreams || [])]}
            loading={loading}
            filter={liveFilter}
            discoveryMode={discoveryMode}
          />
        </div>
            </React.Fragment>
          );
          const communitySection = (
            <React.Fragment key="community-section">
        {/* CREATOR'S POST — right beneath Live Now (per redesign). */}
        <div className="mb-12">
          {/* Round 6: growth rewards — checklist + daily bonus greeting */}
          <GrowthRewardsPanel />
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--gems-text)' }}>From the Community</h2>
          <div className="h-96 rounded-xl overflow-y-auto p-4 space-y-4 border" style={{ 
            backgroundColor: 'var(--gems-bg-1)', 
            borderColor: 'var(--card-border)' 
          }}>
            {postsLoading ? (
              <div className="h-full flex items-center justify-center">
                <p style={{ color: 'var(--gems-text-dim)' }}>Loading posts...</p>
              </div>
            ) : creatorPosts.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center">
                <MessageCircle className="h-12 w-12 mb-4 opacity-50" style={{ color: 'var(--gems-text-dim)' }} />
                <p style={{ color: 'var(--gems-text-dim)' }}>No posts yet.</p>
                <p className="text-sm mt-2" style={{ color: 'var(--gems-text-dim)' }}>Go to your Profile to create a post!</p>
              </div>
            ) : (
              creatorPosts.filter((p) => !modHidden.has(p.user_id)).map(post => (
                <div key={post.id} className="rounded-xl p-4 border hover:border-opacity-30 transition-colors" style={{
                  backgroundColor: 'var(--gems-bg-0)',
                  borderColor: 'var(--card-border)'
                }}>
                  <div className="flex items-start gap-3">
                    {/* User Avatar */}
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-lg flex-shrink-0 overflow-hidden cursor-pointer"
                      style={{ backgroundColor: 'var(--gems-emerald)' }}
                      onClick={() => navigate(`/profile/${post.user_handle || post.user_id}`)}
                    >
                      {post.user_picture ? (
                        <img src={post.user_picture} alt={post.user_name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white font-bold">{post.user_name?.[0] || '?'}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span 
                          className="font-semibold cursor-pointer hover:underline" 
                          style={{ color: 'var(--gems-text)' }}
                          onClick={() => navigate(`/profile/${post.user_handle || post.user_id}`)}
                        >
                          {post.user_name}
                        </span>
                        {post.user_badge === 'sapphire' && (<SapphireBadgeIcon className="w-4 h-4" />)}
                        {post.user_badge === 'canary' && (<CanaryBadgeIcon className="w-4 h-4" />)}
                        {post.user_badge === 'gold_gem' && (<CanaryBadgeIcon className="w-4 h-4" />)}
                        {post.user_handle && (
                          <span className="text-sm" style={{ color: 'var(--gems-text-dim)' }}>@{post.user_handle}</span>
                        )}
                        <span className="text-xs" style={{ color: 'var(--gems-text-dim)' }}>
                          {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                        {post.edited_at && (
                          <span className="text-[10px] italic" style={{ color: 'var(--gems-text-dim)' }}>edited</span>
                        )}
                        {post.user_id !== user?.id && (
                          <span className="ml-auto flex items-center gap-2">
                            {/* Round 13b: admins can remove any post in-app (edit stays author-only) */}
                            {user?.is_admin && (
                              <button
                                className="text-xs px-2 py-0.5 rounded border"
                                style={{ color: '#F87171', borderColor: 'rgba(248,113,113,0.4)' }}
                                data-testid={`admin-delete-post-${post.id}`}
                                onClick={async () => {
                                  if (!window.confirm('Remove this post as an admin? This can\'t be undone.')) return;
                                  try { await api.delete(`/posts/${post.id}`); setCreatorPosts(prev => prev.filter(p => p.id !== post.id)); toast.success('Post removed'); }
                                  catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
                                }}
                              >Delete (admin)</button>
                            )}
                            <ModerationMenu targetType="post" targetId={post.id} targetUserId={post.user_id} targetName={post.user_name} snapshot={post.content} />
                          </span>
                        )}
                        {post.user_id === user?.id && (
                          /* Round 6: creators can fix or remove their own posts */
                          <span className="ml-auto flex items-center gap-2">
                            <button
                              className="text-xs px-2 py-0.5 rounded border"
                              style={{ color: 'var(--gems-text-dim)', borderColor: 'var(--card-border)' }}
                              data-testid={`edit-post-${post.id}`}
                              onClick={async () => {
                                const next = window.prompt('Edit your post', post.content || '');
                                if (next == null || !next.trim() || next === post.content) return;
                                try {
                                  const r = await api.patch(`/posts/${post.id}`, { content: next.trim() });
                                  setCreatorPosts(prev => prev.map(p => p.id === post.id ? { ...p, ...(r.data?.post || { content: next.trim() }) } : p));
                                  toast.success('Post updated');
                                } catch (err) { toast.error(err.response?.data?.detail || 'Edit failed'); }
                              }}
                            >Edit</button>
                            <button
                              className="text-xs px-2 py-0.5 rounded border"
                              style={{ color: '#F87171', borderColor: 'rgba(248,113,113,0.4)' }}
                              data-testid={`delete-post-${post.id}`}
                              onClick={async () => {
                                if (!window.confirm('Delete this post? This can\'t be undone.')) return;
                                try {
                                  await api.delete(`/posts/${post.id}`);
                                  setCreatorPosts(prev => prev.filter(p => p.id !== post.id));
                                  toast.success('Post deleted');
                                } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
                              }}
                            >Delete</button>
                          </span>
                        )}
                      </div>
                      <p className="text-sm mb-3 whitespace-pre-wrap" style={{ color: 'var(--gems-text)' }}>{post.content}</p>
                      {/* Round 23: Table Talk news / score cards */}
                      <CurrentEventsPostBody post={post} />
                      {/* Round 7: repost — quoted original */}
                      {post.repost_of && (
                        <div className="mb-3 rounded-lg border p-3" style={{ borderColor: 'var(--card-border)', background: 'rgba(4,10,8,0.4)' }}>
                          <div className="text-xs font-bold" style={{ color: 'var(--gems-emerald)' }}>
                            {post.repost_of.user_name}{post.repost_of.user_handle ? ` @${post.repost_of.user_handle}` : ''}
                          </div>
                          {post.repost_of.content && (
                            <p className="text-sm mt-1" style={{ color: 'var(--gems-text-dim)' }}>{post.repost_of.content}</p>
                          )}
                          {post.repost_of.media_url && post.repost_of.post_type !== 'video' && (
                            <img src={post.repost_of.media_url} alt="" className="mt-2 rounded max-h-40 object-cover" />
                          )}
                        </div>
                      )}
                      {post.media_url && (
                        <div className="mb-3 rounded-lg overflow-hidden">
                          {post.post_type === 'video' ? (
                            <video src={post.media_url} controls className="max-w-full max-h-64 rounded" />
                          ) : (
                            <img
                              src={post.media_url}
                              alt="Post media"
                              className="max-w-full max-h-64 rounded object-cover cursor-pointer"
                              onClick={() => openMediaLightbox(post.media_url, 'image', post.content)}
                              data-testid={`feed-post-media-${post.id}`}
                            />
                          )}
                        </div>
                      )}
                      {/* Actions — Rose Quartz "Like" (visual only this stage). */}
                      <div className="flex items-center gap-4">
                        <button
                          className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors border"
                          style={{
                            color: post.liked_by?.includes(user?.id) ? '#F472B6' : 'var(--gems-text-dim)',
                            borderColor: post.liked_by?.includes(user?.id) ? 'rgba(244,114,182,0.55)' : 'var(--card-border)',
                            backgroundColor: post.liked_by?.includes(user?.id) ? 'rgba(244,114,182,0.10)' : 'transparent',
                          }}
                          onClick={() => handleLikePost(post.id)}
                          data-testid={`like-post-${post.id}`}
                          title="Rose Quartz like"
                        >
                          <RoseQuartzIcon className="h-4 w-4" />
                          <span className="text-sm font-semibold">Like · {post.likes || 0}</span>
                          <span className="text-[10px] uppercase tracking-wide opacity-70 inline-flex items-center gap-0.5">1 <RoseQuartzIcon className="h-3 w-3" />/like</span>
                        </button>
                        <button 
                          className="flex items-center gap-1 transition-colors" 
                          style={{ color: commentingPostId === post.id ? 'var(--gems-emerald)' : 'var(--gems-text-dim)' }}
                          onClick={() => {
                            if (commentingPostId === post.id) {
                              setCommentingPostId(null);
                            } else {
                              setCommentingPostId(post.id);
                              loadComments(post.id);
                            }
                          }}
                        >
                          <MessageCircle className="h-4 w-4" />
                          <span className="text-sm">{post.comments_count || 0}</span>
                        </button>
                        {/* Round 7: repost to the community with a quote */}
                        <button
                          className="flex items-center gap-1 transition-colors"
                          style={{ color: 'var(--gems-text-dim)' }}
                          data-testid={`share-post-${post.id}`}
                          onClick={async () => {
                            const quote = window.prompt('Say something about this post (optional):', '');
                            if (quote === null) return;
                            try {
                              const r = await api.post(`/posts/${post.id}/repost`, { quote: quote.trim() });
                              if (r.data?.post) setCreatorPosts(prev => [r.data.post, ...prev]);
                              toast.success('Reposted to the community!');
                            } catch (err) { toast.error(err.response?.data?.detail || 'Repost failed'); }
                          }}
                        >
                          <Share2 className="h-4 w-4" />
                          <span className="text-sm">{post.shares || 0}</span>
                        </button>
                        <span className="flex items-center gap-1" style={{ color: 'var(--gems-text-dim)' }}>
                          <Eye className="h-4 w-4" />
                          <span className="text-sm">{post.views || 0}</span>
                        </span>
                      </div>
                      {commentingPostId === post.id && (
                        <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--card-border)' }}>
                          <div className="flex gap-2 mb-3">
                            <MentionBox
                              value={newComment}
                              onChange={setNewComment}
                              onSubmit={() => handleAddComment(post.id)}
                              placeholder="Write a comment... (@ to mention)"
                              api={api}
                              className="flex-1"
                            />
                            <button
                              onClick={() => handleAddComment(post.id)}
                              className="px-3 py-2 rounded-lg text-sm font-medium"
                              style={{ backgroundColor: 'var(--gems-emerald)', color: '#FFFFFF' }}
                            >
                              <Send className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {(postComments[post.id] || []).filter((c) => !modHidden.has(c.user_id)).map(comment => (
                              <div key={comment.id} className="flex gap-2 text-sm">
                                <div className="w-6 h-6 rounded-full flex-shrink-0 overflow-hidden" style={{ backgroundColor: 'var(--gems-emerald)' }}>
                                  {comment.user_picture ? (
                                    <img src={comment.user_picture} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="text-white text-xs flex items-center justify-center h-full">{comment.user_name?.[0]}</span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="font-medium" style={{ color: 'var(--gems-text)' }}>{comment.user_name}</span>
                                  <span className="ml-2" style={{ color: 'var(--gems-text-dim)' }}>{comment.content}</span>
                                </div>
                                <ModerationMenu targetType="comment" targetId={comment.id} targetUserId={comment.user_id} targetName={comment.user_name} snapshot={comment.content} />
                              </div>
                            ))}
                            {(postComments[post.id] || []).length === 0 && (
                              <p className="text-sm" style={{ color: 'var(--gems-text-dim)' }}>No comments yet. Be the first!</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
            </React.Fragment>
          );
          return hasLives
            ? <>{liveNowSection}{communitySection}</>
            : <>{communitySection}{liveNowSection}</>;
        })()}

        {/* CREATOR'S POST moved directly under Live Now — see above. */}
        {/* Round 53: real tournaments (announced / sign-up open / tonight) — the web's banner; hidden when there is none */}
        <TournamentCards />

        {/* BOTTOM: Leaderboard (moved from top per the redesign). */}
        <div className="mb-12" data-testid="discover-leaderboard-bottom">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2" style={{ color: 'var(--gems-text)' }}>
              <Trophy className="h-5 w-5 text-yellow-500" /> Today's Leaderboard
            </h2>
            <button
              onClick={() => navigate('/leaderboards')}
              className="text-emerald-400 text-xs font-mono border border-emerald-500/30 rounded-lg px-3 py-1.5 hover:bg-emerald-500/10 transition-colors"
              data-testid="discover-leaderboard-link"
            >
              Full Board
            </button>
          </div>
          <LeaderboardWidget compact={false} showUserRank={true} />
        </div>
        
        {/* (Live Now moved up — right beneath Go Live.) */}
      </div>
    </div>
  );
};

const LiveNowFeed = ({ streams, loading, filter = 'all', discoveryMode = 'for_you' }) => {
  // Round 13: blocked broadcasters never show in Live Now
  const { hiddenIds: feedHidden = new Set() } = React.useContext(AuthContext) || {};
  // Client-side filter: chip → stream match. `game_type` on the stream
  // powers the game chips; `live_type === 'irl'` powers the IRL chip;
  // `nearby` uses the top-level discovery toggle OR the chip alias.
  const filtered = React.useMemo(() => {
    const list = (streams || []).filter((s) => !feedHidden.has(s.user_id));
    const matchGame = (s, id) => (s.game_type || s.stream_game_type || '') === id;
    let out = list;
    if (filter === 'irl') {
      out = list.filter((s) => s.live_type === 'irl' || s.stream_type === 'IRL');
    } else if (filter === 'audio') {
      out = list.filter((s) => s.live_type === 'audio_live');
    } else if (filter === 'nearby' || discoveryMode === 'nearby') {
      // Nearby is a stub this stage — pass everything through, sorted by
      // (future) distance. Backend will provide `distance_km` later.
      out = [...list].sort((a, b) => (a.distance_km || 999) - (b.distance_km || 999));
    } else if (['spades', 'four_in_a_row', 'checkers', 'crazy_eights'].includes(filter)) {
      out = list.filter((s) => matchGame(s, filter));
    }
    if (discoveryMode === 'following') {
      // Frontend has no follow-graph data on the stream doc yet — leaving
      // this a no-op to avoid blanking the feed. Wire when /streams/following
      // ships.
    }
    return out;
  }, [streams, filter, discoveryMode, feedHidden]);

  if (loading) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Card key={i} className="animate-pulse">
            <div className="aspect-video bg-gray-200 rounded-t-lg"></div>
            <CardContent className="p-4 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="live-now-grid">
      {filtered.map((stream, i) => (
        <StreamCard key={stream.id || stream.stream_id} stream={stream} featured={i === 0 && filtered.length > 1} />
      ))}
      
      {filtered.length === 0 && (
        <div className="col-span-full text-center py-12">
          <Video className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No one's live right now.</h3>
          <p className="text-gray-500">
            Be the one who starts it.{' '}
            <button
              onClick={() => navigate('/live')}
              className="text-emerald-400 font-bold hover:text-emerald-300 underline underline-offset-4"
              data-testid="empty-go-live"
            >
              Go Live ↗
            </button>
          </p>
        </div>
      )}
    </div>
  );
};

const StreamCard = ({ stream, type = 'live', featured = false }) => {
  const navigate = useNavigate();

  const handleWatch = () => {
    if (stream.stream_id) {
      navigate(`/watch/${stream.stream_id}`);
    } else if (stream.game_id) {
      navigate(`/game/${stream.game_id}`);
    }
  };

  const isIRL = stream.live_type === 'irl' || stream.stream_type === 'IRL';
  const isAudio = stream.live_type === 'audio_live';
  const isParty = !!stream.party || stream.live_type === 'party';   // Round 63
  const gameLabel = (stream.game_type || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const badgeLabel = isParty ? 'Party' : isIRL ? 'IRL' : isAudio ? 'Audio' : (gameLabel || 'Live');
  const badgeColor = isParty ? '#F5C542' : isIRL ? '#E6C34A' : isAudio ? '#A78BFA' : '#34E3A4';
  const badgeBg = isParty ? 'rgba(245,197,66,0.16)' : isIRL ? 'rgba(230,195,74,0.14)' : isAudio ? 'rgba(167,139,250,0.18)' : 'rgba(52,227,164,0.14)';
  const linkedCount = stream.broadcaster_count || (stream.linked_broadcasters && stream.linked_broadcasters.length) || (stream.is_linked ? 2 : 0);

  return (
    <div
      onClick={handleWatch}
      className={`rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer border ${featured ? 'md:col-span-2' : ''}`}
      style={{
        backgroundColor: 'rgba(16,22,32,0.62)',
        borderColor: 'rgba(255,255,255,0.08)',
        backdropFilter: 'blur(14px)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(52,227,164,0.55)';
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 14px 38px rgba(52, 227, 164, 0.18)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
      data-testid={`stream-card-${stream.stream_id || stream.id}`}
    >
      {/* Thumbnail + scrim */}
      <div className={`relative overflow-hidden ${featured ? 'aspect-[16/7]' : 'aspect-video'}`} style={{ backgroundColor: '#070B12' }}>
        {stream.thumbnail || stream.preview_url || stream.user_picture ? (
          <img src={stream.thumbnail || stream.preview_url || stream.user_picture} alt={stream.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full" style={{
            background: `radial-gradient(120% 80% at 20% 10%, rgba(52,227,164,.18), transparent 60%), radial-gradient(120% 80% at 90% 90%, ${isIRL ? 'rgba(230,195,74,.15)' : 'rgba(229,108,160,.14)'}, transparent 60%), #0B1119`
          }} />
        )}
        {/* Bottom scrim for legibility */}
        <div className="absolute inset-x-0 bottom-0 h-1/2" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.75) 100%)' }} />
        {/* Monospace LIVE badge */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded" style={{
          backgroundColor: '#FF3B5C', color: '#fff', fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: '10.5px', letterSpacing: '.06em', fontWeight: 800
        }}>
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> ● LIVE
        </div>
        {/* Type/game pill */}
        <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide" style={{
          backgroundColor: badgeBg, color: badgeColor, border: `1px solid ${badgeColor}55`
        }} data-testid="stream-type-badge">{badgeLabel}</div>
        {/* Linked pill */}
        {linkedCount > 1 && (
          <div className="absolute bottom-3 left-3 px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1" style={{
            backgroundColor: 'rgba(52,227,164,0.15)', color: '#34E3A4', border: '1px solid rgba(52,227,164,0.4)'
          }} data-testid="stream-linked-badge">
            {isParty ? '🎉 Party' : '🔗 Linked'} · {linkedCount} on camera
          </div>
        )}
        {/* Viewer count (monospace) */}
        <div className="absolute bottom-3 right-3 px-2 py-1 rounded flex items-center gap-1" style={{
          backgroundColor: 'rgba(0,0,0,0.55)', color: '#fff', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '11px'
        }}>
          <Eye className="h-3 w-3" />
          {(stream.viewer_count || 0).toLocaleString()}
        </div>
        {/* Distance (IRL / Nearby) */}
        {stream.distance_km != null && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-2 py-1 rounded text-[10px] font-mono" style={{
            backgroundColor: 'rgba(0,0,0,0.55)', color: '#E6C34A'
          }}>
            {stream.distance_km.toFixed(1)} km
          </div>
        )}
      </div>

      {/* Info row: avatar + name/handle */}
      <div className="p-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center font-bold" style={{ background: 'linear-gradient(135deg,#34E3A4,#22C98C)', color: '#04150e' }}>
          {stream.user_picture ? (
            <img src={stream.user_picture} alt={stream.user_name} className="w-full h-full object-cover" />
          ) : (
            <span>{stream.user_name?.[0] || '?'}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate" style={{ color: 'var(--gems-text)', fontSize: '14px' }}>
            {stream.title || `${stream.user_name}'s Stream`}
          </div>
          <div className="text-xs truncate" style={{ color: 'var(--gems-text-dim)' }}>
            {stream.user_name}{stream.user_handle ? ` · @${stream.user_handle}` : ''}
          </div>
        </div>
      </div>
    </div>
  );
};


const IRLStreamsFeed = ({ streams, loading }) => {
  if (loading) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Card key={i} className="animate-pulse">
            <div className="aspect-video bg-gray-200 rounded-t-lg"></div>
            <CardContent className="p-4 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {streams.map(stream => (
        <StreamCard key={stream.id} stream={stream} type="irl" />
      ))}
      
      {streams.length === 0 && (
        <div className="col-span-full text-center py-12">
          <Camera className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-400 mb-2">No IRL Streams</h3>
          <p className="text-gray-500">Start streaming with Camera or Screen/Game Capture mode!</p>
        </div>
      )}
    </div>
  );
};

const LookingToPlayFeed = ({ players, loading }) => {
  if (loading) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Card key={i} className="animate-pulse p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-3 bg-gray-200 rounded w-16"></div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {players.map(player => (
        <PlayerCard key={player.user_id} player={player} />
      ))}
      
      {players.length === 0 && (
        <div className="col-span-full text-center py-12">
          <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No Players Looking to Play</h3>
          <p className="text-gray-500">Go live and set yourself as "Looking to Play" to find opponents!</p>
        </div>
      )}
    </div>
  );
};

const PlayerCard = ({ player }) => {
  const [showChallenge, setShowChallenge] = useState(false);
  const [wagerAmount, setWagerAmount] = useState(50);

  const challengePlayer = async () => {
    try {
      await api.post(`/game/create`, { wager_amount: wagerAmount });
      toast.success(`Challenge sent to ${player.user_name}!`);
      setShowChallenge(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Challenge failed');
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative">
            <Avatar className="w-12 h-12 border-2 border-emerald-200">
              <AvatarImage src={player.user_picture} />
              <AvatarFallback>{player.user_name[0]}</AvatarFallback>
            </Avatar>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{player.user_name}</h3>
            <p className="text-sm text-gray-500">@{player.user_handle}</p>
          </div>
          <div className="text-right">
            <NetworkStrengthIndicator strength={player.network_strength} />
          </div>
        </div>
        
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-4 text-sm">
            <span className="text-green-600 font-medium">{player.wins}W</span>
            <span className="text-red-600 font-medium">{player.losses}L</span>
            {player.streak > 0 && (
              <span className="text-amber-600 font-medium">🔥{player.streak}</span>
            )}
          </div>
          
          <Badge variant="secondary" className="text-xs">
            <Spade className="h-3 w-3 mr-1" />
            NY Spades
          </Badge>
        </div>
        
        <Dialog open={showChallenge} onOpenChange={setShowChallenge}>
          <DialogTrigger asChild>
            <Button className="w-full bg-emerald-500 hover:bg-emerald-600">
              <Trophy className="h-4 w-4 mr-2" />
              Challenge Player
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Challenge {player.user_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                  <Trophy className="h-4 w-4" />
                  Player Stats
                </div>
                <div className="flex gap-4 text-sm">
                  <span>{player.wins} Wins</span>
                  <span>{player.losses} Losses</span>
                  <span>Win Rate: {player.wins + player.losses > 0 ? Math.round((player.wins / (player.wins + player.losses)) * 100) : 0}%</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Entry Amount (Emeralds)</label>
                <Select value={wagerAmount.toString()} onValueChange={(value) => setWagerAmount(parseInt(value))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">No Entry (Practice)</SelectItem>
                    <SelectItem value="25">25 Emeralds</SelectItem>
                    <SelectItem value="50">50 Emeralds</SelectItem>
                    <SelectItem value="100">100 Emeralds</SelectItem>
                    <SelectItem value="250">250 Emeralds</SelectItem>
                    <SelectItem value="500">500 Emeralds</SelectItem>
                  </SelectContent>
                </Select>
                {wagerAmount > 0 && (
                  <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                    <p className="text-xs text-amber-700">
                      <strong>Prize Pool:</strong> {wagerAmount * 4 * 2} Rubies (${(wagerAmount * 4 * 2 / 210).toFixed(2)} USD) per winner
                    </p>
                    <p className="text-xs text-amber-600">
                      Platform fee: 7.5% • Processing: Instant
                    </p>
                  </div>
                )}
              </div>
              
              <Button onClick={challengePlayer} className="w-full">
                Send Challenge
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const TablesInProgressFeed = ({ tables, loading }) => {
  if (loading) {
    return (
      <div className="grid md:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map(i => (
          <Card key={i} className="animate-pulse p-6">
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4].map(j => (
                  <div key={j} className="h-10 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {tables.map(table => (
        <GameTableCard key={table.game_id} table={table} />
      ))}
      
      {tables.length === 0 && (
        <div className="col-span-full text-center py-12">
          <Clock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No Active Games</h3>
          <p className="text-gray-500">Challenge a player to start a new game!</p>
        </div>
      )}
    </div>
  );
};

const GameTableCard = ({ table }) => {
  const navigate = useNavigate();

  const handleSpectate = () => {
    navigate(`/game/${table.game_id}`);
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-900">Spades Game</h3>
            <p className="text-sm text-gray-500 capitalize">{table.phase} Phase</p>
          </div>
          
          {table.wager_amount > 0 && (
            <Badge className="bg-amber-100 text-amber-800">
              <EmeraldIcon className="h-4 w-4 mr-1" />
              {table.wager_amount} Emeralds
            </Badge>
          )}
        </div>
        
        {/* Players */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {table.players.map((player, index) => (
            <div key={player.id} className="text-center">
              <Avatar className="w-8 h-8 mx-auto mb-1">
                <AvatarImage src={player.picture} />
                <AvatarFallback className="text-xs">{player.name[0]}</AvatarFallback>
              </Avatar>
              <p className="text-xs text-gray-600 truncate">{player.handle}</p>
              <div className="text-xs font-mono text-gray-500">
                {['N', 'E', 'S', 'W'][index]}
              </div>
            </div>
          ))}
        </div>
        
        {/* Scores */}
        {table.team_scores && (
          <div className="flex justify-between items-center mb-4 p-2 bg-gray-50 rounded">
            <div className="text-center">
              <div className="text-lg font-bold text-emerald-600">{table.team_scores.team1 || 0}</div>
              <div className="text-xs text-gray-500">Team 1</div>
            </div>
            <div className="text-gray-400">VS</div>
            <div className="text-center">
              <div className="text-lg font-bold text-emerald-600">{table.team_scores.team2 || 0}</div>
              <div className="text-xs text-gray-500">Team 2</div>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Target className="h-4 w-4" />
            <span>Target: {table.target_score}</span>
          </div>
          
          <Button onClick={handleSpectate} variant="outline" size="sm">
            <Eye className="h-4 w-4 mr-1" />
            Spectate
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


// ========================================================
// PROFILE PICTURE MODAL — Click to enlarge
// ========================================================
const ProfilePictureModal = ({ imageUrl, isOpen, onClose, userName }) => {
  if (!isOpen || !imageUrl) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={onClose} data-testid="profile-picture-modal">
      <div className="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center">
        <button onClick={onClose} className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 rounded-full p-2 text-white">
          <X className="w-6 h-6" />
        </button>
        <img src={imageUrl} alt={userName || 'Profile'} className="max-w-full max-h-full object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
        {userName && (
          <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-full text-white font-semibold">
            {userName}
          </div>
        )}
      </div>
    </div>
  );
};

// ========================================================
// MESSAGES QUICK MODAL — Send messages while live
// ========================================================
const MessagesQuickModal = ({ isOpen, onClose }) => {
  const { user } = React.useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchConversations();
      const interval = setInterval(fetchConversations, 10000);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  const fetchConversations = async () => {
    try {
      const res = await api.get('/messages/conversations');
      setConversations(res.data.conversations || []);
    } catch (e) {}
  };

  const openConversation = async (convo) => {
    setSelectedConvo(convo);
    try {
      const res = await api.get(`/messages/conversation/${convo.user_id}`);
      setMessages(res.data.messages || []);
    } catch (e) {}
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConvo) return;
    try {
      await api.post('/messages/send', { recipient_id: selectedConvo.user_id, content: newMessage.trim() });
      setNewMessage('');
      await openConversation(selectedConvo);
    } catch (e) { toast.error('Failed to send'); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="messages-quick-modal">
      <div className="bg-slate-900 rounded-xl w-full max-w-2xl h-[70vh] flex overflow-hidden border border-slate-700" onClick={(e) => e.stopPropagation()}>
        {/* Conversations list */}
        <div className="w-1/3 border-r border-slate-700 overflow-y-auto">
          <div className="p-3 border-b border-slate-700 flex items-center justify-between">
            <h3 className="text-white font-semibold text-sm">Messages</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          {conversations.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">No messages yet</div>
          ) : conversations.map(c => (
            <button key={c.user_id} onClick={() => openConversation(c)}
              className={`w-full text-left p-3 hover:bg-slate-800 ${selectedConvo?.user_id === c.user_id ? 'bg-slate-800' : ''}`}>
              <div className="text-white text-sm font-semibold truncate">{c.user_name}</div>
              <div className="text-gray-400 text-xs truncate">{c.last_message}</div>
              {c.unread_count > 0 && <span className="inline-block mt-1 bg-emerald-600 text-white text-xs px-2 rounded-full">{c.unread_count}</span>}
            </button>
          ))}
        </div>
        {/* Message thread */}
        <div className="flex-1 flex flex-col">
          {selectedConvo ? (
            <>
              <div className="p-3 border-b border-slate-700"><div className="text-white font-semibold text-sm">{selectedConvo.user_name}</div></div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {messages.map(m => (
                  <div key={m.id} className={`max-w-[70%] p-2 rounded-lg text-sm ${m.sender_id === user?.id ? 'bg-emerald-600 text-white ml-auto' : 'bg-slate-700 text-white'}`}>
                    {ownLinks(m.content)}
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-slate-700 flex gap-2">
                <MentionBox
                  value={newMessage}
                  onChange={setNewMessage}
                  onSubmit={sendMessage}
                  placeholder="Message... (@ to mention)"
                  api={api}
                  className="flex-1"
                />
                <button onClick={sendMessage} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 rounded-lg text-sm">Send</button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Select a conversation</div>
          )}
        </div>
      </div>
    </div>
  );
};


// ========================================================
// GROWTH REWARDS (SEP 9 2026, round 6) — Get Started checklist +
// daily post-bonus greeting. Server pays each task exactly once.
// ========================================================
const GrowthRewardsPanel = () => {
  const { user } = React.useContext(AuthContext);
  const [onboarding, setOnboarding] = useState(null);
  const [daily, setDaily] = useState(null);
  const [dailyDismissed, setDailyDismissed] = useState(() => {
    try { return sessionStorage.getItem('gems_daily_modal_seen') === '1'; } catch { return false; }
  });
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    api.get('/users/me/onboarding').then(r => setOnboarding(r.data)).catch(() => {});
    // Round 10: the bonus day is the user's LOCAL calendar day, not UTC.
    api.get('/rewards/daily-status', { params: { local_date: new Date().toLocaleDateString('en-CA') } }).then(r => setDaily(r.data)).catch(() => {});
    const onReward = () => {
      api.get('/users/me/onboarding').then(r => setOnboarding(r.data)).catch(() => {});
    };
    window.addEventListener('sync:reward_earned', onReward);
    return () => window.removeEventListener('sync:reward_earned', onReward);
  }, [user?.id]);

  // Round 8 FIX: this panel mounts in BOTH responsive layout variants, so
  // two identical modals stacked — clicking dismissed only the top one and
  // the button looked dead. Dismissal now broadcasts to every instance.
  const dismissDaily = () => {
    try { sessionStorage.setItem('gems_daily_modal_seen', '1'); } catch { /* noop */ }
    try { window.dispatchEvent(new CustomEvent('gems:daily-dismissed')); } catch { /* noop */ }
    setDailyDismissed(true);
  };

  useEffect(() => {
    const onDismiss = () => setDailyDismissed(true);
    window.addEventListener('gems:daily-dismissed', onDismiss);
    return () => window.removeEventListener('gems:daily-dismissed', onDismiss);
  }, []);

  return (
    <>
      {/* daily bonus greeting — once per browser session */}
      {daily?.show_modal && !dailyDismissed && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6" style={{ background: 'rgba(4,9,7,0.8)' }} data-testid="daily-bonus-modal">
          <div className="w-full max-w-sm rounded-2xl p-6 text-center" style={{ background: '#0B130F', border: '1.5px solid rgba(212,175,55,0.55)' }}>
            <div className="text-5xl">🎁</div>
            <h3 className="text-lg font-extrabold mt-2" style={{ color: '#F4FBF7' }}>Daily bonus waiting!</h3>
            <p className="text-sm mt-2" style={{ color: 'rgba(244,251,247,0.75)' }}>
              Post at least once today and {daily.emeralds} Emeralds land straight in your wallet.
            </p>
            <button
              className="mt-4 w-full rounded-full py-3 font-extrabold"
              style={{ background: '#22C98C', color: '#062015' }}
              onClick={dismissDaily}
              data-testid="daily-bonus-ok"
            >
              <span className="inline-flex items-center gap-1 justify-center">Post today · earn {daily.emeralds} <EmeraldIcon className="h-4 w-4 inline" /></span>
            </button>
            <button className="mt-3 text-xs" style={{ color: 'rgba(244,251,247,0.5)' }} onClick={dismissDaily}>
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Get Started checklist */}
      {onboarding && !onboarding.complete && (
        <div className="mb-8 rounded-xl p-4" style={{ background: 'rgba(4,10,8,0.8)', border: '1px solid rgba(212,175,55,0.4)' }} data-testid="get-started-card">
          <button className="w-full flex items-center gap-2 text-left" onClick={() => setCollapsed(c => !c)}>
            <div className="flex-1">
              <div className="font-extrabold" style={{ color: '#F5E9C8' }}>Get Started — earn Emeralds</div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--gems-text-dim)' }}>
                {onboarding.tasks.filter(t => t.done).length}/{onboarding.tasks.length} done · {onboarding.earned_emeralds}/{onboarding.total_emeralds} collected
              </div>
            </div>
            <span style={{ color: 'var(--gems-text-dim)' }}>{collapsed ? '▼' : '▲'}</span>
          </button>
          {!collapsed && onboarding.tasks.map(t => (
            <div key={t.key} className="flex items-center gap-3 mt-3">
              <span style={{ color: t.done ? '#3EE6A4' : 'rgba(244,251,247,0.35)', fontSize: 16 }}>{t.done ? '✓' : '○'}</span>
              <span className="flex-1 text-sm font-semibold" style={{ color: t.done ? 'var(--gems-text-dim)' : 'var(--gems-text)', textDecoration: t.done ? 'line-through' : 'none' }}>
                {t.label}
              </span>
              <span className="text-xs font-extrabold rounded-full px-2 py-0.5 border" style={{ color: t.done ? '#3EE6A4' : '#D4AF37', borderColor: t.done ? 'rgba(62,230,164,0.5)' : 'rgba(212,175,55,0.5)' }}>
                +{t.emeralds}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ========================================================
// LEADERBOARD WIDGET — Reusable component for Discover, Go Live, Live Stream sidebar
// ========================================================
const LeaderboardWidget = ({ compact = false, showUserRank = true }) => {
  const { user } = React.useContext(AuthContext);
  const [leaderboard, setLeaderboard] = useState({ most_wins: [], most_rubies: [], most_games: [], date: '', last_updated: '' });
  const [userRank, setUserRank] = useState(null);
  const [activeTab, setActiveTab] = useState('wins');
  // SEP 8 2026 (beta round 5): Daily ↔ Weekly period toggle. Weekly sums the
  // last 7 days of snapshots server-side (/leaderboards/weekly).
  const [period, setPeriod] = useState('daily');
  const [loading, setLoading] = useState(true);
  const [lastFlash, setLastFlash] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const response = await api.get(`/leaderboards/${period}?limit=${expanded ? 25 : 10}`);
      setLeaderboard(response.data);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  const fetchUserRank = useCallback(async () => {
    if (!user?.id || !showUserRank) return;
    try {
      const response = await api.get(`/leaderboards/daily/user/${user.id}`);
      setUserRank(response.data);
    } catch (err) { /* ignore */ }
  }, [user?.id, showUserRank]);

  useEffect(() => {
    fetchLeaderboard();
    fetchUserRank();
    const poll = setInterval(() => { fetchLeaderboard(); fetchUserRank(); }, 30000);
    return () => clearInterval(poll);
  }, [fetchLeaderboard, fetchUserRank]);

  useEffect(() => {
    const handleUpdate = () => { fetchLeaderboard(); fetchUserRank(); setLastFlash(Date.now()); setTimeout(() => setLastFlash(null), 1500); };
    window.addEventListener('sync:leaderboard_update', handleUpdate);
    window.addEventListener('sync:leaderboard_reset', handleUpdate);
    return () => { window.removeEventListener('sync:leaderboard_update', handleUpdate); window.removeEventListener('sync:leaderboard_reset', handleUpdate); };
  }, [fetchLeaderboard, fetchUserRank]);

  const formatNumber = (num) => num >= 1000 ? `${(num / 1000).toFixed(1)}K` : (num?.toString() || '0');
  const getRankDisplay = (i) => i === 0 ? '1st' : i === 1 ? '2nd' : i === 2 ? '3rd' : `${i + 1}th`;
  const currentList = (activeTab === 'wins' ? leaderboard.most_wins : activeTab === 'rubies' ? leaderboard.most_rubies : leaderboard.most_games) || [];
  // Daily rows carry daily_* fields, weekly rows carry weekly_* sums.
  const entryVal = (entry, kind) => period === 'daily'
    ? (kind === 'wins' ? entry.daily_wins : kind === 'rubies' ? entry.daily_rubies_earned : entry.daily_games) || 0
    : (kind === 'wins' ? entry.weekly_wins : kind === 'rubies' ? entry.weekly_rubies_earned : entry.weekly_games) || 0;

  if (loading) return (
    <div className="rounded-xl border p-4 text-center text-sm" style={{ background: '#0D1117', borderColor: 'rgba(0,232,122,0.15)', color: '#8B9BB4' }}>
      Loading leaderboards...
    </div>
  );

  return (
    <div className="rounded-xl overflow-hidden" data-testid="leaderboard-widget" style={{ background: '#0D1117', border: '1px solid rgba(0,232,122,0.15)' }}>
      {/* Header */}
      <div className="flex items-center justify-between" style={{ padding: compact ? '10px 14px' : '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-yellow-400" />
          <span className="font-bold tracking-wider" style={{ fontSize: compact ? '13px' : '15px', color: '#FFD23F' }}>
            {period === 'daily' ? "TODAY'S LEADERS" : "THIS WEEK'S LEADERS"}
          </span>
          {lastFlash && <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded animate-pulse">LIVE</span>}
        </div>
        <div className="flex items-center gap-2">
          {/* Daily / Weekly period toggle */}
          <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
            {['daily', 'weekly'].map(p => (
              <button key={p} onClick={() => { setPeriod(p); setLoading(true); }}
                data-testid={`leaderboard-period-${p}`}
                style={{
                  padding: '3px 10px', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase',
                  letterSpacing: '0.5px', border: 'none', cursor: 'pointer',
                  background: period === p ? 'rgba(255,210,63,0.15)' : 'transparent',
                  color: period === p ? '#FFD23F' : 'rgba(139,155,180,0.6)'
                }}>
                {p}
              </button>
            ))}
          </div>
          <span className="text-[10px] font-mono" style={{ color: 'rgba(139,155,180,0.5)' }}>
            {period === 'daily' ? (leaderboard.date || 'Today') : 'Last 7 days'}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        {[
          { id: 'games', label: 'Most Games', color: '#38BDF8', icon: <Gamepad2 className="h-3 w-3" /> },
          { id: 'wins', label: 'Most Wins', color: '#00E87A', icon: <Trophy className="h-3 w-3" /> },
          { id: 'rubies', label: 'Most Rubies', color: '#FF4D6D', icon: <RubyIcon className="h-3 w-3" /> }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="flex-1 flex items-center justify-center gap-1.5 transition-all"
            style={{
              padding: compact ? '7px 8px' : '10px 12px', fontSize: compact ? '11px' : '13px',
              fontWeight: activeTab === tab.id ? '700' : '400',
              color: activeTab === tab.id ? tab.color : 'rgba(139,155,180,0.7)',
              background: activeTab === tab.id ? `${tab.color}11` : 'transparent',
              borderBottom: activeTab === tab.id ? `2px solid ${tab.color}` : '2px solid transparent',
              border: 'none', cursor: 'pointer'
            }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Entries */}
      <div style={{ padding: compact ? '4px 0' : '6px 0' }}>
        {currentList.length === 0 ? (
          <div className="text-center py-6" style={{ color: 'rgba(139,155,180,0.5)', fontSize: compact ? '11px' : '13px' }}>
            <Gamepad2 className="h-8 w-8 mx-auto mb-2 opacity-30" />
            No players yet today. Be the first!
          </div>
        ) : (
          currentList.slice(0, expanded ? 25 : (compact ? 5 : 10)).map((entry, index) => {
            const isMe = entry.user_id === user?.id;
            const n = entryVal(entry, activeTab);
            const val = activeTab === 'wins' ? `${n} win${n !== 1 ? 's' : ''}`
              : activeTab === 'games' ? `${n} game${n !== 1 ? 's' : ''}`
              : `${formatNumber(n)}`;
            return (
              <div key={entry.user_id || index} data-testid={`leaderboard-entry-${index}`}
                className="flex items-center gap-2 transition-colors"
                style={{
                  padding: compact ? '5px 14px' : '8px 18px',
                  background: isMe ? 'rgba(0,232,122,0.06)' : index < 3 ? 'rgba(255,210,63,0.02)' : 'transparent',
                  borderLeft: isMe ? '3px solid #00E87A' : '3px solid transparent'
                }}>
                {/* Rank */}
                <span className="font-bold" style={{ minWidth: compact ? '20px' : '26px', textAlign: 'center', fontSize: index < 3 ? (compact ? '14px' : '16px') : '12px', color: index === 0 ? '#FFD23F' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : 'rgba(139,155,180,0.5)' }}>
                  {index === 0 ? <Crown className="h-4 w-4 inline text-yellow-400" /> : getRankDisplay(index)}
                </span>
                {/* Avatar */}
                <div className="rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold overflow-hidden"
                  style={{ width: compact ? '26px' : '32px', height: compact ? '26px' : '32px', background: entry.user_picture ? 'transparent' : 'linear-gradient(135deg, #00E87A, #00C96A)', fontSize: compact ? '11px' : '13px', border: isMe ? '2px solid #00E87A' : 'none' }}>
                  {entry.user_picture ? <img src={entry.user_picture} alt="" className="w-full h-full object-cover" /> : (entry.user_name?.[0] || '?').toUpperCase()}
                </div>
                {/* Name */}
                <div className="flex-1 min-w-0">
                  <div className="truncate font-semibold" style={{ fontSize: compact ? '12px' : '13px', color: isMe ? '#00E87A' : '#EEF2FF' }}>
                    {entry.user_name || 'Player'}{isMe && <span className="text-[10px] ml-1 opacity-60">(you)</span>}
                  </div>
                  {!compact && entry.user_handle && <div className="text-[11px]" style={{ color: 'rgba(139,155,180,0.5)' }}>@{entry.user_handle}</div>}
                </div>
                {/* Value */}
                <div className="font-bold whitespace-nowrap flex items-center gap-1" style={{ fontSize: compact ? '11px' : '13px', color: activeTab === 'wins' ? '#00E87A' : activeTab === 'games' ? '#38BDF8' : '#FF4D6D' }}>
                  {val} {activeTab === 'rubies' && <RubyIcon className="h-3 w-3" />}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* User rank if not in top */}
      {showUserRank && period === 'daily' && activeTab !== 'games' && userRank && !currentList.find(e => e.user_id === user?.id) && (userRank.daily_wins > 0 || userRank.daily_rubies_earned > 0) && (
        <div className="flex items-center gap-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)', padding: compact ? '5px 14px' : '8px 18px', background: 'rgba(0,232,122,0.04)' }}>
          <span className="text-[11px] font-mono" style={{ color: 'rgba(139,155,180,0.5)', minWidth: '26px', textAlign: 'center' }}>
            #{activeTab === 'wins' ? userRank.wins_rank || '—' : userRank.rubies_rank || '—'}
          </span>
          <div className="rounded-full flex items-center justify-center text-white font-bold" style={{ width: '24px', height: '24px', background: 'linear-gradient(135deg, #00E87A, #00C96A)', fontSize: '11px', border: '2px solid #00E87A' }}>
            {user?.name?.[0]?.toUpperCase() || '?'}
          </div>
          <span className="text-xs font-semibold" style={{ color: '#00E87A' }}>
            You — {activeTab === 'wins' ? `${userRank.daily_wins} wins` : `${formatNumber(userRank.daily_rubies_earned)}`}
          </span>
        </div>
      )}

      {/* Expand/Collapse */}
      <div className="text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.04)', padding: compact ? '6px' : '9px' }}>
        <button onClick={() => { setExpanded(prev => !prev); if (!expanded) fetchLeaderboard(); }}
          className="text-emerald-400/70 hover:text-emerald-400 transition-colors font-mono tracking-wider uppercase"
          style={{ fontSize: compact ? '10px' : '11px', background: 'none', border: 'none', cursor: 'pointer' }}
          data-testid="view-full-leaderboard-btn">
          {expanded ? 'Show Less ↑' : 'View Full Leaderboard List ↓'}
        </button>
      </div>
    </div>
  );
};

// ========================================================
// LEADERBOARD PAGE — Full dedicated page
// ========================================================
const LeaderboardPage = () => {
  const { user } = React.useContext(AuthContext);
  const [leaderboard, setLeaderboard] = useState({ most_wins: [], most_rubies: [], date: '' });
  const [history, setHistory] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [lbRes, histRes] = await Promise.all([
          api.get('/leaderboards/daily?limit=25'),
          api.get('/leaderboards/history?days=7&limit=5')
        ]);
        setLeaderboard(lbRes.data);
        setHistory(histRes.data.history || []);
        if (user?.id) {
          const rankRes = await api.get(`/leaderboards/daily/user/${user.id}`);
          setUserRank(rankRes.data);
        }
      } catch (err) { console.error('Failed to load leaderboards:', err); }
      finally { setLoading(false); }
    };
    fetchAll();
    const handleUpdate = () => fetchAll();
    window.addEventListener('sync:leaderboard_update', handleUpdate);
    return () => window.removeEventListener('sync:leaderboard_update', handleUpdate);
  }, [user?.id]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--gems-bg, #070B0F)' }}>
      <NavBar />
      <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div data-testid="leaderboard-page" style={{ minHeight: '100vh', background: 'var(--gems-bg, #070B0F)' }}>
      <NavBar />
      <div style={{ padding: '80px 24px 40px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="font-bold tracking-wider" style={{ fontSize: 'clamp(40px, 8vw, 64px)', color: '#FFD23F', lineHeight: 1 }}>LEADERBOARDS</h1>
        <p className="font-mono text-sm mt-2" style={{ color: 'rgba(139,155,180,0.7)' }}>
          {leaderboard.date || new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} · Resets at Midnight UTC
        </p>
      </div>

      {/* Your Rank Banner */}
      {userRank && (
        <div className="rounded-xl mb-8 flex justify-around flex-wrap gap-4" data-testid="user-rank-banner"
          style={{ background: 'rgba(0,232,122,0.06)', border: '1px solid rgba(0,232,122,0.2)', padding: '16px 24px' }}>
          <div className="text-center">
            <div className="text-[11px] font-mono tracking-widest uppercase mb-1" style={{ color: 'rgba(139,155,180,0.6)' }}>Your Wins Rank</div>
            <div className="text-3xl font-bold" style={{ color: '#00E87A' }}>{userRank.wins_rank ? `#${userRank.wins_rank}` : '—'}</div>
            <div className="text-sm" style={{ color: 'rgba(139,155,180,0.8)' }}>{userRank.daily_wins} wins today</div>
          </div>
          <div className="text-center">
            <div className="text-[11px] font-mono tracking-widest uppercase mb-1" style={{ color: 'rgba(139,155,180,0.6)' }}>Your Rubies Rank</div>
            <div className="text-3xl font-bold" style={{ color: '#FF4D6D' }}>{userRank.rubies_rank ? `#${userRank.rubies_rank}` : '—'}</div>
            <div className="text-sm" style={{ color: 'rgba(139,155,180,0.8)' }}>{userRank.daily_rubies_earned?.toLocaleString()} rubies today</div>
          </div>
        </div>
      )}

      {/* Full Widget */}
      <LeaderboardWidget compact={false} showUserRank={false} />

      {/* History */}
      {history.length > 0 && (
        <div className="mt-12" data-testid="leaderboard-history">
          <h2 className="font-bold tracking-wider mb-5" style={{ fontSize: '24px', color: '#FFD23F' }}>PAST WINNERS</h2>
          <div className="flex flex-col gap-4">
            {history.map(day => (
              <div key={day.date} className="rounded-xl p-4" style={{ background: '#0D1117', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="font-mono text-xs mb-3 tracking-wider" style={{ color: 'rgba(139,155,180,0.5)' }}>
                  {new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase()}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {day.top_players.slice(0, 3).map((p, i) => (
                    <div key={p.user_id || i} className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <span>{i === 0 ? <Crown className="h-3.5 w-3.5 text-yellow-400" /> : i === 1 ? <span className="text-gray-300">2nd</span> : <span className="text-amber-600">3rd</span>}</span>
                      <span style={{ color: '#EEF2FF' }}>{p.user_name}</span>
                      <span className="font-bold" style={{ color: '#00E87A' }}>{p.daily_wins}W</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};


const NetworkStrengthIndicator = ({ strength = 5 }) => {
  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <div 
          key={i}
          className={`w-1 h-3 rounded ${
            i < strength 
              ? strength >= 4 ? 'bg-green-500' 
                : strength >= 2 ? 'bg-yellow-500' 
                : 'bg-red-500'
              : 'bg-gray-300'
          }`}
        />
      ))}
    </div>
  );
};

const WalletCard = () => {
  const [showComingSoon, setShowComingSoon] = useState(false);

  return (
    <>
      <Card 
        className="shadow-lg border-0 cursor-pointer hover:shadow-xl transition-shadow"
        onClick={() => setShowComingSoon(true)}
      >
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Coins className="h-5 w-5 text-emerald-600" />
            Wallet
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Phase 1: Show placeholder values */}
          <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
            <div className="flex items-center gap-2">
              <EmeraldIcon className="h-6 w-6" />
              <span className="font-medium text-emerald-800">Emeralds</span>
            </div>
            <span className="font-bold text-emerald-800">∞</span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
            <div className="flex items-center gap-2">
              <RubyIcon className="h-6 w-6" />
              <span className="font-medium text-red-800">Rubies</span>
            </div>
            <span className="font-bold text-red-800">∞</span>
          </div>
          
          <p className="text-xs text-center text-gray-500 mt-2">
            Click for details
          </p>
        </CardContent>
    </Card>
    
    {/* Coming Soon Modal */}
    <Dialog open={showComingSoon} onOpenChange={setShowComingSoon}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <EmeraldIcon className="h-6 w-6" />
            <RubyIcon className="h-6 w-6" />
            Wallet & Currency System
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="text-center">
            <div className="text-6xl mb-4">🚀</div>
            <h3 className="text-xl font-bold mb-2">Coming Soon!</h3>
            <p className="text-gray-600 mb-4">
              Our virtual currency and wallet system is currently in development.
            </p>
            <div className="bg-emerald-50 p-4 rounded-lg text-left">
              <p className="text-sm text-emerald-800 font-medium mb-2">Currency System:</p>
              <ul className="text-sm text-emerald-700 space-y-1">
                <li>• Enter matches with Emeralds</li>
                <li>• Win Rubies from victories</li>
                <li>• GEMS takes {Math.round((economyRates?.rake_1v1 ?? 0.08) * 100)}% platform fee</li>
                <li>• Minimum entry: 50 Emeralds</li>
              </ul>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Currency system will be available in Phase 2
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

const MatchmakingCard = () => {
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await api.get('/feeds/looking-to-play');
        setPlayers(response.data.slice(0, 3)); // Show top 3
      } catch (error) {
        console.error('Failed to fetch players:', error);
      }
    };

    fetchPlayers();
    const interval = setInterval(fetchPlayers, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Card className="shadow-lg border-0">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="h-5 w-5 text-purple-600" />
          Available Opponents
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {players.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No opponents available</p>
        ) : (
          players.map(player => (
            <div key={player.user_id} className="flex items-center gap-3 p-2 border rounded-lg hover:bg-gray-50 transition-colors">
              <Avatar className="w-8 h-8">
                <AvatarImage src={player.user_picture} />
                <AvatarFallback>{player.user_name[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{player.user_name}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{player.wins}W {player.losses}L</span>
                  <NetworkStrengthIndicator strength={player.network_strength} />
                </div>
              </div>
              <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                Challenge
              </Button>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

const QuickStatsCard = () => {
  const { user } = React.useContext(AuthContext);

  const winRate = user && (user.wins + user.losses > 0) 
    ? Math.round((user.wins / (user.wins + user.losses)) * 100) 
    : 0;

  return (
    <Card className="shadow-lg border-0">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <TrendingUp className="h-5 w-5 text-indigo-600" />
          Quick Stats
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="text-center p-2 bg-green-50 rounded">
            <div className="font-bold text-green-700">{user?.wins || 0}</div>
            <div className="text-xs text-green-600">Wins</div>
          </div>
          <div className="text-center p-2 bg-red-50 rounded">
            <div className="font-bold text-red-700">{user?.losses || 0}</div>
            <div className="text-xs text-red-600">Losses</div>
          </div>
          <div className="text-center p-2 bg-blue-50 rounded">
            <div className="font-bold text-blue-700">{winRate}%</div>
            <div className="text-xs text-blue-600">Win Rate</div>
          </div>
          <div className="text-center p-2 bg-purple-50 rounded">
            <div className="font-bold text-purple-700">{user?.streak || 0}</div>
            <div className="text-xs text-purple-600">Streak</div>
          </div>
        </div>
        
        <Separator />
        
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Overs Total</span>
            <span className="font-medium">{user?.overs_total || 0}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Boston Count</span>
            <span className="font-medium">{user?.boston_count || 0}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Additional Pages (simplified for space)
const CrazyEightsPracticePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = React.useContext(AuthContext);
  const meId = 'human';
  // Parse ?ai=N (1..3, default 3) so `Practice with AI` can request the
  // desired opponent count from the Games page modal.
  const aiCount = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const n = parseInt(params.get('ai') || '3', 10);
    if (Number.isNaN(n)) return 3;
    return Math.max(1, Math.min(3, n));
  }, [location.search]);
  const aiIds = React.useMemo(() => ['cpu1', 'cpu2', 'cpu3'].slice(0, aiCount), [aiCount]);
  const { state, play, draw, pass, declareLastCard, reset } = useCrazyEightsPractice(meId, aiIds);

  // Fly-in animation for the most-recent played card (helps the eye follow
  // whose turn played which card). Watches discard_top length changes.
  const [flyKey, setFlyKey] = React.useState(0);
  const prevDiscardTopRef = React.useRef(null);
  React.useEffect(() => {
    const top = state.discard_top;
    if (!top) return;
    const key = `${top.suit}-${top.rank}`;
    if (prevDiscardTopRef.current !== key) {
      prevDiscardTopRef.current = key;
      setFlyKey((k) => k + 1);
    }
  }, [state.discard_top]);

  const [suitChooserOpen, setSuitChooserOpen] = useState(false);
  const [pendingEightIndex, setPendingEightIndex] = useState(null);

  const myTurn = state.current_player === meId;
  const iHaveOne = (state.hand_counts?.[meId] || 0) === 1;
  const iAmPending = state.last_card_pending_pid === meId;
  const facingStack = (state.pending_draw || 0) > 0;
  const engineView = state.__engine_view__ || state;

  const playerNames = { [meId]: user?.name || 'You' };
  aiIds.forEach((id, i) => { playerNames[id] = C8_AI_NAMES[i % C8_AI_NAMES.length]; });

  // Build the opponents list for PracticeTable (excludes the human).
  const opponents = aiIds.map((id) => ({
    id,
    name: playerNames[id],
    cardCount: state.hand_counts?.[id] || 0,
    isCurrentTurn: state.current_player === id,
    isBot: true,
  }));

  const handleCardClick = (card, idx) => {
    if (!myTurn) return;
    const wouldWin = (state.hand_counts?.[meId] || 0) === 1;
    if (card.rank === '8' && !wouldWin) {
      setPendingEightIndex(idx);
      setSuitChooserOpen(true);
      return;
    }
    play(idx, null);
  };
  const handleSuitChosen = (suit) => {
    setSuitChooserOpen(false);
    if (pendingEightIndex != null) { play(pendingEightIndex, suit); setPendingEightIndex(null); }
  };

  // Center content — discard top + active-suit badge + stock pile w/ count.
  const centerContent = (
    <>
      <div className="flex flex-col items-center gap-2">
        <CardBack size="lg" />
        <span className="text-white/70 text-xs" data-testid="c8-stock-count">Stock: {state.stock_count}</span>
      </div>
      <div className="flex flex-col items-center gap-2" data-testid="c8-discard-top">
        {state.discard_top && (
          <div
            key={flyKey}
            className="c8-card-flyin"
            data-testid="c8-discard-top-card"
          >
            <PlayingCard
              card={{ suit: state.discard_top.suit, value: state.discard_top.rank }}
              playable={false}
              isTrump={false}
              onPlay={() => {}}
            />
          </div>
        )}
        <ActiveSuitBadge suit={state.active_suit} />
        {facingStack && (
          <span className="text-red-300 text-xs font-bold" data-testid="c8-pending-draw">
            +{state.pending_draw} pending
          </span>
        )}
      </div>
    </>
  );

  const myHand = (
    <div className="flex justify-center items-end pl-3 pr-1 pb-2 overflow-x-auto" data-testid="c8-hand-row">
      {(state.my_hand || []).map((card, idx) => (
        <PlayingCard
          key={idx}
          card={{ suit: card.suit, value: card.rank }}
          playable={myTurn && (state.__engine_view__ ? c8IsLegal(engineView, card) : c8IsPlayablePublic(state, card))}
          notMyTurn={!myTurn}
          isTrump={false}
          onPlay={() => handleCardClick(card, idx)}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(1200px 500px at 50% -10%, #16233b 0%, #0A0E17 55%)' }}>
      <NavBar />
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-extrabold text-white">Crazy Eights — Practice</h1>
            <p className="text-white/60 text-sm">vs {aiCount} AI · no timers, no entry, no stats</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={reset}
              data-testid="c8-practice-restart"
              className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-bold"
            >
              Restart
            </button>
            <button
              onClick={() => navigate('/games')}
              className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/80 text-sm"
            >
              Back
            </button>
          </div>
        </div>

        <PracticeTable
          opponents={opponents}
          centerContent={centerContent}
          myHand={myHand}
          myTurn={myTurn}
          myName={playerNames[meId]}
        />

        {/* Action row — Draw / Pass / Last Card. */}
        <div className="flex items-center justify-center gap-3 mt-3">
          <button
            type="button"
            disabled={!myTurn || state.state === 'AWAITING_PLAY_DRAWN'}
            onClick={draw}
            data-testid="c8-draw-btn"
            className={[
              'px-4 py-2 rounded-xl text-sm font-bold',
              (myTurn && state.state !== 'AWAITING_PLAY_DRAWN')
                ? 'bg-white text-gray-900 hover:bg-gray-100'
                : 'bg-white/20 text-white/40 cursor-not-allowed',
            ].join(' ')}
          >
            {facingStack ? `Draw +${state.pending_draw}` : 'Draw'}
          </button>
          {state.state === 'AWAITING_PLAY_DRAWN' && myTurn && (
            <button
              type="button"
              onClick={pass}
              data-testid="c8-pass-btn"
              className="px-4 py-2 rounded-xl text-sm font-bold bg-amber-400 text-amber-950 hover:bg-amber-300"
            >
              Pass
            </button>
          )}
          {iHaveOne && iAmPending && !state.winner && (
            <button
              type="button"
              onClick={declareLastCard}
              data-testid="c8-last-card-btn"
              className="px-4 py-2 rounded-xl text-sm font-extrabold bg-rose-500 text-white hover:bg-rose-400 animate-pulse"
            >
              Last Card!
            </button>
          )}
        </div>

        {/* Win overlay */}
        {state.winner && (
          <div className="fixed inset-0 z-[110] bg-black/80 flex items-center justify-center" data-testid="c8-win-overlay">
            <div className="bg-white rounded-2xl p-8 text-center max-w-sm">
              <div className="text-5xl mb-3">🏆</div>
              <h2 className="text-2xl font-extrabold text-gray-900">
                {state.winner === meId ? 'You won!' : `${playerNames[state.winner] || state.winner} won!`}
              </h2>
              <button
                onClick={reset}
                className="mt-4 px-5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold"
                data-testid="c8-win-play-again"
              >
                Play Again
              </button>
            </div>
          </div>
        )}
      </div>

      <SuitChooser
        open={suitChooserOpen}
        onChoose={handleSuitChosen}
        onCancel={() => { setSuitChooserOpen(false); setPendingEightIndex(null); }}
      />
    </div>
  );
};

const GamesPage = () => {
  const { user, setUser } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showGameOptionsModal, setShowGameOptionsModal] = useState(false);
  // Which game the "Choose Game Mode" modal is currently opened for.
  // Drives title/copy + routing of Practice-with-AI. (Jul 27, 2026)
  const [selectedGameForModal, setSelectedGameForModal] = useState('spades');
  const [showWagerModal, setShowWagerModal] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [inviteGemId, setInviteGemId] = useState('');
  const [selectedGameMode, setSelectedGameMode] = useState(null);
  const [wagerAmount, setWagerAmount] = useState(0);
  const [allowBlindSix, setAllowBlindSix] = useState(true);
  const [allowAceCheck, setAllowAceCheck] = useState(true);
  // Round 10: 'Play to' — match target score (NY tables play to 250 / 300 / 500).
  const [spadesTargetScore, setSpadesTargetScore] = useState(250);
  const [inviteGameId, setInviteGameId] = useState(null);
  const [invitedPlayers, setInvitedPlayers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loadingOnlineUsers, setLoadingOnlineUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchingMatch, setIsSearchingMatch] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  
  // 4 In A Row specific state
  const [showFourInARowOptions, setShowFourInARowOptions] = useState(false);
  // SEP 7 2026: games-page full-rules modal (per-game Rules button on tiles)
  const [rulesGameId, setRulesGameId] = useState(null);
  const [showFourInARowWager, setShowFourInARowWager] = useState(false);
  const [showFourInARowRules, setShowFourInARowRules] = useState(false);
  const [fourInARowMode, setFourInARowMode] = useState(null);
  const [fourInARowWager, setFourInARowWager] = useState(50);
  const [creatingFourInARow, setCreatingFourInARow] = useState(false);

  // Crazy Eights — its own entry modal (no Spades rules toggles).
  const [showC8EntryModal, setShowC8EntryModal] = useState(false);
  const [c8EntryMode, setC8EntryMode] = useState(null); // 'practice' | 'quickMatch'
  const [c8PlayerCount, setC8PlayerCount] = useState(4);
  const [c8Wager, setC8Wager] = useState(MIN_MATCH_ENTRY);

  // Checkers — dedicated entry modal (rule controls + timer + difficulty)
  const [showCheckersEntryModal, setShowCheckersEntryModal] = useState(false);
  const [checkersEntryMode, setCheckersEntryMode] = useState(null); // 'practice' | 'quickMatch'
  const [checkersWager, setCheckersWager] = useState(MIN_MATCH_ENTRY);
  // Linked opponent for a live Checkers match (set from the in-stream
  // Play Together picker via `?launch=checkers&mode=live&partnerId=…`).
  // Practice mode leaves this null.
  const [linkedGameOpponent, setLinkedGameOpponent] = useState(null);

  // Live economy rates for fee display (rake / conversion / withdrawal).
  // Falls back to hard-coded defaults if the fetch fails so nothing crashes.
  const [economyRates, setEconomyRates] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get('/economy/rates');
        if (alive) setEconomyRates(r.data);
      } catch { /* fallback defaults are read via `?.` */ }
    })();
    return () => { alive = false; };
  }, []);

  // Auto-open the appropriate entry modal when landing here from the
  // in-stream "Play Together" picker (via `?launch=crazy_eights|checkers`).
  // Strip the query param after use so refreshes don't re-open.
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const launch = params.get('launch');
    if (!launch) return;
    if (launch === 'crazy_eights') {
      setSelectedGameForModal('crazy_eights');
      setC8EntryMode('quickMatch');
      setC8PlayerCount(4);
      setC8Wager(MIN_MATCH_ENTRY);
      setShowC8EntryModal(true);
    } else if (launch === 'checkers') {
      const mode = params.get('mode');   // 'live' | (default) 'practice'
      const partnerId = params.get('partnerId');
      const partnerName = params.get('partnerName');
      setSelectedGameForModal('checkers');
      if (mode === 'live' && partnerId) {
        // Live match from the in-stream Play Together picker — pre-fill
        // the linked partner + entry so handleStartCheckers's live path
        // (POST /checkers/create + linked-game-invite) fires on Start.
        setLinkedGameOpponent({ id: partnerId, name: partnerName || partnerId });
        setCheckersEntryMode('quickMatch');
        setCheckersWager(MIN_MATCH_ENTRY);
      } else {
        // Games-page tile → practice-vs-AI (10 emerald charge, no linked req).
        setCheckersEntryMode('practice');
      }
      setShowCheckersEntryModal(true);
    }
    // Clear the param so a hard refresh doesn't re-trigger the modal.
    if (window.history.replaceState) {
      window.history.replaceState({}, '', location.pathname);
    }
  }, []);
  
  // Fetch online users when invite modal opens
  useEffect(() => {
    if (showInviteModal) {
      fetchOnlineUsers();
    }
  }, [showInviteModal]);
  
  const fetchOnlineUsers = async () => {
    setLoadingOnlineUsers(true);
    try {
      const response = await api.get('/users/online');
      setOnlineUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setOnlineUsers([]);
    } finally {
      setLoadingOnlineUsers(false);
    }
  };
  
  // Filter users based on search query (null-guarded — a single user with a
  // missing name/handle used to throw here and blank the WHOLE list), then
  // surface live/online broadcasters first so a just-unlinked partner (still
  // live) shows at the TOP of the invite box instead of buried or missed.
  const filteredOnlineUsers = onlineUsers
    .filter(u =>
      (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.handle || '').toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) =>
      (Number(!!b.is_live) - Number(!!a.is_live)) ||
      (Number(!!b.is_online) - Number(!!a.is_online))
    );
  
  const handleCreatePrivateGame = () => {
    setShowGameOptionsModal(true);
  };
  
  const handleSelectGameMode = (mode) => {
    setSelectedGameMode(mode);
    setShowGameOptionsModal(false);

    // Crazy Eights uses its own dedicated entry modal (no Spades toggles).
    // Route BOTH Practice and Quick Match through it so the user picks
    // player count + (for live) match entry there.
    if (selectedGameForModal === 'crazy_eights' && (mode === 'quickMatchWithBots' || mode === 'quickMatch')) {
      setC8EntryMode(mode === 'quickMatchWithBots' ? 'practice' : 'quickMatch');
      setC8PlayerCount(mode === 'quickMatchWithBots' ? 4 : 4);
      setC8Wager(MIN_MATCH_ENTRY);
      setShowC8EntryModal(true);
      return;
    }

    // Checkers routes through its own entry modal (rule controls +
    // timer + practice difficulty).
    if (selectedGameForModal === 'checkers' && (mode === 'quickMatchWithBots' || mode === 'quickMatch')) {
      setCheckersEntryMode(mode === 'quickMatchWithBots' ? 'practice' : 'quickMatch');
      setCheckersWager(MIN_MATCH_ENTRY);
      setShowCheckersEntryModal(true);
      return;
    }

    if (mode === 'quickMatchWithBots') {
      setWagerAmount(0); // Practice is FREE (Jul 27, 2026)
    } else {
      setWagerAmount(0); // Default for other modes
    }
    
    setShowWagerModal(true);
  };

  // Checkers — start-practice / go-live handler.
  const handleStartCheckers = async ({ capture_rule, pool_rules, timer_on, difficulty }) => {
    if (checkersEntryMode === 'practice') {
      try {
        const chargeRes = await api.post('/game/practice/charge');
        if (chargeRes.data?.new_balance != null && setUser) {
          setUser((u) => (u ? { ...u, emeralds: chargeRes.data.new_balance } : u));
        }
      } catch (chargeErr) {
        if (chargeErr.response?.status === 402) {
          toast.error(chargeErr.response?.data?.detail || 'Not enough Emeralds to practice. Please top up.');
        } else {
          toast.error(chargeErr.response?.data?.detail || 'Failed to start practice.');
        }
        return;
      }
      setShowCheckersEntryModal(false);
      navigate('/checkers/practice');
      return;
    }
    // ---- LIVE 2-player match --------------------------------------
    // Requires a linked partner; entry deduction happens atomically
    // inside /game/{id}/start (unchanged money path) once the invitee
    // accepts. We only *create* the doc + send the challenge here.
    if (!linkedGameOpponent?.id) {
      toast.error('Link with a broadcaster on your stream to start a Checkers match.');
      return;
    }
    if (checkersWager < MIN_MATCH_ENTRY) {
      toast.error(`Minimum match entry is ${MIN_MATCH_ENTRY} emeralds.`);
      return;
    }
    if (checkersWager > (user?.emeralds || 0)) {
      toast.error('Insufficient Emeralds. Reduce the match entry amount.');
      return;
    }
    try {
      const createRes = await api.post('/checkers/create', {
        wagerAmount: checkersWager,
        linkedOpponentId: linkedGameOpponent.id,
        checkersConfig: { capture_rule, pool_rules, timer_on },
      });
      const gameId = createRes.data?.gameId;
      if (!gameId) throw new Error('No gameId returned');

      // Show the "waiting for accept" overlay via the shared linked-game state.
      setActiveLinkedGame({
        gameId,
        gameType: 'checkers',
        myPlayerNum: 1,
        players: [
          { id: user?.id, name: user?.name, player_num: 1 },
          { id: linkedGameOpponent.id, name: linkedGameOpponent.name, player_num: 2 },
        ],
        phase: 'waiting_accept',
        wagerAmount: checkersWager,
      });

      // Send the challenge via the game-type-aware invite endpoint.
      await api.post('/four-in-a-row/linked-game-invite', {
        game_id: gameId,
        opponent_id: linkedGameOpponent.id,
        wager_amount: checkersWager,
        inviter_name: user?.name,
        game_type: 'checkers',
      });

      setShowCheckersEntryModal(false);
      toast.info(`Checkers challenge sent! Waiting for ${linkedGameOpponent.name} to accept…`);
    } catch (err) {
      console.error('[checkers] Failed to send live challenge:', err);
      toast.error(err.response?.data?.detail || 'Failed to send Checkers challenge.');
    }
  };

  // Crazy Eights — start-practice / go-live handler.
  const handleStartCrazyEights = async () => {
    if (c8EntryMode === 'practice') {
      // Charge the universal 10-emerald practice fee first (atomic server-side).
      try {
        const chargeRes = await api.post('/game/practice/charge');
        if (chargeRes.data?.new_balance != null && setUser) {
          setUser((u) => (u ? { ...u, emeralds: chargeRes.data.new_balance } : u));
        }
      } catch (chargeErr) {
        if (chargeErr.response?.status === 402) {
          toast.error(chargeErr.response?.data?.detail || 'Not enough Emeralds to practice. Please top up.');
        } else {
          toast.error(chargeErr.response?.data?.detail || 'Failed to start practice.');
        }
        return;
      }
      setShowC8EntryModal(false);
      navigate(`/crazy-eights/practice?ai=${c8PlayerCount - 1}`);
      return;
    }
    // Quick Match (live): reuse the existing wager pipeline.
    if (c8Wager < MIN_MATCH_ENTRY) {
      toast.error(`Minimum match entry is ${MIN_MATCH_ENTRY} emeralds.`);
      return;
    }
    if (c8Wager > (user?.emeralds || 0)) {
      toast.error('Insufficient Emeralds. Reduce the match entry amount.');
      return;
    }
    try {
      const gameResponse = await api.post('/game/create', {
        gameType: 'crazy_eights',
        isPrivate: false,
      });
      const gameId = gameResponse.data?.gameId;
      if (gameId) {
        await api.post(`/game/${gameId}/set-entry`, { entryAmount: c8Wager });
      }
      setShowC8EntryModal(false);
      toast.success('Crazy Eights game created — waiting for players…');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create game');
    }
  };
  
  const handleSetWager = async () => {
    try {
      // Practice-with-AI: charge the universal 10-emerald fee server-side
      // (atomic; blocks on 402). Live modes still validate wager balance below.
      if (selectedGameMode === 'quickMatchWithBots') {
        try {
          const chargeRes = await api.post('/game/practice/charge');
          if (chargeRes.data?.new_balance != null && setUser) {
            setUser((u) => (u ? { ...u, emeralds: chargeRes.data.new_balance } : u));
          }
        } catch (chargeErr) {
          if (chargeErr.response?.status === 402) {
            toast.error(chargeErr.response?.data?.detail || 'Not enough Emeralds to practice. Please top up.');
          } else {
            toast.error(chargeErr.response?.data?.detail || 'Failed to start practice.');
          }
          return;
        }
      } else {
        // Enforce universal minimum for LIVE match entries.
        if (wagerAmount > 0 && wagerAmount < MIN_MATCH_ENTRY) {
          toast.error(`Minimum match entry is ${MIN_MATCH_ENTRY} emeralds.`);
          return;
        }
        // Validate emeralds for other game modes
        if (wagerAmount > (user?.emeralds || 0)) {
          toast.error('Insufficient Emeralds. Please reduce match entry amount or recharge your balance.');
          return;
        }
      }

      let gameResponse;
      
      // Create the game first
      if (selectedGameMode === 'quickMatch') {
        // For Quick Match, force user to go live first, then matchmaking happens while live
        // Phase 1: No minimum wager
        
        setShowWagerModal(false);
        
        // Store wager for use on Live page
        localStorage.setItem('pendingQuickMatch', JSON.stringify({
          wagerAmount: wagerAmount,
          allowBlindSix: allowBlindSix,
          allowAceCheck: allowAceCheck,
          mode: 'quickMatch'
        }));
        
        toast.info('Redirecting to Go Live... You must be streaming to play Quick Match');
        
        // Navigate to Live page where streaming will start, then matchmaking begins
        navigate('/live');
        return; // Exit early for Quick Match flow
      } else if (selectedGameMode === 'quickMatchWithBots') {
        // NOTE: crazy_eights practice never reaches here — it's routed to
        // /crazy-eights/practice above in handleSelectGameMode.
        gameResponse = await api.post('/game/create', { 
          gameType: selectedGameForModal === 'crazy_eights' ? 'crazy_eights' : 'spades',
          isPrivate: true, // Make bot practice private (offline)
          practiceMode: true // Mark as practice mode
        });
      } else if (selectedGameMode === 'invitePlayer') {
        // Create invite game (no players yet)
        const inviteGameResponse = await api.post('/game/invite/create', {
          wagerAmount: wagerAmount
        });
        
        setInviteGameId(inviteGameResponse.data.game_id);
        setShowWagerModal(false);
        setShowInviteModal(true); // Show invite modal to add players
        toast.success('Game created! Now invite players by GEM ID');
        return; // Don't proceed with normal flow
      }
      
      const gameId = gameResponse.data.gameId;
      
      // Set wager and match settings
      await api.post('/game/set-entry', {
        gameId: gameId,
        entryAmount: wagerAmount,
        allowBlindSix: allowBlindSix,
        allowAceCheck: allowAceCheck,
        targetScore: spadesTargetScore
      });
      
      // Add bots if needed
      if (selectedGameMode === 'quickMatchWithBots') {
        await api.post(`/game/add-bots/${gameId}`, { 
          bot_count: 3 
        });
      }
      
      // Send invitation if needed
      if (selectedGameMode === 'invitePlayer') {
        await api.post(`/game/${gameId}/invite`, {
          gem_id: inviteGemId.trim()
        });
        toast.success(`Game created with ${wagerAmount > 0 ? wagerAmount + ' Emerald' : 'no'} wager. Invitation sent to ${inviteGemId}`);
      } else if (selectedGameMode === 'quickMatchWithBots') {
        toast.success('Practice game created! Playing offline with bots.');
      } else {
        toast.success(`Game created with ${wagerAmount > 0 ? wagerAmount + ' Emerald' : 'no'} entry!`);
      }
      
      setShowWagerModal(false);
      setWagerAmount(0);
      setAllowBlindSix(true);
      setAllowAceCheck(true);
      setInviteGemId('');
      navigate(`/game/${gameId}`);
      
    } catch (error) {
      console.error('Game creation error:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Insufficient')) {
        toast.error('Insufficient Emeralds. Please recharge your balance.');
      } else {
        toast.error('Failed to create game. Please try again.');
      }
    }
  };
  
  const handleQuickMatch = async () => {
    try {
      // Create a quick match game
      const response = await api.post('/game/create', {
        gameType: 'spades',
        isPrivate: false
      });
      navigate(`/game/${response.data.gameId}`);
    } catch (error) {
      toast.error('Failed to create quick match');
    }
  };

  const handleQuickMatchWithBots = async () => {
    try {
      const response = await api.post('/game/create', { 
        gameType: 'spades', 
        isPrivate: false 
      });
      const gameId = response.data.gameId;
      await api.post(`/game/add-bots/${gameId}`, { 
        bot_count: 3 
      });
      navigate(`/game/${gameId}`);
      toast.success('Test game created with 3 bots!');
      setShowGameOptionsModal(false);
    } catch (error) {
      console.error('Bot game creation error:', error);
      toast.error('Failed to create test game');
    }
  };

  const handleInvitePlayer = async () => {
    // Get selected users or manual GEM ID
    let usersToInvite = [];
    
    if (selectedUserIds.length > 0) {
      // Use checkbox selections
      usersToInvite = onlineUsers.filter(u => selectedUserIds.includes(u.id));
    } else if (inviteGemId.trim()) {
      // Use manual GEM ID entry
      const user = onlineUsers.find(u => u.handle === inviteGemId.trim());
      if (user) {
        usersToInvite = [user];
      } else {
        toast.error('User not found');
        return;
      }
    } else {
      toast.error('Please select players to invite');
      return;
    }

    try {
      // Close modal and navigate to Live page to start streaming
      setShowInviteModal(false);
      
      // Store invited players and redirect to Live page
      localStorage.setItem('pendingInvites', JSON.stringify(usersToInvite));
      toast.info('Redirecting to Go Live...');
      
      // Navigate to live page where auto go-live will happen
      navigate('/live');
      
    } catch (error) {
      console.error('Invite error:', error);
      toast.error('Failed to invite players');
    }
  };
  
  // ============================================================
  // 4 IN A ROW HANDLERS
  // ============================================================
  const handleFourInARowModeSelect = (mode) => {
    setFourInARowMode(mode);
    setShowFourInARowOptions(false);
    
    // Set default wager for each mode
    if (mode === 'practice') {
      setFourInARowWager(0); // Practice is FREE (Jul 27, 2026)
    } else {
      setFourInARowWager(50); // Min wager for competitive
    }
    
    setShowFourInARowWager(true);
  };
  
  const handleStartFourInARow = async () => {
    if (creatingFourInARow) return;

    // Validate wager (live) OR let the server charge the practice fee (10 emeralds)
    if (fourInARowMode !== 'practice') {
      if (fourInARowWager < MIN_MATCH_ENTRY) {
        toast.error(`Minimum match entry is ${MIN_MATCH_ENTRY} emeralds.`);
        return;
      }
      if ((user?.emeralds || 0) < fourInARowWager) {
        toast.error('Insufficient Emeralds');
        return;
      }
    }

    setCreatingFourInARow(true);

    try {
      // Practice → charge the universal 10-emerald fee (atomic; server-side).
      if (fourInARowMode === 'practice') {
        try {
          const chargeRes = await api.post('/game/practice/charge');
          if (chargeRes.data?.new_balance != null && setUser) {
            setUser((u) => (u ? { ...u, emeralds: chargeRes.data.new_balance } : u));
          }
        } catch (chargeErr) {
          setCreatingFourInARow(false);
          if (chargeErr.response?.status === 402) {
            toast.error(chargeErr.response?.data?.detail || 'Not enough Emeralds to practice. Please top up.');
          } else {
            toast.error(chargeErr.response?.data?.detail || 'Failed to start practice.');
          }
          return;
        }
      }

      const requiredWager = fourInARowMode === 'practice' ? 0 : fourInARowWager;
      const response = await api.post('/four-in-a-row/create', {
        practiceMode: fourInARowMode === 'practice',
        wagerAmount: requiredWager,
        isPrivate: fourInARowMode === 'invite'
      });
      
      const gameId = response.data.gameId;
      
      // Refresh user to update emerald balance
      try {
        const userResponse = await api.get('/auth/me');
        if (userResponse.data) {
          setUser(userResponse.data);
        }
      } catch (e) {
        console.log('Failed to refresh user data');
      }
      
      // If practice mode, add AI
      if (fourInARowMode === 'practice') {
        await api.post(`/four-in-a-row/${gameId}/add-ai`);
        toast.success('Practice game started!');
      } else if (fourInARowMode === 'quickMatch') {
        toast.success('Game created! Waiting for opponent...');
      }
      
      setShowFourInARowWager(false);
      setFourInARowMode(null);
      navigate(`/four-in-a-row/${gameId}`);
      
    } catch (error) {
      console.error('Failed to create 4 In A Row game:', error);
      toast.error(error.response?.data?.detail || 'Failed to create game');
    } finally {
      setCreatingFourInARow(false);
    }
  };
  
  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(1200px 500px at 50% -10%, #16233b 0%, #0A0E17 55%)' }}>
      <NavBar />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-4xl font-extrabold text-white text-center">Game Room</h1>
        <p className="text-center text-white/60 mt-1">
          Practice solo, or go live to play real opponents face-to-face.
        </p>

        {/* Practice-mode hero notice (CRAZY_EIGHTS_RULES Part 1) */}
        <div
          className="mt-6 rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
          data-testid="games-practice-notice"
        >
          <div className="flex-1">
            <div className="text-emerald-300 font-bold flex items-center gap-2">
              <span aria-hidden>🔴</span> You must be LIVE to play against others
            </div>
            <p className="text-white/70 text-sm mt-1">
              Games here are <b>practice mode</b> — sharpen up against the computer.
              Real opponents, real time, real competition — on your stream.
            </p>
          </div>
          <button
            onClick={() => navigate('/live')}
            data-testid="games-go-live-cta"
            className="shrink-0 rounded-xl px-5 py-3 font-bold bg-emerald-500 hover:bg-emerald-400 text-emerald-950"
          >
            Go Live &amp; Play →
          </button>
        </div>

        {/* Modern grid of tiles — reads GAME_CATALOG (single source of truth). */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {GAME_CATALOG.map((g) => (
            <GameTile
              key={g.id}
              g={g}
              onPlay={(game) => {
                if (game.id === 'spades') {
                  setSelectedGameForModal('spades');
                  setShowGameOptionsModal(true);
                } else if (game.id === 'four_in_a_row') {
                  setShowFourInARowOptions(true);
                } else if (game.id === 'crazy_eights') {
                  setSelectedGameForModal('crazy_eights');
                  setShowGameOptionsModal(true);
                } else if (game.id === 'checkers') {
                  // Games page = practice-vs-AI (live requires a linked
                  // broadcaster). Opens the existing CheckersEntryModal
                  // in practice mode; Start runs handleStartCheckers's
                  // practice path (charges 10 emeralds → /app/checkers/practice).
                  setSelectedGameForModal('checkers');
                  setCheckersEntryMode('practice');
                  setShowCheckersEntryModal(true);
                }
              }}
              onRules={(game) => setRulesGameId(game.id)}
            />
          ))}
        </div>

        <GameRulesModal gameId={rulesGameId} onClose={() => setRulesGameId(null)} />

        {/* Restyled stats panel — LIVE games only. Practice-vs-AI never
            increments these counters (guarded server-side in
            record_game_result callers). */}
        <div className="mt-10 rounded-2xl bg-[#0F1420] ring-1 ring-white/10 p-6 max-w-md mx-auto" data-testid="games-stats-panel">
          <h4 className="text-white font-bold text-center mb-4">Your Stats <span className="text-xs font-normal text-white/40">(live games)</span></h4>
          <div className="grid grid-cols-3 text-center">
            <div>
              <div className="text-2xl font-extrabold text-emerald-400" data-testid="games-stats-wins">
                {user?.wins || 0}
              </div>
              <div className="text-xs text-white/50">Wins</div>
            </div>
            <div>
              <div className="text-2xl font-extrabold text-red-400" data-testid="games-stats-losses">
                {user?.losses || 0}
              </div>
              <div className="text-xs text-white/50">Losses</div>
            </div>
            <div>
              <div className="text-2xl font-extrabold text-fuchsia-400" data-testid="games-stats-streak">
                {user?.streak || 0}
              </div>
              <div className="text-xs text-white/50">Streak</div>
            </div>
          </div>
          {/* Per-game breakdown row */}
          <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-white/70" data-testid="games-stats-by-game">
            <span data-testid="games-stats-by-game-spades">
              <span className="text-white/50">Spades</span>{' '}
              <span className="text-emerald-300 font-bold">{user?.by_game?.spades?.w || 0}W</span>
              <span className="text-white/30 mx-0.5">-</span>
              <span className="text-red-300 font-bold">{user?.by_game?.spades?.l || 0}L</span>
            </span>
            <span className="text-white/20">·</span>
            <span data-testid="games-stats-by-game-four_in_a_row">
              <span className="text-white/50">4iaR</span>{' '}
              <span className="text-emerald-300 font-bold">{user?.by_game?.four_in_a_row?.w || 0}W</span>
              <span className="text-white/30 mx-0.5">-</span>
              <span className="text-red-300 font-bold">{user?.by_game?.four_in_a_row?.l || 0}L</span>
            </span>
            <span className="text-white/20">·</span>
            <span data-testid="games-stats-by-game-crazy_eights">
              <span className="text-white/50">C8</span>{' '}
              <span className="text-emerald-300 font-bold">{user?.by_game?.crazy_eights?.w || 0}W</span>
              <span className="text-white/30 mx-0.5">-</span>
              <span className="text-red-300 font-bold">{user?.by_game?.crazy_eights?.l || 0}L</span>
            </span>
            <span className="text-white/20">·</span>
            <span data-testid="games-stats-by-game-checkers">
              <span className="text-white/50">Checkers</span>{' '}
              <span className="text-emerald-300 font-bold">{user?.by_game?.checkers?.w || 0}W</span>
              <span className="text-white/30 mx-0.5">-</span>
              <span className="text-red-300 font-bold">{user?.by_game?.checkers?.l || 0}L</span>
            </span>
          </div>
        </div>
      </div>

        {/* Legacy game grid — hidden by design (kept out of DOM to save render
            cost). All launch handlers used here (setShowGameOptionsModal /
            setShowFourInARowOptions) are still wired via the tiles above. */}
        {false && (
        <div className="text-center space-y-8" style={{ display: 'none' }}>
          <h1 className="text-4xl font-bold text-gray-900">Game Room</h1>
          <p className="text-xl text-gray-600">Create or join games and compete with others</p>

          {/* Game Selection - Main Spades Section */}
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="text-center">
                  <Gamepad2 className="h-16 w-16 text-emerald-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-semibold mb-2">Ready to Play Spades?</h3>
                  <p className="text-gray-600">Challenge other players or create a new game</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowGameOptionsModal(true)}>
                    <Trophy className="h-5 w-5 mr-2" />
                    Quick Match
                  </Button>
                  <Button size="lg" variant="outline" onClick={() => {
                    setShowGameOptionsModal(false);
                    setShowInviteModal(true);
                  }}>
                    <Users className="h-5 w-5 mr-2" />
                    Invite Player
                  </Button>
                </div>
                
                {/* Game Rules Button */}
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="w-full border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => setShowRulesModal(true)}
                >
                  <Book className="h-4 w-4 mr-2" />
                  View Game Rules (NY Ruleset)
                </Button>

                {/* Game Options Modal will be added after the card structure */}
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Your Stats</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-green-600">{user?.wins || 0}</div>
                      <div className="text-sm text-gray-600">Wins</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-red-600">{user?.losses || 0}</div>
                      <div className="text-sm text-gray-600">Losses</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">{user?.streak || 0}</div>
                      <div className="text-sm text-gray-600">Streak</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* ============================================================ */}
          {/* 4 IN A ROW - PLAYABLE */}
          {/* ============================================================ */}
          <Card className="max-w-2xl mx-auto shadow-lg border-2 border-blue-500 hover:border-blue-400 transition-colors">
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <span className="text-3xl">🔴</span>
                  </div>
                  <h3 className="text-2xl font-semibold mb-2 text-gray-900">4 In A Row</h3>
                  <p className="text-gray-600">Drop pieces to connect 4 in a row!</p>
                  <div className="flex justify-center gap-2 mt-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">NEW</span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">2 Players</span>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <Button 
                    size="lg" 
                    className="bg-blue-600 hover:bg-blue-700" 
                    onClick={() => setShowFourInARowOptions(true)}
                  >
                    <Trophy className="h-5 w-5 mr-2" />
                    Play Now
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={() => setShowFourInARowRules(true)}
                    className="border-blue-500 text-blue-700 hover:bg-blue-50"
                  >
                    <Book className="h-5 w-5 mr-2" />
                    Game Rules
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Coming Soon games — rendered from COMING_SOON_GAMES (single
              source of truth shared with the in-stream GamePickerModal).
              Add or promote a game there and both surfaces update. */}
          {COMING_SOON_GAMES.map((g) => (
            <Card
              key={g.id}
              className="max-w-md mx-auto shadow-lg relative overflow-hidden opacity-75"
              data-testid={`games-page-${g.id}`}
            >
              <CardContent className="p-6">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-gray-400 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-3xl">{g.emoji}</span>
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-600">{g.name}</h3>
                  <p className="text-gray-500">{g.description}</p>
                  <Button
                    size="lg"
                    className="w-full bg-gray-400 cursor-not-allowed"
                    disabled
                  >
                    <Clock className="h-5 w-5 mr-2" />
                    Coming Soon
                  </Button>
                </div>
              </CardContent>
              <div className="absolute top-4 right-4">
                <div className="bg-gradient-to-r from-amber-500 to-amber-400 text-amber-900 px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                  COMING SOON
                </div>
              </div>
            </Card>
          ))}
        </div>
        )}

      {/* Game Options Modal — game-aware (Spades + Crazy Eights share it). */}
      <Dialog open={showGameOptionsModal} onOpenChange={setShowGameOptionsModal}>
        <DialogContent
          className="sm:max-w-md max-h-[85vh] overflow-y-auto bg-[#0F1420] text-white border border-white/10"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Choose Game Mode
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600" data-testid="game-mode-modal-subtitle">
              Select how you'd like to start your{' '}
              {selectedGameForModal === 'crazy_eights' ? 'Crazy Eights' : 'Spades'} game.
            </p>
            
            <div className="space-y-3">
              {/* Quick Match with Real Players */}
              <Card 
                className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-emerald-500"
                onClick={() => handleSelectGameMode('quickMatch')}
                data-testid="game-mode-quick-match"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center">
                      <Trophy className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-emerald-800">Quick Match</h3>
                      <p className="text-sm text-gray-600">Find random players to compete against</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Match with Bots (Testing) */}
              <Card 
                className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-blue-500"
                onClick={() => handleSelectGameMode('quickMatchWithBots')}
                data-testid="game-mode-practice-ai"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                      <div className="text-white font-bold text-sm">🎮</div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-blue-800">Practice with AI</h3>
                      <p className="text-sm text-gray-600">Play against AI opponents for practice</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Invite Specific Player */}
              <Card 
                className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-purple-500"
                onClick={() => {
                  setShowGameOptionsModal(false);
                  setShowInviteModal(true);
                }}
                data-testid="game-mode-invite"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-purple-800">Invite Player</h3>
                      <p className="text-sm text-gray-600">Send invite to specific player by GEM ID</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="pt-2">
              <Button 
                variant="outline" 
                onClick={() => setShowGameOptionsModal(false)} 
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Player Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Invite Players ({invitedPlayers.length + 1}/4)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 flex-1 overflow-auto">
            <p className="text-gray-600">Invite 3 players by their GEM ID or select from online players.</p>
            
            {/* Invited Players List */}
            {invitedPlayers.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <h4 className="font-semibold text-sm text-gray-700">Invited Players:</h4>
                {invitedPlayers.map((player, index) => {
                  const isLinked = player.status === 'linked' || player.status === 'connected';
                  return (
                    <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                      <span className="font-medium">{player.name}</span>
                      {isLinked ? (
                        <Badge className="bg-emerald-600 text-white">Linked</Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending</Badge>
                      )}
                    </div>
                  );
                })}
                <div className="flex items-center justify-between bg-emerald-50 p-2 rounded border border-emerald-200">
                  <span className="font-medium">{user?.name} (You)</span>
                  <Badge className="bg-emerald-600">Host</Badge>
                </div>
              </div>
            )}
            
            {/* Only show invite form if less than 6 players (5 invited + host = 6 total) */}
            {invitedPlayers.length < 5 && (
              <>
                {/* Search/Manual Entry */}
                <div>
                  <label className="block text-sm font-medium mb-2">Search Player or Enter GEM ID</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search by name or enter GEM ID"
                      value={searchQuery || inviteGemId}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSearchQuery(val);
                        setInviteGemId(val);
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && handleInvitePlayer()}
                      className="pl-10"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Need {3 - invitedPlayers.length} more player{3 - invitedPlayers.length !== 1 ? 's' : ''}
                  </p>
                </div>
                
                {/* All Users List with Checkboxes */}
                <div className="border rounded-lg">
                  <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
                    <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                      <Users className="h-4 w-4 text-gray-700" />
                      All Players
                    </h4>
                    <div className="flex items-center gap-2">
                      {selectedUserIds.length > 0 && (
                        <Badge className="text-xs bg-emerald-600">
                          {selectedUserIds.length} selected
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">
                        {onlineUsers.filter(u => u.is_live).length} live
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {onlineUsers.filter(u => u.is_online).length} online
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="max-h-60 overflow-y-auto">
                    {loadingOnlineUsers ? (
                      <div className="p-4 text-center text-gray-500">Loading players...</div>
                    ) : filteredOnlineUsers.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        {searchQuery ? 'No players found matching your search' : 'No players available'}
                      </div>
                    ) : (
                      <div className="divide-y">
                        {filteredOnlineUsers.map((player) => (
                          <div 
                            key={player.id}
                            className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => {
                              // Toggle selection
                              if (selectedUserIds.includes(player.id)) {
                                setSelectedUserIds(selectedUserIds.filter(id => id !== player.id));
                              } else {
                                setSelectedUserIds([...selectedUserIds, player.id]);
                              }
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={selectedUserIds.includes(player.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (e.target.checked) {
                                    setSelectedUserIds([...selectedUserIds, player.id]);
                                  } else {
                                    setSelectedUserIds(selectedUserIds.filter(id => id !== player.id));
                                  }
                                }}
                                className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <div className="font-medium">{player.name}</div>
                                  {/* Sapphire Badge */}
                                  {player.badge_type === 'sapphire' && (
                                    <SapphireBadgeIcon className="w-4 h-4" />
                                  )}
                                  {/* Canary Badge */}
                                  {(player.badge_type === 'canary' || player.badge_type === 'verified') && (
                                    <CanaryBadgeIcon className="w-4 h-4" />
                                  )}
                                  {player.is_live && (
                                    <Badge className="text-xs bg-red-500 text-white animate-pulse">
                                      🔴 LIVE
                                    </Badge>
                                  )}
                                  {!player.is_live && player.is_online && (
                                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                      <Signal className="h-2 w-2 mr-1" />
                                      Online
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500">@{player.handle}</div>
                                <div className="flex gap-3 mt-1 text-xs text-gray-600">
                                  <span className="flex items-center gap-1">
                                    <Trophy className="h-3 w-3" />
                                    {player.wins}W / {player.losses}L
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <EmeraldIcon className="h-4 w-4" />
                                    {player.emeralds}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            
            <div className="flex gap-3">
              <Button 
                onClick={handleInvitePlayer} 
                className="flex-1" 
                disabled={selectedUserIds.length === 0 && !inviteGemId.trim()}
              >
                <Send className="h-4 w-4 mr-2" />
                Send Invitation{selectedUserIds.length > 1 ? 's' : ''} ({selectedUserIds.length || (inviteGemId.trim() ? 1 : 0)})
              </Button>
              <Button variant="outline" onClick={() => {
                setShowInviteModal(false);
                setSelectedUserIds([]);
              }} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Match Entry & Rules Modal */}
      <Dialog open={showWagerModal} onOpenChange={setShowWagerModal}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto bg-[#0F1420] text-white border border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Match Entry & Rules Setup
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              {selectedGameMode === 'invitePlayer' 
                ? `Configure game settings and match entry for your competition.`
                : 'Configure game settings and match entry amount. All players must accept the same settings.'
              }
            </p>
            
            <div className="space-y-4">
              {/* Match Entry Amount */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  {selectedGameMode === 'quickMatchWithBots' ? 'Practice Cost (Fixed)' : 'Match Entry Amount (Emeralds per player)'}
                </label>
                <Input
                  type="number"
                  placeholder="Minimum 50 Emeralds"
                  value={wagerAmount}
                  onChange={(e) => setWagerAmount(Math.max(0, parseInt(e.target.value) || 0))}
                  min="0"
                  max={user?.emeralds || 0}
                  disabled={selectedGameMode === 'quickMatchWithBots'}
                  className={selectedGameMode === 'quickMatchWithBots' ? 'bg-gray-100 cursor-not-allowed' : ''}
                />
                <p className="text-sm text-gray-500 mt-1">
                  {selectedGameMode === 'quickMatchWithBots'
                    ? `Practice costs 10 Emeralds. Your balance: ${user?.emeralds || 0} Emeralds`
                    : `Your balance: ${user?.emeralds || 0} Emeralds`
                  }
                </p>
              </div>
              
              {/* Simplified Practice Game Details */}
              {selectedGameMode === 'quickMatchWithBots' ? (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Practice Game Details</h4>
                  <div className="text-sm text-blue-700 space-y-1">
                    <p>• Entry: 10 Emeralds per practice game</p>
                    <p>• Play against 3 AI opponents</p>
                    <p>• AI across from you is your partner (Team 1)</p>
                    <p>• No live streaming - offline practice only</p>
                  </div>
                </div>
              ) : (
              <div>
              {/* Game Rules Toggles */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Game Rules</h4>
                
                {/* Round 10: PLAY TO — match target score (NY tables play to 250/300/500). */}
                <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg mb-3" data-testid="spades-play-to">
                  <div className="flex-1">
                    <div className="font-medium text-emerald-800">Play to</div>
                    <div className="text-sm text-emerald-700">First team to reach this score wins the match.</div>
                  </div>
                  <div className="flex gap-1">
                    {[250, 300, 500].map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setSpadesTargetScore(t)}
                        className={`px-3 py-1 rounded-full text-sm font-semibold border ${spadesTargetScore === t ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-800 border-emerald-300'}`}
                        data-testid={`spades-play-to-${t}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Allow Blind 6+ Toggle */}
                <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-3">
                  <div className="flex-1">
                    <div className="font-medium text-yellow-800">Allow Blind 6+</div>
                    <div className="text-sm text-yellow-700">
                      Players can declare blind bids ≥6 before seeing cards. Max 3 per team per match.
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allowBlindSix}
                      onChange={(e) => setAllowBlindSix(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                  </label>
                </div>

                {/* Allow Ace Check Toggle */}
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-green-800">Allow Ace Check</div>
                    <div className="text-sm text-green-700">
                      Teams can declare Ace Check to capture all four Aces. +100/-100 points.
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allowAceCheck}
                      onChange={(e) => setAllowAceCheck(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>
              </div>
              </div>
              )}
              
              {wagerAmount > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h4 className="font-semibold text-emerald-800 mb-2">Match Entry Details</h4>
                  <div className="text-sm text-emerald-700 space-y-1">
                    <div className="flex justify-between">
                      <span>Per player:</span>
                      <span>{wagerAmount} Emeralds</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Match Entry Pool:</span>
                      <span>{wagerAmount * 4} Emeralds</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Platform service fee (10%):</span>
                      <span>{Math.floor(wagerAmount * 4 * (economyRates?.rake_1v1 ?? 0.08))} Emeralds</span>
                    </div>
                    <div className="flex justify-between font-semibold border-t pt-1">
                      <span>Prize Pool:</span>
                      <span>{wagerAmount * 4 - Math.floor(wagerAmount * 4 * (economyRates?.rake_1v1 ?? 0.08))} Emeralds</span>
                    </div>
                    <p className="text-xs mt-2 text-emerald-600">
                      Winners receive the match prize in Rubies
                    </p>
                  </div>
                </div>
              )}
              
              {wagerAmount === 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-700">
                    No match entry selected - this will be a practice game, just for fun
                  </p>
                </div>
              )}
              
              {/* Skill-based disclaimer */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs text-gray-600 text-center">
                  This is a skill-based competitive game. Outcomes are determined by player performance, not chance.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button 
                onClick={handleSetWager} 
                className="flex-1"
                disabled={wagerAmount > (user?.emeralds || 0)}
              >
                <Trophy className="h-4 w-4 mr-2" />
                {selectedGameMode === 'quickMatchWithBots'
                  ? <>Start Practice (10 <EmeraldIcon className="h-4 w-4 inline" />)</>
                  : selectedGameMode === 'invitePlayer'
                    ? <>Go Live &amp; Invite{wagerAmount > 0 ? <> ({wagerAmount} <EmeraldIcon className="h-4 w-4 inline" />)</> : null}</>
                    : selectedGameMode === 'quickMatch'
                      ? <>Go Live &amp; Play{wagerAmount > 0 ? <> ({wagerAmount} <EmeraldIcon className="h-4 w-4 inline" />)</> : null}</>
                      : wagerAmount > 0
                        ? `Create Game (${wagerAmount} Emeralds)`
                        : 'Create Game'}
              </Button>
              <Button variant="outline" onClick={() => setShowWagerModal(false)} className="flex-1">
                Back
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Crazy Eights — dedicated entry modal (no Spades rules toggles). */}
      <CheckersEntryModal
        open={showCheckersEntryModal}
        onOpenChange={setShowCheckersEntryModal}
        mode={checkersEntryMode}
        wager={checkersWager}
        setWager={setCheckersWager}
        minMatchEntry={MIN_MATCH_ENTRY}
        userEmeralds={user?.emeralds || 0}
        hasLinkedOpponent={!!linkedGameOpponent?.id}
        onStart={handleStartCheckers}
      />

      <Dialog open={showC8EntryModal} onOpenChange={setShowC8EntryModal}>
        <DialogContent
          className="sm:max-w-md rounded-2xl border-white/10"
          style={{ background: 'linear-gradient(180deg, #0f1420 0%, #08111f 100%)', color: '#fff' }}
          data-testid="c8-entry-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <span className="text-2xl">🎴</span>
              {c8EntryMode === 'practice' ? 'Crazy Eights — Practice' : 'Crazy Eights — Quick Match'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            {/* Rules summary */}
            <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-4" data-testid="c8-entry-rules">
              <h4 className="text-sm font-bold text-white/90 mb-2">Quick rules</h4>
              <ul className="space-y-1.5 text-sm text-white/70">
                {C8_RULES.map((r, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-emerald-400">•</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Player count */}
            <PlayerCountSelector
              min={2}
              max={c8EntryMode === 'practice' ? 6 : 4}
              value={Math.min(c8PlayerCount, c8EntryMode === 'practice' ? 6 : 4)}
              onChange={setC8PlayerCount}
              note="2 players → 7 cards each · 3+ players → 5 cards each."
            />

            {/* Match entry (live only) */}
            {c8EntryMode === 'quickMatch' && (
              <div data-testid="c8-entry-wager-block">
                <label className="block text-sm font-medium mb-2 text-white/80">
                  Match Entry (Emeralds per player)
                </label>
                <input
                  type="number"
                  min={MIN_MATCH_ENTRY}
                  step="10"
                  value={c8Wager}
                  onChange={(e) => setC8Wager(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 text-white ring-1 ring-white/15 focus:ring-emerald-400 focus:outline-none"
                  data-testid="c8-entry-wager-input"
                />
                <p className="text-xs text-white/50 mt-1">
                  Minimum {MIN_MATCH_ENTRY} <EmeraldIcon className="h-3.5 w-3.5 inline" /> · Your balance: {user?.emeralds || 0} Emeralds
                </p>
              </div>
            )}

            {c8EntryMode === 'practice' && (
              <div className="rounded-lg bg-emerald-500/10 ring-1 ring-emerald-400/30 p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-emerald-200 font-medium text-sm">Practice Mode Cost:</span>
                  <span className="text-emerald-100 font-bold text-sm inline-flex items-center gap-1">10 <EmeraldIcon className="h-4 w-4 inline" /></span>
                </div>
                <p className="text-xs text-emerald-200/80">
                  Deducted when you start. Your balance: {user?.emeralds || 0} <EmeraldIcon className="h-3.5 w-3.5 inline" />.
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowC8EntryModal(false)}
                className="flex-1 bg-white/5 text-white/80 border-white/15 hover:bg-white/10"
                data-testid="c8-entry-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleStartCrazyEights}
                className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold"
                data-testid="c8-entry-start-btn"
                disabled={
                  c8EntryMode === 'practice'
                    ? (user?.emeralds || 0) < 10
                    : c8Wager > (user?.emeralds || 0)
                }
              >
                {c8EntryMode === 'practice'
                  ? <>Start Practice (10 <EmeraldIcon className="h-4 w-4 inline" />)</>
                  : <>Go Live &amp; Play{c8Wager > 0 ? <> ({c8Wager} <EmeraldIcon className="h-4 w-4 inline" />)</> : null}</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Game Rules Modal */}
      <Dialog open={showRulesModal} onOpenChange={setShowRulesModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-gradient-to-br from-gray-900 to-black text-white">
          <DialogHeader>
            <DialogTitle className="text-3xl font-bold text-center text-emerald-400">
              🎴 NY Style Spades Rules
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <section>
              <h3 className="text-xl font-bold text-emerald-300 mb-2">🎯 Game Objective</h3>
              <p className="text-gray-300">First team to reach <span className="font-bold text-yellow-400">500 points</span> wins! Play in teams of 2 (you + partner vs 2 opponents).</p>
            </section>
            
            <Separator className="bg-gray-700" />
            
            <section>
              <h3 className="text-xl font-bold text-emerald-300 mb-2">♠️ Trump Cards (Highest to Lowest)</h3>
              <ol className="list-decimal list-inside space-y-1 text-gray-300">
                <li><span className="font-bold text-red-400">Big Joker</span> (highest)</li>
                <li><span className="font-bold text-purple-400">Little Joker</span></li>
                <li><span className="font-bold">2♦ (Two of Diamonds)</span> - counts as spade!</li>
                <li><span className="font-bold">2♠</span> (Two of Spades)</li>
                <li>A♠, K♠, Q♠, J♠, 10♠, 9♠, 8♠, 7♠, 6♠, 5♠, 4♠, 3♠</li>
              </ol>
            </section>
            
            <Separator className="bg-gray-700" />
            
            <section>
              <h3 className="text-xl font-bold text-emerald-300 mb-2">🎲 Hand 1: "Bid Itself"</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-300">
                <li><span className="font-bold">NO bidding</span> in the first hand</li>
                <li>Cards are dealt and you play immediately</li>
                <li>Score = <span className="text-yellow-400">10 points × books won</span></li>
                <li>No bags, no penalties - simple scoring!</li>
              </ul>
            </section>
            
            <Separator className="bg-gray-700" />
            
            <section>
              <h3 className="text-xl font-bold text-emerald-300 mb-2">📋 Bidding (Hand 2+)</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-300">
                <li><span className="font-bold">Bidding Order:</span> Team that plays first → opponent bids first</li>
                <li>Within team: <span className="text-emerald-400">Partner bids before you</span> (you get final say!)</li>
                <li><span className="font-bold text-red-400">Minimum Requirements:</span></li>
                <ul className="list-circle list-inside ml-6 space-y-1">
                  <li>Each team must bid ≥ 4</li>
                  <li>Table total must be ≥ 11</li>
                  <li><span className="text-yellow-400">MISDEAL</span> if not met - bid again!</li>
                </ul>
              </ul>
            </section>
            
            <Separator className="bg-gray-700" />
            
            <section>
              <h3 className="text-xl font-bold text-emerald-300 mb-2">💯 Scoring</h3>
              <div className="space-y-3">
                <div className="bg-green-900/30 p-3 rounded border border-green-600">
                  <p className="font-bold text-green-300">✓ Made Bid (0-2 bags)</p>
                  <p className="text-gray-300">Score = (10 × bid) + bags</p>
                  <p className="text-sm text-gray-400">Example: Bid 7, won 9 = 70 + 2 = <span className="text-yellow-400">+72 points</span></p>
                </div>
                
                <div className="bg-red-900/30 p-3 rounded border border-red-600">
                  <p className="font-bold text-red-300">✗ SET (3+ bags OR failed bid)</p>
                  <p className="text-gray-300">Score = -(10 × bid)</p>
                  <p className="text-sm text-gray-400">Example: Bid 8, won 11 (3 bags) = <span className="text-red-400">-80 points</span></p>
                  <p className="text-sm text-red-400 font-bold mt-1">⚠️ 3 SETs = Instant Loss!</p>
                </div>
                
                <div className="bg-yellow-900/30 p-3 rounded border border-yellow-600">
                  <p className="font-bold text-yellow-300">📊 Bags Accumulation</p>
                  <p className="text-gray-300">Every 10 total bags = <span className="text-red-400">-100 points penalty</span></p>
                  <p className="text-sm text-gray-400">Bags carry over between hands</p>
                </div>
              </div>
            </section>
            
            <Separator className="bg-gray-700" />
            
            <section>
              <h3 className="text-xl font-bold text-emerald-300 mb-2">🎴 Special Rules</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li><span className="font-bold text-blue-400">Boston (13 books):</span> +200 bonus points</li>
                <li><span className="font-bold text-purple-400">Ace Check:</span> Declare before bidding
                  <ul className="list-circle list-inside ml-6">
                    <li>Win 1st book with Ace: <span className="text-green-400">+100 points</span></li>
                    <li>Fail: <span className="text-red-400">-100 points</span></li>
                  </ul>
                </li>
                <li><span className="font-bold text-orange-400">Blind 6+:</span> Bid without seeing cards (doubles points!)</li>
                {/* Renege detection temporarily disabled - will revisit later */}
                {/* <li><span className="font-bold text-red-400">Renege:</span> Fail to follow suit = <span className="text-red-400">-3 books penalty</span></li> */}
              </ul>
            </section>
            
            <Separator className="bg-gray-700" />
            
            <section>
              <h3 className="text-xl font-bold text-emerald-300 mb-2">🔄 Gameplay</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-300">
                <li><span className="font-bold">Turn Order:</span> Clockwise (You → Left → Top → Right)</li>
                <li><span className="font-bold">Following Suit:</span> MUST follow led suit if you have it</li>
                <li><span className="font-bold">2♦ Rule:</span> If 2♦ is led, you must play trump (spades), NOT diamonds!</li>
                <li><span className="font-bold">Spades Breaking:</span> Can't lead spades until broken (or only spades left)</li>
                <li><span className="font-bold">Winning Books:</span> Highest trump wins, or highest card of led suit</li>
              </ul>
            </section>
            
            <Separator className="bg-gray-700" />
            
            <section className="bg-emerald-900/30 p-4 rounded border border-emerald-600">
              <h3 className="text-xl font-bold text-emerald-300 mb-2">🏆 Winning the Match</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-300">
                <li>First team to <span className="font-bold text-yellow-400">500 points</span> wins</li>
                <li>OR opponent gets <span className="font-bold text-red-400">3 SETs</span> (instant loss for them)</li>
              </ul>
            </section>
          </div>
          
          <div className="flex justify-center pt-4">
            <Button 
              onClick={() => setShowRulesModal(false)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Got It! Let's Play
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* ============================================================ */}
      {/* 4 IN A ROW MODALS */}
      {/* ============================================================ */}
      
      {/* 4 In A Row Game Options Modal */}
      <Dialog open={showFourInARowOptions} onOpenChange={setShowFourInARowOptions}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto bg-[#0F1420] text-white border border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-green-500 rounded-full flex items-center justify-center">
                <span className="text-lg">🔴</span>
              </div>
              4 In A Row - Choose Mode
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">Select how you'd like to play.</p>
            
            <div className="space-y-3">
              {/* Practice with AI */}
              <Card 
                className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-blue-500"
                onClick={() => handleFourInARowModeSelect('practice')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-xl">🤖</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-blue-800">Practice with AI</h3>
                      <p className="text-sm text-gray-600">Play against AI opponent for practice</p>
                      <span className="text-xs text-amber-600 font-medium flex items-center gap-1">10 <EmeraldIcon className="h-3 w-3 inline" /></span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Match */}
              <Card 
                className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-emerald-500"
                onClick={() => handleFourInARowModeSelect('quickMatch')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center">
                      <Trophy className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-emerald-800">Quick Match</h3>
                      <p className="text-sm text-gray-600">Find a random opponent to compete</p>
                      <span className="text-xs text-amber-600 font-medium flex items-center gap-1">Min 50 <EmeraldIcon className="h-3 w-3 inline" /></span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Invite Player */}
              <Card 
                className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-purple-500"
                onClick={() => handleFourInARowModeSelect('invite')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-purple-800">Invite Player</h3>
                      <p className="text-sm text-gray-600">Challenge a specific friend</p>
                      <span className="text-xs text-amber-600 font-medium">Custom Entry</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Button 
              variant="outline" 
              onClick={() => setShowFourInARowOptions(false)} 
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 4 In A Row Wager Modal */}
      <Dialog open={showFourInARowWager} onOpenChange={setShowFourInARowWager}>
        <DialogContent className="sm:max-w-md bg-[#0F1420] text-white border border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-green-500 rounded-full flex items-center justify-center">
                <span className="text-lg">🔴</span>
              </div>
              {fourInARowMode === 'practice' ? 'Start Practice Game' : 'Set Your Entry'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {fourInARowMode === 'practice' ? (
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-blue-700 font-medium">Practice Mode Cost:</span>
                  <span className="text-blue-800 font-bold flex items-center gap-1">10 <EmeraldIcon className="h-4 w-4" /></span>
                </div>
                <p className="text-sm text-gray-600">Play against AI to improve your skills. No entry, no prize - just practice!</p>
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Your Balance:</span>
                    <span className="font-medium flex items-center gap-1">{user?.emeralds || 0} <EmeraldIcon className="h-3 w-3" /></span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1 mb-2">
                    Entry Amount <EmeraldIcon className="h-4 w-4" />
                  </label>
                  <input
                    type="number"
                    min="50"
                    max={user?.emeralds || 1000}
                    step="10"
                    value={fourInARowWager}
                    onChange={(e) => setFourInARowWager(Math.max(50, parseInt(e.target.value) || 50))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">Minimum: 50 <EmeraldIcon className="h-3 w-3 inline" /> • Your balance: {user?.emeralds || 0} <EmeraldIcon className="h-3 w-3 inline" /></p>
                </div>

                {fourInARowWager > 0 && (
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Your Entry:</span>
                      <span className="font-medium flex items-center gap-1">{fourInARowWager} <EmeraldIcon className="h-3 w-3" /></span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Prize Pool:</span>
                      <span className="font-medium text-emerald-600 flex items-center gap-1">{fourInARowWager * 2} <EmeraldIcon className="h-3 w-3" /></span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Platform Fee (10%):</span>
                      <span className="font-medium text-gray-500 flex items-center gap-1">{Math.floor(fourInARowWager * 2 * (economyRates?.rake_1v1 ?? 0.08))} <EmeraldIcon className="h-3 w-3" /></span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-gray-600">Winner Takes:</span>
                      <span className="font-bold text-purple-600 flex items-center gap-1">{Math.floor(fourInARowWager * 2 * (1 - (economyRates?.rake_1v1 ?? 0.08)))} <RubyIcon className="h-3 w-3" /></span>
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowFourInARowWager(false)} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleStartFourInARow}
                disabled={creatingFourInARow || (fourInARowMode === 'practice' ? (user?.emeralds || 0) < 10 : fourInARowWager > (user?.emeralds || 0))}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="four-in-a-row-start-btn"
              >
                {creatingFourInARow ? 'Creating...' : fourInARowMode === 'practice' ? (
                  <span className="flex items-center gap-1">Start Practice (10 <EmeraldIcon className="h-4 w-4" />)</span>
                ) : 'Create Game'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 4 In A Row Rules Modal */}
      <Dialog open={showFourInARowRules} onOpenChange={setShowFourInARowRules}>
        <DialogContent className="sm:max-w-lg bg-gray-900 text-white border-gray-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Book className="h-6 w-6 text-blue-400" />
              4 In A Row - Game Rules
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-gray-300">
            <section>
              <h3 className="text-lg font-bold text-blue-300 mb-2">🎯 Objective</h3>
              <p>Be the first player to connect <span className="font-bold text-yellow-400">4 of your pieces</span> in a row!</p>
            </section>
            
            <Separator className="bg-gray-700" />
            
            <section>
              <h3 className="text-lg font-bold text-blue-300 mb-2">🎮 How to Play</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Players take turns dropping pieces into columns</li>
                <li>Pieces fall to the lowest available slot (gravity!)</li>
                <li><span className="font-bold text-red-400">Player 1</span> uses <span className="text-red-400">Red</span> pieces</li>
                <li><span className="font-bold text-green-400">Player 2</span> uses <span className="text-green-400">Green</span> pieces</li>
                <li>Simply tap/click a column to drop your piece</li>
              </ul>
            </section>
            
            <Separator className="bg-gray-700" />
            
            <section>
              <h3 className="text-lg font-bold text-blue-300 mb-2">🏆 Winning</h3>
              <p className="mb-2">Connect 4 pieces in any direction:</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gray-800 p-2 rounded text-center">
                  <span className="text-blue-400">Horizontal</span> ➡️
                </div>
                <div className="bg-gray-800 p-2 rounded text-center">
                  <span className="text-blue-400">Vertical</span> ⬇️
                </div>
                <div className="bg-gray-800 p-2 rounded text-center">
                  <span className="text-blue-400">Diagonal</span> ↗️
                </div>
                <div className="bg-gray-800 p-2 rounded text-center">
                  <span className="text-blue-400">Diagonal</span> ↘️
                </div>
              </div>
            </section>
            
            <Separator className="bg-gray-700" />
            
            <section>
              <h3 className="text-lg font-bold text-blue-300 mb-2">🤝 Draw</h3>
              <p>If the board fills up with no winner, the game is a <span className="font-bold text-yellow-400">draw</span> and entries are refunded to both players.</p>
            </section>
            
            <Separator className="bg-gray-700" />
            
            <section className="bg-blue-900/30 p-4 rounded border border-blue-600">
              <h3 className="text-lg font-bold text-blue-300 mb-2 flex items-center gap-2"><EmeraldIcon className="h-5 w-5" /> Entries</h3>
              <ul className="list-disc list-inside space-y-1">
                <li className="flex items-center gap-1">Minimum entry: <span className="font-bold text-yellow-400 flex items-center gap-1">50 <EmeraldIcon className="h-4 w-4" /></span></li>
                <li className="flex items-center gap-1">Winner receives 90% of the prize (in <RubyIcon className="h-4 w-4 inline" />)</li>
                <li>Draw = Both players get their entry back</li>
              </ul>
            </section>
          </div>
          
          <div className="flex justify-center pt-4">
            <Button 
              onClick={() => setShowFourInARowRules(false)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Got It! Let's Play
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================
// GEMS Plus subscription — offer modal + wallet banner (Phase 4-real)
// ============================================================
// Auto-shown once per session on the Discover page after onboarding.
// Dismissed with "Maybe later" persists via localStorage (`gems_plus_offer_dismissed`).
// The user can always re-open via the wallet banner or Start Plus flow.
const GEMS_PLUS_DISMISS_KEY = 'gems_plus_offer_dismissed_v1';

/** Render a fee as a percent string with one decimal only when the
 *  percentage isn't whole. e.g. 0.03 → "3", 0.015 → "1.5", 0.005 → "0.5".
 *  Returns just the number — callers add the `%` themselves. */
const formatFeePct = (fee) => {
  const pct = Number(fee || 0) * 100;
  return pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(1);
};

const startGemsPlusFlow = async () => {
  try {
    const res = await api.post('/subscriptions/create');
    if (res.data?.approve_url) {
      window.location.href = res.data.approve_url;
    } else {
      toast.error('Subscription setup failed. Please try again.');
    }
  } catch (err) {
    toast.error(err.response?.data?.detail || 'Subscription setup unavailable');
  }
};

/** Lightweight hook — polls `/subscriptions/status` once per mount and
 *  returns `{isPlus, loading}`. Callers use this to hide Plus CTAs from
 *  members without threading subscription state through props. */
const useIsPlus = () => {
  const [isPlus, setIsPlus] = useState(null);   // null = unknown / loading
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get('/subscriptions/status');
        if (alive) setIsPlus(!!r.data?.is_plus);
      } catch {
        if (alive) setIsPlus(false);
      }
    })();
    return () => { alive = false; };
  }, []);
  return { isPlus, loading: isPlus === null };
};

// Round 59: every price comes from the server (Admin → Currency Management → Pricing), never from this file.
const DEFAULT_PRICING = { plus_price_usd: 12.99, plus_price_ios_usd: 12.99, emerald_packages: null, min_recharge_usd: 10, max_recharge_usd: 3000, emeralds_per_usd: 100 };
let __pricingCache = null;
let __pricingPromise = null;
const loadPricing = () => {
  if (!__pricingPromise) {
    __pricingPromise = api.get('/config/features')
      .then((r) => { __pricingCache = { ...DEFAULT_PRICING, ...(r.data?.pricing || {}) }; return __pricingCache; })
      .catch(() => DEFAULT_PRICING);
  }
  return __pricingPromise;
};
const usePricing = () => {
  const [p, setP] = useState(__pricingCache || DEFAULT_PRICING);
  useEffect(() => { let on = true; loadPricing().then((v) => { if (on) setP(v); }); return () => { on = false; }; }, []);
  return p;
};
const PlusPriceTag = ({ suffix = '/mo' }) => { const p = usePricing(); return <>${Number(p.plus_price_usd).toFixed(2)}{suffix}</>; };

const GemsPlusOfferModal = ({ open, onClose, benefits }) => {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="sm:max-w-lg bg-gradient-to-br from-emerald-50 to-white"
        data-testid="gems-plus-offer-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-emerald-700 flex items-center gap-2">
            <EmeraldIcon className="h-7 w-7" />
            Introducing GEMS Plus
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-gray-700">
            Play more, keep more. <span className="font-semibold">${Number(benefits?.price_usd ?? 12.99).toFixed(2)}/month</span>, cancel anytime.
          </p>
          <ul className="space-y-2 text-sm">
            <li>• <strong>1,200 Emeralds</strong> every month — the day you join and on every renewal</li>
            <li>• <strong>Keep 95%</strong> when you withdraw (vs 92%) · <strong>5% platform fee</strong> at your tables (vs 8%)</li>
            <li>• <strong>Weekly withdrawal up to $9,000</strong> (vs $7,000)</li>
            <li>• <strong>Half-price</strong> Ruby → Emerald conversions ({formatFeePct(benefits?.ruby_to_emerald_fee ?? 0.015)}% vs 3%)</li>
            <li>• Subscriber badge · Early game access</li>
          </ul>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => { onClose(); startGemsPlusFlow(); }}
              data-testid="gems-plus-start-btn"
            >
              Start GEMS Plus
            </Button>
            <Button variant="outline" onClick={onClose} data-testid="gems-plus-later-btn">
              Maybe later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// One-shot auto-open of the offer for non-Plus users who haven't dismissed it.
const GemsPlusOffer = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [benefits, setBenefits] = useState(null);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/subscriptions/status');
        if (!alive) return;
        setBenefits({ ...res.data.benefits, price_usd: res.data.price_usd });
        const alreadyPlus = res.data?.is_plus === true;
        const dismissed = localStorage.getItem(GEMS_PLUS_DISMISS_KEY) === '1';
        if (!alreadyPlus && !dismissed) setOpen(true);
      } catch {
        /* silent — auth-optional */
      }
    })();
    return () => { alive = false; };
  }, [user?.id]);
  return (
    <GemsPlusOfferModal
      open={open}
      onClose={() => {
        localStorage.setItem(GEMS_PLUS_DISMISS_KEY, '1');
        setOpen(false);
      }}
      benefits={benefits}
    />
  );
};

// Persistent dismissible banner on the wallet page. Uses a DIFFERENT
// storage key so the wallet nudge is independent of the auto-open modal.
const GEMS_PLUS_BANNER_KEY = 'gems_plus_banner_dismissed_v1';
const GemsPlusBanner = ({ economyRates, subscriptionStatus }) => {
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && localStorage.getItem(GEMS_PLUS_BANNER_KEY) === '1'
  );
  if (subscriptionStatus?.is_plus) return null;   // hide for Plus members
  if (dismissed) return null;
  const savings = economyRates
    ? `save ${Math.round(((economyRates.withdrawal_fee || 0.08) - 0.05) * 100)}% on withdrawals`
    : 'save 3% on withdrawals';
  return (
    <div
      className="flex items-center justify-between gap-3 bg-gradient-to-r from-emerald-100 to-emerald-50 border border-emerald-300 rounded-lg p-3"
      data-testid="wallet-plus-banner"
    >
      <div className="text-sm text-emerald-900">
        <span className="font-semibold">GEMS Plus — <PlusPriceTag /></span>
        <span className="mx-2 text-emerald-600">·</span>
        <span>1,200 Emeralds/mo · 5% platform fee · {savings}</span>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700"
          onClick={startGemsPlusFlow}
          data-testid="wallet-plus-banner-start-btn"
        >
          Start Plus
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => { localStorage.setItem(GEMS_PLUS_BANNER_KEY, '1'); setDismissed(true); }}
          data-testid="wallet-plus-banner-dismiss-btn"
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
};

const WalletPage = () => {
  const { user, refreshUser } = React.useContext(AuthContext);
  const [wallet, setWallet] = useState({ emeralds: 0, rubies: 0, usd_value: 0 });
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showEmeraldPacks, setShowEmeraldPacks] = useState(false);
  const [showConversion, setShowConversion] = useState(false);
  const [showCustomAmount, setShowCustomAmount] = useState(false);
  const [convertAmount, setConvertAmount] = useState('');
  const [customDollarAmount, setCustomDollarAmount] = useState('');
  const [purchasing, setPurchasing] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [txnTab, setTxnTab] = useState('emeralds');
  const [withdrawEligibility, setWithdrawEligibility] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  // Withdrawal method + payout details captured in the withdraw modal.
  const [withdrawMethod, setWithdrawMethod] = useState(
    (typeof window !== 'undefined' && localStorage.getItem('last_withdraw_method')) || 'paypal'
  );
  const [payoutDetails, setPayoutDetails] = useState({});
  // Canonical economy rates fetched from /api/economy/rates on mount.
  // NEVER hardcode 100/$1, 8%, 2%, 3% anywhere in this component.
  const [economyRates, setEconomyRates] = useState(null);
  // GEMS Plus subscription state (for the banner + Currency Info label).
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  
  // Private Beta Mode - state for restriction modal
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [betaFeatureName, setBetaFeatureName] = useState('');

  // ============================================================
  // PAYPAL REDIRECT LANDING + AUTO-RECOVERY (May 17, 2026)
  // ============================================================
  // Backend GET /api/paypal/capture-success bounces the buyer here with
  // ?purchase=success&emeralds=<n> (or purchase=failed | cancelled).
  // Handle the three cases + a recovery path for when the redirect made
  // it through but the browser stripped the emeralds= query param — poll
  // GET /api/paypal/capture-status/{id} (no side effects) up to 3 times
  // to confirm crediting from server-of-truth.
  // ============================================================
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const purchase = params.get('purchase');
    if (!purchase) return;

    const stripQuery = () => {
      try {
        const clean = window.location.pathname + window.location.hash;
        window.history.replaceState({}, '', clean);
      } catch { /* noop */ }
    };

    const refreshBalance = async () => {
      try {
        const r = await api.get('/wallet');
        setWallet(r.data);
      } catch { /* balance will refresh on next fetch */ }
      try { refreshUser && await refreshUser(); } catch { /* noop */ }
    };

    if (purchase === 'success') {
      const emeralds = parseInt(params.get('emeralds') || '0', 10) || 0;
      if (emeralds > 0) {
        // Normal success path — everything present, toast + refetch.
        toast.success(`${emeralds.toLocaleString()} emeralds added!`);
        refreshBalance();
        stripQuery();
      } else {
        // Truncated redirect — no ?emeralds=. Fall back to server-side status.
        const orderId = (() => {
          try { return localStorage.getItem('gems_last_order_id'); } catch { return null; }
        })();
        if (!orderId) {
          toast.success('Purchase complete!');
          refreshBalance();
          stripQuery();
          return;
        }
        stripQuery(); // clear the ambiguous query immediately; the poll drives the rest
        let attempts = 0;
        const poll = async () => {
          attempts += 1;
          try {
            const r = await api.get(`/paypal/capture-status/${encodeURIComponent(orderId)}`);
            const { credited, emeralds: creditedAmount } = r.data || {};
            if (credited) {
              const n = parseInt(creditedAmount || 0, 10) || 0;
              toast.success(n > 0 ? `${n.toLocaleString()} emeralds added!` : 'Purchase complete!');
              refreshBalance();
              try { localStorage.removeItem('gems_last_order_id'); } catch {}
              return;
            }
          } catch { /* transient — allow retry */ }
          if (attempts < 3) {
            setTimeout(poll, 3000);
          }
          // Silent stop after 3 attempts — user can pull to refresh; capture
          // may still complete server-side. Do not toast an error here to
          // avoid contradicting a valid completion that lands milliseconds later.
        };
        poll();
      }
    } else if (purchase === 'failed') {
      toast.error('Purchase failed. If you were charged, please contact support.');
      stripQuery();
    } else if (purchase === 'cancelled') {
      toast.info('Purchase cancelled.');
      stripQuery();
    }
  }, []);  // once on mount is intentional — the redirect only lands here once

  // Updated Emerald packages matching canonical economy_config.py (Phase 2).
  // Rate is always 100 Emeralds/$1. Bonuses cap at 25% (arbitrage boundary:
  // 125 emeralds × 0.80 gift × 0.01 ruby × 0.92 fees = 0.92 < 1.0).
  // Self-serve max is $3,000; larger amounts go through Recharge@gemslive.tv.
  const pricing = usePricing();                           // Round 59: the admin's prices, never this file's
  const MIN_RECHARGE_USD = pricing.min_recharge_usd;
  const MAX_RECHARGE_USD = pricing.max_recharge_usd;
  const EMERALDS_PER_USD = pricing.emeralds_per_usd || 100;
  const emeraldPacks = pricing.emerald_packages || [
    { id: 'pkg_1000',   price: 10.00,   emeralds: 1000,   bonus: 0,     discount: '—' },
    { id: 'pkg_2000',   price: 20.00,   emeralds: 2000,   bonus: 0,     discount: '—' },
    { id: 'pkg_5000',   price: 50.00,   emeralds: 5000,   bonus: 250,   discount: '5% more' },
    { id: 'pkg_10000',  price: 100.00,  emeralds: 10000,  bonus: 800,   discount: '8% more' },
    { id: 'pkg_20000',  price: 200.00,  emeralds: 20000,  bonus: 2400,  discount: '12% more' },
    { id: 'pkg_50000',  price: 500.00,  emeralds: 50000,  bonus: 7500,  discount: '15% more' },
    { id: 'pkg_100000', price: 1000.00, emeralds: 100000, bonus: 20000, discount: '20% more' },
    { id: 'pkg_200000', price: 2000.00, emeralds: 200000, bonus: 50000, discount: '25% more' },
    { id: 'pkg_300000', price: 3000.00, emeralds: 300000, bonus: 75000, discount: '25% more — BEST VALUE' },
  ];

  useEffect(() => {
    const fetchWallet = async () => {
      try {
        const response = await api.get('/wallet');
        setWallet(response.data);
      } catch (error) {
        console.error('Failed to fetch wallet:', error);
      }
    };
    const fetchTransactions = async () => {
      try {
        const response = await api.get('/wallet/transactions');
        setTransactions(response.data.transactions || []);
      } catch (error) {
        console.error('Failed to fetch transactions:', error);
      }
    };
    const fetchRates = async () => {
      try {
        const response = await api.get('/economy/rates');
        setEconomyRates(response.data);
      } catch (error) {
        console.error('Failed to fetch economy rates:', error);
      }
    };
    const fetchPlusStatus = async () => {
      try {
        const r = await api.get('/subscriptions/status');
        setSubscriptionStatus(r.data);
      } catch (error) {
        // Non-fatal — banner just stays visible.
      }
    };
    fetchWallet();
    fetchTransactions();
    fetchRates();
    fetchPlusStatus();
    // Load withdrawal eligibility on mount so the Rubies card renders
    // cooldown state (next_available_at, last withdrawal amount).
    checkWithdrawEligibility();
  }, []);

  // PayPal Purchase Handler
  const handlePurchasePackage = async (pkg) => {
    setPurchasing(true);
    try {
      // Create PayPal order
      const response = await api.post('/paypal/create-order', {
        package_id: pkg.id,
        emeralds: pkg.emeralds + pkg.bonus,
        amount_cents: Math.round(pkg.price * 100)
      });
      
      if (response.data.approve_url) {
        // Store order ID for capture after approval AND for post-redirect
        // auto-recovery (May 17, 2026) — if the browser's redirect drops the
        // ?emeralds= query, WalletPage polls capture-status to confirm credit.
        sessionStorage.setItem('paypal_order_id', response.data.order_id);
        try { localStorage.setItem('gems_last_order_id', response.data.order_id); } catch {}
        window.location.href = response.data.approve_url;
      } else {
        toast.error('Failed to create payment');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setPurchasing(false);
    }
  };
  
  const handleCustomPurchase = async () => {
    const dollars = parseFloat(customDollarAmount);
    if (isNaN(dollars) || dollars < MIN_RECHARGE_USD) {
      toast.error(`Minimum recharge is $${MIN_RECHARGE_USD}`);
      return;
    }
    if (dollars > MAX_RECHARGE_USD) {
      toast.error(`Self-serve recharges are capped at $${MAX_RECHARGE_USD.toLocaleString()}. Contact Recharge@gemslive.tv for larger amounts.`);
      return;
    }
    
    setPurchasing(true);
    const emeralds = Math.floor(dollars * EMERALDS_PER_USD); // $1 = 100 Emeralds (canonical)
    
    try {
      // Create PayPal order
      const response = await api.post('/paypal/create-order', {
        package_id: 'custom',
        emeralds: emeralds,
        amount_cents: Math.round(dollars * 100)
      });
      
      if (response.data.approve_url) {
        sessionStorage.setItem('paypal_order_id', response.data.order_id);
        try { localStorage.setItem('gems_last_order_id', response.data.order_id); } catch {}
        window.location.href = response.data.approve_url;
      } else {
        toast.error('Failed to create payment');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setPurchasing(false);
    }
  };
  
  const checkWithdrawEligibility = async () => {
    try {
      const response = await api.get('/wallet/withdrawal/eligibility');
      setWithdrawEligibility(response.data);
      return response.data;
    } catch (error) {
      toast.error('Failed to check withdrawal eligibility');
      return null;
    }
  };
  
  const handleWithdrawClick = async () => {
    const eligibility = await checkWithdrawEligibility();
    if (!eligibility) return;

    // Cooldown active — send them to the convert nudge instead.
    if (eligibility.cooldown && !eligibility.cooldown.eligible) {
      const s = eligibility.cooldown.seconds_remaining || 0;
      const d = Math.floor(s / 86400);
      const h = Math.floor((s % 86400) / 3600);
      toast.info(`Next withdrawal in ${d}d ${h}h. Convert Rubies to Emeralds instead?`);
      return;
    }

    if (eligibility.ruby_balance < eligibility.min_withdrawal) {
      toast.error(`Minimum ${eligibility.min_withdrawal.toLocaleString()} Rubies required to withdraw`);
      return;
    }

    setShowWithdraw(true);
  };
  
  const handleWithdrawSubmit = async () => {
    const amount = parseInt(withdrawAmount);
    if (!withdrawEligibility || amount < withdrawEligibility.min_withdrawal) {
      toast.error(`Minimum ${withdrawEligibility?.min_withdrawal || 5000} Rubies required`);
      return;
    }
    // Client-side validation mirrors backend `_validate_payout_details`.
    if (withdrawMethod === 'paypal') {
      const email = (payoutDetails.paypal_email || '').trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        toast.error('Enter a valid PayPal email');
        return;
      }
    } else if (withdrawMethod === 'airwallex') {
      for (const k of ['account_holder_name', 'bank_country', 'currency']) {
        if (!(payoutDetails[k] || '').toString().trim()) {
          toast.error(`Airwallex requires ${k.replace('_', ' ')}`);
          return;
        }
      }
      if (!(payoutDetails.iban || payoutDetails.account_number)) {
        toast.error('Airwallex requires IBAN or account number');
        return;
      }
      if (!(payoutDetails.swift_bic || payoutDetails.routing_number)) {
        toast.error('Airwallex requires SWIFT/BIC or routing number');
        return;
      }
    }
    
    setWithdrawing(true);
    try {
      const response = await api.post('/wallet/withdraw', {
        amount_rubies: amount,
        payment_method: withdrawMethod,
        payout_details: payoutDetails,
      });
      // Remember last-used method to prefill next time (never store details).
      try { localStorage.setItem('last_withdraw_method', withdrawMethod); } catch {}
      const net = response.data?.net_usd ?? response.data?.amount_usd;
      toast.success(`Withdrawal submitted — you'll receive $${Number(net).toFixed(2)} in 2–5 business days`);
      setShowWithdraw(false);
      setWithdrawAmount('');
      setPayoutDetails({});
      refreshUser && refreshUser();
      checkWithdrawEligibility();
    } catch (error) {
      // 429 → cooldown active; show the friendly countdown.
      if (error.response?.status === 429) {
        const detail = error.response.data?.detail;
        toast.error(detail?.message || 'One withdrawal per 7 days.');
      } else {
        toast.error(error.response?.data?.detail || 'Withdrawal failed');
      }
    } finally {
      setWithdrawing(false);
    }
  };

  const handleConversion = async () => {
    const rubiesAmount = parseInt(convertAmount);
    
    // Validate minimum amount
    if (rubiesAmount < 20) {
      toast.error('Minimum 20 Rubies required to convert');
      return;
    }
    
    if (rubiesAmount > (user?.rubies || 0)) {
      toast.error('Insufficient rubies');
      return;
    }
    
    try {
      const response = await api.post('/wallet/convert-rubies-to-emeralds', {
        rubies: rubiesAmount
      });
      setWallet(response.data);
      const emeraldsReceived = response.data.emeralds_received;
      setConvertAmount('');
      setShowConversion(false);
      toast.success(`Converted ${rubiesAmount} Rubies to ${emeraldsReceived} Emeralds!`);
      refreshUser && refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Conversion failed');
    }
  };
  
  // Calculate emeralds to receive (0.8 rate)
  const calculateEmeralds = (rubies) => {
    const amount = parseInt(rubies) || 0;
    // 3% haircut (0% for Plus). Rates come from /api/economy/rates —
    // never hardcode. Falls back to the standard 3% until fetch resolves.
    const fee = economyRates?.ruby_to_emerald_fee ?? 0.03;
    return Math.floor(amount * (1 - fee));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 to-emerald-800">
      <NavBar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white flex items-center justify-center gap-2">
              Wallet
              {subscriptionStatus?.is_plus && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-500 text-white border border-emerald-300"
                  data-testid="wallet-plus-badge"
                  title="GEMS Plus member"
                >
                  PLUS
                </span>
              )}
            </h1>
            <p className="text-xl text-emerald-100">Manage your Emeralds and Rubies</p>
          </div>

          <GemsPlusBanner economyRates={economyRates} subscriptionStatus={subscriptionStatus} />

          {/* Phase 4: wallet freeze banner (blocks value-out actions). */}
          {withdrawEligibility?.wallet_frozen && (
            <div
              className="bg-red-100 border border-red-400 text-red-800 rounded-lg p-4 text-center"
              data-testid="wallet-frozen-banner"
              role="alert"
            >
              <div className="font-semibold mb-1">Your wallet is frozen pending review.</div>
              <div className="text-sm">
                Please contact{' '}
                <a href="mailto:support@gemslive.tv" className="underline font-medium">
                  support@gemslive.tv
                </a>
                . Purchases remain available; withdrawal, conversion, and gifting are paused.
              </div>
            </div>
          )}

          {/* Phase 4: first-funds unlock hint (only when relevant + not frozen). */}
          {!withdrawEligibility?.wallet_frozen && withdrawEligibility?.unlock &&
           !withdrawEligibility.unlock.eligible && (
            <div
              className="bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-lg p-3 text-sm text-center"
              data-testid="wallet-unlock-banner"
            >
              {withdrawEligibility.unlock.reason === "not_unlocked" ? (
                <>
                  First withdrawal unlocks 7 days after your first recharge or once you receive over $50 in gifts.
                </>
              ) : (
                <>
                  First withdrawal available in{' '}
                  <span className="font-semibold" data-testid="wallet-unlock-remaining">
                    {(() => {
                      const s = withdrawEligibility.unlock.seconds_remaining || 0;
                      const d = Math.floor(s / 86400);
                      const h = Math.floor((s % 86400) / 3600);
                      return `${d}d ${h}h`;
                    })()}
                  </span>
                  .
                </>
              )}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            {/* Emeralds Card */}
            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <EmeraldIcon className="h-7 w-7" />
                  Emeralds
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="text-center space-y-4">
                  <div className="text-4xl font-bold text-emerald-600">{user?.emeralds || 0}</div>
                  <p className="text-gray-600">Used for match entry and in-app participation</p>
                  <div className="space-y-2">
                    <Button 
                      className={`w-full ${PRIVATE_BETA_MODE ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-600'} text-white`}
                      onClick={() => {
                        if (PRIVATE_BETA_MODE) {
                          setBetaFeatureName('Recharge Emeralds');
                          setShowBetaModal(true);
                        } else {
                          setShowEmeraldPacks(true);
                        }
                      }}
                      data-testid="recharge-emeralds-btn"
                    >
                      <EmeraldIcon className="h-4 w-4 mr-2" />
                      Recharge Emeralds
                      {PRIVATE_BETA_MODE && <Lock className="h-4 w-4 ml-2" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Rubies Card */}
            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-red-500 to-red-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <RubyIcon className="h-7 w-7" />
                  Rubies
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="text-center space-y-4">
                  <div className="text-4xl font-bold text-red-600" data-testid="wallet-rubies-balance">{user?.rubies || 0}</div>
                  {/* Show NET USD (after 8% + 2% fees), never gross. */}
                  <p className="text-gray-600" data-testid="wallet-rubies-net-usd">
                    You'll receive{' '}
                    <span className="font-semibold text-green-700">
                      ${(withdrawEligibility?.estimated_usd_net ?? Math.max(0, (user?.rubies || 0) * (economyRates?.ruby_to_usd_rate ?? 0.01) * (1 - (economyRates?.withdrawal_fee ?? 0.08) - (economyRates?.processing_fee ?? 0.02)))).toFixed(2)}
                    </span>
                    {' '}when you withdraw
                  </p>

                  {/* Cooldown state: Ready → button; else countdown + convert nudge. */}
                  {withdrawEligibility?.wallet_frozen ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="wallet-rubies-frozen-state">
                      Withdrawal and conversion are paused while your wallet is under review.
                    </div>
                  ) : withdrawEligibility?.cooldown && !withdrawEligibility.cooldown.eligible ? (
                    <div className="space-y-2" data-testid="wallet-cooldown-panel">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-left">
                        <div className="text-sm font-semibold text-amber-800">
                          Next withdrawal in{' '}
                          <span data-testid="wallet-cooldown-remaining">
                            {(() => {
                              const s = withdrawEligibility.cooldown.seconds_remaining || 0;
                              const d = Math.floor(s / 86400);
                              const h = Math.floor((s % 86400) / 3600);
                              return `${d}d ${h}h`;
                            })()}
                          </span>
                        </div>
                        {withdrawEligibility.cooldown.last_withdrawal_amount_rubies && (
                          <div className="text-xs text-amber-700 mt-1">
                            Last withdrawal: {Number(withdrawEligibility.cooldown.last_withdrawal_amount_rubies).toLocaleString()} Rubies
                          </div>
                        )}
                        <div className="text-xs text-amber-700">
                          One withdrawal per {withdrawEligibility.cooldown.cooldown_days || 7} days.
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => setShowConversion(true)}
                        disabled={(user?.rubies || 0) < 20}
                        data-testid="wallet-convert-nudge-btn"
                      >
                        Keep Playing — Convert to Emeralds ({formatFeePct(economyRates?.ruby_to_emerald_fee ?? 0.03)}% fee)
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Button
                        className={`w-full ${PRIVATE_BETA_MODE ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'} text-white`}
                        onClick={() => {
                          if (PRIVATE_BETA_MODE) {
                            setBetaFeatureName('Withdraw Earned Rewards');
                            setShowBetaModal(true);
                            return;
                          }
                          handleWithdrawClick();
                        }}
                        disabled={(user?.rubies || 0) < (withdrawEligibility?.min_withdrawal ?? 5000)}
                        data-testid="withdraw-rubies-btn"
                      >
                        <ArrowUpDown className="h-4 w-4 mr-2" />
                        Withdraw
                        {PRIVATE_BETA_MODE && <Lock className="h-4 w-4 ml-2" />}
                      </Button>
                      <Button 
                        variant="outline"
                        className="w-full border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                        onClick={() => setShowConversion(true)}
                        disabled={(user?.rubies || 0) < 20}
                        data-testid="wallet-convert-btn"
                      >
                        <ArrowUpDown className="h-4 w-4 mr-2" />
                        Keep Playing — Convert to Emeralds
                      </Button>
                    </div>
                  )}

                  {/* Withdrawal parameters summary. */}
                  <div className="text-xs text-gray-500 pt-1">
                    Min ${((economyRates?.withdrawal_min ?? 5000) * (economyRates?.ruby_to_usd_rate ?? 0.01)).toFixed(0)}
                    {' · '}Max ${((withdrawEligibility?.max_withdrawal ?? economyRates?.withdrawal_max_standard ?? 700000) * (economyRates?.ruby_to_usd_rate ?? 0.01)).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    {economyRates?.withdrawal_max_plus && !withdrawEligibility?.is_plus && (
                      <> (GEMS Plus ${(economyRates.withdrawal_max_plus * (economyRates?.ruby_to_usd_rate ?? 0.01)).toLocaleString()})</>
                    )}
                    {' · '}one withdrawal per week
                  </div>
                  {(user?.rubies || 0) < 20 && (
                    <span className="text-xs text-gray-500 block">Need at least 20 Rubies to convert</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Transaction History Button */}
          <div className="flex justify-center">
            <Button 
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
              onClick={() => setShowHistory(true)}
            >
              <Clock className="h-4 w-4 mr-2" />
              Transaction History
            </Button>
          </div>

          {/* Updated Token Economy */}
          <Card className="bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4 text-amber-800">Currency Info</h3>
              <div className="grid md:grid-cols-3 gap-6 text-sm">
                <div>
                  <h4 className="font-semibold text-amber-700">Exchange Rates</h4>
                  <ul className="space-y-1 text-amber-600">
                    <li>$1.00 = {economyRates?.emeralds_per_usd ?? 100} Emeralds</li>
                    <li>{Math.round(1 / (economyRates?.ruby_to_usd_rate ?? 0.01))} Rubies = $1.00 USD</li>
                    <li>
                      Convert Rubies → Emeralds{' '}
                      <span className="font-medium">
                        ({formatFeePct(economyRates?.ruby_to_emerald_fee ?? 0.03)}% fee
                        {economyRates?.is_plus ? ` · half-price Plus` : ''})
                      </span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-amber-700">Withdrawal Info</h4>
                  <ul className="space-y-1 text-amber-600">
                    <li>Min {(economyRates?.withdrawal_min ?? 5000).toLocaleString()} Rubies (${((economyRates?.withdrawal_min ?? 5000) * (economyRates?.ruby_to_usd_rate ?? 0.01)).toFixed(0)})</li>
                    <li>Max {(withdrawEligibility?.max_withdrawal ?? economyRates?.withdrawal_max_standard ?? 700000).toLocaleString()} Rubies / {economyRates?.cooldown_days ?? 7} days</li>
                    <li>Processed by PayPal · 2–5 business days</li>
                    <li>One withdrawal per week</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-amber-700">Earn More With GEMS Plus</h4>
                  <p className="text-amber-600 text-xs leading-relaxed">
                    GEMS Plus members earn higher withdrawal limits — up to{' '}
                    <strong>$9,000/week</strong> — and keep more of every withdrawal.
                  </p>
                  <p className="text-amber-600 text-xs leading-relaxed mt-2">
                    Need to recharge more than $3,000? Email{' '}
                    <a href="mailto:Recharge@gemslive.tv" className="underline font-medium">
                      Recharge@gemslive.tv
                    </a>
                    .
                  </p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-amber-200">
                <p className="text-xs text-amber-600 text-center">
                  Emeralds and Rubies are virtual tokens used within the GEMS Live platform. They are not a currency, stored value, or financial account.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Emerald Packs Modal */}
      <Dialog open={showEmeraldPacks} onOpenChange={setShowEmeraldPacks}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EmeraldIcon className="h-6 w-6" />
              Recharge Emeralds
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {emeraldPacks.map((pack, index) => (
              <Card 
                key={index} 
                className="cursor-pointer hover:shadow-md hover:border-emerald-400 transition-all"
                onClick={() => !purchasing && handlePurchasePackage(pack)}
                data-testid={`recharge-package-${pack.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <EmeraldIcon className="h-6 w-6" />
                      <div>
                        <div className="font-semibold">
                          {(pack.emeralds + pack.bonus).toLocaleString()} Emeralds
                        </div>
                        {pack.bonus > 0 && (
                          <div className="text-sm text-emerald-600 font-medium">
                            {pack.emeralds.toLocaleString()} + {pack.bonus.toLocaleString()} bonus ({pack.discount})
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-lg">${pack.price.toFixed(2)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* Custom Amount Option */}
            <Card 
              className="cursor-pointer hover:shadow-md hover:border-emerald-400 transition-all border-dashed"
              onClick={() => setShowCustomAmount(true)}
              data-testid="recharge-custom-amount-tile"
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Calculator className="h-6 w-6 text-emerald-600" />
                    <div>
                      <div className="font-semibold">Custom Amount</div>
                      <div className="text-sm text-gray-500">$1 = 100 Emeralds</div>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              </CardContent>
            </Card>

            {/* Contact Sales for >$3,000 recharges */}
            <a
              href="mailto:Recharge@gemslive.tv?subject=Large%20recharge%20request"
              data-testid="recharge-contact-large"
              className="block"
            >
              <Card className="cursor-pointer hover:shadow-md hover:border-emerald-400 transition-all">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-semibold text-gray-800">Need more than ${MAX_RECHARGE_USD.toLocaleString()}?</div>
                      <div className="text-sm text-gray-500">Contact us for enterprise recharges →</div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            </a>

            {/* Disclaimer */}
            <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500">
              Emeralds are virtual items used for in-app engagement and participation. They are not real currency, stored value, or financial accounts and have no cash value.
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Custom Amount Modal */}
      <Dialog open={showCustomAmount} onOpenChange={setShowCustomAmount}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Custom Amount</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-sm text-emerald-700">
                <strong>Rate:</strong> $1.00 = {EMERALDS_PER_USD} Emeralds
              </p>
              <p className="text-xs text-emerald-600 mt-1">
                Min: ${MIN_RECHARGE_USD}.00 | Max: ${MAX_RECHARGE_USD.toLocaleString()}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Dollar Amount</label>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold">$</span>
                <Input
                  type="number"
                  placeholder="10.00"
                  value={customDollarAmount}
                  onChange={(e) => setCustomDollarAmount(e.target.value)}
                  min={MIN_RECHARGE_USD}
                  max={MAX_RECHARGE_USD}
                  step="0.01"
                  data-testid="custom-recharge-amount-input"
                />
              </div>
            </div>
            
            {customDollarAmount && parseFloat(customDollarAmount) >= MIN_RECHARGE_USD && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <EmeraldIcon className="h-8 w-8" />
                  <span className="text-3xl font-bold text-emerald-600">
                    {Math.floor(parseFloat(customDollarAmount) * EMERALDS_PER_USD).toLocaleString()}
                  </span>
                </div>
                <p className="text-emerald-600 text-sm">Emeralds for ${parseFloat(customDollarAmount).toFixed(2)}</p>
              </div>
            )}
            
            <Button 
              className="w-full bg-emerald-500 hover:bg-emerald-600"
              onClick={handleCustomPurchase}
              disabled={!customDollarAmount || parseFloat(customDollarAmount) < MIN_RECHARGE_USD || parseFloat(customDollarAmount) > MAX_RECHARGE_USD || purchasing}
              data-testid="custom-recharge-submit-btn"
            >
              {purchasing ? 'Processing...' : 'Proceed to Payment'}
            </Button>

            <p className="text-xs text-gray-500 text-center" data-testid="custom-recharge-large-contact">
              Need to recharge more than ${MAX_RECHARGE_USD.toLocaleString()}? Email{' '}
              <a href="mailto:Recharge@gemslive.tv?subject=Large%20recharge%20request" className="underline font-medium">
                Recharge@gemslive.tv
              </a>
              .
            </p>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Withdraw Modal */}
      <Dialog open={showWithdraw} onOpenChange={setShowWithdraw}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RubyIcon className="h-6 w-6" />
              Withdraw
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {withdrawEligibility && (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">
                    <strong>Available:</strong> {withdrawEligibility.ruby_balance.toLocaleString()} Rubies
                  </p>
                  <p className="text-sm text-red-700">
                    <strong>You'll receive:</strong>{' '}
                    <span className="font-semibold text-green-700">
                      ${Number(withdrawEligibility.estimated_usd_net ?? 0).toFixed(2)}
                    </span>{' '}for your full balance
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    Min {withdrawEligibility.min_withdrawal.toLocaleString()} · Max {withdrawEligibility.max_withdrawal.toLocaleString()} Rubies · One withdrawal per {withdrawEligibility.cooldown?.cooldown_days ?? 7} days
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Ruby Amount</label>
                  <Input
                    type="number"
                    placeholder={`Min: ${withdrawEligibility.min_withdrawal.toLocaleString()}`}
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    min={withdrawEligibility.min_withdrawal}
                    max={withdrawEligibility.max_withdrawal}
                    data-testid="withdraw-amount-input"
                  />
                </div>

                {/* Net preview — payout math is method-independent (tier fees only),
                    so show the receive-amount right under the Ruby Amount input. */}
                {withdrawAmount && parseInt(withdrawAmount) >= withdrawEligibility.min_withdrawal && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center" data-testid="withdraw-net-preview">
                    <div className="text-green-700 text-sm mb-1">
                      Withdraw {parseInt(withdrawAmount).toLocaleString()} Rubies → You receive{' '}
                      <span className="text-2xl font-bold text-green-600 align-middle">
                        ${(
                          parseInt(withdrawAmount) *
                          (withdrawEligibility.ruby_to_usd_rate ?? 0.01) *
                          (1 - (withdrawEligibility.withdrawal_fee_pct ?? 0.08) - (withdrawEligibility.processing_fee_pct ?? 0.02))
                        ).toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-green-600">Same amount for every method — fees are tier-based.</p>
                  </div>
                )}

                {/* Withdrawal method selector — Airwallex is manual bank transfer. */}
                <div>
                  <label className="block text-sm font-medium mb-2">Withdrawal method</label>
                  <select
                    value={withdrawMethod}
                    onChange={(e) => { setWithdrawMethod(e.target.value); setPayoutDetails({}); }}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white"
                    data-testid="withdraw-method-select"
                  >
                    <option value="paypal">PayPal</option>
                    <option value="airwallex">Airwallex (bank transfer)</option>
                  </select>
                </div>

                {withdrawMethod === 'paypal' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">PayPal email</label>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={payoutDetails.paypal_email || ''}
                      onChange={(e) => setPayoutDetails({ ...payoutDetails, paypal_email: e.target.value })}
                      data-testid="withdraw-paypal-email-input"
                    />
                  </div>
                )}

                {withdrawMethod === 'airwallex' && (
                  <div className="space-y-2" data-testid="withdraw-airwallex-fields">
                    <Input placeholder="Account holder name" value={payoutDetails.account_holder_name || ''}
                      onChange={(e) => setPayoutDetails({ ...payoutDetails, account_holder_name: e.target.value })}
                      data-testid="withdraw-aw-name" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Bank country (e.g. US)" value={payoutDetails.bank_country || ''}
                        onChange={(e) => setPayoutDetails({ ...payoutDetails, bank_country: e.target.value })}
                        data-testid="withdraw-aw-country" />
                      <Input placeholder="Currency (e.g. USD)" value={payoutDetails.currency || ''}
                        onChange={(e) => setPayoutDetails({ ...payoutDetails, currency: e.target.value })}
                        data-testid="withdraw-aw-currency" />
                    </div>
                    <Input placeholder="IBAN OR account number" value={payoutDetails.iban || payoutDetails.account_number || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        // Route to `iban` if it looks like one, else `account_number`.
                        if (/^[A-Z]{2}[0-9A-Z]{2,}/.test(v)) setPayoutDetails({ ...payoutDetails, iban: v, account_number: '' });
                        else setPayoutDetails({ ...payoutDetails, account_number: v, iban: '' });
                      }}
                      data-testid="withdraw-aw-iban-or-account" />
                    <Input placeholder="SWIFT/BIC OR routing number" value={payoutDetails.swift_bic || payoutDetails.routing_number || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (/^\d{9}$/.test(v)) setPayoutDetails({ ...payoutDetails, routing_number: v, swift_bic: '' });
                        else setPayoutDetails({ ...payoutDetails, swift_bic: v, routing_number: '' });
                      }}
                      data-testid="withdraw-aw-swift-or-routing" />
                    <Input placeholder="Bank name (optional)" value={payoutDetails.bank_name || ''}
                      onChange={(e) => setPayoutDetails({ ...payoutDetails, bank_name: e.target.value })}
                      data-testid="withdraw-aw-bank-name" />
                  </div>
                )}
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                  Rubies are your earnings from playing and from viewer gifts. Emeralds cannot be withdrawn. Processed by PayPal · 2–5 business days.
                </div>
                
                <Button 
                  className="w-full bg-red-500 hover:bg-red-600"
                  onClick={handleWithdrawSubmit}
                  disabled={!withdrawAmount || parseInt(withdrawAmount) < withdrawEligibility.min_withdrawal || withdrawing}
                  data-testid="withdraw-submit-btn"
                >
                  {withdrawing ? 'Processing...' : 'Withdraw'}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Transaction History Modal */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Transaction History</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2 mb-3">
              <button onClick={() => setTxnTab('emeralds')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-bold border transition-colors ${
                  txnTab === 'emeralds' ? 'bg-emerald-600 text-white border-emerald-500'
                  : 'bg-transparent text-gray-500 border-gray-300'}`}
                data-testid="txn-tab-emeralds">
                <EmeraldIcon className="h-4 w-4" /> Emeralds
              </button>
              <button onClick={() => setTxnTab('rubies')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-bold border transition-colors ${
                  txnTab === 'rubies' ? 'bg-rose-600 text-white border-rose-500'
                  : 'bg-transparent text-gray-500 border-gray-300'}`}
                data-testid="txn-tab-rubies">
                <RubyIcon className="h-4 w-4" /> Rubies
              </button>
            </div>
            {(() => {
              const isRubyTxn = (tx) => tx.currency === 'rubies' || tx.currency === 'rubies_to_emeralds';
              const visibleTxns = transactions.filter((tx) => txnTab === 'rubies' ? isRubyTxn(tx) : !isRubyTxn(tx));
              if (visibleTxns.length === 0) {
                return (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{txnTab === 'rubies' ? 'No Ruby transactions yet' : 'No Emerald transactions yet'}</p>
                    <p className="text-sm">Your purchases, game entries, and earnings will appear here.</p>
                  </div>
                );
              }
              return visibleTxns.map((tx, index) => {
                // Bug fix (May 17, 2026): backend stores `timestamp` and
                // `transaction_type` — the previous code read `created_at` /
                // `type` which produced "Invalid Date" and red rows for all
                // wins/purchases. Guard against unparseable dates too.
                const rawDate = tx.timestamp ?? tx.created_at;
                let dateLabel = '—';
                if (rawDate) {
                  const d = new Date(rawDate);
                  if (!isNaN(d.getTime())) dateLabel = d.toLocaleDateString();
                }
                const txType = tx.transaction_type ?? tx.type;
                // Credits (green, +): purchase, win, ruby→emerald conversion credit,
                // gift RECEIVED. Everything else is a debit.
                const CREDIT_TYPES = new Set([
                  'purchase', 'win', 'gift_received', 'ruby_conversion_credit',
                  'ruby_to_emerald_credit', 'conversion_credit', 'earnings',
                  'refund', 'admin_grant',
                  'post_like_earning', 'referral_bonus', 'subscription_stipend',
                  'withdrawal_refund',
                ]);
                const isCredit = CREDIT_TYPES.has(txType);
                const amount = typeof tx.amount === 'number' ? tx.amount : 0;
                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`tx-row-${index}`}>
                    <div className="flex items-center gap-3">
                      {tx.currency === 'emeralds' ? (
                        <EmeraldIcon className="h-5 w-5" />
                      ) : (
                        <RubyIcon className="h-5 w-5" />
                      )}
                      <div>
                        <div className="font-medium">{tx.description || (txType ? txType.replace(/_/g, ' ') : 'Transaction')}</div>
                        <div className="text-xs text-gray-500" data-testid={`tx-date-${index}`}>
                          {dateLabel}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`font-bold ${isCredit ? 'text-green-600' : 'text-red-600'}`}
                      data-testid={`tx-amount-${index}`}
                    >
                      {isCredit ? '+' : '-'}{amount.toLocaleString()}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Rubies to Emeralds Conversion Modal */}
      <Dialog open={showConversion} onOpenChange={setShowConversion}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RubyIcon className="h-6 w-6" />
              Keep Playing — Convert to Emeralds
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-700">
                <strong>Conversion fee:</strong>{' '}
                {formatFeePct(economyRates?.ruby_to_emerald_fee ?? 0.03)}%
                {economyRates?.is_plus && ' — half-price Plus rate'}
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Minimum: 20 Rubies. Conversion is one-way and cannot be reversed.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Amount to Convert</label>
              <Input
                type="number"
                placeholder="Enter Rubies amount (min 20)"
                value={convertAmount}
                onChange={(e) => setConvertAmount(e.target.value)}
                min={20}
                max={user?.rubies || 0}
                data-testid="convert-amount-input"
              />
              <div className="mt-2 space-y-1">
                <p className="text-sm text-gray-500">
                  Available: <span className="font-semibold text-red-600">{user?.rubies || 0}</span> Rubies
                </p>
                {convertAmount && parseInt(convertAmount) >= 20 && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="convert-preview">
                    <p className="text-emerald-700 font-medium">
                      Turn {parseInt(convertAmount).toLocaleString()} Rubies into{' '}
                      <span className="text-3xl font-bold" data-testid="convert-emeralds-out">
                        {calculateEmeralds(convertAmount).toLocaleString()}
                      </span>{' '}Emeralds
                    </p>
                  </div>
                )}
                {convertAmount && parseInt(convertAmount) < 20 && parseInt(convertAmount) > 0 && (
                  <p className="text-sm text-red-600">
                    ⚠️ Minimum 20 Rubies required to convert
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button 
                onClick={handleConversion} 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={!convertAmount || parseInt(convertAmount) < 20 || parseInt(convertAmount) > (user?.rubies || 0)}
                data-testid="convert-submit-btn"
              >
                <EmeraldIcon className="h-4 w-4 mr-2" />
                Convert Now
              </Button>
              <Button variant="outline" onClick={() => setShowConversion(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Private Beta Restriction Modal */}
      <BetaRestrictionModal 
        isOpen={showBetaModal} 
        onClose={() => setShowBetaModal(false)} 
        featureName={betaFeatureName}
      />
    </div>
  );
};

const ProfilePage = () => {
  const { user, setUser } = React.useContext(AuthContext);
  const [profileImage, setProfileImage] = useState(user?.picture || null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPictureModal, setShowPictureModal] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    gemsTag: user?.handle || '',
    age: null,
    location: user?.location || '',
    bio: user?.bio || '',
    fans: user?.fans_count || user?.fans || 0,
    emeralds: user?.emeralds || 0,
    rubies: user?.rubies || 0,
    totalRubiesEarned: user?.rubies_earned || user?.rubies || 0,
    totalEmeraldsSpent: user?.emeralds_spent || 0,
    gamesWon: user?.wins || 0,
    winStreak: user?.streak || 0
  });
  
  // State for profile photos (up to 5 photos)
  const [profilePhotos, setProfilePhotos] = useState([]);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  // Load real user data from backend (not mock data)
  React.useEffect(() => {
    if (user) {
      // Calculate age from birth_date if available
      let calculatedAge = null;
      if (user.birth_date) {
        const birthDate = new Date(user.birth_date);
        const today = new Date();
        calculatedAge = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          calculatedAge--;
        }
      }
      
      setProfileData({
        name: user.name || '',
        gemsTag: user.handle || '',
        age: calculatedAge,
        location: user.location || '',
        bio: user.bio || '',
        fans: user.fans_count || user.fans || 0,
        emeralds: user.emeralds || 0,
        rubies: user.rubies || 0,
        totalRubiesEarned: user.rubies_earned || user.rubies || 0,
        totalEmeraldsSpent: user.emeralds_spent || 0,
        gamesWon: user.wins || 0,
        winStreak: user.streak || 0
      });
      setProfileImage(user.picture || null);
      if (user.profile_photos) {
        setProfilePhotos(user.profile_photos);
      }
    }
  }, [user]);

  // Save profile to backend and update AuthContext
  const saveProfile = async () => {
    try {
      const updates = {
        name: profileData.name,
        handle: profileData.gemsTag,
        bio: profileData.bio,
        location: profileData.location,
        picture: profileImage,
        profile_photos: profilePhotos,
      };
      
      const response = await api.put('/users/profile', updates);
      
      if (response.data.success && response.data.user) {
        setUser(response.data.user);
        toast.success('Profile updated!');
      } else {
        toast.success('Profile saved!');
      }
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save profile:', err);
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    }
  };

  // Handle profile photo upload (up to 5 photos)
  const handleProfilePhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    const remainingSlots = 5 - profilePhotos.length;
    const filesToProcess = files.slice(0, remainingSlots);
    
    filesToProcess.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageDataUrl = e.target.result;
        setProfilePhotos(prev => [...prev, imageDataUrl]);
        // Set first photo as profile image if none exists
        if (!profileImage && profilePhotos.length === 0) {
          setProfileImage(imageDataUrl);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove photo from gallery
  const removePhoto = (index) => {
    const newPhotos = profilePhotos.filter((_, i) => i !== index);
    setProfilePhotos(newPhotos);
    // If removed photo was profile image, set new one or clear
    if (profilePhotos[index] === profileImage) {
      setProfileImage(newPhotos.length > 0 ? newPhotos[0] : null);
    }
  };

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageDataUrl = e.target.result;
        setProfileImage(imageDataUrl);
        // Persist profile picture to backend so it's visible everywhere (livestreams,
        // chat, public profile, etc.) and survives across devices/sessions.
        try {
          await api.put('/users/profile', { picture: imageDataUrl });
          if (typeof setUser === 'function') {
            setUser((prev) => (prev ? { ...prev, picture: imageDataUrl } : prev));
          }
          toast.success('Profile picture updated');
        } catch (err) {
          console.error('Failed to save profile picture:', err);
          toast.error(err?.response?.data?.detail || 'Failed to save profile picture');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <NavBar />
      
      {/* Hero Section with Profile Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 text-6xl">♠</div>
          <div className="absolute top-32 right-20 text-4xl">♥</div>
          <div className="absolute bottom-20 left-20 text-5xl">♦</div>
          <div className="absolute bottom-32 right-32 text-6xl">♣</div>
        </div>
        
        <div className="max-w-6xl mx-auto px-4 py-12 relative z-10">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-8 shadow-2xl">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
              
              {/* Profile Picture Section */}
              <div className="flex-shrink-0">
                <div className="relative group">
                  <div 
                    className="w-48 h-48 rounded-2xl overflow-hidden border-4 border-white/30 shadow-xl cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => profileImage && setShowPictureModal(true)}
                  >
                    {profileImage ? (
                      <img 
                        src={profileImage} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                        <Users className="w-20 h-20 text-white/80" />
                      </div>
                    )}
                  </div>
                  
                  {/* Upload Button */}
                  <label className="absolute bottom-4 right-4 bg-amber-500 hover:bg-amber-400 text-amber-900 w-12 h-12 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 transform hover:scale-110 shadow-lg group-hover:opacity-100 opacity-90">
                    <Camera className="w-5 h-5" />
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleImageUpload}
                      className="hidden" 
                    />
                  </label>
                  
                  {/* Delete button — show if there's a picture */}
                  {(profileImage || user?.picture) && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm('Remove your profile picture?')) return;
                        try {
                          await api.put('/users/profile', { picture: '' });
                          setProfileImage(null);
                          const meRes = await api.get('/auth/me');
                          if (meRes.data) setUser(meRes.data);
                          toast.success('Profile picture removed');
                        } catch (err) {
                          toast.error('Failed to remove profile picture');
                        }
                      }}
                      className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 rounded-full p-2 border-2 border-white shadow-lg"
                      title="Remove profile picture"
                      data-testid="delete-profile-pic-btn"
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  )}
                </div>
              </div>

              {/* Profile Info */}
              <div className="flex-1 text-center md:text-left">
                <div className="mb-6">
                  <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                    {profileData.name}
                  </h1>
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-4">
                    <span className="bg-amber-400/20 text-amber-200 px-4 py-2 rounded-full text-lg font-semibold border border-amber-400/30">
                      @{profileData.gemsTag}
                    </span>
                    <div className="flex items-center gap-4 text-emerald-200">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {profileData.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Age {profileData.age}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-emerald-100 text-lg mb-6 max-w-2xl">
                  {profileData.bio}
                </p>

                {/* Stats Row - Updated with 3 boxes: Fans (purple), Rubies (red), Emeralds (green) */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-purple-500/20 backdrop-blur-sm rounded-xl p-4 border border-purple-500/30">
                    <div className="text-2xl font-bold text-purple-300">{profileData.fans.toLocaleString()}</div>
                    <div className="text-purple-200 text-sm">Fans</div>
                  </div>
                  <div className="bg-red-500/20 backdrop-blur-sm rounded-xl p-4 border border-red-500/30">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <RubyIcon className="h-6 w-6" />
                    </div>
                    <div className="text-2xl font-bold text-red-300">{profileData.totalRubiesEarned.toLocaleString()}</div>
                    <div className="text-red-200 text-sm">Rubies Earned</div>
                  </div>

                  <div className="bg-green-500/20 backdrop-blur-sm rounded-xl p-4 border border-green-500/30">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <EmeraldIcon className="h-6 w-6" />
                    </div>
                    <div className="text-2xl font-bold text-green-300">{profileData.totalEmeraldsSpent.toLocaleString()}</div>
                    <div className="text-green-200 text-sm">Emeralds Spent</div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col md:flex-row gap-4">
                  <Button 
                    onClick={() => setIsEditing(!isEditing)}
                    className="bg-amber-500 hover:bg-amber-400 text-amber-900 font-semibold px-6 py-3 rounded-full transition-all duration-300 transform hover:scale-105 border-2 border-amber-400"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {isEditing ? 'Cancel Edit' : 'Edit Profile'}
                  </Button>
                  <Button 
                    onClick={() => {
                      const profileUrl = `${window.location.origin}/profile/${user.handle}`;
                      navigator.clipboard.writeText(profileUrl);
                      toast.success('Profile link copied to clipboard!');
                    }}
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 font-semibold px-6 py-3 rounded-full transition-all duration-300 transform hover:scale-105"
                  >
                    <Share className="w-4 h-4 mr-2" />
                    Share Profile
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Left Column - Contributors Only */}
          <div className="space-y-6">

            {/* Contributors Card - Updated to remove @ signs and emerald symbols */}
            <Card className="bg-gradient-to-br from-amber-900/50 to-amber-800/50 border-amber-500/30 backdrop-blur-lg shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Heart className="w-6 h-6 text-amber-300" />
                  <h3 className="text-xl font-bold text-white">Top Contributors</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center text-amber-900 font-bold text-sm">1</div>
                    <span className="text-amber-200">spadesking</span>
                    <span className="ml-auto text-amber-300 font-semibold">450</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full flex items-center justify-center text-gray-800 font-bold text-sm">2</div>
                    <span className="text-amber-200">cardshark</span>
                    <span className="ml-auto text-amber-300 font-semibold">320</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-amber-600 to-amber-700 rounded-full flex items-center justify-center text-amber-100 font-bold text-sm">3</div>
                    <span className="text-amber-200">gamepro</span>
                    <span className="ml-auto text-amber-300 font-semibold">280</span>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="w-full mt-4 border-amber-400 text-amber-300 hover:bg-amber-900/30"
                  >
                    View All Contributors
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Profile Details & Edit Form */}
          <div className="lg:col-span-2">
            {isEditing ? (
              // Edit Form
              <Card className="bg-white/10 backdrop-blur-lg border-white/20 shadow-xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-6">Edit Profile</h3>
                  <div className="space-y-4">
                    {/* Photo Gallery Section */}
                    <div>
                      <label className="block text-emerald-200 mb-2">Profile Photos (Up to 5)</label>
                      <div className="grid grid-cols-5 gap-2 mb-4">
                        {[...Array(5)].map((_, index) => (
                          <div key={index} className="relative">
                            <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-white/20 bg-white/10">
                              {profilePhotos[index] ? (
                                <>
                                  <img 
                                    src={profilePhotos[index]} 
                                    alt={`Profile ${index + 1}`}
                                    className="w-full h-full object-cover cursor-pointer"
                                    onClick={() => setProfileImage(profilePhotos[index])}
                                  />
                                  <button
                                    onClick={() => removePhoto(index)}
                                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                                  >
                                    ×
                                  </button>
                                </>
                              ) : (
                                <label className="w-full h-full flex items-center justify-center cursor-pointer hover:bg-white/20 transition-colors">
                                  <Camera className="w-6 h-6 text-white/50" />
                                  <input 
                                    type="file" 
                                    accept="image/*" 
                                    onChange={handleProfilePhotoUpload}
                                    className="hidden" 
                                    multiple={profilePhotos.length === 0}
                                  />
                                </label>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-emerald-300 text-sm">Click any photo to set as profile picture</p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-emerald-200 mb-2">Name</label>
                        <input 
                          type="text"
                          value={profileData.name}
                          onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-amber-400 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-emerald-200 mb-2">GEMS Tag</label>
                        <input 
                          type="text"
                          value={profileData.gemsTag}
                          onChange={(e) => setProfileData(prev => ({ ...prev, gemsTag: e.target.value }))}
                          className="w-full p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-amber-400 focus:outline-none"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-emerald-200 mb-2">Age</label>
                        <input 
                          type="number"
                          value={profileData.age}
                          onChange={(e) => setProfileData(prev => ({ ...prev, age: parseInt(e.target.value) }))}
                          className="w-full p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-amber-400 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-emerald-200 mb-2">Location</label>
                        <input 
                          type="text"
                          value={profileData.location}
                          onChange={(e) => setProfileData(prev => ({ ...prev, location: e.target.value }))}
                          className="w-full p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-amber-400 focus:outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-emerald-200 mb-2">Bio</label>
                      <textarea 
                        value={profileData.bio}
                        onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                        rows="3"
                        className="w-full p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-amber-400 focus:outline-none"
                      />
                    </div>
                    <div className="flex gap-4 pt-4">
                      <Button 
                        onClick={saveProfile}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-6 py-3 rounded-full transition-all duration-300 transform hover:scale-105"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </Button>
                      <Button 
                        onClick={() => setIsEditing(false)}
                        variant="outline"
                        className="border-white/30 text-white hover:bg-white/10 font-semibold px-6 py-3 rounded-full"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              // Profile Content Tabs
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-4 bg-white/10 backdrop-blur-lg border border-white/20">
                  <TabsTrigger value="overview" className="data-[state=active]:bg-amber-500 data-[state=active]:text-amber-900">Overview</TabsTrigger>
                  <TabsTrigger value="stats" className="data-[state=active]:bg-amber-500 data-[state=active]:text-amber-900">Stats</TabsTrigger>
                  <TabsTrigger value="achievements" className="data-[state=active]:bg-amber-500 data-[state=active]:text-amber-900">Achievements</TabsTrigger>
                  <TabsTrigger value="activity" className="data-[state=active]:bg-amber-500 data-[state=active]:text-amber-900">Activity</TabsTrigger>
                </TabsList>
                
                <TabsContent value="overview" className="mt-6">
                  <Card className="bg-white/10 backdrop-blur-lg border-white/20 shadow-xl">
                    <CardContent className="p-8">
                      <h3 className="text-2xl font-bold text-white mb-6">Profile Overview</h3>
                      <div className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-4">
                            <div>
                              <label className="text-emerald-200 text-sm">Name</label>
                              <p className="text-white text-lg font-semibold">{profileData.name}</p>
                            </div>
                            <div>
                              <label className="text-emerald-200 text-sm">GEMS Tag</label>
                              <p className="text-white text-lg font-semibold">@{profileData.gemsTag}</p>
                            </div>
                            <div>
                              <label className="text-emerald-200 text-sm">Location</label>
                              <p className="text-white text-lg">{profileData.location}</p>
                            </div>
                          </div>
                          <div className="space-y-4">
                            <div>
                              <label className="text-emerald-200 text-sm">Age</label>
                              <p className="text-white text-lg">{profileData.age} years old</p>
                            </div>
                            <div>
                              <label className="text-emerald-200 text-sm">Member Since</label>
                              <p className="text-white text-lg">January 2024</p>
                            </div>
                            <div>
                              <label className="text-emerald-200 text-sm">Status</label>
                              <p className="text-emerald-300 text-lg flex items-center gap-2">
                                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                                Online
                              </p>
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="text-emerald-200 text-sm">Bio</label>
                          <p className="text-white text-lg mt-2">{profileData.bio}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="stats" className="mt-6">
                  <Card className="bg-white/10 backdrop-blur-lg border-white/20 shadow-xl">
                    <CardContent className="p-8">
                      <h3 className="text-2xl font-bold text-white mb-6">Gaming Statistics</h3>
                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-6 text-center">
                          <div className="text-3xl font-bold text-green-300 mb-2">{profileData.gamesWon}</div>
                          <div className="text-green-200">Games Won</div>
                        </div>
                        <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-6 text-center">
                          <div className="text-3xl font-bold text-red-300 mb-2">23</div>
                          <div className="text-red-200">Games Lost</div>
                        </div>
                        <div className="bg-purple-500/20 border border-purple-500/30 rounded-xl p-6 text-center">
                          <div className="text-3xl font-bold text-purple-300 mb-2">{Math.round((profileData.gamesWon / (profileData.gamesWon + 23)) * 100)}%</div>
                          <div className="text-purple-200">Win Rate</div>
                        </div>
                        <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl p-6 text-center">
                          <div className="text-3xl font-bold text-amber-300 mb-2">{profileData.winStreak}</div>
                          <div className="text-amber-200">Current Streak</div>
                        </div>
                        <div className="bg-blue-500/20 border border-blue-500/30 rounded-xl p-6 text-center">
                          <div className="text-3xl font-bold text-blue-300 mb-2">15</div>
                          <div className="text-blue-200">Best Streak</div>
                        </div>
                        <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-6 text-center">
                          <div className="text-3xl font-bold text-emerald-300 mb-2">{profileData.totalRubiesEarned.toLocaleString()}</div>
                          <div className="text-emerald-200">Total Rubies Earned</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="achievements" className="mt-6">
                  <Card className="bg-white/10 backdrop-blur-lg border-white/20 shadow-xl">
                    <CardContent className="p-8">
                      <h3 className="text-2xl font-bold text-white mb-6">Achievements & Badges</h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 border border-amber-500/30 rounded-xl p-6 text-center">
                          <Trophy className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                          <h4 className="text-lg font-bold text-amber-300">First Win</h4>
                          <p className="text-amber-200 text-sm">Won your first game</p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border border-purple-500/30 rounded-xl p-6 text-center">
                          <Crown className="w-12 h-12 text-purple-400 mx-auto mb-3" />
                          <h4 className="text-lg font-bold text-purple-300">Spades Royalty</h4>
                          <p className="text-purple-200 text-sm">Won 50 games</p>
                        </div>
                        <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 border border-emerald-500/30 rounded-xl p-6 text-center">
                          <Star className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                          <h4 className="text-lg font-bold text-emerald-300">Streak Master</h4>
                          <p className="text-emerald-200 text-sm">10 game win streak</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="activity" className="mt-6">
                  <Card className="bg-white/10 backdrop-blur-lg border-white/20 shadow-xl">
                    <CardContent className="p-8">
                      <h3 className="text-2xl font-bold text-white mb-6">Recent Activity</h3>
                      <div className="space-y-4">
                        <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg border border-white/10">
                          <Trophy className="w-8 h-8 text-amber-400" />
                          <div className="flex-1">
                            <p className="text-white font-semibold">Won a Spades game</p>
                            <p className="text-emerald-200 text-sm">Earned $12.50 • 2 hours ago</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg border border-white/10">
                          <Users className="w-8 h-8 text-purple-400" />
                          <div className="flex-1">
                            <p className="text-white font-semibold">Gained 15 new fans</p>
                            <p className="text-emerald-200 text-sm">After livestream • 1 day ago</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg border border-white/10">
                          <Video className="w-8 h-8 text-blue-400" />
                          <div className="flex-1">
                            <p className="text-white font-semibold">Started livestream</p>
                            <p className="text-emerald-200 text-sm">"Friday Night Spades" • 1 day ago</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
      </div>
      {/* Profile Picture Enlarge Modal */}
      <ProfilePictureModal
        imageUrl={profileImage}
        isOpen={showPictureModal}
        onClose={() => setShowPictureModal(false)}
        userName={user?.name}
      />
    </div>
  );
};



// ==================== WATCH STREAM PAGE ====================
const WatchStreamPage = () => {
  const { streamId } = useParams();
  const { user, setUser } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [streamError, setStreamError] = useState(null);  // 'not_found' | 'fetch_failed' | null
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [previewTimeLeft, setPreviewTimeLeft] = useState(10);  // 10 second preview for unauthenticated users
  // Round 13: blocked users never reach this screen's chat/feed
  const watchCtx = React.useContext(AuthContext) || {};
  const watchHiddenRef = useRef(new Set());
  useEffect(() => { watchHiddenRef.current = watchCtx.hiddenIds || new Set(); }, [watchCtx.hiddenIds]);
  
  // New livestreaming features
  const [viewerId, setViewerId] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [botViewerCount, setBotViewerCount] = useState(0);
  const displayViewerCount = (viewerCount || 0) + (botViewerCount || 0);
  const [chatMessages, setChatMessages] = useState([]);
  const [newChatMessage, setNewChatMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  // Bug 8 (May 7 testing): true when broadcaster's WS room emits stream_ended.
  // Drives the "Stream ended" UI + auto-redirect to /discover after 5s.
  const [streamEnded, setStreamEnded] = useState(false);
  // Round 6: broadcaster backgrounded their app — away scrim until they return.
  const [broadcasterAway, setBroadcasterAway] = useState(false);
  
  // Gift panel state
  const [showGiftPanel, setShowGiftPanel] = useState(false);
  const [selectedGift, setSelectedGift] = useState(null);
  const [showInsufficientFundsModal, setShowInsufficientFundsModal] = useState(false);
  
  // Spectator Boost state
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [boostEnabled, setBoostEnabled] = useState(true);
  const [totalBoosted, setTotalBoosted] = useState(0);
  const [boostingAmount, setBoostingAmount] = useState(null);
  
  // NEW: Stream stats and viewers
  const [rubiesEarned, setRubiesEarned] = useState(0);
  const [showHostPopup, setShowHostPopup] = useState(false);
  const hostPopupContainerRef = useRef(null);
  const [topViewers, setTopViewers] = useState([]);
  const [activeGiftAnimation, setActiveGiftAnimation] = useState(null);
  
  // Share modal
  const [showShareStreamModal, setShowShareStreamModal] = useState(false);
  // ===== Viewer full-screen (mobile + desktop) =====
  const viewerVideoRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [iosFsActive, setIosFsActive] = useState(false);
  useEffect(() => {
    const onFsChange = () => {
      const fs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(fs || iosFsActive);
      if (!fs) { try { window.screen?.orientation?.unlock?.(); } catch (e) {} }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, [iosFsActive]);
  useEffect(() => {
    return () => { document.body.style.overflow = ''; };
  }, []);
  const toggleViewerFullscreen = async () => {
    const el = viewerVideoRef.current;
    if (!el) return;
    const doc = document;
    const nativeFs = doc.fullscreenElement || doc.webkitFullscreenElement;
    if (nativeFs || iosFsActive) {
      if (nativeFs) {
        try { await (doc.exitFullscreen ? doc.exitFullscreen() : doc.webkitExitFullscreen()); } catch (e) {}
      }
      try { window.screen?.orientation?.unlock?.(); } catch (e) {}
      setIosFsActive(false);
      document.body.style.overflow = '';
      setIsFullscreen(false);
      return;
    }
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (req) {
      try {
        await req.call(el);
        setIsFullscreen(true);
        try { await window.screen?.orientation?.lock?.('landscape'); } catch (e) {}
        return;
      } catch (e) { /* iOS Safari rejects div fullscreen -> CSS fallback */ }
    }
    setIosFsActive(true);
    document.body.style.overflow = 'hidden';
    setIsFullscreen(true);
  };

  // ========================================================
  // HOST POPUP — explicit open/close + click-outside + Escape
  // Stable handlers (refs/useCallback) so the polling loop can't
  // close the popup by causing parent re-renders.
  // ========================================================
  const openHostPopup = React.useCallback(() => setShowHostPopup(true), []);
  const closeHostPopup = React.useCallback(() => setShowHostPopup(false), []);
  const toggleHostPopup = React.useCallback((e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    setShowHostPopup((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!showHostPopup) return undefined;

    const handleOutside = (event) => {
      const node = hostPopupContainerRef.current;
      if (node && !node.contains(event.target)) {
        setShowHostPopup(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setShowHostPopup(false);
    };

    // Use mousedown so the popup closes before any internal click handlers fire elsewhere
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showHostPopup]);
  
  // ========================================================
  // BROWSER NAVIGATION PROTECTION FOR VIEWERS
  // Prevent accidental page leave while watching a live stream
  // ========================================================
  useEffect(() => {
    const isWatching = stream && stream.status === 'live' && !loading;
    
    if (!isWatching) return;
    
    // Prevent browser back/forward
    const handlePopState = (e) => {
      // Push a new state to prevent navigation
      window.history.pushState(null, '', window.location.href);
      
      // Show confirmation
      const confirmLeave = window.confirm(
        'Are you sure you want to leave this live stream?'
      );
      
      if (confirmLeave) {
        // User confirmed - allow navigation
        window.history.back();
      }
    };
    
    // Push initial state
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [stream, loading]);

  // Gift options with different emerald prices and animations
  const viewerGifts = [
    { id: 1, name: "👍 Like", price: 1, icon: "👍", animation: "bounce" },
    { id: 2, name: "❤️ Heart", price: 2, icon: "❤️", animation: "pulse" },
    { id: 3, name: "🔥 Fire", price: 5, icon: "🔥", animation: "fire" },
    { id: 4, name: "⭐ Star", price: 10, icon: "⭐", animation: "spin" },
    { id: 5, name: "Gem", price: 20, icon: "gem", isCustomIcon: true, animation: "sparkle" },
    { id: 6, name: "🎉 Party", price: 25, icon: "🎉", animation: "confetti" },
    { id: 7, name: "🏆 Trophy", price: 50, icon: "🏆", animation: "shine" },
    { id: 8, name: "👑 Crown", price: 100, icon: "👑", animation: "crown" },
    { id: 9, name: "🚀 Rocket", price: 150, icon: "🚀", animation: "rocket" },
    { id: 10, name: "💰 Money Bag", price: 200, icon: "💰", animation: "rain" }
  ];
  
  // Agora streaming
  const [agoraToken, setAgoraToken] = useState(null);
  const [agoraChannel, setAgoraChannel] = useState(null);
  const [agoraAppId, setAgoraAppId] = useState(null);
  const [agoraLoading, setAgoraLoading] = useState(true);
  
  // LiveKit for Solo Streams (viewers)
  const [livekitToken, setLivekitToken] = useState(null);
  const [livekitRoomName, setLivekitRoomName] = useState(null);
  const [livekitUrl, setLivekitUrl] = useState(null); // Dynamic LiveKit URL from token response
  const [useLiveKit, setUseLiveKit] = useState(false);
  // Link v2: viewer token for a LiveKit linked (multi-broadcaster) room.
  const [linkViewToken, setLinkViewToken] = useState(null);
  const [linkViewUrl, setLinkViewUrl] = useState(null); // Dynamic LiveKit URL for the linked viewer room
  // Ref mirror for idempotence guard (skip token refetch if we're already
  // in the same linked viewer room). Prevents remount → disconnect loop.
  const linkViewRoomNameRef = useRef(null);

  // May 12 Viewer Fix (Behaviors B/C): brief WS-driven placeholder shown
  // while the viewer is mid-transition between LiveKit (solo) and Agora
  // (linked/multiplayer). Values:
  //   null         — no transition in flight
  //   'to_multi'   — switching solo → linked ("Switching to multi-stream view…")
  //   'to_solo'    — switching linked → solo ("Returning to solo stream…")
  const [streamTransitioning, setStreamTransitioning] = useState(null);
  const viewerModeInitializedRef = useRef(false);
  const watcherWsRef = useRef(null);
  
  // Trigger gift animation
  const showGiftAnimation = (gift, senderName) => {
    setActiveGiftAnimation({ gift, senderName, id: Date.now() });
    setTimeout(() => setActiveGiftAnimation(null), 2500);
  };

  // Handle sending gift
  const handleSendGift = async (gift) => {
    if (!user) {
      toast.error('Please sign in to send gifts');
      setShowAuthModal(true);
      return;
    }
    
    // Check if viewer is the streamer (cannot gift yourself)
    if (user.id === stream?.user_id) {
      toast.error('You cannot send gifts to yourself');
      return;
    }
    
    // Check if user has enough emeralds
    if ((user?.emeralds || 0) < gift.price) {
      setShowInsufficientFundsModal(true);
      setSelectedGift(gift);
      return;
    }
    
    try {
      const response = await api.post('/gifts/send', {
        recipient_stream_id: streamId,
        gift_type: gift.name,
        amount_emeralds: gift.price
      });
      
      // Show gift animation
      showGiftAnimation(gift, user.name || 'Anonymous');
      
      // Update rubies earned counter from backend response
      if (response.data?.stream_rubies_total !== undefined) {
        setRubiesEarned(response.data.stream_rubies_total || 0);
      } else {
        // Fallback: Update locally (1:1 with emerald cost)
        setRubiesEarned(prev => prev + gift.price);
      }
      
      toast.success(`${gift.isCustomIcon ? '🎁' : gift.icon} sent to ${stream?.user_name}!`);
      setShowGiftPanel(false);
      setSelectedGift(null);
      
      // Update user's emerald count from response or refetch
      if (response.data?.new_balance !== undefined) {
        setUser(prev => ({ ...prev, emeralds: response.data.new_balance }));
      }
    } catch (error) {
      console.error('Error sending gift:', error);
      // Handle Pydantic validation errors (which return objects)
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to send gift';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Pydantic validation errors return an array of error objects
        errorMessage = errorDetail.map(e => e.msg || e.message).join(', ') || errorMessage;
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      toast.error(errorMessage);
    }
  };
  
  // Exit the live stream and return to Discover.
  const handleExitLive = async () => {
    try {
      if (viewerId) await api.post(`/streams/${streamId}/leave`, { viewer_id: viewerId });
    } catch {}
    navigate('/discover');
  };
  
  // Handle spectator boost
  const handleSpectatorBoost = async (amount) => {
    if (!user) {
      toast.error('Please sign in to boost the prize');
      return;
    }
    
    if ((user?.emeralds || 0) < amount) {
      toast.error(`Insufficient emeralds. You need ${amount} emeralds to boost.`);
      return;
    }
    
    setBoostingAmount(amount);
    
    try {
      const response = await api.post(`/game/${stream?.game_id}/spectator-boost`, {
        amount: amount
      });
      
      toast.success(`Thanks for boosting! Your ${amount} Emeralds have been added as Rubies.`);
      setShowBoostModal(false);
      setBoostingAmount(null);
      setTotalBoosted(prev => prev + amount);
      
      // Update user's emerald balance
      setUser(prev => ({ ...prev, emeralds: response.data.new_emerald_balance }));
    } catch (error) {
      console.error('Boost error:', error);
      toast.error(error.response?.data?.detail || 'Failed to boost prize');
      setBoostingAmount(null);
    }
  };
  
  // Fetch boost status for spades games
  useEffect(() => {
    const fetchBoostStatus = async () => {
      if (!stream?.game_id) return;
      
      try {
        const response = await api.get(`/game/${stream.game_id}/boost-status`);
        setBoostEnabled(response.data.boost_enabled);
        setTotalBoosted(response.data.total_boosted || 0);
      } catch (error) {
        console.log('Could not fetch boost status:', error);
      }
    };
    
    fetchBoostStatus();
    const interval = setInterval(fetchBoostStatus, 10000); // Poll every 10 seconds
    return () => clearInterval(interval);
  }, [stream?.game_id]);

  // Fetch stream details and Agora token
  useEffect(() => {
    const fetchStream = async () => {
      await ensureFeatureFlags(); // Link v2: resolve the runtime flag before the transport decision
      try {
        console.log('📺 Fetching stream:', streamId);
        const response = await api.get(`/streams/${streamId}`);
        console.log('✅ Stream data received:', {
          stream_id: response.data.stream_id || response.data.id,
          is_active: response.data.is_active,
          user_name: response.data.user_name,
          viewer_count: response.data.viewer_count
        });
        
        setStream(response.data);
        setViewerCount(response.data.viewer_count || 0);
        setBotViewerCount(response.data.bot_viewer_count || 0);
        setRubiesEarned(response.data.rubies_earned || 0);
        
        // Fetch top viewers
        try {
          const viewersResponse = await api.get(`/streams/${streamId}/viewers`);
          setTopViewers(viewersResponse.data.viewers || []);
        } catch (viewerError) {
          console.log('Could not fetch viewers:', viewerError);
          // Generate some placeholder viewers for better UX
          setTopViewers([]);
        }
        
        // Check if stream is active before fetching Agora token
        if (!response.data.is_active) {
          console.warn('⚠️ Stream is not active!');
          setAgoraLoading(false);
          return;
        }
        
        // ========================================================
        // AGORA VIEWER ACCESS - MULTIPLAYER AND LINKED STREAMS
        // ========================================================
        // Viewers get Agora tokens for:
        //   1. Streams with active multiplayer games
        //   2. LINKED STREAMS (two broadcasters streaming together)
        // Solo streams do NOT use Agora - they will use LiveKit instead.
        // ========================================================
        
        // May 11 audit: unify fetch + render predicate (Bug candidate 1).
        const isMultiplayerStream = isLinkedOrMultiplayerStream(response.data);
        const hasActiveGame = !!(response.data.game_id || response.data.active_game);
        const isLinkedStream = !!(response.data.is_linked || response.data.linked_to_stream || response.data.linked_session_id);
        const linkedCount = response.data.linked_count || (response.data.linked_partners?.length || 0) + 1;
        
        console.log('🎬 Stream type check:', {
          streamId,
          hasActiveGame,
          isLinkedStream,
          isMultiplayerStream,
          linkedCount,
          streamType: response.data.stream_type,
          linkedSessionId: response.data.linked_session_id,
          linkedPartners: response.data.linked_partners
        });
        
        // ========================================================
        // VIEWER TOKEN POLICY
        // ========================================================
        // For MULTIPLAYER/LINKED streams: viewers get Agora audience tokens
        //   to see the split-screen view of all broadcasters (2, 3, or 4)
        // For SOLO streams: viewers use LiveKit (more efficient)
        // ========================================================
        
        if (isMultiplayerStream) {
          // MULTIPLAYER/LINKED: Use Agora so viewers see all broadcasters
          console.log('📺 Multiplayer/Linked stream - Using Agora for full experience, linked count:', linkedCount);

          if (LIVEKIT_LINKS) {
            // Link v2: watch the shared LiveKit linked room (viewer-only).
            try {
              const roomName = livekitLinkRoomName(response.data);
              // Idempotence guard — skip if we already have a viewer token
              // for this exact room. Prevents duplicate fetches remounting
              // LiveKitMultiViewer and dropping the first connection.
              if (linkViewRoomNameRef.current === roomName && linkViewToken) {
                console.log('[LiveKitLink] viewer init skipped — already in room:', roomName);
              } else {
                const lk = await api.post('/livekit/token', {
                  roomName,
                  isPublisher: false,
                  sessionType: 'multiplayer_viewer',
                });
                linkViewRoomNameRef.current = lk.data.roomName || roomName;
                setLinkViewToken(lk.data.token);
                setLinkViewUrl(lk.data.url || null);
                setLivekitRoomName(lk.data.roomName || roomName);
                setUseLiveKit(false);
                setAgoraToken(null);
                setAgoraChannel(null);
                setAgoraAppId(null);
              }
            } catch (lkErr) {
              console.error('❌ [LiveKitLink] viewer token failed:', lkErr);
              setStreamError('fetch_failed');
              toast.error('Could not connect to the stream. Please refresh to try again.');
            }
            setAgoraLoading(false);
            return;
          }

          try {
            // ============================================================
            // VIEWER CHANNEL FIX (May 16, 2026)
            // ============================================================
            // Broadcasters publish on stream_${streamId} / linked_session_id
            // at ALL times — solo, multi, AND during in-stream games. Pre-fix
            // we incorrectly switched viewers to `game_${gameId}` when a game
            // started, but no broadcaster ever publishes there → black screen
            // bug confirmed in production (May-16 screenshots).
            //
            // Correct priority:
            //   linked_session_id → use it (multi-party)
            //   linked_to_stream  → stream_${linked_to_stream} (legacy 2-party)
            //   else              → stream_${streamId} (solo or pre-link inviter)
            // No game_${gameId} branch. Same channel during games.
            // ============================================================
            let channelName;
            if (response.data.linked_session_id) {
              channelName = response.data.linked_session_id;
            } else if (response.data.linked_to_stream) {
              channelName = `stream_${response.data.linked_to_stream}`;
            } else {
              channelName = `stream_${streamId}`;
            }

            console.log('📺 Viewer joining Agora channel:', channelName, 'for', linkedCount, 'broadcasters', {
              hasLinkedSession: !!response.data.linked_session_id,
              hasLinkedTo: !!response.data.linked_to_stream,
              hasGame: !!response.data.game_id,
              activePhase: response.data.game_id ? 'in-game' : 'pre-game',
            });
            
            const agoraResponse = await api.post('/agora/token', {
              channelName: channelName,
              uid: resolveViewerAgoraUid(user),  // Checkpoint 3: never 0 — anon viewers get a stable visitor-id hash
              role: 'publisher',  // Use publisher-role token (rtc mode requires this even for view-only viewers)
              sessionType: 'multiplayer_viewer'
            });
            
            console.log('✅ Agora viewer token received:', {
              channelName: agoraResponse.data.channelName,
              hasToken: !!agoraResponse.data.token
            });
            
            setAgoraToken(agoraResponse.data.token);
            setAgoraChannel(agoraResponse.data.channelName);
            setAgoraAppId(agoraResponse.data.appId);
            setUseLiveKit(false);
          } catch (agoraError) {
            console.error('❌ Failed to get Agora viewer token:', agoraError);
            // Fallback to LiveKit if Agora fails
            console.log('🔄 Falling back to LiveKit for viewing');
            try {
              const livekitResponse = await api.post('/livekit/token', {
                roomName: `solo_stream_${streamId}`,
                isPublisher: false,
                sessionType: 'multiplayer_viewer'  // Checkpoint 3: only this sessionType admits anon viewers
              });
              setLivekitToken(livekitResponse.data.token);
              setLivekitRoomName(livekitResponse.data.roomName);
              setLivekitUrl(livekitResponse.data.url || null);
              setUseLiveKit(true);
            } catch (livekitError) {
              console.error('❌ LiveKit fallback also failed:', livekitError);
            }
          }
        } else {
          // SOLO: Use LiveKit (more efficient for single broadcaster)
          console.log('📺 Solo stream - Using LiveKit');
          
          // Clear Agora state for solo streams
          setAgoraToken(null);
          setAgoraChannel(null);
          setAgoraAppId(null);
          
          try {
            const livekitResponse = await api.post('/livekit/token', {
              roomName: `solo_stream_${streamId}`,
              isPublisher: false,
              sessionType: 'multiplayer_viewer'  // Checkpoint 3: only this sessionType admits anon viewers (root cause of "share link only works once" — anon viewers were 401'd here)
            });
            
            console.log('✅ LiveKit viewer token received:', {
              roomName: livekitResponse.data.roomName,
              url: livekitResponse.data.url
            });
            
            setLivekitToken(livekitResponse.data.token);
            setLivekitRoomName(livekitResponse.data.roomName);
            setLivekitUrl(livekitResponse.data.url || null);
            setUseLiveKit(true);
          } catch (livekitError) {
            // Checkpoint 3: surface a clear error UI instead of indefinite
            // white screen. The previous catch only logged, so anon viewers
            // hitting a 401 (pre-fix) sat on "Connecting…" forever.
            console.error('❌ Failed to get LiveKit token:', livekitError);
            setStreamError('fetch_failed');
            toast.error('Could not connect to the stream. Please refresh to try again.');
          }
        }
        
        setAgoraLoading(false);
      } catch (error) {
        console.error('Error fetching stream:', error);
        console.error('Error details:', error.response?.data);
        const status = error?.response?.status;
        if (status === 404) {
          setStreamError('not_found');
        } else {
          setStreamError('fetch_failed');
        }
      } finally {
        setLoading(false);
        setAgoraLoading(false);
      }
    };

    if (streamId) {
      fetchStream();
    }
  }, [streamId]);

  // While viewing, listen for linked-session updates so the viewer's tokens stay in sync
  // with broadcasters joining/leaving the multi-party group.
  useEffect(() => {
    if (!streamId) return undefined;

    const handleLinkUpdate = async (event) => {
      const { linked_session_id: lsid, all_linked_users } = event.detail || {};
      if (!stream) return;
      const isAffected = (
        lsid === stream.linked_session_id ||
        (Array.isArray(all_linked_users) && all_linked_users.includes(stream.user_id))
      );
      if (isAffected) {
        console.log('📺 Linked session changed — refreshing stream metadata');
        try {
          const fresh = await api.get(`/streams/${streamId}`);
          // Token swap is handled by the type-change effect (Bug Candidate 3),
          // which clears ONLY the opposite mode's token. Do NOT clear tokens
          // here — that re-introduces the white-screen flash.
          setStream(fresh.data);
        } catch (e) {
          console.warn('Failed to refresh stream metadata:', e?.message);
        }
      }
    };

    window.addEventListener('linked-session-updated', handleLinkUpdate);
    window.addEventListener('broadcaster-joined-session', handleLinkUpdate);
    window.addEventListener('broadcaster-left-session', handleLinkUpdate);

    return () => {
      window.removeEventListener('linked-session-updated', handleLinkUpdate);
      window.removeEventListener('broadcaster-joined-session', handleLinkUpdate);
      window.removeEventListener('broadcaster-left-session', handleLinkUpdate);
    };
  }, [streamId, stream?.linked_session_id, stream?.user_id]);

  // When the stream's type changes (e.g. all co-hosts left → host now solo,
  // or a partner just linked-in → host now multi-party), proactively swap
  // the viewer's token to the right mode. We:
  //   1. Clear ONLY the OPPOSITE mode's tokens (clearing the active mode's
  //      tokens too — as the pre-May-12 code did — caused a black/white
  //      flash because the player unmounted before the new player had
  //      tokens).
  //   2. Set `streamTransitioning` so the UI shows a brief placeholder
  //      (Behaviors B/C) instead of falling through to the generic
  //      "Connecting to video stream…" state.
  //   3. Fetch the right token inline (no 5s polling dependency).
  useEffect(() => {
    if (!stream) return;

    // Initial mount is NOT a transition — fetchStream gets the first token.
    if (!viewerModeInitializedRef.current) {
      viewerModeInitializedRef.current = true;
      return;
    }

    const newIsMultiplayer = isLinkedOrMultiplayerStream(stream);
    const currentMode = useLiveKit ? 'solo' : 'multiplayer';
    const targetMode = newIsMultiplayer ? 'multiplayer' : 'solo';

    if (currentMode === targetMode) return;

    let cancelled = false;
    console.log(`🔄 [ViewerFix] Stream type changed: ${currentMode} → ${targetMode}. Swapping token (clearing opposite mode only).`);

    if (targetMode === 'multiplayer') {
      // Going to linked/multi-party — show "Switching to multi-stream view…",
      // tear down LiveKit ONLY, request an Agora token for the right channel.
      setStreamTransitioning('to_multi');
      setLivekitToken(null);
      setLivekitRoomName(null);
      setLivekitUrl(null);
      setUseLiveKit(false);

      // Link v2: fetch a viewer token for the shared LiveKit linked room and
      // skip the Agora path entirely.
      if (LIVEKIT_LINKS) {
        const roomName = livekitLinkRoomName(stream);
        // Idempotence guard — same linked viewer room, no refetch/remount.
        if (linkViewRoomNameRef.current === roomName && linkViewToken) {
          setStreamTransitioning(null);
          return () => { cancelled = true; };
        }
        (async () => {
          try {
            const lk = await api.post('/livekit/token', {
              roomName,
              isPublisher: false,
              sessionType: 'multiplayer_viewer',
            });
            if (cancelled) return;
            linkViewRoomNameRef.current = lk.data.roomName || roomName;
            setLinkViewToken(lk.data.token);
            setLinkViewUrl(lk.data.url || null);
            setLivekitRoomName(lk.data.roomName || roomName);
          } catch (e) {
            console.error('[LiveKitLink] viewer transition token failed:', e);
          } finally {
            if (!cancelled) setStreamTransitioning(null);
          }
        })();
        return () => { cancelled = true; };
      }

      // Bug Candidate 4: derive channel from stream metadata, NOT from
      // raw streamId, so co-hosts on a linked session join the inviter's
      // channel (linked_session_id / linked_to_stream) instead of their
      // own per-stream channel.
      let channelName;
      if (stream.linked_session_id) {
        channelName = stream.linked_session_id;
      } else if (stream.linked_to_stream) {
        channelName = `stream_${stream.linked_to_stream}`;
      } else {
        // No game_${gameId} branch — broadcasters publish on stream_${id}
        // during games too. Viewer channel is unchanged at game start.
        channelName = `stream_${streamId}`;
      }

      (async () => {
        try {
          const agoraResponse = await api.post('/agora/token', {
            channelName,
            uid: resolveViewerAgoraUid(user),  // Checkpoint 3: never 0 — anon viewers get a stable visitor-id hash
            role: 'publisher',
            sessionType: 'multiplayer_viewer'
          });
          if (cancelled) return;
          setAgoraToken(agoraResponse.data.token);
          setAgoraChannel(agoraResponse.data.channelName);
          setAgoraAppId(agoraResponse.data.appId);
        } catch (e) {
          console.error('[ViewerFix] Agora token refetch on transition failed:', e);
        } finally {
          if (!cancelled) setStreamTransitioning(null);
        }
      })();
    } else {
      // Going back to solo — show "Returning to solo stream…", tear down
      // Agora ONLY, request a LiveKit token. Bug Candidate 4: the LiveKit
      // room name is keyed on THIS viewer's URL streamId (the broadcaster
      // the viewer was originally on), never on the inviter's stream.
      setStreamTransitioning('to_solo');
      setAgoraToken(null);
      setAgoraChannel(null);
      setAgoraAppId(null);
      setLinkViewToken(null); // Link v2: leave the LiveKit linked room
      setLinkViewUrl(null);
      linkViewRoomNameRef.current = null;

      (async () => {
        try {
          const lkRes = await api.post('/livekit/token', {
            roomName: `solo_stream_${streamId}`,
            isPublisher: false,
            sessionType: 'multiplayer_viewer'  // Checkpoint 3: only this sessionType admits anon viewers
          });
          if (cancelled) return;
          setLivekitToken(lkRes.data.token);
          setLivekitRoomName(lkRes.data.roomName);
          setLivekitUrl(lkRes.data.url || null);
          setUseLiveKit(true);
        } catch (e) {
          console.error('[ViewerFix] LiveKit token refetch on transition failed:', e);
        } finally {
          if (!cancelled) setStreamTransitioning(null);
        }
      })();
    }

    return () => { cancelled = true; };
  }, [stream?.stream_type, stream?.is_linked, stream?.linked_session_id, stream?.linked_to_stream, stream?.game_id, useLiveKit, streamId, user?.id]);

  // Never leave the transition placeholder up once the target token landed.
  useEffect(() => {
    if (!streamTransitioning) return;
    const soloReady = useLiveKit && livekitToken && livekitRoomName;
    const multiReady = LIVEKIT_LINKS ? !!linkViewToken : !!(agoraToken && agoraChannel && agoraAppId);
    if ((streamTransitioning === 'to_solo' && soloReady) ||
        (streamTransitioning === 'to_multi' && multiReady)) {
      setStreamTransitioning(null);
    }
  }, [streamTransitioning, useLiveKit, livekitToken, livekitRoomName, agoraToken, agoraChannel, agoraAppId, linkViewToken]);

  // May 12 Viewer Fix (Bug Candidate 2): the previous 5s polling that
  // detected solo ↔ linked transitions via REST is removed. Transitions
  // are now driven by:
  //   1. The per-stream WebSocket below (`stream_state_changed`), which
  //      every viewer (auth + anon) is subscribed to.
  //   2. The window-event listener above (`linked-session-updated` etc.)
  //      for authenticated viewers who already have the global sync WS.
  // We keep a much slower (30s) defensive heartbeat to catch stream-end /
  // viewer-count drift if the WS ever silently drops.
  useEffect(() => {
    if (!streamId || !stream?.is_active) return;

    const checkStreamState = async () => {
      try {
        const response = await api.get(`/streams/${streamId}`);
        if (!response.data.is_active) {
          setStream(prev => prev ? { ...prev, is_active: false } : prev);
          return;
        }
        if (response.data.viewer_count !== undefined) setViewerCount(response.data.viewer_count);
        if (response.data.bot_viewer_count !== undefined) setBotViewerCount(response.data.bot_viewer_count || 0);
        if (response.data.rubies_earned !== undefined) setRubiesEarned(response.data.rubies_earned || 0);
      } catch (error) {
        console.log('Stream state check error:', error);
      }
    };

    const stateInterval = setInterval(checkStreamState, 30000);
    return () => clearInterval(stateInterval);
  }, [streamId, stream?.is_active]);

  // NAVIGATION PROTECTION: Prevent accidental back button from leaving stream
  useEffect(() => {
    if (!stream?.is_active) return;
    
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Are you sure you want to leave this live stream?';
      return e.returnValue;
    };
    
    const handlePopState = (e) => {
      // Push state back to prevent navigation
      window.history.pushState(null, '', window.location.href);
      
      // Show confirmation dialog
      const confirmLeave = window.confirm('Are you sure you want to leave this live stream?');
      if (confirmLeave) {
        // Actually navigate away
        window.history.go(-2);
      }
    };
    
    // Push initial state to enable popstate blocking
    window.history.pushState(null, '', window.location.href);
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [stream?.is_active]);

  // Join stream as viewer
  useEffect(() => {
    const joinStream = async () => {
      if (!streamId || !stream?.is_active) return;
      
      try {
        // Checkpoint 3: route through the same helper that resolves anonymous
        // Agora UIDs so the visitor_id sent to /streams/join is identical to
        // the one fed into uidFromUserId on /agora/token. No more
        // anon-viewer-collides-with-uid-0 mapping drift.
        const visitorId = getOrCreateVisitorId();

        const response = await api.post(`/streams/${streamId}/join`, { visitor_id: visitorId });
        setViewerId(response.data.viewer_id);
        setViewerCount(response.data.viewer_count);
      } catch (error) {
        console.error('Error joining stream:', error);
      }
    };

    if (stream) {
      joinStream();
    }
  }, [streamId, stream]);

  // Send viewer heartbeat every 10 seconds (keeps viewer count and rubies real-time)
  useEffect(() => {
    if (!viewerId || !streamId) return;

    const heartbeatInterval = setInterval(async () => {
      try {
        const response = await api.post(`/streams/${streamId}/heartbeat-viewer`, {
          viewer_id: viewerId
        });
        setViewerCount(response.data.viewer_count);
        if (response.data.bot_viewer_count !== undefined) setBotViewerCount(response.data.bot_viewer_count || 0);
        
        // Update rubies earned if returned
        if (response.data.rubies_earned !== undefined) {
          setRubiesEarned(response.data.rubies_earned || 0);
        }
        
        // Update top viewers if returned
        if (response.data.top_viewers) {
          setTopViewers(response.data.top_viewers || []);
        }
      } catch (error) {
        console.error('Error sending heartbeat:', error);
      }
    }, 10000); // 10 seconds for real-time feel

    return () => clearInterval(heartbeatInterval);
  }, [viewerId, streamId]);

  // Leave stream on unmount
  useEffect(() => {
    return () => {
      if (viewerId && streamId) {
        api.post(`/streams/${streamId}/leave`, { viewer_id: viewerId })
          .catch(err => console.error('Error leaving stream:', err));
      }
    };
  }, [viewerId, streamId]);

  // Load chat messages and process gift events for ALL viewers
  useEffect(() => {
    const processedGiftIds = new Set();
    
    const loadChat = async () => {
      if (!streamId) return;
      
      try {
        const response = await api.get(`/streams/${streamId}/chat?limit=50`);
        const messages = (response.data || []).filter((m) => !watchHiddenRef.current.has(m.user_id));
        setChatMessages(messages);
        
        // ==================== GIFT SYNC FOR ALL VIEWERS ====================
        // Process gift messages and show animation for ALL viewers/broadcaster
        // Only show animation for gifts we haven't processed yet
        // ====================================================================
        messages.forEach((msg) => {
          if (msg.message_type === 'gift' && msg.gift_data && !processedGiftIds.has(msg.id)) {
            // Check if this gift was sent in the last 5 seconds (for real-time feel)
            const msgTime = new Date(msg.timestamp).getTime();
            const now = Date.now();
            if (now - msgTime < 5000) {
              processedGiftIds.add(msg.id);
              // Trigger animation for everyone except the sender (they already see it)
              if (msg.user_id !== user?.id) {
                showGiftAnimation({
                  name: msg.gift_data.gift_name,
                  icon: msg.gift_data.gift_icon,
                  animation: msg.gift_data.animation || 'burst',
                  isCustomIcon: msg.gift_data.gift_name === 'Gem'
                }, msg.user_name);
              }
            }
          }
        });
        
        // Clean up old processed IDs (keep only last 100)
        if (processedGiftIds.size > 100) {
          const oldIds = Array.from(processedGiftIds).slice(0, processedGiftIds.size - 50);
          oldIds.forEach(id => processedGiftIds.delete(id));
        }
      } catch (error) {
        console.error('Error loading chat:', error);
      }
    };

    if (!stream?.is_active || !streamId) return;

    // Initial fetch — populate the last 50 messages.
    loadChat();

    // Bug 2 (May 7 testing): subscribe to the per-stream WebSocket so chat
    // from a linked host's stream lands here in real-time. The backend now
    // fans out chat to every linked stream's WS room, so this single
    // subscription is enough. Replaces the 2s REST polling.
    // Bug 8: same socket also delivers `stream_ended` so the viewer can
    // tear down and redirect when the broadcaster ends.
    const backend = API_BASE_URL || '';
    const wsBase = backend.replace('http://', 'ws://').replace('https://', 'wss://').replace(/\/api$/, '');
    const wsUrl = `${wsBase}/api/ws/stream/${streamId}`;
    console.log('[Bug2] Watcher WS connecting:', wsUrl);

    let ws;
    let pingInterval;
    let reconnectTimer;
    let cancelled = false;

    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
        watcherWsRef.current = ws;
      } catch (e) {
        console.error('[Bug2] Watcher WS create failed:', e);
        return;
      }

      ws.onopen = () => {
        console.log('[Bug2] Watcher WS open for stream', streamId);
        // Identify ourselves so the backend can route stream_ended events.
        try {
          ws.send(JSON.stringify({ type: 'join', user_id: user?.id || `anon_${streamId}`, username: user?.name || undefined, is_streamer: false }));
        } catch {}
        pingInterval = setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: 'ping' })); } catch {}
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        let data;
        try { data = JSON.parse(event.data); } catch { return; }

        // Round 13: moderation frames
        if (data.type === 'chat_removed' && data.id) {
          setChatMessages(prev => prev.filter(m => m.id !== data.id));
          return;
        }
        if (data.type === 'chat_rejected') {
          toast.error(data.reason || 'That message was not sent.');
          return;
        }
        // Round 13: never show people you blocked (or who blocked you)
        if ((data.type === 'chat' || data.type === 'gift' || data.type === 'heart') && data.user_id && watchHiddenRef.current.has(data.user_id)) return;
        // Bug 2: incoming chat (including chat fanned-out from a linked host)
        if (data.type === 'chat') {
          const msg = {
            id: data.id,
            user_id: data.user_id,
            user_name: data.user_name || data.username,
            user_handle: data.user_handle,
            user_picture: data.user_picture,
            message: data.message || data.content,
            timestamp: data.timestamp,
            message_type: data.message_type,
            gift_data: data.gift_data,
          };
          setChatMessages(prev => {
            if (prev.find(m => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
          // Gift animation for non-sender
          if (msg.message_type === 'gift' && msg.gift_data && msg.user_id !== user?.id && !processedGiftIds.has(msg.id)) {
            const t = new Date(msg.timestamp).getTime();
            if (Date.now() - t < 5000) {
              processedGiftIds.add(msg.id);
              try {
                showGiftAnimation({
                  name: msg.gift_data.gift_name,
                  icon: msg.gift_data.gift_icon,
                  animation: msg.gift_data.animation || 'burst',
                  isCustomIcon: msg.gift_data.gift_name === 'Gem'
                }, msg.user_name);
              } catch {}
            }
          }
          return;
        }

        // Gift arrived — sync animation to every viewer + broadcaster.
        if (data.type === 'gift') {
          // Dedupe: REST broadcast + WS echo may arrive; play only once.
          window.__gemsSeenGiftEvents = window.__gemsSeenGiftEvents || new Set();
          if (data.gift_event_id) {
            if (window.__gemsSeenGiftEvents.has(data.gift_event_id)) return;
            window.__gemsSeenGiftEvents.add(data.gift_event_id);
            if (window.__gemsSeenGiftEvents.size > 200) window.__gemsSeenGiftEvents.clear();
          }
          if (data.gift_id || data.reveal_gift_id) {
            window.dispatchEvent(new CustomEvent('gems:gift', {
              detail: { giftId: data.reveal_gift_id || data.gift_id, qty: data.quantity || 1, sender: data.sender }
            }));
          }
          return;
        }

        // Bug 8 / Behavior D (May 12): broadcaster ended the stream.
        // Show the "Stream Ended" UI for 5s, then route the viewer to the
        // most-watched OTHER live stream (so we keep them engaged) instead
        // of dumping them on /discover blindly. If no other live stream
        // exists, fall back to home (`/`).
        if (data.type === 'stream_ended') {
          console.log('[Bug8] stream_ended received on watcher WS', data);
          // Round 9 (Sep 10 2026): the server fans stream_ended into EVERY
          // linked room, so a co-host ending THEIR live lands here too. Only
          // act when it's the live we're actually watching — the partner
          // case arrives with stream_state_changed, which re-routes us to
          // the survivor's solo room (viewers stay in the original live).
          if (data.stream_id && String(data.stream_id) !== String(streamId)) {
            console.log('[Round9] stream_ended is for a linked partner — ignoring', data.stream_id);
            return;
          }
          // Round 63: a party member's viewers go straight to the HOST's live
          if (data.next_stream_id && String(data.next_stream_id) !== String(streamId)) {
            toast.info(`${stream?.user_name || 'The broadcaster'} ended their live — taking you to ${data.next_user_name || 'the host'}.`);
            navigate(`/watch/${data.next_stream_id}`);
            return;
          }
          setStreamEnded(true);
          toast.info('The broadcaster has ended this stream.');
          setTimeout(async () => {
            if (cancelled) return;
            try {
              const res = await api.get('/streams/active');
              const streams = Array.isArray(res.data?.streams) ? res.data.streams : [];
              const others = streams
                .filter(s => s && s.id && s.id !== streamId && s.is_live !== false)
                .sort((a, b) => (b.viewer_count || 0) - (a.viewer_count || 0));
              if (others.length > 0) {
                navigate(`/watch/${others[0].id}`);
              } else {
                navigate('/');
              }
            } catch (e) {
              console.warn('[Behavior D] Failed to fetch active streams for smart redirect; falling back to /', e?.message);
              if (!cancelled) navigate('/');
            }
          }, 5000);
          return;
        }

        // May 12 Viewer Fix (Bug Candidate 2): server signals that the
        // viewer must re-fetch stream metadata because the broadcaster
        // just linked/unlinked. The type-change effect handles the
        // actual token swap once `stream` is updated.
        if (data.type === 'broadcaster_away') {
          // Round 9: away messages are tagged with the live they are about
          // (a linked co-host's away must not dim THIS broadcaster).
          if (data.stream_id && String(data.stream_id) !== String(streamId)) return;
          setBroadcasterAway(true);
          return;
        }
        if (data.type === 'broadcaster_back') {
          if (data.stream_id && String(data.stream_id) !== String(streamId)) return;
          setBroadcasterAway(false);
          return;
        }
        if (data.type === 'stream_state_changed') {
          console.log('[ViewerFix] stream_state_changed received — refetching metadata', data);
          (async () => {
            try {
              const fresh = await api.get(`/streams/${streamId}`);
              if (!cancelled) setStream(fresh.data);
            } catch (e) {
              console.warn('[ViewerFix] Failed to refetch stream on stream_state_changed:', e?.message);
            }
          })();
          return;
        }
      };

      ws.onclose = (event) => {
        console.log('[Bug2] Watcher WS closed', event.code);
        if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
        if (!cancelled && !event.wasClean) {
          // Reconnect with backoff
          reconnectTimer = setTimeout(() => { if (!cancelled) connect(); }, 3000);
        }
      };

      ws.onerror = (err) => {
        console.error('[Bug2] Watcher WS error:', err);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (pingInterval) clearInterval(pingInterval);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) {
        try { ws.close(1000, 'unmount'); } catch {}
      }
      watcherWsRef.current = null;
    };
  }, [streamId, stream?.is_active, user?.id, navigate]);

  // 10-second preview timer for non-authenticated users
  useEffect(() => {
    if (!user && stream) {
      const timer = setInterval(() => {
        setPreviewTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setShowAuthModal(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [user, stream]);

  // Send chat message
  // Determine if user is in viewer-only mode (non-beta user)
  const isViewerMode = user && user.beta_access === false;

  const sendChatMessage = async () => {
    if (!newChatMessage.trim() || sendingMessage) return;
    if (isViewerMode) {
      toast.error('Viewer Mode: Sign up for beta access to chat');
      return;
    }
    
    setSendingMessage(true);
    try {
      await api.post(`/streams/${streamId}/chat`, {
        message: newChatMessage.trim(),
        user_name: user?.name || 'Anonymous'
      });
      setNewChatMessage('');
      // Reload chat immediately
      const response = await api.get(`/streams/${streamId}/chat?limit=50`);
      setChatMessages((response.data || []).filter((m) => !watchHiddenRef.current.has(m.user_id)));
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--gems-bg-0)' }}>
        <p style={{ color: 'var(--gems-text)' }}>Loading...</p>
      </div>
    );
  }

  const awayOverlay = broadcasterAway && !streamEnded ? (
    <div className="fixed inset-0 z-40 flex flex-col items-center justify-center pointer-events-none" style={{ background: 'rgba(4,9,7,0.85)' }} data-testid="away-overlay">
      {[0, 1, 2, 3, 4].map(i => (
        <div key={i} style={{ position: 'absolute', top: `${12 + i * 19}%`, left: 0, right: 0, height: 2, background: 'rgba(62,230,164,0.12)' }} />
      ))}
      <div className="text-5xl">⏸️</div>
      <div className="font-extrabold text-lg mt-3" style={{ color: '#F4FBF7' }}>Broadcaster is away</div>
      <div className="text-sm mt-1" style={{ color: 'rgba(244,251,247,0.6)' }}>Hang tight — they'll be right back</div>
    </div>
  ) : null;

  if (streamEnded || !stream || !stream.is_active) {
    // Distinguish "stream not found / API failure" from "stream ended"
    const isLookupFailure = !stream && (streamError === 'not_found' || streamError === 'fetch_failed');
    const headline = isLookupFailure
      ? (streamError === 'not_found' ? 'Stream Unavailable' : 'Couldn\'t Reach Stream')
      : 'This Live Has Ended';
    const subline = isLookupFailure
      ? (streamError === 'not_found'
          ? 'This stream link is no longer valid or the stream was removed.'
          : 'Network or server error. Please try again in a moment.')
      : (stream?.user_name ? `${stream.user_name}'s stream is no longer live` : '');
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--gems-bg-0)' }}>
        <NavBar />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center" data-testid="stream-unavailable">
            {/* Icon */}
            <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--gems-bg-2)' }}>
              <Video className="h-12 w-12" style={{ color: 'var(--gems-text-dim)' }} />
            </div>

            <h1 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: 'var(--gems-text)' }}>
              {headline}
            </h1>

            {subline && (
              <p className="text-base md:text-lg mb-6" style={{ color: 'var(--gems-text-dim)' }}>
                {subline}
              </p>
            )}
            
            {/* Action Buttons */}
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/discover')}
                className="w-full py-4 text-base font-semibold"
                style={{ backgroundColor: 'var(--gems-emerald)', color: '#FFFFFF' }}
              >
                <Video className="h-5 w-5 mr-2" />
                Browse Other Lives
              </Button>
              
              <Button 
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full py-4 text-base"
                style={{ borderColor: 'var(--gems-bg-3)', color: 'var(--gems-text)' }}
              >
                <Home className="h-5 w-5 mr-2" />
                Go to Home
              </Button>
              
              {stream?.user_handle && (
                <Button 
                  onClick={() => navigate(`/profile/${stream.user_handle}`)}
                  variant="outline"
                  className="w-full py-4 text-base"
                  style={{ borderColor: 'var(--gems-bg-3)', color: 'var(--gems-text)' }}
                >
                  <User className="h-5 w-5 mr-2" />
                  View {stream.user_name}'s Profile
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stream.game_id && user && !isViewerMode) {
    navigate(`/game/${stream.game_id}`);
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--gems-bg-0)' }}>
      <NavBar />
      {/* Round 6: broadcaster-away scrim */}
      {awayOverlay}

      {/* Auth Modal - Blocks viewing after 10 seconds */}
      {showAuthModal && !user && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="rounded-2xl p-6 md:p-8 max-w-md w-full text-center border" style={{
            backgroundColor: 'var(--gems-bg-1)',
            borderColor: 'var(--gems-emerald)'
          }}>
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full mx-auto mb-4 md:mb-6 flex items-center justify-center" style={{
              backgroundColor: 'var(--gems-emerald)',
            }}>
              <Video className="h-8 w-8 md:h-10 md:w-10 text-white" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4" style={{ color: 'var(--gems-text)' }}>
              Continue Watching?
            </h2>
            <p className="text-base md:text-lg mb-4 md:mb-6" style={{ color: 'var(--gems-text-dim)' }}>
              Sign up or log in to keep watching {stream?.user_name}&apos;s live stream and join the action!
            </p>
            
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/')}
                className="w-full py-4 md:py-6 text-base md:text-lg font-bold"
                style={{
                  backgroundColor: 'var(--gems-emerald)',
                  color: '#FFFFFF'
                }}
              >
                Sign Up Free
              </Button>
              
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full py-4 md:py-6 text-base md:text-lg"
                style={{
                  borderColor: 'var(--gems-emerald)',
                  color: 'var(--gems-emerald)'
                }}
              >
                Log In
              </Button>

              {/* Round 12: shared-link visitors go straight to the app */}
              <Button
                onClick={() => navigate('/get-app')}
                variant="outline"
                className="w-full py-4 md:py-6 text-base md:text-lg"
                style={{ borderColor: 'var(--gems-text-dim)', color: 'var(--gems-text)' }}
                data-testid="viewer-get-app-btn"
              >
                📱 Get the GEMS app
              </Button>
            </div>
            
            <p className="text-xs md:text-sm mt-4 md:mt-6" style={{ color: 'var(--gems-text-dim)' }}>
              Join thousands of players streaming and competing on GEMS Live
            </p>
          </div>
        </div>
      )}
      
      {/* Main Content - Mobile-first responsive layout */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Video Section */}
        <div className="flex-shrink-0 lg:flex-1 flex flex-col">
          {/* Preview Timer Banner */}
          {!user && previewTimeLeft > 0 && (
            <div className="px-3 py-2 text-center border-b animate-pulse" style={{
              backgroundColor: 'rgba(227, 65, 62, 0.1)',
              borderColor: 'var(--gems-red)'
            }}>
              <p className="text-sm font-semibold" style={{ color: 'var(--gems-red)' }}>
                ⏱️ Preview: {previewTimeLeft}s • Sign up to continue
              </p>
            </div>
          )}
          
          {/* Video Container - Takes significant portion on mobile */}
          <div ref={viewerVideoRef} className="relative w-full" style={ iosFsActive ? {
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100vh',
            maxHeight: '100vh',
            minHeight: '100vh',
            zIndex: 9999,
            backgroundColor: '#000'
          } : {
            height: 'calc(100vw * 9 / 16)',
            maxHeight: '60vh',
            minHeight: '200px',
            backgroundColor: '#000'
          }}>
            {/* Blurred overlay when auth modal is showing */}
            {showAuthModal && (
              <div className="absolute inset-0 backdrop-blur-xl bg-black/50 z-10"></div>
            )}
            
            {/* Gift Animation Overlay - Premium animations */}
            {activeGiftAnimation && (
              <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
                <div className={`gift-animation gift-${activeGiftAnimation.gift.animation || 'burst'}`}>
                  <div className="text-center animate-gift-entrance">
                    {/* Particle effects background */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <div className="w-64 h-64 md:w-96 md:h-96 rounded-full bg-gradient-radial from-yellow-400/30 via-pink-500/20 to-transparent animate-ping"></div>
                      </div>
                    </div>
                    
                    {/* Gift icon with glow */}
                    <div className="relative">
                      <div className="text-6xl md:text-8xl mb-2 animate-bounce drop-shadow-[0_0_30px_rgba(255,215,0,0.8)]">
                        {activeGiftAnimation.gift.isCustomIcon ? (
                          <EmeraldIcon className="h-16 w-16 md:h-24 md:w-24 mx-auto filter drop-shadow-[0_0_20px_rgba(39,208,125,0.8)]" />
                        ) : (
                          <span className="filter drop-shadow-[0_0_20px_rgba(255,215,0,0.8)]">
                            {activeGiftAnimation.gift.icon}
                          </span>
                        )}
                      </div>
                      {/* Sparkle effects */}
                      <div className="absolute -top-4 -left-4 text-2xl animate-ping">✨</div>
                      <div className="absolute -top-2 -right-4 text-xl animate-ping delay-100">⭐</div>
                      <div className="absolute -bottom-2 -left-2 text-lg animate-ping delay-200">💫</div>
                    </div>
                    
                    {/* Sender info card */}
                    <div className="bg-black/80 backdrop-blur-md rounded-2xl px-6 py-3 border border-yellow-400/30 shadow-[0_0_30px_rgba(255,215,0,0.3)]">
                      <p className="text-white font-bold text-base md:text-lg">
                        <span className="text-yellow-400">{activeGiftAnimation.senderName}</span> sent
                      </p>
                      <p className="text-yellow-300 font-extrabold text-xl md:text-2xl">
                        {(() => { const n = activeGiftAnimation.gift?.name || 'Gift'; return n.includes(' ') ? n.split(' ')[1] : n; })()}!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* ==================== MOBILE-OPTIMIZED TOP BAR ==================== */}
            {/* Horizontal layout with proper spacing - no overlap */}
            {stream?.game_id && !streamEnded && (
              <SpectatorGameOverlay gameId={stream.game_id} viewerId={getOrCreateVisitorId()} />
            )}
            <div className="absolute top-2 md:top-4 left-2 md:left-4 right-2 md:right-4 flex items-center justify-between z-20">
              {/* Left Side: LIVE badge */}
              <div className="flex items-center gap-1.5 md:gap-2">
                <div className="px-2 md:px-3 py-0.5 md:py-1 rounded-full flex items-center gap-1.5" style={{ 
                  backgroundColor: 'rgba(227, 65, 62, 0.95)',
                  color: '#FFFFFF'
                }}>
                  <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-white rounded-full animate-pulse"></div>
                  <span className="text-xs md:text-sm font-bold">{stream?.party ? 'PARTY' : 'LIVE'}</span>
                </div>
                <button
                  onClick={handleExitLive}
                  className="px-2.5 md:px-3 py-0.5 md:py-1 rounded-full flex items-center gap-1 text-xs md:text-sm font-bold"
                  style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', color: '#FFFFFF' }}
                  data-testid="exit-live-btn"
                >
                  <X className="h-3 w-3 md:h-4 md:w-4" />
                  Exit
                </button>
              </div>
              
              {/* Center: Viewer Count & Ruby Counter */}
              <div className="flex items-center gap-1.5 md:gap-2">
                {/* Viewer Count */}
                <div className="px-2 md:px-3 py-0.5 md:py-1 rounded-full flex items-center gap-1" style={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.75)',
                  color: '#FFFFFF'
                }}>
                  <Eye className="h-3 w-3 md:h-4 md:w-4" />
                  <span className="text-xs md:text-sm font-medium">{displayViewerCount}</span>
                </div>
                
                {/* Ruby Counter - No "earned" text */}
                <div className="px-2 md:px-3 py-0.5 md:py-1 rounded-full flex items-center gap-1" style={{ 
                  backgroundColor: 'rgba(220, 38, 127, 0.95)',
                  color: '#FFFFFF'
                }}>
                  <RubyIcon className="h-3 w-3 md:h-4 md:w-4" />
                  <span className="text-xs md:text-sm font-bold">{(rubiesEarned || 0).toLocaleString()}</span>
                </div>
              </div>
              
              {/* Right Side: Share (Round 63 — every viewer can share this live) + top viewers */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setShowShareStreamModal(true)}
                  className="px-2.5 md:px-3 py-0.5 md:py-1 rounded-full flex items-center gap-1 text-xs md:text-sm font-bold"
                  style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', color: '#FFFFFF' }}
                  data-testid="viewer-share-top-btn"
                  aria-label="Share this live"
                >
                  <Share2 className="h-3 w-3 md:h-4 md:w-4" />
                  <span className="hidden sm:inline">Share</span>
                </button>
              <div className="hidden sm:flex items-center gap-1">
                {(topViewers || []).slice(0, 3).map((viewer, index) => (
                  <div 
                    key={viewer.id || index}
                    className="w-6 h-6 md:w-8 md:h-8 rounded-full border-2 border-white overflow-hidden bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold"
                    style={{ marginLeft: index > 0 ? '-6px' : '0' }}
                    title={viewer.name}
                  >
                    {viewer.picture ? (
                      <img src={viewer.picture} alt={viewer.name} className="w-full h-full object-cover" />
                    ) : (
                      viewer.name?.charAt(0) || '?'
                    )}
                  </div>
                ))}
                {displayViewerCount > 3 && (
                  <div className="px-1.5 py-0.5 rounded-full text-xs font-bold" style={{ 
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: '#FFFFFF',
                    marginLeft: '-4px'
                  }}>
                    +{displayViewerCount - 3}
                  </div>
                )}
              </div>
              </div>
            </div>
            
            {/* Full-screen toggle (viewers) — mobile + desktop */}
            <button
              onClick={toggleViewerFullscreen}
              className="absolute bottom-3 right-3 z-40 p-2 rounded-full bg-black/60 hover:bg-black/80 active:bg-black/90 text-white backdrop-blur-sm transition-colors"
              title={isFullscreen ? 'Exit full screen' : 'Full screen'}
              aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
              data-testid="viewer-fullscreen-btn"
            >
              {isFullscreen
                ? <Minimize2 className="h-4 w-4 md:h-5 md:w-5" />
                : <Maximize2 className="h-4 w-4 md:h-5 md:w-5" />}
            </button>
            {/* Video Player */}
            <div className="w-full h-full">
              {(() => {
                // May 11 audit: use the shared predicate so fetch and render
                // can never disagree (Bug candidate 1 — white screen).
                const isMultiplayerStream = isLinkedOrMultiplayerStream(stream);

                // Diagnostic — surfaces every state change so the next
                // black-screen report can be triaged from console alone.
                console.log('[Viewer-render]', {
                  loading: agoraLoading,
                  streamEnded,
                  useLiveKit,
                  hasLivekitToken: !!livekitToken,
                  livekitRoomName,
                  hasAgoraToken: !!agoraToken,
                  agoraChannel,
                  streamId: stream?.stream_id || stream?.id,
                  is_active: stream?.is_active,
                  is_linked: stream?.is_linked,
                  linked_session_id: stream?.linked_session_id,
                  linked_to_stream: stream?.linked_to_stream,
                  stream_type: stream?.stream_type,
                  isMultiplayerStream,
                });

                if (agoraLoading) {
                  const coverBg = stream?.thumbnail || stream?.user_picture;
                  return (
                    <div className="relative w-full h-full flex items-center justify-center bg-black overflow-hidden" data-testid="viewer-loading">
                      {coverBg && (
                        <img
                          src={coverBg}
                          alt=""
                          className="absolute inset-0 w-full h-full object-cover"
                          style={{ filter: 'blur(24px) brightness(0.5)', transform: 'scale(1.1)' }}
                        />
                      )}
                      <div className="relative text-center z-10">
                        <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-white mx-auto mb-3"></div>
                        <p className="text-sm md:text-base" style={{ color: coverBg ? '#fff' : 'var(--gems-text-dim)' }}>Connecting to stream…</p>
                      </div>
                    </div>
                  );
                }

                // May 12 Viewer Fix (Behaviors B/C): show a WS-driven
                // placeholder while the viewer's token is being swapped
                // between LiveKit (solo) and Agora (linked). Brief — clears
                // as soon as the new token lands.
                if (streamTransitioning) {
                  const transitionLabel = streamTransitioning === 'to_multi'
                    ? 'Switching to multi-stream view…'
                    : 'Returning to solo stream…';
                  return (
                    <div
                      className="w-full h-full flex items-center justify-center bg-black"
                      data-testid={`viewer-transition-${streamTransitioning}`}
                    >
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-emerald-400 mx-auto mb-3"></div>
                        <p className="text-sm md:text-base font-medium" style={{ color: 'var(--gems-text-dim)' }}>
                          {transitionLabel}
                        </p>
                      </div>
                    </div>
                  );
                }
                
                // MULTIPLAYER: Use Agora with split-screen viewer component
                if (LIVEKIT_LINKS && isMultiplayerStream && linkViewToken) {
                  // Link v2: watch the shared LiveKit linked room.
                  return (
                    <LiveKitMultiViewer
                      token={linkViewToken}
                      serverUrl={linkViewUrl || LIVEKIT_URL}
                      roomName={livekitLinkRoomName(stream)}
                      hostInfo={stream ? {
                        user_id: stream.user_id,
                        name: stream.user_name,
                        picture: stream.user_picture,
                      } : null}
                      linkedPartners={stream?.linked_partners || []}
                      onError={(e) => console.error('[LiveKitLink] multi-viewer error', e, { serverUrl: linkViewUrl || LIVEKIT_URL })}
                      onDisconnected={() => console.log('👋 LiveKit multi-viewer disconnected')}
                    />
                  );
                }
                if (isMultiplayerStream && agoraToken && agoraChannel && agoraAppId) {
                  return (
                    <AgoraMultiplayerViewer
                      token={agoraToken}
                      channelName={agoraChannel}
                      appId={agoraAppId}
                      // Checkpoint 4: pass the SAME UID we used to mint the
                      // token. Pre-fix, the viewer joined with `null` →
                      // Agora's server randomized the UID and the signed
                      // token's uid claim no longer matched, which broke the
                      // participants-map identity resolution.
                      viewerUid={resolveViewerAgoraUid(user)}
                      streamerName={stream?.user_name}
                      hostInfo={stream ? {
                        user_id: stream.user_id,
                        name: stream.user_name,
                        handle: stream.user_handle,
                        picture: stream.user_picture,
                      } : null}
                      linkedPartners={stream?.linked_partners || []}
                    />
                  );
                }
                
                // SOLO: Use LiveKit for viewer video
                if (useLiveKit && livekitToken && livekitRoomName) {
                  return (
                    <LiveKitStreamViewer
                      token={livekitToken}
                      serverUrl={livekitUrl || LIVEKIT_URL}
                      roomName={livekitRoomName}
                      streamerName={stream?.user_name}
                      onStreamerConnected={(info) => console.log('✅ Connected to streamer:', info)}
                      onError={(err) => console.error('❌ LiveKit viewer error:', err, { serverUrl: livekitUrl || LIVEKIT_URL, roomName: livekitRoomName })}
                      onDisconnected={() => {
                        console.log('👋 LiveKit viewer disconnected');
                        setUseLiveKit(false);
                      }}
                    />
                  );
                }
                
                // Fallback - Show "Connecting" with proper messaging
                return (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
                    <div className="text-center p-4 md:p-8">
                      <div className="relative mb-4 md:mb-6">
                        <div className="w-16 h-16 md:w-24 md:h-24 mx-auto rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center animate-pulse">
                          <Video className="h-8 w-8 md:h-12 md:w-12 text-white" />
                        </div>
                        <div className="absolute -top-1 -right-1 w-5 h-5 md:w-6 md:h-6 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                          <div className="w-2 h-2 md:w-3 md:h-3 bg-white rounded-full"></div>
                        </div>
                      </div>
                      <h3 className="text-base md:text-xl font-bold text-white mb-2">
                        {stream?.user_name} is Live!
                      </h3>
                      <p className="text-gray-400 text-sm md:text-base mb-3">
                        {stream?.title || 'Live Stream'}
                      </p>
                      <div className="flex items-center justify-center gap-3 text-xs md:text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3 md:w-4 md:h-4" />
                          <span>{displayViewerCount} watching</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-3 max-w-xs mx-auto">
                        Connecting to video stream...
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>
            <GiftStage />
          </div>
          
          {/* Stream Info Bar - Below video */}
          <div className="p-3 md:p-4 border-b flex items-center justify-between" style={{ 
            backgroundColor: 'var(--gems-bg-1)',
            borderColor: 'var(--gems-bg-2)'
          }}>
            <div 
              ref={hostPopupContainerRef}
              className="flex items-center gap-2 md:gap-3 cursor-pointer hover:opacity-80 transition-opacity flex-1 min-w-0 relative"
              onClick={toggleHostPopup}
              data-testid="host-name-trigger"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg overflow-hidden flex-shrink-0">
                {stream?.user_picture ? (
                  <img src={stream.user_picture} alt={stream.user_name} className="w-full h-full object-cover" />
                ) : (
                  stream?.user_name?.charAt(0)
                )}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm md:text-base truncate" style={{ color: 'var(--gems-text)' }}>
                  {stream?.user_name}
                </p>
                <p className="text-xs md:text-sm truncate" style={{ color: 'var(--gems-text-dim)' }}>
                  {stream?.title || 'Live Stream'}
                </p>
              </div>
              
              {/* Host popup - Follow/View Profile */}
              {showHostPopup && (
                <div className="absolute top-full left-0 mt-2 z-50 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-3 min-w-[200px]" onClick={(e) => e.stopPropagation()} data-testid="host-action-popup">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-700">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold overflow-hidden">
                      {stream?.user_picture ? <img src={stream.user_picture} alt="" className="w-full h-full object-cover" /> : stream?.user_name?.charAt(0)}
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">{stream?.user_name}</p>
                      <p className="text-gray-400 text-xs">@{stream?.user_handle}</p>
                    </div>
                  </div>
                  <button
                    className="w-full text-left px-3 py-2 rounded-lg text-sm text-emerald-400 hover:bg-emerald-900/30 transition-colors flex items-center gap-2"
                    data-testid="host-follow-btn"
                    onClick={async () => {
                      if (!stream?.user_id) return;
                      try {
                        await api.post(`/users/${stream.user_id}/follow`);
                        toast.success(`Following ${stream.user_name}!`);
                      } catch (e) {
                        if (e.response?.status === 400) {
                          try { await api.post(`/users/${stream.user_id}/unfollow`); toast.info(`Unfollowed ${stream.user_name}`); } catch(e2) {}
                        }
                      }
                      setShowHostPopup(false);
                    }}
                  >
                    <UserPlus className="w-4 h-4" /> Follow / Unfollow
                  </button>
                  <button
                    className="w-full text-left px-3 py-2 rounded-lg text-sm text-white hover:bg-gray-800 transition-colors flex items-center gap-2"
                    data-testid="host-view-profile-btn"
                    onClick={(e) => { e.stopPropagation(); closeHostPopup(); navigate(`/profile/${stream.user_handle}`); }}
                  >
                    <User className="w-4 h-4" /> View Profile
                  </button>
                  {/* Round 13: report this live / block the broadcaster */}
                  {user && stream?.user_id && stream.user_id !== user.id && (
                    <>
                      <button
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-amber-300 hover:bg-gray-800 transition-colors flex items-center gap-2"
                        data-testid="host-report-btn"
                        onClick={(e) => { e.stopPropagation(); closeHostPopup(); watchCtx.openReport?.({ targetType: 'stream', targetId: streamId, targetUserId: stream.user_id, targetName: stream.user_name, snapshot: stream.title, streamId }); }}
                      >
                        🚩 Report this live
                      </button>
                      <button
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-gray-800 transition-colors flex items-center gap-2"
                        data-testid="host-block-btn"
                        onClick={async (e) => {
                          e.stopPropagation(); closeHostPopup();
                          if (!window.confirm(`Block ${stream.user_name}? You won't see their lives, posts or messages.`)) return;
                          if (await watchCtx.blockUser?.(stream.user_id, stream.user_name)) navigate('/discover');
                        }}
                      >
                        ⛔ Block {stream.user_name}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            
            {/* Share Button */}
            <button
              onClick={() => setShowShareStreamModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-xs md:text-sm hover:opacity-80 transition-opacity flex-shrink-0"
              style={{ backgroundColor: 'var(--gems-bg-2)', color: 'var(--gems-text)' }}
              data-testid="share-live-btn"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </button>
          </div>
        </div>
        
        {/* Chat Section - Takes remaining space on mobile, side panel on desktop */}
        <div className="flex-1 lg:w-80 lg:flex-none flex flex-col min-h-0" style={{ backgroundColor: 'var(--gems-bg-1)' }}>
          {/* Chat Header */}
          <div className="p-3 md:p-4 border-b flex-shrink-0" style={{ borderColor: 'var(--gems-bg-2)' }}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-sm md:text-base" style={{ color: 'var(--gems-text)' }}>Live Chat</h3>
                <p className="text-xs" style={{ color: 'var(--gems-text-dim)' }}>
                  {chatMessages.length} messages
                </p>
              </div>
              {/* Mobile: Show viewer count in chat header too */}
              <div className="flex items-center gap-2 sm:hidden">
                <div className="px-2 py-1 rounded-full flex items-center gap-1 text-xs" style={{ 
                  backgroundColor: 'var(--gems-bg-2)',
                  color: 'var(--gems-text-dim)'
                }}>
                  <Eye className="h-3 w-3" />
                  {displayViewerCount}
                </div>
              </div>
            </div>
          </div>

          {/* Chat Messages - Scrollable */}
          <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-2 md:space-y-3 min-h-0" style={{ maxHeight: 'calc(100vh - 400px)' }}>
            {chatMessages.length === 0 ? (
              <div className="text-center py-6 md:py-8">
                <p className="text-sm" style={{ color: 'var(--gems-text-dim)' }}>No messages yet. Be the first to chat!</p>
              </div>
            ) : (
              chatMessages.map((msg, idx) => (
                msg.message_type === 'system_join' ? (
                  <div key={msg.id || idx} className="flex items-center gap-2 py-1 px-2 md:px-3 rounded-lg" style={{ backgroundColor: 'rgba(16, 185, 129, 0.08)' }}>
                    <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-emerald-500" />
                    <p className="text-xs font-medium" style={{ color: 'var(--gems-emerald)' }}>
                      {msg.message}
                    </p>
                  </div>
                ) : (
                <div key={msg.id || idx} className="flex gap-2">
                  <div 
                    className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 cursor-pointer hover:opacity-80 overflow-hidden"
                    onClick={() => msg.user_handle && navigate(`/profile/${msg.user_handle}`)}
                  >
                    {msg.user_picture ? (
                      <img src={msg.user_picture} alt={msg.user_name} className="w-full h-full object-cover" />
                    ) : (
                      msg.user_name?.charAt(0) || 'A'
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-1.5 flex-wrap">
                      <p 
                        className="font-semibold text-xs md:text-sm truncate cursor-pointer hover:underline" 
                        style={{ color: 'var(--gems-text)' }}
                        onClick={() => msg.user_handle && navigate(`/profile/${msg.user_handle}`)}
                      >
                        {msg.user_name || 'Anonymous'}
                      </p>
                      {/* Round 13: report / block from any chat line */}
                      <ModerationMenu targetType="chat_message" targetId={msg.id} targetUserId={msg.user_id} targetName={msg.user_name} snapshot={msg.message} streamId={streamId} />
                      {/* Badges */}
                      {msg.user_badge === 'sapphire' && (
                        <SapphireBadgeIcon className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                      )}
                      {(msg.user_badge === 'canary' || msg.user_badge === 'verified') && (
                        <CanaryBadgeIcon className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                      )}
                      {/* AI badge for AI users */}
                      {msg.is_ai_user && (
                        <span className="text-xs px-1 py-0.5 rounded bg-purple-500/20 text-purple-400 font-medium">AI</span>
                      )}
                      <p className="text-xs flex-shrink-0" style={{ color: 'var(--gems-text-dim)' }}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <p className="text-xs md:text-sm break-words" style={{ color: 'var(--gems-text-dim)' }}>
                      {msg.message}
                    </p>
                  </div>
                </div>
                )
              ))
            )}
          </div>

          {/* Chat Input - Fixed at bottom, easy to access */}
          <div className="p-3 md:p-4 border-t flex-shrink-0" style={{ 
            borderColor: 'var(--gems-bg-2)',
            backgroundColor: 'var(--gems-bg-1)'
          }}>
            {isViewerMode ? (
              <div className="space-y-2" data-testid="viewer-mode-banner">
                <div className="text-center p-2 md:p-3 rounded-lg" style={{ backgroundColor: 'var(--gems-bg-2)' }}>
                  <p className="text-xs md:text-sm font-medium" style={{ color: 'var(--gems-emerald)' }}>Viewer Mode</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--gems-text-dim)' }}>
                    Get beta access to chat & gift
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { window.location.href = '/beta-access'; }}
                    className="flex-1 text-xs py-2 rounded-lg font-medium text-white"
                    style={{ backgroundColor: 'var(--gems-emerald)' }}
                    data-testid="viewer-signup-btn"
                  >
                    Sign Up
                  </button>
                  <button
                    onClick={() => { window.location.href = `/beta-access?ref=${stream?.user_id || ''}&stream=${streamId}`; }}
                    className="flex-1 text-xs py-2 rounded-lg font-medium border"
                    style={{ borderColor: 'var(--gems-emerald)', color: 'var(--gems-emerald)' }}
                    data-testid="viewer-beta-code-btn"
                  >
                    Beta Code
                  </button>
                </div>
              </div>
            ) : user ? (
              <div className="space-y-2">
                {/* Gift/Boost Row - Always visible */}
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowGiftPanel(true)}
                    className="flex-1 bg-pink-500 hover:bg-pink-600 text-sm py-2"
                    title="Send a gift"
                  >
                    <Gift className="h-4 w-4 mr-1" />
                    Gift
                  </Button>
                  {/* Boost Button - Only show for Spades games */}
                  {stream?.game_id && boostEnabled && user?.id !== stream?.user_id && (
                    <Button
                      onClick={() => setShowBoostModal(true)}
                      className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-sm py-2"
                      title="Boost the prize"
                    >
                      <EmeraldIcon className="h-4 w-4 inline mr-1" /> Boost
                    </Button>
                  )}
                </div>
                {/* Chat Input Row */}
                <div className="flex gap-2">
                  <MentionBox
                    value={newChatMessage}
                    onChange={setNewChatMessage}
                    onSubmit={sendChatMessage}
                    placeholder="Say hi to viewers... (@ to mention)"
                    api={api}
                    className="flex-1"
                  />
                  <Button
                    onClick={sendChatMessage}
                    disabled={!newChatMessage.trim() || sendingMessage}
                    className="px-3"
                    style={{
                      backgroundColor: 'var(--gems-emerald)',
                      color: '#FFFFFF'
                    }}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'var(--gems-bg-2)' }}>
                <p className="text-sm" style={{ color: 'var(--gems-text-dim)' }}>
                  Sign in to join the conversation
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Share Stream Modal */}
      <ShareStreamModal
        isOpen={showShareStreamModal}
        onClose={() => setShowShareStreamModal(false)}
        streamId={streamId}
        streamerName={stream?.user_name || 'Streamer'}
        viewerCount={displayViewerCount}
        isHost={user?.id === stream?.user_id}
        api={api}
        userId={user?.id}
      />
      {showGiftPanel && (
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/60 p-3" onClick={() => setShowGiftPanel(false)}>
          <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <GiftPicker
              emeralds={user?.emeralds || 0}
              onClose={() => setShowGiftPanel(false)}
              onSend={async (giftId, qty) => {
                try {
                  const res = await api.post('/gifts/send', {
                    recipient_stream_id: streamId,
                    gift_id: giftId,
                    quantity: qty,
                  });
                  if (typeof res.data?.new_balance === 'number' && setUser) {
                    setUser(prev => (prev ? { ...prev, emeralds: res.data.new_balance } : prev));
                  }
                  // Echo via WS so cross-process/replica setups still animate for everyone.
                  try {
                    const ws = watcherWsRef.current;
                    if (ws && ws.readyState === WebSocket.OPEN) {
                      ws.send(JSON.stringify({
                        type: 'gift',
                        gift_event_id: res.data?.gift_event_id,
                        user_id: user?.id,
                        username: user?.name || 'Someone',
                        sender: user?.name || 'Someone',
                        gift_id: giftId,
                        reveal_gift_id: res.data?.reveal_gift_id,
                        quantity: qty,
                        gift: res.data?.gift || null,
                      }));
                    }
                  } catch {}
                  setShowGiftPanel(false);
                } catch (err) {
                  const s = err.response?.status;
                  if (s === 400 || s === 402) toast.error("Not enough Emeralds for that.", { action: { label: 'Get Emeralds', onClick: () => navigate('/wallet') } });
                  else toast.error('Could not send gift');
                }
              }}
            />
          </div>
        </div>
      )}
      
      {/* Insufficient Funds Modal */}
      {showInsufficientFundsModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 max-w-md w-full border border-red-500/30">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <EmeraldIcon className="h-8 w-8 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Insufficient Emeralds</h3>
              <p className="text-gray-400 mb-4">
                You need <span className="text-emerald-400 font-bold">{selectedGift?.price}</span> emeralds to send this gift, 
                but you only have <span className="text-red-400 font-bold">{user?.emeralds || 0}</span>.
              </p>
              <p className="text-gray-500 text-sm mb-6">
                Recharge your emeralds to send gifts to your favorite streamers!
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowInsufficientFundsModal(false);
                    setSelectedGift(null);
                  }}
                  variant="outline"
                  className="flex-1 border-gray-600 text-gray-400 hover:bg-gray-800"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setShowInsufficientFundsModal(false);
                    setShowGiftPanel(false);
                    navigate('/wallet');
                  }}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  <EmeraldIcon className="h-4 w-4 mr-2" />
                  Recharge Emeralds
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Spectator Boost Modal */}
      {showBoostModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 max-w-md w-full border border-amber-500/30">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <EmeraldIcon className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Boost the Prize!</h3>
              <p className="text-gray-400">
                Add your Emeralds to the prize. They'll be converted to Rubies 1:1.
              </p>
              {totalBoosted > 0 && (
                <p className="text-amber-400 mt-2 text-sm">
                  Current boosts: <span className="font-bold">{totalBoosted}</span> Rubies
                </p>
              )}
            </div>
            
            {/* Boost Options */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {[10, 25, 50, 100].map((amount) => (
                <button
                  key={amount}
                  onClick={() => handleSpectatorBoost(amount)}
                  disabled={boostingAmount !== null || (user?.emeralds || 0) < amount}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    boostingAmount === amount 
                      ? 'border-amber-500 bg-amber-500/20' 
                      : (user?.emeralds || 0) < amount
                        ? 'border-gray-600 bg-gray-800 opacity-50 cursor-not-allowed'
                        : 'border-gray-600 bg-gray-800 hover:border-amber-500 hover:bg-amber-500/10'
                  }`}
                >
                  <div className="text-2xl mb-1"><EmeraldIcon className="h-6 w-6 mx-auto" /></div>
                  <div className="text-white font-bold">{amount}</div>
                  <div className="text-gray-400 text-xs">Emeralds</div>
                  {boostingAmount === amount && (
                    <div className="text-amber-400 text-xs mt-1">Boosting...</div>
                  )}
                </button>
              ))}
            </div>
            
            <p className="text-center text-gray-500 text-sm mb-4">
              Your balance: <span className="text-emerald-400 font-bold">{user?.emeralds || 0}</span> Emeralds
            </p>
            
            <Button
              onClick={() => setShowBoostModal(false)}
              variant="outline"
              className="w-full border-gray-600 text-gray-400 hover:bg-gray-800"
              disabled={boostingAmount !== null}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      
    </div>
  );
};

// ==================== PUBLIC PROFILE PAGE ====================
const PublicProfilePage = () => {
  const { handle } = useParams();
  const { user } = React.useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [followLoading, setFollowLoading] = useState(false);
  const [socialModal, setSocialModal] = useState({ open: false, type: null, data: [], loading: false });
  const [showPicModal, setShowPicModal] = useState(false);
  // Post composer state (May 17, 2026) — added because /api/posts existed
  // but no frontend UI wired to it → users had no way to create a post,
  // and the feed was carrying only AI-scheduler content.
  const [composerText, setComposerText] = useState('');
  const [composerFile, setComposerFile] = useState(null);
  const [composerPosting, setComposerPosting] = useState(false);
  const composerFileInputRef = useRef(null);
  // Feb 2026: anthem player for visitors. Because browsers block
  // audio autoplay, we start muted and let the visitor tap the pill
  // to unmute / play the loop.
  const visitorAudioRef = useRef(null);
  const [visitorAnthemPlaying, setVisitorAnthemPlaying] = useState(false);
  const [visitorAnthemMuted, setVisitorAnthemMuted] = useState(true);
  const navigate = useNavigate();

  const handleCreatePost = async () => {
    const text = composerText.trim();
    if (!text && !composerFile) {
      toast.error('Write something or attach an image/video.');
      return;
    }
    setComposerPosting(true);
    try {
      let media_url = null;
      let thumbnail_url = null;
      let post_type = 'text';
      if (composerFile) {
        const isVideo = composerFile.type.startsWith('video/');
        const isImage = composerFile.type.startsWith('image/');
        if (!isVideo && !isImage) {
          toast.error('Only images and videos are supported.');
          setComposerPosting(false);
          return;
        }
        // Round 54: the server's limits (video 300 MB by default) instead of a hard-coded number; progress while a video goes up
        let limits = { video_mb: 300, image_mb: 20 };
        try { const f = await api.get('/config/features'); if (f.data?.upload_limits) limits = { ...limits, ...f.data.upload_limits }; } catch (e) { /* defaults */ }
        const capMb = isVideo ? limits.video_mb : limits.image_mb;
        if (composerFile.size > capMb * 1024 * 1024) {
          toast.error(isVideo ? `That video is ${Math.round(composerFile.size / 1048576)} MB — videos up to ${capMb} MB can be posted. The GEMS app shrinks a clip for you; on the web, try a shorter or lower-resolution clip.`
            : `That picture is too large — pictures up to ${capMb} MB can be posted.`);
          setComposerPosting(false);
          return;
        }
        const formData = new FormData();
        formData.append('file', composerFile);
        const progressId = isVideo ? toast.loading('Uploading video… 0%') : null;
        let uploadRes;
        try {
          uploadRes = await api.post('/media/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: isVideo ? 10 * 60 * 1000 : 120 * 1000,
            onUploadProgress: (e) => { if (progressId && e.total) toast.loading(`Uploading video… ${Math.round((e.loaded / e.total) * 100)}%`, { id: progressId }); },
          });
        } catch (upErr) {
          if (progressId) toast.dismiss(progressId);
          const detail = upErr?.response?.data?.detail;
          toast.error(typeof detail === 'string' ? detail : 'The upload did not finish — check your connection and try again.');
          setComposerPosting(false);
          return;
        }
        if (progressId) toast.dismiss(progressId);
        media_url = uploadRes.data.url || uploadRes.data.file_url || null;
        post_type = isVideo ? 'video' : 'image';
        // Videos have no thumbnail here — the /media/upload response doesn't
        // extract a poster frame. Leaving thumbnail_url null; the card
        // <video> element uses the first frame automatically.
      }
      const createRes = await api.post('/posts', {
        content: text,
        media_url,
        thumbnail_url,
        post_type,
      });
      setComposerText('');
      setComposerFile(null);
      if (composerFileInputRef.current) composerFileInputRef.current.value = '';
      toast.success('Posted!');
      // Round 6: growth rewards ride the create response.
      try {
        const rw = createRes?.data?.rewards;
        if (rw?.first_post_emeralds) toast.success(<span className="flex items-center gap-1">First post! +{rw.first_post_emeralds} <EmeraldIcon className="h-4 w-4" /></span>);
        if (rw?.daily_bonus_emeralds) toast.success(<span className="flex items-center gap-1">Daily bonus! +{rw.daily_bonus_emeralds} <EmeraldIcon className="h-4 w-4" /></span>);
      } catch { /* noop */ }
      // Refresh the profile's post list so the new post appears immediately.
      try {
        const postsResponse = await api.get(`/posts/user/${profile.id}`);
        setPosts(postsResponse.data.posts || []);
      } catch { /* noop */ }
    } catch (e) {
      console.error('Create post failed:', e);
      toast.error(e.response?.data?.detail || 'Failed to post.');
    } finally {
      setComposerPosting(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get(`/users/handle/${handle}`);
      setProfile(response.data);
      try {
        const postsResponse = await api.get(`/posts/user/${response.data.id}`);
        setPosts(postsResponse.data.posts || []);
      } catch (e) { /* no posts */ }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (handle) fetchProfile(); }, [handle]);

  // Feb 2026 visitor anthem player — attempt muted autoplay when
  // the profile loads. If it succeeds, the equalizer bars animate;
  // if the browser blocks even muted autoplay, the visitor just
  // taps the pill to start it (see toggleVisitorAnthemPlay below).
  useEffect(() => {
    const el = visitorAudioRef.current;
    const url = profile?.anthem?.url;
    if (!el || !url) return;
    if (el.getAttribute('data-src') === url) return;
    el.setAttribute('data-src', url);
    el.src = url;
    el.loop = true;
    el.muted = true;
    setVisitorAnthemMuted(true);
    el.play().then(() => setVisitorAnthemPlaying(true)).catch(() => {
      setVisitorAnthemPlaying(false);
    });
  }, [profile?.anthem?.url]);

  const toggleVisitorAnthemPlay = () => {
    const el = visitorAudioRef.current;
    if (!el) return;
    // Belt-and-braces: some browsers wipe src after a failed muted-
    // autoplay attempt. Re-hydrate from profile.anthem.url on every
    // tap so the play button always works on the first press.
    const url = profile?.anthem?.url;
    if (url && el.src !== url) {
      el.src = url;
      el.loop = true;
    }
    if (el.paused) {
      el.muted = false;
      setVisitorAnthemMuted(false);
      el.play().then(() => setVisitorAnthemPlaying(true)).catch((err) => {
        console.warn('Visitor anthem play blocked:', err);
      });
    } else {
      el.pause();
      setVisitorAnthemPlaying(false);
    }
  };
  const toggleVisitorAnthemMute = () => {
    const el = visitorAudioRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setVisitorAnthemMuted(el.muted);
  };

  const handleFollow = async () => {
    if (!user || !profile) return;
    setFollowLoading(true);
    try {
      const isCurrentlyFollowing = profile.relationship === 'following' || profile.relationship === 'friends';
      if (isCurrentlyFollowing) {
        await api.post('/users/unfollow', { user_id: profile.id });
      } else {
        await api.post('/users/follow', { user_id: profile.id });
      }
      await fetchProfile();
    } catch (error) {
      console.error('Follow action failed:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  const openSocialList = async (type) => {
    if (!profile) return;
    setSocialModal({ open: true, type, data: [], loading: true });
    try {
      const response = await api.get(`/users/${profile.id}/social/${type}`);
      const users = response.data.users || [];
      console.log(`Social ${type}: received ${users.length} users (requested: ${response.data.requested || '?'})`);
      setSocialModal(prev => ({ ...prev, data: users, loading: false }));
    } catch (e) {
      console.error(`Social list error (${type}):`, e?.response?.status, e?.response?.data || e.message);
      setSocialModal(prev => ({ ...prev, data: [], loading: false, error: `Failed to load ${type} list` }));
    }
  };

  const handleSocialAction = async (targetId, relationship) => {
    try {
      if (relationship === 'following' || relationship === 'friends') {
        await api.post('/users/unfollow', { user_id: targetId });
      } else {
        await api.post('/users/follow', { user_id: targetId });
      }
      openSocialList(socialModal.type);
      fetchProfile();
    } catch (error) { /* handled */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--gems-bg-0)' }}>
        <p style={{ color: 'var(--gems-text)' }}>Loading profile...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--gems-bg-0)' }}>
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4" style={{ color: 'var(--gems-text)' }}>Profile Not Found</h1>
          <p style={{ color: 'var(--gems-text-dim)' }}>This user doesn't exist</p>
          <Button onClick={() => navigate('/discover')} className="mt-4 bg-emerald-600 hover:bg-emerald-700">
            Back to Discover
          </Button>
        </div>
      </div>
    );
  }

  const isSelf = user && user.id === profile.id;
  const followBtnText = profile.relationship === 'friends' ? 'Friends' : profile.relationship === 'following' ? 'Following' : profile.relationship === 'fan' ? 'Follow Back' : 'Follow';
  const followBtnClass = (profile.relationship === 'following' || profile.relationship === 'friends')
    ? 'bg-slate-600 hover:bg-red-600 text-white'
    : 'bg-emerald-600 hover:bg-emerald-500 text-white';

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--gems-bg-0)' }}>
      <NavBar />
      <div className="max-w-4xl mx-auto px-4 py-8 pt-20">
        {/* Profile Header */}
        <div className="rounded-2xl p-6 mb-6" style={{ backgroundColor: 'var(--gems-bg-1)', border: '1px solid var(--card-border)' }}>
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <div className="relative">
              <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white text-5xl font-bold cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => (profile.picture || profile.profile_pic) && setShowPicModal(true)}>
                {profile.picture || profile.profile_pic ? (
                  <img src={profile.picture || profile.profile_pic} alt={profile.name} className="w-full h-full object-cover" />
                ) : (
                  profile.name?.charAt(0)
                )}
              </div>
              {profile.is_ai_creator && (
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full">AI Creator</div>
              )}
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
                <h1 className="text-3xl font-bold" style={{ color: 'var(--gems-text)' }}>{profile.name}</h1>
                {profile.badge_type === 'sapphire' && <SapphireBadgeIcon className="w-6 h-6" />}
                {(profile.badge_type === 'canary' || profile.badge_type === 'verified') && <CanaryBadgeIcon className="w-6 h-6" />}
              </div>
              <p className="text-lg mb-3" style={{ color: 'var(--gems-text-dim)' }}>@{profile.handle}</p>
              
              {profile.bio && <p className="mb-4" style={{ color: 'var(--gems-text)' }}>{profile.bio}</p>}
              
              {/* Meta info row - Level, Location, Zodiac, Age */}
              <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-4 text-sm">
                {profile.level != null && (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30">
                    <span className="text-yellow-400 font-semibold">Lv {profile.level}</span>
                  </div>
                )}
                {(profile.location || profile.city) && (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--gems-bg-0)', color: 'var(--gems-text-dim)' }}>
                    <MapPin className="w-3 h-3" /> {profile.location || profile.city}
                  </div>
                )}
                {profile.zodiac_sign && (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--gems-bg-0)', color: 'var(--gems-text-dim)' }}>
                    {profile.zodiac_sign}
                  </div>
                )}
                {profile.age && (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--gems-bg-0)', color: 'var(--gems-text-dim)' }}>
                    {profile.age}
                  </div>
                )}
                {profile.gender && (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--gems-bg-0)', color: 'var(--gems-text-dim)' }}>
                    {profile.gender}
                  </div>
                )}
              </div>

              {/* Feb 2026 visitor anthem player — mirrors ProfilePage
                  behavior. The hidden <audio> element auto-plays muted;
                  first tap on Play unmutes and unpauses. */}
              {profile.anthem?.url && (
                <div
                  className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full text-sm border border-white/15 mb-4"
                  style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
                  data-testid="visitor-anthem-pill"
                >
                  <button
                    onClick={toggleVisitorAnthemPlay}
                    className="flex items-center gap-1.5"
                    aria-label={visitorAnthemPlaying ? 'Pause anthem' : 'Play anthem'}
                    data-testid="visitor-anthem-play-toggle"
                  >
                    {visitorAnthemPlaying ? (
                      <Pause className="w-4 h-4" style={{ color: '#34E3A4' }} />
                    ) : (
                      <Play className="w-4 h-4" style={{ color: '#34E3A4' }} />
                    )}
                  </button>
                  <Music className="w-3.5 h-3.5 text-white/60" />
                  <span className="text-white/90 max-w-[200px] truncate" title={`${profile.anthem.title} — ${profile.anthem.artist}`}>
                    <span className="font-medium">{profile.anthem.title}</span>
                    <span className="text-white/50"> · {profile.anthem.artist}</span>
                  </span>
                  {/* Equalizer bars */}
                  <span className="ml-1 flex items-end gap-0.5 h-3.5">
                    {[0, 1, 2, 3].map(i => (
                      <span
                        key={i}
                        className="w-0.5 rounded-full"
                        style={{
                          backgroundColor: '#34E3A4',
                          height: visitorAnthemPlaying ? `${40 + ((i * 23) % 60)}%` : '30%',
                          animation: visitorAnthemPlaying ? `anthem-bounce 900ms ${i * 120}ms ease-in-out infinite` : 'none',
                        }}
                      />
                    ))}
                  </span>
                  <button
                    onClick={toggleVisitorAnthemMute}
                    className="ml-1 opacity-70 hover:opacity-100"
                    aria-label={visitorAnthemMuted ? 'Unmute anthem' : 'Mute anthem'}
                    data-testid="visitor-anthem-mute-toggle"
                    title={visitorAnthemMuted ? 'Tap to hear the anthem' : 'Mute'}
                  >
                    {visitorAnthemMuted ? (
                      <VolumeX className="w-4 h-4 text-white/70" />
                    ) : (
                      <Volume2 className="w-4 h-4" style={{ color: '#34E3A4' }} />
                    )}
                  </button>
                  {visitorAnthemMuted && (
                    <span className="text-[10px] uppercase tracking-widest text-emerald-400 ml-1">Tap to play</span>
                  )}
                  <audio ref={visitorAudioRef} preload="none" loop playsInline data-testid="visitor-anthem-audio" />
                </div>
              )}
              
              {/* Accomplishments */}
              {(profile.tournament_wins > 0 || profile.streak >= 5 || profile.boston_count > 0) && (
                <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-4">
                  {profile.tournament_wins > 0 && (
                    <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30">
                      <Trophy className="w-3 h-3 text-amber-400" />
                      <span className="text-xs text-amber-300">{profile.tournament_wins} tournament{profile.tournament_wins !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {profile.streak >= 5 && (
                    <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30">
                      <span className="text-xs text-red-300">{profile.streak}-game streak</span>
                    </div>
                  )}
                  {profile.boston_count > 0 && (
                    <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/30">
                      <span className="text-xs text-purple-300">{profile.boston_count} Boston{profile.boston_count !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Follow/Unfollow Button */}
              {!isSelf && user && (
                <div className="flex justify-center md:justify-start gap-2 mb-4">
                  <button
                    onClick={handleFollow}
                    disabled={followLoading}
                    className={`px-6 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${followBtnClass} disabled:opacity-50`}
                    data-testid="profile-follow-btn"
                  >
                    {followLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : profile.relationship === 'friends' ? (
                      <UserCheck className="w-4 h-4" />
                    ) : profile.relationship === 'following' ? (
                      <UserCheck className="w-4 h-4" />
                    ) : (
                      <UserPlus className="w-4 h-4" />
                    )}
                    {followBtnText}
                  </button>
                  <button
                    onClick={() => navigate(`/messages`)}
                    className="px-6 py-2 rounded-full font-medium transition-colors border border-gray-500 hover:bg-white/10"
                    style={{ color: 'var(--gems-text)' }}
                  >
                    <MessageCircle className="w-4 h-4 inline mr-1" /> Message
                  </button>
                  {/* Round 13: report / block from the profile */}
                  <ModerationMenu targetType="user" targetId={profile.id} targetUserId={profile.id} targetName={profile.name} size="md" label="⋯ Report / Block" />
                </div>
              )}
              
              {/* Clickable Social Stats */}
              <div className="flex justify-center md:justify-start gap-6 mb-4">
                <button onClick={() => openSocialList('fans')} className="text-center hover:opacity-80 transition-opacity" data-testid="public-profile-fans">
                  <div className="text-xl font-bold" style={{ color: 'var(--gems-text)' }}>{profile.followers_count?.toLocaleString() || 0}</div>
                  <div className="text-sm" style={{ color: 'var(--gems-text-dim)' }}>Fans</div>
                </button>
                <button onClick={() => openSocialList('following')} className="text-center hover:opacity-80 transition-opacity" data-testid="public-profile-following">
                  <div className="text-xl font-bold" style={{ color: 'var(--gems-text)' }}>{profile.following_count?.toLocaleString() || 0}</div>
                  <div className="text-sm" style={{ color: 'var(--gems-text-dim)' }}>Following</div>
                </button>
                <button onClick={() => openSocialList('friends')} className="text-center hover:opacity-80 transition-opacity" data-testid="public-profile-friends">
                  <div className="text-xl font-bold" style={{ color: 'var(--gems-emerald)' }}>{profile.friends_count?.toLocaleString() || 0}</div>
                  <div className="text-sm" style={{ color: 'var(--gems-text-dim)' }}>Friends</div>
                </button>
              </div>
              
              {/* Game Stats */}
              <div className="flex justify-center md:justify-start gap-6 flex-wrap">
                <div className="text-center">
                  <div className="text-2xl font-bold" style={{ color: 'var(--gems-emerald)' }}>{profile.wins || 0}</div>
                  <div className="text-sm" style={{ color: 'var(--gems-text-dim)' }}>Wins</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold" style={{ color: 'var(--gems-red)' }}>{profile.losses || 0}</div>
                  <div className="text-sm" style={{ color: 'var(--gems-text-dim)' }}>Losses</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold" style={{ color: 'var(--gems-text)' }}>{profile.win_rate || 0}%</div>
                  <div className="text-sm" style={{ color: 'var(--gems-text-dim)' }}>Win Rate</div>
                </div>
                <div className="text-center flex flex-col items-center">
                  <div className="flex items-center gap-1">
                    <EmeraldIcon className="w-5 h-5" />
                    <div className="text-xl font-bold" style={{ color: 'var(--gems-emerald)' }}>{(profile.emeralds || 0).toLocaleString()}</div>
                  </div>
                  <div className="text-sm" style={{ color: 'var(--gems-text-dim)' }}>Emeralds</div>
                </div>
                <div className="text-center flex flex-col items-center">
                  <div className="flex items-center gap-1">
                    <RubyIcon className="w-5 h-5" />
                    <div className="text-xl font-bold" style={{ color: 'var(--gems-red)' }}>{(profile.rubies || 0).toLocaleString()}</div>
                  </div>
                  <div className="text-sm" style={{ color: 'var(--gems-text-dim)' }}>Rubies</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Photo Gallery Section */}
        {profile.profile_photos && profile.profile_photos.length > 0 && (
          <div className="rounded-2xl p-6 mb-6" style={{ backgroundColor: 'var(--gems-bg-1)', border: '1px solid var(--card-border)' }} data-testid="public-photo-gallery">
            <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--gems-text)' }}>Photos</h2>
            <div className="grid grid-cols-3 gap-3">
              {profile.profile_photos.map((photo, index) => (
                <div key={index} className="aspect-square rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => setShowPicModal(true)}>
                  <img src={photo} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Posts Section */}
        <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--gems-bg-1)', border: '1px solid var(--card-border)' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--gems-text)' }}>Recent Posts</h2>

          {/* ============================================================
              POST COMPOSER (May 17, 2026) — self-only.
              Was completely missing before this change; /api/posts existed
              but nothing on the frontend called it, so the feed carried
              only AI-scheduler content.
              ============================================================ */}
          {isSelf && (
            <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: 'var(--gems-bg-0)', border: '1px solid var(--card-border)' }} data-testid="post-composer">
              <textarea
                value={composerText}
                onChange={(e) => setComposerText(e.target.value)}
                placeholder="Share something with your community…"
                rows={3}
                maxLength={1000}
                data-testid="post-composer-text"
                className="w-full bg-transparent outline-none resize-none text-sm"
                style={{ color: 'var(--gems-text)' }}
              />
              {composerFile && (
                <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: 'var(--gems-text-dim)' }}>
                  <span>📎 {composerFile.name}</span>
                  <button
                    type="button"
                    onClick={() => { setComposerFile(null); if (composerFileInputRef.current) composerFileInputRef.current.value = ''; }}
                    className="text-red-400 hover:text-red-300"
                    data-testid="post-composer-clear-file"
                  >
                    remove
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between mt-3">
                <label className="cursor-pointer text-sm px-3 py-1.5 rounded-lg" style={{ backgroundColor: 'var(--gems-bg-1)', color: 'var(--gems-text)', border: '1px solid var(--card-border)' }}>
                  📷 Add image/video
                  <input
                    type="file"
                    accept="image/*,video/*"
                    ref={composerFileInputRef}
                    onChange={(e) => setComposerFile(e.target.files?.[0] || null)}
                    className="hidden"
                    data-testid="post-composer-file"
                  />
                </label>
                <button
                  type="button"
                  disabled={composerPosting || (!composerText.trim() && !composerFile)}
                  onClick={handleCreatePost}
                  data-testid="post-composer-submit"
                  className="px-4 py-1.5 rounded-lg text-white font-medium text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {composerPosting ? 'Posting…' : 'Post'}
                </button>
              </div>
            </div>
          )}

          {posts.length === 0 ? (
            <p className="text-center py-8" style={{ color: 'var(--gems-text-dim)' }}>No posts yet</p>
          ) : (
            <div className="space-y-4">
              {posts.slice(0, 5).map(post => (
                <div key={post.id} className="p-4 rounded-xl" style={{ backgroundColor: 'var(--gems-bg-0)', border: '1px solid var(--card-border)' }} data-testid={`profile-post-${post.id}`}>
                  <p style={{ color: 'var(--gems-text)' }}>{post.content}</p>
                  <CurrentEventsPostBody post={post} />
                  {post.media_url && (
                    // May-17 fix: video posts need <video>, not <img>.
                    (post.post_type === 'video' || /\.(mp4|webm|mov|m4v)(\?|$)/i.test(post.media_url)) ? (
                      <video
                        src={post.media_url}
                        poster={post.thumbnail_url || undefined}
                        controls
                        playsInline
                        className="mt-3 rounded-lg max-h-64 w-full object-cover bg-black"
                      />
                    ) : (
                      <img
                        src={post.media_url}
                        alt="Post"
                        className="mt-3 rounded-lg max-h-64 object-cover cursor-pointer"
                        onClick={() => openMediaLightbox(post.media_url, 'image', post.content)}
                      />
                    )
                  )}
                  <div className="flex items-center gap-4 mt-3 text-sm" style={{ color: 'var(--gems-text-dim)' }}>
                    <span>&#10084; {post.likes || 0}</span>
                    <span>&#128172; {post.comments_count || 0}</span>
                    <span>{new Date(post.created_at).toLocaleDateString()}</span>
                    {post.edited_at && <span className="text-[10px] italic">edited</span>}
                    {/* Round 13: report/block someone else's post */}
                    {post.user_id !== user?.id && (
                      <span className="ml-auto flex items-center gap-2">
                        {user?.is_admin && (
                          <button
                            className="text-xs px-2 py-0.5 rounded border"
                            style={{ color: '#F87171', borderColor: 'rgba(248,113,113,0.4)' }}
                            data-testid={`admin-delete-profile-post-${post.id}`}
                            onClick={async () => {
                              if (!window.confirm('Remove this post as an admin? This can\'t be undone.')) return;
                              try { await api.delete(`/posts/${post.id}`); setPosts(prev => prev.filter(p => p.id !== post.id)); toast.success('Post removed'); }
                              catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
                            }}
                          >Delete (admin)</button>
                        )}
                        <ModerationMenu targetType="post" targetId={post.id} targetUserId={post.user_id} targetName={profile?.name} snapshot={post.content} />
                      </span>
                    )}
                    {/* Round 6: edit/delete your own profile posts (Round 13: owner only) */}
                    {post.user_id === user?.id && (
                    <span className="ml-auto flex items-center gap-2">
                      <button
                        className="text-xs px-2 py-0.5 rounded border"
                        style={{ color: 'var(--gems-text-dim)', borderColor: 'var(--card-border)' }}
                        data-testid={`profile-edit-post-${post.id}`}
                        onClick={async () => {
                          const next = window.prompt('Edit your post', post.content || '');
                          if (next == null || !next.trim() || next === post.content) return;
                          try {
                            const r = await api.patch(`/posts/${post.id}`, { content: next.trim() });
                            setPosts(prev => prev.map(p => p.id === post.id ? { ...p, ...(r.data?.post || { content: next.trim() }) } : p));
                            toast.success('Post updated');
                          } catch (err) { toast.error(err.response?.data?.detail || 'Edit failed'); }
                        }}
                      >Edit</button>
                      <button
                        className="text-xs px-2 py-0.5 rounded border"
                        style={{ color: '#F87171', borderColor: 'rgba(248,113,113,0.4)' }}
                        data-testid={`profile-delete-post-${post.id}`}
                        onClick={async () => {
                          if (!window.confirm('Delete this post? This can\'t be undone.')) return;
                          try {
                            await api.delete(`/posts/${post.id}`);
                            setPosts(prev => prev.filter(p => p.id !== post.id));
                            toast.success('Post deleted');
                          } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
                        }}
                      >Delete</button>
                    </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Social List Modal for Public Profile */}
      {socialModal.open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSocialModal({ open: false, type: null, data: [], loading: false })}>
          <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md max-h-[70vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="public-social-modal">
            <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-500" />
                {socialModal.type === 'friends' ? 'Friends' : socialModal.type === 'following' ? 'Following' : 'Fans'}
              </h3>
              <button onClick={() => setSocialModal({ open: false, type: null, data: [], loading: false })} className="text-gray-400 hover:text-white">&#10005;</button>
            </div>
            <div className="overflow-y-auto max-h-96 p-2">
              {socialModal.loading ? (
                <div className="text-center py-8"><div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto"></div></div>
              ) : socialModal.error ? (
                <div className="text-center py-8 text-red-400 text-sm">{socialModal.error}</div>
              ) : socialModal.data.length === 0 ? (
                <div className="text-center py-8 text-gray-400">No {socialModal.type} yet</div>
              ) : (
                socialModal.data.map(person => (
                  <div key={person.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer" onClick={() => { setSocialModal({ open: false, type: null, data: [], loading: false }); navigate(`/profile/${person.handle || person.id}`); }}>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold overflow-hidden flex-shrink-0">
                        {person.picture ? <img src={person.picture} alt={person.name} className="w-full h-full object-cover" /> : person.name?.[0] || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate text-sm">{person.name}</p>
                        <p className="text-emerald-400 text-xs truncate">@{person.handle || 'user'}</p>
                      </div>
                    </div>
                    {user && person.id !== user.id && (
                      <button
                        onClick={() => handleSocialAction(person.id, person.relationship)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium flex-shrink-0 transition-colors ${
                          person.relationship === 'friends' || person.relationship === 'following' ? 'bg-slate-600/50 text-gray-300 hover:bg-red-600/30 hover:text-red-300' : 'bg-emerald-600 text-white hover:bg-emerald-500'
                        }`}
                      >
                        {person.relationship === 'friends' ? 'Friends' : person.relationship === 'following' ? 'Following' : 'Follow'}
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MESSAGES PAGE ====================
const MessagesPage = () => {
  const { user } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // Pinned GEMS Official channel expansion (round 5)
  const [officialOpen, setOfficialOpen] = useState(false);

  // New Message Modal State
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const searchTimeout = useRef(null);
  
  // Attachment state
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch conversations
  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch messages when conversation selected
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.user_id);
      const msgInterval = setInterval(() => fetchMessages(selectedConversation.user_id), 3000);
      return () => clearInterval(msgInterval);
    }
  }, [selectedConversation]);
  
  // Live user search with debounce
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    
    if (!userSearchQuery || userSearchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    
    searchTimeout.current = setTimeout(async () => {
      setLoadingSearch(true);
      try {
        const response = await api.get(`/users/search?q=${encodeURIComponent(userSearchQuery)}&limit=15`);
        const users = (response.data.users || []).filter(u => u.id !== user?.id);
        setSearchResults(users);
      } catch (error) {
        console.error('User search error:', error);
        setSearchResults([]);
      } finally {
        setLoadingSearch(false);
      }
    }, 300);
    
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [userSearchQuery, user?.id]);
  
  const startNewConversation = (selectedUser) => {
    setSelectedConversation({
      user_id: selectedUser.id,
      user_name: selectedUser.name,
      user_handle: selectedUser.handle,
      badge_type: selectedUser.badge_type,
      is_admin: selectedUser.is_admin
    });
    setShowNewMessageModal(false);
    setUserSearchQuery('');
    setSearchResults([]);
    setMessages([]);
    fetchMessages(selectedUser.id);
  };

  const fetchConversations = async () => {
    try {
      const response = await api.get('/messages/conversations');
      setConversations(response.data.conversations || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setLoading(false);
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const response = await api.get(`/messages/conversation/${userId}`);
      setMessages(response.data.messages || []);
      setTimeout(() => {
        const container = document.getElementById('messages-container');
        if (container) container.scrollTop = container.scrollHeight;
      }, 100);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Handle file selection for attachment
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }
    
    setAttachmentFile(file);
    
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setAttachmentPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview(null);
    }
  };
  
  const clearAttachment = () => {
    setAttachmentFile(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && !attachmentFile) || !selectedConversation) return;
    
    if (newMessage.length > 500) {
      toast.error('Message too long (max 500 characters)');
      return;
    }

    setSending(true);
    try {
      let attachmentUrl = null;
      
      // Upload attachment if present
      if (attachmentFile) {
        setUploading(true);
        try {
          const formData = new FormData();
          formData.append('file', attachmentFile);
          const uploadRes = await api.post('/media/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          attachmentUrl = uploadRes.data.url || uploadRes.data.file_url;
        } catch (uploadErr) {
          console.error('Upload failed:', uploadErr);
          toast.error('Failed to upload attachment');
          setSending(false);
          setUploading(false);
          return;
        }
        setUploading(false);
      }
      
      await api.post('/messages/send', {
        recipient_id: selectedConversation.user_id,
        content: newMessage.trim(),
        attachment_url: attachmentUrl
      });
      
      setNewMessage('');
      clearAttachment();
      fetchMessages(selectedConversation.user_id);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // SEP 8 2026 (beta round 5): pinned GEMS Official channel — web parity with
  // the mobile Messages tab. Official conversations live behind the pinned
  // gold row; the personal list below excludes them.
  const { hiddenIds: dmHidden = new Set() } = React.useContext(AuthContext) || {};
  const officialConvs = conversations.filter((c) => c.is_official);
  const personalConvs = conversations.filter((c) => !c.is_official && !dmHidden.has(c.user_id));
  const officialUnread = officialConvs.reduce((n, c) => n + (c.unread_count || 0), 0);
  const latestOfficial = officialConvs[0];
  const handleOfficialClick = () => {
    if (officialConvs.length === 1) {
      setSelectedConversation(officialConvs[0]);
      setOfficialOpen(false);
    } else {
      setOfficialOpen((prev) => !prev);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">Messages</h1>
          <Button variant="outline" onClick={() => navigate('/profile')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Profile
          </Button>
        </div>

        <div className="grid md:grid-cols-3 gap-4 h-[calc(100vh-200px)]">
          {/* Conversations List */}
          <Card className="md:col-span-1 bg-gray-800 border-gray-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Conversations</CardTitle>
              <Button 
                size="sm" 
                onClick={() => setShowNewMessageModal(true)}
                className="bg-emerald-600 hover:bg-emerald-500"
              >
                <MessageCircle className="h-4 w-4 mr-1" />
                New
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-y-auto h-[calc(100vh-320px)]">
                {/* Pinned GEMS Official channel — announcements from the verified GEMS team */}
                <div
                  onClick={handleOfficialClick}
                  data-testid="official-channel-row"
                  className="p-4 cursor-pointer transition-colors hover:bg-gray-700"
                  style={{ borderBottom: '1px solid rgba(212,175,55,0.35)', background: 'rgba(212,175,55,0.06)' }}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={BRAND_ASSETS.logo}
                      alt="GEMS"
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      style={{ border: '2px solid #D4AF37', background: '#0A0F0C' }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold" style={{ color: '#F5E9C8' }}>GEMS Official</span>
                        <CanaryBadgeIcon className="w-4 h-4" />
                      </div>
                      <p className="text-sm text-gray-400 truncate">
                        {latestOfficial?.last_message || 'Announcements, events & updates from GEMS'}
                      </p>
                    </div>
                    {officialUnread > 0 && <Badge className="bg-emerald-600">{officialUnread}</Badge>}
                    {officialConvs.length !== 1 && (
                      <span className="text-gray-500 text-xs">{officialOpen ? '▲' : '▼'}</span>
                    )}
                  </div>
                </div>
                {officialOpen && (
                  <div style={{ background: 'rgba(212,175,55,0.03)', borderBottom: '1px solid rgba(212,175,55,0.25)' }}>
                    <p className="px-4 pt-3 pb-2 text-xs text-gray-400">
                      Verified messages from the GEMS team — announcements, events, and updates.
                      We will never ask for your password here.
                    </p>
                    {officialConvs.length === 0 ? (
                      <p className="px-4 pb-3 text-sm text-gray-500">No official messages yet.</p>
                    ) : (
                      officialConvs.map((conv) => (
                        <div
                          key={conv.user_id}
                          onClick={() => { setSelectedConversation(conv); setOfficialOpen(false); }}
                          className={`px-4 py-3 cursor-pointer hover:bg-gray-700 transition-colors ${
                            selectedConversation?.user_id === conv.user_id ? 'bg-gray-700' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-sm" style={{ color: '#F5E9C8' }}>{conv.user_name}</span>
                              <p className="text-sm text-gray-400 truncate">{conv.last_message}</p>
                            </div>
                            {conv.unread_count > 0 && <Badge className="bg-emerald-600">{conv.unread_count}</Badge>}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
                {loading ? (
                  <div className="p-4 text-center text-gray-400">Loading...</div>
                ) : personalConvs.length === 0 ? (
                  <div className="p-4 text-center text-gray-400">
                    <p>No messages yet</p>
                    <p className="text-sm mt-1">Start a conversation with other Streamers!</p>
                    <Button
                      className="mt-4 bg-emerald-600 hover:bg-emerald-500"
                      onClick={() => setShowNewMessageModal(true)}
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      New Message
                    </Button>
                  </div>
                ) : (
                  personalConvs.map((conv) => (
                    <div
                      key={conv.user_id}
                      onClick={() => setSelectedConversation(conv)}
                      className={`p-4 border-b border-gray-700 cursor-pointer hover:bg-gray-700 transition-colors ${
                        selectedConversation?.user_id === conv.user_id ? 'bg-gray-700' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{conv.user_name}</span>
                            {/* Sapphire Badge - Celebrity Verified */}
                            {conv.badge_type === 'sapphire' && (
                              <SapphireBadgeIcon className="w-4 h-4" />
                            )}
                            {/* Canary Badge - GEMS Live Verified */}
                            {(conv.badge_type === 'canary' || conv.badge_type === 'verified') && (
                              <CanaryBadgeIcon className="w-4 h-4" />
                            )}
                            {conv.is_admin && (
                              <CanaryBadgeIcon className="w-4 h-4" />
                            )}
                          </div>
                          <p className="text-sm text-gray-400 truncate">{conv.last_message}</p>
                          {conv.is_broadcast && (
                            <Badge className="mt-1 bg-red-600 text-xs">Broadcast</Badge>
                          )}
                        </div>
                        {conv.unread_count > 0 && (
                          <Badge className="bg-emerald-600">{conv.unread_count}</Badge>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Message Thread */}
          <Card className="md:col-span-2 bg-gray-800 border-gray-700">
            {selectedConversation ? (
              <>
                <CardHeader className="border-b border-gray-700">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-white">{selectedConversation.user_name}</CardTitle>
                    {/* Sapphire Badge - Celebrity Verified */}
                    {selectedConversation.badge_type === 'sapphire' && (
                      <SapphireBadgeIcon className="w-5 h-5" />
                    )}
                    {/* Canary Badge - GEMS Live Verified */}
                    {(selectedConversation.badge_type === 'canary' || selectedConversation.badge_type === 'verified') && (
                      <CanaryBadgeIcon className="w-5 h-5" />
                    )}
                    {selectedConversation.is_admin && (
                      <CanaryBadgeIcon className="w-5 h-5" />
                    )}
                  </div>
                  <p className="text-sm text-gray-400">@{selectedConversation.user_handle}</p>
                </CardHeader>
                <CardContent className="p-4 flex flex-col h-[calc(100vh-400px)]">
                  {/* Messages */}
                  <div id="messages-container" className="flex-1 overflow-y-auto space-y-4 mb-4">
                    {messages.map((msg) => {
                      const isMe = msg.sender_id === user?.id;
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-lg p-3 ${
                              isMe
                                ? 'bg-emerald-600 text-white'
                                : msg.is_broadcast
                                ? 'bg-red-900 text-white'
                                : 'bg-gray-700 text-white'
                            }`}
                          >
                            {msg.is_broadcast && (
                              <div className="flex items-center gap-1 mb-1 text-xs">
                                <span className="text-red-300">GEMS Live Broadcast</span>
                              </div>
                            )}
                            {msg.attachment_url && (
                              <div className="mb-2">
                                {msg.attachment_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                  <img 
                                    src={msg.attachment_url} 
                                    alt="Attachment" 
                                    className="max-w-full rounded-lg max-h-60 object-cover cursor-pointer"
                                    onClick={() => window.open(msg.attachment_url, '_blank')}
                                  />
                                ) : (
                                  <a 
                                    href={msg.attachment_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 bg-black/20 rounded-lg p-2 text-sm hover:bg-black/30 transition-colors"
                                  >
                                    <Paperclip className="h-4 w-4" />
                                    <span className="truncate">Attachment</span>
                                  </a>
                                )}
                              </div>
                            )}
                            {msg.content && <p className="break-words whitespace-pre-line">{ownLinks(msg.content)}</p>}
                            <div className="flex items-center justify-between mt-2 text-xs opacity-70">
                              <span>{new Date(msg.created_at).toLocaleTimeString()}</span>
                              {isMe && msg.read && (
                                <span className="text-blue-300">✓✓ Read</span>
                              )}
                              {isMe && !msg.read && (
                                <span>✓ Sent</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Attachment Preview */}
                  {attachmentFile && (
                    <div className="flex items-center gap-2 bg-gray-700 rounded-lg px-3 py-2 mb-2">
                      {attachmentPreview ? (
                        <img src={attachmentPreview} alt="Preview" className="h-12 w-12 rounded object-cover" />
                      ) : (
                        <div className="h-12 w-12 rounded bg-gray-600 flex items-center justify-center">
                          <Paperclip className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{attachmentFile.name}</p>
                        <p className="text-xs text-gray-400">{(attachmentFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <button onClick={clearAttachment} className="text-gray-400 hover:text-red-400">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Input */}
                  <div className="flex gap-2">
                    <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept="image/*,.pdf,.doc,.docx,.txt,.mp4,.mov" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-gray-400 hover:text-emerald-400 px-2"
                      title="Attach file"
                      data-testid="attach-file-btn"
                    >
                      <Paperclip className="h-5 w-5" />
                    </Button>
                    <MentionBox
                      value={newMessage}
                      onChange={setNewMessage}
                      onSubmit={sendMessage}
                      placeholder="Message... (@ to mention)"
                      api={api}
                      className="flex-1"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={(!newMessage.trim() && !attachmentFile) || sending || uploading}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="send-message-btn"
                    >
                      {uploading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {newMessage.length}/500 characters
                  </p>
                </CardContent>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                Select a conversation to start messaging
              </div>
            )}
          </Card>
        </div>
      </div>
      
      {/* New Message Modal */}
      <Dialog open={showNewMessageModal} onOpenChange={setShowNewMessageModal}>
        <DialogContent className="sm:max-w-md bg-gray-800 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-emerald-500" />
              New Message
            </DialogTitle>
          </DialogHeader>
          
          {/* Search Users */}
          <div className="mb-4">
            <Input
              placeholder="Type a name or GEMS tag to search..."
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white"
              autoComplete="off"
              autoFocus
              data-testid="user-search-input"
            />
            {userSearchQuery.length > 0 && userSearchQuery.length < 2 && (
              <p className="text-xs text-gray-500 mt-1">Type at least 2 characters to search</p>
            )}
          </div>
          
          {/* Search Results */}
          <div className="max-h-80 overflow-y-auto space-y-2">
            {loadingSearch ? (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-gray-400 mt-2">Searching...</p>
              </div>
            ) : userSearchQuery.length < 2 ? (
              <div className="text-center py-8">
                <Search className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-400">Search for any user by name or GEMS tag</p>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-400">No users found for "{userSearchQuery}"</p>
              </div>
            ) : (
              searchResults.map((u) => (
                <div 
                  key={u.id}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-700 cursor-pointer transition-colors"
                  onClick={() => startNewConversation(u)}
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold overflow-hidden">
                    {u.picture ? (
                      <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
                    ) : (
                      u.name?.[0] || '?'
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium truncate">{u.name}</p>
                      {u.badge_type === 'sapphire' && <SapphireBadgeIcon className="w-4 h-4" />}
                      {(u.badge_type === 'canary' || u.badge_type === 'verified') && <CanaryBadgeIcon className="w-4 h-4" />}
                    </div>
                    <p className="text-emerald-400 text-sm">@{u.handle || 'user'}</p>
                  </div>
                  <MessageCircle className="w-5 h-5 text-gray-400" />
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== INVITATIONS PAGE ====================
const InvitationsPage = () => {
  const { user } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvitations();
    const interval = setInterval(fetchInvitations, 5000); // Poll every 5s
    return () => clearInterval(interval);
  }, []);

  const fetchInvitations = async () => {
    try {
      const response = await api.get('/invitations/pending');
      setInvitations(response.data.invitations || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching invitations:', error);
      setLoading(false);
    }
  };

  const acceptInvitation = async (invitationId, streamId) => {
    try {
      await api.post(`/invitations/accept/${invitationId}`);
      toast.success('Invitation accepted! Joining linked session...');
      // Navigate to live page and start stream
      navigate('/live', { state: { joinStreamId: streamId } });
    } catch (error) {
      toast.error('Failed to accept invitation');
    }
  };

  const declineInvitation = async (invitationId) => {
    try {
      await api.post(`/invitations/decline/${invitationId}`);
      toast.info('Invitation declined');
      fetchInvitations();
    } catch (error) {
      toast.error('Failed to decline invitation');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">Stream Invitations</h1>
          <Button variant="outline" onClick={() => navigate('/profile')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-400">Loading invitations...</div>
          </div>
        ) : invitations.length === 0 ? (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-12 text-center">
              <Users className="h-16 w-16 mx-auto mb-4 text-gray-600" />
              <h2 className="text-xl font-semibold mb-2">No Pending Invitations</h2>
              <p className="text-gray-400">When someone invites you to join their stream, it will appear here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {invitations.map((inv) => (
              <Card key={inv.id} className="bg-gray-800 border-gray-700 hover:border-emerald-500 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xl">
                          {inv.inviter_name?.charAt(0)}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold">{inv.inviter_name}</h3>
                          <p className="text-gray-400">@{inv.inviter_handle}</p>
                        </div>
                      </div>
                      <p className="text-gray-300 mb-2">
                        invited you to join their stream: <span className="font-semibold text-emerald-400">{inv.stream_title}</span>
                      </p>
                      <p className="text-sm text-gray-500">
                        Expires: {new Date(inv.expires_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => acceptInvitation(inv.id, inv.stream_id)}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Accept & Go Live
                      </Button>
                      <Button
                        onClick={() => declineInvitation(inv.id)}
                        variant="outline"
                        className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Decline
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      {/* Profile Picture Modal */}
      <ProfilePictureModal 
        imageUrl={profile?.picture || profile?.profile_pic} 
        isOpen={showPicModal}
        onClose={() => setShowPicModal(false)}
        userName={profile?.name}
      />
    </div>
  );
};



// Import the proper Spades game engine
import { SpadesGameEngine } from './SpadesGameEngine.js';
import AgoraVideoGrid from './AgoraVideoGrid.js';
import AgoraStreamViewer from './AgoraStreamViewer.js';
import AgoraStreamPublisher from './AgoraStreamPublisher.js';
import CardPlayArea from './CardPlayArea.js';

const GameRoomPage = () => {
  const { gameId } = useParams();
  const { user } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirectTo, setRedirectTo] = useState(null); // For game type redirects
  const [gameEngine, setGameEngine] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [selectedCardIndex, setSelectedCardIndex] = useState(-1);
  const [wagerStatus, setWagerStatus] = useState(null);
  const [showWagerAcceptModal, setShowWagerAcceptModal] = useState(false);
  const [blindWindowTimer, setBlindWindowTimer] = useState(0);
  const [selectedBlindBid, setSelectedBlindBid] = useState(null);
  const [showHandScoreModal, setShowHandScoreModal] = useState(false);
  const [showMatchCompleteModal, setShowMatchCompleteModal] = useState(false);
  
  // Agora livestreaming state
  const [agoraToken, setAgoraToken] = useState(null);
  const [agoraChannel, setAgoraChannel] = useState(null);
  const [agoraAppId, setAgoraAppId] = useState(null);
  const [showVideoGrid, setShowVideoGrid] = useState(false);
  // Link v2: LiveKit game room ({token, roomName}) when the flag is on.
  const [linkGameRoomInfo, setLinkGameRoomInfo] = useState(null);
  
  // Card animation state
  const [animatingCard, setAnimatingCard] = useState(null);
  
  // Prize distribution state
  const [prizeDistributions, setPrizeDistributions] = useState(null);
  const [gameCompleting, setGameCompleting] = useState(false);
  
  // Leave game confirmation state
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  
  // WebSocket state
  const [ws, setWs] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);

  // WebSocket connection for multiplayer sync
  const connectWebSocket = (gameId, userId) => {
    try {
      const backendUrl = API_BASE_URL || 'http://localhost:8001';
      const wsUrl = backendUrl.replace('http', 'ws').replace('https', 'wss');
      const socket = new WebSocket(`${wsUrl}/ws/game/${gameId}?user_id=${userId}`);
      
      socket.onopen = () => {
        console.log('✅ WebSocket connected to game room');
        setWsConnected(true);
      };
      
      socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log('WebSocket message received:', data);
        
        handleWebSocketMessage(data);
      };
      
      socket.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setWsConnected(false);
      };
      
      socket.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
      };
      
      setWs(socket);
      
      return () => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.close();
        }
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  };
  
  // Handle incoming WebSocket messages
  const handleWebSocketMessage = (data) => {
    const { type } = data;
    
    switch (type) {
      case 'player_joined':
        console.log(`Player ${data.user_id} joined the game`);
        toast.info(`Player joined the game`);
        break;
      
      case 'player_left':
        console.log(`Player ${data.user_id} left the game`);
        toast.warning(`Player left the game`);
        break;
      
      case 'player_disconnected':
        // Player disconnected during active game - forfeit
        console.log(`Player ${data.player_name} disconnected. Team ${data.winning_team + 1} wins by forfeit!`);
        toast.error(`${data.player_name} lost connection!`, { duration: 5000 });
        toast.success(`Team ${data.winning_team + 1} wins by forfeit!`, { duration: 5000 });
        break;
      
      case 'game_complete_forfeit':
        // Game ended by forfeit
        console.log('Game completed by forfeit:', data);
        setPrizeDistributions(data.prize_distributions);
        setShowMatchCompleteModal(true);
        toast.warning('Game ended - player disconnected');
        break;
      
      case 'card_played':
        // Another player played a card - sync the animation
        console.log(`Player at seat ${data.seat} played card:`, data.card);
        
        // Trigger card animation for remote player
        setAnimatingCard({ seat: data.seat, cardIndex: data.card_index });
        setTimeout(() => setAnimatingCard(null), 600);
        
        // Update game state if we have the engine
        if (gameEngine) {
          const newState = gameEngine.getGameState();
          setGameState(newState);
        }
        break;
      
      case 'bid_submitted':
        console.log(`Player at seat ${data.seat} bid: ${data.bid}${data.is_blind ? ' (BLIND)' : ''}`);
        toast.info(`Seat ${data.seat} bid ${data.bid}${data.is_blind ? ' BLIND' : ''}`);
        
        // Update game state
        if (gameEngine) {
          const newState = gameEngine.getGameState();
          setGameState(newState);
        }
        break;
      
      case 'game_state_update':
        console.log('Received full game state update');
        setGameState(data.game_state);
        break;
      
      case 'turn_change':
        console.log(`Turn changed to player ${data.current_player}`);
        break;
      
      case 'trick_complete':
        console.log(`Book won by seat ${data.winner_seat}`);
        break;
      
      case 'chat_message':
        console.log(`Chat from ${data.user_id}: ${data.message}`);
        toast(`💬 ${data.message}`);
        break;
      
      case 'game_started':
        console.log('Game started by host:', data.started_by);
        toast.success('Game is starting! Dealing cards...', { duration: 3000 });
        // Trigger game initialization for non-host players
        if (gameEngine && data.started_by !== user?.id) {
          gameEngine.startNewHand();
          const newState = gameEngine.getGameState();
          setGameState(newState);
          startCardDealingAnimation(gameEngine);
        }
        break;
      
      default:
        console.log('Unknown WebSocket message type:', type);
    }
  };
  
  // Send WebSocket message
  const sendWebSocketMessage = (message) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(message));
      console.log('Sent WebSocket message:', message);
    } else {
      console.warn('WebSocket not connected, cannot send message');
    }
  };
  
  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [ws]);

  // Handle game completion and prize distribution
  const handleGameCompletion = async () => {
    if (gameCompleting || !gameState) return;
    
    setGameCompleting(true);
    
    try {
      // Determine winning team
      const team0Score = gameState.matchScores?.[0] || 0;
      const team1Score = gameState.matchScores?.[1] || 0;
      const team0Sets = gameState.teamSetCount?.[0] || 0;
      const team1Sets = gameState.teamSetCount?.[1] || 0;
      
      let winningTeam;
      if (team0Sets >= 3) {
        winningTeam = 1; // Team 1 wins if Team 0 got 3 SETs
      } else if (team1Sets >= 3) {
        winningTeam = 0; // Team 0 wins if Team 1 got 3 SETs
      } else {
        winningTeam = team0Score > team1Score ? 0 : 1;
      }
      
      // Call backend to complete game and distribute prizes
      const response = await api.post(`/game/${gameId}/complete`, {
        winningTeam,
        finalScores: [team0Score, team1Score]
      });
      
      setPrizeDistributions(response.data.distributions);
      console.log('Prize distributions:', response.data);
      
      // Show match complete modal
      setShowMatchCompleteModal(true);

      // Fire-and-forget: capture the match-complete overlay as dispute
      // evidence. LIVE games only — practice/bot tables skip.
      captureAndUploadGameResult({
        gameId,
        gameType: 'spades',
        selector: '[data-testid="spades-match-complete-modal"]',
        isPractice: Boolean(game?.practice_mode) || (game?.players || []).some(p => p?.is_bot),
        winnerId: (game?.players || []).find(p => (p?.team ?? p?.seat % 2) === winningTeam)?.id || null,
        resultSummary: `Team ${winningTeam} won ${team0Score}-${team1Score}`,
        wager: Number(game?.wager_amount) || 0,
        participants: (game?.players || []).map(p => ({
          user_id: p?.id,
          name: p?.name,
          handle: p?.handle,
        })),
      });
      
    } catch (error) {
      console.error('Error completing game:', error);
      toast.error('Failed to process game completion');
      // Still show modal even if backend fails
      setShowMatchCompleteModal(true);
    } finally {
      setGameCompleting(false);
    }
  };

  // Card dealing animation function
  const startCardDealingAnimation = (engine) => {
    console.log('🎴 Starting card dealing animation...');
    
    // Show dealing progress without revealing cards
    let cardIndex = 0;
    const totalCards = 52; // 13 cards per player × 4 players
    const dealingInterval = setInterval(() => {
      const playerIndex = cardIndex % 4;
      const cardNumber = Math.floor(cardIndex / 4) + 1;
      
      // Show dealing progress (cards are face-down during this phase)
      if (cardNumber <= 13 && cardNumber % 3 === 1) { // Only show every 3rd card to reduce spam
        toast.info(`Dealing cards... (${Math.floor(cardIndex / 4) + 1}/13)`, {
          duration: 400
        });
      }
      
      cardIndex++;
      
      // After all cards are dealt (52 cards total)
      if (cardIndex >= totalCards) {
        clearInterval(dealingInterval);
        
        // After dealing, check if blind window should open (team down 100+ points)
        setTimeout(() => {
          // Check if any team qualifies for blind bids (down 100+ points)
          const team0CanBlind = engine.canTeamUseBlinds(0);
          const team1CanBlind = engine.canTeamUseBlinds(1);
          
          // Hand 1 never has blind window (no score difference yet)
          // For other hands, check if blind window conditions are met
          if (engine.currentHand > 1 && (team0CanBlind || team1CanBlind)) {
            console.log('🎯 Blind window conditions met! Opening blind bid window...');
            engine.startBlindWindow();
            const blindState = engine.getGameState();
            setGameState(blindState);
            
            // Show notification about blind opportunity
            if (team0CanBlind) {
              toast.info('Your team is down 100+ points! You can declare a BLIND BID for double points!', {
                duration: 5000
              });
            } else {
              toast.info('Opponent team can declare blind bids (they are down 100+ points)', {
                duration: 3000
              });
            }
          } else {
            // No blind window - go directly to bidding
            engine.startBiddingPhase();
            const updatedState = engine.getGameState();
            setGameState(updatedState);
            
            // Check if Hand 1 (bid itself - no bidding)
            if (updatedState.currentHand === 1) {
              toast.success('Hand 1: Bid Itself! No bidding - just play! You lead the first book.');
              // Hand 1 skips bidding and goes straight to playing
              setTimeout(() => {
                // User leads first book in Hand 1
                if (updatedState.leaderSeat !== 0) {
                  simulateNextBotPlay();
                }
              }, 1500);
            } else {
              toast.success('All cards dealt! Look at your hand and make your bid!');
              
              // Start bot bidding if first bidder is a bot
              const firstBidder = engine.getCurrentBidder();
              if (firstBidder > 0) {
                setTimeout(() => {
                  simulateNextBotBid();
                }, 1000);
              }
            }
          }
        }, 1000);
      }
    }, 150); // Deal a card every 150ms for faster, more realistic timing
  };

  useEffect(() => {
    const fetchGame = async () => {
      try {
        const response = await api.get(`/game/${gameId}`);
        const gameData = response.data;
        
        // Check if this is a redirect to a different game type (e.g., 4 In A Row)
        if (gameData.redirect) {
          console.log('🔄 Redirecting to:', gameData.redirect_to);
          toast.info(gameData.message || 'Redirecting to correct game...');
          setRedirectTo(gameData.redirect_to);
          setLoading(false);
          return;
        }
        
        setGame(gameData);
        
        // Initialize Spades game engine with match settings from game data
        const engine = new SpadesGameEngine(user?.name || 'You');
        if (gameData.match_settings) {
          engine.matchSettings = gameData.match_settings;
        }
        setGameEngine(engine);
        
        // Connect to WebSocket for multiplayer games (non-practice)
        if (!gameData.practice_mode && user?.id) {
          connectWebSocket(gameId, user.id);
        }
        
        // Fetch Agora token for livestreaming (only for non-practice games)
        if (!gameData.practice_mode) {
          try {
            if (LIVEKIT_LINKS) {
              // Link v2: publish player video into the shared LiveKit game room.
              const roomName = `game_${gameId}`;
              const lk = await api.post('/livekit/token', {
                roomName, isPublisher: true, sessionType: 'game_player',
              });
              setLinkGameRoomInfo({ token: lk.data.token, roomName: lk.data.roomName || roomName, url: lk.data.url || null });
              setShowVideoGrid(true);
              console.log('✅ LiveKit game_player token received');
            } else {
              const agoraResponse = await api.post('/agora/token', {
                channelName: `game_${gameId}`,
                uid: uidFromUserId(user?.id),
                role: 'publisher',
                sessionType: 'multiplayer_game'
              });
              setAgoraToken(agoraResponse.data.token);
              setAgoraChannel(agoraResponse.data.channelName);
              setAgoraAppId(agoraResponse.data.appId);
              setShowVideoGrid(true);
              console.log('✅ Agora token received for livestreaming');
            }
          } catch (error) {
            console.error('❌ Failed to get video token:', error);
            // Continue without livestreaming if token fetch fails
          }
        }
        
        // Handle practice games differently
        if (gameData.practice_mode) {
          console.log('Initializing practice game with bots...');
          // Practice games always start fresh - reset to hand 1
          engine.currentHand = 1;
          engine.matchScores = [0, 0];
          engine.totalBags = [0, 0];
          engine.teamSetCount = [0, 0];
          
          // Practice games start immediately - no wager acceptance needed
          engine.startNewHand();
          setGameState(engine.getGameState());
          
          toast.success('Practice game started! Watch the cards being dealt...');
          
          // Start card dealing animation
          startCardDealingAnimation(engine);
          
          return; // Exit early for practice games
        }
        
        // Handle regular games with wager system
        let wagerResponse = null;
        try {
          wagerResponse = await api.get(`/game/${gameId}/entry-status`);
          setWagerStatus(wagerResponse.data);
          
          // Show wager accept modal if wager exists and user hasn't accepted
          if (wagerResponse.data.wager_amount > 0 && !wagerResponse.data.wager_locked) {
            const userAccepted = wagerResponse.data.players_status.find(p => p.id === user?.id)?.accepted;
            if (!userAccepted) {
              setShowWagerAcceptModal(true);
            }
          }
          
          // Start game only if wager is accepted by all players or no wager
          if (wagerResponse.data.all_accepted || wagerResponse.data.wager_amount === 0) {
            engine.startNewHand();
            setGameState(engine.getGameState());
            
            // Start card dealing animation
            startCardDealingAnimation(engine);
          } else {
            // Set waiting state - game won't start until wager is accepted
            setGameState({
              gameState: 'WAITING_FOR_WAGER',
              players: engine.players,
              currentTrick: [],
              leaderSeat: 1,
              spadesBroken: false,
              bids: [0, 0, 0, 0],
              teamBids: [0, 0],
              booksWon: [0, 0],
              matchScores: [0, 0],
              handScores: [0, 0],
              currentBook: 0,
              matchSettings: engine.matchSettings,
              teamRemainingBlinds: [3, 3],
              aceCheckDeclared: [false, false],
              blindBids: [
                { active: false, value: 0 },
                { active: false, value: 0 },
                { active: false, value: 0 },
                { active: false, value: 0 }
              ]
            });
          }
        } catch (error) {
          console.log('No entry information available - starting game directly');
          // If no wager info, just start the game
          engine.startNewHand();
          setGameState(engine.getGameState());
          
          // Start card dealing animation
          startCardDealingAnimation(engine);
        }
        
      } catch (error) {
        console.error('Failed to fetch game:', error);
        toast.error('Failed to load game');
      } finally {
        setLoading(false);
      }
    };

    if (gameId) {
      fetchGame();
    }
  }, [gameId, user?.id]);

  // Handle redirect to different game type
  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo);
    }
  }, [redirectTo, navigate]);

  // Timer for blind window
  useEffect(() => {
    let interval;
    if (gameState?.blindWindowActive) {
      interval = setInterval(() => {
        const remaining = gameEngine?.getBlindWindowTimeRemaining() || 0;
        setBlindWindowTimer(Math.ceil(remaining / 1000));
        
        if (remaining <= 0) {
          // Blind window closed - transition to bidding
          clearInterval(interval);
          gameEngine.closeBlindWindow();
          const newState = gameEngine.getGameState();
          setGameState(newState);
          
          toast.success('Blind window closed! Time to bid!');
          
          // Start bot bidding if first bidder is a bot
          const firstBidder = gameEngine.getCurrentBidder();
          if (firstBidder > 0) {
            setTimeout(() => {
              simulateNextBotBid();
            }, 1000);
          }
        }
      }, 100);
    }
    
    return () => clearInterval(interval);
  }, [gameState?.blindWindowActive, gameEngine]);

  // Detect when hand scoring phase begins
  useEffect(() => {
    if (gameState?.gameState === 'HAND_SCORING') {
      // Check if match is complete before showing hand score modal
      const isMatchComplete = 
        gameState.matchScores?.[0] >= 500 || 
        gameState.matchScores?.[1] >= 500 ||
        gameState.teamSetCount?.[0] >= 3 || 
        gameState.teamSetCount?.[1] >= 3;
      
      // Check if this is a practice game
      const isPracticeMode = game?.practice_mode === true;
      
      if (isMatchComplete) {
        // Match is complete - trigger prize distribution for non-practice games
        if (!isPracticeMode) {
          handleGameCompletion();
        } else {
          // Practice game - just show winner modal
          setShowMatchCompleteModal(true);
        }
      } else {
        // Regular hand - show hand score modal
        setShowHandScoreModal(true);
      }
    } else if (gameState?.gameState === 'MATCH_COMPLETE') {
      // Check if this is a practice game
      const isPracticeMode = game?.practice_mode === true;
      
      // Show match complete modal immediately
      if (!isPracticeMode) {
        handleGameCompletion();
      } else {
        setShowMatchCompleteModal(true);
      }
    }
  }, [gameState?.gameState, gameState?.matchScores, gameState?.teamSetCount, game?.practice_mode]);

  // ========================================================
  // NAVIGATION PROTECTION: Prevent accidental exit during game
  // Shows confirmation prompt if user tries to refresh/close/navigate
  // ========================================================
  useEffect(() => {
    // Only apply protection for practice games with active gameplay
    const isPracticeMode = game?.practice_mode === true;
    const isActive = gameState && gameState.gameState !== 'MATCH_COMPLETE' && !showMatchCompleteModal;
    
    if (!isPracticeMode || !isActive) return;
    
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'You have an active game. Leaving will forfeit your match. Are you sure?';
      return e.returnValue;
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [game?.practice_mode, gameState, showMatchCompleteModal]);

  const handleContinueToNextHand = () => {
    setShowHandScoreModal(false);
    
    // Note: Match complete modal now appears automatically before hand score modal
    // So this function should only be called for regular hands, not match completion
    
    // Check for 3 SETs auto-loss
    if (gameState.teamSetCount[0] >= 3) {
      toast.success(`🎉 MATCH COMPLETE! Team 2 wins! (Team 1 set 3 times)`);
      return;
    }
    if (gameState.teamSetCount[1] >= 3) {
      toast.success(`🎉 MATCH COMPLETE! Team 1 wins! (Team 2 set 3 times)`);
      return;
    }
    
    // Start next hand
    if (gameEngine) {
      console.log('Starting next hand. Current state before:', gameEngine.gameState);
      gameEngine.startNewHand();
      const updatedState = gameEngine.getGameState();
      setGameState(updatedState);
      console.log('After startNewHand. New state:', updatedState.gameState, 'Hand:', updatedState.currentHand);
      toast.success(`Hand ${updatedState.currentHand} starting...`);
      
      // Start card dealing animation for next hand
      startCardDealingAnimation(gameEngine);
    }
  };

  const handleBid = (bidAmount) => {
    // Check engine state directly, not React state
    const currentEngineState = gameEngine?.getGameState();
    if (!gameEngine || currentEngineState?.gameState !== 'BIDDING') {
      console.log('handleBid: Cannot bid - gameEngine:', !!gameEngine, 'state:', currentEngineState?.gameState);
      return;
    }
    
    const success = gameEngine.placeBid(0, bidAmount); // Player is seat 0
    const newState = gameEngine.getGameState();
    setGameState(newState);
    
    if (success) {
      toast.success(`You bid ${bidAmount}`);
      
      console.log('After player bid - Game state:', newState.gameState, 'All bids:', newState.bids);
      
      // Check if bidding is complete immediately
      if (newState.gameState === 'PLAYING') {
        // Announce who leads first book
        const firstPlayer = newState.players[newState.leaderSeat];
        if (newState.leaderSeat === 0) {
          toast.success(`Bidding complete! You lead the first book. Play a card!`);
        } else {
          toast.success(`Bidding complete! ${firstPlayer.name} leads the first book.`);
        }
        setTimeout(() => {
          simulateNextBotPlay();
        }, 1000);
      } else {
        // Continue with bot bidding
        setTimeout(() => {
          simulateNextBotBid();
        }, 1000);
      }
    } else {
      // Check if misdeal occurred (all bids placed but invalid)
      if (newState.gameState === 'BIDDING' && newState.bids.every(b => b === 0)) {
        toast.error('MISDEAL! Bids do not meet minimum requirements. Each team must bid ≥4, table total ≥11. Please bid again!', {
          duration: 5000
        });
        // Restart bidding sequence after short delay
        setTimeout(() => {
          simulateNextBotBid();
        }, 2000);
      }
    }
  };

  const simulateNextBotBid = () => {
    // Check engine state directly, not React state (which is async and may be stale)
    const currentEngineState = gameEngine?.getGameState();
    if (!gameEngine || currentEngineState?.gameState !== 'BIDDING') {
      console.log('simulateNextBotBid: Cannot bid - gameEngine:', !!gameEngine, 'gameState:', currentEngineState?.gameState);
      return;
    }
    
    // Get current bidder using proper turn order
    const currentBidder = gameEngine.getCurrentBidder();
    console.log('simulateNextBotBid: currentBidder =', currentBidder, 'Bids so far:', gameEngine.bids);
    
    // Only simulate if it's a bot's turn (not player 0)
    if (currentBidder > 0 && currentBidder < 4) {
      const botBid = Math.floor(Math.random() * 4) + 2; // Bids 2-5
      console.log(`Bot ${currentBidder} (${currentEngineState.players[currentBidder]?.name}) placing bid: ${botBid}`);
      gameEngine.placeBid(currentBidder, botBid);
      
      // Update React state after bid
      const newState = gameEngine.getGameState();
      setGameState(newState);
      
      toast.info(`${newState.players[currentBidder]?.name || `Player ${currentBidder + 1}`} bids ${botBid}`);
      
      // Schedule next bot bid if game is still in bidding phase
      setTimeout(() => {
        const updatedEngineState = gameEngine.getGameState();
        if (updatedEngineState.gameState === 'BIDDING') {
          simulateNextBotBid();
        } else if (updatedEngineState.gameState === 'PLAYING') {
          // All bids placed, game is now in playing phase
          const firstPlayer = updatedEngineState.players[updatedEngineState.leaderSeat];
          if (updatedEngineState.leaderSeat === 0) {
            toast.success(`Bidding complete! You lead the first book. Play a card!`);
          } else {
            toast.success(`Bidding complete! ${firstPlayer.name} leads the first book.`);
            // Start bot play if bot leads
            setTimeout(() => {
              simulateNextBotPlay();
            }, 1000);
          }
        }
      }, 1500);
    } else if (currentBidder === 0) {
      console.log('simulateNextBotBid: Current bidder is player 0 (you) - waiting for your bid');
    } else {
      console.log('simulateNextBotBid: Invalid currentBidder:', currentBidder);
    }
    
    // Check if bidding is complete after this bid
    const currentState = gameEngine.getGameState();
    if (currentState.gameState === 'PLAYING') {
      const firstPlayer = currentState.players[currentState.leaderSeat];
      if (currentState.leaderSeat === 0) {
        toast.success(`Bidding complete! You lead the first book. Play a card!`);
      } else {
        toast.success(`Bidding complete! ${firstPlayer.name} leads the first book.`);
        // Start bot play if bot leads  
        setTimeout(() => {
          simulateNextBotPlay();
        }, 1000);
      }
    }
  };

  const handleBlindBid = (blindValue) => {
    if (!gameEngine || gameState?.gameState !== 'BLIND_WINDOW') return;
    
    const success = gameEngine.declareBlind(0, blindValue); // Player is seat 0
    if (success) {
      toast.success(`🎯 Declared Blind ${blindValue}! Double points if you make it!`);
      
      // Close blind window and transition to bidding
      gameEngine.closeBlindWindow();
      const newState = gameEngine.getGameState();
      setGameState(newState);
      
      toast.info('Cards revealed! Your blind bid is locked at ' + blindValue);
      
      // Start bot bidding if first bidder is a bot
      const firstBidder = gameEngine.getCurrentBidder();
      if (firstBidder > 0) {
        setTimeout(() => {
          simulateNextBotBid();
        }, 1000);
      }
    }
  };

  const handleDeclineBlind = () => {
    if (!gameEngine || gameState?.gameState !== 'BLIND_WINDOW') return;
    
    const success = gameEngine.declineBlind(0);
    if (success) {
      // Close the blind window and transition to bidding immediately
      gameEngine.closeBlindWindow();
      const newState = gameEngine.getGameState();
      setGameState(newState);
      toast.info('Skipped blind bid - your cards are now revealed!');
      
      // Start bot bidding if first bidder is a bot
      const firstBidder = gameEngine.getCurrentBidder();
      if (firstBidder > 0) {
        setTimeout(() => {
          simulateNextBotBid();
        }, 1000);
      }
    }
  };

  const handleAceCheck = (team) => {
    if (!gameEngine || gameState?.gameState !== 'BIDDING') return;
    
    const success = gameEngine.declareAceCheck(team);
    if (success) {
      setGameState(gameEngine.getGameState());
      toast.success('Ace Check declared!');
    }
  };

  const handlePlayCard = (cardIndex) => {
    if (!gameEngine || gameState?.gameState !== 'PLAYING') {
      console.log('Cannot play card - game not in PLAYING state:', gameState?.gameState);
      toast.error(`Game not ready for card play. Current state: ${gameState?.gameState}`);
      return;
    }
    
    // Calculate whose turn it is based on trick progress (clockwise from player view: 0->3->2->1)
    const currentTrickLength = gameState.currentTrick ? gameState.currentTrick.length : 0;
    // Clockwise visual progression: 0(you) -> 3(left) -> 2(top) -> 1(right)
    const currentPlayerTurn = currentTrickLength === 0 ? 
      gameState.leaderSeat : 
      (gameState.leaderSeat - currentTrickLength + 4) % 4;
    
    console.log('DEBUG Turn Calculation:', {
      leaderSeat: gameState.leaderSeat,
      currentTrickLength: currentTrickLength,
      calculatedTurn: currentPlayerTurn,
      humanPlayer: 0
    });
    
    // Only allow human player (seat 0) to play on their turn
    if (currentPlayerTurn !== 0) {
      toast.info(`Wait for your turn. Current player: ${gameState.players[currentPlayerTurn]?.name} (Player ${currentPlayerTurn + 1})`);
      console.log('Not player turn:', currentPlayerTurn, 'Expected: 0');
      return;
    }
    
    // Get the card BEFORE playing it (since hand changes after play)
    const cardToPlay = gameState.players[0].hand[cardIndex];
    console.log('Attempting to play card at index:', cardIndex, 'Card:', cardToPlay);
    
    if (!cardToPlay) {
      console.error('No card at index:', cardIndex);
      toast.error('Invalid card selection');
      return;
    }
    
    const success = gameEngine.playCard(0, cardIndex); // Player is seat 0
    if (success) {
      console.log('Card played successfully!');
      const newState = gameEngine.getGameState();
      
      // Trigger card animation
      setAnimatingCard({ seat: 0, cardIndex: newState.currentTrick.length - 1 });
      setTimeout(() => setAnimatingCard(null), 600); // Clear after animation
      
      setGameState(newState);
      setSelectedCardIndex(-1);
      
      // Send card play to other players via WebSocket (if multiplayer)
      if (!isPracticeGame && wsConnected) {
        sendWebSocketMessage({
          type: 'card_played',
          seat: 0,
          card: cardToPlay,
          card_index: newState.currentTrick.length - 1
        });
      }
      
      toast.success(`You played ${cardToPlay.value}${cardToPlay.suit}!`);
      
      // Check if trick was completed by this card
      if (newState.lastCompletedTrick && newState.lastCompletedTrick.length === 4) {
        const winnerSeat = newState.leaderSeat; // After resolution, leaderSeat is updated to winner
        const winnerName = newState.players[winnerSeat]?.name || `Player ${winnerSeat + 1}`;
        setTimeout(() => {
          toast.success(`Book complete! Winner: ${winnerName}`);
        }, 500);
        
        // Check if hand is complete
        if (newState.gameState === 'HAND_SCORING') {
          // Hand complete - scores will be shown via modal
          return;
        }
        
        // Continue with next book after delay
        setTimeout(() => {
          simulateNextBotPlay();
        }, 2500);
      } else {
        // Wait before allowing next bot to play
        setTimeout(() => {
          simulateNextBotPlay();
        }, 1500);
      }
    } else {
      console.log('Failed to play card - rule violation');
      // Determine why the card is invalid
      const currentTrick = gameState.currentTrick;
      if (currentTrick && currentTrick.length > 0) {
        const leadSuit = currentTrick[0].card.suit;
        const hasLeadSuit = gameState.players[0].hand.some(c => c.suit === leadSuit);
        if (hasLeadSuit && cardToPlay.suit !== leadSuit) {
          toast.error(`Must follow suit! You have ${leadSuit} cards - you must play ${leadSuit} if you have it.`);
        } else {
          toast.error('Cannot play that card. Check Spades rules.');
        }
      } else {
        toast.error('Cannot play that card. Check Spades rules.');
      }
    }
  };

  const simulateNextBotPlay = () => {
    if (!gameEngine) return;
    
    const currentState = gameEngine.getGameState();
    
    // Handle different game states
    if (currentState.gameState === 'BIDDING') {
      // Handle bot bidding
      const currentBidder = gameEngine.getCurrentBidder();
      if (currentBidder > 0 && currentBidder < 4) {
        const botBid = Math.floor(Math.random() * 4) + 2; // Bids 2-5
        gameEngine.placeBid(currentBidder, botBid);
        setGameState(gameEngine.getGameState());
        
        toast.info(`${currentState.players[currentBidder]?.name || `Player ${currentBidder + 1}`} bids ${botBid}`);
        
        // Continue bidding or transition to playing
        setTimeout(() => {
          const newState = gameEngine.getGameState();
          console.log('After bot bid - Game state:', newState.gameState, 'All bids:', newState.bids);
          
          if (newState.gameState === 'PLAYING') {
            // Announce who leads first book
            const firstPlayer = newState.players[newState.leaderSeat];
            if (newState.leaderSeat === 0) {
              toast.success(`Bidding complete! You lead the first book. Play a card!`);
            } else {
              toast.success(`Bidding complete! ${firstPlayer.name} leads the first book.`);
            }
            
            // Make sure state is updated
            setGameState(newState);
            
            // Start first book - determine who leads
            setTimeout(() => {
              simulateNextBotPlay();
            }, 1000);
          } else {
            simulateNextBotBid();
          }
        }, 1500);
      }
      return;
    }
    
    if (currentState.gameState !== 'PLAYING') return;
    
    // Calculate whose turn it is based on trick progress
    const currentTrickLength = currentState.currentTrick ? currentState.currentTrick.length : 0;
    // Clockwise visual progression: 0(you) -> 3(left) -> 2(top) -> 1(right)
    const currentPlayerTurn = currentTrickLength === 0 ?
      currentState.leaderSeat :
      (currentState.leaderSeat - currentTrickLength + 4) % 4;
    
    console.log('Bot Play DEBUG:', {
      leaderSeat: currentState.leaderSeat,
      trickLength: currentTrickLength,
      currentTurn: currentPlayerTurn,
      isHumanTurn: currentPlayerTurn === 0
    });
    
    // If it's human player's turn, don't auto-play
    if (currentPlayerTurn === 0) {
      toast.info('Your turn to play a card!');
      return;
    }
    
    // Play card for current bot
    const currentPlayer = gameEngine.players[currentPlayerTurn];
    if (currentPlayer && currentPlayer.hand.length > 0) {
      let cardPlayed = false;
      let selectedCardIndex = -1;
      
      // Smart bot logic - especially for partner (seat 2)
      const isPartner = currentPlayerTurn === 2; // Seat 2 is user's partner
      const currentTrick = currentState.currentTrick || [];
      
      if (isPartner && currentTrick.length > 0) {
        // Partner should play smart - don't cut partner's winning card
        const userSeat = 0;
        const userPlayedInTrick = currentTrick.some(play => play.playerSeat === userSeat);
        
        if (userPlayedInTrick) {
          // Determine if user is currently winning the trick
          const gameEngine_internal = gameEngine; // Access to check trump ranks
          let highestRank = -1;
          let winnerSoFar = null;
          let highestCard = null;
          
          currentTrick.forEach((play) => {
            if (!play || !play.card) return; // Safety check
            
            const card = play.card;
            let rank = card.trumpRank || 0;
            
            // Check if non-trump but following suit
            if (rank === 0) {
              const leadCard = currentTrick[0]?.card;
              if (!leadCard) return; // Safety check
              
              const leadIsTrump = leadCard.suit === '♠' || (leadCard.suit === '♦' && leadCard.value === '2');
              
              if (!leadIsTrump && card.suit === leadCard.suit && !(card.suit === '♦' && card.value === '2')) {
                const valueRanks = { 'A': 14, 'K': 13, 'Q': 12, 'J': 11, '10': 10, '9': 9, '8': 8, '7': 7, '6': 6, '5': 5, '4': 4, '3': 3, '2': 2 };
                rank = valueRanks[card.value] || 0;
              }
            }
            
            if (rank > highestRank) {
              highestRank = rank;
              winnerSoFar = play.playerSeat;
              highestCard = card;
            }
          });
          
          // If user is winning, partner should slough (play lowest card)
          if (winnerSoFar === userSeat) {
            console.log('Partner AI: User is winning, will slough low card');
            
            // Try to play lowest valid card
            for (let i = currentPlayer.hand.length - 1; i >= 0; i--) {
              const cardBeingPlayed = currentPlayer.hand[i]; // Save card reference BEFORE playing
              const success = gameEngine.playCard(currentPlayerTurn, i);
              if (success) {
                cardPlayed = true;
                const newGameState = gameEngine.getGameState();
                
                // Trigger card animation for bot
                setAnimatingCard({ seat: currentPlayerTurn, cardIndex: newGameState.currentTrick.length - 1 });
                setTimeout(() => setAnimatingCard(null), 600);
                
                setGameState(newGameState);
                toast.info(`${currentPlayer.name} plays ${cardBeingPlayed.value}${cardBeingPlayed.suit}`);
                break;
              }
            }
          } else {
            // User is NOT winning - check if partner can win or should slough
            // Advanced logic: Don't waste high cards trying to beat opponent's ace
            console.log('Partner AI: User not winning, evaluating if we can/should win');
            
            const leadCard = currentTrick[0]?.card;
            const leadIsTrump = leadCard && (leadCard.suit === '♠' || (leadCard.suit === '♦' && leadCard.value === '2'));
            const leadSuit = leadCard?.suit;
            
            // Check if opponent has an unbeatable card (Ace or close to it)
            let shouldSlough = false;
            if (!leadIsTrump && highestCard) {
              const valueRanks = { 'A': 14, 'K': 13, 'Q': 12, 'J': 11, '10': 10, '9': 9, '8': 8, '7': 7, '6': 6, '5': 5, '4': 4, '3': 3, '2': 2 };
              const highestValue = valueRanks[highestCard.value] || 0;
              
              // If opponent has Ace, don't waste King
              if (highestValue === 14) {
                shouldSlough = true;
                console.log('Partner AI: Opponent has Ace, will save high cards');
              }
            }
            
            if (shouldSlough) {
              // Try to slough low card
              for (let i = currentPlayer.hand.length - 1; i >= 0; i--) {
                const cardBeingPlayed = currentPlayer.hand[i];
                const success = gameEngine.playCard(currentPlayerTurn, i);
                if (success) {
                  cardPlayed = true;
                  const newGameState = gameEngine.getGameState();
                  
                  // Trigger card animation for bot
                  setAnimatingCard({ seat: currentPlayerTurn, cardIndex: newGameState.currentTrick.length - 1 });
                  setTimeout(() => setAnimatingCard(null), 600);
                  
                  setGameState(newGameState);
                  toast.info(`${currentPlayer.name} plays ${cardBeingPlayed.value}${cardBeingPlayed.suit}`);
                  break;
                }
              }
            } else {
              // Try to win with appropriate card (try from lowest winning to highest)
              for (let i = currentPlayer.hand.length - 1; i >= 0; i--) {
                const cardBeingPlayed = currentPlayer.hand[i];
                const success = gameEngine.playCard(currentPlayerTurn, i);
                if (success) {
                  cardPlayed = true;
                  const newGameState = gameEngine.getGameState();
                  
                  // Trigger card animation for bot
                  setAnimatingCard({ seat: currentPlayerTurn, cardIndex: newGameState.currentTrick.length - 1 });
                  setTimeout(() => setAnimatingCard(null), 600);
                  
                  setGameState(newGameState);
                  toast.info(`${currentPlayer.name} plays ${cardBeingPlayed.value}${cardBeingPlayed.suit}`);
                  break;
                }
              }
            }
          }
        }
      }
      
      // If not partner or no special logic applied, play first valid card
      if (!cardPlayed) {
        for (let i = 0; i < currentPlayer.hand.length; i++) {
          const cardBeingPlayed = currentPlayer.hand[i]; // Save card reference BEFORE playing
          const success = gameEngine.playCard(currentPlayerTurn, i);
          if (success) {
            const newGameState = gameEngine.getGameState();
            
            // Trigger card animation for bot
            setAnimatingCard({ seat: currentPlayerTurn, cardIndex: newGameState.currentTrick.length - 1 });
            setTimeout(() => setAnimatingCard(null), 600);
            
            setGameState(newGameState);
            toast.info(`${currentPlayer.name} plays ${cardBeingPlayed.value}${cardBeingPlayed.suit}`);
            cardPlayed = true;
            break;
          }
        }
      }
      
      if (cardPlayed) {
        // Check if trick is complete (4 cards played)
        const newGameState = gameEngine.getGameState();
        
        // Check if we have a completed trick (will have lastCompletedTrick set)
        if (newGameState.lastCompletedTrick && newGameState.lastCompletedTrick.length === 4) {
          // Force UI update to show all 4 cards in the trick
          setGameState(newGameState);
          
          const winnerSeat = newGameState.leaderSeat; // After resolution, leaderSeat is updated to winner
          const winnerName = newGameState.players[winnerSeat]?.name || `Player ${winnerSeat + 1}`;
          toast.success(`Book complete! Winner: ${winnerName}`);
          
          // Give UI time to display all 4 cards before continuing
          setTimeout(() => {
            // Update state again and continue
            const updatedState = gameEngine.getGameState();
            setGameState(updatedState);
            
            // Check if hand is complete (HAND_SCORING state)
            if (updatedState.gameState === 'HAND_SCORING') {
              // Hand complete - scores will be shown via modal
              return;
            }
            
            // Continue with next trick
            setTimeout(() => {
              simulateNextBotPlay();
            }, 1000);
          }, 2500); // Give time to see all 4 cards
        } else {
          // Not end of trick, continue with next player
          setGameState(newGameState);
          setTimeout(() => {
            simulateNextBotPlay();
          }, 1500);
        }
      }
    }
  };

  const handleAcceptWager = async () => {
    try {
      await api.post(`/game/${gameId}/accept-entry`);
      
      // Refresh wager status
      const wagerResponse = await api.get(`/game/${gameId}/entry-status`);
      setWagerStatus(wagerResponse.data);
      setShowWagerAcceptModal(false);
      
      // Start game when all wagers are accepted
      if (wagerResponse.data.all_accepted && gameEngine) {
        // Properly initialize the game
        gameEngine.matchSettings = {
          allowBlindSix: true,
          allowAceCheck: true,
          wagerEmeralds: wagerResponse.data.wager_amount
        };
        
        // Start the hand immediately since it's a bot game
        gameEngine.startNewHand();
        const currentState = gameEngine.getGameState();
        
        // If blind window is active, skip it for bot games and go straight to bidding
        if (currentState.gameState === 'BLIND_WINDOW') {
          setTimeout(() => {
            gameEngine.closeBlindWindow();
            setGameState(gameEngine.getGameState());
            toast.success('Game started! Beginning bidding phase...');
          }, 1000);
        } else {
          setGameState(currentState);
          toast.success('Everyone is in! Game started!');
        }
      } else {
        toast.success('Entry accepted! Waiting for other players...');
      }
      
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Insufficient Emeralds for this entry');
      } else {
        toast.error('Failed to accept entry');
      }
    }
  };

  const handleDeclineWager = () => {
    setShowWagerAcceptModal(false);
    toast.info('You declined the entry. Leaving game...');
    window.history.back();
  };

  // Handle redirect to different game type (e.g., 4 In A Row)
  if (redirectTo) {
    return (
      <div className="min-h-screen bg-black">
        <NavBar />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-emerald-600">Redirecting to game...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <NavBar />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-emerald-600">Loading game...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-black">
        <NavBar />
        <div className="max-w-7xl mx-auto px-4 py-8 text-center">
          <Card className="max-w-md mx-auto">
            <CardContent className="p-8">
              <p className="text-gray-600">Game not found or failed to load</p>
              <Button onClick={() => window.history.back()} className="mt-4">
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Check if this is a practice game
  const isPracticeGame = game?.practice_mode === true;
  
  // ========================================================
  // NAVIGATION PROTECTION: Prevent accidental exit during game
  // Shows confirmation prompt if user tries to refresh/close/navigate
  // ========================================================
  const isGameActive = gameState && gameState.gameState !== 'MATCH_COMPLETE' && !showMatchCompleteModal;

  return (
    <div className={`min-h-screen overflow-hidden ${
      isPracticeGame 
        ? 'bg-gradient-to-br from-green-800 to-green-900' 
        : 'bg-gradient-to-br from-emerald-900 to-emerald-800'
    }`}>
      {/* Game Header */}
      <div className="bg-black/20 backdrop-blur-sm p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="text-white">
            <h1 className="text-xl font-bold">
              {isPracticeGame ? 'Practice Spades Game (NY Ruleset)' : 'Live Spades Game (NY Ruleset)'}
            </h1>
            <p className="text-green-200 text-sm">
              4 Players • Game ID: {gameId.slice(0, 8)}... • Hand {gameState?.currentHand || 1}
              {gameState && ` • Books Won: Team 1: ${gameState.booksWon[0]} | Team 2: ${gameState.booksWon[1]}`}
              {gameState?.matchScores && (
                <span className="ml-2 text-yellow-300 font-bold">
                  • Match Score: Team 1: {gameState.matchScores[0]} | Team 2: {gameState.matchScores[1]}
                </span>
              )}
              {gameState?.gameState === 'PLAYING' && gameState.teamBids && (
                <span className="ml-2 text-cyan-300">
                  • Team Bids: Team 1: {gameState.teamBids[0]} | Team 2: {gameState.teamBids[1]}
                </span>
              )}
              {wagerStatus && wagerStatus.wager_amount > 0 && !isPracticeGame && (
                <span className="ml-2">
                  🏆 Prize: {wagerStatus.prize_pot} Emeralds ({wagerStatus.wager_amount} per player)
                </span>
              )}
              {isPracticeGame && (
                <span className="ml-2 text-yellow-300">
                  🤖 Practice Mode - Playing with Bots
                </span>
              )}
            </p>
            
            {/* Show blind counters and match settings */}
            <div className="flex items-center gap-4 mt-1 text-xs text-amber-300">
              {gameState?.matchSettings?.allowBlindSix && (
                <span>
                  Blinds Left - Team 1: {gameState.teamRemainingBlinds?.[0] || 0}/3 | Team 2: {gameState.teamRemainingBlinds?.[1] || 0}/3
                </span>
              )}
              {gameState?.matchSettings?.allowAceCheck && (
                <span className="text-blue-400">
                  • Ace Check Left - Team 1: {gameState.aceCheckDeclared?.[0] ? 0 : 1}/1 | Team 2: {gameState.aceCheckDeclared?.[1] ? 0 : 1}/1
                  {gameState.aceCheckDeclared?.[0] && ` (Team 1 ${gameState.aceCheckSuccess?.[0] ? '✓' : '✗'})`}
                  {gameState.aceCheckDeclared?.[1] && ` (Team 2 ${gameState.aceCheckSuccess?.[1] ? '✓' : '✗'})`}
                </span>
              )}
              {gameState?.teamSetCount && (
                <span className="text-red-400 font-bold" title={`Debug: teamSetCount = [${gameState.teamSetCount[0]}, ${gameState.teamSetCount[1]}]`}>
                  • SETs: Team 1: {gameState.teamSetCount[0]} | Team 2: {gameState.teamSetCount[1]}
                </span>
              )}
            </div>
            
            {wagerStatus && wagerStatus.wager_amount > 0 && !wagerStatus.all_accepted && !isPracticeGame && (
              <p className="text-amber-300 text-xs mt-1">
                ⏳ Waiting for all players to accept entry...
              </p>
            )}
          </div>
          <div className="flex items-center gap-4 text-white">
            {!isPracticeGame && (
              <div className="bg-red-500 px-3 py-1 rounded-full animate-pulse">
                🔴 LIVE
              </div>
            )}
            <Button 
              variant="outline" 
              onClick={() => {
                if (isPracticeGame && isGameActive) {
                  setShowLeaveConfirm(true);
                } else {
                  navigate('/games');
                }
              }}
            >
              Leave Game
            </Button>
          </div>
        </div>
      </div>

      {/* Main Game Area */}
      <div className="p-4 h-[calc(100vh-80px)]">
        <div className="w-full h-full">
          {isPracticeGame ? (
            /* Simple Practice Game Table Layout */
            <div className="flex items-center justify-center h-full">
              <div className="bg-green-800 rounded-3xl w-full max-w-3xl h-full max-h-[450px] relative border-8 border-green-700 shadow-2xl">
                {/* Green Felt Table */}
                <div className="w-full h-full rounded-2xl bg-gradient-to-br from-green-700 to-green-800 flex items-center justify-center relative">
                  
                  {/* Player Positions */}
                  {/* You (Bottom) */}
                  <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white text-center">
                    <div className="bg-black/50 rounded-lg px-4 py-2">
                      <p className="font-bold">{gameState?.players[0]?.name || 'You'}</p>
                      <p className="text-sm">Team {(gameState?.players[0]?.team || 0) + 1}</p>
                    </div>
                  </div>
                  
                  {/* Bot 1 (Right) */}
                  <div className="absolute right-8 top-1/2 transform -translate-y-1/2 text-white text-center">
                    <div className="bg-black/50 rounded-lg px-4 py-2">
                      <p className="font-bold">🤖 {gameState?.players[1]?.name || 'Bot 1'}</p>
                      <p className="text-sm">Team {(gameState?.players[1]?.team || 1) + 1}</p>
                    </div>
                  </div>
                  
                  {/* Bot 2 (Top - Your Partner) */}
                  <div className="absolute top-8 left-1/2 transform -translate-x-1/2 text-white text-center">
                    <div className="bg-black/50 rounded-lg px-4 py-2">
                      <p className="font-bold">🤖 {gameState?.players[2]?.name || 'Bot 2'} (Partner)</p>
                      <p className="text-sm">Team {(gameState?.players[2]?.team || 0) + 1}</p>
                    </div>
                  </div>
                  
                  {/* Bot 3 (Left) */}
                  <div className="absolute left-8 top-1/2 transform -translate-y-1/2 text-white text-center">
                    <div className="bg-black/50 rounded-lg px-4 py-2">
                      <p className="font-bold">🤖 {gameState?.players[3]?.name || 'Bot 3'}</p>
                      <p className="text-sm">Team {(gameState?.players[3]?.team || 1) + 1}</p>
                    </div>
                  </div>
                  
                  {/* Card Play Area with Animations */}
                  <CardPlayArea 
                    currentTrick={gameState?.currentTrick || []}
                    lastCompletedTrick={gameState?.lastCompletedTrick || []}
                    players={gameState?.players || []}
                    animatingCard={animatingCard}
                  />
                  
                  {/* Dealing Animation Overlay - Shows when cards are being dealt face-down */}
                  {gameState?.gameState === 'DEALING' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-2xl">
                      <div className="text-center bg-black/80 rounded-xl p-6 animate-pulse">
                        <div className="flex justify-center mb-4">
                          {/* Animated card backs being dealt */}
                          <div className="flex gap-1">
                            {[0, 1, 2, 3].map((i) => (
                              <div 
                                key={i}
                                className="w-8 h-12 bg-blue-700 rounded border-2 border-blue-400 shadow-lg animate-bounce"
                                style={{ animationDelay: `${i * 100}ms` }}
                              >
                                <div className="w-full h-full flex items-center justify-center text-white text-xs font-bold">
                                  ♠
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <p className="text-white text-xl font-bold">Dealing Cards...</p>
                        <p className="text-gray-300 text-sm mt-2">Cards will be revealed when dealing is complete</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Live Streaming 4-Way Layout with Agora Video */
            <div className="relative w-full h-full">
              {LIVEKIT_LINKS && linkGameRoomInfo ? (
                <LiveKitLinkGrid
                  token={linkGameRoomInfo.token}
                  serverUrl={linkGameRoomInfo.url || LIVEKIT_URL}
                  roomName={linkGameRoomInfo.roomName}
                  selfInfo={{ user_id: user?.id, name: user?.name, picture: user?.picture }}
                  partners={(gameState?.players || game?.players || []).map(p => ({ id: p.id || p.user_id, name: p.name, picture: p.picture }))}
                  onError={(e) => console.error('[LiveKitLink] game grid error', e, { serverUrl: linkGameRoomInfo.url || LIVEKIT_URL, roomName: linkGameRoomInfo.roomName })}
                />
              ) : showVideoGrid && agoraToken && agoraChannel && agoraAppId ? (
                <AgoraVideoGrid 
                  channelName={agoraChannel}
                  token={agoraToken}
                  appId={agoraAppId}
                  localUid={0}
                  playerSeats={gameState?.players || []}
                />
              ) : (
              <div className="grid grid-cols-2 grid-rows-2 gap-1 h-full w-full">
                {/* Placeholder while loading Agora */}
                {gameState?.players?.map((player, index) => (
                  <div key={index} className={`relative bg-black overflow-hidden border-2 ${
                    index === 0 ? 'border-emerald-400' : 
                    index === 1 ? 'border-red-400' :
                    index === 2 ? 'border-blue-400' : 'border-yellow-400'
                  }`}>
                    <div className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-2"></div>
                        <p className="text-sm">Connecting video...</p>
                      </div>
                    </div>
                    <div className="absolute bottom-2 left-2 bg-black/80 text-white px-2 py-1 rounded text-xs">
                      {player.name}
                    </div>
                  </div>
                ))}
              </div>
              )}
              
              {/* Card Play Area Overlay on Video */}
              <CardPlayArea 
                currentTrick={gameState?.currentTrick || []}
                lastCompletedTrick={gameState?.lastCompletedTrick || []}
                players={gameState?.players || []}
                animatingCard={animatingCard}
              />
            </div>
          )}

            {/* Keep the old placeholder for reference - will be removed */}
            {false && gameState?.players[0] && (
              <div className="relative bg-black overflow-hidden border-2 border-emerald-400">
                <div className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
                  <div className="text-white text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-lg">👤</span>
                    </div>
                    <p className="font-bold text-sm">{gameState.players[0].name}</p>
                    <div className="mt-1 bg-red-500 px-2 py-1 rounded-full animate-pulse text-xs">
                      🔴 LIVE
                    </div>
                  </div>
                </div>
                
                {/* Player info overlay - Top Left Corner */}
                <div className="absolute top-1 left-1 bg-black/80 text-white px-2 py-1 rounded text-xs">
                  {gameState.players[0].name}
                </div>
                
                {/* Viewer count - Bottom Left */}
                <div className="absolute bottom-1 left-1 bg-black/80 text-white px-2 py-1 rounded text-xs">
                  1K
                </div>
                
                {/* Team indicator - Top Right */}
                <div className={`absolute top-1 right-1 w-3 h-3 rounded-full ${
                  gameState.players[0].team === 0 ? 'bg-emerald-400' : 'bg-red-400'
                }`}></div>
              </div>
            )}
        </div>
      </div>

      {/* Your Cards - Bottom of Screen (Only during BIDDING and PLAYING phases - NOT during DEALING or BLIND_WINDOW) */}
      {gameState?.players[0] && (gameState.gameState === 'BIDDING' || gameState.gameState === 'PLAYING') && (
        <div className="fixed bottom-0 left-0 right-0 bg-black/90 backdrop-blur-sm p-4 z-[60]">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-center gap-2 overflow-x-auto">
              {gameState.players[0].hand.map((card, index) => (
                <div
                  key={index}
                  onClick={gameState.gameState === 'PLAYING' ? () => handlePlayCard(index) : undefined}
                  className={`bg-white rounded-lg p-3 shadow-lg transition-all min-w-16 flex-shrink-0 ${
                    gameState.gameState === 'PLAYING' ? 'cursor-pointer hover:scale-110 hover:-translate-y-2' : 'cursor-default'
                  } ${selectedCardIndex === index ? 'ring-4 ring-yellow-400' : ''}`}
                >
                  <div className={`text-3xl text-center ${card.color === 'red' ? 'text-red-600' : 'text-black'}`}>
                    {card.suit}
                  </div>
                  <div className="text-xl font-bold text-center text-black">{card.value}</div>
                </div>
              ))}
            </div>
            
            {gameState.players[0].hand.length === 0 && (
              <div className="text-center text-white">
                <p>No cards remaining - Hand Complete!</p>
                <div className="mt-2 text-emerald-300">
                  Hand Score: Team 1: {gameState.handScores[0]} | Team 2: {gameState.handScores[1]}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* REMOVED: Conflicting footer that said "Cards are hidden during bidding phase" - cards should be visible per NY Ruleset */}

      {/* Match Entry Acceptance Modal */}
      {showWagerAcceptModal && wagerStatus && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md mx-4">
            <h2 className="text-2xl font-bold text-center mb-6">Accept Match Entry?</h2>
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <h4 className="font-semibold text-emerald-800 mb-2">Match Entry Details</h4>
                <div className="text-sm text-emerald-700 space-y-1">
                  <div className="flex justify-between">
                    <span>Your entry:</span>
                    <span className="font-semibold">{wagerStatus.wager_amount} Emeralds</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Prize Pool:</span>
                    <span className="font-semibold">{wagerStatus.prize_pot} Emeralds</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span>Platform service fee:</span>
                    <span>{wagerStatus.total_pot - wagerStatus.prize_pot} Emeralds</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Player Status</h4>
                <div className="space-y-2">
                  {wagerStatus.players_status.map((player, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span>{player.name}{player.is_bot ? ' (AI)' : ''}</span>
                      <span className={player.accepted || player.is_bot ? 'text-green-600' : 'text-amber-600'}>
                        {player.is_bot ? '✓ Auto' : player.accepted ? '✓ Accepted' : '⏳ Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Your balance:</strong> {user?.emeralds || 0} Emeralds
                </p>
                {(user?.emeralds || 0) < wagerStatus.wager_amount && (
                  <p className="text-sm text-red-600 mt-1">
                    ⚠️ Insufficient funds! You need {wagerStatus.wager_amount - (user?.emeralds || 0)} more Emeralds.
                  </p>
                )}
              </div>
              
              <div className="flex gap-3">
                <Button 
                  onClick={handleAcceptWager}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  disabled={(user?.emeralds || 0) < wagerStatus.wager_amount}
                >
                  Accept Entry
                </Button>
                <Button 
                  onClick={handleDeclineWager}
                  variant="outline" 
                  className="flex-1"
                >
                  Decline & Leave
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Blind Window Phase - 10 second countdown (appears after dealing, before cards are revealed) */}
      {gameState?.gameState === 'BLIND_WINDOW' && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md mx-4">
            <h2 className="text-2xl font-bold text-center mb-4">🎯 Blind Bid Opportunity!</h2>
            <div className="text-center mb-6">
              <div className="text-4xl font-bold text-red-600 mb-2">{blindWindowTimer}s</div>
              <p className="text-gray-600">Your team is down 100+ points. Declare a blind bid for DOUBLE points!</p>
              <p className="text-amber-600 text-sm mt-2">(Cards will be revealed after this window closes)</p>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Your Team Blinds Remaining</h4>
                <div className="text-2xl font-bold text-blue-700">
                  {gameState.teamRemainingBlinds[0]}/3
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Score Difference:</strong> You're down by {Math.abs(gameState.matchScores[1] - gameState.matchScores[0])} points
                </p>
              </div>
              
              {gameState.teamRemainingBlinds[0] > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 text-center">Choose your blind bid (6-13) for 2x points:</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[6, 7, 8, 9, 10, 11, 12, 13].map(value => (
                      <Button
                        key={value}
                        onClick={() => handleBlindBid(value)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        size="sm"
                      >
                        {value}
                      </Button>
                    ))}
                  </div>
                  <Button
                    onClick={handleDeclineBlind}
                    variant="outline"
                    className="w-full mt-3"
                  >
                    No Blind - See My Cards
                  </Button>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-amber-600 font-semibold">No blinds remaining for your team</p>
                  <Button
                    onClick={handleDeclineBlind}
                    variant="outline"
                    className="w-full mt-3"
                  >
                    Skip Blind Window
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bidding Phase Modal - Compact Version Above Cards */}
      {gameState?.gameState === 'BIDDING' && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-white rounded-xl p-4 shadow-2xl border-4 border-emerald-500 max-w-sm max-h-[70vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-center mb-3">Your Turn to Bid</h2>
            
            {/* Debug info */}
            <div className="text-xs bg-gray-100 p-2 rounded mb-2">
              <p>Current Bidder: {gameEngine?.getCurrentBidder()}</p>
              <p>Hand: {gameState.currentHand}</p>
              <p>Bids: [{gameState.bids.join(', ')}]</p>
            </div>
            
            {/* Show current team bids only */}
            <div className="mb-4">
              <div className="text-sm text-gray-600 text-center mb-2">
                Team 1: {gameState.teamBids?.[0] || 0} | Team 2: {gameState.teamBids?.[1] || 0}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="text-center">
                  <div className="font-semibold">Your Partner:</div>
                  <div>{gameState.players[2]?.name}: {gameState.bids[2] > 0 ? gameState.bids[2] : '?'}</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold">Opponents:</div>
                  <div>
                    {gameState.players[1]?.name}: {gameState.bids[1] > 0 ? gameState.bids[1] : '?'} | 
                    {gameState.players[3]?.name}: {gameState.bids[3] > 0 ? gameState.bids[3] : '?'}
                  </div>
                </div>
              </div>
            </div>

            {/* Ace Check controls if enabled */}
            {gameState.matchSettings?.allowAceCheck && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Ace Check (Optional):</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className={`p-3 rounded-lg border ${
                    gameState.aceCheckDeclared[0] ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="font-semibold">Your Team</div>
                    <Button
                      onClick={() => gameState.aceCheckDeclared[0] ? 
                        gameEngine.cancelAceCheck(0) && setGameState(gameEngine.getGameState()) : 
                        handleAceCheck(0)}
                      size="sm"
                      className={gameState.aceCheckDeclared[0] ? 
                        'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
                    >
                      {gameState.aceCheckDeclared[0] ? 'Cancel Ace Check' : 'Declare Ace Check'}
                    </Button>
                  </div>
                  <div className={`p-3 rounded-lg border ${
                    gameState.aceCheckDeclared[1] ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="font-semibold">Opponent Team</div>
                    <div className="text-sm text-gray-600">
                      {gameState.aceCheckDeclared[1] ? '✓ Declared' : 'Not declared'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Your bid input (only if your turn) */}
            {gameEngine?.getCurrentBidder() === 0 ? (
              <div className="space-y-2">
                <div>
                  <p className="text-center mb-2 text-sm font-bold text-emerald-600">Your Turn! Enter your bid (1-13):</p>
                  <div className="grid grid-cols-7 gap-1">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map(bid => (
                      <Button
                        key={bid}
                        onClick={() => handleBid(bid)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-xs"
                        size="sm"
                      >
                        {bid}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            ) : gameEngine?.getCurrentBidder() > 0 ? (
              <div className="text-center text-sm text-gray-700 py-3 bg-yellow-50 rounded">
                <p className="font-semibold">Waiting for {gameState.players[gameEngine?.getCurrentBidder()]?.name} to bid...</p>
                <p className="text-xs mt-1">Current bidder: Seat {gameEngine?.getCurrentBidder()}</p>
              </div>
            ) : (
              <div className="text-center text-sm text-red-600 py-3">
                <p>Error: No current bidder found</p>
                <p className="text-xs">getCurrentBidder() returned: {gameEngine?.getCurrentBidder()}</p>
              </div>
            )}

            {/* Show validation info */}
            <div className="mt-4 text-sm text-gray-600 text-center">
              <p>Team minimums: {gameState.config?.TEAM_MIN_BID || 4} each</p>
              <p>Table minimum: {gameState.config?.TABLE_MIN_TOTAL_BID || 11} total</p>
              <p>Current total: {gameState.teamBids[0] + gameState.teamBids[1]}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Hand Score Modal */}
      <Dialog open={showHandScoreModal} onOpenChange={() => {}}>
        <DialogContent className="bg-gradient-to-br from-gray-900 to-black text-white border-2 border-emerald-500 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center text-emerald-400">
              🎴 Hand {gameState?.currentHand || 1} Complete!
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Team Scores */}
            <div className="grid grid-cols-2 gap-4">
              {/* Team 1 (Team 0 in code) */}
              <div className="bg-gradient-to-br from-blue-900/50 to-blue-800/50 p-4 rounded-lg border-2 border-blue-500">
                <h3 className="text-lg font-bold text-center text-blue-300 mb-2">Team 1</h3>
                <div className="text-center">
                  <p className="text-sm text-gray-300">This Hand:</p>
                  <p className="text-3xl font-bold text-white">{gameState?.handScores?.[0] || 0}</p>
                  <div className="mt-2 pt-2 border-t border-blue-600">
                    <p className="text-xs text-gray-400">Match Total:</p>
                    <p className="text-xl font-bold text-emerald-400">{gameState?.matchScores?.[0] || 0}</p>
                  </div>
                </div>
                <div className="mt-3 text-xs text-gray-300 space-y-1">
                  {gameState?.currentHand === 1 ? (
                    // Hand 1: No bidding, just show books and score
                    <>
                      <p className="font-semibold">Hand 1: Bid Itself</p>
                      <p className="font-semibold">Team Books: {((gameState?.playerBooksWon?.[0] || 0) + (gameState?.playerBooksWon?.[2] || 0))}</p>
                      <p className="text-[10px] italic">No bidding in Hand 1</p>
                    </>
                  ) : (
                    // Hand 2+: Show bids and books
                    <>
                      <p className="font-semibold">Team Bid: {gameState?.teamBids?.[0] || 0}</p>
                      <p className="font-semibold">Team Books: {((gameState?.playerBooksWon?.[0] || 0) + (gameState?.playerBooksWon?.[2] || 0))}</p>
                      <div className="mt-2 pt-2 border-t border-blue-700 text-[10px] space-y-0.5">
                        <p>{gameState?.players?.[0]?.name}: Bid {gameState?.bids?.[0] || 0}, Won {gameState?.playerBooksWon?.[0] || 0}</p>
                        <p>{gameState?.players?.[2]?.name}: Bid {gameState?.bids?.[2] || 0}, Won {gameState?.playerBooksWon?.[2] || 0}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Team 2 (Team 1 in code) */}
              <div className="bg-gradient-to-br from-red-900/50 to-red-800/50 p-4 rounded-lg border-2 border-red-500">
                <h3 className="text-lg font-bold text-center text-red-300 mb-2">Team 2</h3>
                <div className="text-center">
                  <p className="text-sm text-gray-300">This Hand:</p>
                  <p className="text-3xl font-bold text-white">{gameState?.handScores?.[1] || 0}</p>
                  <div className="mt-2 pt-2 border-t border-red-600">
                    <p className="text-xs text-gray-400">Match Total:</p>
                    <p className="text-xl font-bold text-emerald-400">{gameState?.matchScores?.[1] || 0}</p>
                  </div>
                </div>
                <div className="mt-3 text-xs text-gray-300 space-y-1">
                  {gameState?.currentHand === 1 ? (
                    // Hand 1: No bidding, just show books and score
                    <>
                      <p className="font-semibold">Hand 1: Bid Itself</p>
                      <p className="font-semibold">Team Books: {((gameState?.playerBooksWon?.[1] || 0) + (gameState?.playerBooksWon?.[3] || 0))}</p>
                      <p className="text-[10px] italic">No bidding in Hand 1</p>
                    </>
                  ) : (
                    // Hand 2+: Show bids and books
                    <>
                      <p className="font-semibold">Team Bid: {gameState?.teamBids?.[1] || 0}</p>
                      <p className="font-semibold">Team Books: {((gameState?.playerBooksWon?.[1] || 0) + (gameState?.playerBooksWon?.[3] || 0))}</p>
                      <div className="mt-2 pt-2 border-t border-red-700 text-[10px] space-y-0.5">
                        <p>{gameState?.players?.[1]?.name}: Bid {gameState?.bids?.[1] || 0}, Won {gameState?.playerBooksWon?.[1] || 0}</p>
                        <p>{gameState?.players?.[3]?.name}: Bid {gameState?.bids?.[3] || 0}, Won {gameState?.playerBooksWon?.[3] || 0}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Renege Warning - Only show if reneges occurred */}
            {gameState?.reneges && (gameState.reneges[0]?.length > 0 || gameState.reneges[1]?.length > 0) && (
              <div className="bg-red-900/30 border-2 border-red-500 p-3 rounded-lg">
                <h4 className="text-center font-bold text-red-400 mb-2">⚠️ RENEGE DETECTED ⚠️</h4>
                <div className="text-xs text-red-200 space-y-2">
                  {gameState.reneges[0]?.length > 0 && (
                    <div className="bg-red-950/50 p-2 rounded">
                      <p className="font-bold text-red-300">Team 1 Renege(s): -3 Books Penalty</p>
                      {gameState.reneges[0].map((renege, idx) => (
                        <div key={idx} className="mt-1 pl-2 border-l-2 border-red-600">
                          <p>Player: {gameState.players[renege.playerSeat]?.name}</p>
                          <p>Card Played: {renege.offendingCard?.value}{renege.offendingCard?.suit}</p>
                          <p>Book #{renege.bookIndex + 1}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {gameState.reneges[1]?.length > 0 && (
                    <div className="bg-red-950/50 p-2 rounded">
                      <p className="font-bold text-red-300">Team 2 Renege(s): -3 Books Penalty</p>
                      {gameState.reneges[1].map((renege, idx) => (
                        <div key={idx} className="mt-1 pl-2 border-l-2 border-red-600">
                          <p>Player: {gameState.players[renege.playerSeat]?.name}</p>
                          <p>Card Played: {renege.offendingCard?.value}{renege.offendingCard?.suit}</p>
                          <p>Book #{renege.bookIndex + 1}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-center text-[10px] italic mt-2">
                    Renege = Failed to follow suit or play trump when able
                  </p>
                </div>
              </div>
            )}
            
            {/* Match Status */}
            <div className="bg-black/50 p-3 rounded-lg border border-emerald-600">
              <p className="text-center text-sm text-gray-300">
                Playing to {gameState?.config?.MATCH_TARGET_POINTS || 500} points
              </p>
              {gameState?.matchScores && (gameState.matchScores[0] >= 500 || gameState.matchScores[1] >= 500) ? (
                <p className="text-center text-lg font-bold text-yellow-400 mt-2">
                  🏆 MATCH COMPLETE! 🏆
                </p>
              ) : (
                <p className="text-center text-xs text-gray-400 mt-1">
                  Next: Hand {(gameState?.currentHand || 1) + 1}
                </p>
              )}
            </div>
            
            {/* Continue Button */}
            <Button
              onClick={handleContinueToNextHand}
              className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-bold py-3"
            >
              {gameState?.matchScores && (gameState.matchScores[0] >= 500 || gameState.matchScores[1] >= 500) 
                ? 'View Final Results' 
                : 'Continue to Next Hand'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Match Complete Modal - Dramatic Winner Announcement */}
      <Dialog open={showMatchCompleteModal} onOpenChange={setShowMatchCompleteModal}>
        <DialogContent data-testid="spades-match-complete-modal" className="bg-gradient-to-br from-black via-gray-900 to-black text-white border-4 border-yellow-400 max-w-2xl max-h-[90vh] overflow-y-auto animate-in zoom-in duration-700 shadow-2xl shadow-yellow-500/50">
          {/* Animated background effects */}
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 via-transparent to-yellow-400/10 animate-pulse pointer-events-none"></div>
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
            <div className="absolute top-10 left-10 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
            <div className="absolute top-20 right-20 w-2 h-2 bg-yellow-300 rounded-full animate-ping" style={{animationDelay: '0.2s'}}></div>
            <div className="absolute bottom-20 left-20 w-2 h-2 bg-yellow-400 rounded-full animate-ping" style={{animationDelay: '0.4s'}}></div>
            <div className="absolute bottom-10 right-10 w-3 h-3 bg-yellow-300 rounded-full animate-ping" style={{animationDelay: '0.6s'}}></div>
            <div className="absolute top-1/2 left-5 w-2 h-2 bg-emerald-400 rounded-full animate-ping" style={{animationDelay: '0.3s'}}></div>
            <div className="absolute top-1/2 right-5 w-2 h-2 bg-emerald-400 rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
          </div>
          
          <DialogHeader>
            <DialogTitle className="text-6xl font-black text-center text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-yellow-200 to-yellow-400 animate-pulse pb-4 drop-shadow-[0_0_30px_rgba(250,204,21,0.8)]">
              🏆 MATCH COMPLETE! 🏆
            </DialogTitle>
            <div className="text-center text-yellow-300 text-xl font-semibold animate-bounce">
              ⭐ ⭐ ⭐
            </div>
          </DialogHeader>
          
          <div className="space-y-6 py-6 relative z-10">
            {/* Winner Announcement */}
            {(() => {
              const team1Score = gameState?.matchScores?.[0] || 0;
              const team2Score = gameState?.matchScores?.[1] || 0;
              const team1Sets = gameState?.teamSetCount?.[0] || 0;
              const team2Sets = gameState?.teamSetCount?.[1] || 0;
              
              let winnerTeam, winReason;
              
              // Check if this was a forfeit win
              const isForfeit = prizeDistributions?.some(d => d.result === 'win_forfeit' || d.result === 'loss_forfeit');
              
              if (isForfeit) {
                // Forfeit win - determine winner from prize distributions
                const forfeitWinner = prizeDistributions.find(d => d.result === 'win_forfeit');
                winnerTeam = forfeitWinner ? forfeitWinner.team + 1 : 1;
                winReason = "Opponent Disconnected!";
              } else {
                // Determine winner and reason
                if (team1Sets >= 3) {
                  winnerTeam = 2;
                  winReason = "Team 1 SET 3 times!";
                } else if (team2Sets >= 3) {
                  winnerTeam = 1;
                  winReason = "Team 2 SET 3 times!";
                } else if (team1Score >= 500 && team2Score >= 500) {
                  winnerTeam = team1Score > team2Score ? 1 : 2;
                  winReason = "Highest Score!";
                } else if (team1Score >= 500) {
                  winnerTeam = 1;
                  winReason = "Reached 500 Points!";
                } else if (team2Score >= 500) {
                  winnerTeam = 2;
                  winReason = "Reached 500 Points!";
                }
              }
              
              return (
                <div className={`text-center py-8 px-6 rounded-2xl border-4 animate-in slide-in-from-top duration-700 ${
                  winnerTeam === 1 
                    ? 'bg-gradient-to-br from-blue-600/30 to-blue-800/30 border-blue-400' 
                    : 'bg-gradient-to-br from-red-600/30 to-red-800/30 border-red-400'
                }`}>
                  <div className="text-7xl font-black mb-4 animate-bounce">
                    {winnerTeam === 1 ? '👑' : '👑'}
                  </div>
                  <h2 className={`text-6xl font-black mb-3 ${
                    winnerTeam === 1 ? 'text-blue-300' : 'text-red-300'
                  }`}>
                    TEAM {winnerTeam} WINS!
                  </h2>
                  <p className="text-2xl text-yellow-300 font-semibold mb-2">
                    {winReason}
                  </p>
                  {winnerTeam === 1 && (
                    <div className="mt-4 space-y-1">
                      <p className="text-lg text-blue-200">🎮 {gameState?.players?.[0]?.name}</p>
                      <p className="text-lg text-blue-200">🎮 {gameState?.players?.[2]?.name}</p>
                    </div>
                  )}
                  {winnerTeam === 2 && (
                    <div className="mt-4 space-y-1">
                      <p className="text-lg text-red-200">🎮 {gameState?.players?.[1]?.name}</p>
                      <p className="text-lg text-red-200">🎮 {gameState?.players?.[3]?.name}</p>
                    </div>
                  )}
                </div>
              );
            })()}
            
            {/* Final Scores */}
            <div className="grid grid-cols-2 gap-6">
              {/* Team 1 */}
              <div className="bg-gradient-to-br from-blue-900/70 to-blue-950/70 p-6 rounded-xl border-3 border-blue-400 animate-in slide-in-from-left duration-500">
                <h3 className="text-2xl font-bold text-center text-blue-300 mb-4">Team 1</h3>
                <div className="text-center">
                  <p className="text-sm text-gray-300 mb-1">Final Score</p>
                  <p className="text-5xl font-black text-white mb-4">{gameState?.matchScores?.[0] || 0}</p>
                  <div className="space-y-2 text-sm text-gray-300">
                    <div className="flex justify-between px-2">
                      <span>Hands Played:</span>
                      <span className="font-bold text-white">{gameState?.currentHand || 1}</span>
                    </div>
                    <div className="flex justify-between px-2">
                      <span>SETs:</span>
                      <span className="font-bold text-white">{gameState?.teamSetCount?.[0] || 0}</span>
                    </div>
                    <div className="flex justify-between px-2">
                      <span>Total Bags:</span>
                      <span className="font-bold text-white">{gameState?.totalBags?.[0] || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-blue-700 text-xs text-gray-300 space-y-1">
                  <p className="font-semibold text-blue-200">{gameState?.players?.[0]?.name}</p>
                  <p className="font-semibold text-blue-200">{gameState?.players?.[2]?.name}</p>
                </div>
              </div>
              
              {/* Team 2 */}
              <div className="bg-gradient-to-br from-red-900/70 to-red-950/70 p-6 rounded-xl border-3 border-red-400 animate-in slide-in-from-right duration-500">
                <h3 className="text-2xl font-bold text-center text-red-300 mb-4">Team 2</h3>
                <div className="text-center">
                  <p className="text-sm text-gray-300 mb-1">Final Score</p>
                  <p className="text-5xl font-black text-white mb-4">{gameState?.matchScores?.[1] || 0}</p>
                  <div className="space-y-2 text-sm text-gray-300">
                    <div className="flex justify-between px-2">
                      <span>Hands Played:</span>
                      <span className="font-bold text-white">{gameState?.currentHand || 1}</span>
                    </div>
                    <div className="flex justify-between px-2">
                      <span>SETs:</span>
                      <span className="font-bold text-white">{gameState?.teamSetCount?.[1] || 0}</span>
                    </div>
                    <div className="flex justify-between px-2">
                      <span>Total Bags:</span>
                      <span className="font-bold text-white">{gameState?.totalBags?.[1] || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-red-700 text-xs text-gray-300 space-y-1">
                  <p className="font-semibold text-red-200">{gameState?.players?.[1]?.name}</p>
                  <p className="font-semibold text-red-200">{gameState?.players?.[3]?.name}</p>
                </div>
              </div>
            </div>
            
            {/* Prize Distribution - Only for non-practice games */}
            {!isPracticeGame && prizeDistributions && (
              <div className="bg-gradient-to-br from-emerald-900/30 to-green-900/30 p-6 rounded-xl border-2 border-emerald-500 animate-in slide-in-from-bottom duration-700">
                <h4 className="text-center text-2xl font-bold text-emerald-300 mb-4 flex items-center justify-center gap-2">
                  💰 Prize Distribution 💰
                </h4>
                
                <div className="grid grid-cols-1 gap-3">
                  {prizeDistributions.map((dist, index) => {
                    const isWin = dist.result === 'win' || dist.result === 'win_forfeit';
                    const isForfeit = dist.result === 'loss_forfeit';
                    
                    return (
                      <div 
                        key={index}
                        className={`p-4 rounded-lg border-2 ${
                          isWin 
                            ? 'bg-emerald-900/50 border-emerald-400' 
                            : 'bg-red-900/50 border-red-400'
                        } flex items-center justify-between`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
                            isWin ? 'bg-emerald-600' : 'bg-red-600'
                          }`}>
                            {isWin ? '👑' : isForfeit ? '🔌' : '😞'}
                          </div>
                          <div>
                            <p className="font-bold text-white text-lg">{dist.player_name}</p>
                            <p className="text-xs text-gray-300">
                              Team {dist.team + 1}
                              {isForfeit && <span className="ml-2 text-red-400">(Disconnected)</span>}
                            </p>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-sm text-gray-300">Entry: -{dist.wager} <EmeraldIcon className="h-3 w-3 inline" /></p>
                          {isWin && (
                            <p className="text-sm text-emerald-300">Prize: +{dist.prize} <RubyIcon className="h-3 w-3 inline" /></p>
                          )}
                          <p className={`text-xl font-bold ${
                            dist.net >= 0 ? 'text-emerald-400' : 'text-red-400'
                          }`}>
                            {dist.net >= 0 ? '+' : ''}{dist.net} <EmeraldIcon className="h-4 w-4 inline" />
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-4 pt-4 border-t border-emerald-700 text-center">
                  <p className="text-sm text-gray-400">
                    Total Prize Pool: <span className="text-emerald-400 font-bold">{prizeDistributions.reduce((sum, d) => sum + (d.result === 'win' ? d.prize : 0), 0)} <EmeraldIcon className="h-3.5 w-3.5 inline" /></span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">({Math.round((economyRates?.rake_1v1 ?? 0.08) * 100)}% GEMS platform fee applied)</p>
                </div>
              </div>
            )}
            
            {/* Match Stats */}
            <div className="bg-gradient-to-r from-gray-900/50 via-black/50 to-gray-900/50 p-4 rounded-lg border border-yellow-600 animate-in fade-in duration-1000">
              <h4 className="text-center text-lg font-bold text-yellow-400 mb-3">📊 Match Statistics</h4>
              <div className="grid grid-cols-3 gap-4 text-center text-sm">
                <div>
                  <p className="text-gray-400">Total Hands</p>
                  <p className="text-2xl font-bold text-white">{gameState?.currentHand || 1}</p>
                </div>
                <div>
                  <p className="text-gray-400">Entry</p>
                  <p className="text-2xl font-bold text-emerald-400">{gameState?.matchSettings?.wagerEmeralds || 50} <EmeraldIcon className="h-5 w-5 inline" /></p>
                </div>
                <div>
                  <p className="text-gray-400">Duration</p>
                  <p className="text-2xl font-bold text-white">{Math.floor((gameState?.currentHand || 1) * 2.5)} min</p>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col gap-3 sticky bottom-0 bg-gradient-to-t from-black via-gray-900 to-transparent pt-4">
              <Button
                onClick={() => {
                  setShowMatchCompleteModal(false);
                  navigate('/games');
                }}
                className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-bold py-4 text-xl shadow-lg shadow-emerald-500/50 border-2 border-emerald-400"
              >
                ✨ Return to Games
              </Button>
              <Button
                onClick={() => {
                  setShowMatchCompleteModal(false);
                  // Reset and start new game
                  window.location.reload();
                }}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 text-lg border border-blue-400"
              >
                🎮 Play Again
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Leave Game Confirmation Dialog */}
      <Dialog open={showLeaveConfirm} onOpenChange={setShowLeaveConfirm}>
        <DialogContent className="bg-gray-900 text-white border border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center text-red-400">
              ⚠️ Leave Game?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <p className="text-gray-300 mb-4">
              You have an active practice game in progress.
            </p>
            <p className="text-yellow-400 font-semibold">
              Leaving now will forfeit this match!
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowLeaveConfirm(false)}
              className="flex-1 bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowLeaveConfirm(false);
                navigate('/games');
              }}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, Leave Game
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const NavBar = () => {
  const { user, logout } = React.useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [notificationsList, setNotificationsList] = useState([]);
  const notificationsRef = useRef(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const mobileMenuRef = useRef(null);
  const mobileMenuPanelRef = useRef(null);
  const { isPlus } = useIsPlus();
  const isOnLivePage = location.pathname === '/live';
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);
  const isActive = (path) => location.pathname === path;
  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const response = await api.get('/notifications?limit=10');
      setNotificationsList(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) setShowNotificationsDropdown(false);
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) setShowProfileMenu(false);
      if (
        mobileMenuRef.current && !mobileMenuRef.current.contains(e.target) &&
        (!mobileMenuPanelRef.current || !mobileMenuPanelRef.current.contains(e.target))
      ) setShowMobileMenu(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchResults(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => { setShowMobileMenu(false); }, [location.pathname]);
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) { setSearchResults([]); setShowSearchResults(false); return; }
    setSearchLoading(true);
    setShowSearchResults(true);
    try {
      const response = await api.get(`/users/search?q=${encodeURIComponent(query)}&limit=10`);
      setSearchResults(response.data.users || []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };
  useEffect(() => {
    const fetchUnreadCounts = async () => {
      try {
        const [messagesRes, notificationsRes] = await Promise.all([
          api.get('/messages/conversations'),
          api.get('/notifications')
        ]);
        const msgCount = messagesRes.data.conversations?.reduce((sum, conv) => sum + (conv.unread_count || 0), 0) || 0;
        const notifCount = notificationsRes.data.unread_count || 0;
        setUnreadMessages(msgCount);
        setUnreadNotifications(notifCount);
      } catch (error) {
        console.error('Error fetching unread counts:', error);
      }
    };
    if (user) {
      fetchUnreadCounts();
      const interval = setInterval(fetchUnreadCounts, 10000);
      return () => clearInterval(interval);
    }
  }, [user]);
  const navItems = [
    { href: '/discover',     label: 'Discover',    icon: Video,    active: isActive('/discover') || isActive('/live') },
    { href: '/games',        label: 'Games',       icon: Gamepad2, active: isActive('/games'),        blockedWhileLive: true },
    { href: '/leaderboards', label: 'Leaderboard', icon: Trophy,   active: isActive('/leaderboards') },
    { href: '/wallet',       label: 'Wallet',      icon: Coins,    active: isActive('/wallet'),       blockedWhileLive: true },
    { href: '/messages',     label: 'Messages',    icon: Mail,     active: isActive('/messages'),     badge: unreadMessages, blockedWhileLive: true },
    { href: '/profile',      label: 'Profile',     icon: User,     active: isActive('/profile'),      blockedWhileLive: true },
  ];
  const go = (href, blockedWhileLive) => {
    if (blockedWhileLive && isOnLivePage) { toast.info('End your stream first'); return; }
    navigate(href);
  };
  const renderSearch = (mobile = false) => (
    <>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search users"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
          className={`${mobile ? 'w-full' : 'w-36 2xl:w-52'} pl-10 pr-4 py-2 bg-white/10 border border-white/10 rounded-full text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent`}
          autoComplete="off"
        />
      </div>
      {showSearchResults && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 max-h-80 overflow-y-auto z-50">
          {searchLoading ? (
            <div className="p-4 text-center text-gray-500">
              <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-sm mt-2">Searching...</p>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="p-4 text-center text-gray-500"><p className="text-sm">No users found</p></div>
          ) : (
            searchResults.map((result) => (
              <div
                key={result.id}
                onClick={() => { navigate(`/profile/${result.handle || result.id}`); setShowSearchResults(false); setSearchQuery(''); setShowMobileMenu(false); }}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold overflow-hidden">
                  {result.picture ? <img src={result.picture} alt={result.name} className="w-full h-full object-cover" /> : (result.name?.[0] || '?')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 truncate">{result.name}</p>
                    {result.badge_type === 'sapphire' && <SapphireBadgeIcon className="w-4 h-4" />}
                    {(result.badge_type === 'canary' || result.badge_type === 'verified') && <CanaryBadgeIcon className="w-4 h-4" />}
                    {result.is_bot && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">AI</span>}
                  </div>
                  <p className="text-sm text-emerald-600">@{result.handle || 'user'}</p>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </>
  );
  return (
    <nav className="bg-[#080C13]/95 backdrop-blur-md border-b border-white/10 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-3 lg:px-4">
        <div className="flex items-center h-16 gap-3">
          <button onClick={() => navigate('/discover')} className="flex items-center gap-2 shrink-0" aria-label="GEMS home">
            <div className="w-8 h-8 relative">
              <img src={BRAND_ASSETS.logo} alt="GEMS" className="w-full h-full object-contain filter drop-shadow-sm" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-emerald-800 bg-clip-text text-transparent">GEMS</span>
          </button>
          <div className="hidden xl:block relative shrink-0" ref={searchRef}>
            {renderSearch(false)}
          </div>
          <div className="hidden xl:flex flex-1 items-center justify-center gap-1" data-testid="header-nav-links">
            {navItems.map((item) => (
              <NavLink key={item.href} href={item.href} icon={item.icon} active={item.active} badge={item.badge} blockedWhileLive={item.blockedWhileLive}>
                {item.label}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-2 lg:gap-3 shrink-0 ml-auto" data-testid="header-right-cluster">
            <div className="hidden sm:flex items-center gap-3 text-sm text-white bg-white/5 border border-white/10 rounded-full px-3 py-1.5 shrink-0" data-testid="header-gem-counts">
              <div className="flex items-center gap-1"><EmeraldIcon className="h-5 w-5" /><span className="font-medium tabular-nums">{user?.emeralds || 0}</span></div>
              <div className="flex items-center gap-1"><RubyIcon className="h-5 w-5" /><span className="font-medium tabular-nums">{user?.rubies || 0}</span></div>
            </div>
            {isPlus === false && (
              <button type="button" onClick={startGemsPlusFlow} data-testid="plus-cta-nav" title="Upgrade to GEMS Plus"
                className="hidden xl:inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 shadow-sm shrink-0">
                <span>Go Plus</span><span className="text-emerald-100">✦</span>
              </button>
            )}
            <div className="relative shrink-0" ref={notificationsRef}>
              <button onClick={() => {
                if (isOnLivePage) { fetchNotifications(); setShowNotificationsDropdown(!showNotificationsDropdown); }
                else { navigate('/notifications'); }
                api.post('/notifications/mark-all-read').then(() => setUnreadNotifications(0)).catch(() => {});
              }}
                className="relative p-2 rounded-full hover:bg-white/10 transition-colors" title="Notifications">
                <Bell className="h-5 w-5 text-gray-300" />
                {unreadNotifications > 0 && (
                  <span className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadNotifications > 99 ? '99+' : unreadNotifications}
                  </span>
                )}
              </button>
              {showNotificationsDropdown && isOnLivePage && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-[100] max-h-96 overflow-y-auto">
                  <div className="p-3 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="font-semibold text-gray-900">Notifications</h3>
                    <button onClick={() => setShowNotificationsDropdown(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {notificationsList.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">No notifications</div>
                    ) : (
                      notificationsList.slice(0, 5).map((notif, idx) => (
                        <div key={idx} className="p-3 hover:bg-gray-50 cursor-pointer">
                          <p className="text-sm text-gray-800">{notif.message || notif.title}</p>
                          <p className="text-xs text-gray-400 mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="p-2 border-t border-gray-100 bg-gray-50">
                    <p className="text-xs text-center text-gray-500">End your stream to view all notifications</p>
                  </div>
                </div>
              )}
            </div>
            <div className="relative shrink-0" ref={profileMenuRef}>
              <button type="button" onClick={() => setShowProfileMenu((v) => !v)} aria-haspopup="menu" aria-expanded={showProfileMenu}
                data-testid="profile-menu-trigger" title={user?.name || 'Profile'} className="p-0.5 rounded-full hover:ring-2 hover:ring-emerald-200 transition-shadow flex">
                <Avatar className="w-9 h-9 border-2 border-emerald-200">
                  <AvatarImage src={user?.picture} alt={user?.name || 'user'} />
                  <AvatarFallback className="bg-emerald-100 text-emerald-800 font-semibold">
                    {(user?.name?.[0] || user?.handle?.[0] || 'U').toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </button>
              {showProfileMenu && (
                <div role="menu" data-testid="profile-menu-dropdown" className="absolute right-0 top-12 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900 truncate">{user?.name || 'Signed in'}</p>
                    {user?.handle && <p className="text-xs text-gray-500 truncate">@{user.handle}</p>}
                  </div>
                  {isPlus === false && (
                    <button type="button" role="menuitem" data-testid="plus-cta-profile-menu"
                      onClick={() => { setShowProfileMenu(false); startGemsPlusFlow(); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 font-medium border-b border-gray-100">
                      <Gem className="w-4 h-4" />Upgrade to GEMS Plus<span className="ml-auto text-emerald-500">✦</span>
                    </button>
                  )}
                  <button type="button" role="menuitem" data-testid="profile-menu-settings"
                    onClick={() => { setShowProfileMenu(false); if (isOnLivePage) { toast.info('End your stream to access settings'); return; } navigate('/settings'); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <Settings className="w-4 h-4 text-gray-500" />Settings
                  </button>
                  <button type="button" role="menuitem" data-testid="profile-menu-logout"
                    onClick={async () => { setShowProfileMenu(false); if (isOnLivePage) { toast.info('End your stream before logging out'); return; } await logout?.(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-700 hover:bg-rose-50">
                    <LogOut className="w-4 h-4" />Log Out
                  </button>
                </div>
              )}
            </div>
            <div className="relative xl:hidden shrink-0" ref={mobileMenuRef}>
              <button type="button" onClick={() => setShowMobileMenu((v) => !v)} aria-label="Menu" aria-expanded={showMobileMenu}
                className="p-2 rounded-full hover:bg-white/10 transition-colors">
                {showMobileMenu ? <X className="h-5 w-5 text-gray-200" /> : <Menu className="h-5 w-5 text-gray-200" />}
              </button>
            </div>
          </div>
        </div>
        {showMobileMenu && (
          <div ref={mobileMenuPanelRef} className="xl:hidden border-t border-gray-100 pt-2 pb-3" data-testid="header-mobile-menu">
            <div className="relative px-1 pb-2" ref={searchRef}>{renderSearch(true)}</div>
            {/* Emeralds / Rubies balance — hidden in the bar on phones, surfaced here */}
            <div className="flex items-center justify-center gap-5 mx-1 mb-2 py-2 bg-gray-50 rounded-lg text-sm" data-testid="header-mobile-gem-counts">
              <div className="flex items-center gap-1.5"><EmeraldIcon className="h-5 w-5" /><span className="font-semibold tabular-nums">{user?.emeralds || 0}</span></div>
              <div className="flex items-center gap-1.5"><RubyIcon className="h-5 w-5" /><span className="font-semibold tabular-nums">{user?.rubies || 0}</span></div>
            </div>
            <div className="flex flex-col">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button key={item.href} onClick={() => { setShowMobileMenu(false); go(item.href, item.blockedWhileLive); }}
                    className={`flex items-center gap-3 px-3 py-3 text-[15px] font-medium rounded-lg transition-colors ${item.active ? 'bg-emerald-100 text-emerald-800' : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'}`}>
                    <Icon className="h-5 w-5" /><span>{item.label}</span>
                    {item.badge > 0 && <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">{item.badge > 99 ? '99+' : item.badge}</span>}
                  </button>
                );
              })}
            </div>
            {isPlus === false && (
              <button type="button" onClick={() => { setShowMobileMenu(false); startGemsPlusFlow(); }}
                className="mt-2 w-full inline-flex items-center justify-center gap-1 text-sm font-semibold px-3 py-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
                <span>Go Plus</span><span className="text-emerald-100">✦</span>
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};
const NavLink = ({ href, icon: Icon, children, active, badge, blockedWhileLive }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isOnLivePage = location.pathname === '/live';
  const handleClick = (e) => {
    e.preventDefault();
    if (blockedWhileLive && isOnLivePage) { toast.info('End your stream first'); return; }
    navigate(href);
  };
  return (
    <button onClick={handleClick} title={typeof children === 'string' ? children : undefined}
      className={`flex items-center gap-1.5 px-2 py-2 text-sm font-medium rounded-lg transition-all duration-200 relative whitespace-nowrap ${
        active ? 'bg-emerald-100 text-emerald-800 shadow-sm' : 'text-gray-600 hover:text-emerald-700 hover:bg-emerald-50'
      }`}>
      {Icon ? <Icon className="h-4 w-4" /> : null}
      <span>{children}</span>
      {badge > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
};


export default App;
